import { ATOM_SYMBOL, ArithmeticError, AssertionError, DvalaError, EFFECT_SYMBOL, FUNCTION_SYMBOL, KeyError, MAX_MACRO_EXPANSION_DEPTH, MacroError, MatchError, NodeTypes, PersistentMap, PersistentVector, REGEXP_SYMBOL, RecurSignal, ReferenceError, RuntimeError, TypeError as TypeError$1, UserError, arityAcceptsMin, asAny, asArray, asEffect, asFunctionLike, asNonUndefined, asNumber, asSeq, asString, asStringOrNumber, assert2dVector, assert3dVector, assertAny, assertArray, assertCharArray, assertColl, assertEffect, assertFunctionLike, assertGrid, assertMatrix, assertNonEmptyVector, assertNonUndefined, assertNumber, assertNumberOfParams, assertObj, assertRegularExpression, assertSeq, assertSquareMatrix, assertString, assertStringOrNumber, assertStringOrRegularExpression, assertVector, bindingTargetTypes, cons, getArityFromFunction, getCommonArityFromFunctions, isAny, isArr, isAtom, isBuiltinSymbolNode, isColl, isDvalaFunction, isEffect, isGrid, isHandlerFunction, isMacroFunction, isMatrix, isNodeType, isNormalExpressionNodeWithName, isNumber, isObj, isPersistentMap, isPersistentVector, isRegularExpression, isSeq, isSpecialSymbolNode, isSpreadNode, isSquareMatrix, isString, isStringOrNumber, isUnknownRecord, isUserDefinedFunction, isUserDefinedSymbolNode, isVector, listDrop, listFromArray, listPrependAll, listSize, listTake, listToArray, reservedSymbolRecord, resolveSourceCodeInfo, specialExpressionTypes, toFixedArity, valueToString } from "@mojir/dvala-types";
//#region src/builtin/core/assertion.ts
const assertionNormalExpression = { assert: {
	evaluate: (params, sourceCodeInfo) => {
		const value = params.get(0);
		const message = params.size === 2 ? params.get(1) : `${value}`;
		assertString(message, sourceCodeInfo);
		if (!value) throw new AssertionError(message, sourceCodeInfo);
		return true;
	},
	arity: {
		min: 1,
		max: 2
	},
	docs: {
		type: "((cond: Boolean) -> asserts {cond | cond == true}) & ((cond: Boolean, message: String) -> asserts {cond | cond == true})",
		category: "assertion",
		description: "If `value` is `false` it throws `AssertionError` with `message`. If no `message` is provided, the message is derived from the value. Under strict Boolean, the value must be `Boolean` — e.g. `assert(x != null, \"x is null\")` instead of `assert(x, \"x is null\")`.",
		returns: { type: "boolean" },
		args: {
			value: { type: "boolean" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["value"] }, { argumentNames: ["value", "message"] }],
		examples: ["do with handler @dvala.error(arg) -> resume(arg) end; assert(false, \"Expected a positive value\") end"],
		seeAlso: ["assertion.assertTruthy", "assertion.assertTrue"],
		hideOperatorForm: true
	}
} };
//#endregion
//#region src/builtin/core/bitwise.ts
function getOperatorArgs$1(a, b) {
	return {
		a: { type: a },
		b: { type: b }
	};
}
const bitwiseNormalExpression = {
	"<<": {
		evaluate: ([num, count], sourceCodeInfo) => {
			assertNumber(num, sourceCodeInfo, { integer: true });
			assertNumber(count, sourceCodeInfo, {
				integer: true,
				nonNegative: true
			});
			return num << count;
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Number, Number) -> Number",
			category: "bitwise",
			returns: { type: "integer" },
			args: { ...getOperatorArgs$1("integer", "integer") },
			variants: [{ argumentNames: ["a", "b"] }],
			description: "Shifts `a` arithmetically left by `b` bit positions.",
			seeAlso: [">>", ">>>"],
			examples: [
				"1 << 10",
				"<<(1, 10)",
				"<<(-4, 2)"
			]
		}
	},
	">>": {
		evaluate: ([num, count], sourceCodeInfo) => {
			assertNumber(num, sourceCodeInfo, { integer: true });
			assertNumber(count, sourceCodeInfo, {
				integer: true,
				nonNegative: true
			});
			return num >> count;
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Number, Number) -> Number",
			category: "bitwise",
			returns: { type: "integer" },
			args: { ...getOperatorArgs$1("integer", "integer") },
			variants: [{ argumentNames: ["a", "b"] }],
			description: "Shifts `a` arithmetically right by `b` bit positions.",
			seeAlso: ["<<", ">>>"],
			examples: [
				"2048 >> 10",
				">>(2048, 10)",
				">>>(-16, 2)",
				">>(4, 10)"
			]
		}
	},
	">>>": {
		evaluate: ([num, count], sourceCodeInfo) => {
			assertNumber(num, sourceCodeInfo, { integer: true });
			assertNumber(count, sourceCodeInfo, {
				integer: true,
				nonNegative: true
			});
			return num >>> count;
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Number, Number) -> Number",
			category: "bitwise",
			returns: { type: "integer" },
			args: { ...getOperatorArgs$1("integer", "integer") },
			variants: [{ argumentNames: ["a", "b"] }],
			description: "Shifts `a` arithmetically right by `b` bit positions without sign extension.",
			seeAlso: ["<<", ">>"],
			examples: [
				"-16 >>> 2",
				">>>(2048, 10)",
				">>>(-16, 2)",
				">>>(4, 10)",
				">>>(-1, 10)"
			]
		}
	},
	"&": {
		evaluate: ([first, ...rest], sourceCodeInfo) => {
			assertNumber(first, sourceCodeInfo, { integer: true });
			return rest.reduce((result, value) => {
				assertNumber(value, sourceCodeInfo, { integer: true });
				return result & value;
			}, first);
		},
		arity: { min: 2 },
		docs: {
			type: "(Number, Number, ...Number[]) -> Number",
			category: "bitwise",
			returns: { type: "integer" },
			args: {
				...getOperatorArgs$1("integer", "integer"),
				c: {
					type: "integer",
					rest: true
				}
			},
			variants: [{ argumentNames: ["a", "b"] }, { argumentNames: [
				"a",
				"b",
				"c"
			] }],
			description: "Returns bitwise `and` of all arguments.",
			seeAlso: [
				"|",
				"xor",
				"bitwise.bitNot",
				"bitwise.bitAndNot"
			],
			examples: [
				"0b0011 & 0b0110",
				"&(0b0011, 0b0110)",
				"&(0b0011, 0b0110, 0b1001)"
			]
		}
	},
	"|": {
		evaluate: ([first, ...rest], sourceCodeInfo) => {
			assertNumber(first, sourceCodeInfo, { integer: true });
			return rest.reduce((result, value) => {
				assertNumber(value, sourceCodeInfo, { integer: true });
				return result | value;
			}, first);
		},
		arity: { min: 2 },
		docs: {
			type: "(Number, Number, ...Number[]) -> Number",
			category: "bitwise",
			returns: { type: "integer" },
			args: {
				...getOperatorArgs$1("integer", "integer"),
				c: {
					type: "integer",
					rest: true
				}
			},
			variants: [{ argumentNames: ["a", "b"] }, { argumentNames: [
				"a",
				"b",
				"c"
			] }],
			description: "Returns bitwise `or` of all arguments.",
			seeAlso: [
				"&",
				"xor",
				"bitwise.bitNot",
				"bitwise.bitAndNot"
			],
			examples: [
				"0b0011 | 0b0110",
				"|(0b0011, 0b0110)",
				"|(0b1000, 0b0100, 0b0010)"
			]
		}
	},
	xor: {
		evaluate: ([first, ...rest], sourceCodeInfo) => {
			assertNumber(first, sourceCodeInfo, { integer: true });
			return rest.reduce((result, value) => {
				assertNumber(value, sourceCodeInfo, { integer: true });
				return result ^ value;
			}, first);
		},
		arity: { min: 2 },
		docs: {
			type: "(Number, Number, ...Number[]) -> Number",
			category: "bitwise",
			returns: { type: "integer" },
			args: {
				...getOperatorArgs$1("integer", "integer"),
				c: {
					type: "integer",
					rest: true
				}
			},
			variants: [{ argumentNames: ["a", "b"] }, { argumentNames: [
				"a",
				"b",
				"c"
			] }],
			description: "Returns bitwise `xor` of all arguments.",
			seeAlso: [
				"&",
				"|",
				"bitwise.bitNot",
				"bitwise.bitAndNot"
			],
			examples: [
				"0b0011 xor 0b0110",
				"xor(0b0011, 0b0110)",
				"xor(0b11110000, 0b00111100, 0b10101010)"
			]
		}
	}
};
//#endregion
//#region src/utils.ts
function collHasKey(coll, key) {
	if (!isColl(coll)) return false;
	if (typeof coll === "string") {
		if (!isNumber(key, { integer: true })) return false;
		return key >= 0 && key < coll.length;
	}
	if (isPersistentVector(coll)) {
		if (!isNumber(key, { integer: true })) return false;
		return key >= 0 && key < coll.size;
	}
	if (isPersistentMap(coll)) return coll.has(String(key));
	return false;
}
function compare(a, b, sourceCodeInfo) {
	if (isAtom(a) && isAtom(b)) return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
	assertStringOrNumber(a, sourceCodeInfo);
	assertStringOrNumber(b, sourceCodeInfo);
	if (typeof a === "string" && typeof b === "string") return a < b ? -1 : a > b ? 1 : 0;
	if (typeof a === "number" && typeof b === "number") return Math.sign(a - b);
	throw new TypeError$1(`Cannot compare values of different types: ${typeof a} and ${typeof b}`, sourceCodeInfo);
}
function deepEqual(a, b, sourceCodeInfo) {
	if (a === b) return true;
	if (typeof a === "number" && typeof b === "number") return approxEqual(a, b);
	if (isAtom(a) && isAtom(b)) return a.name === b.name;
	if (isPersistentVector(a) && isPersistentVector(b)) {
		if (a.size !== b.size) return false;
		let i = 0;
		for (const item of a) {
			if (!deepEqual(item, b.get(i), sourceCodeInfo)) return false;
			i++;
		}
		return true;
	}
	if (Array.isArray(a) && Array.isArray(b)) {
		if (a.length !== b.length) return false;
		for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], b[i], sourceCodeInfo)) return false;
		return true;
	}
	if (isPersistentMap(a) && isPersistentMap(b)) {
		if (a.size !== b.size) return false;
		for (const [key, val] of a) {
			if (!b.has(key)) return false;
			if (!deepEqual(val, b.get(key), sourceCodeInfo)) return false;
		}
		return true;
	}
	if (isRegularExpression(a) && isRegularExpression(b)) return a.s === b.s && a.f === b.f;
	if (isUnknownRecord(a) && isUnknownRecord(b)) {
		const aKeys = Object.keys(a);
		const bKeys = Object.keys(b);
		if (aKeys.length !== bKeys.length) return false;
		for (let i = 0; i < aKeys.length; i += 1) {
			const key = asString(aKeys[i], sourceCodeInfo);
			if (!deepEqual(a[key], b[key], sourceCodeInfo)) return false;
		}
		return true;
	}
	return false;
}
function toNonNegativeInteger(num) {
	return Math.max(0, Math.ceil(num));
}
function toAny(value) {
	return value ?? null;
}
function joinSets(...results) {
	const result = /* @__PURE__ */ new Set();
	for (const symbols of results) symbols.forEach((symbol) => result.add(symbol));
	return result;
}
function addToSet(target, source) {
	source.forEach((symbol) => target.add(symbol));
}
const EPSILON = 1e-10;
function approxEqual(a, b, epsilon = EPSILON) {
	if (a === b) return true;
	const diff = Math.abs(a - b);
	if (a === 0 || b === 0 || diff < epsilon) return diff < epsilon;
	return diff / (Math.abs(a) + Math.abs(b)) < epsilon;
}
function approxZero(value) {
	return Math.abs(value) < EPSILON;
}
function smartTrim(str, minIndent = 0) {
	const lines = str.split("\n");
	while (lines[0]?.match(/^\s*$/)) lines.shift();
	while (lines[lines.length - 1]?.match(/^\s*$/)) lines.pop();
	const indent = lines.reduce((acc, line) => {
		if (line.match(/^\s*$/)) return acc;
		const lineIndent = line.match(/^\s*/)[0].length;
		return Math.min(acc, lineIndent);
	}, Infinity);
	return lines.map((line) => " ".repeat(minIndent) + line.slice(indent)).join("\n").trimEnd();
}
//#endregion
//#region src/builtin/core/collection.ts
function get$1(coll, key) {
	if (isObj(coll)) {
		if (typeof key === "string" && collHasKey(coll, key)) return toAny(coll.get(key));
	} else if (typeof coll === "string") {
		if (isNumber(key, {
			nonNegative: true,
			integer: true
		}) && key >= 0 && key < coll.length) return toAny(coll[key]);
	} else if (isNumber(key, {
		nonNegative: true,
		integer: true
	}) && key >= 0 && key < coll.size) return toAny(coll.get(key));
}
function assoc$1(coll, key, value, sourceCodeInfo) {
	assertColl(coll, sourceCodeInfo);
	assertStringOrNumber(key, sourceCodeInfo);
	if (isPersistentVector(coll) || typeof coll === "string") {
		assertNumber(key, sourceCodeInfo, { integer: true });
		assertNumber(key, sourceCodeInfo, { gte: 0 });
		const seqLength = typeof coll === "string" ? coll.length : coll.size;
		assertNumber(key, sourceCodeInfo, { lte: seqLength });
		if (typeof coll === "string") {
			assertString(value, sourceCodeInfo, { char: true });
			return `${coll.slice(0, key)}${value}${coll.slice(key + 1)}`;
		}
		if (key === seqLength) return coll.append(value);
		return coll.set(key, value);
	}
	assertString(key, sourceCodeInfo);
	return coll.assoc(key, value);
}
const collectionNormalExpression = {
	filter: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("filter is implemented in Dvala");
		},
		arity: toFixedArity(2),
		docs: {
			type: "((A[], (A) -> Boolean) -> A[]) & (({...}, (Unknown) -> Boolean) -> {...})",
			category: "collection",
			returns: { type: "collection" },
			args: {
				a: { type: "collection" },
				b: { type: "function" },
				coll: { type: "collection" },
				fun: { type: "function" }
			},
			variants: [{ argumentNames: ["coll", "fun"] }],
			description: "Creates a new collection with all elements that pass the test implemented by `fun`.",
			seeAlso: [
				"collection.filteri",
				"map",
				"sequence.remove"
			],
			examples: [
				`
filter(
  ["Albert", "Mojir", 160, [1, 2]],
  isString
)`,
				`
filter(
  [5, 10, 15, 20],
  -> $ > 10
)`,
				`
filter(
  { a: 1, b: 2 },
  isOdd
)`
			]
		}
	},
	map: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("map is implemented in Dvala");
		},
		arity: { min: 2 },
		docs: {
			type: "((A[], (A) -> B) -> B[]) & ((A[], A[], (A, A) -> B) -> B[]) & (({...}, (Unknown) -> Unknown) -> {...}) & (({...}, {...}, (Unknown, Unknown) -> Unknown) -> {...})",
			category: "collection",
			returns: { type: "collection" },
			args: {
				a: { type: "collection" },
				b: { type: "function" },
				colls: {
					type: "collection",
					rest: true,
					description: "At least one."
				},
				fun: { type: "function" }
			},
			variants: [{ argumentNames: ["colls", "fun"] }],
			description: "Creates a new collection populated with the results of calling `fun` on every element in `colls`.",
			seeAlso: [
				"collection.mapi",
				"filter",
				"reduce",
				"sequence.mapcat",
				"grid.cellMap",
				"grid.cellMapi"
			],
			examples: [
				"[1, 2, 3] map -",
				"[1, 2, 3] map -> -($)",
				"map([\"Albert\", \"Mojir\", 42], str)",
				"map([1, 2, 3], inc)",
				"map([1, 2, 3], [1, 10, 100], *)",
				"map({ a: 1, b: 2 }, inc)",
				"map({ a: 1, b: 2 }, { a: 10, b: 20 }, +)"
			]
		}
	},
	reduce: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("reduce is implemented in Dvala");
		},
		arity: toFixedArity(3),
		docs: {
			type: "((A[], (B, A) -> B, B) -> B) & (({...}, (B, Unknown) -> B, B) -> B)",
			category: "collection",
			returns: { type: "any" },
			args: {
				fun: { type: "function" },
				coll: { type: "collection" },
				initial: { type: "any" }
			},
			variants: [{ argumentNames: [
				"coll",
				"fun",
				"initial"
			] }],
			description: "Runs `fun` function on each element of the `coll`, passing in the return value from the calculation on the preceding element. The final result of running the reducer across all elements of the `coll` is a single value.",
			seeAlso: [
				"collection.reduceRight",
				"collection.reducei",
				"collection.reductions",
				"map",
				"grid.cellReduce",
				"grid.cellReducei"
			],
			examples: [
				"reduce([1, 2, 3], +, 0)",
				"reduce([], +, 0)",
				"reduce({ a: 1, b: 2 }, +, 0)",
				`
reduce(
  [1, 2, 3, 4, 5, 6, 7, 8, 9],
  (result, value) -> result + (if isEven(value) then value else 0 end),
  0)`
			]
		}
	},
	get: {
		evaluate: (params, sourceCodeInfo) => {
			const [coll, key] = params;
			const defaultValue = toAny(params.get(2));
			assertStringOrNumber(key, sourceCodeInfo);
			if (coll === null) return defaultValue;
			assertColl(coll, sourceCodeInfo);
			const result = get$1(coll, key);
			return result === void 0 ? defaultValue : result;
		},
		arity: {
			min: 2,
			max: 3
		},
		docs: {
			type: "((R, K) -> R[K]) & ((String | Unknown[] | {...} | Null, String | Number) -> Unknown) & ((String | Unknown[] | {...} | Null, String | Number, Unknown) -> Unknown)",
			category: "collection",
			returns: { type: "any" },
			args: {
				a: { type: "collection" },
				b: { type: ["string", "integer"] },
				notFound: {
					type: "any",
					description: "Default value to return if `b` is not found."
				}
			},
			variants: [{ argumentNames: ["a", "b"] }, { argumentNames: [
				"a",
				"b",
				"notFound"
			] }],
			description: "Returns value in `a` mapped at `b`. When both `a` and `b` are concrete and the key is in bounds, the typechecker resolves the return to the precise element / field type; otherwise widens to `Unknown`.",
			seeAlso: [
				"collection.getIn",
				"contains",
				"find",
				"nth"
			],
			examples: [
				"[1, 2, 3] get 1",
				"{ a: 1 } get \"a\"",
				"\"Albert\" get \"3\"",
				`
get(
  [1, 2, 3],
  1, // Optional comma after last argument
)`,
				`
get(
  [],
  1
)`,
				`
get(
  [],
  1,
  "default"
)`,
				`
get(
  { a: 1 },
  "a"
)`,
				`
get(
  { a: 1 },
  "b"
)`,
				`
get(
  { a: 1 },
  "b",
  "default"
)`,
				`
get(
  null,
  "a"
)`,
				`
get(
  null,
  "b",
  "default"
)`
			]
		}
	},
	count: {
		evaluate: ([coll], sourceCodeInfo) => {
			if (coll === null) return 0;
			if (typeof coll === "string") return coll.length;
			assertColl(coll, sourceCodeInfo);
			if (isPersistentVector(coll)) return coll.size;
			return coll.size;
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String | Unknown[] | {...} | Null) -> Integer",
			category: "collection",
			returns: { type: "integer" },
			args: { coll: { type: ["collection", "null"] } },
			variants: [{ argumentNames: ["coll"] }],
			description: "Returns number of elements in `coll`.",
			seeAlso: ["isEmpty"],
			examples: [
				"count([1, 2, 3])",
				"count([])",
				"count({ a: 1 })",
				"count(\"\")",
				"count(\"Albert\")",
				"count(null)"
			]
		}
	},
	contains: {
		evaluate: ([coll, key], sourceCodeInfo) => {
			if (coll === null) return false;
			assertColl(coll, sourceCodeInfo);
			if (isString(coll)) {
				assertString(key, sourceCodeInfo);
				return coll.includes(key);
			}
			if (isSeq(coll)) {
				assertAny(key, sourceCodeInfo);
				for (const elem of coll) if (deepEqual(asAny(elem), key, sourceCodeInfo)) return true;
				return false;
			}
			assertString(key, sourceCodeInfo);
			return coll.has(key);
		},
		arity: toFixedArity(2),
		docs: {
			type: "(String | Unknown[] | {...} | Null, Unknown) -> Boolean",
			category: "collection",
			returns: { type: "boolean" },
			args: {
				a: { type: ["collection", "null"] },
				b: { type: ["string", "integer"] }
			},
			variants: [{ argumentNames: ["a", "b"] }],
			description: "Returns `true` if `a` contains `b`, otherwise returns `false`. For strings, it checks if substring is included.",
			seeAlso: [
				"get",
				"find",
				"indexOf"
			],
			examples: [
				"[1, 2, 3] contains 1",
				"null contains 1",
				"{ a: 1, b: 2 } contains \"a\"",
				`
contains(
  [],
  1
)`,
				`
contains(
  [1],
  1
)`,
				`
contains(
  [1, 2, 3],
  1
)`,
				`
contains(
  {},
  "a"
)`,
				`
contains(
  { a: 1, b: 2 },
  "a"
)`
			]
		}
	},
	assoc: {
		evaluate: ([coll, key, value], sourceCodeInfo) => {
			assertColl(coll, sourceCodeInfo);
			assertStringOrNumber(key, sourceCodeInfo);
			assertAny(value, sourceCodeInfo);
			return assoc$1(coll, key, value, sourceCodeInfo);
		},
		arity: toFixedArity(3),
		docs: {
			type: "(String | Unknown[] | {...}, String | Number, Unknown) -> String | Unknown[] | {...}",
			category: "collection",
			returns: { type: "collection" },
			args: {
				coll: { type: "collection" },
				key: { type: ["string", "number"] },
				value: { type: "any" },
				kvs: {
					type: "any",
					description: "Key-value pairs to associate.",
					rest: true
				}
			},
			variants: [{ argumentNames: [
				"coll",
				"key",
				"value"
			] }, { argumentNames: [
				"coll",
				"key",
				"value",
				"kvs"
			] }],
			description: `
Add or replace the value of element \`key\` to \`value\` in \`coll\`. Repeated for all key-value pairs in \`kvs\`.
If \`coll\` is an 'array', \`key\` must be \`number\` satisfying \`0 <=\` \`key\` \`<= length\`.`,
			seeAlso: [
				"collection.assocIn",
				"dissoc",
				"merge",
				"collection.update"
			],
			examples: [
				`
assoc(
  [1, 2, 3],
  1,
  "Two"
)`,
				`
assoc(
  [1, 2, 3],
  3,
  "Four"
)`,
				`
assoc(
  { a: 1, b: 2 },
  "a",
  "One")`,
				`
assoc(
  { a: 1, b: 2 },
  "c",
  "Three")`,
				`
assoc(
  "Albert",
  6,
  "a")`
			]
		}
	},
	"++": {
		evaluate: (params, sourceCodeInfo) => {
			const first = params.get(0);
			if (!isNumber(first)) assertColl(first, sourceCodeInfo);
			if (isPersistentVector(first)) {
				let result = PersistentVector.empty();
				for (const arr of params) {
					assertArray(arr, sourceCodeInfo);
					for (const item of arr) result = result.append(item);
				}
				return result;
			} else if (isStringOrNumber(first)) {
				let result = "";
				for (const s of params) {
					assertStringOrNumber(s, sourceCodeInfo);
					result = `${result}${s}`;
				}
				return result;
			} else {
				let result = PersistentMap.empty();
				for (const obj of params) {
					assertObj(obj, sourceCodeInfo);
					for (const [k, v] of obj) result = result.assoc(k, v);
				}
				return result;
			}
		},
		arity: { min: 1 },
		docs: {
			type: "((String | A[] | {...}) -> String | A[] | {...}) & ((String, ...String[]) -> String) & ((A[], ...A[][]) -> A[]) & (({...}, ...{...}[]) -> {...})",
			category: "collection",
			returns: { type: "collection" },
			args: {
				a: { type: "collection" },
				b: { type: "collection" },
				colls: {
					type: "collection",
					rest: true
				}
			},
			variants: [{ argumentNames: ["a"] }, { argumentNames: ["a", "colls"] }],
			description: "Concatenates collections into one collection.",
			seeAlso: [
				"sequence.mapcat",
				"str",
				"join",
				"push",
				"sequence.unshift"
			],
			examples: [
				"\"Albert\" ++ \" \" ++ \"Mojir\"",
				"\"Albert\" ++ \"Mojir\"",
				"++(\"Albert\", \"-\", \"Mojir\")",
				"++(\"Albert\")",
				"++(\"A\", \"l\", \"b\", \"e\", \"r\", \"t\")",
				"++([1, 2], [3, 4])",
				"++([], [3, 4])",
				"++([1, 2], [])",
				"++([1, 2], [3, 4], [5, 6])",
				"++([])",
				"++({ a: 1, b: 2 }, { b: 1, c: 2 })",
				"++({}, { a: 1 })"
			]
		}
	}
};
//#endregion
//#region src/builtin/core/array.ts
/**
* Recursively flatten an iterable, treating both PersistentVectors and plain
* arrays as flattenable containers. `Array.prototype.flat` only flattens plain
* arrays, so PVs (the native Dvala array type) must be handled explicitly.
*/
function flattenDeep(items, depth) {
	const result = [];
	for (const item of items) if (depth > 0 && (isPersistentVector(item) || Array.isArray(item))) result.push(...flattenDeep(item, depth - 1));
	else result.push(item);
	return result;
}
const arrayNormalExpression = {
	range: {
		evaluate: (params, sourceCodeInfo) => {
			const [first, second, third] = params;
			let from;
			let to;
			let step;
			assertNumber(first, sourceCodeInfo, { finite: true });
			if (params.size === 1) {
				from = 0;
				to = first;
				step = to >= 0 ? 1 : -1;
			} else if (params.size === 2) {
				assertNumber(second, sourceCodeInfo, { finite: true });
				from = first;
				to = second;
				step = to >= from ? 1 : -1;
			} else {
				assertNumber(second, sourceCodeInfo, { finite: true });
				assertNumber(third, sourceCodeInfo, { finite: true });
				from = first;
				to = second;
				step = third;
				if (to > from) assertNumber(step, sourceCodeInfo, { positive: true });
				else if (to < from) assertNumber(step, sourceCodeInfo, { negative: true });
				else assertNumber(step, sourceCodeInfo, { nonZero: true });
			}
			const result = [];
			for (let i = from; step < 0 ? i > to : i < to; i += step) result.push(i);
			return PersistentVector.from(result);
		},
		arity: {
			min: 1,
			max: 3
		},
		docs: {
			type: "((Number) -> Number[]) & ((Number, Number) -> Number[]) & ((Number, Number, Number) -> Number[])",
			category: "array",
			returns: {
				type: "number",
				array: true
			},
			args: {
				a: { type: "number" },
				b: { type: "number" },
				step: { type: "number" }
			},
			variants: [
				{ argumentNames: ["b"] },
				{ argumentNames: ["a", "b"] },
				{ argumentNames: [
					"a",
					"b",
					"step"
				] }
			],
			description: `\`range\` creates an array with a range of numbers from \`a\` to \`b\` (exclusive), by \`step\`.

\`a\` defaults to 0.
\`step\` defaults to 1.`,
			seeAlso: ["repeat", "vector.linspace"],
			examples: [
				"range(4)",
				"range(1, 4)",
				"1 range 10",
				"range(0.4, 4.9)",
				`
range(
  0.25, // start value
  1,    // end value (exclusive)
  0.25, // step value
)`
			]
		}
	},
	repeat: {
		evaluate: ([value, count], sourceCodeInfo) => {
			assertNumber(count, sourceCodeInfo, {
				integer: true,
				nonNegative: true
			});
			const t = PersistentVector.empty().asTransient();
			for (let i = 0; i < count; i += 1) t.append(value);
			return t.persistent();
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Unknown, Number) -> Unknown[]",
			category: "array",
			returns: {
				type: "any",
				array: true
			},
			args: {
				a: { type: "any" },
				b: { type: "integer" }
			},
			variants: [{ argumentNames: ["a", "b"] }],
			description: "Returns an array with `a` repeated `b` times.",
			seeAlso: ["range", "string.stringRepeat"],
			examples: [
				"repeat(10, 3)",
				"repeat(10, 0)",
				"\"Albert\" repeat 5"
			]
		}
	},
	flatten: {
		evaluate: ([seq, depth], sourceCodeInfo) => {
			assertArray(seq, sourceCodeInfo);
			const actualDepth = depth === void 0 ? Number.MAX_SAFE_INTEGER : asNumber(depth, sourceCodeInfo, {
				integer: true,
				nonNegative: true
			});
			return PersistentVector.from(flattenDeep(seq, actualDepth));
		},
		arity: {
			min: 1,
			max: 2
		},
		docs: {
			type: "((Unknown[]) -> Unknown[]) & ((Unknown[], Number) -> Unknown[])",
			category: "array",
			returns: {
				type: "any",
				array: true
			},
			args: {
				x: {
					type: ["array", "any"],
					description: "If `x` is not an array, `[ ]` is returned."
				},
				depth: {
					type: "integer",
					description: "The depth level specifying how deep a nested array structure should be flattened. Defaults to full depth."
				}
			},
			variants: [{ argumentNames: ["x"] }, { argumentNames: ["x", "depth"] }],
			description: "Takes a nested array `x` and flattens it.",
			seeAlso: ["sequence.mapcat"],
			examples: [
				"flatten([1, 2, [3, 4], 5])",
				"flatten([1, [2, [3, [4]]]], 1)",
				"flatten([1, [2, [3, [4]]]], 2)",
				`
let foo = "bar";
flatten([
  1,
  " 2 A ",
  [foo, [4, ["ABC"]]],
  6,
])`
			],
			hideOperatorForm: true
		}
	}
};
//#endregion
//#region src/builtin/core/sequence.ts
const sequenceNormalExpression = {
	nth: {
		evaluate: (params, sourceCodeInfo) => {
			const [seq, i] = params;
			const defaultValue = toAny(params.get(2));
			assertNumber(i, sourceCodeInfo, { integer: true });
			if (seq === null) return defaultValue;
			assertSeq(seq, sourceCodeInfo);
			if (typeof seq === "string") {
				if (i >= 0 && i < seq.length) return toAny(seq[i]);
				return defaultValue;
			}
			if (i >= 0 && i < seq.size) return toAny(seq.get(i));
			else return defaultValue;
		},
		arity: {
			min: 2,
			max: 3
		},
		docs: {
			type: "((String | Unknown[] | Null, Integer) -> Unknown) & ((String | Unknown[] | Null, Integer, Unknown) -> Unknown)",
			category: "sequence",
			returns: { type: "any" },
			args: {
				a: { type: "sequence" },
				b: { type: "integer" },
				seq: { type: ["sequence", "null"] },
				n: { type: "integer" },
				notFound: { type: "any" }
			},
			variants: [{ argumentNames: ["seq", "n"] }, { argumentNames: [
				"seq",
				"n",
				"notFound"
			] }],
			description: "Accesses element `n` of `seq`. Accessing out-of-bounds indices returns `not-found`, if present, else `null`.",
			seeAlso: [
				"first",
				"second",
				"last",
				"get",
				"slice"
			],
			examples: [
				"[1, 2, 3] nth 1",
				"\"A string\" nth 3",
				"nth([1, 2, 3], 1)",
				"nth([1, 2, 3], 3)",
				"nth([1, 2, 3], -1)",
				"nth([1, 2, 3], 3, 99)",
				"nth(\"A string\", 1)",
				"nth(\"A string\", 3)",
				"nth(\"A string\", -3)",
				"nth(\"A string\", 30, \"X\")",
				"nth(null, 1)",
				"nth(null, 1, \"Default value\")"
			]
		}
	},
	first: {
		evaluate: ([array], sourceCodeInfo) => {
			if (array === null) return null;
			assertSeq(array, sourceCodeInfo);
			if (typeof array === "string") return toAny(array[0]);
			return toAny(array.get(0));
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String | Unknown[] | Null) -> Unknown",
			category: "sequence",
			returns: { type: "any" },
			args: { seq: { type: ["sequence", "null"] } },
			variants: [{ argumentNames: ["seq"] }],
			description: "Returns the first element of `seq`. If `seq` is empty or `null`, `null` is returned.",
			seeAlso: [
				"second",
				"last",
				"nth",
				"rest",
				"next"
			],
			examples: [
				"first([\"Albert\", \"Mojir\", 160, [1, 2]])",
				"first([])",
				"first(null)"
			]
		}
	},
	last: {
		evaluate: ([array], sourceCodeInfo) => {
			if (array === null) return null;
			assertSeq(array, sourceCodeInfo);
			if (typeof array === "string") return toAny(array.length > 0 ? array[array.length - 1] : void 0);
			return toAny(array.get(array.size - 1));
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String | Unknown[] | Null) -> Unknown",
			category: "sequence",
			returns: { type: "any" },
			args: { seq: { type: ["sequence", "null"] } },
			variants: [{ argumentNames: ["seq"] }],
			description: "Returns the last element of `seq`. If `seq` is empty, `null` is returned.",
			seeAlso: [
				"first",
				"second",
				"nth",
				"pop"
			],
			examples: [
				"last([\"Albert\", \"Mojir\", 160, [1, 2]])",
				"last([1, 2])",
				"last([1])",
				"last([])",
				"last(null)"
			]
		}
	},
	pop: {
		evaluate: ([seq], sourceCodeInfo) => {
			assertSeq(seq, sourceCodeInfo);
			if (typeof seq === "string") return seq.substring(0, seq.length - 1);
			return PersistentVector.from([...seq].slice(0, seq.size - 1));
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String | Unknown[]) -> String | Unknown[]",
			category: "sequence",
			returns: {
				type: ["sequence", "null"],
				rest: true
			},
			args: { seq: { type: "sequence" } },
			variants: [{ argumentNames: ["seq"] }],
			description: "Returns a copy of `seq` with last element removed. If `seq` is empty `null` is returned.",
			seeAlso: ["push", "last"],
			examples: ["pop([1, 2, 3])", "pop([])"]
		}
	},
	indexOf: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("indexOf is implemented in Dvala");
		},
		arity: toFixedArity(2),
		docs: {
			type: "(String | Unknown[] | Null, Unknown) -> Number | Null",
			category: "sequence",
			returns: { type: ["number", "null"] },
			args: {
				a: { type: "sequence" },
				b: { type: "any" },
				seq: { type: ["sequence", "null"] },
				x: { type: "any" }
			},
			variants: [{ argumentNames: ["seq", "x"] }],
			description: "Returns the index of `x` in `seq`. If element is not present in `seq` `null` is returned.",
			seeAlso: [
				"sequence.lastIndexOf",
				"sequence.position",
				"contains"
			],
			examples: [
				"[[1], [2], [1], [2]] indexOf [1]",
				"indexOf([\"Albert\", \"Mojir\", 160, [1, 2]], \"Mojir\")",
				"indexOf([5, 10, 15, 20], 15)",
				"indexOf([5, 10, 15, 20], 1)",
				"indexOf(null, 1)"
			]
		}
	},
	push: {
		evaluate: ([seq, ...values], sourceCodeInfo) => {
			assertSeq(seq, sourceCodeInfo);
			if (typeof seq === "string") {
				assertCharArray(PersistentVector.from(values), sourceCodeInfo);
				return [seq, ...values].join("");
			} else {
				let result = seq;
				for (const v of values) result = result.append(v);
				return result;
			}
		},
		arity: { min: 2 },
		docs: {
			type: "(String | Unknown[], Unknown, ...Unknown[]) -> String | Unknown[]",
			category: "sequence",
			returns: { type: "sequence" },
			args: {
				a: { type: "sequence" },
				b: { type: "any" },
				seq: { type: "sequence" },
				values: {
					type: "any",
					rest: true,
					description: "At least one."
				}
			},
			variants: [{ argumentNames: ["seq", "values"] }],
			description: "Returns copy of `seq` with `values` added to the end of it.",
			seeAlso: [
				"sequence.unshift",
				"pop",
				"++"
			],
			examples: [
				"[1, 2, 3] push 4",
				"\"Albert\" push \"!\"",
				"push([1, 2, 3], 4)",
				"push([1, 2, 3], 4, 5, 6)",
				`
let l = [1, 2, 3];
push(l, 4);
l`
			]
		}
	},
	rest: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("rest is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String | Unknown[]) -> String | Unknown[]",
			category: "sequence",
			returns: { type: ["sequence", "null"] },
			args: { seq: { type: "sequence" } },
			variants: [{ argumentNames: ["seq"] }],
			description: `If \`seq\` is an array, returns a new array with all but the first element from \`seq\`.
If \`seq\` has less than two elements, an empty array is returned.
For string \`seq\` returns all but the first characters in \`seq\`.`,
			seeAlso: ["next", "first"],
			examples: [
				"rest([\"Albert\", \"Mojir\", 160, [1, 2]])",
				"rest([\"Albert\"])",
				"rest([])",
				"rest(\"Albert\")",
				"rest(\"A\",)",
				"rest(\"\")"
			]
		}
	},
	next: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("next is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String | Unknown[]) -> String | Unknown[] | Null",
			category: "sequence",
			returns: { type: ["sequence", "null"] },
			args: { seq: { type: "sequence" } },
			variants: [{ argumentNames: ["seq"] }],
			description: "If `seq` is an array, returns a new array with all but the first element from `seq`. If `seq` has less than two elements, `null` is returned. For string `seq` returns all but the first characters in `seq`. If length of string `seq` is less than two, `null` is returned.",
			seeAlso: ["rest", "first"],
			examples: [
				"next([\"Albert\", \"Mojir\", 160, [1, 2]])",
				"next([\"Albert\"])",
				"next([])",
				"next(\"Albert\")",
				"next(\"A\",)",
				"next(\"\")"
			]
		}
	},
	reverse: {
		evaluate: ([seq], sourceCodeInfo) => {
			if (seq === null) return null;
			assertSeq(seq, sourceCodeInfo);
			if (isPersistentVector(seq)) return PersistentVector.from([...seq].reverse());
			return seq.split("").reverse().join("");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String | Unknown[] | Null) -> String | Unknown[] | Null",
			category: "sequence",
			returns: { type: ["sequence", "null"] },
			args: { seq: { type: ["sequence", "null"] } },
			variants: [{ argumentNames: ["seq"] }],
			description: "If `seq` is an array, creates a new array with the elements from `seq` in reversed order. If `seq` is a string, returns new reversed string.",
			seeAlso: ["sort"],
			examples: [
				"reverse([\"Albert\", \"Mojir\", 160, [1, 2]])",
				"reverse([])",
				"reverse(\"Albert\")",
				"reverse(null)"
			]
		}
	},
	second: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("second is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String | Unknown[] | Null) -> Unknown",
			category: "sequence",
			returns: { type: "any" },
			args: { seq: { type: ["sequence", "null"] } },
			variants: [{ argumentNames: ["seq"] }],
			description: "Returns the second element of `seq`. If `seq` has less than two elements or is `null`, `null` is returned.",
			seeAlso: [
				"first",
				"last",
				"nth"
			],
			examples: [
				"second([\"Albert\", \"Mojir\", 160, [1, 2]])",
				"second([1])",
				"second([])",
				"second(null)"
			]
		}
	},
	slice: {
		evaluate: (params, sourceCodeInfo) => {
			const [seq, from, to] = params;
			assertSeq(seq, sourceCodeInfo);
			assertNumber(from, sourceCodeInfo, { integer: true });
			if (params.size === 2) {
				if (isPersistentVector(seq)) return PersistentVector.from([...seq].slice(from));
				return seq.slice(from);
			}
			assertNumber(to, sourceCodeInfo, { integer: true });
			if (isPersistentVector(seq)) return PersistentVector.from([...seq].slice(from, to));
			return seq.slice(from, to);
		},
		arity: {
			min: 2,
			max: 3
		},
		docs: {
			type: "((String | Unknown[], Number) -> String | Unknown[]) & ((String | Unknown[], Number, Number) -> String | Unknown[])",
			category: "sequence",
			returns: { type: "sequence" },
			args: {
				a: { type: "sequence" },
				b: { type: "integer" },
				seq: { type: "sequence" },
				start: {
					type: "integer",
					description: "Defaults to `0`."
				},
				stop: {
					type: "integer",
					description: "Defaults to length of sequence + 1."
				}
			},
			variants: [{ argumentNames: ["seq", "start"] }, { argumentNames: [
				"seq",
				"start",
				"stop"
			] }],
			description: "Returns a copy of a portion of `seq` from index `start` (inclusive) to `stop` (exclusive).",
			seeAlso: [
				"take",
				"drop",
				"sequence.splice",
				"nth"
			],
			examples: [
				"[1, 2, 3, 4, 5] slice 2",
				"slice([1, 2, 3, 4, 5], 2, 4)",
				"slice([1, 2, 3, 4, 5], 2)"
			]
		}
	},
	some: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("some is implemented in Dvala");
		},
		arity: toFixedArity(2),
		docs: {
			type: "(A[], (A) -> Boolean) -> A",
			category: "sequence",
			returns: { type: "any" },
			args: {
				a: { type: "sequence" },
				b: { type: "function" },
				seq: { type: ["sequence", "null"] },
				fun: { type: "function" }
			},
			variants: [{ argumentNames: ["seq", "fun"] }],
			description: "Returns the first element that passes the test implemented by `fun`. I no element was found, `null` is returned.",
			seeAlso: [
				"sequence.position",
				"collection.isAny",
				"find"
			],
			examples: [
				`
some(
  ["Albert", "Mojir", 160, [1, 2]],
  isString
)`,
				`
some(
  [5, 10, 15, 20],
  -> $ > 10
)`,
				`
some(
  [1, 2, 3, 4],
  -> $ > 10
)`,
				`
some(
  [],
  -> $ > 10
)`,
				{
					code: `
some(
  null,
  -> $ > 10
)`,
					noCheck: true
				}
			]
		}
	},
	sort: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("sort is implemented in Dvala");
		},
		arity: {
			min: 1,
			max: 2
		},
		docs: {
			type: "((A[]) -> A[]) & ((A[], (A, A) -> Number) -> A[])",
			category: "sequence",
			returns: {
				type: "any",
				rest: true
			},
			args: {
				a: { type: "sequence" },
				b: { type: "function" },
				seq: { type: "sequence" },
				fun: { type: "function" }
			},
			variants: [{ argumentNames: ["seq"] }, { argumentNames: ["seq", "fun"] }],
			description: "Returns a new sequence with the elements from `seq` sorted according to `fun`. If no `fun` is supplied, builtin `compare` will be used.",
			seeAlso: [
				"sequence.sortBy",
				"compare",
				"reverse",
				"sortIndices"
			],
			examples: [
				"[3, 1, 2] sort (a, b) -> b - a",
				"sort([3, 1, 2])",
				`
sort(
  [3, 1, 2],
  (a, b) -> if a < b then -1 else if a > b then 1 else -1 end
)`,
				`
sort(
  [3, 1, 2],
  (a, b) -> if a > b then -1 else if a < b then 1 else -1 end
)`
			]
		}
	},
	take: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("take is implemented in Dvala");
		},
		arity: toFixedArity(2),
		docs: {
			type: "(String | Unknown[], Number) -> String | Unknown[]",
			category: "sequence",
			returns: { type: "sequence" },
			args: {
				a: { type: "sequence" },
				b: { type: "integer" },
				n: { type: "integer" },
				seq: { type: "sequence" }
			},
			variants: [{ argumentNames: ["seq", "n"] }],
			description: "Constructs a new array/string with the `n` first elements from `seq`.",
			seeAlso: [
				"takeLast",
				"takeWhile",
				"drop",
				"slice",
				"sequence.splitAt"
			],
			examples: [
				"take([1, 2, 3, 4, 5], 3)",
				"[1, 2, 3, 4, 5] take 3",
				"take([1, 2, 3, 4, 5], 0)",
				"take(\"Albert\", 2)",
				"take(\"Albert\", 50)"
			]
		}
	},
	takeLast: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("takeLast is implemented in Dvala");
		},
		arity: toFixedArity(2),
		docs: {
			type: "(String | Unknown[], Number) -> String | Unknown[]",
			category: "sequence",
			returns: { type: "sequence" },
			args: {
				a: { type: "sequence" },
				b: { type: "integer" },
				n: { type: "integer" },
				seq: { type: "sequence" }
			},
			variants: [{ argumentNames: ["seq", "n"] }],
			description: "Constructs a new array with the `n` last elements from `seq`.",
			seeAlso: ["take", "dropLast"],
			examples: [
				"takeLast([1, 2, 3, 4, 5], 3)",
				"[1, 2, 3, 4, 5] takeLast 3",
				"takeLast([1, 2, 3, 4, 5], 0)"
			]
		}
	},
	drop: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("drop is implemented in Dvala");
		},
		arity: toFixedArity(2),
		docs: {
			type: "(String | Unknown[], Number) -> String | Unknown[]",
			category: "sequence",
			returns: { type: "sequence" },
			args: {
				a: { type: "sequence" },
				b: { type: "integer" },
				seq: { type: "sequence" },
				n: { type: "integer" }
			},
			variants: [{ argumentNames: ["seq", "n"] }],
			description: "Constructs a new array/string with the `n` first elements dropped from `seq`.",
			seeAlso: [
				"dropLast",
				"dropWhile",
				"take",
				"slice",
				"sequence.splitAt"
			],
			examples: [
				"drop([1, 2, 3, 4, 5], 3)",
				"[1, 2, 3, 4, 5] drop 0",
				"drop(\"Albert\", 2)",
				"drop(\"Albert\", 50)"
			]
		}
	},
	dropLast: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("dropLast is implemented in Dvala");
		},
		arity: toFixedArity(2),
		docs: {
			type: "(String | Unknown[], Number) -> String | Unknown[]",
			category: "sequence",
			returns: { type: "sequence" },
			args: {
				a: { type: "sequence" },
				b: { type: "integer" },
				seq: { type: "sequence" },
				n: { type: "integer" }
			},
			variants: [{ argumentNames: ["seq", "n"] }],
			description: "Constructs a new array with the `n` last elements dropped from `seq`.",
			seeAlso: ["drop", "takeLast"],
			examples: [
				"dropLast([1, 2, 3, 4, 5], 3)",
				"[1, 2, 3, 4, 5] dropLast 3",
				"dropLast([1, 2, 3, 4, 5], 0)"
			]
		}
	},
	takeWhile: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("takeWhile is implemented in Dvala");
		},
		arity: toFixedArity(2),
		docs: {
			type: "(A[], (A) -> Boolean) -> A[]",
			category: "sequence",
			returns: { type: "sequence" },
			args: {
				a: { type: "sequence" },
				b: { type: "function" },
				seq: { type: "sequence" },
				fun: { type: "function" }
			},
			variants: [{ argumentNames: ["seq", "fun"] }],
			description: "Returns the members of `seq` in order, stopping before the first one for which `predicate` returns a falsy value.",
			seeAlso: [
				"take",
				"dropWhile",
				"sequence.splitWith"
			],
			examples: [`takeWhile(
  [1, 2, 3, 2, 1],
  -> $ < 3
)`, `takeWhile(
  [1, 2, 3, 2, 1],
  -> $ > 3
)`]
		}
	},
	dropWhile: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("dropWhile is implemented in Dvala");
		},
		arity: toFixedArity(2),
		docs: {
			type: "(A[], (A) -> Boolean) -> A[]",
			category: "sequence",
			returns: { type: "sequence" },
			args: {
				a: { type: "sequence" },
				b: { type: "function" },
				seq: { type: "sequence" },
				fun: { type: "function" }
			},
			variants: [{ argumentNames: ["seq", "fun"] }],
			description: "Returns the members of `seq` in order, skipping the fist elements for witch the `predicate` returns a truethy value.",
			seeAlso: [
				"drop",
				"takeWhile",
				"sequence.splitWith"
			],
			examples: [`dropWhile(
  [1, 2, 3, 2, 1],
  -> $ < 3
)`, `dropWhile(
  [1, 2, 3, 2, 1],
  -> $ > 3
)`]
		}
	},
	minIndex: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("minIndex is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number[]) -> Integer",
			category: "sequence",
			returns: { type: "integer" },
			args: { vector: { type: "vector" } },
			variants: [{ argumentNames: ["vector"] }],
			description: "Returns the index of the smallest element in `vector`. If several elements share the minimum, the earliest index is returned. Throws for an empty vector.",
			seeAlso: [
				"maxIndex",
				"min",
				"sortIndices"
			],
			examples: [
				"minIndex([3, 1, 2])",
				"minIndex([3, 1, 2, 1])",
				"minIndex([5])"
			]
		}
	},
	maxIndex: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("maxIndex is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number[]) -> Integer",
			category: "sequence",
			returns: { type: "integer" },
			args: { vector: { type: "vector" } },
			variants: [{ argumentNames: ["vector"] }],
			description: "Returns the index of the largest element in `vector`. If several elements share the maximum, the earliest index is returned. Throws for an empty vector.",
			seeAlso: [
				"minIndex",
				"max",
				"sortIndices"
			],
			examples: [
				"maxIndex([1, 3, 2])",
				"maxIndex([1, 3, 2, 3])",
				"maxIndex([5])"
			]
		}
	},
	sortIndices: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("sortIndices is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number[]) -> Integer[]",
			category: "sequence",
			returns: { type: "array" },
			args: { vector: { type: "vector" } },
			variants: [{ argumentNames: ["vector"] }],
			description: "Returns the indices that would sort `vector` in ascending order. The sort is stable: indices of equal elements keep their original order. Returns `[]` for an empty vector.",
			seeAlso: [
				"sort",
				"minIndex",
				"maxIndex",
				"countValues"
			],
			examples: [
				"sortIndices([30, 10, 20])",
				"sortIndices([30, 10, 20, 10])",
				"sortIndices([])"
			]
		}
	},
	countValues: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("countValues is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number[]) -> [Number, Integer][]",
			category: "sequence",
			returns: { type: "array" },
			args: { vector: { type: "vector" } },
			variants: [{ argumentNames: ["vector"] }],
			description: "Returns `[value, count]` pairs for the distinct elements in `vector`, sorted by count descending and, for ties, by value ascending. Returns `[]` for an empty vector.",
			seeAlso: [
				"sequence.frequencies",
				"sortIndices",
				"vector.bincount"
			],
			examples: [
				"countValues([3, 1, 3, 2, 1, 1])",
				"countValues([1, 2, 3])",
				"countValues([])"
			]
		}
	}
};
//#endregion
//#region src/builtin/core/math.ts
function getNumberOperands(params, sourceCodeInfo) {
	const operands = Array.from(params);
	operands.forEach((param) => {
		if (!isNumber(param)) throw new RuntimeError(`Invalid parameter type: ${typeof param}`, sourceCodeInfo);
	});
	return operands;
}
function checkedFn(fn, a, b, sourceCodeInfo) {
	const result = b === void 0 ? fn(a) : fn(a, b);
	if (!Number.isFinite(result)) throw new ArithmeticError("Number is not finite", sourceCodeInfo);
	return result;
}
function unaryMathOp$1(fn) {
	return (params, sourceCodeInfo) => {
		return checkedFn(fn, getNumberOperands(params, sourceCodeInfo)[0], void 0, sourceCodeInfo);
	};
}
function binaryMathOp(fn) {
	return (params, sourceCodeInfo) => {
		const operands = getNumberOperands(params, sourceCodeInfo);
		return checkedFn(fn, operands[0], operands[1], sourceCodeInfo);
	};
}
function reduceMathOp(identity, fn) {
	return (params, sourceCodeInfo) => {
		if (params.size === 0) return identity;
		return getNumberOperands(params, sourceCodeInfo).reduce((a, b) => fn(a, b), identity);
	};
}
const mathNormalExpression = {
	inc: {
		evaluate: unaryMathOp$1((val) => val + 1),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number)",
			category: "math",
			returns: { type: "number" },
			args: { x: { type: "number" } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `inc` function increments a number by 1.",
			seeAlso: ["dec", "+"],
			examples: [
				"inc(0)",
				"inc(1)",
				"inc(100.1)",
				"inc(-2.5)"
			]
		}
	},
	dec: {
		evaluate: unaryMathOp$1((val) => val - 1),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number)",
			category: "math",
			returns: { type: "number" },
			args: { x: { type: "number" } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `dec` function decrements a number by 1.",
			seeAlso: ["inc", "-"],
			examples: [
				"dec(0)",
				"dec(1)",
				"dec(100.1)",
				"dec(-2.5)"
			]
		}
	},
	"+": {
		evaluate: reduceMathOp(0, (a, b) => a + b),
		arity: {},
		docs: {
			type: "(() -> Number) & ((Number, ...Number[]) -> Number)",
			category: "math",
			returns: { type: "number" },
			args: {
				a: { type: "number" },
				b: { type: "number" },
				xs: {
					type: "number",
					rest: true
				}
			},
			variants: [{ argumentNames: ["xs"] }],
			description: "The `+` function adds numbers. With no arguments it returns `0`.",
			seeAlso: [
				"-",
				"*",
				"/",
				"inc"
			],
			examples: [
				"1 + 2",
				"1 + 20 + 30",
				"+(1, 2, 3, 4)",
				"+()",
				"+(1)",
				"-2 + 2"
			]
		}
	},
	"*": {
		evaluate: reduceMathOp(1, (a, b) => a * b),
		arity: {},
		docs: {
			type: "(() -> Number) & ((Number, ...Number[]) -> Number)",
			category: "math",
			returns: { type: "number" },
			args: {
				a: { type: "number" },
				b: { type: "number" },
				xs: {
					type: "number",
					rest: true
				}
			},
			variants: [{ argumentNames: ["xs"] }],
			description: "The `*` function multiplies numbers. With no arguments it returns `1`.",
			seeAlso: [
				"/",
				"+",
				"-",
				"^"
			],
			examples: [
				"6 * 7",
				"-1 * 4",
				"*(4, 7)",
				"*(1, 2, 3, 4, 5)",
				"*()",
				"*(8)",
				"2 * 2"
			]
		}
	},
	"/": {
		evaluate: (params, sourceCodeInfo) => {
			if (params.size === 0) return 1;
			const [first, ...rest] = getNumberOperands(params, sourceCodeInfo);
			if (rest.length === 0) return checkedFn((val) => 1 / val, first, void 0, sourceCodeInfo);
			return rest.reduce((result, param) => checkedFn((a, b) => a / b, result, param, sourceCodeInfo), first);
		},
		arity: {},
		docs: {
			type: "(() -> Number) & ((Number, ...Number[]) -> Number)",
			category: "math",
			returns: { type: "number" },
			args: {
				a: { type: "number" },
				b: { type: "number" },
				xs: {
					type: "number",
					rest: true
				}
			},
			variants: [{ argumentNames: ["xs"] }],
			description: "The `/` function divides numbers. With no arguments it returns `1`, and with one argument it returns the reciprocal.",
			seeAlso: [
				"*",
				"+",
				"-",
				"quot",
				"mod",
				"%"
			],
			examples: [
				"12 / 100",
				"-1 / 4",
				"/(7, 4)",
				"/(1, 2, 4, 8)",
				"/()",
				"/(8)",
				"2 / 5"
			]
		}
	},
	"-": {
		evaluate: (params, sourceCodeInfo) => {
			if (params.size === 0) return 0;
			const [first, ...rest] = getNumberOperands(params, sourceCodeInfo);
			if (rest.length === 0) return checkedFn((val) => -val, first, void 0, sourceCodeInfo);
			return rest.reduce((result, param) => checkedFn((a, b) => a - b, result, param, sourceCodeInfo), first);
		},
		arity: {},
		docs: {
			type: "(() -> Number) & ((Number, ...Number[]) -> Number)",
			category: "math",
			returns: { type: "number" },
			args: {
				a: { type: "number" },
				b: { type: "number" },
				xs: {
					type: "number",
					rest: true
				}
			},
			variants: [{ argumentNames: ["xs"] }],
			description: "Computes the difference between the first number and the rest. With one argument it negates the number.",
			seeAlso: [
				"+",
				"*",
				"/",
				"dec",
				"abs"
			],
			examples: [
				"50 - 8",
				"1 - 1 - 1",
				"-()",
				"-(4, 2)",
				"-(4, 3, 2, 1,)",
				"let a = 0; let b = 2; a - b"
			]
		}
	},
	quot: {
		evaluate: binaryMathOp((a, b) => Math.trunc(a / b)),
		arity: toFixedArity(2),
		docs: {
			type: "((Number, Number) -> Integer)",
			category: "math",
			returns: { type: "integer" },
			args: {
				a: { type: "number" },
				b: { type: "number" }
			},
			variants: [{ argumentNames: ["a", "b"] }],
			description: "The `quot` function performs integer division truncated toward zero on two numbers.",
			seeAlso: [
				"mod",
				"%",
				"/",
				"trunc"
			],
			examples: [
				"quot(5, 3)",
				"quot(5.2, 3.1)",
				"quot(-5, 3)",
				"5 quot -3",
				"-5 quot -3",
				"quot(0, 5)",
				"quot(13.75, 3.25)"
			]
		}
	},
	mod: {
		evaluate: binaryMathOp((a, b) => a - b * Math.floor(a / b)),
		arity: toFixedArity(2),
		docs: {
			type: "((Number, Number) -> Number)",
			category: "math",
			returns: { type: "number" },
			args: {
				a: { type: "number" },
				b: { type: "number" }
			},
			variants: [{ argumentNames: ["a", "b"] }],
			description: "The `mod` function computes the modulo of division with the same sign as the divisor.",
			seeAlso: [
				"%",
				"quot",
				"/"
			],
			examples: [
				"mod(5, 3)",
				"mod(5.2, 3.1)",
				"mod(-5, 3)",
				"5 mod -3",
				"-5 mod -3",
				"mod(13.75, 3.25)"
			]
		}
	},
	"%": {
		evaluate: binaryMathOp((a, b) => a % b),
		arity: toFixedArity(2),
		docs: {
			type: "((Number, Number) -> Number)",
			category: "math",
			returns: { type: "number" },
			args: {
				a: { type: "number" },
				b: { type: "number" }
			},
			variants: [{ argumentNames: ["a", "b"] }],
			description: "The `%` function computes the remainder of division with the same sign as the dividend.",
			seeAlso: [
				"mod",
				"quot",
				"/"
			],
			examples: [
				"5 % 3",
				"5.2 % 3.1",
				"-5 % 3",
				"%(5, -3)",
				"%(-5, -3)",
				"%(13.75, 3.25)"
			]
		}
	},
	sqrt: {
		evaluate: unaryMathOp$1((val) => Math.sqrt(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number)",
			category: "math",
			returns: { type: "number" },
			args: { x: { type: "number" } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `sqrt` function calculates the square root of a number.",
			seeAlso: ["cbrt", "^"],
			examples: [
				"sqrt(0)",
				"sqrt(9)",
				"sqrt(2)",
				"sqrt(1)"
			]
		}
	},
	cbrt: {
		evaluate: unaryMathOp$1((val) => Math.cbrt(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number)",
			category: "math",
			returns: { type: "number" },
			args: { x: { type: "number" } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `cbrt` function calculates the cube root of a number.",
			seeAlso: ["sqrt", "^"],
			examples: [
				"cbrt(0)",
				"cbrt(27)",
				"cbrt(2)",
				"cbrt(1)",
				"cbrt(-8)"
			]
		}
	},
	"^": {
		evaluate: binaryMathOp((a, b) => a ** b),
		arity: toFixedArity(2),
		docs: {
			type: "((Number, Number) -> Number)",
			category: "math",
			returns: { type: "number" },
			args: {
				a: { type: "number" },
				b: { type: "number" }
			},
			variants: [{ argumentNames: ["a", "b"] }],
			description: "The `^` function computes exponentiation, raising the first number to the power of the second.",
			seeAlso: [
				"sqrt",
				"cbrt",
				"*",
				"math.ln"
			],
			examples: [
				"2 ^ 3",
				"2 ^ 0",
				"2 ^ -3",
				"^(-2, 3)",
				"^(-2, -3)",
				"^(16, 0.5)"
			]
		}
	},
	round: {
		evaluate: ([value, decimals], sourceCodeInfo) => {
			const [operand] = getNumberOperands([value], sourceCodeInfo);
			if (decimals === void 0 || decimals === 0) return Math.round(operand);
			else {
				assertNumber(decimals, sourceCodeInfo, {
					integer: true,
					positive: true
				});
				const factor = 10 ** decimals;
				return Math.round(operand * factor) / factor;
			}
		},
		arity: {
			min: 1,
			max: 2
		},
		docs: {
			type: "((Number) -> Integer) & ((Number, Integer) -> Number)",
			category: "math",
			returns: { type: "number" },
			args: {
				a: { type: "number" },
				b: { type: "integer" }
			},
			variants: [{ argumentNames: ["a"] }, { argumentNames: ["a", "b"] }],
			description: "The `round` function rounds a number to the nearest integer or to a specified number of decimal places.",
			seeAlso: [
				"floor",
				"ceil",
				"trunc"
			],
			examples: [
				"round(2)",
				"round(2.49)",
				"round(2.5)",
				"round(-2.49)",
				"round(-2.5)",
				"round(-2.501)",
				"round(1.23456789, 4)",
				"1.123456789 round 2",
				"round(-0.125, 1)"
			]
		}
	},
	trunc: {
		evaluate: unaryMathOp$1((val) => Math.trunc(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Integer)",
			category: "math",
			returns: { type: "integer" },
			args: { x: { type: "number" } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `trunc` function truncates a number toward zero, removing its decimal portion without rounding.",
			seeAlso: [
				"round",
				"floor",
				"ceil",
				"quot"
			],
			examples: [
				"trunc(2)",
				"trunc(2.49)",
				"trunc(2.5)",
				"trunc(-2.49)",
				"trunc(-2.5)",
				"trunc(-2.501)",
				"trunc(0.999)"
			]
		}
	},
	floor: {
		evaluate: unaryMathOp$1((val) => Math.floor(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Integer)",
			category: "math",
			returns: { type: "integer" },
			args: { x: { type: "number" } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `floor` function returns the largest integer less than or equal to a number.",
			seeAlso: [
				"ceil",
				"round",
				"trunc"
			],
			examples: [
				"floor(2)",
				"floor(2.49)",
				"floor(2.5)",
				"floor(-2.49)",
				"floor(-2.5)",
				"floor(-2.501)",
				"floor(0.4)"
			]
		}
	},
	ceil: {
		evaluate: unaryMathOp$1((val) => Math.ceil(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Integer)",
			category: "math",
			returns: { type: "integer" },
			args: { x: { type: "number" } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `ceil` function returns the smallest integer greater than or equal to a number.",
			seeAlso: [
				"floor",
				"round",
				"trunc"
			],
			examples: [
				"ceil(2)",
				"ceil(2.49)",
				"ceil(2.5)",
				"ceil(-2.49)",
				"ceil(-2.5)",
				"ceil(-2.501)",
				"ceil(0.4)"
			]
		}
	},
	min: {
		evaluate: (params, sourceCodeInfo) => {
			if (params.size === 1 && isVector(params.get(0))) return assertNonEmptyVector(params.get(0), sourceCodeInfo).reduce((m, val) => Math.min(m, val), Infinity);
			const [first, ...rest] = params;
			assertNumber(first, sourceCodeInfo);
			return rest.reduce((m, value) => {
				assertNumber(value, sourceCodeInfo);
				return Math.min(m, value);
			}, first);
		},
		arity: { min: 1 },
		docs: {
			type: "((Number, ...Number[]) -> Number) & ((Number[]) -> Number)",
			category: "math",
			returns: { type: "number" },
			args: {
				a: { type: "number" },
				b: { type: "number" },
				xs: {
					type: "number",
					rest: true
				},
				vector: { type: "vector" }
			},
			variants: [{ argumentNames: ["xs"] }, { argumentNames: ["vector"] }],
			description: "Returns the smallest value. Accepts either multiple numbers or a single vector of numbers.",
			seeAlso: [
				"max",
				"vector.span",
				"minIndex"
			],
			examples: [
				"2 min 3",
				"min(2, 0, 1)",
				"min(2, -1, 1)",
				"min([2, 0, -1])",
				"12 min 14"
			]
		}
	},
	max: {
		evaluate: (params, sourceCodeInfo) => {
			if (params.size === 1 && isVector(params.get(0))) return assertNonEmptyVector(params.get(0), sourceCodeInfo).reduce((m, val) => Math.max(m, val), -Infinity);
			const [first, ...rest] = params;
			assertNumber(first, sourceCodeInfo);
			return rest.reduce((m, value) => {
				assertNumber(value, sourceCodeInfo);
				return Math.max(m, value);
			}, first);
		},
		arity: { min: 1 },
		docs: {
			type: "((Number, ...Number[]) -> Number) & ((Number[]) -> Number)",
			category: "math",
			returns: { type: "number" },
			args: {
				a: { type: "number" },
				b: { type: "number" },
				xs: {
					type: "number",
					rest: true
				},
				vector: { type: "vector" }
			},
			variants: [{ argumentNames: ["xs"] }, { argumentNames: ["vector"] }],
			description: "Returns the largest value. Accepts either multiple numbers or a single vector of numbers.",
			seeAlso: [
				"min",
				"vector.span",
				"maxIndex"
			],
			examples: [
				"2 max 3",
				"max(2, 0, 1)",
				"max(2, -1, 1)",
				"max([2, 0, -1])",
				"4 max 2"
			]
		}
	},
	abs: {
		evaluate: unaryMathOp$1((val) => Math.abs(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number)",
			category: "math",
			returns: { type: "number" },
			args: { x: { type: "number" } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `abs` function returns the absolute value of a number.",
			seeAlso: ["sign", "-"],
			examples: [
				"abs(-2.3)",
				"abs(0)",
				"abs(2.5)",
				"abs(-0)"
			]
		}
	},
	sign: {
		evaluate: unaryMathOp$1((val) => Math.sign(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Integer)",
			category: "math",
			returns: { type: "integer" },
			args: { x: { type: "number" } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `sign` function returns the sign of a number: `-1` for negative, `0` for zero, and `1` for positive.",
			seeAlso: ["abs"],
			examples: [
				"sign(-2.3)",
				"sign(-0)",
				"sign(0)",
				"sign(12312)",
				"sign(-2)"
			]
		}
	},
	sum: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("sum is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number[]) -> Number",
			category: "math",
			returns: { type: "number" },
			args: { vector: {
				type: "vector",
				description: "The vector to sum."
			} },
			variants: [{ argumentNames: ["vector"] }],
			description: "Returns the **sum** of all elements in `vector`. Returns `0` for an empty vector.",
			seeAlso: [
				"prod",
				"cumsum",
				"vector.mean",
				"vector.median",
				"vector.movingSum",
				"vector.centeredMovingSum",
				"vector.runningSum"
			],
			examples: [
				"sum([1, 2, 3, 4, 5])",
				"sum([1, -2, 3])",
				"sum([])"
			]
		}
	},
	prod: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("prod is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number[]) -> Number",
			category: "math",
			returns: { type: "number" },
			args: { vector: {
				type: "vector",
				description: "The vector to multiply."
			} },
			variants: [{ argumentNames: ["vector"] }],
			description: "Returns the **product** of all elements in `vector`. Returns `1` for an empty vector.",
			seeAlso: [
				"sum",
				"cumprod",
				"vector.mean",
				"vector.median",
				"vector.movingProd",
				"vector.centeredMovingProd",
				"vector.runningProd"
			],
			examples: [
				"prod([1, 2, 3, 4, 5])",
				"prod([1, -2, 3])",
				"prod([])"
			]
		}
	},
	cumsum: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("cumsum is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number[]) -> Number[]",
			category: "math",
			returns: { type: "array" },
			args: { vector: { type: "vector" } },
			variants: [{ argumentNames: ["vector"] }],
			description: "Returns the **cumulative sum** of `vector`: a new vector where each element is the sum of all preceding elements and itself. Returns `[]` for an empty vector.",
			seeAlso: [
				"sum",
				"cumprod",
				"vector.runningSum"
			],
			examples: [
				"cumsum([1, 2, 3, 4])",
				"cumsum([1, -2, 3])",
				"cumsum([])"
			]
		}
	},
	cumprod: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("cumprod is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number[]) -> Number[]",
			category: "math",
			returns: { type: "array" },
			args: { vector: { type: "vector" } },
			variants: [{ argumentNames: ["vector"] }],
			description: "Returns the **cumulative product** of `vector`: a new vector where each element is the product of all preceding elements and itself. Returns `[]` for an empty vector.",
			seeAlso: [
				"prod",
				"cumsum",
				"vector.runningProd"
			],
			examples: [
				"cumprod([1, 2, 3, 4])",
				"cumprod([1, -2, 3])",
				"cumprod([])"
			]
		}
	}
};
//#endregion
//#region src/builtin/core/misc.ts
function isEqual(params, sourceCodeInfo) {
	const iter = params[Symbol.iterator]();
	const firstAny = asAny(iter.next().value, sourceCodeInfo);
	for (let next = iter.next(); !next.done; next = iter.next()) if (!deepEqual(firstAny, asAny(next.value, sourceCodeInfo), sourceCodeInfo)) return false;
	return true;
}
const miscNormalExpression = {
	"==": {
		evaluate: (params, sourceCodeInfo) => {
			return isEqual(params, sourceCodeInfo);
		},
		arity: { min: 1 },
		docs: {
			type: "(Unknown, ...Unknown[]) -> Boolean",
			category: "misc",
			returns: { type: "boolean" },
			args: {
				a: { type: "any" },
				b: { type: "any" },
				x: { type: "any" },
				ys: {
					type: "any",
					rest: true
				}
			},
			variants: [{ argumentNames: ["x"] }, { argumentNames: ["x", "ys"] }],
			description: "Returns `true` if all `values` are structaul equal to each other, otherwise result is `false`.",
			seeAlso: ["!="],
			examples: [
				"1 == 1",
				"[1, 2] == [1, 2]",
				`
{
 a: 1,
 b: 2,
} == {
 b: 2,
 a: 1,
}`,
				"==(1, 1)",
				"==(1.01, 1)",
				"==(\"1\", 1)",
				"==(\"2\", \"2\", \"2\", \"2\")",
				"==(2, 2, 1, 2)",
				"==([1, 2], [1, 2])",
				"==({ a: 1, b: 2 }, { b: 2, a: 1 })"
			]
		}
	},
	"!=": {
		evaluate: (params, sourceCodeInfo) => {
			return !isEqual(params, sourceCodeInfo);
		},
		arity: { min: 1 },
		docs: {
			type: "(Unknown, ...Unknown[]) -> Boolean",
			category: "misc",
			returns: { type: "boolean" },
			args: {
				a: { type: "any" },
				b: { type: "any" },
				x: { type: "any" },
				ys: {
					type: "any",
					rest: true
				}
			},
			variants: [{ argumentNames: ["x"] }, { argumentNames: ["x", "ys"] }],
			description: "Returns `true` if all `values` are not equal to each other, otherwise result is `false`. `(!= a b c)` is same as `(not (== a b c))`.",
			seeAlso: ["=="],
			examples: [
				"1 != 2",
				"3 != 3",
				"!=(3)",
				"!=(3, 3, 2)",
				"!=(\"3\", \"2\", \"1\", \"0\",)",
				"!=(0, -0)"
			]
		}
	},
	">": {
		evaluate: ([first, ...rest], sourceCodeInfo) => {
			let currentValue = asStringOrNumber(first);
			for (const param of rest) {
				if (compare(currentValue, asStringOrNumber(param), sourceCodeInfo) <= 0) return false;
				currentValue = asStringOrNumber(param);
			}
			return true;
		},
		arity: { min: 1 },
		docs: {
			type: "(Number | String, ...(Number | String)[]) -> Boolean",
			category: "misc",
			returns: { type: "boolean" },
			args: {
				a: { type: ["number", "string"] },
				b: { type: ["number", "string"] },
				x: { type: ["number", "string"] },
				ys: {
					type: ["number", "string"],
					rest: true
				}
			},
			variants: [{ argumentNames: ["x"] }, { argumentNames: ["x", "ys"] }],
			description: "Returns `true` if `x` and `ys` are in decreasing order, `false` otherwise.",
			seeAlso: [
				"<",
				">=",
				"<=",
				"compare"
			],
			examples: [
				">(1, 0)",
				">(1.01, 1)",
				">(1, 1)",
				">(4, 3, 2, 1)",
				">(3, 2, 2, 1)"
			]
		}
	},
	"<": {
		evaluate: ([first, ...rest], sourceCodeInfo) => {
			let currentValue = asStringOrNumber(first);
			for (const param of rest) {
				if (compare(currentValue, asStringOrNumber(param), sourceCodeInfo) >= 0) return false;
				currentValue = asStringOrNumber(param);
			}
			return true;
		},
		arity: { min: 1 },
		docs: {
			type: "(Number | String, ...(Number | String)[]) -> Boolean",
			category: "misc",
			returns: { type: "boolean" },
			args: {
				a: { type: ["number", "string"] },
				b: { type: ["number", "string"] },
				x: { type: ["number", "string"] },
				ys: {
					type: ["number", "string"],
					rest: true
				}
			},
			variants: [{ argumentNames: ["x"] }, { argumentNames: ["x", "ys"] }],
			description: "Returns `true` if `x` and `ys` are in increasing order, `false` otherwise.",
			seeAlso: [
				">",
				">=",
				"<=",
				"compare"
			],
			examples: [
				"<(0, 1)",
				"<(1, 1.01)",
				"<(1, 1)",
				"<(1, 2, 2, 3)",
				"<(\"a\", \"b\")"
			]
		}
	},
	">=": {
		evaluate: ([first, ...rest], sourceCodeInfo) => {
			let currentValue = asStringOrNumber(first);
			for (const param of rest) {
				if (compare(currentValue, asStringOrNumber(param), sourceCodeInfo) < 0) return false;
				currentValue = asStringOrNumber(param);
			}
			return true;
		},
		arity: { min: 1 },
		docs: {
			type: "(Number | String, ...(Number | String)[]) -> Boolean",
			category: "misc",
			returns: { type: "boolean" },
			args: {
				a: { type: ["number", "string"] },
				b: { type: ["number", "string"] },
				x: { type: ["number", "string"] },
				ys: {
					type: ["number", "string"],
					rest: true
				}
			},
			variants: [{ argumentNames: ["x"] }, { argumentNames: ["x", "ys"] }],
			description: "Returns `true` if `x` and `ys` are in non increasing order, `false` otherwise.",
			seeAlso: [
				">",
				"<",
				"<=",
				"compare"
			],
			examples: [
				"1 >= 1",
				"0 >= 1",
				">=(1, 0)",
				">=(1.01, 1)",
				">=(1, 1)",
				">=(4, 3, 2, 1)",
				">=(3, 2, 2, 1)"
			]
		}
	},
	"<=": {
		evaluate: ([first, ...rest], sourceCodeInfo) => {
			let currentValue = asStringOrNumber(first);
			for (const param of rest) {
				if (compare(currentValue, asStringOrNumber(param), sourceCodeInfo) > 0) return false;
				currentValue = asStringOrNumber(param);
			}
			return true;
		},
		arity: { min: 1 },
		docs: {
			type: "(Number | String, ...(Number | String)[]) -> Boolean",
			category: "misc",
			returns: { type: "boolean" },
			args: {
				a: { type: ["number", "string"] },
				b: { type: ["number", "string"] },
				x: { type: ["number", "string"] },
				ys: {
					type: ["number", "string"],
					rest: true
				}
			},
			variants: [{ argumentNames: ["x"] }, { argumentNames: ["x", "ys"] }],
			description: "Returns `true` if `x` and `ys` are in non decreasing order, `false` otherwise.",
			seeAlso: [
				">",
				"<",
				">=",
				"compare"
			],
			examples: [
				"1 <= 1",
				"<=(0, 1)",
				"<=(1, 1.01)",
				"<=(1, 1)",
				"<=(1, 2, 3, 4)",
				"<=(1, 2, 2, 3)"
			]
		}
	},
	"!": {
		evaluate: ([first]) => !first,
		arity: toFixedArity(1),
		docs: {
			type: "(Boolean) -> Boolean",
			category: "misc",
			returns: { type: "boolean" },
			args: { x: { type: "boolean" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Logical negation. Usable as a first-class function value, e.g. `filter(bs, !)`.",
			seeAlso: [
				"&&",
				"||",
				"isBoolean"
			],
			examples: [
				"!true",
				"!false",
				"!(1 == 2)",
				"!!true"
			]
		}
	},
	compare: {
		evaluate: ([a, b], sourceCodeInfo) => {
			return compare(a, b, sourceCodeInfo);
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Number | String | Atom, Number | String | Atom) -> Number",
			category: "misc",
			returns: { type: "number" },
			args: {
				a: { type: [
					"number",
					"string",
					"atom"
				] },
				b: { type: [
					"number",
					"string",
					"atom"
				] }
			},
			variants: [{ argumentNames: ["a", "b"] }],
			description: "Compares two values. Returns `-1` if `a` < `b`, `1` if `a` > `b` and `0` if `a` and `b` have the same sort order. Works on numbers, strings, and atoms. Note: the `<`, `>`, `<=`, `>=` operators only accept numbers and strings, not atoms — use `compare` for atom ordering.",
			seeAlso: [
				"<",
				">",
				"<=",
				">=",
				"sort",
				"sequence.sortBy"
			],
			examples: [
				"compare(0, 1)",
				"compare(0, 0)",
				"compare(1, 0)",
				"compare(\"Albert\", \"Mojir\")",
				"compare(:apple, :banana)"
			]
		}
	},
	effectName: {
		evaluate: ([first], sourceCodeInfo) => {
			return asEffect(first, sourceCodeInfo).name;
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Effect) -> String",
			category: "meta",
			returns: { type: "string" },
			args: { e: {
				type: "any",
				description: "An effect reference."
			} },
			variants: [{ argumentNames: ["e"] }],
			description: "Returns the name of an effect reference as a string.",
			seeAlso: [
				"isEffect",
				"qualifiedName",
				"qualifiedMatcher"
			],
			examples: ["effectName(@dvala.error)", "effectName(@llm.complete)"]
		}
	},
	macroexpand: {
		evaluate: () => {
			throw new Error("macroexpand is handled by the evaluator");
		},
		arity: { min: 1 },
		docs: {
			type: "(Unknown, Unknown) -> Unknown",
			category: "meta",
			returns: { type: "any" },
			args: {
				macroFn: {
					type: "any",
					description: "A macro function."
				},
				args: {
					type: "any",
					description: "AST node arguments to pass to the macro."
				}
			},
			variants: [{ argumentNames: ["macroFn", "args"] }],
			hideOperatorForm: true,
			description: "Calls a macro's body with the given AST arguments and returns the expanded AST as data, without evaluating it. Use quote...end blocks to construct the AST arguments.",
			examples: ["let double = macro (ast) -> quote $^{ast} + $^{ast} end; macroexpand(double, quote 21 end)", {
				code: "let { prettyPrint } = import(\"ast\"); let double = macro (ast) -> quote $^{ast} + $^{ast} end; macroexpand(double, quote 21 end) |> prettyPrint",
				noCheck: true
			}]
		}
	},
	qualifiedName: {
		evaluate: ([first]) => {
			if (isEffect(first)) return first.name;
			return null;
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Unknown) -> String | Null",
			category: "meta",
			returns: { type: ["string", "null"] },
			args: { entity: {
				type: "any",
				description: "An effect reference or any other value."
			} },
			variants: [{ argumentNames: ["entity"] }],
			description: "Returns the qualified name (dotted DNS-style identifier) of an entity, or null if it has none. Works on effect references.",
			seeAlso: [
				"effectName",
				"qualifiedMatcher",
				"isEffect"
			],
			examples: ["qualifiedName(@dvala.io.print)", "qualifiedName(42)"]
		}
	},
	qualifiedMatcher: {
		evaluate: ([pattern], sourceCodeInfo) => {
			if (typeof pattern === "string") return {
				[FUNCTION_SYMBOL]: true,
				sourceCodeInfo,
				functionType: "QualifiedMatcher",
				matchType: "string",
				pattern,
				flags: "",
				arity: toFixedArity(1)
			};
			if (isRegularExpression(pattern)) return {
				[FUNCTION_SYMBOL]: true,
				sourceCodeInfo,
				functionType: "QualifiedMatcher",
				matchType: "regexp",
				pattern: pattern.s,
				flags: pattern.f,
				arity: toFixedArity(1)
			};
			throw new TypeError$1("qualifiedMatcher expects a string or regexp pattern", sourceCodeInfo);
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String | Regex) -> (Unknown) -> Boolean",
			category: "meta",
			returns: { type: "function" },
			args: { pattern: {
				type: ["string", "regexp"],
				description: "A wildcard pattern or regexp to match against qualified names."
			} },
			variants: [{ argumentNames: ["pattern"] }],
			description: "Returns a predicate function that matches any entity with a qualified name (effect references). If `pattern` is a string, uses wildcard matching: no wildcard means exact match, `.*` suffix matches the prefix and all descendants (dot boundary enforced), and `*` alone matches everything. If `pattern` is a regexp, tests the qualified name against the regexp. Returns false for entities without a qualified name.",
			seeAlso: [
				"qualifiedName",
				"effectName",
				"isEffect"
			],
			examples: [
				"qualifiedMatcher(\"dvala.*\")(@dvala.error)",
				"qualifiedMatcher(\"dvala.*\")(@custom.foo)",
				"qualifiedMatcher(\"*\")(@dvala.io.print)"
			]
		}
	},
	typeOf: {
		evaluate: ([value]) => {
			if (value === null || value === void 0) return "null";
			if (typeof value === "boolean") return "boolean";
			if (typeof value === "number") return "number";
			if (typeof value === "string") return "string";
			if (isAtom(value)) return "atom";
			if (isEffect(value)) return "effect";
			if (isRegularExpression(value)) return "regexp";
			if (isMacroFunction(value)) return "macro";
			if (isHandlerFunction(value)) return "handler";
			if (isDvalaFunction(value)) return "function";
			if (isPersistentVector(value) || Array.isArray(value)) return "array";
			return "object";
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Unknown) -> String",
			category: "misc",
			returns: { type: "string" },
			args: { x: {
				type: "any",
				description: "The value to inspect."
			} },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns a string representing the type of `x`. Possible return values are `\"number\"`, `\"string\"`, `\"boolean\"`, `\"null\"`, `\"atom\"`, `\"array\"`, `\"object\"`, `\"function\"`, `\"macro\"`, `\"regexp\"`, and `\"effect\"`.",
			seeAlso: [
				"isNumber",
				"isString",
				"isBoolean",
				"isNull",
				"isAtom",
				"isArray",
				"isObject",
				"isFunction",
				"isMacro",
				"isRegexp",
				"isEffect"
			],
			examples: [
				"typeOf(42)",
				"typeOf(\"hello\")",
				"typeOf(true)",
				"typeOf(null)",
				"typeOf([1, 2, 3])",
				"typeOf({ a: 1 })",
				"typeOf((x) -> x + 1)",
				"typeOf(regexp(\"^start\"))",
				"typeOf(@dvala.io.print)"
			]
		}
	},
	raise: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("raise is implemented in Dvala");
		},
		arity: {
			min: 1,
			max: 2
		},
		docs: {
			type: "((String) -> @{dvala.error} Never) & ((String, Unknown) -> @{dvala.error} Never)",
			category: "misc",
			returns: { type: "never" },
			args: {
				message: {
					type: "string",
					description: "Error message."
				},
				data: {
					type: "any",
					description: "Optional structured data attached to the error."
				}
			},
			variants: [{ argumentNames: ["message"] }, { argumentNames: ["message", "data"] }],
			hideOperatorForm: true,
			description: "Raises an error by performing `@dvala.error` with a structured payload `{ type: \"UserError\", message, data }`. Convenience wrapper — use `perform(@dvala.error, ...)` directly for custom error types or additional fields.",
			seeAlso: ["perform"],
			examples: ["do with handler @dvala.error(err) -> resume(\"caught\") end; raise(\"oops\") end", "do with handler @dvala.error(err) -> resume(null) end; raise(\"bad input\", { field: \"email\" }) end"]
		}
	}
};
//#endregion
//#region src/builtin/core/object.ts
const objectNormalExpression = {
	keys: {
		evaluate: ([obj], sourceCodeInfo) => {
			assertObj(obj, sourceCodeInfo);
			return PersistentVector.from(obj.keys());
		},
		arity: toFixedArity(1),
		docs: {
			type: "({...}) -> String[]",
			category: "object",
			returns: {
				type: "any",
				array: true
			},
			args: { obj: { type: "object" } },
			variants: [{ argumentNames: ["obj"] }],
			description: "Returns array of all keys in `obj`.",
			seeAlso: [
				"vals",
				"entries",
				"zipmap",
				"selectKeys"
			],
			examples: [
				"keys({})",
				"keys({ x: 10, y: true, z: \"A string\" })",
				"keys(object(\"x\", 10, \"y\", true, \"z\", \"A string\"))"
			]
		}
	},
	vals: {
		evaluate: ([obj], sourceCodeInfo) => {
			assertObj(obj, sourceCodeInfo);
			return PersistentVector.from(obj.values());
		},
		arity: toFixedArity(1),
		docs: {
			type: "({...}) -> Unknown[]",
			category: "object",
			returns: {
				type: "any",
				array: true
			},
			args: { obj: { type: "object" } },
			variants: [{ argumentNames: ["obj"] }],
			description: "Returns array of all values in `obj`.",
			seeAlso: [
				"keys",
				"entries",
				"zipmap"
			],
			examples: [
				"vals({})",
				"vals({ x: 10, y: true, z: \"A string\" })",
				"vals(object(\"x\", 10, \"y\", true, \"z\", \"A string\"))"
			]
		}
	},
	entries: {
		evaluate: ([obj], sourceCodeInfo) => {
			assertObj(obj, sourceCodeInfo);
			return PersistentVector.from(obj.entries().map(([k, v]) => PersistentVector.from([k, v])));
		},
		arity: toFixedArity(1),
		docs: {
			type: "({...}) -> Unknown[][]",
			category: "object",
			returns: { type: "array" },
			args: { obj: { type: "object" } },
			variants: [{ argumentNames: ["obj"] }],
			description: "Returns nested array of all key - value pairs in `obj`.",
			seeAlso: [
				"keys",
				"vals",
				"zipmap",
				"find"
			],
			examples: [
				"entries({})",
				"entries({ x: 10, y: true, z: \"A string\" })",
				"entries(object(\"x\", 10, \"y\", true, \"z\", \"A string\"))"
			]
		}
	},
	find: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("find is implemented in Dvala");
		},
		arity: toFixedArity(2),
		docs: {
			type: "({...}, String) -> Unknown[] | Null",
			category: "object",
			returns: { type: ["array", "null"] },
			args: {
				a: { type: "object" },
				b: { type: "string" },
				obj: { type: "object" },
				key: { type: "string" }
			},
			variants: [{ argumentNames: ["obj", "key"] }],
			description: "Returns entry (key-value pair) for `key`, or `null` if `key` not present in `obj`.",
			seeAlso: [
				"get",
				"contains",
				"entries",
				"sequence.position",
				"some"
			],
			examples: [
				"{ a: 1, \"b\": 2 } find \"a\"",
				"find(object(\"a\", 1, \"b\", 2), \"b\")",
				"find(object(\"a\", 1, \"b\", 2), \"c\")"
			]
		}
	},
	dissoc: {
		evaluate: ([obj, key], sourceCodeInfo) => {
			assertObj(obj, sourceCodeInfo);
			assertString(key, sourceCodeInfo);
			return obj.dissoc(key);
		},
		arity: toFixedArity(2),
		docs: {
			type: "({...}, String) -> {...}",
			category: "object",
			returns: { type: "object" },
			args: {
				a: { type: "object" },
				b: { type: "string" },
				obj: { type: "object" },
				key: { type: "string" }
			},
			variants: [{ argumentNames: ["obj", "key"] }],
			description: "Return shallow copy of `obj` with `key` deleted.",
			seeAlso: ["assoc", "selectKeys"],
			examples: [
				"{ x: 10, y: 20 } dissoc \"y\"",
				"dissoc({ x: 10, y: 20 }, \"x\")",
				"dissoc({ x: 10 }, \"y\")",
				`
let o = { a: 5 };
dissoc(o, "a");
o`
			]
		}
	},
	merge: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("merge is implemented in Dvala");
		},
		arity: { min: 0 },
		docs: {
			type: "(() -> Null) & (({...}, ...{...}[]) -> {...})",
			category: "object",
			returns: { type: ["object", "null"] },
			args: {
				a: { type: "object" },
				b: { type: "object" },
				objs: {
					type: "object",
					rest: true
				}
			},
			variants: [{ argumentNames: ["objs"] }],
			description: `Returns a new object created by merging together all arguments.

If two keys appears in more than one object the value from the last object is used.
If no arguments are provided \`null\` is returned.`,
			seeAlso: ["mergeWith", "assoc"],
			examples: [
				"{ x: 10 } merge { y: 20 }",
				"merge(object(\"x\", 10), object(\"y\", 20))",
				"merge(object(\"x\", 10), object(\"x\", 15, \"y\", 20))"
			]
		}
	},
	mergeWith: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("mergeWith is implemented in Dvala");
		},
		arity: { min: 2 },
		docs: {
			type: "(({...}, {...}, (Unknown, Unknown) -> Unknown) -> {...}) & (({...}, {...}, {...}, (Unknown, Unknown) -> Unknown) -> {...}) & (({...}, {...}, {...}, {...}, (Unknown, Unknown) -> Unknown) -> {...})",
			category: "object",
			returns: { type: "object" },
			args: {
				objs: {
					type: "object",
					rest: true
				},
				fun: { type: "function" }
			},
			variants: [{ argumentNames: ["objs", "fun"] }],
			description: `
Returns a new object created by merging together all arguments.
If two keys appears in more than one object \`fun\` is used to calculate the new value.

If no arguments are provided \`null\` is returned.`,
			seeAlso: ["merge"],
			examples: [
				{
					code: "mergeWith(object(\"x\", 10), object(\"y\", 20), +)",
					noCheck: true
				},
				{
					code: "mergeWith(object(\"x\", 10), object(\"x\", 15, \"y\", 20), +)",
					noCheck: true
				},
				{
					code: "mergeWith({ x: 10 }, { x: 20 }, { x: 30 }, { x: 40 }, -)",
					noCheck: true
				}
			],
			hideOperatorForm: true
		}
	},
	zipmap: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("zipmap is implemented in Dvala");
		},
		arity: toFixedArity(2),
		docs: {
			type: "(String[], Unknown[]) -> {...}",
			category: "object",
			returns: { type: "object" },
			args: {
				a: { type: "array" },
				b: { type: "array" }
			},
			variants: [{ argumentNames: ["a", "b"] }],
			description: "Returns a new object created by mapping `a` to `b`.",
			seeAlso: [
				"entries",
				"keys",
				"vals",
				"sequence.interleave"
			],
			examples: [
				"[\"a\", \"b\", \"c\"] zipmap [1, 2, 3]",
				"zipmap([\"a\", \"b\", \"c\"], [10, null, [1, 2, 3]])",
				"zipmap([\"a\", \"b\", \"c\"], [1])",
				"zipmap([], [10, null, [1, 2, 3]])"
			]
		}
	},
	selectKeys: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("selectKeys is implemented in Dvala");
		},
		arity: toFixedArity(2),
		docs: {
			type: "({...}, String[]) -> {...}",
			category: "object",
			returns: { type: "object" },
			args: {
				a: { type: "object" },
				b: { type: "array" }
			},
			variants: [{ argumentNames: ["a", "b"] }],
			description: "Returns an object containing only those entries in `a` whose key is in `b`.",
			seeAlso: ["dissoc", "keys"],
			examples: [
				"{ a: 1, b: 2, c: 3 } selectKeys [\"a\", \"b\"]",
				"selectKeys({ a: 1, b: 2, c: 3 }, [\"a\", \"b\"])",
				"selectKeys({ a: 1 }, [\"a\", \"b\"])"
			]
		}
	}
};
//#endregion
//#region src/builtin/core/predicates.ts
const predicatesNormalExpression = {
	isFunction: {
		evaluate: ([first]) => isDvalaFunction(first) && !isMacroFunction(first),
		arity: toFixedArity(1),
		docs: {
			type: "(x: Unknown) -> x is Function",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: "any" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is a function (not a macro), otherwise `false`.",
			seeAlso: [
				"isMacro",
				"isString",
				"isNumber",
				"isBoolean",
				"isAtom",
				"isNull",
				"isArray",
				"isObject",
				"isRegexp",
				"typeOf"
			],
			examples: [
				"isFunction(+)",
				"isFunction(/)",
				"isFunction((x, y) -> x + y)",
				"isFunction(false)",
				"isFunction(\"false\")",
				"isFunction([1, 2, 3])"
			]
		}
	},
	isMacro: {
		evaluate: ([first]) => isMacroFunction(first),
		arity: toFixedArity(1),
		docs: {
			type: "(x: Unknown) -> x is Macro",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: "any" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is a macro, otherwise `false`.",
			seeAlso: ["isFunction", "typeOf"],
			examples: [
				"isMacro(macro (ast) -> ast)",
				"isMacro((x) -> x)",
				"isMacro(42)"
			]
		}
	},
	isString: {
		evaluate: ([first]) => typeof first === "string",
		arity: toFixedArity(1),
		docs: {
			type: "(x: Unknown) -> x is String",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: "any" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is a string, otherwise `false`.",
			seeAlso: [
				"isBlank",
				"isNumber",
				"isBoolean",
				"isAtom",
				"isNull",
				"isArray",
				"isObject",
				"isRegexp",
				"isFunction",
				"isCollection",
				"isSequence",
				"typeOf"
			],
			examples: [
				"isString(\"\")",
				"isString(\"A string\")",
				"isString(if true then \"A string\" else false end)",
				"isString(false)",
				"isString([1, 2, 3])",
				"isString(100)"
			]
		}
	},
	isNumber: {
		evaluate: ([first]) => typeof first === "number",
		arity: toFixedArity(1),
		docs: {
			type: "(x: Unknown) -> x is Number",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: "any" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is a number, otherwise `false`.",
			seeAlso: [
				"isInteger",
				"isZero",
				"isPos",
				"isNeg",
				"number",
				"isString",
				"isBoolean",
				"isAtom",
				"isNull",
				"isFunction",
				"typeOf"
			],
			examples: [
				"isNumber(0)",
				"isNumber(2)",
				"isNumber(-0.12)",
				"isNumber(false)",
				"isNumber([1, 2, 3])",
				"isNumber(\"A string\")"
			]
		}
	},
	isInteger: {
		evaluate: ([first]) => typeof first === "number" && isNumber(first, { integer: true }),
		arity: toFixedArity(1),
		docs: {
			type: "(x: Unknown) -> x is Integer",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: "any" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is an integer, otherwise `false`.",
			seeAlso: [
				"isNumber",
				"number",
				"isEven",
				"isOdd"
			],
			examples: [
				"isInteger(0)",
				"isInteger(-12)",
				"isInteger(42)",
				"isInteger(10.1)",
				"isInteger((x, y) -> x + y)",
				"isInteger(false)",
				"isInteger(\"false\")",
				"isInteger([1, 2, 3])"
			]
		}
	},
	isBoolean: {
		evaluate: ([first]) => typeof first === "boolean",
		arity: toFixedArity(1),
		docs: {
			type: "(x: Unknown) -> x is Boolean",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: "any" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is a `boolean`, otherwise `false`.",
			seeAlso: [
				"isTrue",
				"isFalse",
				"!",
				"isString",
				"isNumber",
				"isAtom",
				"isNull",
				"isFunction",
				"typeOf"
			],
			examples: [
				"isBoolean(true)",
				"isBoolean(false)",
				"isBoolean([1, 2, 3])",
				"isBoolean(0)",
				"isBoolean(\"A string\")"
			]
		}
	},
	isAtom: {
		evaluate: ([first]) => isAtom(first),
		arity: toFixedArity(1),
		docs: {
			type: "(x: Unknown) -> x is Atom",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: "any" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is an atom (e.g. `:ok`, `:error`), otherwise `false`.",
			seeAlso: [
				"isString",
				"isNumber",
				"isBoolean",
				"isNull",
				"isFunction",
				"typeOf"
			],
			examples: [
				"isAtom(:ok)",
				"isAtom(:error)",
				"isAtom(\"atom\")",
				"isAtom(42)"
			]
		}
	},
	isNull: {
		evaluate: ([first]) => first === null || first === void 0,
		arity: toFixedArity(1),
		docs: {
			type: "(x: Unknown) -> x is Null",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: "any" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is `null`, otherwise `false`.",
			seeAlso: [
				"isEmpty",
				"isNotEmpty",
				"isString",
				"isNumber",
				"isBoolean",
				"isAtom",
				"isFunction",
				"typeOf"
			],
			examples: [
				"isNull(null)",
				"isNull(false)",
				"isNull([1, 2, 3])",
				"isNull(0)",
				"isNull(\"A string\")"
			]
		}
	},
	isZero: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("isZero is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number) -> Boolean",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: "number" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is `0`, otherwise `false`.",
			seeAlso: [
				"isPos",
				"isNeg",
				"isEven",
				"isNumber"
			],
			examples: [
				"isZero(0)",
				"isZero(-0.0)",
				"isZero(1)",
				"isZero(0.1)"
			]
		}
	},
	isPos: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("isPos is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number) -> Boolean",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: "number" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is greater than `0`, otherwise `false`.",
			seeAlso: [
				"isNeg",
				"isZero",
				"isNumber"
			],
			examples: [
				"isPos(0)",
				"isPos(-0.0)",
				"isPos(1)",
				"isPos(-0.1)"
			]
		}
	},
	isNeg: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("isNeg is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number) -> Boolean",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: "number" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is less than `0`, otherwise `false`.",
			seeAlso: [
				"isPos",
				"isZero",
				"isNumber"
			],
			examples: [
				"isNeg(0)",
				"isNeg(-0.0)",
				"isNeg(1)",
				"isNeg(-0.1)"
			]
		}
	},
	isEven: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("isEven is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number) -> Boolean",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: "number" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is even, otherwise `false`.",
			seeAlso: [
				"isOdd",
				"isInteger",
				"isZero"
			],
			examples: [
				"isEven(0)",
				"isEven(-0.0)",
				"isEven(-1)",
				"isEven(2.1)"
			]
		}
	},
	isOdd: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("isOdd is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number) -> Boolean",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: "number" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is odd, otherwise `false`.",
			seeAlso: ["isEven", "isInteger"],
			examples: [
				"isOdd(1.0)",
				"isOdd(1.001)",
				"isOdd(-1)",
				"isOdd(2.1)"
			]
		}
	},
	isArray: {
		evaluate: ([first]) => {
			return isArr(first) || Array.isArray(first);
		},
		arity: toFixedArity(1),
		docs: {
			type: "(x: Unknown) -> x is Unknown[]",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: "any" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is an array, otherwise `false`.",
			seeAlso: [
				"isSequence",
				"isCollection",
				"isObject",
				"isString",
				"isVector",
				"isMatrix",
				"isGrid",
				"isFunction",
				"typeOf"
			],
			examples: [
				"isArray([])",
				"isArray([1, 2, 3])",
				"isArray(object(\"a\", 10))",
				"isArray(42)",
				"isArray(10.1)",
				"isArray((x, y) -> x + y)"
			]
		}
	},
	isCollection: {
		evaluate: ([first]) => {
			return isColl(first);
		},
		arity: toFixedArity(1),
		docs: {
			type: "(x: Unknown) -> Boolean",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: "any" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is a collection i.e. an array, an object or a string, otherwise `false`.",
			seeAlso: [
				"isSequence",
				"isArray",
				"isObject",
				"isString"
			],
			examples: [
				"isCollection([])",
				"isCollection([1, 2, 3])",
				"isCollection(object(\"a\", 10))",
				"isCollection(\"Albert\")",
				"isCollection(42)",
				"isCollection(10.1)",
				"isCollection((x, y) -> x + y)"
			]
		}
	},
	isSequence: {
		evaluate: ([first]) => {
			return isSeq(first);
		},
		arity: toFixedArity(1),
		docs: {
			type: "(x: Unknown) -> Boolean",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: "any" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is a sequence i.e. an array or a string, otherwise `false`.",
			seeAlso: [
				"isCollection",
				"isArray",
				"isString"
			],
			examples: [
				"isSequence([])",
				"isSequence([1, 2, 3])",
				"isSequence(object(\"a\", 10))",
				"isSequence(\"Albert\")",
				"isSequence(42)",
				"isSequence(10.1)",
				"isSequence((x, y) -> x + y)"
			]
		}
	},
	isObject: {
		evaluate: ([first]) => isObj(first),
		arity: toFixedArity(1),
		docs: {
			type: "(x: Unknown) -> x is {...}",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: "any" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is an object, otherwise `false`.",
			seeAlso: [
				"isCollection",
				"isArray",
				"isString",
				"isFunction",
				"typeOf"
			],
			examples: [
				"isObject(object(\"a\", 10))",
				"isObject(42)",
				"isObject(10.1)",
				"isObject((x, y) -> x + y)",
				"isObject(#\"^start\")",
				"isObject(\"false\")",
				"isObject([1, 2, 3])"
			]
		}
	},
	isRegexp: {
		evaluate: ([value]) => isRegularExpression(value),
		arity: toFixedArity(1),
		docs: {
			type: "(x: Unknown) -> x is Regex",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: "any" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is a regexp, otherwise `false`.",
			seeAlso: [
				"regexp",
				"reMatch",
				"isString",
				"isFunction",
				"typeOf"
			],
			examples: [
				"isRegexp(regexp(\"^start\"))",
				"isRegexp(#\"^start\")",
				"isRegexp(-12)",
				"isRegexp({})",
				"isRegexp(10.1)",
				"isRegexp((x, y) -> x + y)",
				"isRegexp(false)",
				"isRegexp(\"false\")",
				"isRegexp([1, 2, 3])"
			]
		}
	},
	isEffect: {
		evaluate: ([value]) => isEffect(value),
		arity: toFixedArity(1),
		docs: {
			type: "(x: Unknown) -> x is Effect",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: "any" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is an effect, otherwise `false`.",
			seeAlso: [
				"effect",
				"effectName",
				"perform",
				"typeOf",
				"qualifiedName",
				"qualifiedMatcher"
			],
			examples: [
				"isEffect(@dvala.io.print)",
				"isEffect(42)",
				"isEffect(\"hello\")",
				"isEffect(null)",
				"isEffect({})",
				"isEffect([1, 2, 3])"
			]
		}
	},
	isTrue: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("isTrue is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Unknown) -> Boolean",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: "any" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is `true`, otherwise `false`.",
			seeAlso: ["isFalse", "isBoolean"],
			examples: [
				"isTrue(false)",
				"isTrue(true)",
				"isTrue(1)",
				"isTrue(0)"
			]
		}
	},
	isFalse: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("isFalse is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Unknown) -> Boolean",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: "any" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is `true`, otherwise `false`.",
			seeAlso: ["isTrue", "isBoolean"],
			examples: [
				"isFalse(false)",
				"isFalse(true)",
				"isFalse(1)",
				"isFalse(0)"
			]
		}
	},
	isEmpty: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("isEmpty is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String | Unknown[] | {...} | Null) -> Boolean",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: [
				"collection",
				"string",
				"null"
			] } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `true` if `x` is empty or `null`, otherwise `false`.",
			seeAlso: [
				"isNotEmpty",
				"collection.notEmpty",
				"isNull",
				"isBlank",
				"count"
			],
			examples: [
				"isEmpty([])",
				"isEmpty([1, 2, 3])",
				"isEmpty({})",
				"isEmpty({ a: 2 })",
				"isEmpty(\"\")",
				"isEmpty(\"Albert\")",
				"isEmpty(null)"
			]
		}
	},
	isNotEmpty: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("isNotEmpty is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String | Unknown[] | {...} | Null) -> Boolean",
			category: "predicate",
			returns: { type: "boolean" },
			args: { x: { type: [
				"collection",
				"string",
				"null"
			] } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `false` if `x` is empty or `null`, otherwise `true`.",
			seeAlso: [
				"isEmpty",
				"collection.notEmpty",
				"isNull"
			],
			examples: [
				"isNotEmpty([])",
				"isNotEmpty([1, 2, 3])",
				"isNotEmpty({})",
				"isNotEmpty({ a: 2 })",
				"isNotEmpty(\"\")",
				"isNotEmpty(\"Albert\")",
				"isNotEmpty(null)"
			]
		}
	},
	isVector: {
		evaluate: ([vector]) => isVector(vector),
		arity: toFixedArity(1),
		docs: {
			type: "(x: Unknown) -> x is Number[]",
			category: "predicate",
			description: "Checks if a value is a `vector`. A `vector` is an array of `numbers`.",
			seeAlso: [
				"isMatrix",
				"isGrid",
				"isArray"
			],
			returns: { type: "boolean" },
			args: { value: {
				type: "any",
				description: "The value to check."
			} },
			variants: [{ argumentNames: ["value"] }],
			examples: [
				"isVector(1)",
				"isVector([1, 2, 3])",
				"isVector([1, 2, \"3\"])"
			]
		}
	},
	isMatrix: {
		evaluate: ([matrix]) => isMatrix(matrix),
		arity: toFixedArity(1),
		docs: {
			type: "(x: Unknown) -> x is Number[][]",
			category: "predicate",
			description: "Checks if a value is a `matrix`. A `matrix` is an array of arrays of `numbers`.",
			seeAlso: [
				"isVector",
				"isGrid",
				"isArray"
			],
			returns: { type: "boolean" },
			args: { value: {
				type: "any",
				description: "The value to check."
			} },
			variants: [{ argumentNames: ["value"] }],
			examples: [
				"isMatrix(1)",
				"isMatrix([1, 2, 3])",
				"isMatrix([[1, 2], [3, 4]])",
				"isMatrix([[1, 2], [3, \"4\"]])",
				"isMatrix([[1, 2], [3]])"
			]
		}
	},
	isGrid: {
		evaluate: ([table]) => isGrid(table),
		arity: toFixedArity(1),
		docs: {
			type: "(x: Unknown) -> x is Unknown[][]",
			category: "predicate",
			description: "Checks if a `value` is a `grid`. A `grid` is an `array` of `arrays` where all inner `arrays` have the same length.",
			seeAlso: [
				"isVector",
				"isMatrix",
				"isArray"
			],
			returns: { type: "boolean" },
			args: { value: {
				type: "any",
				description: "The value to check."
			} },
			variants: [{ argumentNames: ["value"] }],
			examples: [
				"isGrid(\"1\")",
				"isGrid([\"1\", 2, 3])",
				"isGrid([[\"1\", 2], [3, 4]])",
				"isGrid([[\"1\", 2], [3, \"4\"]])",
				"isGrid([[\"1\", 2], [3]])"
			]
		}
	},
	isIncreasing: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("isIncreasing is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number[]) -> Boolean",
			category: "predicate",
			returns: { type: "boolean" },
			args: { vector: { type: "vector" } },
			variants: [{ argumentNames: ["vector"] }],
			description: "Returns `true` if `vector` is non-decreasing (each element is greater than or equal to the previous one). Vectors with fewer than two elements are `true`.",
			seeAlso: [
				"isDecreasing",
				"isStrictlyIncreasing",
				"isMonotonic"
			],
			examples: [
				"isIncreasing([1, 2, 2, 3])",
				"isIncreasing([1, 3, 2])",
				"isIncreasing([5])",
				"isIncreasing([])"
			]
		}
	},
	isDecreasing: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("isDecreasing is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number[]) -> Boolean",
			category: "predicate",
			returns: { type: "boolean" },
			args: { vector: { type: "vector" } },
			variants: [{ argumentNames: ["vector"] }],
			description: "Returns `true` if `vector` is non-increasing (each element is less than or equal to the previous one). Vectors with fewer than two elements are `true`.",
			seeAlso: [
				"isIncreasing",
				"isStrictlyDecreasing",
				"isMonotonic"
			],
			examples: [
				"isDecreasing([3, 2, 2, 1])",
				"isDecreasing([3, 1, 2])",
				"isDecreasing([5])",
				"isDecreasing([])"
			]
		}
	},
	isStrictlyIncreasing: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("isStrictlyIncreasing is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number[]) -> Boolean",
			category: "predicate",
			returns: { type: "boolean" },
			args: { vector: { type: "vector" } },
			variants: [{ argumentNames: ["vector"] }],
			description: "Returns `true` if `vector` is strictly increasing (each element is greater than the previous one). Vectors with fewer than two elements are `true`.",
			seeAlso: [
				"isStrictlyDecreasing",
				"isIncreasing",
				"isStrictlyMonotonic"
			],
			examples: [
				"isStrictlyIncreasing([1, 2, 3])",
				"isStrictlyIncreasing([1, 2, 2])",
				"isStrictlyIncreasing([5])",
				"isStrictlyIncreasing([])"
			]
		}
	},
	isStrictlyDecreasing: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("isStrictlyDecreasing is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number[]) -> Boolean",
			category: "predicate",
			returns: { type: "boolean" },
			args: { vector: { type: "vector" } },
			variants: [{ argumentNames: ["vector"] }],
			description: "Returns `true` if `vector` is strictly decreasing (each element is less than the previous one). Vectors with fewer than two elements are `true`.",
			seeAlso: [
				"isStrictlyIncreasing",
				"isDecreasing",
				"isStrictlyMonotonic"
			],
			examples: [
				"isStrictlyDecreasing([3, 2, 1])",
				"isStrictlyDecreasing([2, 2, 1])",
				"isStrictlyDecreasing([5])",
				"isStrictlyDecreasing([])"
			]
		}
	},
	isMonotonic: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("isMonotonic is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number[]) -> Boolean",
			category: "predicate",
			returns: { type: "boolean" },
			args: { vector: { type: "vector" } },
			variants: [{ argumentNames: ["vector"] }],
			description: "Returns `true` if `vector` is monotonic, i.e. either non-decreasing or non-increasing. Vectors with fewer than two elements are `true`.",
			seeAlso: [
				"isStrictlyMonotonic",
				"isIncreasing",
				"isDecreasing"
			],
			examples: [
				"isMonotonic([1, 2, 3])",
				"isMonotonic([3, 2, 1])",
				"isMonotonic([1, 3, 2])",
				"isMonotonic([5])"
			]
		}
	},
	isStrictlyMonotonic: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("isStrictlyMonotonic is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number[]) -> Boolean",
			category: "predicate",
			returns: { type: "boolean" },
			args: { vector: { type: "vector" } },
			variants: [{ argumentNames: ["vector"] }],
			description: "Returns `true` if `vector` is strictly monotonic, i.e. either strictly increasing or strictly decreasing. Vectors with fewer than two elements are `true`.",
			seeAlso: [
				"isMonotonic",
				"isStrictlyIncreasing",
				"isStrictlyDecreasing"
			],
			examples: [
				"isStrictlyMonotonic([1, 2, 3])",
				"isStrictlyMonotonic([3, 2, 1])",
				"isStrictlyMonotonic([1, 2, 2])",
				"isStrictlyMonotonic([5])"
			]
		}
	}
};
//#endregion
//#region src/builtin/core/regexp.ts
const regexpNormalExpression = {
	regexp: {
		evaluate: ([sourceArg, flagsArg], sourceCodeInfo) => {
			assertString(sourceArg, sourceCodeInfo);
			const source = sourceArg || "(?:)";
			const flags = typeof flagsArg === "string" ? flagsArg : "";
			try {
				new RegExp(source, flags);
			} catch (_e) {
				throw new RuntimeError(`Invalid regular expression: ${source} ${flags}`, sourceCodeInfo);
			}
			return {
				[REGEXP_SYMBOL]: true,
				sourceCodeInfo,
				s: source,
				f: flags
			};
		},
		arity: {
			min: 1,
			max: 2
		},
		docs: {
			type: "((String) -> Regex) & ((String, String) -> Regex)",
			category: "regular-expression",
			returns: { type: "regexp" },
			args: {
				pattern: { type: "string" },
				flags: {
					type: "string",
					description: "Optional flags for the regular expression. Possible values are the same as Javascript RegExp takes."
				}
			},
			variants: [{ argumentNames: ["pattern"] }, { argumentNames: ["pattern", "flags"] }],
			description: "Creates a RegExp from `pattern` and `flags`.",
			examples: [
				"regexp(\"^\\s*(.*)$\")",
				"#\"^\\s*(.*)$\"",
				"regexp(\"albert\", \"ig\")",
				"#\"albert\"ig"
			],
			seeAlso: [
				"-short-regexp",
				"reMatch",
				"replace",
				"replaceAll",
				"isRegexp"
			],
			hideOperatorForm: true
		}
	},
	reMatch: {
		evaluate: ([text, regexp], sourceCodeInfo) => {
			assertRegularExpression(regexp, sourceCodeInfo);
			if (!isString(text)) return null;
			const match = new RegExp(regexp.s, regexp.f).exec(text);
			if (match) return PersistentVector.from([...match]);
			return null;
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Unknown, Regex) -> String[] | Null",
			category: "regular-expression",
			returns: { type: "any" },
			args: {
				a: { type: "regexp" },
				b: { type: "string" }
			},
			variants: [{ argumentNames: ["a", "b"] }],
			description: `Matches \`b\` against regular expression \`a\`.
If \`b\` is a string and matches the regular expression, a \`reMatch\`-array is returned, otherwise \`null\` is returned.`,
			seeAlso: [
				"regexp",
				"replace",
				"replaceAll",
				"-short-regexp",
				"isRegexp"
			],
			examples: [
				"reMatch(\"  A string\", regexp(\"^\\\\s*(.*)$\"))",
				"reMatch(\"  A string\", #\"^\\s*(.*)$\")",
				"reMatch(\"My name is Albert\", #\"albert\"i)",
				"reMatch(\"My name is Ben\", #\"albert\"i)",
				"reMatch(null, #\"albert\"i)",
				"reMatch(1, #\"albert\"i)",
				"reMatch({}, #\"albert\"i)"
			]
		}
	},
	replace: {
		evaluate: ([str, regexp, value], sourceCodeInfo) => {
			assertString(str, sourceCodeInfo);
			assertStringOrRegularExpression(regexp, sourceCodeInfo);
			assertString(value, sourceCodeInfo);
			const matcher = isRegularExpression(regexp) ? new RegExp(regexp.s, `${regexp.f}`) : regexp;
			return str.replace(matcher, value);
		},
		arity: toFixedArity(3),
		docs: {
			type: "((String, String, String) -> String) & ((String, Regex, String) -> String)",
			category: "regular-expression",
			returns: { type: "string" },
			args: {
				a: { type: "string" },
				b: { type: ["regexp", "string"] },
				x: { type: "string" }
			},
			variants: [{ argumentNames: [
				"a",
				"b",
				"x"
			] }],
			description: "Returns a new string with first match of regular expression `b` replaced by `x`.",
			seeAlso: [
				"replaceAll",
				"regexp",
				"reMatch",
				"-short-regexp"
			],
			examples: [
				"replace(\"Duck duck\", \"u\", \"i\")",
				"replace(\"Duck duck\", #\"u\", \"i\")",
				"replace(\"abcABC\", regexp(\"a\", \"i\"), \"-\")",
				"replace(\"abcABC\", regexp(\"a\", \"gi\"), \"-\")",
				"replace(\"abcABC\", #\"a\"i, \"-\")",
				"replace(\"abcABC\", #\"a\"gi, \"-\")"
			]
		}
	},
	replaceAll: {
		evaluate: ([str, regexp, value], sourceCodeInfo) => {
			assertString(str, sourceCodeInfo);
			assertStringOrRegularExpression(regexp, sourceCodeInfo);
			assertString(value, sourceCodeInfo);
			const matcher = isRegularExpression(regexp) ? new RegExp(regexp.s, `${regexp.f.includes("g") ? regexp.f : `${regexp.f}g`}`) : regexp;
			return str.replaceAll(matcher, value);
		},
		arity: toFixedArity(3),
		docs: {
			type: "((String, String, String) -> String) & ((String, Regex, String) -> String)",
			category: "regular-expression",
			returns: { type: "string" },
			args: {
				a: { type: "string" },
				b: { type: ["regexp", "string"] },
				x: { type: "string" }
			},
			variants: [{ argumentNames: [
				"a",
				"b",
				"x"
			] }],
			description: "Returns a new string with all matches of regular expression `b` replaced by `x`.",
			seeAlso: [
				"replace",
				"regexp",
				"reMatch",
				"-short-regexp"
			],
			examples: [
				"replaceAll(\"Duck duck\", \"u\", \"i\")",
				"replaceAll(\"Duck duck\", regexp(\"u\"), \"i\")",
				"replaceAll(\"abcABC\", regexp(\"a\", \"i\"), \"-\")",
				"replaceAll(\"abcABC\", regexp(\"a\", \"gi\"), \"-\")",
				"replaceAll(\"abcABC\", #\"a\"i, \"-\")",
				"replaceAll(\"abcABC\", #\"a\"gi, \"-\")"
			]
		}
	}
};
//#endregion
//#region src/builtin/core/string.ts
const blankRegexp = /^\s*$/;
const stringNormalExpression = {
	str: {
		evaluate: (params) => {
			let result = "";
			for (const param of params) {
				const paramStr = param === void 0 || param === null ? "" : isAtom(param) ? `:${param.name}` : isObj(param) ? JSON.stringify(param.toRecord()) : isPersistentVector(param) ? JSON.stringify(param.toArray()) : `${param}`;
				result = result + paramStr;
			}
			return result;
		},
		arity: {},
		docs: {
			type: "(() -> String) & ((Unknown, ...Unknown[]) -> String)",
			category: "string",
			returns: { type: "string" },
			args: { values: {
				type: "any",
				rest: true
			} },
			variants: [{ argumentNames: ["values"] }],
			description: "Concatenats `values` into one string. If `value` equals `null` empty string is returned. Atoms are converted to `\":name\"` format.",
			seeAlso: [
				"++",
				"join",
				"string.template",
				"string.stringRepeat",
				"number"
			],
			examples: [
				"str(\"A string\", \", and another string\", \" ...and more\")",
				"str(\"Just one string\")",
				"str()",
				{
					code: "str(0, false, true, null, #\"^kalle\", [1, 2, 3], {a: \"a\"})",
					noCheck: true
				},
				"str(:ok)"
			],
			hideOperatorForm: true
		}
	},
	number: {
		evaluate: ([str], sourceCodeInfo) => {
			assertString(str, sourceCodeInfo);
			const number = Number(str);
			if (Number.isNaN(number)) throw new TypeError$1(`Could not convert '${str}' to a number.`, sourceCodeInfo);
			return number;
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String) -> Number",
			category: "string",
			returns: { type: "number" },
			args: { s: { type: "string" } },
			variants: [{ argumentNames: ["s"] }],
			description: "Parses `s` to a number.",
			seeAlso: [
				"str",
				"isNumber",
				"isInteger"
			],
			examples: [
				"number(\"10\")",
				"number(\"010\")",
				"number(\"-1.01\")"
			]
		}
	},
	lowerCase: {
		evaluate: ([str], sourceCodeInfo) => {
			assertString(str, sourceCodeInfo);
			return str.toLowerCase();
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String) -> String",
			category: "string",
			returns: { type: "string" },
			args: { s: { type: "string" } },
			variants: [{ argumentNames: ["s"] }],
			description: "Returns `s` converted to lower case.",
			seeAlso: ["upperCase", "string.capitalize"],
			examples: ["lowerCase(\"Albert\")", "lowerCase(\"\")"]
		}
	},
	upperCase: {
		evaluate: ([str], sourceCodeInfo) => {
			assertString(str, sourceCodeInfo);
			return str.toUpperCase();
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String) -> String",
			category: "string",
			returns: { type: "string" },
			args: { s: { type: "string" } },
			variants: [{ argumentNames: ["s"] }],
			description: "Returns `s` converted to upper case.",
			seeAlso: ["lowerCase", "string.capitalize"],
			examples: ["upperCase(\"Albert\")", "upperCase(\"\")"]
		}
	},
	trim: {
		evaluate: ([str], sourceCodeInfo) => {
			assertString(str, sourceCodeInfo);
			return str.trim();
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String) -> String",
			category: "string",
			returns: { type: "string" },
			args: { s: { type: "string" } },
			variants: [{ argumentNames: ["s"] }],
			description: "Returns a new string with leading and trailing whitespaces removed.",
			seeAlso: [
				"string.trimLeft",
				"string.trimRight",
				"isBlank"
			],
			examples: [
				"trim(\"  Albert  \")",
				"trim(\"   \")",
				"trim(\"\")"
			]
		}
	},
	join: {
		evaluate: ([stringList, delimiter], sourceCodeInfo) => {
			assertArray(stringList, sourceCodeInfo);
			for (const str of stringList) assertStringOrNumber(str, sourceCodeInfo);
			assertString(delimiter, sourceCodeInfo);
			return [...stringList].join(delimiter);
		},
		arity: toFixedArity(2),
		docs: {
			type: "((String | Number)[], String) -> String",
			category: "string",
			returns: { type: "string" },
			args: {
				a: { type: "array" },
				b: { type: "string" },
				arr: { type: "array" },
				delimiter: { type: "string" }
			},
			variants: [{ argumentNames: ["arr", "delimiter"] }],
			description: "Returns a new string by concatenating all of the elements in `arr`, separated by `delimiter`.",
			seeAlso: [
				"split",
				"str",
				"++",
				"sequence.interpose"
			],
			examples: [
				"map([0, 1, 2, 3, 4, 5, 6, 7, 8, 9], str) join \", \"",
				"([0, 1, 2, 3, 4, 5, 6, 7, 8, 9] map str) join \", \"",
				"join([\"Albert\", 10], \", \")",
				"join([\"Albert\", \"Mojir\"], \" \")",
				"join(map([0, 1, 2, 3, 4, 5, 6, 7, 8, 9], str), \", \")"
			]
		}
	},
	split: {
		evaluate: ([str, stringOrRegExpValue, limit], sourceCodeInfo) => {
			assertString(str, sourceCodeInfo);
			assertStringOrRegularExpression(stringOrRegExpValue, sourceCodeInfo);
			if (limit !== void 0) assertNumber(limit, sourceCodeInfo, {
				integer: true,
				nonNegative: true
			});
			const delimiter = typeof stringOrRegExpValue === "string" ? stringOrRegExpValue : new RegExp(stringOrRegExpValue.s, stringOrRegExpValue.f);
			return PersistentVector.from(str.split(delimiter, limit));
		},
		arity: {
			min: 2,
			max: 3
		},
		docs: {
			type: "((String, String) -> String[]) & ((String, String, Number) -> String[]) & ((String, Regex) -> String[]) & ((String, Regex, Number) -> String[])",
			category: "string",
			returns: {
				type: "string",
				array: true
			},
			args: {
				a: { type: "string" },
				b: { type: "string" },
				s: { type: "string" },
				delimiter: { type: "string" },
				limit: { type: "integer" }
			},
			variants: [{ argumentNames: ["s", "delimiter"] }, { argumentNames: [
				"s",
				"delimiter",
				"limit"
			] }],
			description: "Divides `s` into an array of substrings. The division is done by searching for `delimiter`. If `limit` as provided, at most `limit` number of substrings are returned.",
			seeAlso: ["join", "string.splitLines"],
			examples: [
				"\"Albert Mojir\" split \" \"",
				"split(\"Albert Mojir\", \" \")",
				"split(\"abcdefghijklmnopqrstuvw\", #\"[aoueiy]\")",
				"split(\"0123456789\", \"\")",
				"split(\"0123456789\", \"\", 5) map number"
			]
		}
	},
	isBlank: {
		evaluate: ([value], sourceCodeInfo) => {
			if (value === null) return true;
			assertString(value, sourceCodeInfo);
			return blankRegexp.test(value);
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String | Null) -> Boolean",
			category: "string",
			returns: { type: "boolean" },
			args: { s: { type: ["string", "null"] } },
			variants: [{ argumentNames: ["s"] }],
			description: "Returns true if `s` is null or only contains whitespace characters.",
			seeAlso: [
				"trim",
				"isEmpty",
				"isString"
			],
			examples: [
				"isBlank(\"\")",
				"isBlank(null)",
				"isBlank(\"\n\")",
				"isBlank(\" \")",
				"isBlank(\".\")"
			]
		}
	}
};
//#endregion
//#region src/builtin/core/functional.ts
const functionalNormalExpression = {
	"|>": {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("|> is implemented in Dvala");
		},
		arity: toFixedArity(2),
		docs: {
			type: "(A, (A) -> B) -> B",
			category: "functional",
			returns: { type: "any" },
			args: {
				a: { type: "any" },
				b: { type: "function" }
			},
			variants: [{ argumentNames: ["a", "b"] }],
			description: "Takes a value `a` and a function `b`, and returns the result of applying `b` to `a`.",
			seeAlso: ["apply", "comp"],
			examples: [`
1 |> inc |> inc`, {
				code: `range(10)
  |> map(_, -> $ ^ 2) // [0, 1, 4, 9, 16, 25, 36, 49, 64, 81]
  |> filter(_, isOdd)  // [1, 9, 25, 49, 81]
  |> reduce(_, +, 0)  // 165
  |> sqrt             // 12.84523257866513
  |> round(_, 2)`,
				noCheck: true
			}]
		}
	},
	apply: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("apply is implemented in Dvala");
		},
		arity: { min: 2 },
		docs: {
			type: "(Function, Unknown[]) -> Unknown",
			category: "functional",
			returns: { type: "any" },
			args: {
				a: { type: "function" },
				b: { type: "array" },
				fun: { type: "function" },
				args: { type: "array" }
			},
			variants: [{ argumentNames: ["fun", "args"] }],
			description: "Call supplied function `fun` with specified arguments `args`.",
			seeAlso: ["|>"],
			examples: [
				`
apply(+, [1, 2, 3])`,
				`
apply(
  (x, y) -> sqrt(x ^ 2 + y ^ 2),
  [3, 4]
)`,
				{
					code: `
(x, y) -> sqrt(x ^ 2 + y ^ 2) apply [3, 4]`,
					noCheck: true
				}
			]
		}
	},
	identity: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("identity is implemented in Dvala");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(A) -> A",
			category: "functional",
			returns: { type: "any" },
			args: { x: { type: "any" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `x`.",
			seeAlso: ["constantly", "functional.fnull"],
			examples: [
				"identity(1)",
				"identity(\"Albert\")",
				"identity({ a: 1 })",
				"identity(null)"
			]
		}
	},
	comp: {
		evaluate: (params, sourceCodeInfo) => {
			for (const param of params) assertFunctionLike(param, sourceCodeInfo);
			return {
				[FUNCTION_SYMBOL]: true,
				sourceCodeInfo,
				functionType: "Comp",
				params,
				arity: params.size > 0 ? getArityFromFunction(params.get(params.size - 1)) : {
					min: 1,
					max: 1
				}
			};
		},
		arity: {},
		docs: {
			type: "((Unknown) -> Unknown, (Unknown) -> Unknown) -> (Unknown) -> Unknown",
			category: "functional",
			returns: { type: "function" },
			args: {
				a: { type: "function" },
				b: { type: "function" },
				fns: {
					type: "function",
					rest: true
				}
			},
			variants: [{ argumentNames: ["fns"] }],
			description: `Takes a variable number of functions and returns a function that is the composition of those.

  The returned function takes a variable number of arguments,
  applies the rightmost function to the args,
  the next function (right-to-left) to the result, etc.`,
			seeAlso: [
				"|>",
				"functional.juxt",
				"functional.complement"
			],
			examples: [{
				code: `
let negativeQuotient = comp(-, /);
negativeQuotient(9, 3)`,
				noCheck: true
			}, {
				code: `
let x = { bar: { foo: 42 } };
comp("foo", "bar")(x)`,
				noCheck: true
			}]
		}
	},
	constantly: {
		evaluate: ([value], sourceCodeInfo) => {
			return {
				[FUNCTION_SYMBOL]: true,
				sourceCodeInfo,
				functionType: "Constantly",
				value: toAny(value),
				arity: {}
			};
		},
		arity: toFixedArity(1),
		docs: {
			type: "(A) -> (...Unknown[]) -> A",
			category: "functional",
			returns: { type: "function" },
			args: { x: { type: "any" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns a function that takes any number of arguments and always returns `x`.",
			seeAlso: ["identity", "functional.fnull"],
			examples: [{
				code: `
let alwaysTrue = constantly(true);
alwaysTrue(9, 3)`,
				noCheck: true
			}]
		}
	}
};
//#endregion
//#region src/docString/generateDocString.ts
function generateDocString(reference) {
	return smartTrim(`
    ${reference.title}

    ${reference.description.replace(/`(.+?)`/g, "$1").replace(/\$(\w+)/g, "$1").replace(/\*\*\*(.+)\*\*\*/g, "$1").replace(/\*\*(.+)\*\*/g, "$1")}

    Signature:
    ${signature(reference).join("\n    ")}

    Arguments:
      ${argStrings(reference).join("\n      ")}

    Examples:
${reference.examples.map((example) => smartTrim(typeof example === "string" ? example : example.code, 4)).join("\n\n")}`);
}
function isEffectRef(ref) {
	return "effect" in ref;
}
function signature(reference) {
	const { title, variants, args, returns } = reference;
	const isOperator = !isEffectRef(reference) && reference._isOperator;
	const functionForms = variants.map((variant) => {
		if (isEffectRef(reference)) return `  perform(@${title}${variant.argumentNames.length > 0 ? `, ${variant.argumentNames.map((argName) => {
			let result = "";
			if (args[argName].rest) result += "...";
			result += argName;
			return result;
		}).join(", ")}` : ""}) -> ${type(returns)}`;
		return `${`  ${title}(${variant.argumentNames.map((argName) => {
			let result = "";
			if (args[argName].rest) result += "...";
			result += argName;
			return result;
		}).join(", ")})`} -> ${type(returns)}`;
	});
	const operatorForm = isOperator ? [
		"",
		"Operator:",
		`  a ${title} b -> ${type(returns)}`
	] : [];
	return [...functionForms, ...operatorForm];
}
function type(arg) {
	const argType = arg.type;
	const typeString = (Array.isArray(argType) ? argType : [argType]).join(" | ");
	return arg.array || arg.rest ? `Array<${typeString}>` : typeString;
}
function argStrings(reference) {
	return Object.entries(reference.args).map(([argName, arg]) => `${argName}: ${type(arg)}`);
}
//#endregion
//#region src/builtin/core/meta.ts
function getMetaNormalExpression(normalExpressionReference, effectReference) {
	return {
		doc: {
			evaluate: ([value], sourceCodeInfo) => {
				assertNonUndefined(normalExpressionReference);
				if (isEffect(value)) {
					const ref = effectReference[`-effect-${value.name}`];
					return ref ? generateDocString(ref) : "";
				}
				assertFunctionLike(value, sourceCodeInfo);
				if (!isDvalaFunction(value)) return "";
				if (value.functionType === "Builtin") {
					const reference = normalExpressionReference[value.name];
					return reference ? generateDocString(reference) : "";
				}
				if (value.functionType === "UserDefined") return value.docString;
				return "";
			},
			arity: toFixedArity(1),
			docs: {
				type: "(Unknown) -> String",
				category: "meta",
				returns: { type: "string" },
				args: { value: { type: ["function", "effect"] } },
				variants: [{ argumentNames: ["value"] }],
				description: "Returns documentation string of the `value`. Works on functions and effects.",
				seeAlso: ["arity", "withDoc"],
				examples: [
					"doc(+)",
					"doc(@dvala.io.print)",
					"let add = ((x, y) -> x + y) withDoc \"Adds two numbers.\";\ndoc(add)"
				]
			}
		},
		withDoc: {
			evaluate: ([fn, docString], sourceCodeInfo) => {
				assertFunctionLike(fn, sourceCodeInfo);
				assertString(docString, sourceCodeInfo);
				if (!isDvalaFunction(fn) || fn.functionType !== "UserDefined") throw new Error("withDoc can only be used with user-defined functions");
				return {
					...fn,
					[FUNCTION_SYMBOL]: true,
					docString
				};
			},
			arity: toFixedArity(2),
			docs: {
				type: "(Function, String) -> Function",
				category: "meta",
				returns: { type: "function" },
				args: {
					a: { type: "function" },
					b: { type: "string" }
				},
				variants: [{ argumentNames: ["a", "b"] }],
				description: "Returns a new function with the documentation string `b` attached. The original function is not modified.",
				seeAlso: ["doc"],
				examples: ["((x, y) -> x + y) withDoc \"Adds two numbers.\"", "let add = (x, y) -> x + y;\nadd withDoc \"Adds x and y.\""]
			}
		},
		arity: {
			evaluate: ([value], sourceCodeInfo) => {
				function arityToMap(a) {
					let m = PersistentMap.empty();
					if (a.min !== void 0) m = m.assoc("min", a.min);
					if (a.max !== void 0) m = m.assoc("max", a.max);
					return m;
				}
				if (isEffect(value)) {
					const ref = effectReference[`-effect-${value.name}`];
					if (!ref) return PersistentMap.empty();
					const argCounts = ref.variants.map((v) => v.argumentNames.length);
					return arityToMap({
						min: Math.min(...argCounts),
						max: Math.max(...argCounts)
					});
				}
				assertFunctionLike(value, sourceCodeInfo);
				return arityToMap(isDvalaFunction(value) ? value.arity : toFixedArity(1));
			},
			arity: toFixedArity(1),
			docs: {
				type: "(Unknown) -> {min: Number, max: Number}",
				category: "meta",
				returns: { type: "object" },
				args: { value: { type: ["function", "effect"] } },
				variants: [{ argumentNames: ["value"] }],
				description: "Returns arity of the `value`. The arity is an object with the properties: `min` and `max`. If the function has fixed arity, `min` and `max` are equal to the number of required parameters. If no restrictions apply, empty object is returned. Also works on effects.",
				seeAlso: ["doc"],
				examples: [
					"arity(+)",
					"arity(map)",
					{
						code: "arity(@dvala.random.int)",
						noCheck: true
					},
					`
let add = (x, y = 0) -> do
  x + y;
end;

arity(add)`,
					`
let foo = (k, ...x) -> do
  k + x;
end;
  arity(foo)`
				]
			}
		}
	};
}
//#endregion
//#region src/builtin/normalExpressions/index.ts
const normalExpressionReference = {};
const effectReference = {};
function setNormalExpressionReference(reference) {
	Object.assign(normalExpressionReference, reference);
}
function setEffectReference(reference) {
	Object.assign(effectReference, reference);
}
const expressions = {
	...assertionNormalExpression,
	...bitwiseNormalExpression,
	...collectionNormalExpression,
	...arrayNormalExpression,
	...sequenceNormalExpression,
	...mathNormalExpression,
	...getMetaNormalExpression(normalExpressionReference, effectReference),
	...miscNormalExpression,
	...objectNormalExpression,
	...predicatesNormalExpression,
	...regexpNormalExpression,
	...stringNormalExpression,
	...functionalNormalExpression
};
Object.entries(expressions).forEach(([name, expression]) => {
	expression.name = name;
});
const normalExpressions = { ...expressions };
const normalExpressionTypes = new Set(Object.keys(normalExpressions));
const andSpecialExpression = {
	arity: {},
	docs: {
		category: "special-expression",
		returns: { type: "boolean" },
		args: {
			a: { type: "boolean" },
			b: { type: "boolean" },
			c: {
				type: "boolean",
				rest: true
			}
		},
		variants: [{ argumentNames: ["a", "b"] }, { argumentNames: [
			"a",
			"b",
			"c"
		] }],
		description: `
Computes logical \`and\`. Evaluation starts from left. As soon as an
operand evaluates to \`false\`, the result is \`false\`; otherwise the
result is \`true\`. Every operand must be \`Boolean\`.`,
		seeAlso: ["||", "!"],
		examples: [
			"true && false",
			"&&(true, true)",
			"&&(3 > 2, 4 > 2)",
			"&&(3 < 2, 4 < 2)",
			"&&(true, true, true, true)",
			"&&(true, true, false, true)"
		]
	},
	evaluateAsNormalExpression: (params, sourceCodeInfo) => {
		let value = true;
		for (const param of params) {
			value = asAny(param, sourceCodeInfo);
			if (!value) break;
		}
		return value;
	}
};
//#endregion
//#region src/builtin/bindingNode.ts
function walkDefaults(bindingTarget, onDefault) {
	if (bindingTarget[0] === bindingTargetTypes.object) bindingTarget[1][0].forEach((entry) => {
		const element = entry.target;
		if (element[1][1]) onDefault(element[1][1]);
		walkDefaults(element, onDefault);
	});
	else if (bindingTarget[0] === bindingTargetTypes.array) for (let index = 0; index < bindingTarget[1][0].length; index += 1) {
		const element = bindingTarget[1][0][index] ?? null;
		/* v8 ignore next 3 */
		if (element === null) continue;
		if (element[1][1]) onDefault(element[1][1]);
		walkDefaults(element, onDefault);
	}
}
function getAllBindingTargetNames(bindingTarget) {
	const names = {};
	getNamesFromBindingTarget(bindingTarget, names);
	return names;
}
function getNamesFromBindingTarget(target, names) {
	if (target === null) return;
	if (target[0] === bindingTargetTypes.array) for (const element of target[1][0]) getNamesFromBindingTarget(element, names);
	else if (target[0] === bindingTargetTypes.object) for (const entry of target[1][0]) getNamesFromBindingTarget(entry.target, names);
	else if (target[0] === bindingTargetTypes.rest) {
		if (names[target[1][0]]) throw new RuntimeError(`Duplicate binding name: ${target[1][0]}`, void 0);
		names[target[1][0]] = true;
	} else if (target[0] === bindingTargetTypes.symbol) {
		if (names[target[1][0][1]]) throw new RuntimeError(`Duplicate binding name: ${target[1][0]}`, void 0);
		names[target[1][0][1]] = true;
	}
}
const matchSpecialExpression = {
	arity: {},
	docs: {
		category: "special-expression",
		customVariants: ["match value match-branch match-branch ... end"],
		details: [
			[
				"value",
				"any",
				"The value to match against patterns."
			],
			[
				"match-branch",
				"case pattern [when guard] then body",
				"A branch of the match expression."
			],
			[
				"pattern",
				"pattern",
				"A pattern to match: literal, variable, array destructuring, object destructuring, or wildcard (_)."
			],
			[
				"guard",
				"expression",
				"An optional guard expression that must be truthy for the match to succeed."
			],
			[
				"body",
				"expressions",
				"The expressions to evaluate if the pattern matches."
			]
		],
		description: "Pattern matching expression. Matches `value` against each `pattern` sequentially. If a pattern matches (and the optional `when` guard is truthy), the corresponding `body` is evaluated and its result returned. Bound variables from the pattern are available in the guard and body. If no pattern matches, `null` is returned.",
		examples: [
			`
let day = if true then 1 else 2 end;
match day
  case 1 then "One"
  case 2 then "Two"
end`,
			{
				code: `
match [1, 2, 3]
  case [x] then "one element"
  case [x, y] then "two elements"
  case [x, ...xs] then "first: " ++ str(x) ++ " rest: " ++ str(xs)
end`,
				noCheck: true
			},
			{
				code: `
match { type: "click", x: 10, y: 20 }
  case { type: "click", x, y } then "Click at " ++ str(x) ++ ", " ++ str(y)
  case { type: "keydown", key } then "Key: " ++ key
  case _ then "unknown event"
end`,
				noCheck: true
			},
			`
let user = if true then { role: "admin", name: "Alice" } else { role: "user", name: "Bob" } end;
match user
  case { role: "admin", name } then "Admin: " ++ name
  case { role, name } when role == "user" then "User: " ++ name
  case _ then "Unknown role"
end`
		]
	},
	getUndefinedSymbols: (node, contextStack, { getUndefinedSymbols, builtin }) => {
		const result = /* @__PURE__ */ new Set();
		const [matchValue, cases] = node[1];
		getUndefinedSymbols([matchValue], contextStack, builtin).forEach((s) => result.add(s));
		for (const [pattern, body, guard] of cases) {
			const newContext = {};
			Object.assign(newContext, getAllBindingTargetNames(pattern));
			const caseContextStack = contextStack.create(newContext);
			if (guard) getUndefinedSymbols([guard], caseContextStack, builtin).forEach((s) => result.add(s));
			getUndefinedSymbols([body], caseContextStack, builtin).forEach((s) => result.add(s));
		}
		return result;
	}
};
//#endregion
//#region src/builtin/specialExpressions/functions.ts
const lambdaSpecialExpression = { arity: {} };
function getFunctionUnresolvedSymbols(fn, contextStack, getUndefinedSymbols, builtin) {
	const result = /* @__PURE__ */ new Set();
	const newContext = { self: { value: null } };
	fn[0].forEach((arg) => {
		Object.assign(newContext, getAllBindingTargetNames(arg));
		walkDefaults(arg, (defaultNode) => {
			addToSet(result, getUndefinedSymbols([defaultNode], contextStack, builtin));
		});
	});
	const newContextStack = contextStack.create(newContext);
	addToSet(result, getUndefinedSymbols(fn[1], newContextStack, builtin));
	return result;
}
const letSpecialExpression = {
	arity: toFixedArity(0),
	docs: {
		category: "special-expression",
		customVariants: ["let s = value;"],
		details: [[
			"s",
			"symbol",
			"The name of the variable to bind."
		], [
			"value",
			"any",
			"The value to bind to the variable."
		]],
		description: `
  Binds local variables s to \`value\`. \`value\` can be any expression. The scope of the variables is the body of the let expression.`,
		examples: [`
let a = 1 + 2 + 3 + 4;
let b = -> $ * ( $ + 1 );
b(a)`]
	}
};
const loopSpecialExpression = {
	arity: {},
	docs: {
		category: "special-expression",
		customVariants: ["loop (bindings) -> body"],
		details: [[
			"bindings",
			"binding pairs",
			"Comma-separated bindings with initial values, e.g. `n = 10, acc = 0`."
		], [
			"body",
			"expression",
			"The expression to evaluate repeatedly. Use `recur` to loop back with new values."
		]],
		description: `Creates a loop with initial bindings. Use \`recur\` inside the body to jump back to the loop head with new binding values.

If \`recur\` is not called, the loop terminates and returns the value of the body expression.`,
		examples: [`loop (n = 10, acc = 0) -> do
  if n == 0 then
    acc
  else
    recur(n - 1, acc + n)
  end
end`, `loop (n = 5, acc = 1) -> do
  if n <= 1 then
    acc
  else
    recur(n - 1, acc * n)
  end
end`]
	},
	getUndefinedSymbols: (node, contextStack, { getUndefinedSymbols, builtin }) => {
		const [bindings, body] = node[1];
		const newContext = bindings.reduce((context, [target]) => {
			const names = getAllBindingTargetNames(target);
			Object.keys(names).forEach((name) => {
				context[name] = { value: true };
			});
			return context;
		}, {});
		return joinSets(getUndefinedSymbols(bindings.map(([, value]) => value), contextStack, builtin), getUndefinedSymbols([body], contextStack.create(newContext), builtin));
	}
};
//#endregion
//#region src/builtin/specialExpressions/loops.ts
function analyze(loopNode, contextStack, getUndefinedSymbols, builtin) {
	const result = /* @__PURE__ */ new Set();
	const newContext = {};
	const [loopBindings, body] = loopNode[1];
	loopBindings.forEach((loopBindingNode) => {
		const [binding, letBindings, whenNode, whileNode] = loopBindingNode;
		const [target, value] = binding;
		getUndefinedSymbols([value], contextStack.create(newContext), builtin).forEach((symbol) => result.add(symbol));
		Object.assign(newContext, getAllBindingTargetNames(target));
		/* v8 ignore next */
		if (letBindings) letBindings.forEach((letBinding) => {
			const [letTarget, letValue] = letBinding;
			getUndefinedSymbols([letValue], contextStack.create(newContext), builtin).forEach((symbol) => result.add(symbol));
			Object.assign(newContext, getAllBindingTargetNames(letTarget));
		});
		if (whenNode) getUndefinedSymbols([whenNode], contextStack.create(newContext), builtin).forEach((symbol) => result.add(symbol));
		if (whileNode) getUndefinedSymbols([whileNode], contextStack.create(newContext), builtin).forEach((symbol) => result.add(symbol));
	});
	getUndefinedSymbols([body], contextStack.create(newContext), builtin).forEach((symbol) => result.add(symbol));
	return result;
}
const forSpecialExpression = {
	arity: toFixedArity(1),
	docs: {
		category: "special-expression",
		customVariants: ["for (...binding) -> body"],
		details: [
			[
				"binding",
				"loop-var in collection [...let-binding] [where whereExpr] [while whileExp]",
				"A for loop binding"
			],
			[
				"loop-var",
				"symbol",
				"The name of the loop variable."
			],
			[
				"collection",
				"any",
				"The collection to iterate over."
			],
			[
				"let-binding",
				"let binding",
				"A let binding to create a local variable."
			],
			[
				"whereExpr",
				"expression",
				"An expression that must evaluate to truthy for the loop body to be executed."
			],
			[
				"whileExp",
				"expression",
				"An expression that must evaluate to truthy for the loop to continue."
			],
			[
				"body",
				"expressions",
				"The expressions to evaluate for each iteration of the loop."
			]
		],
		returns: {
			type: "any",
			array: true
		},
		description: "Iterates over `bindings`, evaluates `body` for each `binding` and returns an `array` of results.",
		examples: [`
for (i in [1, 2, 3]) -> i * 2
      `, `
for (
  i in range(10) let ii = i ^ 2 while ii < 40 when ii % 3 == 0,
  j in range(10) when j % 2 == 1
) -> ii + j
      `]
	},
	getUndefinedSymbols: (node, contextStack, { getUndefinedSymbols, builtin }) => analyze(node, contextStack, getUndefinedSymbols, builtin)
};
const orSpecialExpression = {
	arity: {},
	docs: {
		category: "special-expression",
		returns: { type: "boolean" },
		args: {
			a: { type: "boolean" },
			b: { type: "boolean" },
			c: {
				type: "boolean",
				rest: true
			}
		},
		variants: [{ argumentNames: ["a", "b"] }, { argumentNames: [
			"a",
			"b",
			"c"
		] }],
		description: `
  Computes logical \`or\`. Evaluation starts from left. As soon as an
  operand evaluates to \`true\`, the result is \`true\`; otherwise the
  result is \`false\`. Every operand must be \`Boolean\`.`,
		seeAlso: ["&&", "!"],
		examples: [
			"false || true",
			"||(true, true)",
			"||(3 > 2, 4 > 2)",
			"||(3 < 2, 4 < 2)",
			"||(false, false, false, true)",
			"||(true, false, true, false)"
		]
	},
	evaluateAsNormalExpression: (params, sourceCodeInfo) => {
		let value = false;
		for (const param of params) {
			value = asAny(param, sourceCodeInfo);
			/* v8 ignore next 2 */
			if (value) break;
		}
		return value;
	}
};
const qqSpecialExpression = {
	arity: { min: 1 },
	docs: {
		category: "special-expression",
		returns: { type: "any" },
		args: {
			a: { type: "any" },
			b: { type: "any" },
			c: {
				type: "any",
				rest: true
			}
		},
		variants: [
			{ argumentNames: ["a"] },
			{ argumentNames: ["a", "b"] },
			{ argumentNames: [
				"a",
				"b",
				"c"
			] }
		],
		description: `Nullish coalescing operator. Returns the first non-\`null\` value.

Evaluation is short-circuited — as soon as a non-\`null\` value is found, the remaining expressions are not evaluated.

If all values are \`null\`, returns \`null\`.`,
		examples: [
			"1 ?? 2",
			"null ?? 2",
			"??(null)",
			"??(null, \"default\")",
			"??(1, \"default\")",
			"false ?? \"default\"",
			"??(null, null, 3)"
		]
	},
	evaluateAsNormalExpression: (params, sourceCodeInfo) => {
		for (const param of params) {
			const value = asAny(param, sourceCodeInfo);
			if (value !== null) return value;
		}
		return null;
	}
};
const recurSpecialExpression = {
	arity: {},
	docs: {
		category: "special-expression",
		customVariants: ["recur(...recur-args)"],
		description: "Recursevly calls enclosing function or loop with its evaluated `recur-args`.",
		examples: [
			`
let foo = (n) -> do
  if isZero(n) then
    0
  else
    n + recur(n - 1)
  end
end;
foo(3)`,
			`
((n) -> do
  if isZero(n) then
    0
  else
    n + recur(n - 1)
  end
end)(3)`,
			`
loop (n = 3, acc = 0) -> do
  if isZero(n) then
    acc
  else
    recur(n - 1, acc + n)
  end
end`
		]
	},
	evaluateAsNormalExpression: (params) => {
		throw new RecurSignal(params);
	}
};
const arraySpecialExpression = {
	arity: {},
	docs: {
		category: "special-expression",
		returns: {
			type: "any",
			array: true
		},
		args: { values: {
			type: "any",
			rest: true
		} },
		variants: [{ argumentNames: ["values"] }],
		description: "Makes new array from `values`.",
		examples: [
			"array(1, 2, 3)",
			"array(array(null, false, true))",
			"[]",
			"[1, 2, 3]",
			"[1, 2, ...[3, 4, 5], 6]",
			"[[null, false, true]]",
			"[1, 2, 3][1]"
		],
		hideOperatorForm: true
	},
	evaluateAsNormalExpression: (params, sourceCodeInfo) => {
		let result = PersistentVector.empty();
		for (const param of params) result = result.append(asAny(param, sourceCodeInfo));
		return result;
	}
};
const effectSpecialExpression = {
	arity: {},
	docs: {
		category: "special-expression",
		customVariants: ["@name"],
		details: [[
			"name",
			"dotted identifier",
			"The effect name, e.g. `llm.complete` or `dvala.io.print`."
		]],
		description: "Returns the unique effect reference for the given name. Calling `effect` with the same name always returns the same reference. Effect references are first-class values that can be stored, passed, and compared with `==`.",
		examples: ["@dvala.io.print", "==(@llm.complete, @llm.complete)"],
		seeAlso: ["isEffect"]
	}
};
const objectSpecialExpression = {
	arity: {},
	docs: {
		category: "special-expression",
		returns: { type: "object" },
		args: { kvps: {
			type: "any",
			rest: true,
			description: "key - value pairs, where key is a string"
		} },
		variants: [{ argumentNames: ["kvps"] }],
		description: "Constructs a new object. Object members are created from the `kvps` key-value pairs. Requires an even number of arguments.",
		examples: [
			"object()",
			`
let default = {
  type: "Person",
  name: "John Doe",
  age: 42
};

{
  ...default,
  name: "Lisa"
}`,
			"object(\"x\", 10, \"y\", true, \"z\", \"A string\")",
			"{}",
			"{ a: 1, b: 2 }",
			"let x = 10; let y = 20; { x, y }"
		],
		hideOperatorForm: true
	},
	evaluateAsNormalExpression: (params, sourceCodeInfo) => {
		let result = PersistentMap.empty();
		for (let i = 0; i < params.size; i += 2) {
			const key = params.get(i);
			const value = params.get(i + 1);
			assertString(key, sourceCodeInfo);
			result = result.assoc(key, value ?? null);
		}
		return result;
	}
};
const importSpecialExpression = {
	arity: toFixedArity(1),
	docs: {
		category: "special-expression",
		description: "Imports all functions from a module as an object. Use destructuring to pick specific functions.",
		customVariants: ["import(module-name)"],
		returns: { type: "object" },
		examples: [
			"let v = import(\"vector\"); v.stdev([1, 2, 3, 4])",
			"let { linspace } = import(\"vector\"); linspace(0, 10, 5)",
			"let g = import(\"grid\"); g.row([[1, 2], [3, 4]], 0)"
		]
	}
};
const parallelSpecialExpression = {
	arity: {
		min: 1,
		max: 1
	},
	docs: {
		category: "special-expression",
		customVariants: ["parallel(fns)"],
		details: [[
			"fns",
			"array",
			"Array of zero-argument functions to evaluate concurrently. Typically `[-> perform(...), -> perform(...)]`."
		]],
		description: "Evaluates all branch functions concurrently and returns an array of results in order. Each branch runs as an independent trampoline invocation. If any branch errors, throws the first error (fail-fast). If any branch suspends, the entire `parallel` suspends with a composite blob. Only available in async mode (`run()`). Requires at least one branch.",
		examples: [{
			code: "parallel([-> 1 + 2, -> 3 + 4])",
			noRun: true
		}]
	}
};
const performSpecialExpression = {
	arity: {
		min: 1,
		max: 2
	},
	docs: {
		category: "special-expression",
		customVariants: ["perform(eff)", "perform(eff, payload)"],
		details: [[
			"eff",
			"expression",
			"An expression evaluating to an effect value (from `@name`)."
		], [
			"payload",
			"expression",
			"Optional single payload value passed to the effect handler. Defaults to `null` if omitted."
		]],
		description: "Invokes an effect. The nearest enclosing `with handler` matching the effect intercepts the call. The handler clause receives the payload via its parameter and can `resume(value)` to continue the body or return a value to abort. If no local handler matches, the effect is dispatched to the host.",
		examples: [`
do
  with handler @dvala.io.print(arg) -> resume(arg) end;
  perform(@dvala.io.print, "hello")
end
`],
		seeAlso: ["isEffect", "raise"]
	}
};
const raceSpecialExpression = {
	arity: {
		min: 1,
		max: 1
	},
	docs: {
		category: "special-expression",
		customVariants: ["race(fns)"],
		details: [[
			"fns",
			"array",
			"Array of zero-argument functions to race concurrently. Typically `[-> perform(...), -> perform(...)]`."
		]],
		description: "Races all branch functions concurrently. The first branch to complete wins — its value becomes the result. Losing branches are cancelled via AbortSignal. Errored branches are silently dropped. If all branches error, throws an aggregate error. If no branch completes but some suspend, the race suspends. Only available in async mode (`run()`). Requires at least one branch.",
		examples: [{
			code: "race([-> perform(@a.get), -> perform(@b.get)])",
			noRun: true
		}]
	}
};
const settledSpecialExpression = {
	arity: {
		min: 1,
		max: 1
	},
	docs: {
		category: "special-expression",
		customVariants: ["settled(fns)"],
		details: [[
			"fns",
			"array",
			"Array of zero-argument functions to evaluate concurrently. Each result is wrapped as `[:ok, value]` on success or `[:error, errorPayload]` on error."
		]],
		description: "Evaluates all branch functions concurrently and returns an array of tagged results in order. Each result is `[:ok, value]` if the branch succeeded or `[:error, errorPayload]` if it raised an error. Never throws — all errors are captured as results. If any branch suspends, the entire `settled` suspends with a composite blob. Only available in async mode (`run()`). Requires at least one branch.",
		examples: [{
			code: "settled([-> perform(@host.fetch, url1), -> perform(@host.fetch, url2)])",
			noRun: true
		}]
	}
};
//#endregion
//#region src/builtin/index.ts
const specialExpressions = {
	[specialExpressionTypes["??"]]: qqSpecialExpression,
	[specialExpressionTypes["&&"]]: andSpecialExpression,
	[specialExpressionTypes["||"]]: orSpecialExpression,
	[specialExpressionTypes.array]: arraySpecialExpression,
	[specialExpressionTypes["function"]]: lambdaSpecialExpression,
	[specialExpressionTypes.for]: forSpecialExpression,
	[specialExpressionTypes.let]: letSpecialExpression,
	[specialExpressionTypes.loop]: loopSpecialExpression,
	[specialExpressionTypes.object]: objectSpecialExpression,
	[specialExpressionTypes.recur]: recurSpecialExpression,
	[specialExpressionTypes.match]: matchSpecialExpression,
	[specialExpressionTypes.import]: importSpecialExpression,
	[specialExpressionTypes.effect]: effectSpecialExpression,
	[specialExpressionTypes.perform]: performSpecialExpression,
	[specialExpressionTypes.parallel]: parallelSpecialExpression,
	[specialExpressionTypes.race]: raceSpecialExpression,
	[specialExpressionTypes.settled]: settledSpecialExpression
};
const builtin = {
	normalExpressions,
	specialExpressions
};
const normalExpressionKeys = Object.keys(normalExpressions);
const specialExpressionKeys = Object.keys(specialExpressionTypes);
//#endregion
//#region src/evaluator/interface.ts
function isContextEntry(value) {
	return isUnknownRecord(value) && value.value !== void 0;
}
//#endregion
//#region src/evaluator/ContextStack.ts
var ContextStackImpl = class ContextStackImpl {
	_contexts;
	globalContext;
	modules;
	valueModules;
	pure;
	sourceMap;
	fileResolver;
	/** Directory of the currently evaluating file — used to resolve relative imports */
	currentFileDir;
	/** Node ID allocator — shared with the parser so runtime imports get unique IDs */
	allocateNodeId;
	/** Whether debug mode (source map building) is active */
	debug;
	/**
	* Whether `.dvala` coverage collection is active. When true, builtin module
	* sources are parsed with source maps (and merged) so module nodes are
	* attributable — gated separately from `debug` so ordinary debug runs (e.g.
	* runTestFile measuring a user project) don't pay the cost or leak builtins.
	*/
	coverage;
	/** Host-supplied capability to compile source → Ast (severs runtime → parser). */
	parseSource;
	/** Host-supplied capability to format AST → source for the `ast/prettyPrint` builtin. */
	prettyPrint;
	_resolvingFiles;
	constructor({ contexts, modules, valueModules, pure, sourceMap, fileResolver, currentFileDir, resolvingFiles, allocateNodeId, debug, coverage, parseSource, prettyPrint }) {
		this.globalContext = asNonUndefined(contexts[0]);
		this._contexts = contexts;
		this.modules = modules ?? /* @__PURE__ */ new Map();
		this.valueModules = valueModules ?? /* @__PURE__ */ new Map();
		this.pure = pure ?? false;
		this.sourceMap = sourceMap;
		this.fileResolver = fileResolver;
		this.currentFileDir = currentFileDir ?? ".";
		this._resolvingFiles = resolvingFiles ?? /* @__PURE__ */ new Set();
		this.allocateNodeId = allocateNodeId;
		this.debug = debug ?? false;
		this.coverage = coverage ?? false;
		this.parseSource = parseSource;
		this.prettyPrint = prettyPrint;
	}
	resolve(nodeId) {
		return resolveSourceCodeInfo(nodeId, this.sourceMap);
	}
	/** Get the raw context chain for serialization. */
	getContextsRaw() {
		return this._contexts;
	}
	/** Get the top-level module scope as plain key→value bindings. */
	getModuleScopeBindings() {
		const scope = this._contexts[0];
		const result = {};
		for (const [k, v] of Object.entries(scope)) result[k] = v.value;
		return result;
	}
	/**
	* Find the index of globalContext in the _contexts array.
	* Returns -1 if not found (should not happen in valid state).
	*/
	getGlobalContextIndex() {
		return this._contexts.indexOf(this.globalContext);
	}
	/**
	* Create a ContextStack from deserialized data.
	* `contexts` is the restored context chain (already resolved).
	* `globalContextIndex` identifies which element is the globalContext.
	*/
	/* v8 ignore next 15 */
	static fromDeserialized(params) {
		const cs = new ContextStackImpl({
			contexts: params.contexts,
			modules: params.modules,
			pure: params.pure
		});
		if (params.globalContextIndex >= 0 && params.globalContextIndex < params.contexts.length) cs.globalContext = params.contexts[params.globalContextIndex];
		return cs;
	}
	/**
	* Replace the contexts array and globalContext. Used during deserialization
	* to fill in resolved context data after circular references are handled.
	*/
	/* v8 ignore next 6 */
	setContextsFromDeserialized(contexts, globalContextIndex) {
		this._contexts = contexts;
		if (globalContextIndex >= 0 && globalContextIndex < contexts.length) this.globalContext = contexts[globalContextIndex];
	}
	getModule(name) {
		return this.modules.get(name);
	}
	getValueModule(name) {
		if (this.valueModules.has(name)) return {
			value: this.valueModules.get(name),
			found: true
		};
		return {
			value: void 0,
			found: false
		};
	}
	registerValueModule(name, value) {
		this.valueModules.set(name, value);
	}
	isResolvingFile(path) {
		return this._resolvingFiles.has(path);
	}
	markFileResolving(path) {
		this._resolvingFiles.add(path);
	}
	unmarkFileResolving(path) {
		this._resolvingFiles.delete(path);
	}
	create(context) {
		const globalContext = this.globalContext;
		const contextStack = new ContextStackImpl({
			contexts: [context, ...this._contexts],
			modules: this.modules,
			valueModules: this.valueModules,
			pure: this.pure,
			sourceMap: this.sourceMap,
			fileResolver: this.fileResolver,
			currentFileDir: this.currentFileDir,
			resolvingFiles: this._resolvingFiles,
			allocateNodeId: this.allocateNodeId,
			debug: this.debug,
			coverage: this.coverage,
			parseSource: this.parseSource,
			prettyPrint: this.prettyPrint
		});
		contextStack.globalContext = globalContext;
		return contextStack;
	}
	/**
	* Create a new ContextStack that shares all outer scopes but has an
	* independent shallow copy of the innermost context (`_contexts[0]`).
	*
	* Used for multi-shot continuations: `addValues` only mutates `_contexts[0]`,
	* so each resume call must get its own copy of that context. Without this,
	* the second resume would see bindings added by the first resume and throw
	* "Cannot redefine value".
	*/
	withCopiedTopContext() {
		const cs = new ContextStackImpl({
			contexts: [{ ...this._contexts[0] }, ...this._contexts.slice(1)],
			modules: this.modules,
			valueModules: this.valueModules,
			pure: this.pure,
			sourceMap: this.sourceMap
		});
		cs.globalContext = this.globalContext;
		return cs;
	}
	new(context) {
		return new ContextStackImpl({
			contexts: [{}, context],
			modules: this.modules,
			valueModules: this.valueModules,
			pure: this.pure,
			sourceMap: this.sourceMap
		});
	}
	addValues(values, sourceCodeInfo) {
		const currentContext = this._contexts[0];
		for (const [name, value] of Object.entries(values)) {
			if (currentContext[name]) throw new TypeError$1(`Cannot redefine value "${name}"`, sourceCodeInfo);
			if (specialExpressionKeys.includes(name)) throw new TypeError$1(`Cannot shadow special expression "${name}"`, sourceCodeInfo);
			currentContext[name] = { value: toAny(value) };
		}
	}
	getValue(name) {
		for (const context of this._contexts) {
			const contextEntry = context[name];
			if (contextEntry) return contextEntry.value;
		}
	}
	lookUp(node) {
		return this.lookUpByName(node[1]);
	}
	lookUpByName(name) {
		for (const context of this._contexts) {
			const contextEntry = context[name];
			if (contextEntry) return contextEntry;
		}
		return null;
	}
	evaluateSymbol(node) {
		if (isSpecialSymbolNode(node)) {
			const functionType = node[1];
			switch (functionType) {
				case specialExpressionTypes["&&"]:
				case specialExpressionTypes["||"]:
				case specialExpressionTypes.array:
				case specialExpressionTypes.object:
				case specialExpressionTypes.recur:
				case specialExpressionTypes["??"]: {
					const specialExpression = asNonUndefined(builtin.specialExpressions[functionType], this.resolve(node[2]));
					return {
						[FUNCTION_SYMBOL]: true,
						functionType: "SpecialBuiltin",
						specialBuiltinSymbolType: functionType,
						sourceCodeInfo: this.resolve(node[2]),
						arity: specialExpression.arity
					};
				}
				default: throw new TypeError$1(`Unknown special builtin symbol type: ${functionType}`, this.resolve(node[2]));
			}
		}
		if (isBuiltinSymbolNode(node)) {
			const name = node[1];
			const normalExpression = normalExpressions[name];
			const userValue = this.lookUpByName(name);
			if (isContextEntry(userValue)) return userValue.value;
			return {
				[FUNCTION_SYMBOL]: true,
				functionType: "Builtin",
				normalBuiltinSymbolType: name,
				sourceCodeInfo: this.resolve(node[2]),
				arity: normalExpression.arity,
				name
			};
		}
		const lookUpResult = this.lookUp(node);
		if (isContextEntry(lookUpResult)) return lookUpResult.value;
		throw new ReferenceError(node[1], this.resolve(node[2]));
	}
};
function createContextStack(options = {}) {
	const globalContext = options.globalContext ?? {};
	const contextStack = new ContextStackImpl({
		contexts: options.contexts ? [globalContext, ...options.contexts] : [globalContext],
		modules: options.modules,
		pure: options.pure,
		sourceMap: options.sourceMap,
		fileResolver: options.fileResolver,
		currentFileDir: options.currentFileDir,
		allocateNodeId: options.allocateNodeId,
		debug: options.debug,
		coverage: options.coverage,
		parseSource: options.parseSource,
		prettyPrint: options.prettyPrint
	});
	return options.globalModuleScope ? contextStack : contextStack.create({});
}
//#endregion
//#region src/builtin/bindingSlot.ts
/**
* Validate that the root value matches the expected binding target structure.
*
* For array destructuring (`let [a, b] = value`), value must be an array.
* For object destructuring (`let {a, b} = value`), value must be an object.
* For symbol binding (`let x = value`), no validation needed.
*/
function validateBindingRootType(target, value, sourceCodeInfo) {
	switch (target[0]) {
		case bindingTargetTypes.array:
			assertArray(value, sourceCodeInfo);
			break;
		case bindingTargetTypes.object:
			assertObj(value, sourceCodeInfo);
			break;
	}
}
/**
* Flatten a binding pattern into a linear list of slots.
*
* Example: `{a = 1, b: {c = 2}}`
* Produces: [
*   { name: 'a', path: [{type: 'key', key: 'a'}], defaultNode: <1> },
*   { name: 'c', path: [{type: 'key', key: 'b'}, {type: 'key', key: 'c'}], defaultNode: <2> }
* ]
*/
function flattenBindingPattern(target) {
	const slots = [];
	flattenTarget(target, [], slots);
	return slots;
}
function flattenTarget(target, path, slots) {
	const nodeId = target[2];
	switch (target[0]) {
		case bindingTargetTypes.symbol: {
			const symbolNode = target[1][0];
			const defaultNode = target[1][1];
			slots.push({
				name: symbolNode[1],
				path: [...path],
				defaultNode,
				nodeId
			});
			break;
		}
		case bindingTargetTypes.rest: {
			const name = target[1][0];
			const defaultNode = target[1][1];
			slots.push({
				name,
				path: [...path],
				defaultNode,
				isRest: true,
				nodeId
			});
			break;
		}
		case bindingTargetTypes.object: {
			const entries = target[1][0];
			const capturedKeys = /* @__PURE__ */ new Set();
			for (const { key, target: element } of entries) {
				if (element[0] === bindingTargetTypes.rest) continue;
				capturedKeys.add(key);
				const newPath = [...path, {
					type: "key",
					key
				}];
				const hasDefault = element[1][1] !== void 0;
				const isCompound = element[0] === bindingTargetTypes.object || element[0] === bindingTargetTypes.array;
				if (hasDefault && isCompound) slots.push({
					name: `_intermediate_${slots.length}`,
					path: newPath,
					defaultNode: element[1][1],
					nestedTarget: element,
					nodeId: element[2]
				});
				else flattenTarget(element, newPath, slots);
			}
			for (const { target: element } of entries) if (element[0] === bindingTargetTypes.rest) {
				const name = element[1][0];
				slots.push({
					name,
					path: [...path],
					isRest: true,
					restKeys: capturedKeys,
					nodeId: element[2]
				});
				break;
			}
			break;
		}
		case bindingTargetTypes.array: {
			const elements = target[1][0];
			for (let i = 0; i < elements.length; i++) {
				const element = elements[i];
				if (element === null || element === void 0) continue;
				if (element[0] === bindingTargetTypes.rest) {
					const name = element[1][0];
					slots.push({
						name,
						path: [...path],
						isRest: true,
						restIndex: i,
						nodeId: element[2]
					});
					break;
				}
				const newPath = [...path, {
					type: "index",
					index: i
				}];
				const hasDefault = element[1][1] !== void 0;
				const isCompound = element[0] === bindingTargetTypes.object || element[0] === bindingTargetTypes.array;
				if (hasDefault && isCompound) slots.push({
					name: `_intermediate_${slots.length}`,
					path: newPath,
					defaultNode: element[1][1],
					nestedTarget: element,
					nodeId: element[2]
				});
				else flattenTarget(element, newPath, slots);
			}
			break;
		}
	}
}
/**
* Extract a value from a nested structure by following a path.
*
* Returns undefined if the path cannot be followed (missing key/index).
*/
function extractValueByPath(rootValue, path, sourceCodeInfo) {
	let current = rootValue;
	for (const step of path) {
		if (current === null || current === void 0) return;
		if (step.type === "key") {
			assertObj(current, sourceCodeInfo);
			current = current.get(step.key);
		} else {
			assertArray(current, sourceCodeInfo);
			current = current.get(step.index);
		}
	}
	return current;
}
/**
* Extract rest values for an object rest binding.
* Returns an object with all keys except those in restKeys.
*/
function extractObjectRest(value, restKeys, sourceCodeInfo) {
	assertObj(value, sourceCodeInfo);
	let result = PersistentMap.empty();
	for (const [key, val] of value) if (!restKeys.has(key)) result = result.assoc(key, val);
	return result;
}
/**
* Extract rest values for an array rest binding.
* Returns elements from restIndex onwards.
*/
function extractArrayRest(value, restIndex, sourceCodeInfo) {
	assertArray(value, sourceCodeInfo);
	const arr = value;
	const items = [];
	for (let i = restIndex; i < arr.size; i++) items.push(arr.get(i));
	return PersistentVector.from(items);
}
//#endregion
//#region src/builtin/matchSlot.ts
/**
* Flatten a binding pattern into a linear list of match slots.
* Returns slots in order they should be processed.
*
* Type checks come first (to fail fast on type mismatch).
* Then binding/literal slots in depth-first order.
*/
function flattenMatchPattern(target) {
	const slots = [];
	flattenMatchTarget(target, [], slots);
	return slots;
}
function flattenMatchTarget(target, path, slots) {
	switch (target[0]) {
		case bindingTargetTypes.wildcard:
			slots.push({
				kind: "wildcard",
				path: [...path]
			});
			break;
		case bindingTargetTypes.literal: {
			const literalNode = target[1][0];
			slots.push({
				kind: "literal",
				path: [...path],
				literalNode,
				nodeId: target[2]
			});
			break;
		}
		case bindingTargetTypes.symbol: {
			const name = target[1][0][1];
			const defaultNode = target[1][1];
			slots.push({
				kind: "bind",
				name,
				path: [...path],
				defaultNode,
				nodeId: target[2]
			});
			break;
		}
		case bindingTargetTypes.rest: {
			const name = target[1][0];
			const defaultNode = target[1][1];
			slots.push({
				kind: "rest",
				name,
				path: [...path],
				defaultNode,
				nodeId: target[2]
			});
			break;
		}
		case bindingTargetTypes.object: {
			if (path.length > 0) slots.push({
				kind: "typeCheck",
				path: [...path],
				requiredType: "object",
				nodeId: target[2]
			});
			const entries = target[1][0];
			const capturedKeys = /* @__PURE__ */ new Set();
			let restElement;
			for (const { key, target: element } of entries) {
				if (element[0] === bindingTargetTypes.rest) {
					restElement = element;
					continue;
				}
				capturedKeys.add(key);
				flattenMatchTarget(element, [...path, {
					type: "key",
					key
				}], slots);
			}
			if (restElement) slots.push({
				kind: "rest",
				name: restElement[1][0],
				path: [...path],
				restKeys: capturedKeys,
				nodeId: restElement[2]
			});
			break;
		}
		case bindingTargetTypes.array: {
			if (path.length > 0) slots.push({
				kind: "typeCheck",
				path: [...path],
				requiredType: "array",
				nodeId: target[2]
			});
			const elements = target[1][0];
			for (let i = 0; i < elements.length; i++) {
				const element = elements[i];
				if (element === null || element === void 0) continue;
				if (element[0] === bindingTargetTypes.rest) {
					slots.push({
						kind: "rest",
						name: element[1][0],
						path: [...path],
						restIndex: i,
						nodeId: element[2]
					});
					break;
				}
				flattenMatchTarget(element, [...path, {
					type: "index",
					index: i
				}], slots);
			}
			break;
		}
	}
}
/**
* Extract a value from a nested structure by following a path.
* Returns undefined if path cannot be followed.
*/
function extractMatchValueByPath(rootValue, path) {
	let current = rootValue;
	for (const step of path) {
		if (current === null || current === void 0) return;
		if (step.type === "key") {
			if (!isObj(current)) return void 0;
			current = current.get(step.key);
		} else {
			if (!isPersistentVector(current)) return void 0;
			current = current.get(step.index);
		}
	}
	return current;
}
/**
* Check if value matches required type at path.
*/
function checkTypeAtPath(rootValue, path, requiredType) {
	const value = path.length > 0 ? extractMatchValueByPath(rootValue, path) : rootValue;
	if (value === null || value === void 0) return false;
	if (requiredType === "object") return isObj(value);
	else return isPersistentVector(value);
}
/**
* Extract rest values for object rest.
*/
function extractMatchObjectRest(value, path, restKeys) {
	const obj = path.length > 0 ? extractMatchValueByPath(value, path) : value;
	if (!isObj(obj)) return PersistentMap.empty();
	let result = PersistentMap.empty();
	for (const [key, val] of obj) if (!restKeys.has(key)) result = result.assoc(key, asAny(val));
	return result;
}
/**
* Extract rest values for array rest.
*/
function extractMatchArrayRest(value, path, restIndex) {
	const arr = path.length > 0 ? extractMatchValueByPath(value, path) : value;
	if (!isPersistentVector(arr)) return PersistentVector.empty();
	const items = [];
	for (let i = restIndex; i < arr.size; i++) items.push(asAny(arr.get(i)));
	return PersistentVector.from(items);
}
/**
* Check array length constraints at pattern root.
* For patterns like [a, b, c] - exact match required.
* For patterns like [a, b, ...rest] - minimum match required.
*/
function checkArrayLengthConstraint(target, value) {
	if (target[0] !== bindingTargetTypes.array) return true;
	if (!isPersistentVector(value)) return false;
	const elements = target[1][0];
	let hasRest = false;
	let nonRestCount = 0;
	for (const element of elements) {
		if (element === null) {
			nonRestCount++;
			continue;
		}
		if (element[0] === bindingTargetTypes.rest) {
			hasRest = true;
			break;
		}
		nonRestCount++;
	}
	if (hasRest) return value.size >= nonRestCount;
	else return value.size === nonRestCount;
}
/**
* Check object exists at pattern root.
*/
function checkObjectTypeConstraint(target, value) {
	if (target[0] !== bindingTargetTypes.object) return true;
	return isObj(value);
}
//#endregion
//#region src/evaluator/callStack.ts
/**
* Walk the continuation stack and extract a human-readable call stack.
*
* Filters to call-relevant frames: FnBody (user-defined function calls),
* EvalArgs (named call sites), CallFn (anonymous call sites),
* AlgebraicHandle and HandlerClause (effect handler boundaries).
*/
function reconstructCallStack(k) {
	const entries = [];
	let node = k;
	while (node !== null) {
		const frame = node.head;
		switch (frame.type) {
			case "FnBody":
				entries.push({
					name: frame.fn.name ?? "<anonymous>",
					sourceCodeInfo: frame.sourceCodeInfo
				});
				break;
			case "EvalArgs":
				if (isNormalExpressionNodeWithName(frame.node)) entries.push({
					name: frame.node[1][0][1],
					sourceCodeInfo: frame.sourceCodeInfo
				});
				break;
			case "CallFn":
				entries.push({
					name: frame.fnName ?? "<anonymous>",
					sourceCodeInfo: frame.sourceCodeInfo
				});
				break;
			case "AlgebraicHandle":
				entries.push({
					name: `handler(${[...frame.handler.clauseMap.keys()].join(", ")})`,
					sourceCodeInfo: frame.sourceCodeInfo
				});
				break;
			case "HandlerClause":
				entries.push({
					name: `handler clause(${[...frame.handler.clauseMap.keys()].join(", ")})`,
					sourceCodeInfo: frame.sourceCodeInfo
				});
				break;
		}
		node = node.tail;
	}
	return entries;
}
/**
* Format a call stack as a human-readable string for error messages.
*
* Example output:
*   at foo           myfile.dvala:42:15
*   at processAll    utils.dvala:10:3
*   at <main>        entry.dvala:5:1
*/
function formatCallStack(entries) {
	if (entries.length === 0) return "";
	return entries.map((entry) => {
		const location = entry.sourceCodeInfo ? `${entry.sourceCodeInfo.filePath ?? ""}:${entry.sourceCodeInfo.position.line}:${entry.sourceCodeInfo.position.column}` : "<unknown>";
		return `  at ${entry.name}  ${location}`;
	}).join("\n");
}
//#endregion
//#region src/getUndefinedSymbols/index.ts
const getUndefinedSymbols = (ast, contextStack, builtin) => {
	const nodes = Array.isArray(ast) ? ast : [[
		NodeTypes.Block,
		ast.body,
		0
	]];
	const unresolvedSymbols = /* @__PURE__ */ new Set();
	for (const subNode of nodes) findUnresolvedSymbolsInNode(subNode, contextStack, builtin)?.forEach((symbol) => unresolvedSymbols.add(symbol));
	return unresolvedSymbols;
};
function findUnresolvedSymbolsInNode(node, contextStack, builtin) {
	const nodeType = node[0];
	switch (nodeType) {
		case NodeTypes.Sym: {
			const symbolNode = node;
			if (contextStack.lookUp(symbolNode) === null) return new Set([symbolNode[1]]);
			return null;
		}
		case NodeTypes.Builtin:
		case NodeTypes.Special:
		case NodeTypes.Str:
		case NodeTypes.Num:
		case NodeTypes.Reserved:
		case NodeTypes.Binding: return null;
		case NodeTypes.Call: {
			const normalExpressionNode = node;
			const unresolvedSymbols = /* @__PURE__ */ new Set();
			if (isNormalExpressionNodeWithName(normalExpressionNode)) {
				const [, [symbolNode]] = normalExpressionNode;
				if (isUserDefinedSymbolNode(symbolNode)) {
					if (contextStack.lookUp(symbolNode) === null) unresolvedSymbols.add(symbolNode[1]);
				}
			} else {
				const [, [expressionNode]] = normalExpressionNode;
				findUnresolvedSymbolsInNode(expressionNode, contextStack, builtin)?.forEach((symbol) => unresolvedSymbols.add(symbol));
			}
			for (const subNode of normalExpressionNode[1][1]) findUnresolvedSymbolsInNode(subNode, contextStack, builtin)?.forEach((symbol) => unresolvedSymbols.add(symbol));
			return unresolvedSymbols;
		}
		case NodeTypes.MacroCall: {
			const [symNode, args] = node[1];
			const unresolvedSymbols = /* @__PURE__ */ new Set();
			if (symNode[0] === NodeTypes.Sym) {
				if (contextStack.lookUp(symNode) === null) unresolvedSymbols.add(symNode[1]);
			}
			for (const arg of args) findUnresolvedSymbolsInNode(arg, contextStack, builtin)?.forEach((s) => unresolvedSymbols.add(s));
			return unresolvedSymbols;
		}
		case NodeTypes.SpecialExpression: return null;
		case NodeTypes.Spread: return findUnresolvedSymbolsInNode(node[1], contextStack, builtin);
		case NodeTypes.TmplStr: {
			const unresolvedSymbols = /* @__PURE__ */ new Set();
			for (const segment of node[1]) findUnresolvedSymbolsInNode(segment, contextStack, builtin)?.forEach((symbol) => unresolvedSymbols.add(symbol));
			return unresolvedSymbols;
		}
		case NodeTypes.If: {
			const ifNode = node;
			const unresolvedSymbols = /* @__PURE__ */ new Set();
			for (const subNode of ifNode[1].filter((n) => !!n)) findUnresolvedSymbolsInNode(subNode, contextStack, builtin)?.forEach((symbol) => unresolvedSymbols.add(symbol));
			return unresolvedSymbols;
		}
		case NodeTypes.Block: return getUndefinedSymbols(node[1], contextStack.create({}), builtin);
		case NodeTypes.Effect: return null;
		case NodeTypes.Recur:
		case NodeTypes.Array: return getUndefinedSymbols(node[1], contextStack, builtin);
		case NodeTypes.Parallel:
		case NodeTypes.Race:
		case NodeTypes.Settled: {
			const argExpr = node[1];
			return getUndefinedSymbols([argExpr], contextStack, builtin);
		}
		case NodeTypes.Perform: {
			const [effectExpr, payloadExpr] = node[1];
			const unresolvedSymbols = /* @__PURE__ */ new Set();
			getUndefinedSymbols([effectExpr], contextStack, builtin)?.forEach((s) => unresolvedSymbols.add(s));
			if (payloadExpr) getUndefinedSymbols([payloadExpr], contextStack, builtin)?.forEach((s) => unresolvedSymbols.add(s));
			return unresolvedSymbols;
		}
		case NodeTypes.Object: {
			const objectEntries = node[1];
			const allNodes = [];
			for (const entry of objectEntries) if (Array.isArray(entry) && Array.isArray(entry[0])) {
				const [key, value] = entry;
				allNodes.push(key, value);
			} else allNodes.push(entry);
			return getUndefinedSymbols(allNodes, contextStack, builtin);
		}
		case NodeTypes.And:
		case NodeTypes.Or:
		case NodeTypes.Qq: return getUndefinedSymbols(node[1], contextStack, builtin);
		case NodeTypes.Match: return builtin.specialExpressions.match.getUndefinedSymbols(node, contextStack, {
			getUndefinedSymbols,
			builtin
		});
		case NodeTypes.Loop: return builtin.specialExpressions.loop.getUndefinedSymbols(node, contextStack, {
			getUndefinedSymbols,
			builtin
		});
		case NodeTypes.For: return builtin.specialExpressions.for.getUndefinedSymbols(node, contextStack, {
			getUndefinedSymbols,
			builtin
		});
		case NodeTypes.Import: return /* @__PURE__ */ new Set();
		case NodeTypes.CodeTmpl: {
			const [, spliceExprs] = node[1];
			return getUndefinedSymbols(spliceExprs, contextStack, builtin);
		}
		case NodeTypes.Splice: return null;
		case NodeTypes.Function:
		case NodeTypes.Macro: {
			const fn = node[1];
			return getFunctionUnresolvedSymbols(fn, contextStack, getUndefinedSymbols, builtin);
		}
		case NodeTypes.Let: {
			const [target, value] = node[1];
			const bindingResult = getUndefinedSymbols([value], contextStack, builtin);
			walkDefaults(target, (defaultNode) => {
				addToSet(bindingResult, getUndefinedSymbols([defaultNode], contextStack, builtin));
			});
			contextStack.addValues(getAllBindingTargetNames(target), contextStack.resolve(target[2]));
			return bindingResult;
		}
		case NodeTypes.Handler: {
			const [clauses, transform] = node[1];
			const result = /* @__PURE__ */ new Set();
			for (const clause of clauses) {
				const clauseScope = contextStack.create({});
				addToSet(result, getUndefinedSymbols(clause.body, clauseScope, builtin));
			}
			if (transform) addToSet(result, getUndefinedSymbols(transform[1], contextStack.create({}), builtin));
			return result;
		}
		case NodeTypes.Resume: {
			const payload = node[1];
			if (payload === "ref") return null;
			return getUndefinedSymbols([payload], contextStack, builtin);
		}
		case NodeTypes.WithHandler: {
			const [handlerExpr, bodyExprs] = node[1];
			return joinSets(getUndefinedSymbols([handlerExpr], contextStack, builtin), getUndefinedSymbols(bodyExprs, contextStack, builtin));
		}
		case NodeTypes.InlinedData: return /* @__PURE__ */ new Set();
		case NodeTypes.Atom: return /* @__PURE__ */ new Set();
		/* v8 ignore next 2 */
		default: throw new RuntimeError(`Unhandled node type: ${nodeType}`, void 0);
	}
}
//#endregion
//#region src/interop.ts
/**
* Recursively validate that a host value can be represented in Dvala.
* Throws TypeError for invalid types (undefined, functions, symbols, bigint,
* Date, Map, Set, class instances, circular references).
*
* @param value   The value to validate
* @param context Human-readable description of the boundary (e.g. 'resume() in handler for "fetch"')
* @param seen    Set for circular reference detection
* @param path    Dot-path to current position for error messages (e.g. '.user.tags[0]')
*/
function assertValidHostValue(value, context, seen = /* @__PURE__ */ new Set(), path = "") {
	if (value === null || typeof value === "string" || typeof value === "boolean") return;
	if (typeof value === "number") {
		if (!Number.isFinite(value)) throw new TypeError(`${context}: ${value}${path ? ` at ${path}` : ""} is not a valid Dvala value. Only finite numbers are allowed.`);
		return;
	}
	if (value === void 0) throw new TypeError(`${context}: undefined${path ? ` at ${path}` : ""} is not a valid Dvala value. Use null instead.`);
	if (typeof value === "function") throw new TypeError(`${context}: JS functions${path ? ` at ${path}` : ""} cannot enter the Dvala runtime.`);
	if (typeof value === "symbol") throw new TypeError(`${context}: Symbols${path ? ` at ${path}` : ""} are not valid Dvala values.`);
	if (typeof value === "bigint") throw new TypeError(`${context}: BigInt${path ? ` at ${path}` : ""} is not supported. Convert to number first.`);
	if (isAtom(value) || isRegularExpression(value) || isEffect(value) || isDvalaFunction(value)) return;
	if (isPersistentVector(value) || isPersistentMap(value)) return;
	if (typeof value !== "object") return;
	if (seen.has(value)) throw new TypeError(`${context}: Circular reference${path ? ` at ${path}` : ""} is not supported.`);
	seen.add(value);
	if (value instanceof Date) throw new TypeError(`${context}: Date objects${path ? ` at ${path}` : ""} are not valid Dvala values. Use date.toISOString() or date.getTime().`);
	if (value instanceof Map) throw new TypeError(`${context}: Map${path ? ` at ${path}` : ""} is not a valid Dvala value. Convert to a plain object first.`);
	if (value instanceof Set) throw new TypeError(`${context}: Set${path ? ` at ${path}` : ""} is not a valid Dvala value. Convert to an array first.`);
	if (value instanceof RegExp) throw new TypeError(`${context}: RegExp${path ? ` at ${path}` : ""} is not a valid Dvala value. Use regex("pattern") in Dvala instead.`);
	if (Array.isArray(value)) {
		for (let i = 0; i < value.length; i++) assertValidHostValue(value[i], context, seen, `${path}[${i}]`);
		seen.delete(value);
		return;
	}
	const proto = Object.getPrototypeOf(value);
	if (proto !== null && proto !== Object.prototype) throw new TypeError(`${context}: Class instance (${value.constructor?.name ?? "unknown"})${path ? ` at ${path}` : ""} is not a valid Dvala value. Spread to a plain object first: { ...instance }`);
	for (const [k, v] of Object.entries(value)) assertValidHostValue(v, context, seen, path ? `${path}.${k}` : `.${k}`);
	seen.delete(value);
}
/**
* Convert a plain JS value to a Dvala runtime value, with validation.
* Throws TypeError for values that cannot be represented in Dvala.
*
* Used at all host boundaries where external values enter the runtime.
*/
function validateFromJS(value, context) {
	assertValidHostValue(value, context);
	return fromJS(value);
}
/** Convert a Dvala runtime value to a plain JS value (deep). */
function toJS(value) {
	if (value === null || typeof value !== "object") return value;
	if (isAtom(value) || isRegularExpression(value) || isEffect(value) || isDvalaFunction(value)) return value;
	if (isPersistentVector(value)) {
		const result = [];
		for (const item of value) result.push(toJS(item));
		return result;
	}
	if (isPersistentMap(value)) {
		const result = {};
		for (const [k, v] of value) result[k] = toJS(v);
		return result;
	}
	return value;
}
/** Convert a plain JS value to a Dvala runtime value (deep). */
function fromJS(value) {
	if (value === null || typeof value !== "object") return value;
	if (isAtom(value) || isRegularExpression(value) || isEffect(value) || isDvalaFunction(value)) return value;
	if (isPersistentVector(value) || isPersistentMap(value)) return value;
	if (Array.isArray(value)) {
		let vec = PersistentVector.empty();
		for (const item of value) vec = vec.append(fromJS(item));
		return vec;
	}
	let map = PersistentMap.empty();
	for (const [k, v] of Object.entries(value)) map = map.assoc(k, fromJS(v));
	return map;
}
//#endregion
//#region src/evaluator/effectTypes.ts
const SUSPENDED_MESSAGE = "Program suspended";
/**
* Generate a UUID for identifying a run() or resume() call.
* Uses crypto.randomUUID() when available, falls back to a simple generator.
*/
function generateUUID() {
	if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = Math.random() * 16 | 0;
		return (c === "x" ? r : r & 3 | 8).toString(16);
	});
}
/**
* Create a Snapshot with a freshly generated unique `id`.
* effectArg is converted to plain JS so it serializes cleanly via JSON
* and compares correctly in tests without PV/PM internals leaking out.
*/
function createSnapshot(fields) {
	const effectArg = fields.effectArg !== void 0 ? toJS(fields.effectArg) : void 0;
	return {
		id: generateUUID(),
		...fields,
		effectArg
	};
}
/**
* Create a handler registration for `@dvala.host` from a plain record.
*
* The returned handler resumes with the record value when the name is found,
* or calls `fail()` when it's not. Works with both `run()` (sync) and
* `runAsync()` (async).
*
* @example
* ```typescript
* import { hostHandler } from '@mojir/dvala'
* dvala.runAsync(source, {
*   effectHandlers: [hostHandler({ configExists: true, dirName: '/app' })]
* })
* ```
*/
function hostHandler(values) {
	return {
		pattern: "dvala.host",
		handler: ({ arg, resume, fail }) => {
			if (typeof arg !== "string") {
				fail(`@dvala.host requires a string argument, got ${typeof arg}`);
				return;
			}
			if (arg in values) resume(values[arg]);
			else fail(`Host binding "${arg}" not provided`);
		}
	};
}
/**
* Test whether a handler pattern key matches a given effect name.
*
* Rules:
* - No wildcard → exact match only
* - `.*` suffix → matches the named effect itself AND all descendants
*   (dot boundary enforced: `dvala.*` matches `dvala.error` but NOT `dvalaXXX`)
* - `*` alone → matches everything
*/
function qualifiedNameMatchesPattern(name, pattern) {
	if (pattern === "*") return true;
	if (pattern.endsWith(".*")) {
		const prefix = pattern.slice(0, -2);
		return name === prefix || name.startsWith(`${prefix}.`);
	}
	return name === pattern;
}
/**
* Find all matching async handlers for an effect name, in registration order.
* Returns an array of `[pattern, handler]` pairs.
*/
function findMatchingHandlers(effectName, handlers) {
	if (!handlers || handlers.length === 0) return [];
	const result = [];
	for (const { pattern, handler } of handlers) if (qualifiedNameMatchesPattern(effectName, pattern)) result.push([pattern, handler]);
	return result;
}
/**
* Thrown (as a promise rejection) by `suspend()` inside a host handler.
* Caught by the effect trampoline loop — NOT by Dvala-level try/catch.
*/
var SuspensionSignal = class {
	k;
	snapshots;
	nextSnapshotIndex;
	meta;
	effectName;
	effectArg;
	_brand = "SuspensionSignal";
	constructor(k, snapshots, nextSnapshotIndex, meta, effectName, effectArg) {
		this.k = k;
		this.snapshots = snapshots;
		this.nextSnapshotIndex = nextSnapshotIndex;
		this.meta = meta;
		this.effectName = effectName;
		this.effectArg = effectArg;
	}
};
function isSuspensionSignal(value) {
	return value instanceof SuspensionSignal;
}
/**
* Thrown (as a promise rejection) by `resumeFrom()` inside a host handler.
* Caught by the effect trampoline loop — NOT by Dvala-level try/catch.
* Carries the serialized continuation from the target snapshot, the value
* to resume with, and the snapshot index for trimming.
*/
var ResumeFromSignal = class {
	continuation;
	value;
	trimToIndex;
	boundaryPath;
	_brand = "ResumeFromSignal";
	constructor(continuation, value, trimToIndex, boundaryPath = []) {
		this.continuation = continuation;
		this.value = value;
		this.trimToIndex = trimToIndex;
		this.boundaryPath = boundaryPath;
	}
};
function isResumeFromSignal(value) {
	return value instanceof ResumeFromSignal;
}
/**
* Thrown (as a promise rejection) by `halt()` inside a host handler.
* Caught by the effect trampoline loop — NOT by Dvala-level try/catch.
* Terminates execution immediately and returns a halted result.
*/
var HaltSignal = class {
	value;
	snapshots;
	nextSnapshotIndex;
	_brand = "HaltSignal";
	constructor(value, snapshots, nextSnapshotIndex) {
		this.value = value;
		this.snapshots = snapshots;
		this.nextSnapshotIndex = nextSnapshotIndex;
	}
};
function isHaltSignal(value) {
	return value instanceof HaltSignal;
}
//#endregion
//#region src/evaluator/effectRef.ts
const internMap = /* @__PURE__ */ new Map();
/**
* Returns the unique EffectRef for the given name.
* Calling with the same name always returns the same reference.
*
* @example
* const a = getEffectRef('llm.complete')
* const b = getEffectRef('llm.complete')
* a === b // true
*/
function getEffectRef(name) {
	let ref = internMap.get(name);
	if (!ref) {
		ref = {
			[EFFECT_SYMBOL]: true,
			name
		};
		internMap.set(name, ref);
	}
	return ref;
}
/**
* Clears the intern map. Primarily for testing.
*/
function clearEffectRefInternMap() {
	internMap.clear();
}
//#endregion
//#region src/evaluator/contentHash.ts
/**
* Content-addressable hashing for JSON-compatible value trees.
*
* Uses FNV-1a (32-bit) for fast, deterministic hashing. The hash is
* computed bottom-up (Merkle-style): child hashes feed directly into
* the parent hash as integers, avoiding intermediate string allocation.
*
* The hash is returned as a number (the raw 32-bit FNV-1a result).
*/
const FNV_OFFSET_BASIS = 2166136261;
const FNV_PRIME = 16777619;
/**
* Mix a single byte into an FNV-1a hash state.
*/
function fnvMixByte(hash, byte) {
	return Math.imul(hash ^ byte & 255, FNV_PRIME);
}
/**
* Mix a 32-bit integer (4 bytes, little-endian) into an FNV-1a hash state.
*/
function fnvMixInt(hash, value) {
	hash = fnvMixByte(hash, value);
	hash = fnvMixByte(hash, value >>> 8);
	hash = fnvMixByte(hash, value >>> 16);
	hash = fnvMixByte(hash, value >>> 24);
	return hash;
}
/**
* Mix a string into an FNV-1a hash state byte-by-byte (charCode).
*/
function fnvMixString(hash, str) {
	for (let i = 0; i < str.length; i++) {
		const code = str.charCodeAt(i);
		hash = fnvMixByte(hash, code);
		hash = fnvMixByte(hash, code >>> 8);
	}
	return hash;
}
const TAG_NULL = 0;
const TAG_BOOLEAN_TRUE = 1;
const TAG_BOOLEAN_FALSE = 2;
const TAG_NUMBER = 3;
const TAG_STRING = 4;
const TAG_ARRAY = 5;
const TAG_OBJECT = 6;
const TAG_UNDEFINED = 7;
/**
* Compute a deterministic content hash for any JSON-compatible value tree.
*
* Properties:
* - Identical structures produce identical hashes
* - Object key order does not affect the hash (keys are sorted)
* - Returns a 32-bit integer (FNV-1a)
*
* Supports: null, undefined, boolean, number, string, arrays, plain objects.
* Does not support: class instances, functions, symbols, bigint, etc.
*/
function contentHash(value) {
	let hash = FNV_OFFSET_BASIS;
	if (value === null) {
		hash = fnvMixByte(hash, TAG_NULL);
		return hash >>> 0;
	}
	if (value === void 0) {
		hash = fnvMixByte(hash, TAG_UNDEFINED);
		return hash >>> 0;
	}
	const type = typeof value;
	if (type === "boolean") {
		hash = fnvMixByte(hash, value ? TAG_BOOLEAN_TRUE : TAG_BOOLEAN_FALSE);
		return hash >>> 0;
	}
	if (type === "number") {
		hash = fnvMixByte(hash, TAG_NUMBER);
		hash = fnvMixString(hash, Object.is(value, -0) ? "-0" : String(value));
		return hash >>> 0;
	}
	if (type === "string") {
		hash = fnvMixByte(hash, TAG_STRING);
		hash = fnvMixString(hash, value);
		return hash >>> 0;
	}
	if (Array.isArray(value)) {
		hash = fnvMixByte(hash, TAG_ARRAY);
		hash = fnvMixInt(hash, value.length);
		for (let i = 0; i < value.length; i++) hash = fnvMixInt(hash, contentHash(value[i]));
		return hash >>> 0;
	}
	hash = fnvMixByte(hash, TAG_OBJECT);
	const keys = Object.keys(value).sort();
	hash = fnvMixInt(hash, keys.length);
	for (const key of keys) {
		hash = fnvMixString(hash, key);
		hash = fnvMixInt(hash, contentHash(value[key]));
	}
	return hash >>> 0;
}
//#endregion
//#region src/evaluator/dedupSubTrees.ts
/**
* Sub-tree deduplication for serialized continuation blobs.
*
* Identifies structurally identical sub-trees across multiple root objects,
* stores each unique sub-tree once in a shared pool, and replaces duplicates
* with compact references (`{ __poolRef: id }`).
*
* Works bottom-up: children are deduplicated before parents, so a parent
* node that contains pool references can itself be pooled if it appears
* multiple times with the same pool references.
*/
/**
* Detect nested SuspensionBlobData objects (already-serialized continuation blobs).
* These are opaque — the dedup/expand/clone walkers must not recurse into them.
* Their internal __poolRef markers belong to a different serialization context.
*/
function isSuspensionBlobData$1(value) {
	return value !== null && typeof value === "object" && "__suspensionBlob" in value && value.__suspensionBlob === true;
}
function isPoolRef(value) {
	return value !== null && typeof value === "object" && "__poolRef" in value && typeof value.__poolRef === "number" && Object.keys(value).length === 1;
}
/* v8 ignore start */
function deepEqual$1(a, b) {
	if (a === b) return true;
	if (a === null || b === null) return false;
	if (typeof a !== typeof b) return false;
	if (Array.isArray(a)) {
		if (!Array.isArray(b)) return false;
		if (a.length !== b.length) return false;
		for (let i = 0; i < a.length; i++) if (!deepEqual$1(a[i], b[i])) return false;
		return true;
	}
	if (typeof a !== "object") return false;
	const aObj = a;
	const bObj = b;
	const aKeys = Object.keys(aObj);
	const bKeys = Object.keys(bObj);
	if (aKeys.length !== bKeys.length) return false;
	for (const key of aKeys) {
		if (!(key in bObj)) return false;
		if (!deepEqual$1(aObj[key], bObj[key])) return false;
	}
	return true;
}
/* v8 ignore stop */
function estimateSize(value) {
	if (value === null) return 4;
	if (value === void 0) return 9;
	const type = typeof value;
	if (type === "boolean") return value ? 4 : 5;
	if (type === "number") return String(value).length;
	if (type === "string") return value.length + 2;
	if (Array.isArray(value)) {
		let size = 2;
		for (let i = 0; i < value.length; i++) size += estimateSize(value[i]) + 1;
		return size;
	}
	const obj = value;
	const keys = Object.keys(obj);
	let size = 2;
	for (const key of keys) size += key.length + 2 + 1 + estimateSize(obj[key]) + 1;
	return size;
}
/**
* Deduplicate identical sub-trees across multiple root values.
*
* @param roots - Array of root values to scan for duplicates
* @param threshold - Minimum estimated byte size for a sub-tree to be pooled (default 200)
* @returns Object with rewritten roots and the shared pool
*/
function dedupSubTrees(roots, threshold = 200) {
	const clonedRoots = roots.map((r) => deepClone(r));
	const hashGroups = /* @__PURE__ */ new Map();
	for (let rootIdx = 0; rootIdx < clonedRoots.length; rootIdx++) walkAndCollect(clonedRoots[rootIdx], hashGroups, null, rootIdx);
	const pool = {};
	let nextId = 0;
	const groups = Array.from(hashGroups.values()).flat().filter((info) => info.locations.length > 1 && info.size >= threshold).sort((a, b) => b.size - a.size);
	const alreadyPooled = /* @__PURE__ */ new Map();
	for (const info of groups) {
		/* v8 ignore next 2 */
		if (alreadyPooled.has(info)) continue;
		const id = nextId++;
		pool[id] = info.value;
		alreadyPooled.set(info, id);
		for (const loc of info.locations) if (Array.isArray(loc.parent)) loc.parent[loc.key] = { __poolRef: id };
		else loc.parent[loc.key] = { __poolRef: id };
	}
	return {
		roots: clonedRoots,
		pool
	};
}
/**
* Walk a value tree bottom-up, collecting sub-tree info.
* Only arrays and objects are candidates for pooling (primitives are too small).
*/
function walkAndCollect(value, hashGroups, parentInfo, _rootIdx) {
	if (value === null || typeof value !== "object") return contentHash(value);
	if (isSuspensionBlobData$1(value)) return contentHash(value);
	if (Array.isArray(value)) for (let i = 0; i < value.length; i++) walkAndCollect(value[i], hashGroups, {
		parent: value,
		key: i
	}, _rootIdx);
	else {
		const obj = value;
		for (const key of Object.keys(obj)) walkAndCollect(obj[key], hashGroups, {
			parent: obj,
			key
		}, _rootIdx);
	}
	const hash = contentHash(value);
	const size = estimateSize(value);
	if (parentInfo && size >= 1) {
		let group = hashGroups.get(hash);
		if (!group) {
			group = [];
			hashGroups.set(hash, group);
		}
		let found;
		for (const existing of group) if (deepEqual$1(existing.value, value)) {
			found = existing;
			break;
		}
		if (found) found.locations.push(parentInfo);
		else group.push({
			hash,
			value: deepClone(value),
			size,
			locations: [parentInfo]
		});
	}
	return hash;
}
/**
* Recursively expand all `{ __poolRef: id }` markers in a value tree,
* replacing them with the corresponding pool values.
*
* Pool values themselves may contain pool refs (nested pooling),
* which are expanded recursively.
*
* @throws Error if a pool ref references an unknown ID
*/
function expandPoolRefs(value, pool) {
	if (value === null || typeof value !== "object") return value;
	if (isSuspensionBlobData$1(value)) return value;
	if (isPoolRef(value)) {
		const id = value.__poolRef;
		if (!(id in pool)) throw new Error(`Unknown pool ref: ${id}`);
		return expandPoolRefs(pool[id], pool);
	}
	if (Array.isArray(value)) return value.map((item) => expandPoolRefs(item, pool));
	const obj = value;
	const result = {};
	for (const key of Object.keys(obj)) result[key] = expandPoolRefs(obj[key], pool);
	return result;
}
function deepClone(value) {
	if (value === null || typeof value !== "object") return value;
	if (isSuspensionBlobData$1(value)) return value;
	if (Array.isArray(value)) return value.map((item) => deepClone(item));
	const obj = value;
	const result = {};
	for (const key of Object.keys(obj)) result[key] = deepClone(obj[key]);
	return result;
}
//#endregion
//#region src/evaluator/suspension.ts
/**
* Continuation stack serialization and deserialization.
*
* When a host handler calls `suspend(meta?)`, the trampoline captures the
* continuation stack (an array of Frame objects). This module converts that
* stack to/from a JSON string (the "suspension blob") that can be stored,
* transferred, and later resumed in a new process.
*
* The main challenge is that Frame objects contain `ContextStack` class
* instances (which hold lexical scope chains). These are not plain data
* and may form circular references (e.g., a global context containing a
* UserDefinedFunction whose captured env references the same global context).
*
* The serialization approach:
* 1. Walk the frame tree and collect all unique ContextStack instances (by identity)
* 2. Assign each a numeric ID
* 3. Serialize ContextStacks separately as plain objects
* 4. In the frame tree, replace ContextStack references with `{ __csRef: id }`
* 5. First occurrence of each ContextStack gets `{ __csDef: id, ... }` in the
*    contextStacks array — circular refs just become `{ __csRef: id }`
*
* Deserialization reverses this:
* 1. Parse the blob and create placeholder ContextStack instances
* 2. Deep-resolve all `__csRef` markers back to real instances
* 3. Fill in host bindings (values, modules) on each instance
*/
const SUSPENSION_VERSION = 2;
const DEFAULT_DEDUP_THRESHOLD = 200;
function isCSRef(value) {
	return value !== null && typeof value === "object" && "__csRef" in value && typeof value.__csRef === "number";
}
function isPVMarker(value) {
	return value !== null && typeof value === "object" && "__pv" in value;
}
function isPMMarker(value) {
	return value !== null && typeof value === "object" && "__pm" in value;
}
/**
* Detect nested SuspensionBlobData objects (already-serialized continuation blobs).
* These appear inside Snapshot.continuation when Snapshots are embedded in frames
* (e.g. ParallelResumeFrame.suspendedBranches[i].snapshot). They must NOT be walked
* by the outer serializer/deserializer — their __csRef markers belong to a different
* serialization context.
*/
function isSuspensionBlobData(value) {
	return value !== null && typeof value === "object" && "__suspensionBlob" in value && value.__suspensionBlob;
}
/**
* Serialize a continuation stack and optional metadata into a plain
* JSON-compatible object tree.
*
* The returned object is the "continuation" stored inside a `Snapshot`.
* It is intentionally typed as `SuspensionBlobData` internally but
* exposed as `unknown` via the `Snapshot` interface.
*
* Validates that all values are serializable.
* Throws a descriptive `DvalaError` if non-serializable values are found.
*/
function serializeToObject(k, meta, initialStep) {
	const csMap = /* @__PURE__ */ new Map();
	let nextId = 0;
	function collectContextStacks(value) {
		if (value instanceof ContextStackImpl) {
			if (csMap.has(value)) return;
			csMap.set(value, nextId++);
			for (const ctx of value.getContextsRaw()) for (const entry of Object.values(ctx)) collectContextStacks(entry.value);
			return;
		}
		if (isSuspensionBlobData(value)) return;
		if (Array.isArray(value)) {
			for (const item of value) collectContextStacks(item);
			return;
		}
		if (value !== null && typeof value === "object") for (const v of Object.values(value)) collectContextStacks(v);
	}
	collectContextStacks(k);
	if (meta !== void 0) collectContextStacks(meta);
	if (initialStep !== void 0) collectContextStacks(initialStep);
	function serializeValue(value, path) {
		if (value instanceof ContextStackImpl) return { __csRef: csMap.get(value) };
		if (value instanceof Map) return null;
		if (isSuspensionBlobData(value)) return value;
		if (isPersistentVector(value)) {
			const items = [];
			let i = 0;
			for (const item of value) items.push(serializeValue(item, `${path}[${i++}]`));
			return { __pv: items };
		}
		if (isPersistentMap(value)) {
			const result = {};
			for (const [key, v] of value) result[key] = serializeValue(v, `${path}.${key}`);
			return { __pm: result };
		}
		if (Array.isArray(value)) return value.map((item, i) => serializeValue(item, `${path}[${i}]`));
		if (value !== null && typeof value === "object") {
			const result = {};
			for (const [key, v] of Object.entries(value)) result[key] = serializeValue(v, `${path}.${key}`);
			return result;
		}
		return value;
	}
	const serializedContextStacks = Array.from(csMap.entries()).map(([cs, id]) => ({
		id,
		contexts: cs.getContextsRaw().map((ctx, ctxIdx) => {
			const serialized = {};
			for (const [name, entry] of Object.entries(ctx)) serialized[name] = { value: serializeValue(entry.value, `cs[${id}].contexts[${ctxIdx}].${name}`) };
			return serialized;
		}),
		globalContextIndex: cs.getGlobalContextIndex(),
		pure: cs.pure
	}));
	const serializedK = serializeValue(k, "k");
	const serializedMeta = meta !== void 0 ? serializeValue(meta, "meta") : void 0;
	const serializedInitialStep = initialStep !== void 0 ? serializeValue(initialStep, "initialStep") : void 0;
	return {
		__suspensionBlob: true,
		version: SUSPENSION_VERSION,
		contextStacks: serializedContextStacks,
		k: serializedK,
		...serializedInitialStep !== void 0 ? { initialStep: serializedInitialStep } : {},
		...serializedMeta !== void 0 ? { meta: serializedMeta } : {}
	};
}
/**
* Build a complete suspension blob that includes both the continuation
* and the accumulated snapshot state.  Snapshots are already plain
* JSON-compatible objects (their `continuation` fields were produced by
* earlier `serializeToObject` calls), so they're embedded as-is.
*
* Runs sub-tree deduplication across all serialized continuations to
* reduce blob size when snapshots share identical AST sub-trees.
*/
function serializeSuspensionBlob(k, snapshots, nextSnapshotIndex, meta) {
	const base = serializeToObject(k, meta);
	if (snapshots.length > 0) base.snapshots = snapshots;
	base.nextSnapshotIndex = nextSnapshotIndex;
	const roots = [base.contextStacks, base.k];
	if (base.meta !== void 0) roots.push(base.meta);
	if (base.snapshots) for (const snapshot of base.snapshots) roots.push(snapshot);
	const { roots: dedupedRoots, pool } = dedupSubTrees(roots, DEFAULT_DEDUP_THRESHOLD);
	let rootIdx = 0;
	base.contextStacks = dedupedRoots[rootIdx++];
	base.k = dedupedRoots[rootIdx++];
	if (base.meta !== void 0) base.meta = dedupedRoots[rootIdx++];
	if (base.snapshots) for (let i = 0; i < base.snapshots.length; i++) base.snapshots[i] = dedupedRoots[rootIdx++];
	if (Object.keys(pool).length > 0) base.pool = pool;
	return base;
}
/**
* Create a terminal snapshot for completed or errored program states.
* The continuation is empty (cannot resume), but checkpoints are preserved
* for time travel debugging.
*/
function serializeTerminalSnapshot(snapshots, nextSnapshotIndex) {
	const base = {
		__suspensionBlob: true,
		version: SUSPENSION_VERSION,
		contextStacks: [],
		k: []
	};
	if (snapshots.length > 0) {
		base.snapshots = snapshots;
		const roots = [];
		for (const snapshot of snapshots) roots.push(snapshot);
		const { roots: dedupedRoots, pool } = dedupSubTrees(roots, DEFAULT_DEDUP_THRESHOLD);
		for (let i = 0; i < base.snapshots.length; i++) base.snapshots[i] = dedupedRoots[i];
		if (Object.keys(pool).length > 0) base.pool = pool;
	}
	base.nextSnapshotIndex = nextSnapshotIndex;
	return base;
}
/**
* Deserialize a plain object (as produced by `serializeToObject`) back into
* a continuation stack and metadata.
*
* Reconstructs `ContextStack` instances.
* Handles circular references between ContextStacks and their contained values.
*/
function deserializeFromObject(blobData, options) {
	let data = blobData;
	if (data.version !== SUSPENSION_VERSION) throw new RuntimeError(`Unsupported suspension blob version: ${data.version} (expected ${SUSPENSION_VERSION})`, void 0);
	if (data.pool && Object.keys(data.pool).length > 0) {
		const pool = data.pool;
		data = {
			...data,
			contextStacks: expandPoolRefs(data.contextStacks, pool),
			k: expandPoolRefs(data.k, pool),
			...data.initialStep !== void 0 ? { initialStep: expandPoolRefs(data.initialStep, pool) } : {},
			...data.meta !== void 0 ? { meta: expandPoolRefs(data.meta, pool) } : {},
			...data.snapshots ? { snapshots: data.snapshots.map((s) => expandPoolRefs(s, pool)) } : {}
		};
		delete data.pool;
	}
	const csMap = /* @__PURE__ */ new Map();
	for (const scs of data.contextStacks) {
		const placeholderContexts = scs.contexts.map(() => {
			return {};
		});
		const cs = ContextStackImpl.fromDeserialized({
			contexts: placeholderContexts,
			globalContextIndex: scs.globalContextIndex,
			modules: options?.modules,
			pure: scs.pure
		});
		csMap.set(scs.id, cs);
	}
	function resolveValue(value) {
		if (isCSRef(value)) {
			const cs = csMap.get(value.__csRef);
			if (!cs) throw new RuntimeError(`Invalid suspension blob: unknown context stack ref ${value.__csRef}`, void 0);
			return cs;
		}
		if (isSuspensionBlobData(value)) return value;
		if (isPVMarker(value)) {
			let pv = PersistentVector.empty();
			for (const item of value.__pv) pv = pv.append(resolveValue(item));
			return pv;
		}
		if (isPMMarker(value)) {
			let pm = PersistentMap.empty();
			for (const [k, v] of Object.entries(value.__pm)) pm = pm.assoc(k, resolveValue(v));
			return pm;
		}
		if (Array.isArray(value)) return value.map(resolveValue);
		if (value !== null && typeof value === "object") {
			const result = {};
			for (const [key, v] of Object.entries(value)) result[key] = resolveValue(v);
			if (result.functionType === "Handler" && Array.isArray(result.clauses)) {
				const clauseMap = /* @__PURE__ */ new Map();
				for (const clause of result.clauses) clauseMap.set(clause.effectName, clause);
				result.clauseMap = clauseMap;
			}
			return result;
		}
		return value;
	}
	for (const scs of data.contextStacks) {
		const cs = csMap.get(scs.id);
		const resolvedContexts = scs.contexts.map((serializedCtx) => {
			const ctx = serializedCtx;
			const resolved = {};
			for (const [name, entry] of Object.entries(ctx)) resolved[name] = { value: resolveValue(entry.value) };
			return resolved;
		});
		cs.setContextsFromDeserialized(resolvedContexts, scs.globalContextIndex);
		if (options?.scope) for (const [k, v] of Object.entries(options.scope)) cs.globalContext[k] = v;
	}
	const resolvedK = resolveValue(data.k);
	const resolvedMeta = data.meta !== void 0 ? resolveValue(data.meta) : void 0;
	const resolvedInitialStep = data.initialStep !== void 0 ? resolveValue(data.initialStep) : void 0;
	return {
		k: resolvedK,
		meta: resolvedMeta,
		...resolvedInitialStep !== void 0 ? { initialStep: resolvedInitialStep } : {},
		snapshots: data.snapshots ?? [],
		nextSnapshotIndex: data.nextSnapshotIndex ?? 0
	};
}
/**
* Extract checkpoint snapshots from a serialized continuation blob,
* expanding any pool references so each snapshot is a self-contained blob
* that can be passed directly to `resume()`.
*/
function extractCheckpointSnapshots(continuation) {
	const data = continuation;
	if (!data.snapshots || data.snapshots.length === 0) return [];
	const pool = data.pool && Object.keys(data.pool).length > 0 ? data.pool : void 0;
	return data.snapshots.map((s) => pool ? expandPoolRefs(s, pool) : s);
}
//#endregion
//#region src/evaluator/standardEffects.ts
/**
* Standard effects — built-in effects with default implementations.
*
* These effects are always available without requiring explicit host handlers.
* Host handlers can override them (host handlers take priority in the lookup order).
*
* Lookup order: local try/with → host handlers → standard effects → unhandled error
*
* Each standard effect has co-located documentation (FunctionDocs) and arity,
* mirroring the structure of builtin normal expressions and module functions.
*
* Sync effects work in both `runSync` and `run`.
* Async effects (`dvala.sleep`, `dvala.io.read` in Node, `dvala.io.readStdin`) only work in `run` —
* `runSync` will throw when a Promise surfaces.
*/
function isNode() {
	return typeof process !== "undefined" && typeof process.stdout?.write === "function";
}
/**
* Format a Dvala value for text output (stdout/stderr).
* - Strings are printed as-is (no quotes).
* - Numbers, booleans, null: String() coercion.
* - Functions: `<function name>` or `<builtin function name>`.
* - Effects: `<effect name>`.
* - RegExps (Dvala regular expressions): `/pattern/flags`.
* - Arrays and objects: JSON.stringify with 2-space indent.
*/
function formatForOutput(value) {
	if (typeof value === "string") return value;
	if (value === null) return "null";
	if (typeof value === "number" || typeof value === "boolean") return String(value);
	if (isDvalaFunction(value)) {
		if (value.functionType === "Builtin") return `<builtin function ${value.normalBuiltinSymbolType}>`;
		return `<${value.functionType === "Macro" ? "macro" : "function"} ${"name" in value && value.name ? value.name : "λ"}>`;
	}
	if (isEffect(value)) return `<effect ${value.name}>`;
	if (isRegularExpression(value)) return `/${value.s}/${value.f}`;
	if (typeof value === "object" && value instanceof RegExp) return `${value}`;
	return JSON.stringify(replaceSpecialValues(value), null, 2);
}
function replaceSpecialValues(value) {
	if (isDvalaFunction(value)) return formatForOutput(value);
	if (isEffect(value)) return formatForOutput(value);
	if (isRegularExpression(value)) return formatForOutput(value);
	if (Array.isArray(value)) return value.map(replaceSpecialValues);
	if (typeof value === "object" && value !== null) {
		const result = {};
		for (const [key, val] of Object.entries(value)) result[key] = replaceSpecialValues(val);
		return result;
	}
	return value;
}
function printHandler(arg, k) {
	const value = arg;
	const str = formatForOutput(value);
	if (isNode()) process.stdout.write(str);
	else console.log(str);
	return {
		type: "Value",
		value,
		k
	};
}
function generateUUID$1() {
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
		const random = Math.random() * 16 | 0;
		return (char === "x" ? random : random & 3 | 8).toString(16);
	});
}
const standardEffects = {
	"dvala.io.print": {
		handler: printHandler,
		arity: toFixedArity(1),
		docs: {
			category: "effect",
			description: "Writes a value to stdout without a trailing newline. Accepts any value — strings are printed as-is, other values are auto-formatted. In Node.js uses `process.stdout.write(str)`, in browsers uses `console.log(str)`. Resumes with the original value (identity).",
			returns: { type: "any" },
			args: { value: {
				type: "any",
				description: "Value to print."
			} },
			variants: [{ argumentNames: ["value"] }],
			examples: [
				{
					code: "perform(@dvala.io.print, \"hello\")",
					noRun: true
				},
				{
					code: "perform(@dvala.io.print, 42)",
					noRun: true
				},
				{
					code: "perform(@dvala.io.print, {name: \"Alice\", age: 30})",
					noRun: true
				}
			],
			seeAlso: [
				"-effect-dvala.io.error",
				"-effect-dvala.io.read",
				"perform",
				"effect"
			]
		}
	},
	"dvala.io.error": {
		handler: (arg, k) => {
			const str = formatForOutput(arg);
			if (isNode()) process.stderr.write(`${str}\n`);
			else console.error(str);
			return {
				type: "Value",
				value: arg,
				k
			};
		},
		arity: toFixedArity(1),
		docs: {
			category: "effect",
			description: "Writes a value to stderr followed by a newline. Accepts any value — strings are printed as-is, other values are auto-formatted. In Node.js uses `process.stderr.write(str + \"\\n\")`, in browsers uses `console.error(str)`. Resumes with the original value (identity).",
			returns: { type: "any" },
			args: { value: {
				type: "any",
				description: "Value to write to stderr."
			} },
			variants: [{ argumentNames: ["value"] }],
			examples: [{
				code: "perform(@dvala.io.error, \"something went wrong\")",
				noRun: true
			}, {
				code: "perform(@dvala.io.error, {code: 500, message: \"Internal error\"})",
				noRun: true
			}],
			seeAlso: [
				"-effect-dvala.io.print",
				"perform",
				"effect"
			]
		}
	},
	"dvala.io.read": {
		handler: (arg, k, sourceCodeInfo) => {
			const message = typeof arg === "string" ? arg : "";
			if (typeof globalThis.prompt === "function") return {
				type: "Value",
				value: globalThis.prompt(message) ?? null,
				k
			};
			throw new RuntimeError("dvala.io.read is not supported in this environment. In Node.js, register a \"dvala.io.read\" host handler.", sourceCodeInfo);
		},
		arity: {
			min: 0,
			max: 1
		},
		docs: {
			category: "effect",
			description: "Reads one line of user input. In browsers uses `window.prompt()`. In Node.js uses `readline`. Resumes with the user's input string, or `null` on cancel.",
			returns: { type: ["string", "null"] },
			args: { message: {
				type: "string",
				description: "Optional prompt message to display."
			} },
			variants: [{ argumentNames: [] }, { argumentNames: ["message"] }],
			examples: [{
				code: "let name = perform(@dvala.io.read, \"What is your name?\"); \"Hello, \" ++ name",
				noRun: true
			}, {
				code: "let input = perform(@dvala.io.read); input ?? \"no input\"",
				noRun: true
			}],
			seeAlso: [
				"-effect-dvala.io.readStdin",
				"-effect-dvala.io.print",
				"-effect-dvala.io.pick",
				"-effect-dvala.io.confirm",
				"perform",
				"effect"
			]
		}
	},
	"dvala.io.pick": {
		handler: (arg, k, sourceCodeInfo) => {
			const items = isPersistentVector(arg) ? arg : isPersistentMap(arg) ? arg.get("items") : void 0;
			const options = isPersistentVector(arg) ? void 0 : isPersistentMap(arg) ? arg.get("options") : void 0;
			if (!isPersistentVector(items)) throw new TypeError$1(`dvala.io.pick: first argument must be an array, got ${typeof items}`, sourceCodeInfo);
			if (items.size === 0) throw new TypeError$1("dvala.io.pick: items array must not be empty", sourceCodeInfo);
			for (let i = 0; i < items.size; i++) if (typeof items.get(i) !== "string") throw new TypeError$1(`dvala.io.pick: items[${i}] must be a string, got ${typeof items.get(i)}`, sourceCodeInfo);
			let promptMessage;
			let defaultIndex;
			if (options !== void 0) {
				if (!isPersistentMap(options)) throw new TypeError$1(`dvala.io.pick: second argument must be an object, got ${typeof options}`, sourceCodeInfo);
				if (options.get("prompt") !== void 0) {
					if (typeof options.get("prompt") !== "string") throw new TypeError$1("dvala.io.pick: options.prompt must be a string", sourceCodeInfo);
					promptMessage = options.get("prompt");
				}
				if (options.get("default") !== void 0) {
					const optDefault = options.get("default");
					if (typeof optDefault !== "number" || !Number.isInteger(optDefault)) throw new TypeError$1("dvala.io.pick: options.default must be an integer", sourceCodeInfo);
					defaultIndex = optDefault;
					if (defaultIndex < 0 || defaultIndex >= items.size) throw new TypeError$1(`dvala.io.pick: options.default (${defaultIndex}) is out of bounds for array of length ${items.size}`, sourceCodeInfo);
				}
			}
			if (typeof globalThis.prompt === "function") {
				const listLines = [...items].map((item, i) => `${i}: ${item}`).join("\n");
				const message = `${promptMessage ?? "Choose an item:"}${defaultIndex !== void 0 ? ` [default: ${defaultIndex}]` : ""}\n${listLines}`;
				const result = globalThis.prompt(message);
				if (result === null) return {
					type: "Value",
					value: null,
					k
				};
				const trimmed = result.trim();
				if (trimmed === "") return {
					type: "Value",
					value: defaultIndex !== void 0 ? defaultIndex : null,
					k
				};
				const parsed = Number(trimmed);
				if (!Number.isInteger(parsed) || parsed < 0 || parsed >= items.size) throw new TypeError$1(`dvala.io.pick: invalid selection "${trimmed}"`, sourceCodeInfo);
				return {
					type: "Value",
					value: parsed,
					k
				};
			}
			throw new RuntimeError("dvala.io.pick is not supported in this environment. In Node.js, register a \"dvala.io.pick\" host handler.", sourceCodeInfo);
		},
		arity: {
			min: 1,
			max: 2
		},
		docs: {
			category: "effect",
			description: "Presents a list of items and asks the user to choose one. Accepts either a plain array of strings or an object with `items` and optional `options`. Resumes with the index of the chosen item, or `null` if the user cancels.",
			returns: { type: ["integer", "null"] },
			args: {
				items: {
					type: "array",
					description: "Non-empty array of strings to display. Can be passed directly as the arg."
				},
				options: {
					type: "object",
					description: "Optional settings: `prompt` (string label) and `default` (integer index to use when the user submits an empty input)."
				}
			},
			variants: [{ argumentNames: ["items"] }, { argumentNames: ["items", "options"] }],
			examples: [
				{
					code: "let choice = perform(@dvala.io.pick, [\"Red\", \"Green\", \"Blue\"])",
					noRun: true
				},
				{
					code: "let choice = perform(@dvala.io.pick, {items: [\"Red\", \"Green\", \"Blue\"], options: {prompt: \"Pick a color:\", default: 0}})",
					noRun: true
				},
				{
					code: "do\n  let colors = [\"Red\", \"Green\", \"Blue\"];\n  let idx = perform(@dvala.io.pick, colors);\n  if idx != null then nth(colors, idx) else \"No selection\" end\nend",
					noRun: true
				}
			],
			seeAlso: [
				"-effect-dvala.io.read",
				"-effect-dvala.io.confirm",
				"perform",
				"effect"
			]
		}
	},
	"dvala.io.confirm": {
		handler: (arg, k, sourceCodeInfo) => {
			const question = typeof arg === "string" ? arg : isPersistentMap(arg) ? arg.get("question") : void 0;
			const options = typeof arg === "string" ? void 0 : isPersistentMap(arg) ? arg.get("options") : void 0;
			if (typeof question !== "string") throw new TypeError$1(`dvala.io.confirm: first argument must be a string, got ${typeof question}`, sourceCodeInfo);
			if (options !== void 0) {
				if (!isPersistentMap(options)) throw new TypeError$1(`dvala.io.confirm: second argument must be an object, got ${typeof options}`, sourceCodeInfo);
				if (options.get("default") !== void 0 && typeof options.get("default") !== "boolean") throw new TypeError$1("dvala.io.confirm: options.default must be a boolean", sourceCodeInfo);
			}
			if (typeof globalThis.confirm === "function") return {
				type: "Value",
				value: globalThis.confirm(question),
				k
			};
			throw new RuntimeError("dvala.io.confirm is not supported in this environment. In Node.js, register a \"dvala.io.confirm\" host handler.", sourceCodeInfo);
		},
		arity: {
			min: 1,
			max: 2
		},
		docs: {
			category: "effect",
			description: "Asks the user a yes/no question. In browsers uses `window.confirm()` and returns `true` (OK) or `false` (Cancel). In Node.js, register a host handler. The optional `default` hints the preferred answer to host handlers (e.g. for rendering `[Y/n]` in a CLI), but has no effect on the browser implementation.",
			returns: { type: "boolean" },
			args: {
				question: {
					type: "string",
					description: "The yes/no question to present."
				},
				options: {
					type: "object",
					description: "Optional settings: `default` (boolean, hints the preferred answer for host handlers)."
				}
			},
			variants: [{ argumentNames: ["question"] }, { argumentNames: ["question", "options"] }],
			examples: [{
				code: "if perform(@dvala.io.confirm, \"Delete all files?\") then \"Deleted\" else \"Cancelled\" end",
				noRun: true
			}, {
				code: "perform(@dvala.io.confirm, {question: \"Continue?\", options: {default: true}})",
				noRun: true
			}],
			seeAlso: [
				"-effect-dvala.io.read",
				"-effect-dvala.io.pick",
				"perform",
				"effect"
			]
		}
	},
	"dvala.io.readStdin": {
		handler: (_arg, k, sourceCodeInfo) => {
			if (!isNode() || !process.stdin) throw new RuntimeError("dvala.io.readStdin is not supported in this environment. Node.js is required.", sourceCodeInfo);
			return new Promise((resolve, reject) => {
				const chunks = [];
				process.stdin.setEncoding("utf-8");
				process.stdin.on("data", (chunk) => chunks.push(chunk));
				process.stdin.on("end", () => resolve({
					type: "Value",
					value: chunks.join(""),
					k
				}));
				process.stdin.on("error", (err) => reject(err));
				process.stdin.resume();
			});
		},
		arity: toFixedArity(0),
		docs: {
			category: "effect",
			description: "Reads all of stdin until EOF (Node.js only). Resumes with the full stdin content as a string.",
			returns: { type: "string" },
			args: {},
			variants: [{ argumentNames: [] }],
			examples: [{
				code: "let input = perform(@dvala.io.readStdin); count(input)",
				noRun: true
			}],
			seeAlso: [
				"-effect-dvala.io.read",
				"perform",
				"effect"
			]
		}
	},
	"dvala.random": {
		handler: (_arg, k) => {
			return {
				type: "Value",
				value: Math.random(),
				k
			};
		},
		arity: toFixedArity(0),
		docs: {
			category: "effect",
			description: "Returns a random floating-point number in the range [0, 1). Equivalent to `Math.random()` in JavaScript.",
			returns: { type: "number" },
			args: {},
			variants: [{ argumentNames: [] }],
			examples: ["perform(@dvala.random)"],
			seeAlso: [
				"-effect-dvala.random.int",
				"-effect-dvala.random.uuid",
				"-effect-dvala.random.item",
				"-effect-dvala.random.shuffle",
				"perform",
				"effect"
			]
		}
	},
	"dvala.random.uuid": {
		handler: (_arg, k) => {
			return {
				type: "Value",
				value: generateUUID$1(),
				k
			};
		},
		arity: toFixedArity(0),
		docs: {
			category: "effect",
			description: "Generates a UUID v4 string.",
			returns: { type: "string" },
			args: {},
			variants: [{ argumentNames: [] }],
			examples: ["perform(@dvala.random.uuid)"],
			seeAlso: [
				"-effect-dvala.random",
				"perform",
				"effect"
			]
		}
	},
	"dvala.random.int": {
		handler: (arg, k, sourceCodeInfo) => {
			const min = isPersistentVector(arg) ? arg.get(0) : Array.isArray(arg) ? arg[0] : void 0;
			const max = isPersistentVector(arg) ? arg.get(1) : Array.isArray(arg) ? arg[1] : void 0;
			if (typeof min !== "number" || !Number.isInteger(min)) throw new TypeError$1(`dvala.random.int: min must be an integer, got ${typeof min === "number" ? min : typeof min}`, sourceCodeInfo);
			if (typeof max !== "number" || !Number.isInteger(max)) throw new TypeError$1(`dvala.random.int: max must be an integer, got ${typeof max === "number" ? max : typeof max}`, sourceCodeInfo);
			if (max <= min) throw new ArithmeticError(`dvala.random.int: max (${max}) must be greater than min (${min})`, sourceCodeInfo);
			return {
				type: "Value",
				value: Math.floor(Math.random() * (max - min)) + min,
				k
			};
		},
		arity: toFixedArity(1),
		docs: {
			category: "effect",
			description: "Returns a random integer in the range [min, max). Pass a two-element array `[min, max]`.",
			returns: { type: "integer" },
			args: { range: {
				type: "array",
				description: "A two-element array [min, max] where min is inclusive and max is exclusive. Both must be integers with max > min."
			} },
			variants: [{ argumentNames: ["range"] }],
			examples: ["perform(@dvala.random.int, [1, 100])"],
			seeAlso: [
				"-effect-dvala.random",
				"-effect-dvala.random.item",
				"perform",
				"effect"
			]
		}
	},
	"dvala.random.item": {
		handler: (arg, k, sourceCodeInfo) => {
			if (!isPersistentVector(arg) && !Array.isArray(arg)) throw new TypeError$1(`dvala.random.item: argument must be an array, got ${typeof arg}`, sourceCodeInfo);
			const size = isPersistentVector(arg) ? arg.size : arg.length;
			if (size === 0) throw new TypeError$1("dvala.random.item: cannot pick from an empty array", sourceCodeInfo);
			const index = Math.floor(Math.random() * size);
			return {
				type: "Value",
				value: isPersistentVector(arg) ? arg.get(index) : arg[index],
				k
			};
		},
		arity: toFixedArity(1),
		docs: {
			category: "effect",
			description: "Picks a random element from a non-empty array.",
			returns: { type: "any" },
			args: { array: {
				type: "array",
				description: "Non-empty array to pick from."
			} },
			variants: [{ argumentNames: ["array"] }],
			examples: ["perform(@dvala.random.item, [\"a\", \"b\", \"c\"])"],
			seeAlso: [
				"-effect-dvala.random",
				"-effect-dvala.random.shuffle",
				"-effect-dvala.random.int",
				"perform",
				"effect"
			]
		}
	},
	"dvala.random.shuffle": {
		handler: (arg, k, sourceCodeInfo) => {
			if (!isPersistentVector(arg)) throw new TypeError$1(`dvala.random.shuffle: argument must be an array, got ${typeof arg}`, sourceCodeInfo);
			const shuffled = [...arg];
			for (let i = shuffled.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1));
				const tmp = shuffled[i];
				shuffled[i] = shuffled[j];
				shuffled[j] = tmp;
			}
			return {
				type: "Value",
				value: PersistentVector.from(shuffled),
				k
			};
		},
		arity: toFixedArity(1),
		docs: {
			category: "effect",
			description: "Returns a new array with the elements of the input array in random order. Uses the Fisher-Yates shuffle algorithm.",
			returns: { type: "array" },
			args: { array: {
				type: "array",
				description: "Array to shuffle."
			} },
			variants: [{ argumentNames: ["array"] }],
			examples: ["perform(@dvala.random.shuffle, [1, 2, 3, 4, 5])"],
			seeAlso: [
				"-effect-dvala.random",
				"-effect-dvala.random.item",
				"perform",
				"effect"
			]
		}
	},
	"dvala.time.now": {
		handler: (_arg, k) => {
			return {
				type: "Value",
				value: Date.now(),
				k
			};
		},
		arity: toFixedArity(0),
		docs: {
			category: "effect",
			description: "Returns the current timestamp in milliseconds since the Unix epoch. Equivalent to `Date.now()` in JavaScript.",
			returns: { type: "number" },
			args: {},
			variants: [{ argumentNames: [] }],
			examples: ["perform(@dvala.time.now)"],
			seeAlso: [
				"-effect-dvala.time.zone",
				"-effect-dvala.sleep",
				"perform",
				"effect"
			]
		}
	},
	"dvala.time.zone": {
		handler: (_arg, k) => {
			return {
				type: "Value",
				value: Intl.DateTimeFormat().resolvedOptions().timeZone,
				k
			};
		},
		arity: toFixedArity(0),
		docs: {
			category: "effect",
			description: "Returns the current IANA timezone string, e.g. `\"Europe/Stockholm\"` or `\"America/New_York\"`.",
			returns: { type: "string" },
			args: {},
			variants: [{ argumentNames: [] }],
			examples: ["perform(@dvala.time.zone)"],
			seeAlso: [
				"-effect-dvala.time.now",
				"perform",
				"effect"
			]
		}
	},
	"dvala.checkpoint": {
		arity: toFixedArity(1),
		docs: {
			category: "effect",
			description: "Captures a snapshot of the current program state (continuation stack). The snapshot is stored in an in-memory list accessible via `ctx.snapshots` in host handlers. Takes a mandatory message string. The standard fallback resumes with `null`, but host handlers can override the resume value. The snapshot is always captured regardless of whether a handler intercepts.",
			returns: { type: "null" },
			args: { message: {
				type: "string",
				description: "A human-readable label for the checkpoint."
			} },
			variants: [{ argumentNames: ["message"] }],
			examples: ["perform(@dvala.checkpoint, \"init\")", "perform(@dvala.checkpoint, \"analysis-done\")"],
			seeAlso: ["perform", "effect"]
		}
	},
	"dvala.sleep": {
		handler: (arg, k, sourceCodeInfo) => {
			const ms = arg;
			if (typeof ms !== "number" || ms < 0) throw new TypeError$1(`dvala.sleep requires a non-negative number argument, got ${typeof ms === "number" ? ms : typeof ms}`, sourceCodeInfo);
			return new Promise((resolve) => {
				setTimeout(() => resolve({
					type: "Value",
					value: null,
					k
				}), ms);
			});
		},
		arity: toFixedArity(1),
		docs: {
			category: "effect",
			description: "Waits for the specified number of milliseconds before resuming. Resumes with `null`. Only works in async execution (`run`) — `runSync` will throw when a Promise surfaces.",
			returns: { type: "null" },
			args: { ms: {
				type: "number",
				description: "The number of milliseconds to sleep. Must be a non-negative number."
			} },
			variants: [{ argumentNames: ["ms"] }],
			examples: [{
				code: "do perform(@dvala.sleep, 1000); \"awake!\" end",
				noRun: true
			}, {
				code: "do perform(@dvala.io.print, \"waiting...\"); perform(@dvala.sleep, 500); perform(@dvala.io.print, \"done!\") end",
				noRun: true
			}],
			seeAlso: [
				"-effect-dvala.time.now",
				"perform",
				"effect"
			]
		}
	},
	"dvala.host": {
		arity: toFixedArity(1),
		docs: {
			category: "effect",
			description: "Requests a host-injected value by name. The host must install an effect handler for `@dvala.host` that resumes with the value. If no handler is installed, a descriptive error is thrown. Use this to receive configuration and bindings from the host environment.",
			returns: { type: "any" },
			args: { name: {
				type: "string",
				description: "The name of the host binding to retrieve."
			} },
			variants: [{ argumentNames: ["name"] }],
			examples: [{
				code: "let configExists = perform(@dvala.host, \"configExists\")",
				noRun: true
			}],
			seeAlso: [
				"-effect-dvala.env",
				"-effect-dvala.args",
				"perform",
				"effect"
			]
		}
	},
	"dvala.env": {
		handler: (arg, k, sourceCodeInfo) => {
			if (typeof arg !== "string") throw new TypeError$1(`@dvala.env requires a string argument, got ${typeof arg}`, sourceCodeInfo);
			if (typeof process === "undefined" || !process.env) return {
				type: "Value",
				value: null,
				k
			};
			return {
				type: "Value",
				value: process.env[arg] ?? null,
				k
			};
		},
		arity: toFixedArity(1),
		docs: {
			category: "effect",
			description: "Reads an environment variable by name. Returns the value as a string, or `null` if the variable is not set. Per-entry only — fetch-all is not supported for security and observability.",
			returns: { type: ["string", "null"] },
			args: { name: {
				type: "string",
				description: "The environment variable name, e.g. `\"HOME\"` or `\"PATH\"`."
			} },
			variants: [{ argumentNames: ["name"] }],
			examples: [{
				code: "perform(@dvala.env, \"HOME\")",
				noRun: true
			}, {
				code: "let port = perform(@dvala.env, \"PORT\") ?? \"3000\"",
				noRun: true
			}],
			seeAlso: [
				"-effect-dvala.host",
				"-effect-dvala.args",
				"perform",
				"effect"
			]
		}
	},
	"dvala.args": {
		handler: (_arg, k) => {
			if (typeof process === "undefined" || !process.argv) return {
				type: "Value",
				value: PersistentVector.empty(),
				k
			};
			const args = process.argv.slice(2);
			return {
				type: "Value",
				value: PersistentVector.from(args),
				k
			};
		},
		arity: toFixedArity(0),
		docs: {
			category: "effect",
			description: "Returns the command-line arguments as an array of strings. The `node` binary and script path are stripped — only user arguments are included. Returns an empty array in non-CLI contexts (e.g. browser).",
			returns: { type: "array" },
			args: {},
			variants: [{ argumentNames: [] }],
			examples: [{
				code: "let args = perform(@dvala.args)",
				noRun: true
			}, {
				code: "let [filename, ...flags] = perform(@dvala.args)",
				noRun: true
			}],
			seeAlso: [
				"-effect-dvala.host",
				"-effect-dvala.env",
				"perform",
				"effect"
			]
		}
	}
};
/** All standard effect names. */
const standardEffectNames = new Set(Object.keys(standardEffects));
/** All standard effect definitions (for reference data generation). */
const allStandardEffectDefinitions = standardEffects;
/**
* Look up a standard effect definition by name.
* Returns undefined if the effect is not a standard effect.
*/
function getStandardEffectDefinition(effectName) {
	return standardEffects[effectName];
}
/**
* Look up a standard effect handler by name.
* Validates arity before calling the handler.
* Returns undefined if the effect is not a standard effect.
*/
function getStandardEffectHandler(effectName) {
	const handler = standardEffects[effectName]?.handler;
	if (!handler) return void 0;
	return (arg, k, sourceCodeInfo) => {
		return handler(arg, k, sourceCodeInfo);
	};
}
//#endregion
//#region src/evaluator/trampoline-evaluator.ts
/**
* Trampoline evaluator — explicit-stack evaluation engine.
*
* `stepNode(node, env, k)` maps an AST node to the next `Step`.
* `applyFrame(frame, value, k)` processes a completed sub-result against a frame.
* `tick(step)` processes one step and returns the next (or a Promise<Step> for async).
* `runSyncTrampoline(step)` runs the trampoline synchronously to completion.
* `runAsyncTrampoline(step)` runs the trampoline asynchronously to completion.
*
* Entry points:
* - `evaluate(ast, contextStack)` — evaluate an AST (sync or async)
* - `evaluateNode(node, contextStack)` — evaluate a single node (sync or async)
*
* Design principles:
* - `stepNode` is always synchronous and returns `Step`.
* - `applyFrame` may return `Step | Promise<Step>` when normal expressions
*   or compound function types produce async results.
* - Normal built-in expressions are called directly with pre-evaluated args.
* - All binding and pattern matching use frame-based slot processing.
* - All state lives in frames (no JS closures) — enabling serialization later.
*/
let coverageNodeIdCounter = -1;
const allocateCoverageNodeId = () => coverageNodeIdCounter--;
function evaluateObjectAsFunction(fn, params, sourceCodeInfo) {
	if (params.size !== 1) throw new TypeError$1("Object as function requires one string parameter.", sourceCodeInfo);
	const key = params.get(0);
	assertString(key, sourceCodeInfo);
	if (!fn.has(key)) throw new KeyError(`Key '${key}' not found in object`, sourceCodeInfo);
	return toAny(fn.get(key));
}
function evaluateArrayAsFunction(fn, params, sourceCodeInfo) {
	if (params.size !== 1) throw new TypeError$1("Array as function requires one non negative integer parameter.", sourceCodeInfo);
	const index = params.get(0);
	assertNumber(index, sourceCodeInfo, {
		integer: true,
		nonNegative: true
	});
	if (index < 0 || index >= fn.size) throw new KeyError(`Index ${index} out of bounds for array of size ${fn.size}`, sourceCodeInfo);
	return toAny(fn.get(index));
}
function evaluateStringAsFunction(fn, params, sourceCodeInfo) {
	if (params.size !== 1) throw new TypeError$1("String as function requires one Obj parameter.", sourceCodeInfo);
	const param = toAny(params.get(0));
	if (isObj(param)) {
		if (!param.has(fn)) throw new KeyError(`Key '${fn}' not found in object`, sourceCodeInfo);
		return toAny(param.get(fn));
	}
	if (isNumber(param, { integer: true })) {
		if (param < 0 || param >= fn.length) throw new KeyError(`Index ${param} out of bounds for string of length ${fn.length}`, sourceCodeInfo);
		return toAny(fn[param]);
	}
	throw new TypeError$1(`string as function expects Obj or integer parameter, got ${valueToString(param)}`, sourceCodeInfo);
}
function evaluateNumberAsFunction(fn, params, sourceCodeInfo) {
	assertNumber(fn, void 0, { integer: true });
	if (params.size !== 1) throw new TypeError$1("Number as function requires one Arr parameter.", sourceCodeInfo);
	const param = params.get(0);
	assertSeq(param, sourceCodeInfo);
	const size = typeof param === "string" ? param.length : param.size;
	if (fn < 0 || fn >= size) throw new KeyError(`Index ${fn} out of bounds for sequence of size ${size}`, sourceCodeInfo);
	return toAny(typeof param === "string" ? param[fn] : param.get(fn));
}
function evaluateReservedSymbol(node, env) {
	const reservedName = node[1];
	if (![
		"true",
		"false",
		"null"
	].includes(reservedName)) throw new TypeError$1(`Reserved symbol ${reservedName} cannot be evaluated`, env.resolve(node[2]));
	const value = reservedSymbolRecord[reservedName];
	return asNonUndefined(value, env.resolve(node[2]));
}
function evaluateFunction(fn, contextStack) {
	const functionContext = {};
	const context = fn[0].reduce((ctx, arg) => {
		Object.keys(getAllBindingTargetNames(arg)).forEach((name) => {
			ctx[name] = { value: null };
		});
		return ctx;
	}, {});
	getUndefinedSymbols(fn[1], contextStack.new(context), builtin).forEach((name) => {
		const value = contextStack.getValue(name);
		if (isAny(value)) functionContext[name] = { value };
	});
	return [
		fn[0],
		fn[1],
		functionContext
	];
}
/**
* Given an AST node, its environment, and a continuation stack, return
* the next Step for the trampoline to process.
*
* Leaf nodes (numbers, strings, symbols) immediately produce values.
* Compound nodes (expressions) push frames and return sub-evaluations.
*/
function stepNode(node, env, k) {
	switch (node[0]) {
		case NodeTypes.Num: return {
			type: "Value",
			value: node[1],
			k
		};
		case NodeTypes.Str: return {
			type: "Value",
			value: node[1],
			k
		};
		case NodeTypes.Atom: {
			const name = node[1];
			const atomValue = {
				[ATOM_SYMBOL]: true,
				name
			};
			Object.defineProperty(atomValue, Symbol.for("nodejs.util.inspect.custom"), {
				value: () => `:${name}`,
				enumerable: false
			});
			return {
				type: "Value",
				value: atomValue,
				k
			};
		}
		case NodeTypes.Builtin:
		case NodeTypes.Special:
		case NodeTypes.Sym: return {
			type: "Value",
			value: env.evaluateSymbol(node),
			k
		};
		case NodeTypes.Reserved: return {
			type: "Value",
			value: evaluateReservedSymbol(node, env),
			k
		};
		case NodeTypes.Call: return stepNormalExpression(node, env, k);
		case NodeTypes.MacroCall: {
			const [fnNode, argNodes] = node[1];
			const sourceCodeInfo = env.resolve(node[2]);
			const callee = env.evaluateSymbol(fnNode);
			if (!isMacroFunction(callee)) throw new TypeError$1(`# prefix requires a macro, but '${fnNode[1]}' is not a macro`, sourceCodeInfo);
			return callMacro(callee, argNodes, env, sourceCodeInfo, k);
		}
		case NodeTypes.If: {
			const [conditionNode, thenNode, elseNode] = node[1];
			return {
				type: "Eval",
				node: conditionNode,
				env,
				k: cons({
					type: "IfBranch",
					thenNode,
					elseNode,
					env,
					sourceCodeInfo: env.resolve(node[2])
				}, k)
			};
		}
		case NodeTypes.Block: {
			const nodes = node[1];
			const sourceCodeInfo = env.resolve(node[2]);
			const newEnv = env.create({});
			if (nodes.length === 0) throw new TypeError$1("Block AST requires at least one expression", sourceCodeInfo);
			if (nodes.length === 1) return {
				type: "Eval",
				node: nodes[0],
				env: newEnv,
				k
			};
			const frame = {
				type: "Sequence",
				nodes,
				index: 1,
				env: newEnv,
				sourceCodeInfo
			};
			return {
				type: "Eval",
				node: nodes[0],
				env: newEnv,
				k: cons(frame, k)
			};
		}
		case NodeTypes.Effect: return {
			type: "Value",
			value: getEffectRef(node[1]),
			k
		};
		case NodeTypes.Recur: {
			const nodes = node[1];
			const sourceCodeInfo = env.resolve(node[2]);
			if (nodes.length === 0) return handleRecur(PersistentVector.empty(), k, sourceCodeInfo);
			const frame = {
				type: "Recur",
				nodes,
				index: 1,
				params: PersistentVector.empty(),
				env,
				sourceCodeInfo
			};
			if (nodes.length === 1) {
				const singleFrame = {
					...frame,
					index: 1
				};
				return {
					type: "Eval",
					node: nodes[0],
					env,
					k: cons(singleFrame, k)
				};
			}
			return {
				type: "Eval",
				node: nodes[0],
				env,
				k: cons(frame, k)
			};
		}
		case NodeTypes.Array: {
			const nodes = node[1];
			const sourceCodeInfo = env.resolve(node[2]);
			if (nodes.length === 0) return {
				type: "Value",
				value: PersistentVector.empty(),
				k
			};
			const firstNode = nodes[0];
			const isFirstSpread = isSpreadNode(firstNode);
			const frame = {
				type: "ArrayBuild",
				nodes,
				index: 0,
				result: PersistentVector.empty(),
				isSpread: isFirstSpread,
				env,
				sourceCodeInfo
			};
			return {
				type: "Eval",
				node: isFirstSpread ? firstNode[1] : firstNode,
				env,
				k: cons(frame, k)
			};
		}
		case NodeTypes.Parallel: return {
			type: "Eval",
			node: node[1],
			env,
			k: cons({
				type: "ConcurrentArg",
				mode: "parallel",
				env
			}, k)
		};
		case NodeTypes.Race: return {
			type: "Eval",
			node: node[1],
			env,
			k: cons({
				type: "ConcurrentArg",
				mode: "race",
				env
			}, k)
		};
		case NodeTypes.Settled: return {
			type: "Eval",
			node: node[1],
			env,
			k: cons({
				type: "ConcurrentArg",
				mode: "settled",
				env
			}, k)
		};
		case NodeTypes.Perform: {
			const [effectExpr, payloadExpr] = node[1];
			const sourceCodeInfo = env.resolve(node[2]);
			const allNodes = payloadExpr ? [effectExpr, payloadExpr] : [effectExpr];
			const frame = {
				type: "PerformArgs",
				argNodes: allNodes,
				index: 1,
				params: PersistentVector.empty(),
				env,
				sourceCodeInfo
			};
			return {
				type: "Eval",
				node: allNodes[0],
				env,
				k: cons(frame, k)
			};
		}
		case NodeTypes.Let: {
			const [target, valueNode] = node[1];
			return {
				type: "Eval",
				node: valueNode,
				env,
				k: cons({
					type: "LetBind",
					target,
					env,
					sourceCodeInfo: env.resolve(node[2])
				}, k)
			};
		}
		case NodeTypes.Function: {
			const fn = node[1];
			const evaluatedFunc = evaluateFunction(fn, env);
			const min = evaluatedFunc[0].filter((arg) => arg[0] !== bindingTargetTypes.rest && arg[1][1] === void 0).length;
			const max = evaluatedFunc[0].some((arg) => arg[0] === bindingTargetTypes.rest) ? void 0 : evaluatedFunc[0].length;
			const arity = {
				min: min > 0 ? min : void 0,
				max
			};
			return {
				type: "Value",
				value: {
					[FUNCTION_SYMBOL]: true,
					sourceCodeInfo: env.resolve(node[2]),
					functionType: "UserDefined",
					name: void 0,
					evaluatedfunction: evaluatedFunc,
					arity,
					docString: ""
				},
				k
			};
		}
		case NodeTypes.Macro: {
			const fn = node[1];
			const evaluatedFunc = evaluateFunction(fn, env);
			const min = evaluatedFunc[0].filter((arg) => arg[0] !== bindingTargetTypes.rest && arg[1][1] === void 0).length;
			const max = evaluatedFunc[0].some((arg) => arg[0] === bindingTargetTypes.rest) ? void 0 : evaluatedFunc[0].length;
			const arity = {
				min: min > 0 ? min : void 0,
				max
			};
			return {
				type: "Value",
				value: {
					[FUNCTION_SYMBOL]: true,
					sourceCodeInfo: env.resolve(node[2]),
					functionType: "Macro",
					name: void 0,
					evaluatedfunction: evaluatedFunc,
					arity,
					docString: ""
				},
				k
			};
		}
		case NodeTypes.Handler: {
			const [parsedClauses, transform, shallow, linear] = node[1];
			const clauses = parsedClauses.map((c) => ({
				effectName: c.effectName,
				params: c.params,
				body: c.body
			}));
			const clauseMap = /* @__PURE__ */ new Map();
			for (const clause of clauses) clauseMap.set(clause.effectName, clause);
			return {
				type: "Value",
				value: {
					[FUNCTION_SYMBOL]: true,
					sourceCodeInfo: env.resolve(node[2]),
					functionType: "Handler",
					clauses,
					clauseMap,
					transform,
					shallow: shallow ?? false,
					linear: linear ?? false,
					closureEnv: env,
					arity: {
						min: 1,
						max: 1
					}
				},
				k
			};
		}
		case NodeTypes.WithHandler: {
			const [handlerExpr, bodyExprs] = node[1];
			return {
				type: "Eval",
				node: handlerExpr,
				env,
				k: cons({
					type: "WithHandlerSetup",
					bodyExprs,
					env,
					sourceCodeInfo: env.resolve(node[2])
				}, k)
			};
		}
		case NodeTypes.Resume: {
			const payload = node[1];
			const sourceCodeInfo = env.resolve(node[2]);
			const resumeLookup = env.lookUpByName("resume");
			if (resumeLookup === null) throw new RuntimeError("`resume` can only be used inside a handler clause", sourceCodeInfo);
			const resumeFn = resumeLookup.value;
			if (payload === "ref") return {
				type: "Value",
				value: resumeFn,
				k
			};
			return {
				type: "Eval",
				node: payload,
				env,
				k: cons({
					type: "ResumeCall",
					resumeFn,
					env,
					sourceCodeInfo
				}, k)
			};
		}
		case NodeTypes.CodeTmpl: {
			const [bodyAst, spliceExprs] = node[1];
			const sourceCodeInfo = env.resolve(node[2]);
			const renameMap = buildRenameMap(bodyAst);
			if (spliceExprs.length === 0) return {
				type: "Value",
				value: toAny(bodyAst.length === 1 ? astToData(bodyAst[0], [], renameMap) : bodyAst.map((n) => astToData(n, [], renameMap))),
				k
			};
			const frame = {
				type: "CodeTemplateBuild",
				bodyAst,
				spliceExprs,
				index: 0,
				values: [],
				renameMap,
				env,
				sourceCodeInfo
			};
			return {
				type: "Eval",
				node: spliceExprs[0],
				env,
				k: cons(frame, k)
			};
		}
		case NodeTypes.Object: {
			const entries = node[1];
			const sourceCodeInfo = env.resolve(node[2]);
			if (entries.length === 0) return {
				type: "Value",
				value: PersistentMap.empty(),
				k
			};
			const firstEntry = entries[0];
			const isFirstSpread = isSpreadNode(firstEntry);
			const frame = {
				type: "ObjectBuild",
				entries,
				index: 0,
				result: PersistentMap.empty(),
				currentKey: null,
				isSpread: isFirstSpread,
				env,
				sourceCodeInfo
			};
			return {
				type: "Eval",
				node: isFirstSpread ? firstEntry[1] : firstEntry[0],
				env,
				k: cons(frame, k)
			};
		}
		case NodeTypes.And: {
			const nodes = node[1];
			const sourceCodeInfo = env.resolve(node[2]);
			if (nodes.length === 0) return {
				type: "Value",
				value: true,
				k
			};
			const frame = {
				type: "And",
				nodes,
				index: 1,
				env,
				sourceCodeInfo
			};
			if (nodes.length === 1) return {
				type: "Eval",
				node: nodes[0],
				env,
				k
			};
			return {
				type: "Eval",
				node: nodes[0],
				env,
				k: cons(frame, k)
			};
		}
		case NodeTypes.Or: {
			const nodes = node[1];
			const sourceCodeInfo = env.resolve(node[2]);
			if (nodes.length === 0) return {
				type: "Value",
				value: false,
				k
			};
			const frame = {
				type: "Or",
				nodes,
				index: 1,
				env,
				sourceCodeInfo
			};
			if (nodes.length === 1) return {
				type: "Eval",
				node: nodes[0],
				env,
				k
			};
			return {
				type: "Eval",
				node: nodes[0],
				env,
				k: cons(frame, k)
			};
		}
		case NodeTypes.Qq: {
			const nodes = node[1];
			const sourceCodeInfo = env.resolve(node[2]);
			if (nodes.length === 0) return {
				type: "Value",
				value: null,
				k
			};
			const firstNode = nodes[0];
			if (nodes.length === 1) return {
				type: "Eval",
				node: firstNode,
				env,
				k
			};
			return {
				type: "Eval",
				node: firstNode,
				env,
				k: cons({
					type: "Qq",
					nodes,
					index: 1,
					env,
					sourceCodeInfo
				}, k)
			};
		}
		case NodeTypes.Match: {
			const [matchValueNode, cases] = node[1];
			return {
				type: "Eval",
				node: matchValueNode,
				env,
				k: cons({
					type: "Match",
					phase: "matchValue",
					matchValue: null,
					cases,
					index: 0,
					bindings: {},
					env,
					sourceCodeInfo: env.resolve(node[2])
				}, k)
			};
		}
		case NodeTypes.Loop: {
			const [bindings, body] = node[1];
			const sourceCodeInfo = env.resolve(node[2]);
			/* v8 ignore start */
			if (bindings.length === 0) {
				const newContext = {};
				const frame = {
					type: "LoopIterate",
					bindings,
					bindingContext: newContext,
					body,
					env: env.create(newContext),
					sourceCodeInfo
				};
				return {
					type: "Eval",
					node: body,
					env: env.create(newContext),
					k: cons(frame, k)
				};
			}
			/* v8 ignore stop */
			const frame = {
				type: "LoopBind",
				phase: "value",
				bindings,
				index: 0,
				context: {},
				body,
				env,
				sourceCodeInfo
			};
			return {
				type: "Eval",
				node: bindings[0][1],
				env,
				k: cons(frame, k)
			};
		}
		case NodeTypes.For: {
			const [loopBindings, body] = node[1];
			const sourceCodeInfo = env.resolve(node[2]);
			/* v8 ignore next 3 */
			if (loopBindings.length === 0) return {
				type: "Value",
				value: PersistentVector.empty(),
				k
			};
			const context = {};
			const newEnv = env.create(context);
			const frame = {
				type: "ForLoop",
				bindingNodes: loopBindings,
				body,
				result: PersistentVector.empty(),
				phase: "evalCollection",
				bindingLevel: 0,
				levelStates: [],
				context,
				env: newEnv,
				sourceCodeInfo
			};
			return {
				type: "Eval",
				node: loopBindings[0][0][1],
				env: newEnv,
				k: cons(frame, k)
			};
		}
		case NodeTypes.Import: {
			const moduleName = node[1];
			const sourceCodeInfo = env.resolve(node[2]);
			const valueModule = env.getValueModule(moduleName);
			if (valueModule.found) return {
				type: "Value",
				value: valueModule.value,
				k
			};
			if (moduleName.startsWith("./") || moduleName.startsWith("../") || moduleName.startsWith("/")) {
				if (!env.fileResolver) throw new TypeError$1(`File imports require a file resolver. Cannot import '${moduleName}'`, sourceCodeInfo);
				if (env.isResolvingFile(moduleName)) throw new TypeError$1(`Circular import detected: '${moduleName}'`, sourceCodeInfo);
				env.markFileResolving(moduleName);
				const source = env.fileResolver(moduleName, env.currentFileDir);
				const rawPath = moduleName.startsWith("/") ? moduleName : `${env.currentFileDir}/${moduleName}`;
				const parts = rawPath.split("/");
				const resolved = [];
				for (const part of parts) {
					if (part === "" || part === ".") continue;
					if (part === ".." && resolved.length > 0 && resolved[resolved.length - 1] !== "..") resolved.pop();
					else resolved.push(part);
				}
				const resolvedPath = (rawPath.startsWith("/") ? "/" : "") + resolved.join("/");
				const resolvedPathWithExt = resolvedPath.endsWith(".dvala") ? resolvedPath : `${resolvedPath}.dvala`;
				const ast = asNonUndefined(env.parseSource, sourceCodeInfo)(source, {
					debug: env.debug,
					filePath: env.debug ? resolvedPathWithExt : void 0,
					allocateNodeId: env.allocateNodeId
				});
				if (ast.sourceMap && env.sourceMap) {
					const sourceOffset = env.sourceMap.sources.length;
					env.sourceMap.sources.push(...ast.sourceMap.sources);
					for (const [nodeId, pos] of ast.sourceMap.positions) env.sourceMap.positions.set(nodeId, {
						...pos,
						source: pos.source + sourceOffset
					});
				}
				const fileNodes = ast.body;
				const fileEnv = env.create({});
				const importedFileDir = moduleName.substring(0, moduleName.lastIndexOf("/")) || ".";
				const previousFileDir = env.currentFileDir;
				fileEnv.currentFileDir = importedFileDir.startsWith("/") ? importedFileDir : `${env.currentFileDir}/${importedFileDir}`.replace(/\/\.\//g, "/").replace(/\/+/g, "/");
				const resolveFrame = {
					type: "FileResolve",
					moduleName,
					previousFileDir,
					env
				};
				if (fileNodes.length === 1) return {
					type: "Eval",
					node: fileNodes[0],
					env: fileEnv,
					k: cons(resolveFrame, k)
				};
				const sequenceFrame = {
					type: "Sequence",
					nodes: fileNodes,
					index: 1,
					env: fileEnv
				};
				return {
					type: "Eval",
					node: fileNodes[0],
					env: fileEnv,
					k: cons(sequenceFrame, cons(resolveFrame, k))
				};
			}
			const dvalaModule = env.getModule(moduleName);
			if (!dvalaModule) throw new TypeError$1(`Unknown module: '${moduleName}'`, sourceCodeInfo);
			let result = PersistentMap.empty();
			for (const [functionName, expression] of Object.entries(dvalaModule.functions)) result = result.assoc(functionName, {
				[FUNCTION_SYMBOL]: true,
				sourceCodeInfo,
				functionType: "Module",
				moduleName,
				functionName,
				arity: expression.arity
			});
			/* v8 ignore start */
			if (dvalaModule.source) {
				let nodes;
				if (env.coverage && dvalaModule.sourcePath) {
					if (!dvalaModule._coverageNodes) {
						const parseSource = asNonUndefined(env.parseSource, sourceCodeInfo);
						const moduleAst = parseSource(dvalaModule.source, {
							debug: true,
							filePath: dvalaModule.sourcePath,
							allocateNodeId: allocateCoverageNodeId
						});
						dvalaModule._coverageNodes = moduleAst.body;
						dvalaModule._coverageSourceMap = moduleAst.sourceMap;
						if (!dvalaModule._cachedNodes) dvalaModule._cachedNodes = parseSource(dvalaModule.source).body;
					}
					nodes = dvalaModule._coverageNodes;
					if (dvalaModule._coverageSourceMap && env.sourceMap) {
						const sourceOffset = env.sourceMap.sources.length;
						env.sourceMap.sources.push(...dvalaModule._coverageSourceMap.sources);
						for (const [nodeId, pos] of dvalaModule._coverageSourceMap.positions) env.sourceMap.positions.set(nodeId, {
							...pos,
							source: pos.source + sourceOffset
						});
					}
				} else {
					if (!dvalaModule._cachedNodes) dvalaModule._cachedNodes = asNonUndefined(env.parseSource, sourceCodeInfo)(dvalaModule.source).body;
					nodes = dvalaModule._cachedNodes;
				}
				const sourceEnv = env.create({});
				const mergeFrame = {
					type: "ImportMerge",
					tsFunctions: result,
					moduleName,
					module: dvalaModule,
					env,
					sourceCodeInfo
				};
				if (nodes.length === 1) return {
					type: "Eval",
					node: nodes[0],
					env: sourceEnv,
					k: cons(mergeFrame, k)
				};
				const sequenceFrame = {
					type: "Sequence",
					nodes,
					index: 1,
					env: sourceEnv
				};
				return {
					type: "Eval",
					node: nodes[0],
					env: sourceEnv,
					k: cons(sequenceFrame, cons(mergeFrame, k))
				};
			}
			/* v8 ignore stop */
			env.registerValueModule(moduleName, result);
			return {
				type: "Value",
				value: result,
				k
			};
		}
		case NodeTypes.SpecialExpression: return stepSpecialExpression(node, env, k);
		case NodeTypes.TmplStr: return stepTemplateString(node, env, k);
		/* v8 ignore next 2 */
		default: throw new TypeError$1(`${node[0]}-node cannot be evaluated`, env.resolve(node[2]));
	}
}
function stepTemplateString(node, env, k) {
	const segments = node[1];
	const sourceCodeInfo = env.resolve(node[2]);
	if (segments.length === 0) return {
		type: "Value",
		value: "",
		k
	};
	const frame = {
		type: "TemplateStringBuild",
		segments,
		index: 0,
		result: "",
		env,
		sourceCodeInfo
	};
	return {
		type: "Eval",
		node: segments[0],
		env,
		k: cons(frame, k)
	};
}
/**
* Normal expressions: evaluate arguments left-to-right, then dispatch.
* Push EvalArgsFrame + FiniteCheckFrame, then start evaluating the first arg.
*/
function stepNormalExpression(node, env, k) {
	const argNodes = node[1][1];
	const sourceCodeInfo = env.resolve(node[2]);
	if (isNormalExpressionNodeWithName(node)) {
		const nameSymbol = node[1][0];
		if (isUserDefinedSymbolNode(nameSymbol) || isBuiltinSymbolNode(nameSymbol)) {
			const callee = env.evaluateSymbol(nameSymbol);
			if (isMacroFunction(callee)) return callMacro(callee, argNodes, env, sourceCodeInfo, k);
		}
	}
	const nanFrame = {
		type: "FiniteCheck",
		sourceCodeInfo
	};
	const evalArgsFrame = {
		type: "EvalArgs",
		node,
		index: 0,
		params: PersistentVector.empty(),
		placeholders: [],
		env,
		sourceCodeInfo
	};
	let startIndex = 0;
	while (startIndex < argNodes.length) {
		const arg = argNodes[startIndex];
		if (arg[0] === NodeTypes.Reserved && arg[1] === "_") {
			evalArgsFrame.placeholders.push(evalArgsFrame.params.size);
			startIndex++;
		} else break;
	}
	evalArgsFrame.index = startIndex;
	if (startIndex >= argNodes.length) return dispatchCall(evalArgsFrame, cons(nanFrame, k));
	const firstArg = argNodes[startIndex];
	const newK = cons(evalArgsFrame, cons(nanFrame, k));
	if (isSpreadNode(firstArg)) return {
		type: "Eval",
		node: firstArg[1],
		env,
		k: newK
	};
	return {
		type: "Eval",
		node: firstArg,
		env,
		k: newK
	};
}
/**
* Special expressions: push a frame appropriate to the expression type
* and return an EvalStep for the first sub-expression.
*/
/* v8 ignore next 4 */
function stepSpecialExpression(node, env, _k) {
	const sourceCodeInfo = env.resolve(node[2]);
	throw new RuntimeError(`Unknown special expression type: ${node[1][0]}`, sourceCodeInfo);
}
/**
* After all arguments are collected in an EvalArgsFrame, determine what
* to call and return the next Step.
*/
function dispatchCall(frame, k) {
	const { node, params, placeholders, env, sourceCodeInfo } = frame;
	if (isNormalExpressionNodeWithName(node)) {
		const nameSymbol = node[1][0];
		if (placeholders.length > 0) {
			const fn = env.evaluateSymbol(nameSymbol);
			return {
				type: "Value",
				value: {
					[FUNCTION_SYMBOL]: true,
					function: asFunctionLike(fn, sourceCodeInfo),
					functionType: "Partial",
					params,
					placeholders,
					sourceCodeInfo,
					arity: toFixedArity(placeholders.length)
				},
				k
			};
		}
		if (isBuiltinSymbolNode(nameSymbol)) {
			const builtinName = nameSymbol[1];
			const normalExpression = builtin.normalExpressions[builtinName];
			if (env.pure && normalExpression.pure === false) throw new RuntimeError(`Cannot call impure function '${normalExpression.name}' in pure mode`, sourceCodeInfo);
			if (builtinName === "macroexpand") {
				const macroFn = params.get(0);
				if (!isMacroFunction(macroFn)) throw new TypeError$1("macroexpand: first argument must be a macro", sourceCodeInfo);
				return setupUserDefinedCall(macroFn, PersistentVector.from([...params].slice(1)), env, sourceCodeInfo, k);
			}
			/* v8 ignore next 3 */
			if (normalExpression.dvalaImpl) return setupUserDefinedCall(normalExpression.dvalaImpl, params, env, sourceCodeInfo, k);
			return wrapMaybePromiseAsStep(normalExpression.evaluate(params, sourceCodeInfo, env), k);
		}
		const fn = env.getValue(nameSymbol[1]);
		if (fn !== void 0) return dispatchFunction(asFunctionLike(fn, sourceCodeInfo), params, placeholders, env, sourceCodeInfo, k);
		throw new ReferenceError(nameSymbol[1], sourceCodeInfo);
	} else return {
		type: "Eval",
		node: node[1][0],
		env,
		k: cons({
			type: "CallFn",
			params,
			placeholders,
			env,
			sourceCodeInfo
		}, k)
	};
}
/**
* Dispatch a resolved function value with pre-evaluated parameters.
*/
function dispatchFunction(fn, params, placeholders, env, sourceCodeInfo, k) {
	if (placeholders.length > 0) return {
		type: "Value",
		value: {
			[FUNCTION_SYMBOL]: true,
			function: fn,
			functionType: "Partial",
			params,
			placeholders,
			sourceCodeInfo,
			arity: toFixedArity(placeholders.length)
		},
		k
	};
	if (isDvalaFunction(fn)) return dispatchDvalaFunction(fn, params, env, sourceCodeInfo, k);
	if (isPersistentVector(fn)) return {
		type: "Value",
		value: evaluateArrayAsFunction(fn, params, sourceCodeInfo),
		k
	};
	if (isObj(fn)) return {
		type: "Value",
		value: evaluateObjectAsFunction(fn, params, sourceCodeInfo),
		k
	};
	if (typeof fn === "string") return {
		type: "Value",
		value: evaluateStringAsFunction(fn, params, sourceCodeInfo),
		k
	};
	if (isNumber(fn)) return {
		type: "Value",
		value: evaluateNumberAsFunction(fn, params, sourceCodeInfo),
		k
	};
	/* v8 ignore next 1 */
	throw new RuntimeError("Unexpected function type", sourceCodeInfo);
}
/**
* Dispatch a DvalaFunction. User-defined functions are set up with frames;
* some compound function types still use the recursive executor for iteration.
*/
function dispatchDvalaFunction(fn, params, env, sourceCodeInfo, k) {
	switch (fn.functionType) {
		case "UserDefined":
		case "Macro": return setupUserDefinedCall(fn, params, env, sourceCodeInfo, k);
		case "Constantly": return {
			type: "Value",
			value: fn.value,
			k
		};
		case "QualifiedMatcher": {
			assertNumberOfParams({
				min: 1,
				max: 1
			}, params.size, fn.sourceCodeInfo ?? sourceCodeInfo);
			const entity = params.get(0);
			let qName = null;
			if (isEffect(entity)) qName = entity.name;
			if (qName === null) return {
				type: "Value",
				value: false,
				k
			};
			if (fn.matchType === "string") return {
				type: "Value",
				value: qualifiedNameMatchesPattern(qName, fn.pattern),
				k
			};
			return {
				type: "Value",
				value: new RegExp(fn.pattern, fn.flags).test(qName),
				k
			};
		}
		case "Handler": {
			assertNumberOfParams({
				min: 1,
				max: 1
			}, params.size, fn.sourceCodeInfo ?? sourceCodeInfo);
			const thunkFn = asFunctionLike(params.get(0), sourceCodeInfo);
			const handleFrame = {
				type: "AlgebraicHandle",
				handler: fn,
				env: fn.closureEnv,
				sourceCodeInfo
			};
			return dispatchFunction(thunkFn, PersistentVector.empty(), [], env, sourceCodeInfo, cons(handleFrame, k));
		}
		case "Resume": {
			const resumeValue = params.size > 0 ? params.get(0) : null;
			const performK = fn.performK;
			const handler = fn.handler;
			assertContinuationValid(performK, sourceCodeInfo);
			const freshInnerFrames = freshenContinuationEnvs(listTake(performK, listSize(performK) - 1));
			if (handler.shallow) return {
				type: "Value",
				value: resumeValue,
				k: listPrependAll(listToArray(freshInnerFrames), k)
			};
			const newHandleFrame = {
				type: "AlgebraicHandle",
				handler,
				env: fn.handlerEnv,
				sourceCodeInfo: handler.sourceCodeInfo
			};
			return {
				type: "Value",
				value: resumeValue,
				k: listPrependAll(listToArray(freshInnerFrames), cons(newHandleFrame, k))
			};
		}
		case "Partial": {
			const actualParamsArr = [...fn.params];
			if (params.size !== fn.placeholders.length) throw new TypeError$1(`(partial) expects ${fn.placeholders.length} arguments, got ${params.size}.`, sourceCodeInfo);
			const paramsCopy = [...params];
			for (const placeholderIndex of fn.placeholders) actualParamsArr.splice(placeholderIndex, 0, paramsCopy.shift());
			return dispatchFunction(fn.function, PersistentVector.from(actualParamsArr), [], env, sourceCodeInfo, k);
		}
		case "Fnull": {
			const fnulledParamsArr = Array.from(params).map((param, index) => param === null ? toAny(fn.params.get(index)) : param);
			return dispatchFunction(fn.function, PersistentVector.from(fnulledParamsArr), [], env, sourceCodeInfo, k);
		}
		case "Complement": {
			const frame = {
				type: "Complement",
				sourceCodeInfo
			};
			return dispatchFunction(fn.function, params, [], env, sourceCodeInfo, cons(frame, k));
		}
		case "Comp": {
			const fns = fn.params;
			if (fns.size === 0) {
				if (params.size !== 1) throw new TypeError$1(`(comp) expects one argument, got ${valueToString(params.size)}.`, sourceCodeInfo);
				return {
					type: "Value",
					value: asAny(params.get(0), sourceCodeInfo),
					k
				};
			}
			const startIndex = fns.size - 1;
			const frame = {
				type: "Comp",
				fns,
				index: startIndex - 1,
				env,
				sourceCodeInfo
			};
			return dispatchFunction(asFunctionLike(fns.get(startIndex), sourceCodeInfo), params, [], env, sourceCodeInfo, cons(frame, k));
		}
		case "Juxt": {
			const fns = fn.params;
			if (fns.size === 0) return {
				type: "Value",
				value: PersistentVector.empty(),
				k
			};
			const frame = {
				type: "Juxt",
				fns,
				params,
				index: 1,
				results: PersistentVector.empty(),
				env,
				sourceCodeInfo
			};
			return dispatchFunction(asFunctionLike(fns.get(0), sourceCodeInfo), params, [], env, sourceCodeInfo, cons(frame, k));
		}
		case "EveryPred": {
			const checks = [];
			for (const f of fn.params) for (const p of params) checks.push({
				fn: asFunctionLike(f, sourceCodeInfo),
				param: p
			});
			if (checks.length === 0) return {
				type: "Value",
				value: true,
				k
			};
			const frame = {
				type: "EveryPred",
				checks,
				index: 1,
				env,
				sourceCodeInfo
			};
			const firstCheck = checks[0];
			return dispatchFunction(firstCheck.fn, PersistentVector.from([firstCheck.param]), [], env, sourceCodeInfo, cons(frame, k));
		}
		case "SomePred": {
			const checks = [];
			for (const f of fn.params) for (const p of params) checks.push({
				fn: asFunctionLike(f, sourceCodeInfo),
				param: p
			});
			if (checks.length === 0) return {
				type: "Value",
				value: false,
				k
			};
			const frame = {
				type: "SomePred",
				checks,
				index: 1,
				env,
				sourceCodeInfo
			};
			const firstCheck = checks[0];
			return dispatchFunction(firstCheck.fn, PersistentVector.from([firstCheck.param]), [], env, sourceCodeInfo, cons(frame, k));
		}
		case "SpecialBuiltin": {
			const specialExpression = asNonUndefined(builtin.specialExpressions[fn.specialBuiltinSymbolType], sourceCodeInfo);
			if (specialExpression.evaluateAsNormalExpression) return wrapMaybePromiseAsStep(specialExpression.evaluateAsNormalExpression(params, sourceCodeInfo, env), k);
			throw new TypeError$1(`Special builtin function ${fn.specialBuiltinSymbolType} is not supported as normal expression.`, sourceCodeInfo);
		}
		case "Module": {
			const dvalaModule = env.getModule(fn.moduleName);
			if (!dvalaModule) throw new TypeError$1(`Module '${fn.moduleName}' not found.`, sourceCodeInfo);
			const expression = dvalaModule.functions[fn.functionName];
			if (!expression) throw new TypeError$1(`Function '${fn.functionName}' not found in module '${fn.moduleName}'.`, sourceCodeInfo);
			if (env.pure && expression.pure === false) throw new RuntimeError(`Cannot call impure function '${fn.functionName}' in pure mode`, sourceCodeInfo);
			assertNumberOfParams(expression.arity, params.size, sourceCodeInfo);
			if (expression.dvalaImpl) return setupUserDefinedCall(expression.dvalaImpl, params, env, sourceCodeInfo, k);
			const result = expression.evaluate(params, sourceCodeInfo, env);
			return wrapMaybePromiseAsStep(Array.isArray(result) ? fromJS(result) : result, k);
		}
		case "Builtin": {
			const normalExpression = builtin.normalExpressions[fn.normalBuiltinSymbolType];
			if (env.pure && normalExpression.pure === false) throw new RuntimeError(`Cannot call impure function '${normalExpression.name}' in pure mode`, sourceCodeInfo);
			if (normalExpression.dvalaImpl) return setupUserDefinedCall(normalExpression.dvalaImpl, params, env, sourceCodeInfo, k);
			const result = normalExpression.evaluate(params, sourceCodeInfo, env);
			return wrapMaybePromiseAsStep(Array.isArray(result) ? fromJS(result) : result, k);
		}
	}
}
/**
* Set up a user-defined function call: bind params, push FnBodyFrame.
*
* Uses frame-based binding slots for all argument binding, enabling
* suspension/serialization at any point during destructuring.
*/
function setupUserDefinedCall(fn, params, env, sourceCodeInfo, k) {
	if (!arityAcceptsMin(fn.arity, params.size)) throw new TypeError$1(`Expected ${fn.arity} arguments, got ${params.size}.`, sourceCodeInfo);
	const nbrOfNonRestArgs = fn.evaluatedfunction[0].filter((arg) => arg[0] !== bindingTargetTypes.rest).length;
	return continueArgSlotBinding(fn, params, 0, nbrOfNonRestArgs, { self: { value: fn } }, env, sourceCodeInfo, k);
}
/**
* Continue binding function arguments using slot-based binding.
* Handles provided args, defaults, rest, then proceeds to body.
*/
function continueArgSlotBinding(fn, params, argIndex, nbrOfNonRestArgs, context, outerEnv, sourceCodeInfo, k) {
	const evaluatedFunc = fn.evaluatedfunction;
	const args = evaluatedFunc[0];
	const closureContext = evaluatedFunc[2];
	const bindingEnv = outerEnv.create(closureContext).create(context);
	if (argIndex < params.size && argIndex < nbrOfNonRestArgs) {
		const param = toAny(params.get(argIndex));
		const argTarget = args[argIndex];
		return startBindingSlots(argTarget, param, bindingEnv, sourceCodeInfo, cons({
			type: "FnArgSlotComplete",
			fn,
			params,
			argIndex,
			nbrOfNonRestArgs,
			context,
			outerEnv,
			sourceCodeInfo
		}, k));
	}
	if (argIndex < nbrOfNonRestArgs) return continueBindingArgs(fn, params, argIndex, nbrOfNonRestArgs, context, outerEnv, sourceCodeInfo, k);
	return handleRestArgAndBody(fn, params, nbrOfNonRestArgs, context, outerEnv, sourceCodeInfo, k);
}
/**
* Handle rest argument binding and proceed to body evaluation.
*/
function handleRestArgAndBody(fn, params, nbrOfNonRestArgs, context, outerEnv, sourceCodeInfo, k) {
	const evaluatedFunc = fn.evaluatedfunction;
	const args = evaluatedFunc[0];
	const closureContext = evaluatedFunc[2];
	const bindingEnv = outerEnv.create(closureContext).create(context);
	const rest = PersistentVector.from(Array.from(params).slice(nbrOfNonRestArgs).map(toAny));
	const restArgument = args.find((arg) => arg[0] === bindingTargetTypes.rest);
	if (restArgument) return startBindingSlots(restArgument, rest, bindingEnv, sourceCodeInfo, cons({
		type: "FnRestArgComplete",
		fn,
		context,
		outerEnv,
		sourceCodeInfo
	}, k));
	return proceedToFnBody(fn, context, outerEnv, sourceCodeInfo, k);
}
/**
* Start evaluating function body.
*/
function proceedToFnBody(fn, context, outerEnv, sourceCodeInfo, k) {
	const closureContext = fn.evaluatedfunction[2];
	const bodyEnv = outerEnv.create(closureContext).create(context);
	const bodyNodes = fn.evaluatedfunction[1];
	if (bodyNodes.length === 0) return {
		type: "Value",
		value: null,
		k
	};
	const fnBodyFrame = {
		type: "FnBody",
		fn,
		bodyIndex: 1,
		env: bodyEnv,
		outerEnv,
		sourceCodeInfo
	};
	return {
		type: "Eval",
		node: bodyNodes[0],
		env: bodyEnv,
		k: cons(fnBodyFrame, k)
	};
}
/**
* Given a completed sub-expression value and the top frame from the
* continuation stack, determine the next Step.
*/
function applyFrame(frame, value, k) {
	switch (frame.type) {
		case "Sequence": return applySequence(frame, value, k);
		case "IfBranch": return applyIfBranch(frame, value, k);
		case "Match": return applyMatch(frame, value, k);
		case "And": return applyAnd(frame, value, k);
		case "Or": return applyOr(frame, value, k);
		case "Qq": return applyQq(frame, value, k);
		case "TemplateStringBuild": return applyTemplateStringBuild(frame, value, k);
		case "ArrayBuild": return applyArrayBuild(frame, value, k);
		case "ObjectBuild": return applyObjectBuild(frame, value, k);
		case "LetBind": return applyLetBind(frame, value, k);
		case "LetBindComplete": return applyLetBindComplete(frame, value, k);
		case "LoopBind": return applyLoopBind(frame, value, k);
		case "LoopBindComplete": return applyLoopBindComplete(frame, value, k);
		case "LoopIterate": return applyLoopIterate(frame, value, k);
		case "ForLoop": return applyForLoop(frame, value, k);
		case "ForElementBindComplete": return applyForElementBindComplete(frame, value, k);
		case "ForLetBind": return applyForLetBind(frame, value, k);
		case "Recur": return applyRecur(frame, value, k);
		case "RecurLoopRebind": return applyRecurLoopRebind(frame, value, k);
		case "PerformArgs": return applyPerformArgs(frame, value, k);
		case "AlgebraicHandle": return applyAlgebraicHandleNormalCompletion(frame, value, k);
		case "HandlerTransform": return {
			type: "Value",
			value,
			k
		};
		case "HandlerClause": return applyHandlerClauseAbort(frame, value, k);
		case "ResumeCall": return dispatchFunction(asFunctionLike(frame.resumeFn, frame.sourceCodeInfo), PersistentVector.from([value]), [], frame.env, frame.sourceCodeInfo, k);
		case "WithHandlerSetup": return applyWithHandlerSetup(frame, value, k);
		case "ParallelResume": throw new RuntimeError("ParallelResumeFrame is no longer supported. Snapshot format has changed.", void 0);
		case "ConcurrentArg": {
			if (!isPersistentVector(value)) throw new TypeError$1(`${frame.mode}: expected an array of functions, got ${typeof value}`, void 0);
			const fns = [];
			let i = 0;
			for (const item of value) {
				if (!isDvalaFunction(item)) throw new TypeError$1(`${frame.mode}: branch at index ${i} is not a function`, void 0);
				fns.push(item);
				i++;
			}
			if (fns.length === 0) throw new TypeError$1(`${frame.mode}: requires at least one branch`, void 0);
			if (frame.mode === "race") return {
				type: "Race",
				branches: fns,
				env: frame.env,
				k
			};
			return {
				type: "Parallel",
				branches: fns,
				env: frame.env,
				k,
				mode: frame.mode
			};
		}
		case "ParallelBranchBarrier": return {
			type: "BranchComplete",
			value,
			branchCtx: frame.branchCtx
		};
		case "ReRunParallel": return {
			type: "ReRunParallelExec",
			resumedBranchValue: value,
			frame,
			k
		};
		case "ResumeParallel": return {
			type: "ResumeParallelExec",
			resumedBranchValue: value,
			frame,
			k
		};
		case "EvalArgs": return applyEvalArgs(frame, value, k);
		case "CallFn": return applyCallFn(frame, value, k);
		case "FnBody": return applyFnBody(frame, value, k);
		case "FnArgBind": return applyFnArgBind(frame, value, k);
		case "FnArgSlotComplete": return applyFnArgSlotComplete(frame, value, k);
		case "FnRestArgComplete": return applyFnRestArgComplete(frame, value, k);
		case "BindingSlot": return applyBindingSlot(frame, value, k);
		case "MatchSlot": return applyMatchSlot(frame, value, k);
		case "Complement": return {
			type: "Value",
			value: !value,
			k
		};
		case "Comp": return applyComp(frame, value, k);
		case "Juxt": return applyJuxt(frame, value, k);
		case "EveryPred": return applyEveryPred(frame, value, k);
		case "SomePred": return applySomePred(frame, value, k);
		case "FiniteCheck": return applyFiniteCheck(frame, value, k);
		case "ImportMerge": {
			const dvalaFunctions = isObj(value) ? value : PersistentMap.empty();
			for (const [name, fn] of dvalaFunctions) {
				const expression = frame.module.functions[name];
				if (expression && isUserDefinedFunction(fn)) expression.dvalaImpl = fn;
			}
			let dvalaOnlyFunctions = PersistentMap.empty();
			for (const [name, fn] of dvalaFunctions) if (!frame.module.functions[name]) dvalaOnlyFunctions = dvalaOnlyFunctions.assoc(name, fn);
			let merged = frame.tsFunctions;
			for (const [name, fn] of dvalaOnlyFunctions) merged = merged.assoc(name, fn);
			frame.env.registerValueModule(frame.moduleName, merged);
			return {
				type: "Value",
				value: merged,
				k
			};
		}
		case "FileResolve":
			frame.env.unmarkFileResolving(frame.moduleName);
			frame.env.registerValueModule(frame.moduleName, value);
			frame.env.currentFileDir = frame.previousFileDir;
			return {
				type: "Value",
				value,
				k
			};
		case "CodeTemplateBuild": return applyCodeTemplateBuild(frame, value, k);
		case "MacroEval": return applyMacroEval(frame, value, k);
		case "HandlerCleanup": return applyHandlerCleanup(frame, value, k);
		/* v8 ignore next 2 */
		default: throw new RuntimeError(`Unhandled frame type: ${frame.type}`, void 0);
	}
}
function applySequence(frame, _value, k) {
	const { nodes, index, env } = frame;
	if (index >= nodes.length) return {
		type: "Value",
		value: _value,
		k
	};
	if (index === nodes.length - 1) return {
		type: "Eval",
		node: nodes[index],
		env,
		k
	};
	const newFrame = {
		...frame,
		nodes: frame.nodes.slice(index + 1),
		index: 0
	};
	return {
		type: "Eval",
		node: nodes[index],
		env,
		k: cons(newFrame, k)
	};
}
function applyIfBranch(frame, value, k) {
	const { thenNode, elseNode, env } = frame;
	if (value) return {
		type: "Eval",
		node: thenNode,
		env,
		k,
		forceRecord: true
	};
	if (elseNode) return {
		type: "Eval",
		node: elseNode,
		env,
		k,
		forceRecord: true
	};
	throw new TypeError$1("If AST requires an else branch", env.resolve(thenNode[2]));
}
function applyMatch(frame, value, k) {
	const { cases, env } = frame;
	if (frame.phase === "matchValue") {
		const matchValue = value;
		return processMatchCase({
			...frame,
			matchValue,
			phase: "guard"
		}, k);
	}
	if (frame.phase === "guard") {
		if (!value) return processMatchCase({
			...frame,
			index: frame.index + 1,
			bindings: {}
		}, k);
		const context = {};
		for (const [name, val] of Object.entries(frame.bindings)) context[name] = { value: val };
		const newEnv = env.create(context);
		return {
			type: "Eval",
			node: cases[frame.index][1],
			env: newEnv,
			k,
			forceRecord: true
		};
	}
	return {
		type: "Value",
		value,
		k
	};
}
/**
* Process match cases starting from `frame.index`.
* Uses frame-based slot processing for pattern matching.
*/
function processMatchCase(frame, k) {
	const { matchValue, cases, index, env, sourceCodeInfo } = frame;
	if (index >= cases.length) throw new MatchError(`Non-exhaustive match — no case matched ${valueToString(matchValue)}`, sourceCodeInfo);
	const [pattern] = cases[index];
	return startMatchSlots(pattern, matchValue, frame, env, sourceCodeInfo, k);
}
function applyAnd(frame, value, k) {
	if (!value) return {
		type: "Value",
		value,
		k
	};
	const { nodes, index, env } = frame;
	if (index >= nodes.length) return {
		type: "Value",
		value,
		k
	};
	if (index === nodes.length - 1) return {
		type: "Eval",
		node: nodes[index],
		env,
		k,
		forceRecord: true
	};
	const newFrame = {
		...frame,
		index: index + 1
	};
	return {
		type: "Eval",
		node: nodes[index],
		env,
		k: cons(newFrame, k),
		forceRecord: true
	};
}
function applyOr(frame, value, k) {
	if (value) return {
		type: "Value",
		value,
		k
	};
	const { nodes, index, env } = frame;
	if (index >= nodes.length) return {
		type: "Value",
		value,
		k
	};
	if (index === nodes.length - 1) return {
		type: "Eval",
		node: nodes[index],
		env,
		k,
		forceRecord: true
	};
	const newFrame = {
		...frame,
		index: index + 1
	};
	return {
		type: "Eval",
		node: nodes[index],
		env,
		k: cons(newFrame, k),
		forceRecord: true
	};
}
function applyQq(frame, value, k) {
	if (value !== null) return {
		type: "Value",
		value,
		k
	};
	const { nodes, index, env } = frame;
	if (index >= nodes.length) return {
		type: "Value",
		value: null,
		k
	};
	if (index === nodes.length - 1) return {
		type: "Eval",
		node: nodes[index],
		env,
		k,
		forceRecord: true
	};
	const newFrame = {
		...frame,
		index: index + 1
	};
	return {
		type: "Eval",
		node: nodes[index],
		env,
		k: cons(newFrame, k),
		forceRecord: true
	};
}
function applyTemplateStringBuild(frame, value, k) {
	const { segments, env } = frame;
	const result = frame.result + String(value);
	const nextIndex = frame.index + 1;
	if (nextIndex >= segments.length) return {
		type: "Value",
		value: result,
		k
	};
	const newFrame = {
		...frame,
		index: nextIndex,
		result
	};
	return {
		type: "Eval",
		node: segments[nextIndex],
		env,
		k: cons(newFrame, k)
	};
}
function applyArrayBuild(frame, value, k) {
	const { nodes, env, sourceCodeInfo } = frame;
	let newResult;
	if (frame.isSpread) {
		if (!isPersistentVector(value) && !Array.isArray(value)) throw new TypeError$1("Spread value is not an array", sourceCodeInfo);
		let r = frame.result;
		for (const item of value) r = r.append(item);
		newResult = r;
	} else newResult = frame.result.append(value);
	const nextIndex = frame.index + 1;
	if (nextIndex >= nodes.length) return {
		type: "Value",
		value: newResult,
		k
	};
	const nextNode = nodes[nextIndex];
	const isNextSpread = isSpreadNode(nextNode);
	const newFrame = {
		...frame,
		index: nextIndex,
		result: newResult,
		isSpread: isNextSpread
	};
	return {
		type: "Eval",
		node: isNextSpread ? nextNode[1] : nextNode,
		env,
		k: cons(newFrame, k)
	};
}
function applyObjectBuild(frame, value, k) {
	const { entries, env, sourceCodeInfo } = frame;
	if (frame.isSpread) {
		if (!isObj(value)) throw new TypeError$1("Spread value is not an object", sourceCodeInfo);
		let newResult = frame.result;
		for (const [k2, v] of value) newResult = newResult.assoc(k2, v);
		const nextIndex = frame.index + 1;
		if (nextIndex >= entries.length) return {
			type: "Value",
			value: newResult,
			k
		};
		const nextEntry = entries[nextIndex];
		const isNextSpread = isSpreadNode(nextEntry);
		const newFrame = {
			...frame,
			index: nextIndex,
			result: newResult,
			currentKey: null,
			isSpread: isNextSpread
		};
		return {
			type: "Eval",
			node: isNextSpread ? nextEntry[1] : nextEntry[0],
			env,
			k: cons(newFrame, k)
		};
	}
	if (frame.currentKey === null) {
		assertString(value, sourceCodeInfo);
		return {
			type: "Eval",
			node: entries[frame.index][1],
			env,
			k: cons({
				...frame,
				currentKey: value
			}, k)
		};
	} else {
		const newResult = frame.result.assoc(frame.currentKey, value);
		const nextIndex = frame.index + 1;
		if (nextIndex >= entries.length) return {
			type: "Value",
			value: newResult,
			k
		};
		const nextEntry = entries[nextIndex];
		const isNextSpread = isSpreadNode(nextEntry);
		const newFrame = {
			...frame,
			index: nextIndex,
			result: newResult,
			currentKey: null,
			isSpread: isNextSpread
		};
		return {
			type: "Eval",
			node: isNextSpread ? nextEntry[1] : nextEntry[0],
			env,
			k: cons(newFrame, k)
		};
	}
}
function applyLetBind(frame, value, k) {
	const { target, env, sourceCodeInfo } = frame;
	if (target[0] === "symbol" && (isUserDefinedFunction(value) || isMacroFunction(value)) && value.name === void 0) value.name = target[1][0][1];
	return startBindingSlots(target, value, env, sourceCodeInfo, cons({
		type: "LetBindComplete",
		originalValue: value,
		env,
		sourceCodeInfo
	}, k));
}
function applyLetBindComplete(frame, record, k) {
	const { originalValue, env, sourceCodeInfo } = frame;
	env.addValues(record, sourceCodeInfo);
	return {
		type: "Value",
		value: originalValue,
		k
	};
}
function applyLoopBind(frame, value, k) {
	const { bindings, index, context, body, env, sourceCodeInfo } = frame;
	const [target] = bindings[index];
	const completeFrame = {
		type: "LoopBindComplete",
		bindings,
		index,
		context,
		body,
		env,
		sourceCodeInfo
	};
	return startBindingSlots(target, value, env.create(context), sourceCodeInfo, cons(completeFrame, k));
}
function applyLoopBindComplete(frame, record, k) {
	const { bindings, index, context, body, env, sourceCodeInfo } = frame;
	Object.entries(record).forEach(([name, val]) => {
		context[name] = { value: val };
	});
	const nextIndex = index + 1;
	if (nextIndex >= bindings.length) {
		const loopEnv = env.create(context);
		return {
			type: "Eval",
			node: body,
			env: loopEnv,
			k: cons({
				type: "LoopIterate",
				bindings,
				bindingContext: context,
				body,
				env: loopEnv,
				sourceCodeInfo
			}, k)
		};
	}
	const newFrame = {
		type: "LoopBind",
		phase: "value",
		bindings,
		index: nextIndex,
		context,
		body,
		env,
		sourceCodeInfo
	};
	return {
		type: "Eval",
		node: bindings[nextIndex][1],
		env: env.create(context),
		k: cons(newFrame, k)
	};
}
function applyLoopIterate(_frame, value, k) {
	return {
		type: "Value",
		value,
		k
	};
}
function applyForLoop(frame, value, k) {
	const { bindingNodes, result, env, sourceCodeInfo } = frame;
	const { asColl } = getCollectionUtils();
	switch (frame.phase) {
		case "evalCollection": {
			const coll = asColl(value, sourceCodeInfo);
			let seq;
			if (isPersistentVector(coll)) seq = coll;
			else if (Array.isArray(coll)) seq = PersistentVector.from(coll);
			else if (typeof coll === "string") seq = PersistentVector.from([...coll]);
			else {
				const pairs = [];
				for (const [key, v] of coll) pairs.push(PersistentVector.from([key, v]));
				seq = PersistentVector.from(pairs);
			}
			if (seq.size === 0) return handleForAbort(frame, k);
			const levelStates = [...frame.levelStates];
			levelStates[frame.bindingLevel] = {
				collection: seq,
				index: 0
			};
			const targetNode = bindingNodes[frame.bindingLevel][0][0];
			return startBindingSlots(targetNode, asAny(seq.get(0), sourceCodeInfo), env, sourceCodeInfo, cons({
				type: "ForElementBindComplete",
				forFrame: {
					...frame,
					levelStates
				},
				levelStates,
				env,
				sourceCodeInfo
			}, k));
		}
		case "evalWhen": {
			if (!value) return advanceForElement(frame, k);
			const whileNode = bindingNodes[frame.bindingLevel][3];
			if (whileNode) return {
				type: "Eval",
				node: whileNode,
				env,
				k: cons({
					...frame,
					phase: "evalWhile"
				}, k)
			};
			return processForNextLevel(frame, k);
		}
		case "evalWhile":
			if (!value) {
				const levelStates = [...frame.levelStates];
				levelStates[frame.bindingLevel] = {
					...levelStates[frame.bindingLevel],
					index: Number.POSITIVE_INFINITY
				};
				return advanceForElement({
					...frame,
					levelStates
				}, k);
			}
			return processForNextLevel(frame, k);
		case "evalBody": {
			const newResult = result.append(value);
			return advanceForElement({
				...frame,
				result: newResult
			}, k);
		}
	}
}
/** Handle for-loop abort: no more elements at the outermost level. */
function handleForAbort(frame, k) {
	return {
		type: "Value",
		value: frame.result,
		k
	};
}
/** Advance to the next element at the current binding level. */
function advanceForElement(frame, k) {
	const { bindingNodes, env, sourceCodeInfo } = frame;
	const levelStates = [...frame.levelStates];
	const currentLevel = frame.bindingLevel;
	const currentState = levelStates[currentLevel];
	const nextElementIndex = currentState.index + 1;
	if (nextElementIndex >= currentState.collection.size) {
		if (currentLevel === 0) return handleForAbort(frame, k);
		return advanceForElement({
			...frame,
			bindingLevel: currentLevel - 1
		}, k);
	}
	levelStates[currentLevel] = {
		...currentState,
		index: nextElementIndex
	};
	const targetNode = bindingNodes[currentLevel][0][0];
	return startBindingSlots(targetNode, asAny(currentState.collection.get(nextElementIndex), sourceCodeInfo), env, sourceCodeInfo, cons({
		type: "ForElementBindComplete",
		forFrame: {
			...frame,
			levelStates,
			bindingLevel: currentLevel
		},
		levelStates,
		env,
		sourceCodeInfo
	}, k));
}
/** Handle completion of for-loop element binding. */
function applyForElementBindComplete(frame, record, k) {
	const { forFrame, levelStates, env, sourceCodeInfo } = frame;
	Object.entries(record).forEach(([name, val]) => {
		forFrame.context[name] = { value: val };
	});
	const letBindings = forFrame.bindingNodes[forFrame.bindingLevel][1];
	if (letBindings.length > 0) return startForLetBindings(forFrame, levelStates, letBindings, 0, env, sourceCodeInfo, k);
	return processForGuards(forFrame, levelStates, k);
}
/** Start processing let-bindings at the current for-loop level. */
function startForLetBindings(forFrame, levelStates, letBindings, letIndex, env, sourceCodeInfo, k) {
	return {
		type: "Eval",
		node: letBindings[letIndex][1],
		env,
		k: cons({
			type: "ForLetBind",
			phase: "evalValue",
			forFrame,
			levelStates,
			letBindings,
			letIndex,
			env,
			sourceCodeInfo
		}, k)
	};
}
/** Handle continuation after evaluating a for-loop let-binding value or destructuring. */
function applyForLetBind(frame, value, k) {
	const { phase, forFrame, levelStates, letBindings, letIndex, env, sourceCodeInfo } = frame;
	if (phase === "evalValue") {
		const target = letBindings[letIndex][0];
		return startBindingSlots(target, value, env, sourceCodeInfo, cons({
			type: "ForLetBind",
			phase: "destructure",
			forFrame,
			levelStates,
			letBindings,
			letIndex,
			currentValue: value,
			env,
			sourceCodeInfo
		}, k));
	}
	Object.entries(value).forEach(([name, val]) => {
		forFrame.context[name] = { value: val };
	});
	const nextIndex = letIndex + 1;
	if (nextIndex >= letBindings.length) return processForGuards(forFrame, levelStates, k);
	return startForLetBindings(forFrame, levelStates, letBindings, nextIndex, env, sourceCodeInfo, k);
}
/** Process when/while guards at the current level. */
function processForGuards(frame, levelStates, k) {
	const { bindingNodes, env } = frame;
	const binding = bindingNodes[frame.bindingLevel];
	const whenNode = binding[2];
	const whileNode = binding[3];
	if (whenNode) return {
		type: "Eval",
		node: whenNode,
		env,
		k: cons({
			...frame,
			levelStates,
			phase: "evalWhen"
		}, k)
	};
	if (whileNode) return {
		type: "Eval",
		node: whileNode,
		env,
		k: cons({
			...frame,
			levelStates,
			phase: "evalWhile"
		}, k)
	};
	return processForNextLevel({
		...frame,
		levelStates
	}, k);
}
/** After guards pass, either go deeper (more binding levels) or evaluate body. */
function processForNextLevel(frame, k) {
	const { bindingNodes, body, env } = frame;
	const nextLevel = frame.bindingLevel + 1;
	if (nextLevel < bindingNodes.length) return {
		type: "Eval",
		node: bindingNodes[nextLevel][0][1],
		env,
		k: cons({
			...frame,
			phase: "evalCollection",
			bindingLevel: nextLevel
		}, k)
	};
	const newFrame = {
		...frame,
		phase: "evalBody"
	};
	return {
		type: "Eval",
		node: body,
		env: env.create(frame.context),
		k: cons(newFrame, k)
	};
}
/**
* Search the continuation stack for the nearest TryCatchFrame.
* Since TryCatchFrame has been removed, this now always re-throws the error.
* Kept as a helper for the transition period while `throw` still exists.
*/
/**
* Try to route a DvalaError through the 'dvala.error' algebraic effect.
*
* Mirrors the dispatch logic in `dispatchPerform` but returns `null` instead
* of throwing when no handler matches, so the caller can fall back to
* re-throwing the error as a JS exception.
*
* Search order:
* 1. Local `AlgebraicHandleFrame` handlers (innermost first)
* 2. Host handlers registered for `'dvala.error'`
*/
/**
* Scan the continuation stack for the nearest MacroEvalFrame and return its
* sourceCodeInfo (the macro call site). Returns undefined if no macro frame
* is found. Used to give errors from macro-expanded code a meaningful location.
*/
function findMacroCallSiteInfo(k) {
	let _node = k;
	while (_node !== null) {
		const frame = _node.head;
		_node = _node.tail;
		if (frame.type === "MacroEval") return frame.sourceCodeInfo;
	}
}
/**
* If the continuation stack contains a MacroEvalFrame, patch the error with
* the macro call site location. Errors from macro-expanded code should always
* point to the call site (e.g. `assert(1 > 5)`) rather than internal helper
* functions, since the call site is what the user wrote and can fix.
*/
function patchErrorWithMacroCallSite(error, k) {
	const callSite = findMacroCallSiteInfo(k);
	if (!callSite) return error;
	if (error instanceof UserError) return new UserError(error.userMessage, callSite);
	if (error instanceof AssertionError) return new AssertionError(error.shortMessage, callSite);
	if (error instanceof ReferenceError) return new ReferenceError(error.symbol, callSite);
	return new DvalaError(error.shortMessage, callSite);
}
/** Symbol key for error origin metadata on @dvala.error payloads. Invisible to Dvala code. */
const ERROR_ORIGIN = Symbol("dvala.error.origin");
/** Attach error origin metadata to a payload object. */
function stampErrorOrigin(payload, origin) {
	payload[ERROR_ORIGIN] = origin;
	return payload;
}
/** Read error origin metadata from a payload, if present. */
function getErrorOrigin(payload) {
	return payload[ERROR_ORIGIN];
}
/** Build the structured @dvala.error payload from a DvalaError instance. */
function buildErrorPayload(error) {
	let payload = PersistentMap.empty().assoc("type", error.errorType).assoc("message", error.shortMessage);
	if (error instanceof ReferenceError) payload = payload.assoc("data", PersistentMap.fromRecord({ symbol: error.symbol }));
	stampErrorOrigin(payload, { sourceCodeInfo: error.sourceCodeInfo });
	return payload;
}
function makeAtom(name) {
	const atom = {
		[ATOM_SYMBOL]: true,
		name
	};
	Object.defineProperty(atom, Symbol.for("nodejs.util.inspect.custom"), {
		value: () => `:${name}`,
		enumerable: false
	});
	return atom;
}
function wrapSettledOk(value) {
	return PersistentVector.from([makeAtom("ok"), value]);
}
function wrapSettledError(error) {
	return PersistentVector.from([makeAtom("error"), buildErrorPayload(error)]);
}
/**
* Validate and normalize a manual perform(@dvala.error, payload).
* Returns the normalized payload or throws TypeError if invalid.
*/
function validateErrorPayload(arg, sourceCodeInfo) {
	if (!isObj(arg)) throw new TypeError$1("@dvala.error requires an error object", sourceCodeInfo);
	const obj = arg;
	if (!obj.has("message")) throw new TypeError$1("@dvala.error requires a message field", sourceCodeInfo);
	const rawType = obj.get("type");
	const rawMessage = obj.get("message");
	const normalized = obj.assoc("type", rawType !== null && rawType !== void 0 ? String(rawType) : "UserError").assoc("message", String(rawMessage));
	const existingOrigin = getErrorOrigin(obj);
	if (existingOrigin) stampErrorOrigin(normalized, existingOrigin);
	else stampErrorOrigin(normalized, { sourceCodeInfo });
	return normalized;
}
function tryDispatchDvalaError(error, k) {
	const effect = getEffectRef("dvala.error");
	const arg = buildErrorPayload(error);
	let _node = k;
	while (_node !== null) {
		const frame = _node.head;
		if (frame.type === "ParallelBranchBarrier" || frame.type === "ReRunParallel" || frame.type === "ResumeParallel") break;
		_node = _node.tail;
		if (frame.type === "AlgebraicHandle") {
			if (frame.handler.clauseMap.has("dvala.error")) return {
				type: "Perform",
				effect,
				arg,
				k,
				sourceCodeInfo: error.sourceCodeInfo
			};
		}
	}
	return null;
}
function applyRecur(frame, value, k) {
	const { nodes, index, env } = frame;
	const newParams = frame.params.append(value);
	if (index >= nodes.length) return handleRecur(newParams, k, frame.sourceCodeInfo);
	const newFrame = {
		...frame,
		index: index + 1,
		params: newParams
	};
	return {
		type: "Eval",
		node: nodes[index],
		env,
		k: cons(newFrame, k)
	};
}
/**
* Handle recur by searching the continuation stack for the nearest
* LoopIterateFrame or FnBodyFrame, rebinding parameters, and restarting.
* Uses frame-based slot binding for proper suspension support.
*/
function handleRecur(params, k, sourceCodeInfo) {
	let _node = k;
	while (_node !== null) {
		const frame = _node.head;
		const remainingK = _node.tail;
		_node = remainingK;
		if (frame.type === "LoopIterate") {
			const { bindings, bindingContext, body, env } = frame;
			if (params.size !== bindings.length) throw new TypeError$1(`recur expected ${bindings.length} parameters, got ${params.size}`, sourceCodeInfo);
			return startRecurLoopRebind(bindings, 0, params, bindingContext, body, env, remainingK, sourceCodeInfo);
		}
		if (frame.type === "FnBody") {
			const { fn, outerEnv } = frame;
			return setupUserDefinedCall(fn, params, outerEnv, frame.sourceCodeInfo, remainingK);
		}
	}
	throw new RuntimeError("recur called outside of loop or function body", sourceCodeInfo);
}
/**
* Start rebinding loop variables during recur using slot-based binding.
*/
function startRecurLoopRebind(bindings, bindingIndex, params, bindingContext, body, env, remainingK, sourceCodeInfo) {
	if (bindingIndex >= bindings.length) {
		const innermostContext = env.getContextsRaw()[0];
		if (innermostContext !== bindingContext) for (const [name, entry] of Object.entries(bindingContext)) innermostContext[name] = entry;
		return {
			type: "Eval",
			node: body,
			env,
			k: cons({
				type: "LoopIterate",
				bindings,
				bindingContext,
				body,
				env,
				sourceCodeInfo
			}, remainingK)
		};
	}
	const [target] = bindings[bindingIndex];
	return startBindingSlots(target, toAny(params.get(bindingIndex)), env, sourceCodeInfo, cons({
		type: "RecurLoopRebind",
		bindings,
		bindingIndex,
		params,
		bindingContext,
		body,
		env,
		remainingK,
		sourceCodeInfo
	}, remainingK));
}
/**
* Handle completion of one loop binding during recur rebinding.
*/
function applyRecurLoopRebind(frame, value, _k) {
	const { bindings, bindingIndex, params, bindingContext, body, env, remainingK, sourceCodeInfo } = frame;
	Object.entries(value).forEach(([name, val]) => {
		bindingContext[name] = { value: val };
	});
	return startRecurLoopRebind(bindings, bindingIndex + 1, params, bindingContext, body, env, remainingK, sourceCodeInfo);
}
/**
* WithHandlerSetup: the handler expression has been evaluated.
* Push an AlgebraicHandleFrame and evaluate the body as a sequence.
* No function boundary — preserves `recur` behavior.
*/
function applyWithHandlerSetup(frame, value, k) {
	if (!isHandlerFunction(value)) throw new RuntimeError("`with` expects a handler value (created by handler...end)", frame.sourceCodeInfo);
	const handleFrame = {
		type: "AlgebraicHandle",
		handler: value,
		env: value.closureEnv,
		sourceCodeInfo: frame.sourceCodeInfo
	};
	const { bodyExprs, env } = frame;
	if (bodyExprs.length === 0) return {
		type: "Value",
		value: null,
		k: cons(handleFrame, k)
	};
	if (bodyExprs.length === 1) return {
		type: "Eval",
		node: bodyExprs[0],
		env,
		k: cons(handleFrame, k)
	};
	const sequenceFrame = {
		type: "Sequence",
		nodes: bodyExprs,
		index: 1,
		env,
		sourceCodeInfo: frame.sourceCodeInfo
	};
	return {
		type: "Eval",
		node: bodyExprs[0],
		env,
		k: cons(sequenceFrame, cons(handleFrame, k))
	};
}
/**
* Body completed normally (Path 1) — apply transform clause.
* If no transform, pass through (identity). Transform does NOT apply to abort values.
*/
function applyAlgebraicHandleNormalCompletion(frame, value, k) {
	const { handler, cleanups } = frame;
	const outerK = cleanups && cleanups.length > 0 ? cons({
		type: "HandlerCleanup",
		cleanups,
		handleFrame: frame,
		sourceCodeInfo: frame.sourceCodeInfo
	}, k) : k;
	return applyHandlerTransform(handler, value, frame.env, frame.sourceCodeInfo, outerK);
}
/**
* Fire host-registered cleanup callbacks attached to a handler frame
* that just exited. Callbacks run in LIFO; errors from individual
* callbacks are collected and surfaced as a single aggregate after
* all cleanups have attempted to run. The saved `value` is restored
* as the scope result unchanged.
*
* If any callback returns a Promise, the whole chain is awaited and
* the result is a Promise<Step>. Sync callbacks (the common case)
* stay on the sync path.
*/
function applyHandlerCleanup(frame, value, k) {
	const cleanups = frame.cleanups;
	const sourceCodeInfo = frame.sourceCodeInfo;
	frame.handleFrame.cleanupsFired = true;
	const errors = [];
	let i = cleanups.length - 1;
	while (i >= 0) {
		const entry = cleanups[i];
		try {
			const result = entry.callback();
			if (result && typeof result.then === "function") return result.catch((e) => {
				errors.push(e);
			}).then(() => drainCleanupsAsync(cleanups, i - 1, errors)).then(() => finalizeCleanup(value, k, errors, sourceCodeInfo));
		} catch (e) {
			errors.push(e);
		}
		i--;
	}
	return finalizeCleanup(value, k, errors, sourceCodeInfo);
}
/**
* Drain program-level cleanups (registered by `onScopeExit` when no
* Dvala handler frame was in scope). Fires in LIFO. Called from the
* runEffectLoop terminal paths (completed / halted / error).
*
* `primaryOutcomeInFlight` controls error handling: when true (the
* halted or error terminal paths), cleanup errors are logged to
* stderr and do NOT re-throw — the in-flight primary outcome must
* not be clobbered by a cleanup failure that happened during its
* unwind. When false (the completed path), cleanup errors are
* treated as the primary outcome and re-thrown so the runtime
* reports them instead of a misleading "completed."
*
* Returns a Promise because cleanups may be async; for a fully
* synchronous batch the promise resolves on the next tick.
*/
async function drainProgramCleanups(snapshotState, primaryOutcomeInFlight = false) {
	if (!snapshotState?.programCleanups || snapshotState.programCleanups.length === 0) return;
	const cleanups = snapshotState.programCleanups;
	snapshotState.programCleanups = void 0;
	const errors = [];
	for (let i = cleanups.length - 1; i >= 0; i--) {
		const entry = cleanups[i];
		try {
			await entry.callback();
		} catch (e) {
			errors.push(e);
		}
	}
	if (errors.length === 0) return;
	if (primaryOutcomeInFlight) {
		console.error(`[dvala] ${errors.length} error(s) during program-level cleanup (primary outcome preserved):`, errors);
		return;
	}
	const first = errors[0];
	if (errors.length > 1) console.error(`[dvala] ${errors.length - 1} additional error(s) during program-level cleanup:`, errors.slice(1));
	throw first instanceof Error ? first : new DvalaError(`${first}`, void 0);
}
/** Async continuation of applyHandlerCleanup from a given index. */
async function drainCleanupsAsync(cleanups, startIndex, errors) {
	for (let i = startIndex; i >= 0; i--) {
		const entry = cleanups[i];
		try {
			await entry.callback();
		} catch (e) {
			errors.push(e);
		}
	}
}
/**
* After all cleanups have run, either propagate the saved value forward
* or surface an aggregate error. Aggregation is simple today — we
* re-throw the first error; additional errors are logged to the debug
* channel so they're not silently lost. Proper AggregateError surfacing
* is a follow-up.
*/
function finalizeCleanup(value, k, errors, sourceCodeInfo) {
	if (errors.length === 0) return {
		type: "Value",
		value,
		k
	};
	const first = errors[0];
	if (errors.length > 1) console.error(`[dvala] ${errors.length - 1} additional error(s) occurred during handler cleanup:`, errors.slice(1));
	return {
		type: "Error",
		error: first instanceof DvalaError ? first : new DvalaError(first instanceof Error ? first : `${first}`, sourceCodeInfo),
		k
	};
}
/**
* Apply a handler's transform clause to a value. Used for both:
* - Normal body completion (Path 1)
* - Inside resume return (reinstalled handler's normal completion)
*
* If no transform clause, returns the value unchanged (identity).
*/
function applyHandlerTransform(handler, value, _env, sourceCodeInfo, k) {
	if (!handler.transform) return {
		type: "Value",
		value,
		k
	};
	const [paramTarget, bodyExprs] = handler.transform;
	const closureEnv = handler.closureEnv;
	return startTransformClause(paramTarget, bodyExprs, value, closureEnv, sourceCodeInfo, cons({
		type: "HandlerTransform",
		handler,
		env: closureEnv,
		sourceCodeInfo
	}, k));
}
/**
* Start evaluating a transform clause body with the param bound.
*/
function startTransformClause(paramTarget, bodyExprs, value, closureEnv, sourceCodeInfo, k) {
	const context = {};
	if (paramTarget[0] === bindingTargetTypes.symbol) {
		const symNode = paramTarget[1][0];
		context[symNode[1]] = { value };
	} else {
		const names = getAllBindingTargetNames(paramTarget);
		for (const name of Object.keys(names)) context[name] = { value };
	}
	const bodyEnv = closureEnv.create(context);
	if (bodyExprs.length === 1) return {
		type: "Eval",
		node: bodyExprs[0],
		env: bodyEnv,
		k
	};
	const seqFrame = {
		type: "Sequence",
		nodes: bodyExprs,
		index: 1,
		env: bodyEnv,
		sourceCodeInfo
	};
	return {
		type: "Eval",
		node: bodyExprs[0],
		env: bodyEnv,
		k: cons(seqFrame, k)
	};
}
/**
* Handler clause completed without calling resume — abort.
* The clause's return value becomes the handle block's result,
* bypassing the AlgebraicHandleFrame (and its transform).
*
* We need to pop the continuation up past the AlgebraicHandleFrame.
*/
function applyHandlerClauseAbort(frame, value, k) {
	let _node = k;
	while (_node !== null) {
		if (_node.head.type === "AlgebraicHandle") return {
			type: "Value",
			value,
			k: _node.tail
		};
		_node = _node.tail;
	}
	return {
		type: "Value",
		value,
		k
	};
}
/**
* Create a new continuation where each frame's env has an independent copy
* of its innermost context (_contexts[0]). Used for multi-shot: prevents
* mutations from one resume affecting subsequent resumes.
*
* addValues() only mutates _contexts[0], so copying just that context is
* sufficient to ensure each resume path is independent. Deduplicates env
* references so frames sharing the same env get the same fresh copy.
*/
function freshenContinuationEnvs(k) {
	const envMap = /* @__PURE__ */ new Map();
	return listFromArray(listToArray(k).map((frame) => {
		if (!("env" in frame)) return frame;
		const env = frame.env;
		if (!envMap.has(env)) envMap.set(env, env.withCopiedTopContext());
		return {
			...frame,
			env: envMap.get(env)
		};
	}));
}
/**
* Dispatch a perform against an AlgebraicHandleFrame.
*
* Looks up the effect name in the handler's clauseMap.
* If found: runs clause body with params bound + `resume` in scope.
* If not found: propagates past this handler (returns null to indicate no match).
*/
function dispatchAlgebraicHandler(frame, effect, arg, k, frameIndex, sourceCodeInfo) {
	const { handler } = frame;
	const clause = handler.clauseMap.get(effect.name);
	if (!clause) return null;
	const performK = listTake(k, frameIndex + 1);
	const rawOuterK = listDrop(k, frameIndex + 1);
	const outerK = frame.cleanups && frame.cleanups.length > 0 ? cons({
		type: "HandlerCleanup",
		cleanups: frame.cleanups,
		handleFrame: frame,
		sourceCodeInfo
	}, rawOuterK) : rawOuterK;
	const clauseFrame = {
		type: "HandlerClause",
		performK,
		handler,
		env: handler.closureEnv,
		sourceCodeInfo
	};
	const resumeFn = buildResumeFunction(clauseFrame, handler, performK, frame.env, sourceCodeInfo);
	const closureEnv = handler.closureEnv;
	const clauseContext = { resume: { value: resumeFn } };
	const clauseParams = clause.params;
	if (clauseParams.length === 0) {} else if (clauseParams.length === 1) {
		const target = clauseParams[0];
		if (target[0] === bindingTargetTypes.symbol) {
			const symNode = target[1][0];
			clauseContext[symNode[1]] = { value: arg };
		}
	} else if (Array.isArray(arg)) for (let i = 0; i < clauseParams.length; i++) {
		const target = clauseParams[i];
		if (target[0] === bindingTargetTypes.symbol) {
			const symNode = target[1][0];
			clauseContext[symNode[1]] = { value: arg[i] ?? null };
		}
	}
	else if (isPersistentVector(arg)) for (let i = 0; i < clauseParams.length; i++) {
		const target = clauseParams[i];
		if (target[0] === bindingTargetTypes.symbol) {
			const symNode = target[1][0];
			clauseContext[symNode[1]] = { value: arg.get(i) ?? null };
		}
	}
	else for (let i = 0; i < clauseParams.length; i++) {
		const target = clauseParams[i];
		if (target[0] === bindingTargetTypes.symbol) {
			const symNode = target[1][0];
			clauseContext[symNode[1]] = { value: i === 0 ? arg : null };
		}
	}
	const clauseEnv = closureEnv.create(clauseContext);
	const clauseBodyExprs = clause.body;
	if (clauseBodyExprs.length === 1) return {
		type: "Eval",
		node: clauseBodyExprs[0],
		env: clauseEnv,
		k: cons(clauseFrame, outerK)
	};
	const seqFrame = {
		type: "Sequence",
		nodes: clauseBodyExprs,
		index: 1,
		env: clauseEnv,
		sourceCodeInfo
	};
	return {
		type: "Eval",
		node: clauseBodyExprs[0],
		env: clauseEnv,
		k: cons(seqFrame, cons(clauseFrame, outerK))
	};
}
/**
* Build a `resume` function for a handler clause.
*
* When called with `resume(value)`:
* 1. One-shot guard: error if called twice
* 2. Reinstall the handler (deep semantics) around the continuation
* 3. Evaluate the continuation with the given value at the perform site
* 4. Apply the handler's transform clause on normal completion
* 5. Return the result to the clause body (resume returns the continuation's result)
*/
function buildResumeFunction(clauseFrame, handler, performK, handlerEnv, sourceCodeInfo) {
	return {
		[FUNCTION_SYMBOL]: true,
		functionType: "Resume",
		clauseFrame,
		handler,
		performK,
		handlerEnv,
		arity: {
			min: 0,
			max: 1
		},
		sourceCodeInfo
	};
}
function applyPerformArgs(frame, value, k) {
	const { argNodes, index, env } = frame;
	const newParams = frame.params.append(value);
	if (index >= argNodes.length) {
		const effectRef = newParams.get(0);
		assertEffect(effectRef, frame.sourceCodeInfo);
		if (env.pure) throw new RuntimeError(`Cannot perform effect '${effectRef.name}' in pure mode`, frame.sourceCodeInfo);
		return {
			type: "Perform",
			effect: effectRef,
			arg: newParams.size > 1 ? newParams.get(1) : null,
			k,
			sourceCodeInfo: frame.sourceCodeInfo
		};
	}
	const newFrame = {
		...frame,
		index: index + 1,
		params: newParams
	};
	return {
		type: "Eval",
		node: argNodes[index],
		env,
		k: cons(newFrame, k)
	};
}
/**
* Check that no AlgebraicHandleFrame in a captured continuation has
* had its cleanups discharged. Used when invoking a resume function —
* multi-shot is fine in general, but re-entering a frame whose
* cleanups have already fired is forbidden (the runtime can no
* longer guarantee resource-holding invariants).
*
* See `design/archive/2026-04-19_host-scoped-resources.md` Part 3,
* "Continuation tracking for the multi-shot restriction."
*/
function assertContinuationValid(performK, sourceCodeInfo) {
	let node = performK;
	while (node !== null) {
		if (node.head.type === "AlgebraicHandle" && node.head.cleanupsFired) throw new RuntimeError("cannot resume: continuation refers to a resource-holding handler that has already exited. This happens when a multi-shot resume would re-enter a handler whose cleanups have already fired.", sourceCodeInfo);
		node = node.tail;
	}
}
/**
* Silent predicate: true iff any AlgebraicHandleFrame in `k` has live
* host-registered cleanups. Used by auto-checkpoint to skip snapshot
* capture when resources are held (explicit user-initiated checkpoints
* still error via `assertNoLiveCleanups`).
*
* Why snapshot-discard does NOT need to fire cleanups: user-initiated
* capture is refused by `assertNoLiveCleanups` while cleanups are live,
* and auto-checkpoint skips capture via this predicate. So no snapshot
* in `snapshotState.snapshots` ever contains a resource-holding frame —
* a discard (via `resumeFrom` unwinding, `maxSnapshots` eviction, or
* host-side release) cannot leak cleanups that were never captured.
* The discard-fire hook described in the design doc's Phase 1 is
* therefore a no-op by construction; we intentionally don't implement
* it to keep the surface small.
*/
function hasLiveCleanups(k, snapshotState) {
	let node = k;
	while (node !== null) {
		if (node.head.type === "AlgebraicHandle" && node.head.cleanups && node.head.cleanups.length > 0) return true;
		node = node.tail;
	}
	if (snapshotState?.programCleanups && snapshotState.programCleanups.length > 0) return true;
	return false;
}
/**
* Walk the continuation stack and program-level cleanup list looking
* for live host-registered cleanups. If any are found, throw a
* RuntimeError describing the operation that was refused and listing
* the held resources by effect name (e.g. "2 × file.open, 1 × db.connect").
*
* Called at snapshot-capture sites and at suspend. The runtime refuses
* these operations while resources are held because the host callback
* for cleanup cannot follow a continuation that's been serialized or
* discarded. See `design/archive/2026-04-19_host-scoped-resources.md`
* Part 3.
*/
function assertNoLiveCleanups(k, operation, sourceCodeInfo, snapshotState) {
	const counts = /* @__PURE__ */ new Map();
	let total = 0;
	let node = k;
	while (node !== null) {
		if (node.head.type === "AlgebraicHandle" && node.head.cleanups && node.head.cleanups.length > 0) for (const entry of node.head.cleanups) {
			counts.set(entry.effectName, (counts.get(entry.effectName) ?? 0) + 1);
			total++;
		}
		node = node.tail;
	}
	let programLevelCount = 0;
	if (snapshotState?.programCleanups) for (const entry of snapshotState.programCleanups) {
		counts.set(entry.effectName, (counts.get(entry.effectName) ?? 0) + 1);
		total++;
		programLevelCount++;
	}
	if (total === 0) return;
	const breakdown = [...counts.entries()].map(([name, n]) => `${n} × ${name}`).join(", ");
	const scopeNote = programLevelCount > 0 ? total === programLevelCount ? " (all at program scope)" : ` (includes ${programLevelCount} at program scope)` : "";
	throw new RuntimeError(`cannot ${operation}: ${total} live cleanup(s) held by resource-holding handler(s)${scopeNote} (${breakdown}). Close resources before exiting the scope.`, sourceCodeInfo);
}
/**
* Prepare a continuation for checkpoint serialization by replacing every
* `ParallelBranchBarrierFrame` with a `ReRunParallelFrame`.
*
* During live execution, barriers are lightweight markers. But a serialized
* checkpoint must be a full-program continuation — the `ReRunParallelFrame`
* tells the resume logic to re-run sibling branches from their original AST.
*
* For nested parallel, multiple barriers may appear in the stack. Each is
* replaced independently — the walk continues through the entire stack.
*
* Returns a new continuation stack (PersistentList spine rebuilt from the
* first barrier upward; tail after the last barrier is shared).
*/
function composeCheckpointContinuation(k) {
	if (k === null) return k;
	const frames = [];
	let hasBarrier = false;
	let node = k;
	while (node !== null) {
		if (node.head.type === "ParallelBranchBarrier") hasBarrier = true;
		frames.push(node.head);
		node = node.tail;
	}
	if (!hasBarrier) return k;
	let result = null;
	for (let i = frames.length - 1; i >= 0; i--) {
		const frame = frames[i];
		if (frame.type === "ParallelBranchBarrier") {
			const { branchCtx } = frame;
			result = cons({
				type: "ReRunParallel",
				branchIndex: branchCtx.branchIndex,
				branchCount: branchCtx.branchCount,
				branches: branchCtx.branches,
				env: branchCtx.env,
				mode: branchCtx.mode
			}, result);
		} else result = cons(frame, result);
	}
	return result;
}
/**
* Replace the first `ParallelBranchBarrierFrame` in a continuation stack
* with a different frame, preserving the tail after the barrier.
*
* Used to swap a BarrierFrame for a ResumeParallelFrame during final
* suspension composition.
*/
function replaceBarrierWithFrame(k, replacement) {
	if (k === null) return null;
	const above = [];
	let node = k;
	while (node !== null) {
		if (node.head.type === "ParallelBranchBarrier") {
			let result = cons(replacement, node.tail);
			for (let i = above.length - 1; i >= 0; i--) result = cons(above[i], result);
			return result;
		}
		above.push(node.head);
		node = node.tail;
	}
	return k;
}
/**
* Truncate a continuation stack at the first `ParallelBranchBarrierFrame`,
* returning only the frames above it.
*
* Used when storing sibling continuations in `ResumeParallelFrame` — the
* barrier and outerK tail are stripped because outerK is redundant (it's the
* same as the continuation after the ResumeParallelFrame). On resume, each
* sibling gets a fresh BarrierFrame + outerK reconstructed from context.
*/
function truncateAtBarrier(k) {
	if (k === null) return null;
	const frames = [];
	let node = k;
	while (node !== null) {
		if (node.head.type === "ParallelBranchBarrier") break;
		frames.push(node.head);
		node = node.tail;
	}
	let result = null;
	for (let i = frames.length - 1; i >= 0; i--) result = cons(frames[i], result);
	return result;
}
function getParallelBoundaryPath(k) {
	const path = [];
	let node = k;
	while (node !== null) {
		const frame = node.head;
		if (frame.type === "ParallelBranchBarrier") path.push(`${frame.branchCtx.mode}:${frame.branchCtx.branchIndex}/${frame.branchCtx.branchCount}`);
		else if (frame.type === "ReRunParallel" || frame.type === "ResumeParallel") path.push(`${frame.mode}:${frame.branchIndex}/${frame.branchCount}`);
		node = node.tail;
	}
	return path;
}
function sameBoundaryPath(left, right) {
	if (left.length !== right.length) return false;
	for (let i = 0; i < left.length; i++) if (left[i] !== right[i]) return false;
	return true;
}
function dispatchPerform(effect, arg, k, sourceCodeInfo, handlers, signal, snapshotState) {
	if (effect.name === "dvala.checkpoint" && snapshotState) {
		assertNoLiveCleanups(k, "snapshot (via perform @dvala.checkpoint)", sourceCodeInfo, snapshotState);
		const message = arg;
		const snapshot = createSnapshot({
			continuation: serializeToObject(composeCheckpointContinuation(k)),
			timestamp: Date.now(),
			index: snapshotState.nextSnapshotIndex++,
			executionId: snapshotState.executionId,
			message
		});
		snapshotState.snapshots.push(snapshot);
		if (snapshotState.maxSnapshots !== void 0 && snapshotState.snapshots.length > snapshotState.maxSnapshots) snapshotState.snapshots.shift();
	}
	let searchNode = k;
	let frameIndex = 0;
	let crossedBarrier = false;
	while (searchNode !== null) {
		const frame = searchNode.head;
		if (frame.type === "ParallelBranchBarrier" || frame.type === "ReRunParallel" || frame.type === "ResumeParallel") {
			crossedBarrier = true;
			searchNode = searchNode.tail;
			frameIndex++;
			continue;
		}
		if (frame.type === "AlgebraicHandle") {
			if (crossedBarrier && !frame.handler.linear) {
				searchNode = searchNode.tail;
				frameIndex++;
				continue;
			}
			const result = dispatchAlgebraicHandler(frame, effect, arg, k, frameIndex, sourceCodeInfo);
			if (result !== null) return result;
		}
		searchNode = searchNode.tail;
		frameIndex++;
	}
	const matchingHostHandlers = findMatchingHandlers(effect.name, handlers);
	if (matchingHostHandlers.length > 0) return dispatchHostHandler(effect.name, matchingHostHandlers, arg, k, signal, sourceCodeInfo, snapshotState);
	const standardHandler = getStandardEffectHandler(effect.name);
	if (standardHandler) return standardHandler(arg, k, sourceCodeInfo);
	if (effect.name === "dvala.checkpoint") return {
		type: "Value",
		value: null,
		k
	};
	if (effect.name === "dvala.error") {
		const payload = validateErrorPayload(arg, sourceCodeInfo);
		const origin = getErrorOrigin(payload);
		throw new UserError(payload.get("message"), origin?.sourceCodeInfo ?? sourceCodeInfo);
	}
	if (effect.name === "dvala.host") {
		if (typeof arg !== "string") throw new TypeError$1(`@dvala.host requires a string argument, got ${typeof arg}`, sourceCodeInfo);
		throw new RuntimeError(`Host binding "${arg}" not provided. Install a @dvala.host effect handler.`, sourceCodeInfo);
	}
	throw new RuntimeError(`Unhandled effect: '${effect.name}'`, sourceCodeInfo);
}
/**
* Dispatch an effect to host-provided JavaScript handlers (middleware chain).
*
* Creates an `EffectContext` with `resume`, `suspend`, `fail`, and `next`
* callbacks, then calls the first matching handler. If the handler calls
* `next()`, the next handler in the chain is invoked with the same context
* shape. If no more handlers remain after `next()`, the effect is unhandled.
*
* Each handler must call exactly one of `resume`, `suspend`, `fail`, or
* `next` before its promise resolves (async) or before returning (sync).
*
* - `resume(value)` — resolves with a `ValueStep` that continues evaluation.
* - `suspend(meta?)` — throws a `SuspensionSignal`.
* - `fail(msg?)` — produces an `ErrorStep` routed through `dvala.error`.
* - `next()` — pass to the next matching handler in the chain.
*
* Handlers may return `void` (synchronous) or `Promise<void>` (async).
* When all handlers in a chain are synchronous, this function returns
* a `Step` synchronously, allowing use from the sync trampoline.
*/
function dispatchHostHandler(effectName, matchingHandlers, arg, k, signal, sourceCodeInfo, snapshotState) {
	const effectSignal = signal ?? new AbortController().signal;
	if (effectSignal.aborted) throwSuspension(k, void 0, effectName, arg);
	function resolveOutcome(o, nextIndex) {
		switch (o.kind) {
			case "step": return o.step;
			case "asyncResume": return o.promise.then((v) => ({
				type: "Value",
				value: v,
				k
			}), (e) => ({
				type: "Error",
				error: e instanceof DvalaError ? e : new DvalaError(e instanceof Error ? e : `${e}`, sourceCodeInfo),
				k
			}));
			case "throw": throw o.error;
			case "next": return tryHandler(nextIndex);
		}
	}
	function tryHandler(index) {
		if (index >= matchingHandlers.length) {
			const standardHandler = getStandardEffectHandler(effectName);
			if (standardHandler) return standardHandler(arg, k, sourceCodeInfo);
			if (effectName === "dvala.error") {
				const payload = validateErrorPayload(arg, sourceCodeInfo);
				const origin = getErrorOrigin(payload);
				throw new UserError(payload.get("message"), origin?.sourceCodeInfo ?? sourceCodeInfo);
			}
			if (effectName === "dvala.checkpoint") return {
				type: "Value",
				value: null,
				k
			};
			throw new RuntimeError(`Unhandled effect: '${effectName}'`, sourceCodeInfo);
		}
		const [_pattern, handler] = matchingHandlers[index];
		let outcome;
		let settled = false;
		function assertNotSettled(operation) {
			if (settled) throw new RuntimeError(`Effect handler called ${operation}() after already calling another operation`, sourceCodeInfo);
			settled = true;
		}
		const handlerResult = handler({
			effectName,
			arg: toJS(arg),
			signal: effectSignal,
			resume: (value) => {
				assertNotSettled("resume");
				if (snapshotState?.autoCheckpoint && effectName !== "dvala.checkpoint" && !hasLiveCleanups(k, snapshotState)) {
					const snapshot = createSnapshot({
						continuation: serializeToObject(composeCheckpointContinuation(k)),
						timestamp: Date.now(),
						index: snapshotState.nextSnapshotIndex++,
						executionId: snapshotState.executionId,
						message: `After ${effectName}`
					});
					snapshotState.snapshots.push(snapshot);
					if (snapshotState.maxSnapshots !== void 0 && snapshotState.snapshots.length > snapshotState.maxSnapshots) snapshotState.snapshots.shift();
				}
				const resumeContext = `resume() in handler for '${effectName}'`;
				if (value instanceof Promise) outcome = {
					kind: "asyncResume",
					promise: value.then((v) => validateFromJS(v, resumeContext))
				};
				else outcome = {
					kind: "step",
					step: {
						type: "Value",
						value: validateFromJS(value, resumeContext),
						k
					}
				};
			},
			fail: (msg) => {
				assertNotSettled("fail");
				outcome = {
					kind: "step",
					step: {
						type: "Error",
						error: new RuntimeError(msg ?? `Effect handler failed for '${effectName}'`, sourceCodeInfo),
						k
					}
				};
			},
			suspend: (meta) => {
				assertNotSettled("suspend");
				assertNoLiveCleanups(k, "suspend (via ctx.suspend)", sourceCodeInfo, snapshotState);
				if (meta !== void 0) assertValidHostValue(meta, `suspend() meta in handler for '${effectName}'`);
				outcome = {
					kind: "throw",
					error: new SuspensionSignal(k, snapshotState ? snapshotState.snapshots : [], snapshotState ? snapshotState.nextSnapshotIndex : 0, meta, effectName, arg)
				};
			},
			next: () => {
				assertNotSettled("next");
				outcome = { kind: "next" };
			},
			get snapshots() {
				return snapshotState ? [...snapshotState.snapshots] : [];
			},
			checkpoint: (message, meta) => {
				if (!snapshotState) throw new RuntimeError("checkpoint is not available outside effect-enabled execution", sourceCodeInfo);
				assertNoLiveCleanups(k, "checkpoint (via ctx.checkpoint)", sourceCodeInfo, snapshotState);
				if (meta !== void 0) assertValidHostValue(meta, `checkpoint() meta in handler for '${effectName}'`);
				const snapshot = createSnapshot({
					continuation: serializeToObject(composeCheckpointContinuation(k)),
					timestamp: Date.now(),
					index: snapshotState.nextSnapshotIndex++,
					executionId: snapshotState.executionId,
					message,
					...meta !== void 0 ? { meta } : {}
				});
				snapshotState.snapshots.push(snapshot);
				if (snapshotState.maxSnapshots !== void 0 && snapshotState.snapshots.length > snapshotState.maxSnapshots) snapshotState.snapshots.shift();
				return snapshot;
			},
			resumeFrom: (snapshot, value) => {
				if (settled) throw new RuntimeError("Effect handler called resumeFrom() after already calling another operation", sourceCodeInfo);
				if (!snapshotState) throw new RuntimeError("resumeFrom is not available outside effect-enabled execution", sourceCodeInfo);
				const found = snapshotState.snapshots.find((s) => s.index === snapshot.index && s.executionId === snapshot.executionId);
				if (!found) throw new RuntimeError(`Invalid snapshot: no snapshot with index ${snapshot.index} found in current run`, sourceCodeInfo);
				settled = true;
				outcome = {
					kind: "throw",
					error: new ResumeFromSignal(found.continuation, validateFromJS(value, `resumeFrom() in handler for '${effectName}'`), found.index, getParallelBoundaryPath(k))
				};
			},
			halt: (value = null) => {
				assertNotSettled("halt");
				outcome = {
					kind: "throw",
					error: new HaltSignal(value, snapshotState ? snapshotState.snapshots : [], snapshotState ? snapshotState.nextSnapshotIndex : 0)
				};
			},
			onScopeExit: (callback) => {
				let node = k;
				while (node !== null) {
					if (node.head.type === "AlgebraicHandle") {
						const frame = node.head;
						if (!frame.cleanups) frame.cleanups = [];
						frame.cleanups.push({
							callback,
							effectName
						});
						return;
					}
					node = node.tail;
				}
				if (snapshotState) {
					if (!snapshotState.programCleanups) snapshotState.programCleanups = [];
					snapshotState.programCleanups.push({
						callback,
						effectName
					});
					return;
				}
				throw new RuntimeError(`onScopeExit called from '${effectName}' handler with no enclosing Dvala handler frame and no program-level scope available (pure-mode evaluation without a snapshot state). Wrap the computation in a \`do with handler; ... end\` to scope the cleanup.`, sourceCodeInfo);
			}
		});
		if (!(handlerResult instanceof Promise)) {
			if (!outcome) throw new RuntimeError(`Effect handler for '${effectName}' did not call resume(), fail(), suspend(), halt(), or next()`, sourceCodeInfo);
			return resolveOutcome(outcome, index + 1);
		}
		if (outcome) {
			handlerResult.catch(() => {});
			return resolveOutcome(outcome, index + 1);
		}
		return handlerResult.then(() => {
			if (!outcome) throw new RuntimeError(`Effect handler for '${effectName}' did not call resume(), fail(), suspend(), halt(), or next()`, sourceCodeInfo);
			return resolveOutcome(outcome, index + 1);
		}, (e) => {
			if (outcome) return resolveOutcome(outcome, index + 1);
			if (isSuspensionSignal(e) || isResumeFromSignal(e) || isHaltSignal(e)) throw e;
			return {
				type: "Error",
				error: e instanceof DvalaError ? e : new DvalaError(e instanceof Error ? e : `${e}`, sourceCodeInfo),
				k
			};
		});
	}
	return tryHandler(0);
}
/**
* Throw a SuspensionSignal. Factored out to a helper so ESLint's
* `only-throw-literal` rule can be suppressed in one place.
*/
/** Combine two AbortSignals: aborts when either fires (or already aborted). */
function combineSignals(a, b) {
	const controller = new AbortController();
	if (a.aborted || b.aborted) {
		controller.abort();
		return controller.signal;
	}
	a.addEventListener("abort", () => controller.abort(), { once: true });
	b.addEventListener("abort", () => controller.abort(), { once: true });
	return controller.signal;
}
function throwSuspension(k, meta, effectName, effectArg) {
	throw new SuspensionSignal(k, [], 0, meta, effectName, effectArg);
}
/**
* Run a single trampoline branch to completion with effect handler support.
*
* This is the core building block for `parallel`, `race`, and `settled`.
* Each branch is a function value called with no arguments. The branch runs
* as an independent trampoline invocation through `runEffectLoop`,
* producing a `RunResult` that is either `completed`, `suspended`, or `error`.
*
* The branch receives the same `handlers` and the given `signal`, allowing
* the caller to cancel branches via AbortController.
*/
async function runBranch(branchFn, env, handlers, signal, outerK, branchCtx, outerSnapshotState) {
	const barrierK = cons({
		type: "ParallelBranchBarrier",
		branchCtx
	}, outerK);
	return runEffectLoop(dispatchFunction(branchFn, PersistentVector.empty(), [], env, void 0, barrierK), handlers, signal, outerSnapshotState, outerSnapshotState?.maxSnapshots, void 0, outerSnapshotState?.autoCheckpoint, void 0, void 0, outerSnapshotState?.executionId);
}
/**
* Execute a `parallel(...)` expression.
*
* Runs all branch expressions concurrently as independent trampoline
* invocations using `Promise.allSettled`. Results are collected in order.
*
* Outcome:
* - All branches complete → return `ValueStep` with array of results
* - Any branch suspends → throw `SuspensionSignal` with a `ParallelResumeFrame`
*   on the outer continuation. The host can resume branches one at a time.
* - Any branch errors → throw the first error (other branches still complete
*   but errors take priority)
*/
async function executeParallelBranches(branches, env, k, handlers, signal, snapshotState, mode = "parallel") {
	const parallelAbort = new AbortController();
	const effectSignal = signal ? combineSignals(signal, parallelAbort.signal) : parallelAbort.signal;
	const branchPromises = branches.map(async (branch, i) => {
		const result = await runBranch(branch, env, handlers, effectSignal, k, {
			branchIndex: i,
			branchCount: branches.length,
			branches,
			env,
			mode
		}, snapshotState);
		if (result.type === "suspended") parallelAbort.abort();
		return {
			index: i,
			result
		};
	});
	const results = await Promise.allSettled(branchPromises);
	const completedBranches = [];
	const suspendedBranches = [];
	const errors = [];
	for (let ri = 0; ri < results.length; ri++) {
		const settledResult = results[ri];
		if (settledResult.status === "rejected") if (mode === "settled") {
			const err = new DvalaError(`${settledResult.reason}`, void 0);
			completedBranches.push({
				index: ri,
				value: wrapSettledError(err)
			});
		} else errors.push(new DvalaError(`${settledResult.reason}`, void 0));
		else {
			const { index, result } = settledResult.value;
			switch (result.type) {
				case "completed":
					completedBranches.push({
						index,
						value: mode === "settled" ? wrapSettledOk(result.value) : result.value
					});
					break;
				case "suspended":
					suspendedBranches.push({
						index,
						result
					});
					break;
				case "error":
					if (mode === "settled") completedBranches.push({
						index,
						value: wrapSettledError(result.error)
					});
					else errors.push(result.error);
					break;
			}
		}
	}
	if (mode !== "settled" && errors.length > 0) throw errors[0];
	if (suspendedBranches.length > 0) {
		const primarySuspended = suspendedBranches[0];
		const primaryRaw = primarySuspended.result._rawSuspension;
		const siblingsSuspended = [];
		for (let i = 1; i < suspendedBranches.length; i++) {
			const sb = suspendedBranches[i];
			const raw = sb.result._rawSuspension;
			siblingsSuspended.push({
				index: sb.index,
				k: truncateAtBarrier(raw.k),
				effectName: raw.effectName,
				effectArg: raw.effectArg
			});
		}
		const resumeFrame = {
			type: "ResumeParallel",
			branchIndex: primarySuspended.index,
			branchCount: branches.length,
			branches,
			env,
			completedBranches,
			suspendedBranches: siblingsSuspended,
			mode
		};
		throw new SuspensionSignal(replaceBarrierWithFrame(primaryRaw.k, resumeFrame), primaryRaw.snapshots, primaryRaw.nextSnapshotIndex, primaryRaw.meta, primaryRaw.effectName, primaryRaw.effectArg);
	}
	const resultMutable = Array.from({ length: branches.length });
	for (const { index, value } of completedBranches) resultMutable[index] = value;
	return {
		type: "Value",
		value: PersistentVector.from(resultMutable),
		k
	};
}
/**
* Execute a `race(...)` expression.
*
* Runs all branch expressions concurrently using the same infrastructure
* as parallel (BarrierFrame + outerK). The first branch to complete
* wins — its value becomes the result. Losers are cancelled via
* per-branch AbortControllers.
*
* Branch outcome priority: completed > suspended > errored.
* - First completed branch wins immediately.
* - Errored branches are silently dropped.
* - If no branch completes but some suspend, the race suspends with a
*   ResumeParallelFrame (mode: 'race'). On resume, the frame re-runs/resumes
*   branches and the first to complete wins automatically.
* - If all branches error, throw an aggregate error.
*/
async function executeRaceBranches(branches, env, k, handlers, signal, snapshotState) {
	const parentSignal = signal ?? new AbortController().signal;
	const branchControllers = branches.map(() => new AbortController());
	const onParentAbort = () => {
		for (const ctrl of branchControllers) ctrl.abort(parentSignal.reason);
	};
	parentSignal.addEventListener("abort", onParentAbort, { once: true });
	try {
		let winnerIndex = -1;
		let winnerValue = null;
		const branchPromises = branches.map(async (branch, i) => {
			const branchCtx = {
				branchIndex: i,
				branchCount: branches.length,
				branches,
				env,
				mode: "race"
			};
			const branchSignal = branchControllers[i].signal;
			const result = await runBranch(branch, env, handlers, branchSignal, k, branchCtx, snapshotState);
			if (result.type === "completed" && winnerIndex < 0) {
				winnerIndex = i;
				winnerValue = result.value;
				for (let j = 0; j < branchControllers.length; j++) if (j !== i) branchControllers[j].abort("race: branch lost");
			}
			return {
				index: i,
				result
			};
		});
		const results = await Promise.allSettled(branchPromises);
		if (winnerIndex >= 0) return {
			type: "Value",
			value: winnerValue,
			k
		};
		const suspendedBranches = [];
		const errors = [];
		for (const settled of results) if (settled.status === "rejected") errors.push(new DvalaError(`${settled.reason}`, void 0));
		else {
			const { index, result } = settled.value;
			if (result.type === "suspended") suspendedBranches.push({
				index,
				result
			});
			else if (result.type === "error") errors.push(result.error);
		}
		if (suspendedBranches.length > 0) {
			const primarySuspended = suspendedBranches[0];
			const primaryRaw = primarySuspended.result._rawSuspension;
			const siblingsSuspended = [];
			for (let i = 1; i < suspendedBranches.length; i++) {
				const sb = suspendedBranches[i];
				const raw = sb.result._rawSuspension;
				siblingsSuspended.push({
					index: sb.index,
					k: truncateAtBarrier(raw.k),
					effectName: raw.effectName,
					effectArg: raw.effectArg
				});
			}
			const resumeFrame = {
				type: "ResumeParallel",
				branchIndex: primarySuspended.index,
				branchCount: branches.length,
				branches,
				env,
				completedBranches: [],
				suspendedBranches: siblingsSuspended,
				mode: "race"
			};
			throw new SuspensionSignal(replaceBarrierWithFrame(primaryRaw.k, resumeFrame), primaryRaw.snapshots, primaryRaw.nextSnapshotIndex, primaryRaw.meta, primaryRaw.effectName, primaryRaw.effectArg);
		}
		throw new RuntimeError(`race: all branches failed: ${errors.map((e) => e.message).join("; ")}`, void 0);
	} finally {
		parentSignal.removeEventListener("abort", onParentAbort);
	}
}
/**
* Execute the sibling re-run logic for a `ReRunParallelFrame`.
*
* Called from `tick()` when a `ReRunParallelExecStep` is processed.
* The resumed branch already completed with `step.resumedBranchValue`.
* All sibling branches are re-run from their original AST concurrently.
*/
async function executeReRunParallel(step, handlers, signal, snapshotState) {
	const { resumedBranchValue, frame, k: outerK } = step;
	const { branchIndex, branchCount, branches, env, mode } = frame;
	const allCompleted = [{
		index: branchIndex,
		value: mode === "settled" ? wrapSettledOk(resumedBranchValue) : resumedBranchValue
	}];
	const parallelAbort = new AbortController();
	const effectSignal = signal ? combineSignals(signal, parallelAbort.signal) : parallelAbort.signal;
	const siblingPromises = [];
	const siblingIndices = [];
	for (let i = 0; i < branchCount; i++) {
		if (i === branchIndex) continue;
		const branchCtx = {
			branchIndex: i,
			branchCount,
			branches,
			env,
			mode
		};
		siblingIndices.push(i);
		siblingPromises.push((async () => {
			const result = await runBranch(branches[i], env, handlers, effectSignal, outerK, branchCtx, snapshotState);
			if (result.type === "suspended") parallelAbort.abort();
			return {
				index: i,
				result
			};
		})());
	}
	const reRunSettled = await Promise.allSettled(siblingPromises);
	const newSuspended = [];
	const errors = [];
	for (let ri = 0; ri < reRunSettled.length; ri++) {
		const s = reRunSettled[ri];
		if (s.status === "rejected") {
			const err = new DvalaError(`${s.reason}`, void 0);
			if (mode === "settled") allCompleted.push({
				index: siblingIndices[ri],
				value: wrapSettledError(err)
			});
			else errors.push(err);
		} else {
			const { index, result } = s.value;
			if (result.type === "completed") allCompleted.push({
				index,
				value: mode === "settled" ? wrapSettledOk(result.value) : result.value
			});
			else if (result.type === "suspended" && result._rawSuspension) {
				const raw = result._rawSuspension;
				newSuspended.push({
					index,
					k: truncateAtBarrier(raw.k),
					effectName: raw.effectName,
					effectArg: raw.effectArg,
					snapshots: raw.snapshots,
					nextSnapshotIndex: raw.nextSnapshotIndex
				});
			} else if (result.type === "error") if (mode === "settled") allCompleted.push({
				index,
				value: wrapSettledError(result.error)
			});
			else errors.push(result.error);
		}
	}
	if (mode === "parallel" && errors.length > 0) throw errors[0];
	if (mode === "race" && errors.length > 0 && allCompleted.length === 0 && newSuspended.length === 0) throw new RuntimeError(`race: all branches failed: ${errors.map((e) => e.message).join("; ")}`, void 0);
	if (newSuspended.length > 0 && (mode !== "race" || allCompleted.length === 0)) {
		const primary = newSuspended[0];
		throw new SuspensionSignal(cons({
			type: "ResumeParallel",
			branchIndex: primary.index,
			branchCount,
			branches,
			env,
			completedBranches: allCompleted,
			suspendedBranches: newSuspended.slice(1).map((s) => ({
				index: s.index,
				k: s.k,
				effectName: s.effectName,
				effectArg: s.effectArg
			})),
			mode
		}, outerK), primary.snapshots, primary.nextSnapshotIndex, void 0, primary.effectName, primary.effectArg);
	}
	return buildParallelResult(allCompleted, branchCount, mode, outerK);
}
/**
* Build the final result for a parallel/race expression.
* - parallel: array of all results in order
* - race: first completed value (scalar)
*/
function buildParallelResult(completed, branchCount, mode, outerK) {
	if (mode === "race") return {
		type: "Value",
		value: completed[0]?.value ?? null,
		k: outerK
	};
	const resultMutable = Array.from({ length: branchCount });
	for (const { index, value } of completed) resultMutable[index] = value;
	return {
		type: "Value",
		value: PersistentVector.from(resultMutable),
		k: outerK
	};
}
/**
* Execute the sibling resumption logic for a `ResumeParallelFrame`.
*
* Called from `tick()` when a `ResumeParallelExecStep` is processed, so
* we have access to `handlers` and `signal`.
*
* The resumed branch already completed with `step.resumedBranchValue`.
* Suspended siblings are resumed from their stored continuations concurrently.
* Completed siblings use cached values.
*/
async function executeResumeParallel(step, handlers, signal, snapshotState) {
	const { resumedBranchValue, frame, k: outerK } = step;
	const { branchIndex, branchCount, branches, env, completedBranches, suspendedBranches, mode } = frame;
	const allCompleted = [...completedBranches, {
		index: branchIndex,
		value: mode === "settled" ? wrapSettledOk(resumedBranchValue) : resumedBranchValue
	}];
	if (suspendedBranches.length === 0) return buildParallelResult(allCompleted, branchCount, mode, outerK);
	const parallelAbort = new AbortController();
	const effectSignal = signal ? combineSignals(signal, parallelAbort.signal) : parallelAbort.signal;
	const siblingPromises = suspendedBranches.map(async (sibling) => {
		let fullK = cons({
			type: "ParallelBranchBarrier",
			branchCtx: {
				branchIndex: sibling.index,
				branchCount,
				branches,
				env,
				mode
			}
		}, outerK);
		const truncatedFrames = [];
		let node = sibling.k;
		while (node !== null) {
			truncatedFrames.push(node.head);
			node = node.tail;
		}
		for (let i = truncatedFrames.length - 1; i >= 0; i--) fullK = cons(truncatedFrames[i], fullK);
		if (sibling.effectName && sibling.effectArg !== void 0) {
			const result = await retriggerWithEffects(fullK, sibling.effectName, sibling.effectArg, handlers, snapshotState, void 0, effectSignal, snapshotState?.executionId);
			if (result.type === "suspended") parallelAbort.abort();
			return {
				index: sibling.index,
				result
			};
		}
		const result = await runEffectLoop({
			type: "Value",
			value: null,
			k: fullK
		}, handlers, effectSignal, snapshotState, void 0, void 0, void 0, void 0, void 0, snapshotState?.executionId);
		if (result.type === "suspended") parallelAbort.abort();
		return {
			index: sibling.index,
			result
		};
	});
	const resumeSettled = await Promise.allSettled(siblingPromises);
	const newSuspended = [];
	const errors = [];
	for (let ri = 0; ri < resumeSettled.length; ri++) {
		const s = resumeSettled[ri];
		if (s.status === "rejected") {
			const err = new DvalaError(`${s.reason}`, void 0);
			if (mode === "settled") allCompleted.push({
				index: suspendedBranches[ri].index,
				value: wrapSettledError(err)
			});
			else errors.push(err);
		} else {
			const { index, result } = s.value;
			if (result.type === "completed") allCompleted.push({
				index,
				value: mode === "settled" ? wrapSettledOk(result.value) : result.value
			});
			else if (result.type === "suspended" && result._rawSuspension) {
				const raw = result._rawSuspension;
				newSuspended.push({
					index,
					k: truncateAtBarrier(raw.k),
					effectName: raw.effectName,
					effectArg: raw.effectArg,
					snapshots: raw.snapshots,
					nextSnapshotIndex: raw.nextSnapshotIndex
				});
			} else if (result.type === "error") if (mode === "settled") allCompleted.push({
				index,
				value: wrapSettledError(result.error)
			});
			else errors.push(result.error);
		}
	}
	if (mode === "parallel" && errors.length > 0) throw errors[0];
	if (mode === "race" && errors.length > 0 && allCompleted.length === 0 && newSuspended.length === 0) throw new RuntimeError(`race: all branches failed: ${errors.map((e) => e.message).join("; ")}`, void 0);
	if (newSuspended.length > 0 && (mode !== "race" || allCompleted.length === 0)) {
		const primary = newSuspended[0];
		throw new SuspensionSignal(cons({
			type: "ResumeParallel",
			branchIndex: primary.index,
			branchCount,
			branches,
			env,
			completedBranches: allCompleted,
			suspendedBranches: newSuspended.slice(1).map((s) => ({
				index: s.index,
				k: s.k,
				effectName: s.effectName,
				effectArg: s.effectArg
			})),
			mode
		}, outerK), primary.snapshots, primary.nextSnapshotIndex, void 0, primary.effectName, primary.effectArg);
	}
	return buildParallelResult(allCompleted, branchCount, mode, outerK);
}
function applyEvalArgs(frame, value, k) {
	const { node, env } = frame;
	const argNodes = node[1][1];
	const currentArgNode = argNodes[frame.index];
	let newParams;
	if (isSpreadNode(currentArgNode)) {
		if (!isPersistentVector(value) && !Array.isArray(value)) throw new TypeError$1(`Spread operator requires an array, got ${valueToString(value)}`, env.resolve(currentArgNode[2]));
		let acc = frame.params;
		for (const item of value) acc = acc.append(item);
		newParams = acc;
	} else newParams = frame.params.append(value);
	const placeholders = [...frame.placeholders];
	let nextIndex = frame.index + 1;
	while (nextIndex < argNodes.length) {
		const nextArg = argNodes[nextIndex];
		if (nextArg[0] === NodeTypes.Reserved && nextArg[1] === "_") {
			placeholders.push(newParams.size);
			nextIndex++;
		} else break;
	}
	if (nextIndex >= argNodes.length) return dispatchCall({
		...frame,
		params: newParams,
		placeholders,
		index: nextIndex
	}, k);
	const newFrame = {
		...frame,
		params: newParams,
		placeholders,
		index: nextIndex
	};
	const nextArg = argNodes[nextIndex];
	if (isSpreadNode(nextArg)) return {
		type: "Eval",
		node: nextArg[1],
		env,
		k: cons(newFrame, k)
	};
	return {
		type: "Eval",
		node: nextArg,
		env,
		k: cons(newFrame, k)
	};
}
function applyCallFn(frame, value, k) {
	return dispatchFunction(asFunctionLike(value, frame.sourceCodeInfo), frame.params, frame.placeholders, frame.env, frame.sourceCodeInfo, k);
}
function applyFnBody(frame, value, k) {
	const { fn, bodyIndex, env } = frame;
	const bodyNodes = fn.evaluatedfunction[1];
	if (bodyIndex >= bodyNodes.length) return {
		type: "Value",
		value,
		k
	};
	const newFrame = {
		...frame,
		bodyIndex: bodyIndex + 1
	};
	return {
		type: "Eval",
		node: bodyNodes[bodyIndex],
		env,
		k: cons(newFrame, k)
	};
}
/**
* Handle function argument binding after a default value is evaluated.
* Binds the value to the current argument using slots, then continues with remaining args.
*/
function applyFnArgBind(frame, value, k) {
	const { fn, params, argIndex, context, outerEnv, sourceCodeInfo } = frame;
	const evaluatedFunc = fn.evaluatedfunction;
	const args = evaluatedFunc[0];
	const nbrOfNonRestArgs = args.filter((arg) => arg[0] !== bindingTargetTypes.rest).length;
	const arg = args[argIndex];
	const closureContext = evaluatedFunc[2];
	return startBindingSlots(arg, value, outerEnv.create(closureContext).create(context), sourceCodeInfo, cons({
		type: "FnArgSlotComplete",
		fn,
		params,
		argIndex,
		nbrOfNonRestArgs,
		context,
		outerEnv,
		sourceCodeInfo
	}, k));
}
/**
* Handle completion of slot-based binding for a function argument.
* Merges the binding record into context and continues with next arg.
*/
function applyFnArgSlotComplete(frame, value, k) {
	const { fn, params, argIndex, nbrOfNonRestArgs, context, outerEnv, sourceCodeInfo } = frame;
	Object.entries(value).forEach(([key, val]) => {
		context[key] = { value: val };
	});
	return continueArgSlotBinding(fn, params, argIndex + 1, nbrOfNonRestArgs, context, outerEnv, sourceCodeInfo, k);
}
/**
* Handle completion of rest argument slot-based binding.
* Merges bindings into context and proceeds to body evaluation.
*/
function applyFnRestArgComplete(frame, value, k) {
	const { fn, context, outerEnv, sourceCodeInfo } = frame;
	Object.entries(value).forEach(([key, val]) => {
		context[key] = { value: val };
	});
	return proceedToFnBody(fn, context, outerEnv, sourceCodeInfo, k);
}
/**
* Continue binding function arguments starting from argIndex.
* Handles only the default evaluation phase - rest handling moved to handleRestArgAndBody.
*/
function continueBindingArgs(fn, params, argIndex, nbrOfNonRestArgs, context, outerEnv, sourceCodeInfo, k) {
	const evaluatedFunc = fn.evaluatedfunction;
	const args = evaluatedFunc[0];
	const closureContext = evaluatedFunc[2];
	const bindingEnv = outerEnv.create(closureContext).create(context);
	for (let i = argIndex; i < nbrOfNonRestArgs; i++) {
		const defaultNode = args[i][1][1];
		if (!defaultNode) throw new TypeError$1(`Missing required argument ${i}`, sourceCodeInfo);
		return {
			type: "Eval",
			node: defaultNode,
			env: bindingEnv,
			k: cons({
				type: "FnArgBind",
				phase: "default",
				fn,
				params,
				argIndex: i,
				context,
				outerEnv,
				sourceCodeInfo
			}, k)
		};
	}
	return handleRestArgAndBody(fn, params, nbrOfNonRestArgs, context, outerEnv, sourceCodeInfo, k);
}
/**
* Start processing a binding pattern using linearized slots.
* This is the entry point for frame-based destructuring.
* @internal Exported for testing/incremental migration
*/
function startBindingSlots(target, rootValue, env, sourceCodeInfo, k) {
	validateBindingRootType(target, rootValue, sourceCodeInfo);
	return continueBindingSlots([{
		slots: flattenBindingPattern(target),
		index: 0,
		rootValue
	}], {}, env, sourceCodeInfo, k);
}
/**
* Continue processing binding slots using the context stack.
* Handles extracting values, evaluating defaults, and nested binding targets.
*/
function continueBindingSlots(contexts, record, env, sourceCodeInfo, k) {
	while (contexts.length > 0) {
		const ctx = contexts[contexts.length - 1];
		if (ctx.index >= ctx.slots.length) {
			contexts.pop();
			continue;
		}
		const slot = ctx.slots[ctx.index];
		if (slot.isRest) {
			if (slot.restKeys !== void 0) {
				const parentValue = slot.path.length > 0 ? extractValueByPath(ctx.rootValue, slot.path, sourceCodeInfo) ?? null : ctx.rootValue;
				record[slot.name] = extractObjectRest(parentValue, slot.restKeys, sourceCodeInfo);
			} else if (slot.restIndex !== void 0) {
				const parentValue = slot.path.length > 0 ? extractValueByPath(ctx.rootValue, slot.path, sourceCodeInfo) ?? null : ctx.rootValue;
				record[slot.name] = extractArrayRest(parentValue, slot.restIndex, sourceCodeInfo);
			} else {
				const value = slot.path.length > 0 ? extractValueByPath(ctx.rootValue, slot.path, sourceCodeInfo) ?? null : ctx.rootValue;
				record[slot.name] = value;
			}
			ctx.index++;
			continue;
		}
		const value = extractValueByPath(ctx.rootValue, slot.path, sourceCodeInfo);
		if (value === void 0 && slot.defaultNode) {
			const frame = {
				type: "BindingSlot",
				contexts: contexts.map((c) => ({ ...c })),
				record: { ...record },
				env,
				sourceCodeInfo
			};
			return {
				type: "Eval",
				node: slot.defaultNode,
				env,
				k: cons(frame, k)
			};
		}
		const resolvedValue = value ?? null;
		if (slot.nestedTarget) {
			const nestedSlots = flattenBindingPatternWithoutDefault(slot.nestedTarget);
			validateBindingRootType(slot.nestedTarget, resolvedValue, sourceCodeInfo);
			contexts.push({
				slots: nestedSlots,
				index: 0,
				rootValue: resolvedValue
			});
			ctx.index++;
			continue;
		}
		record[slot.name] = resolvedValue;
		ctx.index++;
	}
	return {
		type: "Value",
		value: record,
		k
	};
}
/**
* Flatten a binding pattern without using its top-level default.
* Used when we've already resolved the default and need to process the nested structure.
*/
function flattenBindingPatternWithoutDefault(target) {
	return flattenBindingPattern([
		target[0],
		[target[1][0], void 0],
		target[2]
	]);
}
/**
* Handle continuation after evaluating a binding slot's default value.
*/
function applyBindingSlot(frame, value, k) {
	const { contexts, record, env, sourceCodeInfo } = frame;
	const ctx = contexts[contexts.length - 1];
	const slot = ctx.slots[ctx.index];
	const resolvedValue = value ?? null;
	if (slot.nestedTarget) {
		const nestedSlots = flattenBindingPatternWithoutDefault(slot.nestedTarget);
		validateBindingRootType(slot.nestedTarget, resolvedValue, sourceCodeInfo);
		contexts.push({
			slots: nestedSlots,
			index: 0,
			rootValue: resolvedValue
		});
		ctx.index++;
	} else {
		record[slot.name] = resolvedValue;
		ctx.index++;
	}
	return continueBindingSlots(contexts, record, env, sourceCodeInfo, k);
}
/**
* Start pattern matching with slot-based processing.
* Returns Step to continue matching, or moves to next case on failure.
*/
function startMatchSlots(pattern, matchValue, matchFrame, env, sourceCodeInfo, k) {
	if (!checkObjectTypeConstraint(pattern, matchValue)) return tryNextMatchCase(matchFrame, k);
	if (!checkArrayLengthConstraint(pattern, matchValue)) return tryNextMatchCase(matchFrame, k);
	const slots = flattenMatchPattern(pattern);
	if (slots.length === 0) return matchSucceeded({}, matchFrame, k);
	return continueMatchSlots([{
		slots,
		index: 0,
		rootValue: matchValue
	}], {}, matchFrame, env, sourceCodeInfo, k);
}
/**
* Continue processing match slots.
* Returns Step for next action (eval, success, or try next case).
*/
function continueMatchSlots(contexts, record, matchFrame, env, sourceCodeInfo, k) {
	while (contexts.length > 0) {
		const ctx = contexts[contexts.length - 1];
		if (ctx.index >= ctx.slots.length) {
			contexts.pop();
			continue;
		}
		const slot = ctx.slots[ctx.index];
		switch (slot.kind) {
			case "wildcard":
				ctx.index++;
				continue;
			case "typeCheck":
				if (!slot.requiredType || !checkTypeAtPath(ctx.rootValue, slot.path, slot.requiredType)) return tryNextMatchCase(matchFrame, k);
				ctx.index++;
				continue;
			case "literal": {
				const frame = {
					type: "MatchSlot",
					contexts: contexts.map((c) => ({ ...c })),
					record: { ...record },
					matchFrame,
					phase: "literal",
					currentSlot: slot,
					env,
					sourceCodeInfo
				};
				return {
					type: "Eval",
					node: slot.literalNode,
					env,
					k: cons(frame, k)
				};
			}
			case "rest":
				if (slot.restKeys !== void 0) record[slot.name] = extractMatchObjectRest(ctx.rootValue, slot.path, slot.restKeys);
				else if (slot.restIndex !== void 0) record[slot.name] = extractMatchArrayRest(ctx.rootValue, slot.path, slot.restIndex);
				else {
					const value = slot.path.length > 0 ? extractMatchValueByPath(ctx.rootValue, slot.path) ?? null : ctx.rootValue;
					record[slot.name] = value;
				}
				ctx.index++;
				continue;
			case "bind": {
				const value = extractMatchValueByPath(ctx.rootValue, slot.path);
				if (value === void 0 || value === null) {
					if (slot.defaultNode) {
						const frame = {
							type: "MatchSlot",
							contexts: contexts.map((c) => ({ ...c })),
							record: { ...record },
							matchFrame,
							phase: "default",
							currentSlot: slot,
							env,
							sourceCodeInfo
						};
						return {
							type: "Eval",
							node: slot.defaultNode,
							env,
							k: cons(frame, k)
						};
					}
					record[slot.name] = value ?? null;
				} else record[slot.name] = value;
				ctx.index++;
				continue;
			}
		}
	}
	return matchSucceeded(record, matchFrame, k);
}
/**
* Handle continuation after evaluating a match slot value.
*/
function applyMatchSlot(frame, value, k) {
	const { contexts, record, matchFrame, phase, currentSlot, env, sourceCodeInfo } = frame;
	const ctx = contexts[contexts.length - 1];
	if (phase === "literal") {
		if (!deepEqual(extractMatchValueByPath(ctx.rootValue, currentSlot.path), value)) return tryNextMatchCase(matchFrame, k);
		ctx.index++;
	} else {
		record[currentSlot.name] = value ?? null;
		ctx.index++;
	}
	return continueMatchSlots(contexts, record, matchFrame, env, sourceCodeInfo, k);
}
/**
* Pattern matching succeeded — proceed to guard or body.
*/
function matchSucceeded(bindings, matchFrame, k) {
	const { cases, index, env } = matchFrame;
	const [, body, guard] = cases[index];
	const context = {};
	for (const [name, val] of Object.entries(bindings)) context[name] = { value: val };
	if (guard) return {
		type: "Eval",
		node: guard,
		env: env.create(context),
		k: cons({
			...matchFrame,
			phase: "guard",
			bindings
		}, k)
	};
	return {
		type: "Eval",
		node: body,
		env: env.create(context),
		k
	};
}
/**
* Current pattern didn't match — try next case.
*/
function tryNextMatchCase(matchFrame, k) {
	return processMatchCase({
		...matchFrame,
		index: matchFrame.index + 1
	}, k);
}
/**
* Handles continuation after evaluating an effect reference expression.
* Stores the evaluated ref, then either evaluates the next ref or starts the body.
*/
/**
* Comp iteration — chain to the next function in the composition.
* Result from previous function is wrapped in array and passed to next.
*/
function applyComp(frame, value, k) {
	const { fns, index, env, sourceCodeInfo } = frame;
	const nextParams = PersistentVector.from([value]);
	if (index < 0) return {
		type: "Value",
		value: asAny(value, sourceCodeInfo),
		k
	};
	const nextFrame = {
		type: "Comp",
		fns,
		index: index - 1,
		env,
		sourceCodeInfo
	};
	return dispatchFunction(asFunctionLike(fns.get(index), sourceCodeInfo), nextParams, [], env, sourceCodeInfo, cons(nextFrame, k));
}
/**
* Juxt iteration — collect result and call next function.
*/
function applyJuxt(frame, value, k) {
	const { fns, params, index, results, env, sourceCodeInfo } = frame;
	const newResults = results.append(value);
	if (index >= fns.size) return {
		type: "Value",
		value: newResults,
		k
	};
	const nextFrame = {
		type: "Juxt",
		fns,
		params,
		index: index + 1,
		results: newResults,
		env,
		sourceCodeInfo
	};
	return dispatchFunction(asFunctionLike(fns.get(index), sourceCodeInfo), params, [], env, sourceCodeInfo, cons(nextFrame, k));
}
/**
* EveryPred iteration — short-circuit on falsy, continue on truthy.
*/
function applyEveryPred(frame, value, k) {
	const { checks, index, env, sourceCodeInfo } = frame;
	if (!value) return {
		type: "Value",
		value: false,
		k
	};
	if (index >= checks.length) return {
		type: "Value",
		value: true,
		k
	};
	const nextFrame = {
		type: "EveryPred",
		checks,
		index: index + 1,
		env,
		sourceCodeInfo
	};
	const check = checks[index];
	return dispatchFunction(check.fn, PersistentVector.from([check.param]), [], env, sourceCodeInfo, cons(nextFrame, k));
}
/**
* SomePred iteration — short-circuit on truthy, continue on falsy.
*/
function applySomePred(frame, value, k) {
	const { checks, index, env, sourceCodeInfo } = frame;
	if (value) return {
		type: "Value",
		value: true,
		k
	};
	if (index >= checks.length) return {
		type: "Value",
		value: false,
		k
	};
	const nextFrame = {
		type: "SomePred",
		checks,
		index: index + 1,
		env,
		sourceCodeInfo
	};
	const check = checks[index];
	return dispatchFunction(check.fn, PersistentVector.from([check.param]), [], env, sourceCodeInfo, cons(nextFrame, k));
}
function applyFiniteCheck(frame, value, k) {
	if (typeof value === "number" && !Number.isFinite(value)) throw new ArithmeticError("Number is not finite", frame.sourceCodeInfo);
	return {
		type: "Value",
		value,
		k
	};
}
/**
* Call a macro. Macros are always pure — they expand at compile time,
* called directly with no effect overhead.
*/
function callMacro(macroFn, argNodes, env, sourceCodeInfo, k) {
	let depth = 0;
	let macroCheckNode = k;
	while (macroCheckNode !== null) {
		const frame = macroCheckNode.head;
		macroCheckNode = macroCheckNode.tail;
		if (frame.type === "MacroEval") {
			depth += 1;
			if (depth >= MAX_MACRO_EXPANSION_DEPTH) throw new MacroError(`Maximum macro expansion depth (${MAX_MACRO_EXPANSION_DEPTH}) exceeded. Possible infinite macro expansion.`, sourceCodeInfo);
		}
	}
	const macroEvalFrame = {
		type: "MacroEval",
		env,
		sourceCodeInfo
	};
	return setupUserDefinedCall(macroFn, PersistentVector.from(argNodes.map((arg) => fromJS(arg))), env, sourceCodeInfo, cons(macroEvalFrame, k));
}
/**
* After a macro function returns a value (the new AST), evaluate it
* in the original calling scope.
*/
function applyMacroEval(frame, value, k) {
	if (frame.expanded) return {
		type: "Value",
		value,
		k
	};
	if (!Array.isArray(value) && !isPersistentVector(value)) return {
		type: "Value",
		value,
		k
	};
	const marker = {
		type: "MacroEval",
		env: frame.env,
		sourceCodeInfo: frame.sourceCodeInfo,
		expanded: true
	};
	return {
		type: "Eval",
		node: isPersistentVector(value) ? toJS(value) : value,
		env: frame.env,
		k: cons(marker, k)
	};
}
/**
* Handle a completed splice expression evaluation in a code template.
* Collect the value, evaluate the next splice, or assemble the final AST data.
*/
function applyCodeTemplateBuild(frame, value, k) {
	const nextValues = [...frame.values, value];
	const nextIndex = frame.index + 1;
	if (nextIndex < frame.spliceExprs.length) {
		const newFrame = {
			...frame,
			values: nextValues,
			index: nextIndex
		};
		return {
			type: "Eval",
			node: frame.spliceExprs[nextIndex],
			env: frame.env,
			k: cons(newFrame, k)
		};
	}
	return {
		type: "Value",
		value: toAny(frame.bodyAst.length === 1 ? astToData(frame.bodyAst[0], nextValues, frame.renameMap) : frame.bodyAst.map((n) => astToData(n, nextValues, frame.renameMap))),
		k
	};
}
let gensymCounter = 0;
/** Generate a unique symbol name that won't collide with user code. */
function gensym(name) {
	return `__gensym_${name}_${gensymCounter++}__`;
}
/**
* Build a rename map for literal binding names in template AST.
* Walks the AST collecting symbol names from binding positions
* (Let targets, Function params), skipping Splice nodes.
*/
function buildRenameMap(nodes) {
	const names = /* @__PURE__ */ new Set();
	for (const node of nodes) collectBindingNames(node, names);
	const renameMap = /* @__PURE__ */ new Map();
	for (const name of names) renameMap.set(name, gensym(name));
	return renameMap;
}
/** Recursively collect symbol names from binding positions in an AST node. */
function collectBindingNames(node, names) {
	const [type, payload] = node;
	if (type === NodeTypes.Splice) return;
	switch (type) {
		case NodeTypes.Let: {
			const [target, value] = payload;
			collectBindingTargetNames(target, names);
			collectBindingNames(value, names);
			break;
		}
		case NodeTypes.Function:
		case NodeTypes.Macro: {
			const [params, bodyExprs] = payload;
			for (const param of params) collectBindingTargetNames(param, names);
			for (const expr of bodyExprs) collectBindingNames(expr, names);
			break;
		}
		default: if (Array.isArray(payload)) {
			for (const item of payload) if (Array.isArray(item) && item.length >= 2 && typeof item[0] === "string") collectBindingNames(item, names);
			else if (Array.isArray(item)) {
				for (const inner of item) if (Array.isArray(inner) && inner.length >= 2 && typeof inner[0] === "string") collectBindingNames(inner, names);
			}
		}
	}
}
/** Extract symbol names from a binding target structure. */
function collectBindingTargetNames(target, names) {
	const targetType = target[0];
	const targetPayload = target[1];
	switch (targetType) {
		case "symbol": {
			const symNode = targetPayload[0];
			if (Array.isArray(symNode) && symNode[0] === NodeTypes.Sym) names.add(symNode[1]);
			break;
		}
		case "rest":
			names.add(targetPayload[0]);
			break;
		case "array": {
			const targets = targetPayload[0];
			for (const t of targets) if (t) collectBindingTargetNames(t, names);
			break;
		}
		case "object": {
			const entries = targetPayload[0];
			for (const entry of entries) collectBindingTargetNames(entry.target, names);
			break;
		}
	}
}
/**
* Convert a pre-parsed AST node into Dvala data (arrays and primitives).
* Splice nodes are replaced with the corresponding evaluated value.
* Literal Sym nodes matching the rename map are gensymed for hygiene.
*
* The result mirrors the AST node format: [type, payload, nodeId].
* This is what macros receive and construct — plain Dvala data.
*/
function astToData(node, spliceValues, renameMap) {
	const [type, payload] = node;
	if (type === NodeTypes.Splice) {
		const sv = spliceValues[payload];
		return isPersistentVector(sv) ? toAny(toJS(sv)) : sv;
	}
	if (type === NodeTypes.InlinedData) {
		const inlined = payload;
		return isPersistentVector(inlined) ? toAny(toJS(inlined)) : inlined;
	}
	if (type === NodeTypes.Sym && renameMap && typeof payload === "string") {
		const renamed = renameMap.get(payload);
		if (renamed) return toAny([
			type,
			renamed,
			-1
		]);
	}
	if (!Array.isArray(payload)) return toAny([
		type,
		payload,
		-1
	]);
	if (type === NodeTypes.CodeTmpl) {
		const [bodyAst, innerSpliceExprs] = payload;
		const innerCount = innerSpliceExprs.length;
		return toAny([
			type,
			[bodyAst.map((n) => astToDataWithCodeTmplAwareness(n, spliceValues, renameMap, innerCount)), innerSpliceExprs.map((e) => astToData(e, [], renameMap))],
			-1
		]);
	}
	if (type === NodeTypes.Let) {
		const [target, valueNode] = payload;
		const convertedValue = astToData(valueNode, spliceValues, renameMap);
		return toAny([
			type,
			[convertBindingTarget(target, spliceValues, renameMap), convertedValue],
			-1
		]);
	}
	return toAny([
		type,
		convertArrayPayload(payload, spliceValues, renameMap),
		-1
	]);
}
/**
* Like astToData, but aware of inner CodeTmpl splice boundaries.
* Splice nodes with index < innerCount are inner splices — preserved as data tuples.
* Splice nodes with index >= innerCount are outer splices — resolved using spliceValues[index - innerCount].
*/
function astToDataWithCodeTmplAwareness(node, spliceValues, renameMap, innerCount) {
	const [type, payload] = node;
	if (type === NodeTypes.Splice) {
		const index = payload;
		if (index < innerCount) return toAny([
			type,
			index,
			-1
		]);
		return toAny([
			NodeTypes.InlinedData,
			spliceValues[index - innerCount],
			-1
		]);
	}
	if (type === NodeTypes.Sym && renameMap && typeof payload === "string") {
		const renamed = renameMap.get(payload);
		if (renamed) return toAny([
			type,
			renamed,
			-1
		]);
	}
	if (!Array.isArray(payload)) return toAny([
		type,
		payload,
		-1
	]);
	const result = [];
	for (const item of payload) {
		if (!Array.isArray(item)) {
			if (typeof item === "string" && renameMap) {
				const renamed = renameMap.get(item);
				result.push(renamed ?? item);
			} else result.push(item);
			continue;
		}
		if (item.length >= 2 && item[0] === NodeTypes.Splice) {
			const spliceIndex = item[1];
			if (spliceIndex < innerCount) result.push(toAny([
				NodeTypes.Splice,
				spliceIndex,
				-1
			]));
			else {
				const spliceValue = spliceValues[spliceIndex - innerCount];
				result.push(toAny([
					NodeTypes.InlinedData,
					spliceValue,
					-1
				]));
			}
		} else if (item.length >= 2 && typeof item[0] === "string") result.push(astToDataWithCodeTmplAwareness(item, spliceValues, renameMap, innerCount));
		else result.push(toAny(item.map((sub) => Array.isArray(sub) ? astToDataWithCodeTmplAwareness(sub, spliceValues, renameMap, innerCount) : sub)));
	}
	return toAny([
		type,
		result,
		-1
	]);
}
/**
* Convert a binding target from template AST, handling splices that resolve to
* Array/Object AST data by converting them to proper binding target format.
*
* When a splice inside a symbol binding target resolves to an Array or Object AST node,
* the target is restructured into the corresponding array/object binding target.
*/
function convertBindingTarget(target, spliceValues, renameMap) {
	const [targetType, targetPayload] = target;
	if (targetType === "symbol" && Array.isArray(targetPayload)) {
		const [nameNode, defaultNode] = targetPayload;
		let resolvedName;
		if (Array.isArray(nameNode) && nameNode[0] === NodeTypes.Splice) resolvedName = spliceValues[nameNode[1]];
		else resolvedName = astToData(nameNode, spliceValues, renameMap);
		if (isPersistentVector(resolvedName)) resolvedName = toJS(resolvedName);
		const convertedDefault = defaultNode ? astToData(defaultNode, spliceValues, renameMap) : null;
		if (Array.isArray(resolvedName) && resolvedName[0] === NodeTypes.Sym) return toAny([
			"symbol",
			[resolvedName, convertedDefault],
			-1
		]);
		if (Array.isArray(resolvedName) && resolvedName[0] === NodeTypes.Array) return toAny([
			"array",
			[resolvedName[1].map((elem) => expressionAstToBindingTarget(elem)), convertedDefault],
			-1
		]);
		if (Array.isArray(resolvedName) && resolvedName[0] === NodeTypes.Object) {
			const entries = resolvedName[1];
			const bindingEntries = [];
			for (const entry of entries) {
				const keyNode = entry[0];
				const valNode = entry[1];
				const key = keyNode[1];
				bindingEntries.push(toAny({
					key,
					keyNodeId: 0,
					target: expressionAstToBindingTarget(valNode)
				}));
			}
			return toAny([
				"object",
				[bindingEntries, convertedDefault],
				-1
			]);
		}
		return toAny([
			"symbol",
			[resolvedName, convertedDefault],
			-1
		]);
	}
	if (targetType === "array" && Array.isArray(targetPayload)) {
		const [targets, defaultNode] = targetPayload;
		return toAny([
			"array",
			[targets.map((t) => t === null ? null : convertBindingTarget(t, spliceValues, renameMap)), defaultNode ? astToData(defaultNode, spliceValues, renameMap) : null],
			-1
		]);
	}
	if (targetType === "object" && Array.isArray(targetPayload)) {
		const [entries, defaultNode] = targetPayload;
		const convertedEntries = [];
		for (const entry of entries) convertedEntries.push(toAny({
			key: entry.key,
			keyNodeId: entry.keyNodeId ?? 0,
			target: convertBindingTarget(entry.target, spliceValues, renameMap)
		}));
		return toAny([
			"object",
			[convertedEntries, defaultNode ? astToData(defaultNode, spliceValues, renameMap) : null],
			-1
		]);
	}
	if (Array.isArray(targetPayload)) return toAny([
		targetType,
		convertArrayPayload(targetPayload, spliceValues, renameMap),
		-1
	]);
	return toAny([
		targetType,
		targetPayload,
		-1
	]);
}
/**
* Convert an expression AST node (Dvala data) to a binding target (Dvala data).
* Used when a splice in binding position resolves to a complex expression.
*/
function expressionAstToBindingTarget(astData) {
	if (!Array.isArray(astData)) return astData;
	const node = astData;
	const nodeType = node[0];
	if (nodeType === NodeTypes.Sym) return toAny([
		"symbol",
		[astData, null],
		-1
	]);
	if (nodeType === NodeTypes.Array) return toAny([
		"array",
		[node[1].map((elem) => expressionAstToBindingTarget(elem)), null],
		-1
	]);
	if (nodeType === NodeTypes.Object) {
		const entries = node[1];
		const bindingEntries = [];
		for (const entry of entries) {
			const keyNode = entry[0];
			const valNode = entry[1];
			const key = keyNode[1];
			bindingEntries.push(toAny({
				key,
				keyNodeId: 0,
				target: expressionAstToBindingTarget(valNode)
			}));
		}
		return toAny([
			"object",
			[bindingEntries, null],
			-1
		]);
	}
	if (nodeType === NodeTypes.Spread) {
		const name = node[1][1];
		return toAny([
			"rest",
			[name, null],
			0
		]);
	}
	return toAny([
		"literal",
		[astData],
		0
	]);
}
/**
* Convert an array payload, handling implicit spread for Splice nodes.
*
* When a Splice node's value is an array of AST nodes (first element is an array),
* the nodes are spread into the parent array. When it's a single AST node
* (first element is a string), it's inserted as-is.
*/
function convertArrayPayload(items, spliceValues, renameMap) {
	const result = [];
	for (const item of items) {
		if (!Array.isArray(item)) {
			if (typeof item === "string" && renameMap) {
				const renamed = renameMap.get(item);
				result.push(renamed ?? item);
			} else result.push(item);
			continue;
		}
		if (item.length >= 2 && item[0] === NodeTypes.Splice) {
			const spliceValue = spliceValues[item[1]];
			if (isSpliceSpread(spliceValue)) for (const spreadItem of spliceValue) result.push(isPersistentVector(spreadItem) ? toAny(toJS(spreadItem)) : spreadItem);
			else result.push(isPersistentVector(spliceValue) ? toAny(toJS(spliceValue)) : spliceValue);
		} else if (item.length >= 2 && typeof item[0] === "string") result.push(astToData(item, spliceValues, renameMap));
		else result.push(toAny(convertArrayPayload(item, spliceValues, renameMap)));
	}
	return result;
}
/**
* Detect whether a splice value should be spread (array of AST nodes)
* or inserted as-is (single AST node or non-AST value).
*
* An array/PV of AST nodes starts with an array/PV: [[type, payload, id], ...]
* A single AST node starts with a string: [type, payload, id]
*/
function isSpliceSpread(value) {
	if (isPersistentVector(value)) {
		if (value.size === 0) return false;
		const first = value.get(0);
		return isPersistentVector(first) || Array.isArray(first);
	}
	if (!Array.isArray(value) || value.length === 0) return false;
	return Array.isArray(value[0]) || isPersistentVector(value[0]);
}
/**
* Wrap a MaybePromise<Any> result into a Step or Promise<Step>.
* If the result is a value, return a ValueStep immediately.
* If it's a Promise, return a Promise<Step> that resolves to a ValueStep.
* The trampoline loop handles the async case: runSyncTrampoline throws,
* runAsyncTrampoline awaits.
*/
function wrapMaybePromiseAsStep(result, k) {
	if (result instanceof Promise) return result.then((value) => ({
		type: "Value",
		value,
		k
	}), (error) => ({
		type: "Error",
		error: error instanceof DvalaError ? error : new DvalaError(`${error}`, void 0),
		k
	}));
	return {
		type: "Value",
		value: result,
		k
	};
}
/** Lazy-load collection utilities to avoid circular imports. */
function getCollectionUtils() {
	return {
		asColl: (v, s) => {
			if (typeof v === "string" || isPersistentVector(v) || Array.isArray(v) || isObj(v)) return v;
			throw new TypeError$1(`Expected collection, got ${valueToString(v)}`, s);
		},
		isSeq: (v) => typeof v === "string" || isPersistentVector(v) || Array.isArray(v)
	};
}
/**
* Process one step of the trampoline. Returns the next step, or a
* Promise<Step> when an async operation (e.g., native JS function) is
* encountered.
*
* - `Value` with empty `k`: the program is done (terminal state).
* - `Value` with non-empty `k`: pop the top frame and apply it.
* - `Eval`: evaluate an AST node via `stepNode` (always synchronous).
* - `Apply`: apply a frame to a value (may return Promise<Step>).
* - `Perform`: effect dispatch — local (try/with) first, then host handlers.
*
* When `handlers` and `signal` are provided (from `run()`), host handlers are
* available as a fallback for effects not matched by any local `try/with`.
*/
function tick(step, handlers, signal, snapshotState) {
	try {
		switch (step.type) {
			case "Value":
				if (step.k === null) return step;
				return applyFrame(step.k.head, step.value, step.k.tail);
			case "Eval": {
				const { node, env, k } = step;
				if (snapshotState?.onNodeEval) {
					const t = node[0];
					const isStructuralLeaf = t === NodeTypes.Sym || t === NodeTypes.Builtin || t === NodeTypes.Special || t === NodeTypes.Reserved || t === NodeTypes.Effect;
					const fireForLeaf = step.forceRecord === true && snapshotState.recordBranchArms === true;
					if (!isStructuralLeaf || fireForLeaf) {
						const result = snapshotState.onNodeEval(node, () => ({
							env,
							k,
							resume: () => {},
							getSnapshots: () => [...snapshotState.snapshots]
						}));
						if (result instanceof Promise) return result.then(() => stepNode(node, env, k));
					}
				}
				return stepNode(node, env, k);
			}
			case "Apply": return applyFrame(step.frame, step.value, step.k);
			case "Perform": return dispatchPerform(step.effect, step.arg, step.k, step.sourceCodeInfo, handlers, signal, snapshotState);
			case "Parallel": return executeParallelBranches(step.branches, step.env, step.k, handlers, signal, snapshotState, step.mode);
			case "Race": return executeRaceBranches(step.branches, step.env, step.k, handlers, signal, snapshotState);
			case "ParallelResume": throw new RuntimeError("ParallelResumeStep is no longer supported.", void 0);
			case "BranchComplete": return step;
			case "ReRunParallelExec": return executeReRunParallel(step, handlers, signal, snapshotState);
			case "ResumeParallelExec": return executeResumeParallel(step, handlers, signal, snapshotState);
			case "Error": {
				const patchedError = patchErrorWithMacroCallSite(step.error, step.k);
				const effectStep = tryDispatchDvalaError(patchedError, step.k);
				if (effectStep) return effectStep;
				patchedError.attachCallStack(reconstructCallStack(step.k));
				throw patchedError;
			}
		}
	} catch (error) {
		if (isSuspensionSignal(error) || isHaltSignal(error)) throw error;
		if (error instanceof DvalaError) {
			const kForDispatch = step.type === "BranchComplete" ? null : step.type === "ReRunParallelExec" || step.type === "ResumeParallelExec" ? step.k : step.type === "Value" ? step.k?.tail ?? null : step.k;
			const patchedError = patchErrorWithMacroCallSite(error, kForDispatch);
			const effectStep = tryDispatchDvalaError(patchedError, kForDispatch);
			if (effectStep) return effectStep;
			if (!(patchedError instanceof UserError) && handlers && findMatchingHandlers("dvala.error", handlers).length > 0) return {
				type: "Perform",
				effect: getEffectRef("dvala.error"),
				arg: buildErrorPayload(patchedError),
				k: kForDispatch,
				sourceCodeInfo: patchedError.sourceCodeInfo
			};
			patchedError.attachCallStack(reconstructCallStack(kForDispatch));
			throw patchedError;
		}
		throw error;
	}
}
/**
* Run the trampoline synchronously to completion.
* Throws if any step produces a Promise (i.e., an async operation was
* encountered in a synchronous context).
*/
function runSyncTrampoline(initial, effectHandlers, snapshotState) {
	let step = initial;
	for (;;) {
		if (step instanceof Promise) throw new RuntimeError("Unexpected async operation in synchronous context.", void 0);
		if (step.type === "Value" && step.k === null) return step.value;
		step = tick(step, effectHandlers, void 0, snapshotState);
	}
}
/**
* Run the trampoline asynchronously to completion.
* Awaits any Promise<Step> that surfaces from async operations.
*/
async function runAsyncTrampoline(initial, snapshotState) {
	let step = initial;
	for (;;) {
		if (step instanceof Promise) step = await step;
		if (step.type === "Value" && step.k === null) return step.value;
		step = tick(step, void 0, void 0, snapshotState);
	}
}
/**
* Build the initial Step for evaluating an AST (sequence of top-level nodes).
*/
function buildInitialStep(nodes, env) {
	if (nodes.length === 0) return {
		type: "Value",
		value: null,
		k: null
	};
	if (nodes.length === 1) return {
		type: "Eval",
		node: nodes[0],
		env,
		k: null
	};
	const sequenceFrame = {
		type: "Sequence",
		nodes,
		index: 1,
		env
	};
	return {
		type: "Eval",
		node: nodes[0],
		env,
		k: cons(sequenceFrame, null)
	};
}
/**
* Merge an AST's source map into the context stack's accumulated source map.
* With global node IDs, each AST's positions are at non-overlapping indices,
* so we just copy positions and add new source entries.
*/
function mergeSourceMap(contextStack, sourceMap) {
	if (!sourceMap) return;
	if (sourceMap === contextStack.sourceMap) return;
	if (!contextStack.sourceMap) {
		contextStack.sourceMap = {
			sources: [...sourceMap.sources],
			positions: new Map(sourceMap.positions)
		};
		return;
	}
	const sourceOffset = contextStack.sourceMap.sources.length;
	contextStack.sourceMap.sources.push(...sourceMap.sources);
	for (const [nodeId, pos] of sourceMap.positions) contextStack.sourceMap.positions.set(nodeId, {
		...pos,
		source: pos.source + sourceOffset
	});
}
/**
* Build a minimal SnapshotState carrying only an onNodeEval hook — for coverage
* tracking on the sync (and sync-effects) paths, which otherwise pass no
* snapshotState. No checkpointing fields are set, so the snapshot machinery
* stays inert; only the per-node coverage hook fires.
*/
function coverageSnapshotState(onNodeEval) {
	return {
		snapshots: [],
		nextSnapshotIndex: 0,
		executionId: generateUUID(),
		onNodeEval,
		recordBranchArms: true
	};
}
/**
* Evaluate an AST using the trampoline.
* Returns the final value synchronously, or a Promise if async operations
* are involved (e.g., native JS functions returning Promises).
*/
function evaluate(ast, contextStack, onNodeEval) {
	mergeSourceMap(contextStack, ast.sourceMap);
	const snapshotState = onNodeEval ? coverageSnapshotState(onNodeEval) : void 0;
	const initial = buildInitialStep(ast.body, contextStack);
	try {
		return runSyncTrampoline(initial, void 0, snapshotState);
	} catch (error) {
		if (error instanceof DvalaError && error.message.includes("Unexpected async operation")) return runAsyncTrampoline(buildInitialStep(ast.body, contextStack), snapshotState);
		throw error;
	}
}
/**
* Evaluate an AST using the async trampoline directly.
* Use this when the caller knows that async operations may be involved
* (e.g., from Dvala.async.run) to avoid the sync-first-then-retry pattern
* which can cause side effects to be executed twice.
*/
function evaluateAsync(ast, contextStack) {
	mergeSourceMap(contextStack, ast.sourceMap);
	return runAsyncTrampoline(buildInitialStep(ast.body, contextStack));
}
/**
* Evaluate a single AST node using the trampoline.
* Used as the `evaluateNode` callback passed to `getUndefinedSymbols`
* and other utilities.
*/
function evaluateNode(node, contextStack) {
	const initial = {
		type: "Eval",
		node,
		env: contextStack,
		k: null
	};
	try {
		return runSyncTrampoline(initial);
	} catch (error) {
		if (error instanceof DvalaError && error.message.includes("Unexpected async operation")) return runAsyncTrampoline({
			type: "Eval",
			node,
			env: contextStack,
			k: null
		});
		throw error;
	}
}
/**
* Evaluate an AST with full effect handler support.
*
* Uses the async trampoline loop, passing `handlers` and `signal` to `tick`
* so that `dispatchPerform` can fall back to host handlers when no local
* `try/with` matches.
*
* Always resolves — never rejects. All errors are captured in
* `RunResult.error`. Suspension is signaled via `RunResult.suspended`.
*
* The `AbortController` is created internally per `run()` call. The signal
* is passed to every host handler. Used for `race()` cancellation (Phase 6)
* and host-side timeouts.
*/
async function evaluateWithEffects(ast, contextStack, handlers, maxSnapshots, deserializeOptions, autoCheckpoint, terminalSnapshot, onNodeEval) {
	mergeSourceMap(contextStack, ast.sourceMap);
	const signal = new AbortController().signal;
	return runEffectLoop(buildInitialStep(ast.body, contextStack), handlers, signal, void 0, maxSnapshots, deserializeOptions, autoCheckpoint, terminalSnapshot, onNodeEval);
}
/**
* Evaluate an AST synchronously with effect handler support.
*
* Uses the sync trampoline with `effectHandlers` threaded through `tick`.
* Throws if an async operation is encountered (e.g., an async handler
* is used). Handlers may call `resume(value)`, `fail(msg?)`, or `next()`.
* Calling `suspend()` will throw a runtime error.
*/
function evaluateWithSyncEffects(ast, contextStack, effectHandlers, onNodeEval) {
	mergeSourceMap(contextStack, ast.sourceMap);
	const snapshotState = onNodeEval ? coverageSnapshotState(onNodeEval) : void 0;
	const initial = buildInitialStep(ast.body, contextStack);
	try {
		return runSyncTrampoline(initial, effectHandlers, snapshotState);
	} catch (error) {
		if (error instanceof DvalaError && error.message.includes("Unexpected async operation")) return runSyncTrampoline(buildInitialStep(ast.body, contextStack), effectHandlers, snapshotState);
		throw error;
	}
}
/**
* Resume a suspended continuation with a value.
*
* Re-enters the trampoline with `{ type: 'Value', value, k }` where `k` is
* the deserialized continuation stack. Host handlers and signal are provided
* fresh for this run.
*/
async function resumeWithEffects(k, value, handlers, initialSnapshotState, deserializeOptions) {
	return continueWithEffects({
		type: "Value",
		value,
		k
	}, handlers, initialSnapshotState, deserializeOptions);
}
async function continueWithEffects(initial, handlers, initialSnapshotState, deserializeOptions, terminalSnapshot) {
	const signal = new AbortController().signal;
	return runEffectLoop(initial, handlers, signal, initialSnapshotState, initialSnapshotState?.maxSnapshots, deserializeOptions, initialSnapshotState?.autoCheckpoint, terminalSnapshot);
}
/**
* Re-trigger the effect from a suspended snapshot.
*
* Deserializes the continuation from `snapshot` and re-dispatches the
* original effect (captured in `snapshot.effectName` / `snapshot.effectArg`)
* to the registered host handlers. The handler then calls `resume(value)`,
* `fail()`, or `suspend()` as normal.
*
* Throws if the snapshot has no captured effect (e.g. suspended from a
* parallel/race branch rather than an effect handler).
*/
async function retriggerWithEffects(k, effectName, effectArg, handlers, initialSnapshotState, deserializeOptions, outerSignal, inheritedExecutionId) {
	const abortController = new AbortController();
	const signal = outerSignal ? combineSignals(outerSignal, abortController.signal) : abortController.signal;
	const snapshotState = initialSnapshotState && "executionId" in initialSnapshotState ? initialSnapshotState : {
		snapshots: initialSnapshotState?.snapshots ?? [],
		nextSnapshotIndex: initialSnapshotState?.nextSnapshotIndex ?? 0,
		executionId: inheritedExecutionId ?? generateUUID(),
		maxSnapshots: initialSnapshotState?.maxSnapshots,
		autoCheckpoint: initialSnapshotState?.autoCheckpoint
	};
	const matchingHandlers = findMatchingHandlers(effectName, handlers);
	let firstStep;
	try {
		firstStep = await Promise.resolve(dispatchHostHandler(effectName, matchingHandlers, effectArg, k, signal, void 0, snapshotState));
	} catch (error) {
		if (isSuspensionSignal(error)) return {
			type: "suspended",
			snapshot: createSnapshot({
				continuation: serializeSuspensionBlob(error.k, error.snapshots, error.nextSnapshotIndex, error.meta),
				timestamp: Date.now(),
				index: snapshotState.nextSnapshotIndex++,
				executionId: snapshotState.executionId,
				message: SUSPENDED_MESSAGE,
				meta: error.meta,
				effectName: error.effectName,
				effectArg: error.effectArg
			}),
			_rawSuspension: {
				k: error.k,
				snapshots: [...snapshotState.snapshots],
				nextSnapshotIndex: snapshotState.nextSnapshotIndex,
				meta: error.meta,
				effectName: error.effectName,
				effectArg: error.effectArg
			}
		};
		if (isHaltSignal(error)) return {
			type: "halted",
			value: error.value
		};
		if (error instanceof DvalaError) return {
			type: "error",
			error
		};
		return {
			type: "error",
			error: new DvalaError(`${error}`, void 0)
		};
	}
	return runEffectLoop(firstStep, handlers, signal, snapshotState, snapshotState.maxSnapshots, deserializeOptions, snapshotState.autoCheckpoint);
}
/**
* Shared effect trampoline loop used by both `evaluateWithEffects` and
* `resumeWithEffects`. Runs the trampoline to completion, suspension, or error.
*
*/
async function runEffectLoop(initial, handlers, signal, initialSnapshotState, maxSnapshots, deserializeOptions, autoCheckpoint, terminalSnapshot, onNodeEval, inheritedExecutionId) {
	const snapshotState = initialSnapshotState && "executionId" in initialSnapshotState ? initialSnapshotState : {
		snapshots: initialSnapshotState ? initialSnapshotState.snapshots : [],
		nextSnapshotIndex: initialSnapshotState ? initialSnapshotState.nextSnapshotIndex : 0,
		executionId: inheritedExecutionId ?? generateUUID(),
		...maxSnapshots !== void 0 ? { maxSnapshots } : {},
		...autoCheckpoint ? { autoCheckpoint } : {},
		...terminalSnapshot ? { terminalSnapshot } : {},
		...onNodeEval ? {
			onNodeEval,
			recordBranchArms: true
		} : {}
	};
	if (snapshotState.autoCheckpoint) {
		const continuation = serializeToObject(initial.type === "Eval" ? initial.k : null, void 0, initial.type === "Eval" ? initial : void 0);
		snapshotState.snapshots.push(createSnapshot({
			continuation,
			timestamp: Date.now(),
			index: snapshotState.nextSnapshotIndex++,
			executionId: snapshotState.executionId,
			message: "Program start"
		}));
	}
	let step = initial;
	function createTerminalSnapshot(options) {
		if (!snapshotState.autoCheckpoint && !snapshotState.terminalSnapshot) return;
		const continuation = serializeTerminalSnapshot(snapshotState.snapshots, snapshotState.nextSnapshotIndex);
		let meta = PersistentMap.empty();
		if (options?.error) meta = meta.assoc("error", toAny(options.error.toJSON()));
		if (options?.halted) meta = meta.assoc("halted", true);
		if (options?.result !== void 0) meta = meta.assoc("result", options.result);
		const message = options?.error ? "Run failed with error" : options?.halted ? "Program halted" : "Run completed successfully";
		return createSnapshot({
			continuation,
			timestamp: Date.now(),
			index: snapshotState.nextSnapshotIndex,
			executionId: snapshotState.executionId,
			message,
			terminal: true,
			...meta.size > 0 ? { meta: toJS(meta) } : {}
		});
	}
	const YIELD_INTERVAL = 1e4;
	let tickCount = 0;
	for (;;) try {
		for (;;) {
			if (step instanceof Promise) {
				step = await step;
				tickCount = 0;
			}
			if (step.type === "Value" && step.k === null) {
				await drainProgramCleanups(snapshotState);
				const snapshot = createTerminalSnapshot({ result: step.value });
				return snapshot ? {
					type: "completed",
					value: step.value,
					snapshot
				} : {
					type: "completed",
					value: step.value
				};
			}
			if (step.type === "BranchComplete") return {
				type: "completed",
				value: step.value
			};
			step = tick(step, handlers, signal, snapshotState);
			if (++tickCount >= YIELD_INTERVAL) {
				tickCount = 0;
				await new Promise((resolve) => setTimeout(resolve, 0));
			}
		}
	} catch (error) {
		if (isResumeFromSignal(error)) {
			const { k: restoredK } = deserializeFromObject(error.continuation, deserializeOptions);
			if (error.boundaryPath.length > 0) {
				const restoredBoundaryPath = getParallelBoundaryPath(restoredK);
				if (!sameBoundaryPath(error.boundaryPath, restoredBoundaryPath)) {
					const runtimeError = new RuntimeError("resumeFrom cannot cross a parallel branch boundary.", void 0);
					const snapshot = createTerminalSnapshot({ error: runtimeError });
					return snapshot ? {
						type: "error",
						error: runtimeError,
						snapshot
					} : {
						type: "error",
						error: runtimeError
					};
				}
			}
			const cutIdx = snapshotState.snapshots.findIndex((s) => s.index > error.trimToIndex);
			if (cutIdx !== -1) snapshotState.snapshots.splice(cutIdx);
			step = {
				type: "Value",
				value: error.value,
				k: restoredK
			};
			continue;
		}
		if (isSuspensionSignal(error)) {
			if (error.nextSnapshotIndex > snapshotState.nextSnapshotIndex) snapshotState.nextSnapshotIndex = error.nextSnapshotIndex;
			return {
				type: "suspended",
				snapshot: createSnapshot({
					continuation: serializeSuspensionBlob(error.k, error.snapshots, error.nextSnapshotIndex, error.meta),
					timestamp: Date.now(),
					index: snapshotState.nextSnapshotIndex++,
					executionId: snapshotState.executionId,
					message: SUSPENDED_MESSAGE,
					meta: error.meta,
					effectName: error.effectName,
					effectArg: error.effectArg
				}),
				_rawSuspension: {
					k: error.k,
					snapshots: [...snapshotState.snapshots],
					nextSnapshotIndex: snapshotState.nextSnapshotIndex,
					meta: error.meta,
					effectName: error.effectName,
					effectArg: error.effectArg
				}
			};
		}
		if (isHaltSignal(error)) {
			await drainProgramCleanups(snapshotState, true);
			const snapshot = createTerminalSnapshot({
				result: error.value,
				halted: true
			});
			return snapshot ? {
				type: "halted",
				value: error.value,
				snapshot
			} : {
				type: "halted",
				value: error.value
			};
		}
		if (error instanceof DvalaError) {
			await drainProgramCleanups(snapshotState, true);
			const snapshot = createTerminalSnapshot({ error });
			return snapshot ? {
				type: "error",
				error,
				snapshot
			} : {
				type: "error",
				error
			};
		}
		await drainProgramCleanups(snapshotState, true);
		const snapshot = createTerminalSnapshot();
		const dvalaError = new DvalaError(`${error}`, void 0);
		return snapshot ? {
			type: "error",
			error: dvalaError,
			snapshot
		} : {
			type: "error",
			error: dvalaError
		};
	}
}
//#endregion
//#region src/evaluator/foldEvaluate.ts
/** Default step budget per fold attempt (decision #1 in the design doc). */
const DEFAULT_FOLD_STEP_BUDGET = 1e4;
/**
* Evaluate a single AST node for constant folding. Does NOT dispatch effects
* to host handlers — if the evaluator surfaces a Perform step, we stop and
* report the effect name so the caller can emit a warning. Any async surface
* (unexpected in pure code) is treated as a generic failure.
*/
function evaluateNodeForFold(node, contextStack, maxSteps = DEFAULT_FOLD_STEP_BUDGET) {
	let step = {
		type: "Eval",
		node,
		env: contextStack,
		k: null
	};
	let stepsRemaining = maxSteps;
	for (;;) {
		if (step instanceof Promise) return {
			ok: false,
			reason: "error"
		};
		if (step.type === "Value" && step.k === null) return {
			ok: true,
			value: step.value
		};
		if (step.type === "Perform") return {
			ok: false,
			reason: "effect",
			effectName: step.effect.name
		};
		if (stepsRemaining-- <= 0) return {
			ok: false,
			reason: "budget"
		};
		try {
			step = tick(step);
		} catch (error) {
			if (error instanceof ReferenceError || error instanceof MacroError) return {
				ok: false,
				reason: "error"
			};
			if (error instanceof DvalaError) return {
				ok: false,
				reason: "effect",
				effectName: "dvala.error"
			};
			return {
				ok: false,
				reason: "error"
			};
		}
	}
}
//#endregion
//#region src/evaluator/serialization.ts
/**
* Checks whether a Dvala runtime value is fully JSON-serializable.
*
* Returns `true` if the value can be serialized and later restored.
* Returns `false` if any part of the value contains a non-serializable reference.
*
* Uses a `Set` to track visited objects and avoid infinite loops from
* circular references (which are themselves not serializable).
*/
function isSerializable(value, visited = /* @__PURE__ */ new Set()) {
	if (value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") return true;
	if (typeof value === "object") {
		if (visited.has(value)) return false;
		visited.add(value);
	}
	if (isRegularExpression(value)) return true;
	if (isEffect(value)) return true;
	if (isDvalaFunction(value)) return isDvalaFunctionSerializable(value, visited);
	if (isArr(value)) {
		for (const item of value) if (!isSerializable(item, visited)) return false;
		return true;
	}
	if (isObj(value)) {
		for (const [, v] of value) if (!isSerializable(v, visited)) return false;
		return true;
	}
	if (typeof value === "object") {
		const obj = value;
		for (const v of Object.values(obj)) if (!isSerializable(v, visited)) return false;
		return true;
	}
	return false;
}
function isDvalaFunctionSerializable(fn, visited) {
	switch (fn.functionType) {
		case "UserDefined":
		case "Builtin":
		case "SpecialBuiltin":
		case "Module":
		case "QualifiedMatcher":
		case "Handler":
		case "Resume": return true;
		case "Partial": {
			const partial = fn;
			return isSerializable(partial.function, visited) && [...partial.params].every((p) => isSerializable(p, visited));
		}
		case "Comp": return [...fn.params].every((p) => isSerializable(p, visited));
		case "Constantly": return isSerializable(fn.value, visited);
		case "Juxt": return [...fn.params].every((p) => isSerializable(p, visited));
		case "Complement": return isSerializable(fn.function, visited);
		case "EveryPred": return [...fn.params].every((p) => isSerializable(p, visited));
		case "SomePred": return [...fn.params].every((p) => isSerializable(p, visited));
		case "Fnull": {
			const fnull = fn;
			return isSerializable(fnull.function, visited) && [...fnull.params].every((p) => isSerializable(p, visited));
		}
		/* v8 ignore next 2 */
		default: return false;
	}
}
/**
* Describes why a value is not serializable.
* Returns `null` if the value is serializable.
* Returns a human-readable string describing the first non-serializable
* component found.
*/
function describeSerializationIssue(value, path = "value") {
	if (value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") return null;
	if (isRegularExpression(value)) return null;
	if (isEffect(value)) return null;
	if (isDvalaFunction(value)) {
		if (value.functionType === "UserDefined" || value.functionType === "Builtin" || value.functionType === "SpecialBuiltin" || value.functionType === "Module" || value.functionType === "QualifiedMatcher" || value.functionType === "Handler" || value.functionType === "Resume") return null;
		if (value.functionType === "Partial") {
			const partial = value;
			const fnIssue = describeSerializationIssue(partial.function, `${path}.function`);
			if (fnIssue) return fnIssue;
			for (let i = 0; i < partial.params.size; i++) {
				const paramIssue = describeSerializationIssue(partial.params.get(i), `${path}.params[${i}]`);
				if (paramIssue) return paramIssue;
			}
			return null;
		}
		if (value.functionType === "Comp") {
			const comp = value;
			for (let i = 0; i < comp.params.size; i++) {
				const paramIssue = describeSerializationIssue(comp.params.get(i), `${path}.params[${i}]`);
				if (paramIssue) return paramIssue;
			}
			return null;
		}
		if (value.functionType === "Complement") return describeSerializationIssue(value.function, `${path}.function`);
		if (value.functionType === "Constantly") return describeSerializationIssue(value.value, `${path}.value`);
		if (value.functionType === "Juxt") {
			const juxt = value;
			for (let i = 0; i < juxt.params.size; i++) {
				const paramIssue = describeSerializationIssue(juxt.params.get(i), `${path}.params[${i}]`);
				if (paramIssue) return paramIssue;
			}
			return null;
		}
		if (value.functionType === "EveryPred") {
			const everyPred = value;
			for (let i = 0; i < everyPred.params.size; i++) {
				const paramIssue = describeSerializationIssue(everyPred.params.get(i), `${path}.params[${i}]`);
				if (paramIssue) return paramIssue;
			}
			return null;
		}
		if (value.functionType === "SomePred") {
			const somePred = value;
			for (let i = 0; i < somePred.params.size; i++) {
				const paramIssue = describeSerializationIssue(somePred.params.get(i), `${path}.params[${i}]`);
				if (paramIssue) return paramIssue;
			}
			return null;
		}
		if (value.functionType === "Fnull") {
			const fnull = value;
			const fnIssue = describeSerializationIssue(fnull.function, `${path}.function`);
			if (fnIssue) return fnIssue;
			for (let i = 0; i < fnull.params.size; i++) {
				const paramIssue = describeSerializationIssue(fnull.params.get(i), `${path}.params[${i}]`);
				if (paramIssue) return paramIssue;
			}
			return null;
		}
		return `${path} has unknown function type ${value}`;
	}
	if (isArr(value)) {
		let i = 0;
		for (const item of value) {
			const issue = describeSerializationIssue(item, `${path}[${i}]`);
			if (issue) return issue;
			i++;
		}
		return null;
	}
	if (isObj(value)) {
		for (const [key, v] of value) {
			const issue = describeSerializationIssue(v, `${path}.${key}`);
			if (issue) return issue;
		}
		return null;
	}
	if (typeof value === "object") {
		const obj = value;
		for (const [key, v] of Object.entries(obj)) {
			const issue = describeSerializationIssue(v, `${path}.${key}`);
			if (issue) return issue;
		}
		return null;
	}
	return `${path} has unexpected type ${typeof value}`;
}
//#endregion
//#region src/builtin/interface.ts
const dataTypes = [
	"number",
	"string",
	"object",
	"array",
	"vector",
	"matrix",
	"grid",
	"boolean",
	"atom",
	"function",
	"integer",
	"any",
	"null",
	"collection",
	"sequence",
	"regexp",
	"effect",
	"never"
];
function isDataType(arg) {
	return dataTypes.includes(arg);
}
const categories = Object.keys({
	"special-expression": true,
	predicate: true,
	sequence: true,
	collection: true,
	array: true,
	object: true,
	string: true,
	math: true,
	functional: true,
	"regular-expression": true,
	bitwise: true,
	misc: true,
	meta: true,
	assertion: true,
	vector: true,
	linearAlgebra: true,
	matrix: true,
	grid: true,
	numberTheory: true,
	convert: true,
	json: true,
	time: true,
	effectHandler: true,
	macros: true,
	shorthand: true,
	datatype: true,
	prelude: true,
	effect: true,
	"playground-effect": true,
	ast: true,
	test: true
});
const coreCategories = [
	"special-expression",
	"array",
	"assertion",
	"bitwise",
	"collection",
	"datatype",
	"functional",
	"math",
	"meta",
	"misc",
	"object",
	"predicate",
	"prelude",
	"regular-expression",
	"sequence",
	"shorthand",
	"string"
];
const coreCategoryDescriptions = {
	"special-expression": "Flow control, binding, functions, effects, and macros.",
	array: "Create and transform ordered sequences of values.",
	assertion: "Throw errors when conditions are not met.",
	bitwise: "Bitwise operations on integers.",
	collection: "Operations shared across arrays, objects, and strings.",
	datatype: "Inspect and assert value types at runtime.",
	functional: "Higher-order functions: compose, curry, and partial application.",
	math: "Arithmetic, rounding, and numeric operations.",
	meta: "Inspect and manipulate Dvala expressions at runtime.",
	misc: "Miscellaneous utilities.",
	object: "Create and transform key-value objects.",
	predicate: "Test values: equality, comparison, and type checks.",
	prelude: "Refined type aliases auto-loaded into every typecheck session (Positive, NonEmpty, ...).",
	"regular-expression": "Create and match regular expressions.",
	sequence: "Operations on arrays and strings as ordered sequences.",
	shorthand: "Concise syntax aliases for common operations.",
	string: "Create and transform text strings."
};
function isFunctionDocs(docs) {
	return "args" in docs && "variants" in docs;
}
//#endregion
//#region src/builtin/normalExpressions/initCoreDvala.ts
const coreDvalaSources = {
	collection: "{\n  map: (firstColl, ...args) -> do\n    let fn = last(args);\n    let otherColls = slice(args, 0, count(args) - 1);\n    let allColls = [firstColl, ...otherColls];\n\n    if isObject(firstColl) then\n      do\n        let expectedKeys = keys(firstColl) |> sort;\n        for(obj in otherColls) -> do\n          if !(isObject(obj)) then\n            perform(@dvala.error, { message: \"Expected object\" })\n          else\n            null\n          end;\n          let objKeys = keys(obj) |> sort;\n          if !(objKeys == expectedKeys) then\n            perform(\n              @dvala.error,\n              {\n                message: ++(\n                  \"All objects must have the same keys. Expected: \",\n                  join(expectedKeys, \", \"),\n                  \". Found: \",\n                  join(objKeys, \", \")\n                ),\n              }\n            )\n          else\n            null\n          end;\n        end;\n        reduce(\n          keys(firstColl),\n          (acc, k) -> do\n            let a = for(coll in allColls) -> coll(k);\n            assoc(acc, k, apply(fn, a));\n          end,\n          {}\n        );\n      end\n    else if isString(firstColl) then\n      do\n        for(s in otherColls) -> do\n          if !(isString(s)) then\n            perform(@dvala.error, { message: \"Expected string\" })\n          else\n            null\n          end;\n        end;\n        let len =\n          reduce(otherColls, (m, s) -> min(m, count(s)), count(firstColl));\n        let mapped =\n          for(i in range(len)) -> do\n            let a = for(coll in allColls) -> nth(coll, i);\n            apply(fn, a);\n          end;\n        reduce(\n          mapped,\n          (acc, ch) -> do\n            if !(isString(ch)) then\n              perform(@dvala.error, { message: \"Expected string\" })\n            else\n              null\n            end;\n            ++(acc, ch);\n          end,\n          \"\"\n        );\n      end\n    else\n      do\n        for(x in otherColls) -> do\n          if !(isArray(x)) then\n            perform(@dvala.error, { message: \"Expected array\" })\n          else\n            null\n          end;\n        end;\n        let len =\n          reduce(otherColls, (m, x) -> min(m, count(x)), count(firstColl));\n        for(i in range(len)) -> do\n          let a = for(coll in allColls) -> nth(coll, i);\n          apply(fn, a);\n        end;\n      end\n    end;\n  end,\n  filter: (coll, fn) -> do\n    if isArray(coll) then\n      reduce(\n        coll,\n        (acc, elem) -> if fn(elem) then [...acc, elem] else acc end,\n        []\n      )\n    else if isString(coll) then\n      reduce(coll, (acc, ch) -> if fn(ch) then ++(acc, ch) else acc end, \"\")\n    else if isObject(coll) then\n      reduce(\n        keys(coll),\n        (acc, k) -> do\n          if fn(coll(k)) then assoc(acc, k, coll(k)) else acc end;\n        end,\n        {}\n      )\n    else\n      perform(@dvala.error, { message: \"Expected collection\" })\n    end;\n  end,\n  reduce: (coll, fn, initial) -> do\n    if isString(coll) then\n      loop(acc = initial, i = 0) -> do\n        if i >= count(coll) then\n          acc\n        else\n          recur(fn(acc, nth(coll, i)), i + 1)\n        end;\n      end\n    else if isArray(coll) then\n      loop(acc = initial, i = 0) -> do\n        if i >= count(coll) then\n          acc\n        else\n          recur(fn(acc, nth(coll, i)), i + 1)\n        end;\n      end\n    else if isObject(coll) then\n      do\n        let values = vals(coll);\n        loop(acc = initial, i = 0) -> do\n          if i >= count(values) then\n            acc\n          else\n            recur(fn(acc, nth(values, i)), i + 1)\n          end;\n        end;\n      end\n    else\n      perform(@dvala.error, { message: \"Expected collection\" })\n    end;\n  end,\n};\n",
	error: "{\n  raise: (message, data = null) -> perform(\n    @dvala.error,\n    { type: \"UserError\", message: message, data: data }\n  ),\n};\n",
	sequence: "{\n  some: (seq, fn) -> do\n    if isNull(seq) then\n      null\n    else\n      do\n        loop(i = 0) -> do\n          if i >= count(seq) then\n            null\n          else\n            do\n              let elem = nth(seq, i);\n              if fn(elem) then elem else recur(i + 1) end;\n            end\n          end;\n        end;\n      end\n    end;\n  end,\n  takeWhile: (seq, fn) -> do\n    let isStr = isString(seq);\n    let len = count(seq);\n    let idx =\n      loop(i = 0) -> do\n        if i >= len then\n          len\n        else if fn(nth(seq, i)) then\n          recur(i + 1)\n        else\n          i\n        end;\n      end;\n    slice(seq, 0, idx);\n  end,\n  dropWhile: (seq, fn) -> do\n    let isStr = isString(seq);\n    let len = count(seq);\n    let idx =\n      loop(i = 0) -> do\n        if i >= len then\n          len\n        else if fn(nth(seq, i)) then\n          recur(i + 1)\n        else\n          i\n        end;\n      end;\n    slice(seq, idx);\n  end,\n  sort: (seq, ...args) -> do\n    let cmp = if count(args) == 0 then compare else first(args) end;\n    let isStr = isString(seq);\n    let arr = if isStr then split(seq, \"\") else seq end;\n    let len = count(arr);\n\n    // merge two sorted arrays\n    let mergeArrays = (left, right) -> do\n      let leftLen = count(left);\n      let rightLen = count(right);\n      loop(result = [], li = 0, ri = 0) -> do\n        if li >= leftLen then\n          ++(result, slice(right, ri))\n        else if ri >= rightLen then\n          ++(result, slice(left, li))\n        else\n          do\n            let l = nth(left, li);\n            let r = nth(right, ri);\n            if cmp(l, r) <= 0 then\n              recur(push(result, l), li + 1, ri)\n            else\n              recur(push(result, r), li, ri + 1)\n            end;\n          end\n        end;\n      end;\n    end;\n\n    // recursive mergeSort\n    let mergeSort = (a) -> do\n      let n = count(a);\n      if n <= 1 then\n        a\n      else\n        do\n          let mid = floor(n / 2);\n          let left = mergeSort(slice(a, 0, mid));\n          let right = mergeSort(slice(a, mid));\n          mergeArrays(left, right);\n        end\n      end;\n    end;\n\n    let sorted = mergeSort(arr);\n    if isStr then join(sorted, \"\") else sorted end;\n  end,\n  minIndex: (vector) -> do\n    if !isVector(vector) || isEmpty(vector) then\n      perform(\n        @dvala.error,\n        \"Expected a non empty vector, but got \" ++ str(vector)\n      )\n    else\n      null\n    end;\n    // loop (not range) keeps the running index an Integer, as nth requires.\n    let n = count(vector);\n    loop(i = 1, best = 0) -> do\n      if i >= n then\n        best\n      else if nth(vector, i) < nth(vector, best) then\n        recur(i + 1, i)\n      else\n        recur(i + 1, best)\n      end;\n    end;\n  end,\n  maxIndex: (vector) -> do\n    if !isVector(vector) || isEmpty(vector) then\n      perform(\n        @dvala.error,\n        \"Expected a non empty vector, but got \" ++ str(vector)\n      )\n    else\n      null\n    end;\n    let n = count(vector);\n    loop(i = 1, best = 0) -> do\n      if i >= n then\n        best\n      else if nth(vector, i) > nth(vector, best) then\n        recur(i + 1, i)\n      else\n        recur(i + 1, best)\n      end;\n    end;\n  end,\n  sortIndices: (vector) -> do\n    if !isVector(vector) then\n      perform(@dvala.error, \"Expected a vector, but got \" ++ str(vector))\n    else\n      null\n    end;\n    // Build an Integer index array via loop, then sort it by the values it points at.\n    let n = count(vector);\n    let indices =\n      loop(i = 0, acc = []) -> do\n        if i >= n then acc else recur(i + 1, push(acc, i)) end;\n      end;\n    sort(indices, (a, b) -> nth(vector, a) - nth(vector, b));\n  end,\n  countValues: (vector) -> do\n    if !isVector(vector) then\n      perform(@dvala.error, \"Expected a vector, but got \" ++ str(vector))\n    else\n      null\n    end;\n    let n = count(vector);\n    if n == 0 then\n      []\n    else\n      do\n        // Sort once (O(n log n)), then run-length-encode adjacent equal values\n        // into [value, count] pairs in a single O(n) pass (value-ascending).\n        let sorted = sort(vector);\n        let pairs =\n          loop(i = 1, prev = nth(sorted, 0), runLen = 1, acc = []) -> do\n            if i >= n then\n              push(acc, [prev, runLen])\n            else\n              do\n                let cur = nth(sorted, i);\n                if cur == prev then\n                  recur(i + 1, prev, runLen + 1, acc)\n                else\n                  recur(i + 1, cur, 1, push(acc, [prev, runLen]))\n                end;\n              end\n            end;\n          end;\n        // Re-sort by count descending, breaking ties by value ascending.\n        sort(pairs, (a, b) -> do\n          let countDiff = nth(b, 1) - nth(a, 1);\n          if countDiff != 0 then countDiff else nth(a, 0) - nth(b, 0) end;\n        end);\n      end\n    end;\n  end,\n  second: (seq) -> nth(seq, 1),\n  rest: (seq) -> slice(seq, 1),\n  next: (seq) -> do\n    let r = slice(seq, 1);\n    if isEmpty(r) then null else r end;\n  end,\n  take: (seq, n) -> slice(seq, 0, max(ceil(n), 0)),\n  drop: (seq, n) -> slice(seq, max(ceil(n), 0)),\n  takeLast: (seq, n) -> slice(seq, count(seq) - max(ceil(n), 0)),\n  dropLast: (seq, n) -> slice(seq, 0, count(seq) - max(ceil(n), 0)),\n  indexOf: (seq, value) -> do\n    if isNull(seq) then\n      null\n    else if isString(seq) then\n      do\n        // String search matches a substring (mirrors JS String.indexOf), which\n        // requires a string needle — reject non-strings rather than returning null.\n        if !(isString(value)) then\n          perform(\n            @dvala.error,\n            { message: str(\"Expected string, got: \", typeOf(value)) }\n          )\n        else\n          null\n        end;\n        let n = count(seq);\n        let m = count(value);\n        if m == 0 then\n          0\n        else\n          loop(i = 0) -> do\n            if i + m > n then\n              null\n            else if slice(seq, i, i + m) == value then\n              i\n            else\n              recur(i + 1)\n            end;\n          end\n        end;\n      end\n    else\n      do\n        // Array search: first element structurally equal to `value`.\n        let n = count(seq);\n        loop(i = 0) -> do\n          if i >= n then\n            null\n          else if nth(seq, i) == value then\n            i\n          else\n            recur(i + 1)\n          end;\n        end;\n      end\n    end;\n  end,\n};\n",
	functional: "{\n  \"|>\": (a, b) -> b(a),\n  apply: (fn, ...args) -> do\n    let arr = last(args);\n    let leading = slice(args, 0, count(args) - 1);\n    fn(...leading, ...arr);\n  end,\n  identity: (value) -> value,\n};\n",
	math: "{\n  sum: (vector) -> do\n    if !isVector(vector) then\n      perform(@dvala.error, \"Expected a vector, but got \" ++ str(vector))\n    else\n      null\n    end;\n    reduce(vector, +, 0);\n  end,\n  prod: (vector) -> do\n    if !isVector(vector) then\n      perform(@dvala.error, \"Expected a vector, but got \" ++ str(vector))\n    else\n      null\n    end;\n    reduce(vector, *, 1);\n  end,\n  cumsum: (vector) -> do\n    if !isVector(vector) then\n      perform(@dvala.error, \"Expected a vector, but got \" ++ str(vector))\n    else\n      null\n    end;\n    reduce(\n      vector,\n      (acc, val) -> do\n        let prev = if isEmpty(acc) then 0 else last(acc) end;\n        push(acc, prev + val);\n      end,\n      []\n    );\n  end,\n  cumprod: (vector) -> do\n    if !isVector(vector) then\n      perform(@dvala.error, \"Expected a vector, but got \" ++ str(vector))\n    else\n      null\n    end;\n    reduce(\n      vector,\n      (acc, val) -> do\n        let prev = if isEmpty(acc) then 1 else last(acc) end;\n        push(acc, prev * val);\n      end,\n      []\n    );\n  end,\n};\n",
	object: "{\n  mergeWith: (...args) -> do\n    let fn = last(args);\n    let objs = slice(args, 0, count(args) - 1);\n    if !(isFunction(fn)) then\n      perform(\n        @dvala.error,\n        { message: str(\"Expected function, got: \", typeOf(fn)) }\n      )\n    else\n      null\n    end;\n    reduce(\n      rest(objs),\n      (result, obj) -> do\n        if !(isObject(obj)) then\n          perform(\n            @dvala.error,\n            { message: str(\"Expected object, got: \", typeOf(obj)) }\n          )\n        else\n          null\n        end;\n        reduce(\n          keys(obj),\n          (res, key) -> do\n            if contains(res, key) then\n              assoc(res, key, fn(get(res, key), get(obj, key)))\n            else\n              assoc(res, key, get(obj, key))\n            end;\n          end,\n          result\n        );\n      end,\n      do\n        let f = first(objs);\n        if !(isObject(f)) then\n          perform(\n            @dvala.error,\n            { message: str(\"Expected object, got: \", typeOf(f)) }\n          )\n        else\n          null\n        end;\n        f;\n      end\n    );\n  end,\n  find: (obj, key) -> do\n    if !(isObject(obj)) then\n      perform(\n        @dvala.error,\n        { message: str(\"Expected object, got: \", typeOf(obj)) }\n      )\n    else\n      null\n    end;\n    if !(isString(key)) then\n      perform(\n        @dvala.error,\n        { message: str(\"Expected string, got: \", typeOf(key)) }\n      )\n    else\n      null\n    end;\n    if contains(obj, key) then [key, get(obj, key)] else null end;\n  end,\n  merge: (...objs) -> do\n    if count(objs) == 0 then\n      null\n    else\n      do\n        let f = first(objs);\n        if !(isObject(f)) then\n          perform(\n            @dvala.error,\n            { message: str(\"Expected object, got: \", typeOf(f)) }\n          )\n        else\n          null\n        end;\n        reduce(\n          rest(objs),\n          (result, obj) -> do\n            if !(isObject(obj)) then\n              perform(\n                @dvala.error,\n                { message: str(\"Expected object, got: \", typeOf(obj)) }\n              )\n            else\n              null\n            end;\n            reduce(\n              keys(obj),\n              (res, key) -> assoc(res, key, get(obj, key)),\n              result\n            );\n          end,\n          f\n        );\n      end\n    end;\n  end,\n  zipmap: (keyList, valueList) -> do\n    if !(isArray(keyList)) then\n      perform(\n        @dvala.error,\n        { message: str(\"Expected array, got: \", typeOf(keyList)) }\n      )\n    else\n      null\n    end;\n    if !(isArray(valueList)) then\n      perform(\n        @dvala.error,\n        { message: str(\"Expected array, got: \", typeOf(valueList)) }\n      )\n    else\n      null\n    end;\n    // assertStringArray: every key must be a string (the whole list, not just used keys).\n    let keyCount = count(keyList);\n    loop(i = 0) -> do\n      if i >= keyCount then\n        null\n      else if isString(nth(keyList, i)) then\n        recur(i + 1)\n      else\n        perform(\n          @dvala.error,\n          {\n            message: str(\"Expected string key, got: \", typeOf(nth(keyList, i))),\n          }\n        )\n      end;\n    end;\n    let n = min(keyCount, count(valueList));\n    loop(i = 0, acc = {}) -> do\n      if i >= n then\n        acc\n      else\n        recur(i + 1, assoc(acc, nth(keyList, i), nth(valueList, i)))\n      end;\n    end;\n  end,\n  selectKeys: (obj, keyList) -> do\n    if !(isArray(keyList)) then\n      perform(\n        @dvala.error,\n        { message: str(\"Expected array, got: \", typeOf(keyList)) }\n      )\n    else\n      null\n    end;\n    let keyCount = count(keyList);\n    loop(i = 0) -> do\n      if i >= keyCount then\n        null\n      else if isString(nth(keyList, i)) then\n        recur(i + 1)\n      else\n        perform(\n          @dvala.error,\n          {\n            message: str(\"Expected string key, got: \", typeOf(nth(keyList, i))),\n          }\n        )\n      end;\n    end;\n    if !(isObject(obj)) then\n      perform(\n        @dvala.error,\n        { message: str(\"Expected object, got: \", typeOf(obj)) }\n      )\n    else\n      null\n    end;\n    reduce(\n      keyList,\n      (acc, key) -> if contains(obj, key) then\n        assoc(acc, key, get(obj, key))\n      else\n        acc\n      end,\n      {}\n    );\n  end,\n};\n",
	predicates: "// Core predicate builtins implemented in Dvala.\n//\n// First slice of the \"minimize KMP runtime surface\" migration: these\n// predicates are pure compositions of already-native primitives (arithmetic,\n// comparison, `count`, `isNull`, `isInteger`), so moving them out of TS means a\n// second runtime (KMP) inherits them for free once it can evaluate this AST.\n//\n// docs + arity stay in predicates.ts (mirrors collection.dvala / sequence.dvala);\n// the TS `evaluate` there is a throw-stub fallback.\n//\n// Behaviour is kept identical to the former native implementations, including\n// the \"throw on non-finite / non-number\" contract:\n//   - `abs` and `%` reject non-numbers (RuntimeError) and non-finite results\n//     (ArithmeticError) — both are DvalaError subclasses — so they reproduce the\n//     native `assertNumber(x, { finite: true })` guard.\n//   - `isZero` keeps the EPSILON tolerance (1e-10, mirrors EPSILON in utils.ts).\n//\n// `isInteger` deliberately stays native: its `Infinity`/`NaN` -> `false`\n// (no-throw) behaviour can't be expressed here, since `floor`/`%` throw on those.\n{\n  isZero: (x) -> abs(x) < 1e-10,\n  isPos: (x) -> do\n    abs(x); // force the finite-number check; matches native assertNumber(finite)\n    x > 0;\n  end,\n  isNeg: (x) -> do\n    abs(x); // force the finite-number check; matches native assertNumber(finite)\n    x < 0;\n  end,\n  isEven: (x) -> (x % 2) == 0,\n  isOdd: (x) -> ((x % 2) != 0) && isInteger(x),\n  isEmpty: (x) -> isNull(x) || (count(x) == 0),\n  isNotEmpty: (x) -> !(isNull(x)) && (count(x) > 0),\n  isTrue: (x) -> x == true,\n  isFalse: (x) -> x == false,\n  isIncreasing: (vector) -> do\n    if !isVector(vector) then\n      perform(@dvala.error, \"Expected a vector, but got \" ++ str(vector))\n    else\n      null\n    end;\n    let n = count(vector);\n    loop(i = 1) -> do\n      if i >= n then\n        true\n      else if nth(vector, i) >= nth(vector, i - 1) then\n        recur(i + 1)\n      else\n        false\n      end;\n    end;\n  end,\n  isDecreasing: (vector) -> do\n    if !isVector(vector) then\n      perform(@dvala.error, \"Expected a vector, but got \" ++ str(vector))\n    else\n      null\n    end;\n    let n = count(vector);\n    loop(i = 1) -> do\n      if i >= n then\n        true\n      else if nth(vector, i) <= nth(vector, i - 1) then\n        recur(i + 1)\n      else\n        false\n      end;\n    end;\n  end,\n  isStrictlyIncreasing: (vector) -> do\n    if !isVector(vector) then\n      perform(@dvala.error, \"Expected a vector, but got \" ++ str(vector))\n    else\n      null\n    end;\n    let n = count(vector);\n    loop(i = 1) -> do\n      if i >= n then\n        true\n      else if nth(vector, i) > nth(vector, i - 1) then\n        recur(i + 1)\n      else\n        false\n      end;\n    end;\n  end,\n  isStrictlyDecreasing: (vector) -> do\n    if !isVector(vector) then\n      perform(@dvala.error, \"Expected a vector, but got \" ++ str(vector))\n    else\n      null\n    end;\n    let n = count(vector);\n    loop(i = 1) -> do\n      if i >= n then\n        true\n      else if nth(vector, i) < nth(vector, i - 1) then\n        recur(i + 1)\n      else\n        false\n      end;\n    end;\n  end,\n  isMonotonic: (vector) -> isIncreasing(vector) || isDecreasing(vector),\n  isStrictlyMonotonic: (vector) -> isStrictlyIncreasing(vector) ||\n    isStrictlyDecreasing(vector),\n};\n"
};
/** Repo-relative path each core source resolves to, for coverage attribution. */
function coreSourcePath(name) {
	return `packages/dvala-engine/src/builtin/core/${name}.dvala`;
}
/**
* Parse + register the core `.dvala` builtins onto the normalExpressions registry.
*
* When `debug` is set, returns a merged source map covering every core builtin
* body (sources offset so a single map spans all files). The host seeds this into
* its accumulated source map so coverage can resolve builtin nodeIds back to their
* `.dvala` source. Returns `undefined` when debug is off (no source maps built).
*/
function initCoreDvalaSources(parseSource, options = {}) {
	const { debug = false, allocateNodeId, recordBuiltinNode } = options;
	let builtinSourceMap;
	for (const [name, source] of Object.entries(coreDvalaSources)) {
		const ast = parseSource(source, {
			debug,
			filePath: debug ? coreSourcePath(name) : void 0,
			allocateNodeId
		});
		if (ast.sourceMap) if (!builtinSourceMap) builtinSourceMap = {
			sources: [...ast.sourceMap.sources],
			positions: new Map(ast.sourceMap.positions)
		};
		else {
			const sourceOffset = builtinSourceMap.sources.length;
			builtinSourceMap.sources.push(...ast.sourceMap.sources);
			for (const [nodeId, pos] of ast.sourceMap.positions) builtinSourceMap.positions.set(nodeId, {
				...pos,
				source: pos.source + sourceOffset
			});
		}
		const result = evaluate(ast, createContextStack(), recordBuiltinNode && ast.sourceMap ? (node) => {
			const pos = ast.sourceMap.positions.get(node[2]);
			if (!pos || pos.structuralLeaf) return;
			const src = ast.sourceMap.sources[pos.source];
			if (src) recordBuiltinNode(node[2], src.path, pos.start, pos.end);
		} : void 0);
		if (result instanceof Promise) throw new TypeError("Core dvala sources must be synchronous");
		if (!isObj(result)) continue;
		for (const [fnName, fn] of result) {
			const expression = normalExpressions[fnName];
			if (expression && !expression.dvalaImpl && isDvalaFunction(fn) && fn.functionType === "UserDefined") expression.dvalaImpl = fn;
		}
	}
	return builtinSourceMap;
}
//#endregion
//#region src/builtin/modules/interface.ts
const MODULE_DESCRIPTION_MAX_LENGTH = 120;
/**
* Derives a docs record from a BuiltinNormalExpressions map.
* Use this for modules whose docs are co-located inline on each function.
*/
function moduleDocsFromFunctions(functions) {
	return Object.fromEntries(Object.entries(functions).map(([k, v]) => {
		if (!v.docs) throw new Error(`Missing docs for function "${k}"`);
		return [k, v.docs];
	}));
}
//#endregion
//#region src/builtin/modules/assertion/assertion.dvala
var assertion_default = "{\n  assertFails: (fn, ...args) -> do\n    let message = first(args);\n    assert(isFunction(fn), \"First argument must be a function\");\n    if !(isNull(message)) then\n      assert(isString(message), \"Second argument must be a string\")\n    else\n      null\n    end;\n    let failedSentinel = @assertion.failed;\n    let errorHandler =\n      handler\n        @dvala.error(err) -> [failedSentinel, err]\n      end;\n    let result = errorHandler(-> fn());\n    if isArray(result) && first(result) == failedSentinel then\n      null\n    else\n      assert(false, message ?? \"Expected function to fail\")\n    end;\n  end,\n  assertFailsWith: (fn, expectedError, ...args) -> do\n    let message = first(args);\n    assert(isFunction(fn), \"First argument must be a function\");\n    assert(isString(expectedError), \"Second argument must be a string\");\n    if !(isNull(message)) then\n      assert(isString(message), \"Third argument must be a string\")\n    else\n      null\n    end;\n    let failedSentinel = @assertion.failed;\n    let errorHandler =\n      handler\n        @dvala.error(err) -> [failedSentinel, err]\n      end;\n    let result = errorHandler(-> fn());\n    if isArray(result) && first(result) == failedSentinel then\n      do\n        let actualError = second(result).message;\n        if actualError == expectedError then\n          null\n        else\n          assert(\n            false,\n            message ??\n              \"Expected function to fail with \\\"\" ++ expectedError ++\n                \"\\\" but got \\\"\" ++\n                actualError ++\n                \"\\\"\"\n          )\n        end;\n      end\n    else\n      assert(\n        false,\n        message ?? \"Expected function to fail with \\\"\" ++ expectedError ++ \"\\\"\"\n      )\n    end;\n  end,\n  assertSucceeds: (fn, ...args) -> do\n    let message = first(args);\n    assert(isFunction(fn), \"First argument must be a function\");\n    if !(isNull(message)) then\n      assert(isString(message), \"Second argument must be a string\")\n    else\n      null\n    end;\n    let failedSentinel = @assertion.failed;\n    let errorHandler =\n      handler\n        @dvala.error(err) -> failedSentinel\n      end;\n    let result = errorHandler(-> fn());\n    if isEffect(result) && result == failedSentinel then\n      assert(false, message ?? \"Expected function to succeed\")\n    else\n      null\n    end;\n  end,\n};\n";
//#endregion
//#region src/builtin/modules/assertion/docs.ts
const moduleDocs$6 = {
	assertNotEqual: {
		type: "(Unknown, Unknown) -> Null",
		category: "assertion",
		description: "If `a` is the same as `b` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			a: { type: "any" },
			b: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["a", "b"] }, { argumentNames: [
			"a",
			"b",
			"message"
		] }],
		examples: [
			"let { assertNotEqual } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertNotEqual(0, 0, \"Expected different values\") end",
			"let { assertNotEqual } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertNotEqual(0, 0) end",
			"let { assertNotEqual } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; 0 assertNotEqual 0 end",
			"let { assertNotEqual } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertNotEqual(0, 1) end"
		],
		seeAlso: ["assertion.assertEqual"],
		hideOperatorForm: true
	},
	assertEqual: {
		type: "(Unknown, Unknown) -> Null",
		category: "assertion",
		description: "If `a` is not structural equal to `b` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			a: { type: "any" },
			b: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["a", "b"] }, { argumentNames: [
			"a",
			"b",
			"message"
		] }],
		examples: [
			"let { assertEqual } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertEqual({ \"a\": 1 }, { \"a\": 2 }, \"Expected equal values\") end",
			"let { assertEqual } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertEqual({ \"a\": 1 }, { \"a\": 2 }) end",
			"let { assertEqual } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertEqual({ \"a\": 1 }, { \"a\": 1 }) end"
		],
		seeAlso: ["assertion.assertNotEqual"],
		hideOperatorForm: true
	},
	assertGt: {
		type: "((String | Number), (String | Number)) -> Null",
		category: "assertion",
		description: "If `a` is not greater than `b` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			a: { type: "any" },
			b: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["a", "b"] }, { argumentNames: [
			"a",
			"b",
			"message"
		] }],
		examples: [
			"let { assertGt } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertGt(0, 1, \"Expected greater value\") end",
			"let { assertGt } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertGt(0, 0) end",
			"let { assertGt } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertGt(1, 0) end"
		],
		seeAlso: [
			"assertion.assertLt",
			"assertion.assertGte",
			"assertion.assertLte"
		],
		hideOperatorForm: true
	},
	assertLt: {
		type: "((String | Number), (String | Number)) -> Null",
		category: "assertion",
		description: "If `a` is not less than `b` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			a: { type: "any" },
			b: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["a", "b"] }, { argumentNames: [
			"a",
			"b",
			"message"
		] }],
		examples: [
			"let { assertLt } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertLt(1, 0, \"Expected smaller value value\") end",
			"let { assertLt } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertLt(1, 1) end",
			"let { assertLt } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertLt(0, 1) end"
		],
		seeAlso: [
			"assertion.assertGt",
			"assertion.assertLte",
			"assertion.assertGte"
		],
		hideOperatorForm: true
	},
	assertGte: {
		type: "((String | Number), (String | Number)) -> Null",
		category: "assertion",
		description: "If `a` is less than `b` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			a: { type: "any" },
			b: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["a", "b"] }, { argumentNames: [
			"a",
			"b",
			"message"
		] }],
		examples: [
			"let { assertGte } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertGte(0, 1, \"Expected greater value\") end",
			"let { assertGte } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertGte(0, 1) end",
			"let { assertGte } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertGte(1, 1) end"
		],
		seeAlso: [
			"assertion.assertLte",
			"assertion.assertGt",
			"assertion.assertLt"
		],
		hideOperatorForm: true
	},
	assertLte: {
		type: "((String | Number), (String | Number)) -> Null",
		category: "assertion",
		description: "If `a` is grater than `b` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			a: { type: "any" },
			b: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["a", "b"] }, { argumentNames: [
			"a",
			"b",
			"message"
		] }],
		examples: [
			"let { assertLte } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertLte(1, 0, \"Expected smaller value value\") end",
			"let { assertLte } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertLte(1, 0) end",
			"let { assertLte } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertLte(1, 1) end"
		],
		seeAlso: [
			"assertion.assertGte",
			"assertion.assertLt",
			"assertion.assertGt"
		],
		hideOperatorForm: true
	},
	assertTrue: {
		type: "(Unknown) -> Null",
		category: "assertion",
		description: "If `value` is not `true` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			value: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["value"] }, { argumentNames: ["value", "message"] }],
		examples: [
			"let { assertTrue } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertTrue(false, \"Expected true\") end",
			"let { assertTrue } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertTrue(false) end",
			"let { assertTrue } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertTrue(true) end"
		],
		seeAlso: [
			"assertion.assertFalse",
			"assertion.assertTruthy",
			"assertion.assertFalsy",
			"assert",
			"assertion.assertBoolean"
		],
		hideOperatorForm: true
	},
	assertFalse: {
		type: "(Unknown) -> Null",
		category: "assertion",
		description: "If `value` is not `false` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			value: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["value"] }, { argumentNames: ["value", "message"] }],
		examples: [
			"let { assertFalse } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertFalse(true, \"Expected false\") end",
			"let { assertFalse } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertFalse(true) end",
			"let { assertFalse } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertFalse(false) end"
		],
		seeAlso: [
			"assertion.assertTrue",
			"assertion.assertFalsy",
			"assertion.assertTruthy",
			"assertion.assertBoolean"
		],
		hideOperatorForm: true
	},
	assertTruthy: {
		type: "(Unknown) -> Null",
		category: "assertion",
		description: "If `value` is not `truthy` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			value: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["value"] }, { argumentNames: ["value", "message"] }],
		examples: [
			"let { assertTruthy } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertTruthy(false, \"Expected truthy\") end",
			"let { assertTruthy } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertTruthy(false) end",
			"let { assertTruthy } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertTruthy(0) end",
			"let { assertTruthy } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertTruthy(null) end",
			"let { assertTruthy } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertTruthy(\"\") end",
			"let { assertTruthy } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertTruthy(true) end",
			"let { assertTruthy } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertTruthy(1) end",
			"let { assertTruthy } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertTruthy(\"x\") end",
			"let { assertTruthy } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertTruthy([]) end",
			"let { assertTruthy } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertTruthy(nd) end"
		],
		seeAlso: [
			"assertion.assertFalsy",
			"assertion.assertTrue",
			"assertion.assertFalse",
			"assert",
			"assertion.assertNull"
		],
		hideOperatorForm: true
	},
	assertFalsy: {
		type: "(Unknown) -> Null",
		category: "assertion",
		description: "If `value` is not `falsy` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			value: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["value"] }, { argumentNames: ["value", "message"] }],
		examples: [
			"let { assertFalsy } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertFalsy(true, \"Expected falsy\") end",
			"let { assertFalsy } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertFalsy(\"x\") end",
			"let { assertFalsy } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertFalsy([]) end",
			"let { assertFalsy } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertFalsy(nd) end",
			"let { assertFalsy } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertFalsy(1) end",
			"let { assertFalsy } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertFalsy(false) end",
			"let { assertFalsy } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertFalsy(0) end",
			"let { assertFalsy } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertFalsy(null) end",
			"let { assertFalsy } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertFalsy(\"\") end"
		],
		seeAlso: [
			"assertion.assertTruthy",
			"assertion.assertFalse",
			"assertion.assertTrue",
			"assertion.assertNull"
		],
		hideOperatorForm: true
	},
	assertNull: {
		type: "(Unknown) -> Null",
		category: "assertion",
		description: "If `value` is not `null` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			value: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["value"] }, { argumentNames: ["value", "message"] }],
		examples: [
			"let { assertNull } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertNull(null) end",
			"let { assertNull } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertNull(true, \"Expected null\") end",
			"let { assertNull } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertNull(\"x\") end",
			"let { assertNull } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertNull([]) end",
			"let { assertNull } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertNull(nd) end",
			"let { assertNull } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertNull(1) end",
			"let { assertNull } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertNull(false) end",
			"let { assertNull } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertNull(0) end",
			"let { assertNull } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertNull(\"\") end"
		],
		seeAlso: ["assertion.assertTruthy", "assertion.assertFalsy"],
		hideOperatorForm: true
	},
	assertFails: {
		type: "((() -> Unknown)) -> Null",
		category: "assertion",
		description: "If `fun` does not fail (perform `dvala.error`), it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			fun: { type: "function" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["fun"] }, { argumentNames: ["fun", "message"] }],
		examples: ["let { assertFails } = import(\"assertion\");\nassertFails(-> perform(@dvala.error, \"Error\"))", "let { assertFails } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertFails(-> identity(\"Error\")) end"],
		seeAlso: ["assertion.assertFailsWith", "assertion.assertSucceeds"],
		hideOperatorForm: true
	},
	assertFailsWith: {
		type: "((() -> Unknown), String) -> Null",
		category: "assertion",
		description: "If `fun` does not fail with `error-message`, it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			fun: { type: "function" },
			errorMessage: { type: "string" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["fun", "errorMessage"] }, { argumentNames: [
			"fun",
			"errorMessage",
			"message"
		] }],
		examples: ["let { assertFailsWith } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertFailsWith(-> perform(@dvala.error, \"Error\"), \"Error\") end", "let { assertFailsWith } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertFailsWith(-> identity(\"Error\"), \"Error\") end"],
		seeAlso: ["assertion.assertFails", "assertion.assertSucceeds"],
		hideOperatorForm: true
	},
	assertSucceeds: {
		type: "((() -> Unknown)) -> Null",
		category: "assertion",
		description: "If `fun` fails (performs `dvala.error`), it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			fun: { type: "function" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["fun"] }, { argumentNames: ["fun", "message"] }],
		examples: ["let { assertSucceeds } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertSucceeds(-> identity(\"OK\")) end", "let { assertSucceeds } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertSucceeds(-> perform(@dvala.error, \"Error\")) end"],
		seeAlso: ["assertion.assertFails", "assertion.assertFailsWith"],
		hideOperatorForm: true
	},
	assertArray: {
		type: "(Unknown) -> Null",
		category: "assertion",
		description: "If `value` is not an `array` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			value: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["value"] }, { argumentNames: ["value", "message"] }],
		examples: [
			"let { assertArray } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertArray([1, 2, 3]) end",
			"let { assertArray } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertArray(\"string\") end",
			"let { assertArray } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertArray(42, \"Expected an array\") end"
		],
		seeAlso: [
			"assertion.assertObject",
			"assertion.assertCollection",
			"assertion.assertSequence"
		],
		hideOperatorForm: true
	},
	assertBoolean: {
		type: "(Unknown) -> Null",
		category: "assertion",
		description: "If `value` is not a `boolean` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			value: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["value"] }, { argumentNames: ["value", "message"] }],
		examples: [
			"let { assertBoolean } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertBoolean(true) end",
			"let { assertBoolean } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertBoolean(false) end",
			"let { assertBoolean } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertBoolean(1, \"Expected a boolean\") end"
		],
		seeAlso: [
			"assertion.assertTrue",
			"assertion.assertFalse",
			"assertion.assertNumber",
			"assertion.assertString"
		],
		hideOperatorForm: true
	},
	assertCollection: {
		type: "(Unknown) -> Null",
		category: "assertion",
		description: "If `value` is not a `collection` (array, object, or string) it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			value: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["value"] }, { argumentNames: ["value", "message"] }],
		examples: [
			"let { assertCollection } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertCollection([1, 2]) end",
			"let { assertCollection } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertCollection({ a: 1 }) end",
			"let { assertCollection } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertCollection(\"hello\") end",
			"let { assertCollection } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertCollection(42, \"Expected a collection\") end"
		],
		seeAlso: [
			"assertion.assertSequence",
			"assertion.assertArray",
			"assertion.assertObject"
		],
		hideOperatorForm: true
	},
	assertFunction: {
		type: "(Unknown) -> Null",
		category: "assertion",
		description: "If `value` is not a `function` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			value: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["value"] }, { argumentNames: ["value", "message"] }],
		examples: ["let { assertFunction } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertFunction(-> $ + 1) end", "let { assertFunction } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertFunction(42, \"Expected a function\") end"],
		seeAlso: ["assertion.assertNumber", "assertion.assertString"],
		hideOperatorForm: true
	},
	assertGrid: {
		type: "(Unknown) -> Null",
		category: "assertion",
		description: "If `value` is not a `grid` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			value: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["value"] }, { argumentNames: ["value", "message"] }],
		examples: ["let { assertGrid } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertGrid([[1, 2], [3, 4]]) end", "let { assertGrid } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertGrid([1, 2], \"Expected a grid\") end"],
		seeAlso: ["assertion.assertMatrix", "assertion.assertVector"],
		hideOperatorForm: true
	},
	assertInteger: {
		type: "(Unknown) -> Null",
		category: "assertion",
		description: "If `value` is not an `integer` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			value: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["value"] }, { argumentNames: ["value", "message"] }],
		examples: ["let { assertInteger } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertInteger(42) end", "let { assertInteger } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertInteger(3.14, \"Expected an integer\") end"],
		seeAlso: ["assertion.assertNumber"],
		hideOperatorForm: true
	},
	assertMatrix: {
		type: "(Unknown) -> Null",
		category: "assertion",
		description: "If `value` is not a `matrix` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			value: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["value"] }, { argumentNames: ["value", "message"] }],
		examples: ["let { assertMatrix } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertMatrix([[1, 2], [3, 4]]) end", "let { assertMatrix } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertMatrix([1, 2], \"Expected a matrix\") end"],
		seeAlso: ["assertion.assertVector", "assertion.assertGrid"],
		hideOperatorForm: true
	},
	assertNumber: {
		type: "(Unknown) -> Null",
		category: "assertion",
		description: "If `value` is not a `number` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			value: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["value"] }, { argumentNames: ["value", "message"] }],
		examples: ["let { assertNumber } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertNumber(42) end", "let { assertNumber } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertNumber(\"hello\", \"Expected a number\") end"],
		seeAlso: [
			"assertion.assertInteger",
			"assertion.assertBoolean",
			"assertion.assertString",
			"assertion.assertFunction"
		],
		hideOperatorForm: true
	},
	assertObject: {
		type: "(Unknown) -> Null",
		category: "assertion",
		description: "If `value` is not an `object` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			value: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["value"] }, { argumentNames: ["value", "message"] }],
		examples: ["let { assertObject } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertObject({ a: 1 }) end", "let { assertObject } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertObject([1, 2], \"Expected an object\") end"],
		seeAlso: ["assertion.assertArray", "assertion.assertCollection"],
		hideOperatorForm: true
	},
	assertRegexp: {
		type: "(Unknown) -> Null",
		category: "assertion",
		description: "If `value` is not a `regexp` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			value: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["value"] }, { argumentNames: ["value", "message"] }],
		examples: ["let { assertRegexp } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertRegexp(#\"^start\") end", "let { assertRegexp } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertRegexp(\"hello\", \"Expected a regexp\") end"],
		seeAlso: ["assertion.assertString"],
		hideOperatorForm: true
	},
	assertSequence: {
		type: "(Unknown) -> Null",
		category: "assertion",
		description: "If `value` is not a `sequence` (array or string) it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			value: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["value"] }, { argumentNames: ["value", "message"] }],
		examples: [
			"let { assertSequence } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertSequence([1, 2]) end",
			"let { assertSequence } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertSequence(\"hello\") end",
			"let { assertSequence } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertSequence({ a: 1 }, \"Expected a sequence\") end"
		],
		seeAlso: ["assertion.assertCollection", "assertion.assertArray"],
		hideOperatorForm: true
	},
	assertString: {
		type: "(Unknown) -> Null",
		category: "assertion",
		description: "If `value` is not a `string` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			value: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["value"] }, { argumentNames: ["value", "message"] }],
		examples: ["let { assertString } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertString(\"hello\") end", "let { assertString } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertString(42, \"Expected a string\") end"],
		seeAlso: [
			"assertion.assertNumber",
			"assertion.assertBoolean",
			"assertion.assertRegexp",
			"assertion.assertFunction"
		],
		hideOperatorForm: true
	},
	assertVector: {
		type: "(Unknown) -> Null",
		category: "assertion",
		description: "If `value` is not a `vector` it throws `AssertionError`.",
		returns: { type: "null" },
		args: {
			value: { type: "any" },
			message: { type: "string" }
		},
		variants: [{ argumentNames: ["value"] }, { argumentNames: ["value", "message"] }],
		examples: ["let { assertVector } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertVector([1, 2, 3]) end", "let { assertVector } = import(\"assertion\");\ndo with handler @dvala.error(arg) -> resume(arg) end; assertVector([\"a\", \"b\"], \"Expected a vector\") end"],
		seeAlso: ["assertion.assertMatrix", "assertion.assertGrid"],
		hideOperatorForm: true
	}
};
//#endregion
//#region src/builtin/modules/assertion/index.ts
const assertNormalExpression = {
	assertEqual: {
		evaluate: ([first, second, message], sourceCodeInfo) => {
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (!deepEqual(asAny(first, sourceCodeInfo), asAny(second, sourceCodeInfo), sourceCodeInfo)) throw new AssertionError(`Expected ${JSON.stringify(first, null, 2)} to deep equal ${JSON.stringify(second, null, 2)}.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 2,
			max: 3
		}
	},
	assertNotEqual: {
		evaluate: ([first, second, message], sourceCodeInfo) => {
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (deepEqual(asAny(first, sourceCodeInfo), asAny(second, sourceCodeInfo), sourceCodeInfo)) throw new AssertionError(`Expected ${JSON.stringify(first)} not to deep equal ${JSON.stringify(second)}.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 2,
			max: 3
		}
	},
	assertGt: {
		evaluate: ([first, second, message], sourceCodeInfo) => {
			assertStringOrNumber(first, sourceCodeInfo);
			assertStringOrNumber(second, sourceCodeInfo);
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (compare(first, second, sourceCodeInfo) <= 0) throw new AssertionError(`Expected ${first} to be grater than ${second}.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 2,
			max: 3
		}
	},
	assertGte: {
		evaluate: ([first, second, message], sourceCodeInfo) => {
			assertStringOrNumber(first, sourceCodeInfo);
			assertStringOrNumber(second, sourceCodeInfo);
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (compare(first, second, sourceCodeInfo) < 0) throw new AssertionError(`Expected ${first} to be grater than or equal to ${second}.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 2,
			max: 3
		}
	},
	assertLt: {
		evaluate: ([first, second, message], sourceCodeInfo) => {
			assertStringOrNumber(first, sourceCodeInfo);
			assertStringOrNumber(second, sourceCodeInfo);
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (compare(first, second, sourceCodeInfo) >= 0) throw new AssertionError(`Expected ${first} to be less than ${second}.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 2,
			max: 3
		}
	},
	assertLte: {
		evaluate: ([first, second, message], sourceCodeInfo) => {
			assertStringOrNumber(first, sourceCodeInfo);
			assertStringOrNumber(second, sourceCodeInfo);
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (compare(first, second, sourceCodeInfo) > 0) throw new AssertionError(`Expected ${first} to be less than or equal to ${second}.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 2,
			max: 3
		}
	},
	assertTrue: {
		evaluate: ([first, message], sourceCodeInfo) => {
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (first !== true) throw new AssertionError(`Expected ${first} to be true.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 1,
			max: 2
		}
	},
	assertFalse: {
		evaluate: ([first, message], sourceCodeInfo) => {
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (first !== false) throw new AssertionError(`Expected ${first} to be false.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 1,
			max: 2
		}
	},
	assertTruthy: {
		evaluate: ([first, message], sourceCodeInfo) => {
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (!first) throw new AssertionError(`Expected ${first} to be truthy.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 1,
			max: 2
		}
	},
	assertFalsy: {
		evaluate: ([first, message], sourceCodeInfo) => {
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (first) throw new AssertionError(`Expected ${first} to be falsy.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 1,
			max: 2
		}
	},
	assertNull: {
		evaluate: ([first, message], sourceCodeInfo) => {
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (first !== null) throw new AssertionError(`Expected ${first} to be null.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 1,
			max: 2
		}
	},
	assertFails: {
		/* v8 ignore next 1 */
		evaluate: () => null,
		arity: {
			min: 1,
			max: 2
		}
	},
	assertFailsWith: {
		/* v8 ignore next 1 */
		evaluate: () => null,
		arity: {
			min: 2,
			max: 3
		}
	},
	assertSucceeds: {
		/* v8 ignore next 1 */
		evaluate: () => null,
		arity: {
			min: 1,
			max: 2
		}
	},
	assertArray: {
		evaluate: ([first, message], sourceCodeInfo) => {
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (!isPersistentVector(first)) throw new AssertionError(`Expected ${JSON.stringify(first)} to be an array.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 1,
			max: 2
		}
	},
	assertBoolean: {
		evaluate: ([first, message], sourceCodeInfo) => {
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (typeof first !== "boolean") throw new AssertionError(`Expected ${JSON.stringify(first)} to be a boolean.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 1,
			max: 2
		}
	},
	assertCollection: {
		evaluate: ([first, message], sourceCodeInfo) => {
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (!isColl(first)) throw new AssertionError(`Expected ${JSON.stringify(first)} to be a collection.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 1,
			max: 2
		}
	},
	assertFunction: {
		evaluate: ([first, message], sourceCodeInfo) => {
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (!isDvalaFunction(first)) throw new AssertionError(`Expected ${JSON.stringify(first)} to be a function.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 1,
			max: 2
		}
	},
	assertGrid: {
		evaluate: ([first, message], sourceCodeInfo) => {
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (!isGrid(first)) throw new AssertionError(`Expected ${JSON.stringify(first)} to be a grid.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 1,
			max: 2
		}
	},
	assertInteger: {
		evaluate: ([first, message], sourceCodeInfo) => {
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (typeof first !== "number" || !isNumber(first, { integer: true })) throw new AssertionError(`Expected ${JSON.stringify(first)} to be an integer.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 1,
			max: 2
		}
	},
	assertMatrix: {
		evaluate: ([first, message], sourceCodeInfo) => {
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (!isMatrix(first)) throw new AssertionError(`Expected ${JSON.stringify(first)} to be a matrix.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 1,
			max: 2
		}
	},
	assertNumber: {
		evaluate: ([first, message], sourceCodeInfo) => {
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (typeof first !== "number") throw new AssertionError(`Expected ${JSON.stringify(first)} to be a number.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 1,
			max: 2
		}
	},
	assertObject: {
		evaluate: ([first, message], sourceCodeInfo) => {
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (!isObj(first)) throw new AssertionError(`Expected ${JSON.stringify(first)} to be an object.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 1,
			max: 2
		}
	},
	assertRegexp: {
		evaluate: ([first, message], sourceCodeInfo) => {
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (!isRegularExpression(first)) throw new AssertionError(`Expected ${JSON.stringify(first)} to be a regexp.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 1,
			max: 2
		}
	},
	assertSequence: {
		evaluate: ([first, message], sourceCodeInfo) => {
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (!isSeq(first)) throw new AssertionError(`Expected ${JSON.stringify(first)} to be a sequence.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 1,
			max: 2
		}
	},
	assertString: {
		evaluate: ([first, message], sourceCodeInfo) => {
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (typeof first !== "string") throw new AssertionError(`Expected ${JSON.stringify(first)} to be a string.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 1,
			max: 2
		}
	},
	assertVector: {
		evaluate: ([first, message], sourceCodeInfo) => {
			if (message !== void 0) {
				assertString(message, sourceCodeInfo);
				message = ` ${message}`;
			}
			message ??= "";
			if (!isVector(first)) throw new AssertionError(`Expected ${JSON.stringify(first)} to be a vector.${message}`, sourceCodeInfo);
			return null;
		},
		arity: {
			min: 1,
			max: 2
		}
	}
};
for (const [key, docs] of Object.entries(moduleDocs$6))
 /* v8 ignore next 2 */
if (assertNormalExpression[key]) assertNormalExpression[key].docs = docs;
const assertModule = {
	name: "assertion",
	description: "Runtime assertions with detailed error messages for testing and validation.",
	functions: assertNormalExpression,
	source: assertion_default,
	sourcePath: "packages/dvala-engine/src/builtin/modules/assertion/assertion.dvala",
	docs: moduleDocs$6
};
//#endregion
//#region src/builtin/modules/ast/index.ts
/**
* Get element i from a value that is either a plain array or a PersistentVector.
* AST nodes come in as PV because dispatch wraps plain-array returns via fromJS.
*/
function nodeElem(node, i) {
	if (isPersistentVector(node)) return node.get(i);
	if (Array.isArray(node)) return node[i];
}
/**
* Return the size of a PV or plain array.
*/
function nodeSize(node) {
	if (isPersistentVector(node)) return node.size;
	if (Array.isArray(node)) return node.length;
	return 0;
}
/** Assert that a value is an AST node (PV or plain array with string type tag). */
function assertAstNode(value, sourceCodeInfo) {
	if (!((isPersistentVector(value) || Array.isArray(value)) && nodeSize(value) >= 2 && typeof nodeElem(value, 0) === "string")) throw new TypeError$1("Expected an AST node [type, payload, nodeId]", sourceCodeInfo);
}
/**
* Check whether a value is an AST node (PV or array with known type tag).
* Used by predicates (isCall, isNum, etc.) that must not throw.
*/
function isAstNodeLike(node, tag) {
	return (isPersistentVector(node) || Array.isArray(node)) && nodeElem(node, 0) === tag;
}
function assertArray$1(value, sourceCodeInfo) {
	if (!isPersistentVector(value) && !Array.isArray(value)) throw new TypeError$1("Expected an array", sourceCodeInfo);
}
/**
* Convert an AST node (may be a PersistentVector after dispatch wrapping) back
* to a plain-array AstNode for prettyPrint, which expects plain JS arrays.
*/
function toPlainAstNode(node) {
	return toJS(node);
}
const astFunctions = {
	num: {
		evaluate: ([n], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo);
			return toAny([
				NodeTypes.Num,
				n,
				0
			]);
		},
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "array" },
			args: { n: { type: "number" } },
			variants: [{ argumentNames: ["n"] }],
			description: "Creates a number AST node.",
			examples: ["let { num } = import(\"ast\"); num(42)"]
		}
	},
	strNode: {
		evaluate: ([s], sourceCodeInfo) => {
			assertString(s, sourceCodeInfo);
			return toAny([
				NodeTypes.Str,
				s,
				0
			]);
		},
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "array" },
			args: { s: { type: "string" } },
			variants: [{ argumentNames: ["s"] }],
			description: "Creates a string AST node.",
			examples: ["let { strNode } = import(\"ast\"); strNode(\"hello\")"]
		}
	},
	bool: {
		evaluate: ([b], sourceCodeInfo) => {
			if (typeof b !== "boolean") throw new TypeError$1("Expected a boolean", sourceCodeInfo);
			return toAny([
				NodeTypes.Reserved,
				b ? "true" : "false",
				0
			]);
		},
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "array" },
			args: { b: { type: "boolean" } },
			variants: [{ argumentNames: ["b"] }],
			description: "Creates a boolean AST node.",
			examples: ["let { bool } = import(\"ast\"); bool(true)"]
		}
	},
	nil: {
		evaluate: () => {
			return toAny([
				NodeTypes.Reserved,
				"null",
				0
			]);
		},
		arity: toFixedArity(0),
		docs: {
			category: "ast",
			returns: { type: "array" },
			args: {},
			variants: [{ argumentNames: [] }],
			description: "Creates a null AST node.",
			examples: ["let { nil } = import(\"ast\"); nil()"]
		}
	},
	sym: {
		evaluate: ([name], sourceCodeInfo) => {
			assertString(name, sourceCodeInfo);
			return toAny([
				NodeTypes.Sym,
				name,
				0
			]);
		},
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "array" },
			args: { name: { type: "string" } },
			variants: [{ argumentNames: ["name"] }],
			description: "Creates a symbol (variable reference) AST node.",
			examples: ["let { sym } = import(\"ast\"); sym(\"x\")"]
		}
	},
	builtin: {
		evaluate: ([name], sourceCodeInfo) => {
			assertString(name, sourceCodeInfo);
			return toAny([
				NodeTypes.Builtin,
				name,
				0
			]);
		},
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "array" },
			args: { name: { type: "string" } },
			variants: [{ argumentNames: ["name"] }],
			description: "Creates a builtin function reference AST node.",
			examples: ["let { builtin } = import(\"ast\"); builtin(\"+\")"]
		}
	},
	effectNode: {
		evaluate: ([name], sourceCodeInfo) => {
			assertString(name, sourceCodeInfo);
			return toAny([
				NodeTypes.Effect,
				name,
				0
			]);
		},
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "array" },
			args: { name: { type: "string" } },
			variants: [{ argumentNames: ["name"] }],
			description: "Creates an effect reference AST node.",
			examples: ["let { effectNode } = import(\"ast\"); effectNode(\"dvala.io.print\")"]
		}
	},
	call: {
		evaluate: ([fn, args], sourceCodeInfo) => {
			assertAstNode(fn, sourceCodeInfo);
			assertArray$1(args, sourceCodeInfo);
			return toAny([
				NodeTypes.Call,
				[fn, args],
				0
			]);
		},
		arity: toFixedArity(2),
		docs: {
			category: "ast",
			returns: { type: "array" },
			args: {
				fn: {
					type: "array",
					description: "Function AST node."
				},
				args: {
					type: "array",
					description: "Array of argument AST nodes."
				}
			},
			variants: [{ argumentNames: ["fn", "args"] }],
			description: "Creates a function call AST node.",
			examples: ["let { call, builtin, num } = import(\"ast\"); call(builtin(\"+\"), [num(1), num(2)])"]
		}
	},
	ifNode: {
		evaluate: ([cond, then, else_], sourceCodeInfo) => {
			assertAstNode(cond, sourceCodeInfo);
			assertAstNode(then, sourceCodeInfo);
			if (else_ === null || else_ === void 0) throw new TypeError$1("ifNode requires an else branch", sourceCodeInfo);
			assertAstNode(else_, sourceCodeInfo);
			return toAny([
				NodeTypes.If,
				[
					cond,
					then,
					else_
				],
				0
			]);
		},
		arity: toFixedArity(3),
		docs: {
			category: "ast",
			returns: { type: "array" },
			args: {
				cond: {
					type: "array",
					description: "Condition AST node."
				},
				thenBranch: {
					type: "array",
					description: "Then-branch AST node."
				},
				elseBranch: {
					type: "array",
					description: "Else-branch AST node."
				}
			},
			variants: [{ argumentNames: [
				"cond",
				"thenBranch",
				"elseBranch"
			] }],
			description: "Creates an if-expression AST node.",
			examples: ["let { ifNode, sym, num } = import(\"ast\"); ifNode(sym(\"x\"), num(1), num(2))"]
		}
	},
	block: {
		evaluate: ([stmts], sourceCodeInfo) => {
			assertArray$1(stmts, sourceCodeInfo);
			if ((isPersistentVector(stmts) ? stmts.size : stmts.length) === 0) throw new TypeError$1("block requires at least one expression", sourceCodeInfo);
			return toAny([
				NodeTypes.Block,
				stmts,
				0
			]);
		},
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "array" },
			args: { stmts: {
				type: "array",
				description: "Array of statement AST nodes."
			} },
			variants: [{ argumentNames: ["stmts"] }],
			description: "Creates a block (do...end) AST node.",
			examples: ["let { block, num } = import(\"ast\"); block([num(1), num(2)])"]
		}
	},
	nodeType: {
		evaluate: ([node], sourceCodeInfo) => {
			assertAstNode(node, sourceCodeInfo);
			return nodeElem(node, 0);
		},
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "string" },
			args: { node: {
				type: "array",
				description: "An AST node."
			} },
			variants: [{ argumentNames: ["node"] }],
			description: "Returns the type tag of an AST node (e.g., \"Num\", \"Str\", \"Call\").",
			examples: ["let { nodeType, num } = import(\"ast\"); nodeType(num(42))", "let { nodeType, sym } = import(\"ast\"); nodeType(sym(\"x\"))"]
		}
	},
	isNum: {
		evaluate: ([node]) => isAstNodeLike(node, NodeTypes.Num),
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "boolean" },
			args: { node: { type: "any" } },
			variants: [{ argumentNames: ["node"] }],
			description: "Returns true if the AST node is a number literal.",
			examples: ["let { isNum, num } = import(\"ast\"); isNum(num(42))"]
		}
	},
	isStr: {
		evaluate: ([node]) => isAstNodeLike(node, NodeTypes.Str),
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "boolean" },
			args: { node: { type: "any" } },
			variants: [{ argumentNames: ["node"] }],
			description: "Returns true if the AST node is a string literal.",
			examples: ["let { isStr, strNode } = import(\"ast\"); isStr(strNode(\"hi\"))"]
		}
	},
	isSym: {
		evaluate: ([node]) => isAstNodeLike(node, NodeTypes.Sym),
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "boolean" },
			args: { node: { type: "any" } },
			variants: [{ argumentNames: ["node"] }],
			description: "Returns true if the AST node is a symbol (variable reference).",
			examples: ["let { isSym, sym } = import(\"ast\"); isSym(sym(\"x\"))"]
		}
	},
	isBuiltin: {
		evaluate: ([node]) => isAstNodeLike(node, NodeTypes.Builtin),
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "boolean" },
			args: { node: { type: "any" } },
			variants: [{ argumentNames: ["node"] }],
			description: "Returns true if the AST node is a builtin function reference.",
			examples: ["let { isBuiltin, builtin } = import(\"ast\"); isBuiltin(builtin(\"+\"))"]
		}
	},
	isCall: {
		evaluate: ([node]) => isAstNodeLike(node, NodeTypes.Call),
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "boolean" },
			args: { node: { type: "any" } },
			variants: [{ argumentNames: ["node"] }],
			description: "Returns true if the AST node is a function call.",
			examples: ["let { isCall, call, builtin, num } = import(\"ast\"); isCall(call(builtin(\"+\"), [num(1)]))"]
		}
	},
	isIf: {
		evaluate: ([node]) => isAstNodeLike(node, NodeTypes.If),
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "boolean" },
			args: { node: { type: "any" } },
			variants: [{ argumentNames: ["node"] }],
			description: "Returns true if the AST node is an if-expression.",
			examples: ["let { isIf, ifNode, sym, num } = import(\"ast\"); isIf(ifNode(sym(\"x\"), num(1), num(2)))"]
		}
	},
	isBlock: {
		evaluate: ([node]) => isAstNodeLike(node, NodeTypes.Block),
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "boolean" },
			args: { node: { type: "any" } },
			variants: [{ argumentNames: ["node"] }],
			description: "Returns true if the AST node is a block (do...end).",
			examples: ["let { isBlock, block, num } = import(\"ast\"); isBlock(block([num(1)]))"]
		}
	},
	isLet: {
		evaluate: ([node]) => isAstNodeLike(node, NodeTypes.Let),
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "boolean" },
			args: { node: { type: "any" } },
			variants: [{ argumentNames: ["node"] }],
			description: "Returns true if the AST node is a let-binding.",
			examples: ["let { isLet } = import(\"ast\"); isLet(quote let x = 1 end)"]
		}
	},
	isFn: {
		evaluate: ([node]) => isAstNodeLike(node, NodeTypes.Function),
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "boolean" },
			args: { node: { type: "any" } },
			variants: [{ argumentNames: ["node"] }],
			description: "Returns true if the AST node is a function definition.",
			examples: ["let { isFn } = import(\"ast\"); isFn(quote (x) -> x end)"]
		}
	},
	isBool: {
		evaluate: ([node]) => (isPersistentVector(node) || Array.isArray(node)) && nodeElem(node, 0) === NodeTypes.Reserved && (nodeElem(node, 1) === "true" || nodeElem(node, 1) === "false"),
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "boolean" },
			args: { node: { type: "any" } },
			variants: [{ argumentNames: ["node"] }],
			description: "Returns true if the AST node is a boolean literal.",
			examples: ["let { isBool, bool } = import(\"ast\"); isBool(bool(true))"]
		}
	},
	isNil: {
		evaluate: ([node]) => (isPersistentVector(node) || Array.isArray(node)) && nodeElem(node, 0) === NodeTypes.Reserved && nodeElem(node, 1) === "null",
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "boolean" },
			args: { node: { type: "any" } },
			variants: [{ argumentNames: ["node"] }],
			description: "Returns true if the AST node is a null literal.",
			examples: ["let { isNil, nil } = import(\"ast\"); isNil(nil())"]
		}
	},
	isEffectNode: {
		evaluate: ([node]) => isAstNodeLike(node, NodeTypes.Effect),
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "boolean" },
			args: { node: { type: "any" } },
			variants: [{ argumentNames: ["node"] }],
			description: "Returns true if the AST node is an effect reference.",
			examples: ["let { isEffectNode, effectNode } = import(\"ast\"); isEffectNode(effectNode(\"dvala.io.print\"))"]
		}
	},
	isAstNode: {
		evaluate: ([node]) => {
			return (isPersistentVector(node) || Array.isArray(node)) && nodeSize(node) >= 2 && typeof nodeElem(node, 0) === "string" && isNodeType(nodeElem(node, 0));
		},
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "boolean" },
			args: { node: { type: "any" } },
			variants: [{ argumentNames: ["node"] }],
			description: "Returns true if the value is a valid AST node (array starting with a known type tag).",
			examples: ["let { isAstNode, num } = import(\"ast\"); isAstNode(num(42))", "let { isAstNode } = import(\"ast\"); isAstNode([1, 2, 3])"]
		}
	},
	payload: {
		evaluate: ([node], sourceCodeInfo) => {
			assertAstNode(node, sourceCodeInfo);
			return nodeElem(node, 1);
		},
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "any" },
			args: { node: {
				type: "array",
				description: "An AST node."
			} },
			variants: [{ argumentNames: ["node"] }],
			description: "Returns the payload (second element) of an AST node.",
			examples: ["let { payload, num } = import(\"ast\"); payload(num(42))", "let { payload, strNode } = import(\"ast\"); payload(strNode(\"hello\"))"]
		}
	},
	prettyPrint: {
		evaluate: ([node], sourceCodeInfo, contextStack) => {
			assertAstNode(node, sourceCodeInfo);
			const prettyPrint = contextStack.prettyPrint;
			if (!prettyPrint) throw new TypeError$1("Host did not provide a prettyPrint capability", sourceCodeInfo);
			return prettyPrint(toPlainAstNode(node));
		},
		arity: toFixedArity(1),
		docs: {
			category: "ast",
			returns: { type: "string" },
			args: { node: {
				type: "array",
				description: "An AST node."
			} },
			variants: [{ argumentNames: ["node"] }],
			description: "Converts an AST node back to readable Dvala source code.",
			examples: [
				"let { prettyPrint, num } = import(\"ast\"); prettyPrint(num(42))",
				"let { prettyPrint, call, builtin, num } = import(\"ast\"); prettyPrint(call(builtin(\"+\"), [num(1), num(2)]))",
				"let { prettyPrint } = import(\"ast\"); prettyPrint(quote 1 + 2 end)"
			]
		}
	}
};
const astSource = `{
  decorate: (ast, transform) ->
    if first(ast) == "Let" then
      ["Let", [first(second(ast)), transform(second(second(ast)))], 0]
    else
      transform(ast)
    end,
  // Returns the binding name for \`#macro let name = ...\`, or null otherwise.
  // Lets decorator macros add contextual info (logs, errors) without teaching
  // each macro the Let-target AST shape.
  letBindingName: (ast) ->
    if first(ast) == "Let" then
      do
        let target = first(second(ast));
        if first(target) == "symbol" then
          second(first(second(target)))
        else
          null
        end;
      end
    else
      null
    end
}`;
const additionalDocs = {
	decorate: {
		category: "ast",
		returns: { type: "array" },
		args: {
			ast: {
				type: "array",
				description: "The original AST node — either a let-binding or any expression."
			},
			transform: {
				type: "function",
				description: "A function that receives the value AST and returns transformed AST."
			}
		},
		variants: [{ argumentNames: ["ast", "transform"] }],
		description: "Decorator helper for macros: extracts the value from a let-binding (or uses the node directly), passes it to `transform`, and rewraps the result in the let. Use inside a macro to support both `#myMacro expr` and `#myMacro let x = expr` with the same transform logic.",
		examples: ["let { decorate, num } = import(\"ast\");\ndecorate(num(1), (value) -> [\"Call\", [[\"Builtin\", \"+\", 0], [value, value]], 0])"]
	},
	letBindingName: {
		category: "ast",
		returns: { type: "any" },
		args: { ast: {
			type: "array",
			description: "An AST node — typically the argument a decorator macro receives."
		} },
		variants: [{ argumentNames: ["ast"] }],
		description: "Returns the binding name when `ast` is `let <name> = <value>` with a symbol target, otherwise `null`. Pairs with `decorate` so decorator macros can include the binding name in messages (logs, error prefixes) without reaching into the Let AST shape themselves.",
		examples: ["let { letBindingName } = import(\"ast\");\nletBindingName([\"Let\", [[\"symbol\", [[\"Sym\", \"greet\", 0], null], 0], [\"Str\", \"hi\", 0]], 0])", "let { letBindingName, num } = import(\"ast\");\nletBindingName(num(42))"]
	}
};
const astModule = {
	name: "ast",
	description: "AST node constructors, predicates, and pretty-printing for metaprogramming.",
	functions: astFunctions,
	source: astSource,
	docs: {
		...moduleDocsFromFunctions(astFunctions),
		...additionalDocs
	}
};
//#endregion
//#region src/builtin/modules/bitwise/bitwise.dvala
var bitwise_default = "{};\n";
//#endregion
//#region src/builtin/modules/bitwise/index.ts
function getOperatorArgs(a, b) {
	return {
		a: { type: a },
		b: { type: b }
	};
}
const bitwiseUtilsNormalExpression = {
	bitNot: {
		evaluate: ([num], sourceCodeInfo) => {
			assertNumber(num, sourceCodeInfo, { integer: true });
			return ~num;
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number) -> Number",
			category: "bitwise",
			returns: { type: "integer" },
			args: { a: { type: "integer" } },
			variants: [{ argumentNames: ["a"] }],
			description: "Returns bitwise `not` of `a`.",
			seeAlso: [
				"&",
				"|",
				"xor",
				"bitwise.bitAndNot"
			],
			examples: ["let { bitNot } = import(\"bitwise\");\nbitNot(0)", "let { bitNot } = import(\"bitwise\");\nbitNot(255)"]
		}
	},
	bitAndNot: {
		evaluate: ([first, ...rest], sourceCodeInfo) => {
			assertNumber(first, sourceCodeInfo, { integer: true });
			return rest.reduce((result, value) => {
				assertNumber(value, sourceCodeInfo, { integer: true });
				return result & ~value;
			}, first);
		},
		arity: { min: 2 },
		docs: {
			type: "(Number, Number, ...Number) -> Number",
			category: "bitwise",
			returns: { type: "integer" },
			args: {
				...getOperatorArgs("integer", "integer"),
				c: {
					type: "integer",
					rest: true
				}
			},
			variants: [{ argumentNames: ["a", "b"] }, { argumentNames: [
				"a",
				"b",
				"c"
			] }],
			description: "Returns bitwise `and` with complement.",
			seeAlso: [
				"&",
				"|",
				"xor",
				"bitwise.bitNot"
			],
			examples: [
				"let { bitAndNot } = import(\"bitwise\");\n0b0011 bitAndNot 0b0110",
				"let { bitAndNot } = import(\"bitwise\");\nbitAndNot(0b0011, 0b0110)",
				"let { bitAndNot } = import(\"bitwise\");\nbitAndNot(0b0011, 0b0110, 0b1001)"
			]
		}
	},
	bitFlip: {
		evaluate: ([num, index], sourceCodeInfo) => {
			assertNumber(num, sourceCodeInfo, { integer: true });
			assertNumber(index, sourceCodeInfo, {
				integer: true,
				nonNegative: true
			});
			return num ^ 1 << index;
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Number, Number) -> Number",
			category: "bitwise",
			returns: { type: "integer" },
			args: { ...getOperatorArgs("integer", "integer") },
			variants: [{ argumentNames: ["a", "b"] }],
			description: "Flips bit number `b`.",
			seeAlso: [
				"bitwise.bitSet",
				"bitwise.bitClear",
				"bitwise.bitTest"
			],
			examples: [
				"let { bitFlip } = import(\"bitwise\");\n0b0011 bitFlip 1",
				"let { bitFlip } = import(\"bitwise\");\nbitFlip(0b0011, 1)",
				"let { bitFlip } = import(\"bitwise\");\nbitFlip(0b1100, 1)"
			]
		}
	},
	bitSet: {
		evaluate: ([num, index], sourceCodeInfo) => {
			assertNumber(num, sourceCodeInfo, { integer: true });
			assertNumber(index, sourceCodeInfo, {
				integer: true,
				nonNegative: true
			});
			return num | 1 << index;
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Number, Number) -> Number",
			category: "bitwise",
			returns: { type: "integer" },
			args: { ...getOperatorArgs("integer", "integer") },
			variants: [{ argumentNames: ["a", "b"] }],
			description: "Sets bit number `b`.",
			seeAlso: [
				"bitwise.bitFlip",
				"bitwise.bitClear",
				"bitwise.bitTest"
			],
			examples: [
				"let { bitSet } = import(\"bitwise\");\n0b0010 bitSet 1",
				"let { bitSet } = import(\"bitwise\");\nbitSet(0b0011, 1)",
				"let { bitSet } = import(\"bitwise\");\nbitSet(0b1100, 1)"
			]
		}
	},
	bitClear: {
		evaluate: ([num, index], sourceCodeInfo) => {
			assertNumber(num, sourceCodeInfo, { integer: true });
			assertNumber(index, sourceCodeInfo, {
				integer: true,
				nonNegative: true
			});
			return num & ~(1 << index);
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Number, Number) -> Number",
			category: "bitwise",
			returns: { type: "integer" },
			args: { ...getOperatorArgs("integer", "integer") },
			variants: [{ argumentNames: ["a", "b"] }],
			description: "Clears bit number `b`.",
			seeAlso: [
				"bitwise.bitFlip",
				"bitwise.bitSet",
				"bitwise.bitTest"
			],
			examples: [
				"let { bitClear } = import(\"bitwise\");\n0b0011 bitClear 1",
				"let { bitClear } = import(\"bitwise\");\nbitClear(0b0011, 1)",
				"let { bitClear } = import(\"bitwise\");\nbitClear(0b1100, 1)"
			]
		}
	},
	bitTest: {
		evaluate: ([num, index], sourceCodeInfo) => {
			assertNumber(num, sourceCodeInfo, { integer: true });
			assertNumber(index, sourceCodeInfo, {
				integer: true,
				nonNegative: true
			});
			return !!(num & 1 << index);
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Number, Number) -> Boolean",
			category: "bitwise",
			returns: { type: "boolean" },
			args: { ...getOperatorArgs("integer", "integer") },
			variants: [{ argumentNames: ["a", "b"] }],
			description: "Checks if bit number `b` is set.",
			seeAlso: [
				"bitwise.bitFlip",
				"bitwise.bitSet",
				"bitwise.bitClear"
			],
			examples: [
				"let { bitTest } = import(\"bitwise\");\n0b0011 bitTest 1",
				"let { bitTest } = import(\"bitwise\");\nbitTest(0b0011, 1)",
				"let { bitTest } = import(\"bitwise\");\nbitTest(0b1100, 1)"
			]
		}
	}
};
const bitwiseUtilsModule = {
	name: "bitwise",
	description: "Bitwise utility functions for AND, OR, XOR, shift, and bit manipulation.",
	functions: bitwiseUtilsNormalExpression,
	source: bitwise_default,
	sourcePath: "packages/dvala-engine/src/builtin/modules/bitwise/bitwise.dvala",
	docs: moduleDocsFromFunctions(bitwiseUtilsNormalExpression)
};
//#endregion
//#region src/builtin/modules/collection/collection.dvala
var collection_default = "{\n  update: (coll, key, fn, ...extraParams) -> assoc(\n    coll,\n    key,\n    apply(fn, [get(coll, key), ...extraParams])\n  ),\n  updateIn: (coll, ks, fn, ...extraParams) -> do\n    let k = first(ks);\n    if count(ks) == 1 then\n      assoc(coll, k, apply(fn, [get(coll, k), ...extraParams]))\n    else\n      do\n        let inner = get(coll, k);\n        let innerColl = if isNull(inner) then {} else inner end;\n        assoc(\n          coll,\n          k,\n          apply(self, [innerColl, slice(ks, 1), fn, ...extraParams])\n        );\n      end\n    end;\n  end,\n  filteri: (coll, fn) -> if isArray(coll) then\n    reduce(\n      for(i in range(count(coll))) -> [nth(coll, i), i],\n      (acc, pair) -> if fn(first(pair), second(pair)) then\n        [...acc, first(pair)]\n      else\n        acc\n      end,\n      []\n    )\n  else if isString(coll) then\n    reduce(\n      for(i in range(count(coll))) -> [nth(coll, i), i],\n      (acc, pair) -> if fn(first(pair), second(pair)) then\n        acc ++ first(pair)\n      else\n        acc\n      end,\n      \"\"\n    )\n  else if isObject(coll) then\n    reduce(\n      keys(coll),\n      (acc, k) -> if fn(coll(k), k) then assoc(acc, k, coll(k)) else acc end,\n      {}\n    )\n  else\n    perform(@dvala.error, \"Expected collection\")\n  end,\n  mapi: (coll, fn) -> if isArray(coll) then\n    for(i in range(count(coll))) -> fn(nth(coll, i), i)\n  else if isString(coll) then\n    reduce(\n      for(i in range(count(coll))) -> fn(nth(coll, i), i),\n      (acc, ch) -> acc ++ ch,\n      \"\"\n    )\n  else if isObject(coll) then\n    reduce(keys(coll), (acc, k) -> assoc(acc, k, fn(coll(k), k)), {})\n  else\n    perform(@dvala.error, \"Expected collection\")\n  end,\n  reducei: (coll, fn, initial) -> if isString(coll) then\n    loop(acc = initial, i = 0) -> do\n      if i >= count(coll) then\n        acc\n      else\n        recur(fn(acc, nth(coll, i), i), i + 1)\n      end;\n    end\n  else if isArray(coll) then\n    loop(acc = initial, i = 0) -> do\n      if i >= count(coll) then\n        acc\n      else\n        recur(fn(acc, nth(coll, i), i), i + 1)\n      end;\n    end\n  else if isObject(coll) then\n    reduce(keys(coll), (acc, k) -> fn(acc, coll(k), k), initial)\n  else\n    perform(@dvala.error, \"Expected collection\")\n  end,\n  reduceRight: (coll, fn, initial) -> if isString(coll) then\n    reduce(reverse(coll), fn, initial)\n  else if isArray(coll) then\n    reduce(reverse(coll), fn, initial)\n  else if isObject(coll) then\n    reduce(reverse(keys(coll)), (acc, k) -> fn(acc, coll(k)), initial)\n  else\n    perform(@dvala.error, \"Expected collection\")\n  end,\n  reduceiRight: (coll, fn, initial) -> if isString(coll) then\n    do\n      let len = count(coll);\n      loop(acc = initial, i = len - 1) -> do\n        if i < 0 then acc else recur(fn(acc, nth(coll, i), i), i - 1) end;\n      end;\n    end\n  else if isArray(coll) then\n    do\n      let len = count(coll);\n      loop(acc = initial, i = len - 1) -> do\n        if i < 0 then acc else recur(fn(acc, nth(coll, i), i), i - 1) end;\n      end;\n    end\n  else if isObject(coll) then\n    reduce(reverse(keys(coll)), (acc, k) -> fn(acc, coll(k), k), initial)\n  else\n    perform(@dvala.error, \"Expected collection\")\n  end,\n  reductions: (coll, fn, initial) -> do\n    let items =\n      if isString(coll) then\n        if count(coll) == 0 then\n          []\n        else\n          for(i in range(count(coll))) -> nth(coll, i)\n        end\n      else if isArray(coll) then\n        coll\n      else if isObject(coll) then\n        for(k in keys(coll)) -> coll(k)\n      else\n        perform(@dvala.error, \"Expected collection\")\n      end;\n    if count(items) == 0 then\n      [initial]\n    else\n      reduce(\n        items,\n        (acc, elem) -> do\n          let prev = last(acc);\n          [...acc, fn(prev, elem)];\n        end,\n        [initial]\n      )\n    end;\n  end,\n  reductionsi: (coll, fn, initial) -> if isString(coll) then\n    do\n      if count(coll) == 0 then\n        [initial]\n      else\n        reduce(\n          for(i in range(count(coll))) -> [nth(coll, i), i],\n          (acc, pair) -> do\n            let prev = last(acc);\n            [...acc, fn(prev, first(pair), second(pair))];\n          end,\n          [initial]\n        )\n      end;\n    end\n  else if isArray(coll) then\n    do\n      if count(coll) == 0 then\n        [initial]\n      else\n        reduce(\n          for(i in range(count(coll))) -> [nth(coll, i), i],\n          (acc, pair) -> do\n            let prev = last(acc);\n            [...acc, fn(prev, first(pair), second(pair))];\n          end,\n          [initial]\n        )\n      end;\n    end\n  else if isObject(coll) then\n    do\n      let ks = keys(coll);\n      if count(ks) == 0 then\n        [initial]\n      else\n        reduce(\n          ks,\n          (acc, k) -> do\n            let prev = last(acc);\n            [...acc, fn(prev, coll(k), k)];\n          end,\n          [initial]\n        )\n      end;\n    end\n  else\n    perform(@dvala.error, \"Expected collection\")\n  end,\n  isEvery: (coll, fn) -> do\n    let items =\n      if isArray(coll) then\n        coll\n      else if isString(coll) then\n        for(i in range(count(coll))) -> nth(coll, i)\n      else if isObject(coll) then\n        for(k in keys(coll)) -> [k, coll(k)]\n      else\n        perform(@dvala.error, \"Expected collection\")\n      end;\n    loop(i = 0) -> if i >= count(items) then\n      true\n    else if !(fn(nth(items, i))) then\n      false\n    else\n      recur(i + 1)\n    end;\n  end,\n  isAny: (coll, fn) -> do\n    let items =\n      if isArray(coll) then\n        coll\n      else if isString(coll) then\n        for(i in range(count(coll))) -> nth(coll, i)\n      else if isObject(coll) then\n        for(k in keys(coll)) -> [k, coll(k)]\n      else\n        perform(@dvala.error, \"Expected collection\")\n      end;\n    loop(i = 0) -> if i >= count(items) then\n      false\n    else if fn(nth(items, i)) then\n      true\n    else\n      recur(i + 1)\n    end;\n  end,\n  notAny: (coll, fn) -> do\n    let items =\n      if isArray(coll) then\n        coll\n      else if isString(coll) then\n        for(i in range(count(coll))) -> nth(coll, i)\n      else if isObject(coll) then\n        for(k in keys(coll)) -> [k, coll(k)]\n      else\n        perform(@dvala.error, \"Expected collection\")\n      end;\n    loop(i = 0) -> if i >= count(items) then\n      true\n    else if fn(nth(items, i)) then\n      false\n    else\n      recur(i + 1)\n    end;\n  end,\n  notEvery: (coll, fn) -> do\n    let items =\n      if isArray(coll) then\n        coll\n      else if isString(coll) then\n        for(i in range(count(coll))) -> nth(coll, i)\n      else if isObject(coll) then\n        for(k in keys(coll)) -> [k, coll(k)]\n      else\n        perform(@dvala.error, \"Expected collection\")\n      end;\n    loop(i = 0) -> if i >= count(items) then\n      false\n    else if !(fn(nth(items, i))) then\n      true\n    else\n      recur(i + 1)\n    end;\n  end,\n};\n";
//#endregion
//#region src/builtin/modules/collection/index.ts
function get(coll, key) {
	if (isObj(coll)) {
		if (typeof key === "string" && collHasKey(coll, key)) return toAny(coll.get(key));
	} else if (isArr(coll)) {
		if (isNumber(key, {
			nonNegative: true,
			integer: true
		}) && key >= 0 && key < coll.size) return toAny(coll.get(key));
	} else if (typeof coll === "string") {
		if (isNumber(key, {
			nonNegative: true,
			integer: true
		}) && key < coll.length) return toAny(coll[key]);
	}
}
function assoc(coll, key, value, sourceCodeInfo) {
	assertColl(coll, sourceCodeInfo);
	if (isArr(coll) || typeof coll === "string") {
		assertNumber(key, sourceCodeInfo, { integer: true });
		assertNumber(key, sourceCodeInfo, { gte: 0 });
		if (typeof coll === "string") {
			assertNumber(key, sourceCodeInfo, { lte: coll.length });
			assertString(value, sourceCodeInfo, { char: true });
			return `${coll.slice(0, key)}${value}${coll.slice(key + 1)}`;
		}
		assertNumber(key, sourceCodeInfo, { lte: coll.size });
		return coll.set(key, value);
	}
	assertString(key, sourceCodeInfo);
	return coll.assoc(key, value);
}
function assocInHelper(coll, keys, value, sourceCodeInfo) {
	if (keys.length === 0) return coll;
	const key = asStringOrNumber(keys[0], sourceCodeInfo);
	if (keys.length === 1) return assoc(coll, key, value, sourceCodeInfo);
	const nested = get(coll, key);
	return assoc(coll, key, toAny(assocInHelper(nested !== void 0 && isColl(nested) ? nested : PersistentMap.empty(), keys.slice(1), value, sourceCodeInfo)), sourceCodeInfo);
}
const collectionUtilsFunctions = {
	getIn: {
		evaluate: (params, sourceCodeInfo) => {
			let coll = toAny(params.get(0));
			const keys = params.get(1) ?? PersistentVector.empty();
			const defaultValue = toAny(params.get(2));
			assertArray(keys, sourceCodeInfo);
			for (const key of keys) {
				assertStringOrNumber(key, sourceCodeInfo);
				if (isColl(coll)) {
					const nextValue = get(coll, key);
					if (nextValue !== void 0) coll = nextValue;
					else return defaultValue;
				} else return defaultValue;
			}
			return coll;
		},
		arity: {
			min: 2,
			max: 3
		},
		docs: {
			type: "(Unknown[], Unknown[], Unknown) -> Unknown",
			category: "collection",
			returns: { type: "any" },
			args: {
				a: { type: "collection" },
				b: { type: "array" },
				notFound: { type: "any" }
			},
			variants: [{ argumentNames: ["a", "b"] }, { argumentNames: [
				"a",
				"b",
				"notFound"
			] }],
			description: "Returns the value in a nested collection, where `b` is an array of keys. Returns `not-found` if the key is not present. If `not-found` is not set, `null` is returned.",
			seeAlso: [
				"get",
				"collection.assocIn",
				"collection.updateIn"
			],
			examples: [
				`
let cu = import("collection");
cu.getIn(
  [[1, 2, 3], [4, { a: "Kalle" }, 6]],
  [1, 1, "a", 0]
)`,
				`
let cu = import("collection");
cu.getIn(
  [[1, 2, 3], [4, { a: "Kalle" }, 6]],
  [1, 1, "b", 0]
)`,
				`
let cu = import("collection");
cu.getIn(
  [[1, 2, 3], [4, { a: "Kalle" }, 6]],
  [1, 1, "b", 0],
  "Lisa"
)`
			]
		}
	},
	assocIn: {
		evaluate: ([originalColl, keys, value], sourceCodeInfo) => {
			assertColl(originalColl, sourceCodeInfo);
			assertArray(keys, sourceCodeInfo);
			assertAny(value, sourceCodeInfo);
			return assocInHelper(originalColl, [...keys], value, sourceCodeInfo);
		},
		arity: toFixedArity(3),
		docs: {
			type: "(Unknown[], Unknown[], Unknown) -> Unknown[]",
			category: "collection",
			returns: { type: "collection" },
			args: {
				coll: { type: "collection" },
				ks: {
					type: ["number", "string"],
					array: true
				},
				value: { type: "any" }
			},
			variants: [{ argumentNames: [
				"coll",
				"ks",
				"value"
			] }],
			description: `
Associates a value in the nested collection \`coll\`, where \`ks\` is an array of keys and \`value\` is the new value.

If any levels do not exist, objects will be created - and the corresponding keys must be of type string.`,
			seeAlso: [
				"assoc",
				"collection.getIn",
				"collection.updateIn"
			],
			examples: [
				`
let cu = import("collection");
cu.assocIn(
  {},
  ["a", "b", "c"],
  "Albert"
)`,
				`
let cu = import("collection");
cu.assocIn(
  [1, 2, [1, 2, 3]],
  [2, 1],
  "Albert"
)`,
				`
let cu = import("collection");
cu.assocIn(
  [1, 2, { name: "albert" }],
  [2, "name", 0],
  "A"
)`
			]
		}
	},
	update: {
		evaluate: () => {
			throw new Error("update: Dvala implementation should be used instead");
		},
		arity: { min: 3 },
		docs: {
			type: "(Unknown[], (String | Number), (Unknown -> Unknown), ...Unknown) -> Unknown[]",
			category: "collection",
			returns: { type: "collection" },
			args: {
				coll: { type: "collection" },
				key: { type: ["string", "number"] },
				fun: { type: "function" },
				funArgs: {
					type: "any",
					rest: true
				}
			},
			variants: [{ argumentNames: [
				"coll",
				"key",
				"fun"
			] }, { argumentNames: [
				"coll",
				"key",
				"fun",
				"funArgs"
			] }],
			description: `
Updates a value in the \`coll\` collection, where \`key\` is a key. \`fun\` is a function
that will take the old value and any supplied \`fun-args\` and
return the new value.
If the key does not exist, \`null\` is passed as the old value.`,
			seeAlso: ["collection.updateIn", "assoc"],
			examples: [`
let cu = import("collection");
let x = { a: 1, b: 2 };
cu.update(x, "a", inc)`, `
let cu = import("collection");
let x = { a: 1, b: 2 };
cu.update(
  x,
  "c",
  val -> if isNull(val) then 0 else inc(val) end
)`]
		}
	},
	updateIn: {
		evaluate: () => {
			throw new Error("updateIn: Dvala implementation should be used instead");
		},
		arity: { min: 3 },
		docs: {
			type: "(Unknown[], Unknown[], (Unknown -> Unknown), ...Unknown) -> Unknown[]",
			category: "collection",
			returns: { type: "collection" },
			args: {
				coll: { type: "collection" },
				ks: { type: "array" },
				fun: { type: "function" },
				funArgs: {
					type: "any",
					rest: true
				}
			},
			variants: [{ argumentNames: [
				"coll",
				"ks",
				"fun"
			] }, { argumentNames: [
				"coll",
				"ks",
				"fun",
				"funArgs"
			] }],
			description: `Updates a value in the \`coll\` collection, where \`ks\` is an array of
keys and \`fun\` is a function that will take the old value and
any supplied \`fun-args\` and return the new value. If any levels do not exist,
objects will be created - and the corresponding keys must be of type string.`,
			seeAlso: [
				"collection.update",
				"collection.assocIn",
				"collection.getIn"
			],
			examples: [
				`
let cu = import("collection");
cu.updateIn(
  { a: [1, 2, 3] },
  ["a", 1],
  -> if isNull($) then 0 else inc($) end
)`,
				`
let cu = import("collection");
cu.updateIn(
  { a: { foo: "bar"} },
  ["a", "foo"],
  -> if isNull($) then "?" else "!" end
)`,
				`
let cu = import("collection");
cu.updateIn(
  { a: { foo: "bar"} },
  ["a", "baz"],
  -> if isNull($) then "?" else "!" end
)`,
				`
let cu = import("collection");
cu.updateIn(
  { a: [1, 2, 3] },
  ["a", 1],
  *,
  10,
  10,
  10,
)`
			]
		}
	},
	filteri: {
		evaluate: () => {
			throw new Error("filteri: Dvala implementation should be used instead");
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Unknown[], (Unknown, Number) -> Boolean) -> Unknown[]",
			category: "collection",
			returns: { type: "collection" },
			args: {
				a: { type: "collection" },
				b: {
					type: "function",
					description: "The function to call for each element in the collection. The function should take two arguments: the element itself and the index."
				}
			},
			variants: [{ argumentNames: ["a", "b"] }],
			description: "Creates a new collection with all elements that pass the test implemented by `b`. The function is called for each element in the collection, and it should take two arguments: the element itself and the index.",
			seeAlso: ["filter", "collection.mapi"],
			examples: [
				"let cu = import(\"collection\"); cu.filteri([1, 2, 3], (x, i) -> i % 2 == 0)",
				"let cu = import(\"collection\"); cu.filteri([1, 2, 3], (x, i) -> x % 2 == 0)",
				"let cu = import(\"collection\"); cu.filteri([1, 2, 3], (x, i) -> x + i > 3)"
			]
		}
	},
	mapi: {
		evaluate: () => {
			throw new Error("mapi: Dvala implementation should be used instead");
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Unknown[], (Unknown, Number) -> Unknown) -> Unknown[]",
			category: "collection",
			returns: { type: "collection" },
			args: {
				a: { type: "collection" },
				b: {
					type: "function",
					description: "The function to call for each element in the collection. The function should take two arguments: the element itself and the index."
				}
			},
			variants: [{ argumentNames: ["a", "b"] }],
			description: "Creates a new collection populated with the results of calling `b` on every element in `a`. The function is called for each element in the collection, and it should take two arguments: the element itself and the index.",
			seeAlso: ["map", "collection.filteri"],
			examples: [
				"let cu = import(\"collection\"); cu.mapi([1, 2, 3], (x, i) -> x + i)",
				"let cu = import(\"collection\"); cu.mapi([1, 2, 3], (x, i) -> x * i)",
				"let cu = import(\"collection\"); cu.mapi([1, 2, 3], (x, i) -> x - i)",
				"let cu = import(\"collection\"); cu.mapi([1, 2, 3], (x, i) -> x / inc(i))",
				"let cu = import(\"collection\"); cu.mapi([1, 2, 3], (x, i) -> x % inc(i))"
			]
		}
	},
	reducei: {
		evaluate: () => {
			throw new Error("reducei: Dvala implementation should be used instead");
		},
		arity: toFixedArity(3),
		docs: {
			type: "(Unknown[], (Unknown, Unknown, Number) -> Unknown, Unknown) -> Unknown",
			category: "collection",
			returns: { type: "any" },
			args: {
				coll: { type: "collection" },
				fun: {
					type: "function",
					description: "The function to call for each element in the collection. The function should take three arguments: the accumulator, the element itself, and the index."
				},
				initial: {
					type: "any",
					description: "The initial value to use as the accumulator."
				}
			},
			variants: [{ argumentNames: [
				"coll",
				"fun",
				"initial"
			] }],
			description: "Runs `fun` function on each element of the `coll`, passing in the return value from the calculation on the preceding element. The final result of running the reducer across all elements of the `coll` is a single value. The function is called for each element in the collection, and it should take three arguments: the accumulator, the element itself, and the index.",
			seeAlso: [
				"reduce",
				"collection.reduceiRight",
				"collection.reductionsi"
			],
			examples: [
				"let cu = import(\"collection\"); cu.reducei([1, 2, 3], (acc, x, i) -> acc + x + i, 0)",
				"let cu = import(\"collection\"); cu.reducei(\"Albert\", (acc, x, i) -> acc ++ x ++ i, \"\")",
				"let cu = import(\"collection\"); cu.reducei({ a: 1, b: 2 }, -> $ ++ $3, \"\")"
			]
		}
	},
	reduceRight: {
		evaluate: () => {
			throw new Error("reduceRight: Dvala implementation should be used instead");
		},
		arity: toFixedArity(3),
		docs: {
			type: "(Unknown[], (Unknown, Unknown) -> Unknown, Unknown) -> Unknown",
			category: "collection",
			returns: { type: "any" },
			args: {
				fun: { type: "function" },
				coll: { type: "collection" },
				initial: { type: "any" }
			},
			variants: [{ argumentNames: [
				"coll",
				"fun",
				"initial"
			] }],
			description: "Runs `fun` function on each element of the `coll` (starting from the last item), passing in the return value from the calculation on the preceding element. The final result of running the reducer across all elements of the `coll` is a single value.",
			seeAlso: ["reduce", "collection.reduceiRight"],
			examples: ["let cu = import(\"collection\"); cu.reduceRight([\"A\", \"B\", \"C\"], str, \"\")", "let cu = import(\"collection\"); cu.reduceRight({ a: 1, b: 2 }, +, 0)"]
		}
	},
	reduceiRight: {
		evaluate: () => {
			throw new Error("reduceiRight: Dvala implementation should be used instead");
		},
		arity: toFixedArity(3),
		docs: {
			type: "(Unknown[], (Unknown, Unknown, Number) -> Unknown, Unknown) -> Unknown",
			category: "collection",
			returns: { type: "any" },
			args: {
				coll: { type: "collection" },
				fun: {
					type: "function",
					description: "The function to call for each element in the collection. The function should take three arguments: the accumulator, the element itself, and the index."
				},
				initial: {
					type: "any",
					description: "The initial value to use as the accumulator."
				}
			},
			variants: [{ argumentNames: [
				"coll",
				"fun",
				"initial"
			] }],
			description: "Runs `fun` function on each element of the `coll` (starting from the last item), passing in the return value from the calculation on the preceding element. The final result of running the reducer across all elements of the `coll` is a single value. The function is called for each element in the collection, and it should take three arguments: the accumulator, the element itself, and the index.",
			seeAlso: ["collection.reducei", "collection.reduceRight"],
			examples: [
				"let cu = import(\"collection\"); cu.reduceiRight([1, 2, 3], (acc, x, i) -> acc + x + i, 0)",
				"let cu = import(\"collection\"); cu.reduceiRight(\"Albert\", (acc, x, i) -> acc ++ x ++ i, \"\")",
				"let cu = import(\"collection\"); cu.reduceiRight({ a: 1, b: 2 }, -> $ ++ $3, \"\")"
			]
		}
	},
	reductions: {
		evaluate: () => {
			throw new Error("reductions: Dvala implementation should be used instead");
		},
		arity: toFixedArity(3),
		docs: {
			type: "(Unknown[], (Unknown, Unknown) -> Unknown, Unknown) -> Unknown[]",
			category: "collection",
			returns: {
				type: "any",
				array: true
			},
			args: {
				fun: { type: "function" },
				coll: { type: "collection" },
				initial: { type: "any" }
			},
			variants: [{ argumentNames: [
				"coll",
				"fun",
				"initial"
			] }],
			description: "Returns an array of the intermediate values of the reduction (see `reduce`) of `coll` by `fun`.",
			seeAlso: ["reduce", "collection.reductionsi"],
			examples: [
				"let cu = import(\"collection\"); cu.reductions([1, 2, 3], +, 0)",
				"let cu = import(\"collection\"); cu.reductions([1, 2, 3], +, 10)",
				"let cu = import(\"collection\"); cu.reductions([], +, 0)",
				"let cu = import(\"collection\"); cu.reductions({ a: 1, b: 2 }, +, 0)",
				`
let cu = import("collection");
cu.reductions(
  [1, 2, 3, 4, 5, 6, 7, 8, 9],
  (result, value) -> result + (if isEven(value) then value else 0 end),
  0
)`
			]
		}
	},
	reductionsi: {
		evaluate: () => {
			throw new Error("reductionsi: Dvala implementation should be used instead");
		},
		arity: toFixedArity(3),
		docs: {
			type: "(Unknown[], (Unknown, Unknown, Number) -> Unknown, Unknown) -> Unknown[]",
			category: "collection",
			returns: {
				type: "any",
				array: true
			},
			args: {
				coll: { type: "collection" },
				fun: {
					type: "function",
					description: "The function to call for each element in the collection. The function should take three arguments: the accumulator, the element itself, and the index."
				},
				initial: {
					type: "any",
					description: "The initial value to use as the accumulator."
				}
			},
			variants: [{ argumentNames: [
				"coll",
				"fun",
				"initial"
			] }],
			description: "Returns an array of the intermediate values of the reduction (see `reduce`) of `coll` by `fun`. The function is called for each element in the collection, and it should take three arguments: the accumulator, the element itself, and the index.",
			seeAlso: ["collection.reductions", "collection.reducei"],
			examples: [
				"let cu = import(\"collection\"); cu.reductionsi([1, 2, 3], (acc, x, i) -> acc + x + i, 0)",
				"let cu = import(\"collection\"); cu.reductionsi(\"Albert\", (acc, x, i) -> acc ++ x ++ i, \"\")",
				"let cu = import(\"collection\"); cu.reductionsi({ a: 1, b: 2 }, -> $ ++ $3, \"\")"
			]
		}
	},
	notEmpty: {
		evaluate: ([coll], sourceCodeInfo) => {
			if (coll === null) return null;
			assertColl(coll, sourceCodeInfo);
			if (typeof coll === "string") return coll.length > 0 ? coll : null;
			if (isArr(coll)) return coll.size > 0 ? coll : null;
			return coll.size > 0 ? coll : null;
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Unknown[] | Null) -> (Unknown[] | Null)",
			category: "collection",
			returns: { type: "any" },
			args: { coll: { type: ["collection", "null"] } },
			variants: [{ argumentNames: ["coll"] }],
			description: "Returns `null` if `coll` is empty or `null`, otherwise `coll`.",
			seeAlso: ["isEmpty", "isNotEmpty"],
			examples: [
				"let cu = import(\"collection\"); cu.notEmpty([])",
				"let cu = import(\"collection\"); cu.notEmpty([1, 2, 3])",
				"let cu = import(\"collection\"); cu.notEmpty({})",
				"let cu = import(\"collection\"); cu.notEmpty({ a: 2 })",
				"let cu = import(\"collection\"); cu.notEmpty(\"\")",
				"let cu = import(\"collection\"); cu.notEmpty(\"Albert\")",
				"let cu = import(\"collection\"); cu.notEmpty(null)"
			]
		}
	},
	isEvery: {
		evaluate: () => {
			throw new Error("isEvery: Dvala implementation should be used instead");
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Unknown[], (Unknown -> Boolean)) -> Boolean",
			category: "collection",
			returns: { type: "boolean" },
			args: {
				a: { type: "collection" },
				b: { type: "function" }
			},
			variants: [{ argumentNames: ["a", "b"] }],
			description: "Returns `true` if all entries in `a` pass the test implemented by `b`, otherwise returns `false`.",
			seeAlso: [
				"collection.isAny",
				"collection.notEvery",
				"collection.notAny",
				"functional.everyPred",
				"grid.isCellEvery"
			],
			examples: [
				"let cu = import(\"collection\"); cu.isEvery([1, 2, 3], isNumber)",
				"let cu = import(\"collection\"); cu.isEvery([1, 2, 3], isEven)",
				`
let cu = import("collection");
cu.isEvery(
  ["Albert", "Mojir", 160, [1, 2]],
  isString,
)`,
				`
let cu = import("collection");
cu.isEvery(
  [50, 100, 150, 200],
  -> $ > 10,
)`,
				"let cu = import(\"collection\"); cu.isEvery([], isNumber)",
				"let cu = import(\"collection\"); cu.isEvery(\"\", isNumber)",
				"let cu = import(\"collection\"); cu.isEvery({}, isNumber)",
				`
let cu = import("collection");
cu.isEvery(
  { a: 2, b: 4},
  -> isEven(second($))
)`,
				`
let cu = import("collection");
cu.isEvery(
  { a: 2, b: 3 },
  -> isEven(second($))
)`
			]
		}
	},
	isAny: {
		evaluate: () => {
			throw new Error("isAny: Dvala implementation should be used instead");
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Unknown[], (Unknown -> Boolean)) -> Boolean",
			category: "collection",
			returns: { type: "boolean" },
			args: {
				a: { type: "collection" },
				b: { type: "function" }
			},
			variants: [{ argumentNames: ["a", "b"] }],
			description: "Returns `true` if any element in `a` pass the test implemented by `b`, otherwise returns `false`.",
			seeAlso: [
				"collection.isEvery",
				"collection.notAny",
				"collection.notEvery",
				"functional.somePred",
				"some",
				"grid.isSome"
			],
			examples: [
				`
let cu = import("collection");
cu.isAny(
  ["Albert", "Mojir", 160, [1, 2]],
  isString
)`,
				`
let cu = import("collection");
cu.isAny(
  [50, 100, 150, 200],
  x -> x > 10
)`,
				"let cu = import(\"collection\"); cu.isAny([], isNumber)",
				"let cu = import(\"collection\"); cu.isAny(\"\", isNumber)",
				"let cu = import(\"collection\"); cu.isAny({}, isNumber)",
				`
let cu = import("collection");
cu.isAny(
  { a: 2, b: 3 },
  -> isEven(second($))
)`,
				`
let cu = import("collection");
cu.isAny(
  { a: 1, b: 3 },
  -> isEven(second($))
)`
			]
		}
	},
	notAny: {
		evaluate: () => {
			throw new Error("notAny: Dvala implementation should be used instead");
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Unknown[], (Unknown -> Boolean)) -> Boolean",
			category: "collection",
			returns: { type: "boolean" },
			args: {
				a: { type: "collection" },
				b: { type: "function" }
			},
			variants: [{ argumentNames: ["a", "b"] }],
			description: "Returns `false` if any element in `a` pass the test implemented by `b`, otherwise returns `true`.",
			seeAlso: [
				"collection.isAny",
				"collection.isEvery",
				"collection.notEvery"
			],
			examples: [
				`
let cu = import("collection");
cu.notAny(
  ["Albert", "Mojir", 160, [1, 2]],
  isString
)`,
				`
let cu = import("collection");
cu.notAny(
  [50, 100, 150, 200],
  x -> x > 10
)`,
				"let cu = import(\"collection\"); cu.notAny([], isNumber)",
				"let cu = import(\"collection\"); cu.notAny(\"\", isNumber)",
				"let cu = import(\"collection\"); cu.notAny({}, isNumber)",
				`
let cu = import("collection");
cu.notAny(
  { a: 2, b: 3 },
  -> isEven(second($))
)`,
				`
let cu = import("collection");
cu.notAny(
  { a: 1, b: 3 },
  -> isEven(second($))
)`
			]
		}
	},
	notEvery: {
		evaluate: () => {
			throw new Error("notEvery: Dvala implementation should be used instead");
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Unknown[], (Unknown -> Boolean)) -> Boolean",
			category: "collection",
			returns: { type: "boolean" },
			args: {
				a: { type: "collection" },
				b: { type: "function" }
			},
			variants: [{ argumentNames: ["a", "b"] }],
			description: "Returns `true` if at least one element in `a` does not pass the test implemented by `b`, otherwise returns `false`.",
			seeAlso: [
				"collection.isEvery",
				"collection.isAny",
				"collection.notAny"
			],
			examples: [
				`
let cu = import("collection");
cu.notEvery(
  ["Albert", "Mojir", 160, [1, 2]],
  isString
)`,
				`
let cu = import("collection");
cu.notEvery(
  [50, 100, 150, 200],
  x -> x > 10
)`,
				"let cu = import(\"collection\"); cu.notEvery([], isNumber)",
				"let cu = import(\"collection\"); cu.notEvery(\"\", isNumber)",
				"let cu = import(\"collection\"); cu.notEvery({}, isNumber)",
				`
let cu = import("collection");
cu.notEvery(
  { a: 2, b: 4 },
  -> isEven(second($))
)`,
				`
let cu = import("collection");
cu.notEvery(
  { a: 2, b: 3 },
  -> isEven(second($))
)`
			]
		}
	}
};
const collectionUtilsModule = {
	name: "collection",
	description: "Advanced collection operations: groupBy, frequencies, partition, and more.",
	functions: collectionUtilsFunctions,
	source: collection_default,
	sourcePath: "packages/dvala-engine/src/builtin/modules/collection/collection.dvala",
	docs: moduleDocsFromFunctions(collectionUtilsFunctions)
};
//#endregion
//#region src/builtin/modules/convert/convert.dvala
var convert_default = "{};\n";
//#endregion
//#region src/builtin/modules/convert/index.ts
const lengthUnits = {
	baseUnit: "m",
	description: "length",
	units: {
		angstrom: 1e-10,
		um: 1e-6,
		mm: .001,
		cm: .01,
		m: 1,
		km: 1e3,
		in: .0254,
		ft: .3048,
		yd: .9144,
		mi: 1609.344,
		nmi: 1852
	}
};
const weightUnits = {
	baseUnit: "kg",
	description: "weight",
	units: {
		mg: 1e-6,
		g: .001,
		kg: 1,
		t: 1e3,
		oz: .028349523125,
		lb: .45359237,
		st: 6.35029318
	}
};
const volumeUnits = {
	baseUnit: "l",
	description: "volume",
	units: {
		ml: .001,
		cl: .01,
		dl: .1,
		l: 1,
		tsp: .00492892159375,
		tbsp: .01478676478125,
		flOz: .0295735295625,
		cup: .2365882365,
		pt: .473176473,
		qt: .946352946,
		gal: 3.785411784
	}
};
const timeUnits = {
	baseUnit: "s",
	description: "time",
	units: {
		ms: .001,
		s: 1,
		min: 60,
		h: 3600,
		day: 86400,
		week: 604800
	}
};
const areaUnits = {
	baseUnit: "m2",
	description: "area",
	units: {
		mm2: 1e-6,
		cm2: 1e-4,
		m2: 1,
		km2: 1e6,
		in2: 64516e-8,
		ft2: .09290304,
		yd2: .83612736,
		acre: 4046.8564224,
		hectare: 1e4
	}
};
const speedUnits = {
	baseUnit: "mps",
	description: "speed",
	units: {
		mps: 1,
		kmh: 1 / 3.6,
		mph: .44704,
		kn: .514444,
		fps: .3048
	}
};
const dataUnits = {
	baseUnit: "b",
	description: "data",
	units: {
		b: 1,
		kb: 1e3,
		mb: 1e6,
		gb: 1e9,
		tb: 0xe8d4a51000,
		pb: 0x38d7ea4c68000
	}
};
const pressureUnits = {
	baseUnit: "pa",
	description: "pressure",
	units: {
		pa: 1,
		kpa: 1e3,
		bar: 1e5,
		atm: 101325,
		psi: 6894.757293168,
		mmhg: 133.322387415
	}
};
const energyUnits = {
	baseUnit: "j",
	description: "energy",
	units: {
		j: 1,
		kj: 1e3,
		cal: 4.184,
		kcal: 4184,
		wh: 3600,
		kwh: 36e5,
		btu: 1055.06
	}
};
const powerUnits = {
	baseUnit: "w",
	description: "power",
	units: {
		w: 1,
		kw: 1e3,
		mw: 1e6,
		hp: 745.7
	}
};
const frequencyUnits = {
	baseUnit: "hz",
	description: "frequency",
	units: {
		hz: 1,
		khz: 1e3,
		mhz: 1e6,
		ghz: 1e9
	}
};
const angleUnits = {
	baseUnit: "rad",
	description: "angle",
	units: {
		deg: Math.PI / 180,
		rad: 1,
		grad: Math.PI / 200,
		turn: 2 * Math.PI
	}
};
function celsiusTo(unit, value) {
	switch (unit) {
		case "c": return value;
		case "f": return value * 9 / 5 + 32;
		case "k": return value + 273.15;
	}
}
function toCelsius(unit, value) {
	switch (unit) {
		case "c": return value;
		case "f": return (value - 32) * 5 / 9;
		case "k": return value - 273.15;
	}
}
const temperatureUnits = [
	"c",
	"f",
	"k"
];
const unitDescriptions = {
	angstrom: "ångströms",
	um: "micrometers",
	mm: "millimeters",
	cm: "centimeters",
	m: "meters",
	km: "kilometers",
	in: "inches",
	ft: "feet",
	yd: "yards",
	mi: "miles",
	nmi: "nautical miles",
	mg: "milligrams",
	g: "grams",
	kg: "kilograms",
	t: "metric tons",
	oz: "ounces",
	lb: "pounds",
	st: "stones",
	ml: "milliliters",
	cl: "centiliters",
	dl: "deciliters",
	l: "liters",
	tsp: "teaspoons",
	tbsp: "tablespoons",
	flOz: "fluid ounces",
	cup: "cups",
	pt: "pints",
	qt: "quarts",
	gal: "gallons",
	ms: "milliseconds",
	s: "seconds",
	min: "minutes",
	h: "hours",
	day: "days",
	week: "weeks",
	mm2: "square millimeters",
	cm2: "square centimeters",
	m2: "square meters",
	km2: "square kilometers",
	in2: "square inches",
	ft2: "square feet",
	yd2: "square yards",
	acre: "acres",
	hectare: "hectares",
	mps: "meters per second",
	kmh: "kilometers per hour",
	mph: "miles per hour",
	kn: "knots",
	fps: "feet per second",
	b: "bytes",
	kb: "kilobytes",
	mb: "megabytes",
	gb: "gigabytes",
	tb: "terabytes",
	pb: "petabytes",
	pa: "pascals",
	kpa: "kilopascals",
	bar: "bars",
	atm: "atmospheres",
	psi: "pounds per square inch",
	mmhg: "millimeters of mercury",
	j: "joules",
	kj: "kilojoules",
	cal: "calories",
	kcal: "kilocalories",
	wh: "wattHours",
	kwh: "kilowattHours",
	btu: "British thermal units",
	w: "watts",
	kw: "kilowatts",
	mw: "megawatts",
	hp: "horsepower",
	hz: "hertz",
	khz: "kilohertz",
	mhz: "megahertz",
	ghz: "gigahertz",
	deg: "degrees",
	rad: "radians",
	grad: "gradians",
	turn: "turns",
	c: "Celsius",
	f: "Fahrenheit",
	k: "Kelvin"
};
function convertFnName(from, to) {
	return `${from}To${to.charAt(0).toUpperCase()}${to.slice(1)}`;
}
function generateLinearConversions(category) {
	const result = {};
	const unitNames = Object.keys(category.units);
	for (const from of unitNames) for (const to of unitNames) {
		if (from === to) continue;
		const conversionFactor = category.units[from] / category.units[to];
		const fnName = `${from}To${to.charAt(0).toUpperCase()}${to.slice(1)}`;
		const fromDesc = unitDescriptions[from];
		const toDesc = unitDescriptions[to];
		const seeAlso = [`convert.${convertFnName(to, from)}`, ...unitNames.filter((u) => u !== from && u !== to).map((u) => `convert.${convertFnName(from, u)}`)];
		result[fnName] = {
			evaluate: ([value], sourceCodeInfo) => {
				assertNumber(value, sourceCodeInfo);
				return value * conversionFactor;
			},
			arity: toFixedArity(1),
			docs: {
				type: "(Number) -> Number",
				category: "convert",
				returns: { type: "number" },
				args: { value: {
					type: "number",
					description: `Value in ${fromDesc}`
				} },
				variants: [{ argumentNames: ["value"] }],
				description: `Converts a value from ${fromDesc} (\`${from}\`) to ${toDesc} (\`${to}\`).`,
				seeAlso,
				examples: [`let { ${fnName} } = import("convert");
${fnName}(1)`]
			}
		};
	}
	return result;
}
function generateTemperatureConversions() {
	const result = {};
	for (const from of temperatureUnits) for (const to of temperatureUnits) {
		if (from === to) continue;
		const fnName = `${from}To${to.charAt(0).toUpperCase()}${to.slice(1)}`;
		const fromDesc = unitDescriptions[from];
		const toDesc = unitDescriptions[to];
		const seeAlso = [`convert.${convertFnName(to, from)}`, ...temperatureUnits.filter((u) => u !== from && u !== to).map((u) => `convert.${convertFnName(from, u)}`)];
		result[fnName] = {
			evaluate: ([value], sourceCodeInfo) => {
				assertNumber(value, sourceCodeInfo);
				return celsiusTo(to, toCelsius(from, value));
			},
			arity: toFixedArity(1),
			docs: {
				type: "(Number) -> Number",
				category: "convert",
				returns: { type: "number" },
				args: { value: {
					type: "number",
					description: `Value in ${fromDesc}`
				} },
				variants: [{ argumentNames: ["value"] }],
				description: `Converts a temperature from ${fromDesc} (\`${from}\`) to ${toDesc} (\`${to}\`).`,
				seeAlso,
				examples: [`let { ${fnName} } = import("convert");
${fnName}(100)`]
			}
		};
	}
	return result;
}
const convertFunctions = {
	...generateLinearConversions(lengthUnits),
	...generateLinearConversions(weightUnits),
	...generateLinearConversions(volumeUnits),
	...generateLinearConversions(timeUnits),
	...generateLinearConversions(areaUnits),
	...generateLinearConversions(speedUnits),
	...generateLinearConversions(dataUnits),
	...generateLinearConversions(pressureUnits),
	...generateLinearConversions(energyUnits),
	...generateLinearConversions(powerUnits),
	...generateLinearConversions(frequencyUnits),
	...generateLinearConversions(angleUnits),
	...generateTemperatureConversions()
};
const convertModule = {
	name: "convert",
	description: "Unit conversions for length, weight, temperature, speed, and more.",
	functions: convertFunctions,
	source: convert_default,
	sourcePath: "packages/dvala-engine/src/builtin/modules/convert/convert.dvala",
	docs: moduleDocsFromFunctions(convertFunctions)
};
//#endregion
//#region src/builtin/modules/effectHandler/effectHandler.dvala
var effectHandler_default = "{\n  fallback: (value) -> handler\n    @dvala.error(msg) -> value\n  end,\n  retry: (n, bodyFn) -> loop(remaining = n) -> do\n    with handler\n      @dvala.error(msg) -> if remaining > 0 then\n        recur(remaining - 1)\n      else\n        perform(@dvala.error, msg)\n      end\n    end;\n    bodyFn();\n  end,\n  chooseAll: (bodyFn) -> do\n    let h =\n      handler\n        @choose(options) -> reduce(options, (acc, x) -> acc ++ resume(x), [])\n        transform result ->  [result]\n      end;\n    h(bodyFn);\n  end,\n  chooseFirst: (bodyFn) -> do\n    let h =\n      handler\n        @choose(options) -> resume(first(options))\n      end;\n    h(bodyFn);\n  end,\n  chooseRandom: (bodyFn) -> do\n    let h =\n      handler\n        @choose(options) -> resume(perform(@dvala.random.item, options))\n      end;\n    h(bodyFn);\n  end,\n  chooseTake: (n, bodyFn) -> do\n    let h =\n      handler\n        @choose(options) -> reduce(\n          options,\n          (acc, x) -> if count(acc) >= n then acc else acc ++ resume(x) end,\n          []\n        )\n        transform result ->  [result]\n      end;\n    h(bodyFn);\n  end,\n};\n";
//#endregion
//#region src/builtin/modules/effectHandler/index.ts
const handlerDocs = {
	retry: {
		type: "(Number, (() -> @{dvala.error | r} A)) -> @{dvala.error | r} A",
		wrapper: {
			paramIndex: 1,
			handled: ["dvala.error"],
			introduced: ["dvala.error"]
		},
		category: "effectHandler",
		returns: { type: "any" },
		args: {
			n: { type: "integer" },
			bodyFn: { type: "function" }
		},
		variants: [{ argumentNames: ["n", "bodyFn"] }],
		description: "Executes ``bodyFn()`` and retries up to ``n`` times on `@dvala.error`. On final failure, propagates the error.",
		examples: ["let { retry } = import(\"effectHandler\");\ndo with (handler @dvala.error(msg) -> \"gave up\" end); retry(3, -> 0 / 0) end"]
	},
	fallback: {
		type: "(Unknown) -> Handler<Unknown, Unknown, @{dvala.error}>",
		category: "effectHandler",
		returns: { type: "function" },
		args: { value: { type: "any" } },
		variants: [{ argumentNames: ["value"] }],
		description: "Returns a handler that catches `@dvala.error` and aborts with ``value``. Install with `with fallback(v);` or `fallback(v)(-> body)`.",
		examples: ["let { fallback } = import(\"effectHandler\");\ndo with fallback(0); 0 / 0 end", "let { fallback } = import(\"effectHandler\");\nfallback(0)(-> 0 / 0)"]
	}
};
const chooseDocs = {
	chooseAll: {
		type: "((() -> @{choose | r} A)) -> @{| r} A[]",
		wrapper: {
			paramIndex: 0,
			handled: ["choose"],
			introduced: []
		},
		category: "effectHandler",
		returns: { type: "array" },
		args: { bodyFn: { type: "function" } },
		variants: [{ argumentNames: ["bodyFn"] }],
		description: "Runs ``bodyFn()`` under a nondeterminism handler that explores **all** branches of every `perform(@choose, options)`. Returns an array of all results. Uses multi-shot continuations — `resume` is called once per option.",
		examples: ["let { chooseAll } = import(\"effectHandler\");\nchooseAll(-> perform(@choose, [1, 2, 3]) * 10)", "let { chooseAll } = import(\"effectHandler\");\nchooseAll(-> do\n  let a = perform(@choose, [1, 2]);\n  let b = perform(@choose, [10, 20]);\n  [a, b]\nend)"]
	},
	chooseFirst: {
		type: "((() -> @{choose | r} A)) -> @{| r} A",
		wrapper: {
			paramIndex: 0,
			handled: ["choose"],
			introduced: []
		},
		category: "effectHandler",
		returns: { type: "any" },
		args: { bodyFn: { type: "function" } },
		variants: [{ argumentNames: ["bodyFn"] }],
		description: "Runs ``bodyFn()`` under a nondeterminism handler that always picks the **first** option. Deterministic — equivalent to replacing every `perform(@choose, options)` with `first(options)`.",
		examples: ["let { chooseFirst } = import(\"effectHandler\");\nchooseFirst(-> perform(@choose, [1, 2, 3]) * 10)"]
	},
	chooseRandom: {
		type: "((() -> @{choose | r} A)) -> @{dvala.random.item | r} A",
		wrapper: {
			paramIndex: 0,
			handled: ["choose"],
			introduced: ["dvala.random.item"]
		},
		category: "effectHandler",
		returns: { type: "any" },
		args: { bodyFn: { type: "function" } },
		variants: [{ argumentNames: ["bodyFn"] }],
		description: "Runs ``bodyFn()`` under a nondeterminism handler that picks a **random** option at each `perform(@choose, options)`. Uses `@dvala.random.item` internally.",
		examples: ["let { chooseRandom } = import(\"effectHandler\");\nchooseRandom(-> perform(@choose, [1, 2, 3, 4, 5]))"]
	},
	chooseTake: {
		type: "(Number, (() -> @{choose | r} A)) -> @{| r} A[]",
		wrapper: {
			paramIndex: 1,
			handled: ["choose"],
			introduced: []
		},
		category: "effectHandler",
		returns: { type: "array" },
		args: {
			n: { type: "integer" },
			bodyFn: { type: "function" }
		},
		variants: [{ argumentNames: ["n", "bodyFn"] }],
		description: "Like `chooseAll` but stops after collecting ``n`` results. Branches beyond the limit are not explored.",
		examples: ["let { chooseTake } = import(\"effectHandler\");\nchooseTake(2, -> perform(@choose, [1, 2, 3]) * 10)"]
	}
};
const handlerModule = {
	name: "effectHandler",
	description: "Utilities for creating and composing algebraic effect handlers.",
	functions: {},
	source: effectHandler_default,
	sourcePath: "packages/dvala-engine/src/builtin/modules/effectHandler/effectHandler.dvala",
	docs: {
		...handlerDocs,
		...chooseDocs
	}
};
//#endregion
//#region src/builtin/modules/functional/functional.dvala
var functional_default = "{};\n";
//#endregion
//#region src/builtin/modules/functional/index.ts
const functionalUtilsNormalExpression = {
	juxt: {
		evaluate: (params, sourceCodeInfo) => {
			for (const param of params) assertFunctionLike(param, sourceCodeInfo);
			const arity = getCommonArityFromFunctions([...params]);
			if (arity === null) throw new TypeError$1("All functions must accept the same number of arguments", sourceCodeInfo);
			return {
				[FUNCTION_SYMBOL]: true,
				sourceCodeInfo,
				functionType: "Juxt",
				params,
				arity
			};
		},
		arity: { min: 1 },
		docs: {
			type: "(...((...Unknown) -> Unknown)) -> ((...Unknown) -> Unknown[])",
			category: "functional",
			returns: { type: "function" },
			args: {
				a: { type: "function" },
				b: { type: "function" },
				fun: { type: "function" },
				fns: {
					type: "function",
					rest: true
				}
			},
			variants: [{ argumentNames: ["fun"] }, { argumentNames: ["fun", "fns"] }],
			description: `Takes one or many function and returns a function that is the juxtaposition of those functions.
The returned function takes a variable number of args,
and returns a vector containing the result of applying each function to the args (left-to-right).`,
			seeAlso: ["comp"],
			examples: [
				`let { juxt } = import("functional");
juxt(+, *, min, max)(
  3,
  4,
  6,
)`,
				`let { juxt } = import("functional");
juxt("a", "b")(
  {
    a: 1,
    b: 2,
    c: 3,
    d: 4
  }
)`,
				`let { juxt } = import("functional");
juxt(+, *, min, max) apply range(1, 11)`
			]
		}
	},
	complement: {
		evaluate: ([fn], sourceCodeInfo) => {
			const fun = asFunctionLike(fn, sourceCodeInfo);
			return {
				[FUNCTION_SYMBOL]: true,
				sourceCodeInfo,
				functionType: "Complement",
				function: fun,
				arity: getArityFromFunction(fun)
			};
		},
		arity: toFixedArity(1),
		docs: {
			type: "((...Unknown) -> Unknown) -> ((...Unknown) -> Boolean)",
			category: "functional",
			returns: { type: "function" },
			args: { fun: { type: "function" } },
			variants: [{ argumentNames: ["fun"] }],
			description: "Takes a function `fun` and returns a new function that takes the same arguments as f, has the same effects, if any, and returns the opposite truth value.",
			seeAlso: [
				"comp",
				"functional.everyPred",
				"functional.somePred"
			],
			examples: [
				"let { complement } = import(\"functional\");\ncomplement(>)(1, 3)",
				"let { complement } = import(\"functional\");\ncomplement(<)(1, 3)",
				"let { complement } = import(\"functional\");\ncomplement(+)(1, 3)",
				"let { complement } = import(\"functional\");\ncomplement(+)(0, 0)"
			]
		}
	},
	everyPred: {
		evaluate: (params, sourceCodeInfo) => {
			return {
				[FUNCTION_SYMBOL]: true,
				sourceCodeInfo,
				functionType: "EveryPred",
				params,
				arity: {
					min: 1,
					max: 1
				}
			};
		},
		arity: { min: 1 },
		docs: {
			type: "(...(Unknown -> Boolean)) -> (Unknown -> Boolean)",
			category: "functional",
			returns: { type: "function" },
			args: {
				fun: { type: "function" },
				fns: {
					type: "function",
					rest: true
				}
			},
			variants: [{ argumentNames: ["fun"] }, { argumentNames: ["fun", "fns"] }],
			description: `
Takes a number of predicates and returns a function that returns \`true\` if all predicates
return a truthy value against all of its arguments, else it returns \`false\`.`,
			seeAlso: [
				"functional.somePred",
				"functional.complement",
				"collection.isEvery"
			],
			examples: [`let { everyPred } = import("functional");
everyPred(isString, -> count($) > 3)(
  "Albert",
  "Mojir"
)`, `let { everyPred } = import("functional");
(isString everyPred -> count($) > 3)(
  "Albert",
  "M"
)`],
			hideOperatorForm: true
		}
	},
	somePred: {
		evaluate: (params, sourceCodeInfo) => {
			return {
				[FUNCTION_SYMBOL]: true,
				sourceCodeInfo,
				functionType: "SomePred",
				params,
				arity: {
					min: 1,
					max: 1
				}
			};
		},
		arity: { min: 1 },
		docs: {
			type: "(...(Unknown -> Boolean)) -> (Unknown -> Boolean)",
			category: "functional",
			returns: { type: "function" },
			args: {
				fun: { type: "function" },
				fns: {
					type: "function",
					rest: true
				}
			},
			variants: [{ argumentNames: ["fun"] }, { argumentNames: ["fun", "fns"] }],
			description: "Takes a number of `predicates` and returns a function that returns `true` if at least one of the `predicates` return a truthy `true` value against at least one of its arguments, else it returns `false`.",
			seeAlso: [
				"functional.everyPred",
				"functional.complement",
				"collection.isAny"
			],
			examples: [
				"let { somePred } = import(\"functional\");\nsomePred(isString, -> count($) > 3)(\"Albert\", \"Mojir\")",
				"let { somePred } = import(\"functional\");\nsomePred(isString, -> count($) > 3)(\"a\", \"M\")",
				"let { somePred } = import(\"functional\");\nsomePred(isString, -> count($) > 3)(\"a\", [1, 2, 3])",
				"let { somePred } = import(\"functional\");\nsomePred(isString, -> count($) > 3)([1, 2, 3], [2])"
			],
			hideOperatorForm: true
		}
	},
	fnull: {
		evaluate: (rawParams, sourceCodeInfo) => {
			const fun = asFunctionLike(rawParams.get(0), sourceCodeInfo);
			const params = PersistentVector.from(Array.from({ length: rawParams.size - 1 }, (_, i) => rawParams.get(i + 1)));
			return {
				[FUNCTION_SYMBOL]: true,
				sourceCodeInfo,
				functionType: "Fnull",
				function: fun,
				params,
				arity: getArityFromFunction(fun)
			};
		},
		arity: { min: 2 },
		docs: {
			type: "((...Unknown) -> Unknown, ...Unknown) -> ((...Unknown) -> Unknown)",
			category: "functional",
			returns: { type: "function" },
			args: {
				a: { type: "function" },
				b: { type: "any" },
				fun: { type: "function" },
				arg: { type: "any" },
				args: {
					type: "any",
					rest: true
				}
			},
			variants: [{ argumentNames: ["fun", "arg"] }, { argumentNames: [
				"fun",
				"arg",
				"args"
			] }],
			description: "Takes a function `fun`, and returns a function that calls `fun`, replacing a null argument to the corresponding argument.",
			seeAlso: ["identity", "constantly"],
			examples: [
				"let { fnull } = import(\"functional\");\nfnull(inc, 0)(1)",
				"let { fnull } = import(\"functional\");\nfnull(inc, 0)(null)",
				"let { fnull } = import(\"functional\");\n(inc fnull 0)(null)",
				"let { fnull } = import(\"functional\");\nfnull(+, 1, 2)(null, 0)",
				"let { fnull } = import(\"functional\");\nfnull(+, 1, 2)(0, null)",
				"let { fnull } = import(\"functional\");\nfnull(+, 1, 2)(null, null)",
				"let { fnull } = import(\"functional\");\nfnull(+, 1, 2)(null, null, 3, 4)"
			]
		}
	}
};
const functionalUtilsModule = {
	name: "functional",
	description: "Higher-order function utilities: compose, partial, memoize, and juxtapose.",
	functions: functionalUtilsNormalExpression,
	source: functional_default,
	sourcePath: "packages/dvala-engine/src/builtin/modules/functional/functional.dvala",
	docs: moduleDocsFromFunctions(functionalUtilsNormalExpression)
};
//#endregion
//#region src/builtin/modules/grid/grid.dvala
var grid_default = "do\n  let _transpose = (g) -> map(\n    range(count(first(g))),\n    (i) -> map(range(count(g)), (j) -> nth(nth(g, j), i))\n  );\n  {\n    isCellEvery: (grid, predicate) -> do\n      let cells = flatten(grid, 1);\n      loop(i = 0) -> if i >= count(cells) then\n        true\n      else if !(predicate(nth(cells, i))) then\n        false\n      else\n        recur(i + 1)\n      end;\n    end,\n    isSome: (grid, predicate) -> do\n      let cells = flatten(grid, 1);\n      loop(i = 0) -> if i >= count(cells) then\n        false\n      else if predicate(nth(cells, i)) then\n        true\n      else\n        recur(i + 1)\n      end;\n    end,\n    isEveryRow: (grid, predicate) -> loop(i = 0) -> if i >= count(grid) then\n      true\n    else if !(predicate(nth(grid, i))) then\n      false\n    else\n      recur(i + 1)\n    end,\n    isSomeRow: (grid, predicate) -> loop(i = 0) -> if i >= count(grid) then\n      false\n    else if predicate(nth(grid, i)) then\n      true\n    else\n      recur(i + 1)\n    end,\n    isEveryCol: (grid, predicate) -> do\n      let cols = _transpose(grid);\n      loop(i = 0) -> if i >= count(cols) then\n        true\n      else if !(predicate(nth(cols, i))) then\n        false\n      else\n        recur(i + 1)\n      end;\n    end,\n    isSomeCol: (grid, predicate) -> do\n      let cols = _transpose(grid);\n      loop(i = 0) -> if i >= count(cols) then\n        false\n      else if predicate(nth(cols, i)) then\n        true\n      else\n        recur(i + 1)\n      end;\n    end,\n    generate: (rows, cols, generator) -> map(\n      range(rows),\n      (i) -> map(range(cols), (j) -> generator(i, j))\n    ),\n    cellMap: (...params) -> do\n      let fn = last(params);\n      let grids = dropLast(params, 1);\n      let rows = count(first(grids));\n      let cols = count(first(first(grids)));\n      map(\n        range(rows),\n        (i) -> map(\n          range(cols),\n          (j) -> apply(fn, map(grids, (g) -> nth(nth(g, i), j)))\n        )\n      );\n    end,\n    cellMapi: (grid, fn) -> do\n      let rows = count(grid);\n      let cols = count(first(grid));\n      map(\n        range(rows),\n        (i) -> map(range(cols), (j) -> fn(nth(nth(grid, i), j), i, j))\n      );\n    end,\n    cellReduce: (grid, fn, initialValue) -> reduce(\n      flatten(grid, 1),\n      fn,\n      initialValue\n    ),\n    cellReducei: (grid, fn, initialValue) -> do\n      let rows = count(grid);\n      let cols = count(first(grid));\n      loop(acc = initialValue, i = 0, j = 0) -> if i >= rows then\n        acc\n      else if j >= cols then\n        recur(acc, i + 1, 0)\n      else\n        recur(fn(acc, nth(nth(grid, i), j), i, j), i, j + 1)\n      end;\n    end,\n  };\nend;\n";
//#endregion
//#region src/builtin/modules/grid/docs.ts
const moduleDocs$5 = {
	isCellEvery: {
		type: "(Unknown[][], (Unknown -> Boolean)) -> Boolean",
		category: "grid",
		description: "Checks if all elements in a grid satisfy a predicate. Returns true only if the predicate returns true for every element in the grid.",
		returns: { type: "boolean" },
		args: {
			a: { type: "grid" },
			b: { type: "function" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { isCellEvery } = import(\"grid\");\nisCellEvery([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], isString)",
			"let { isCellEvery } = import(\"grid\");\nisCellEvery([\n  [\"Albert\", \"father\"],\n  [\"Nina\", \"mother\"],\n  [\"Kian\", \"son\"],\n], isString)",
			"let { isCellEvery } = import(\"grid\");\nisCellEvery([\n  [1, 2],\n  [3, 4],\n], isString)"
		],
		seeAlso: [
			"collection.isEvery",
			"grid.isSome",
			"grid.isEveryRow",
			"grid.isEveryCol"
		]
	},
	isSome: {
		type: "(Unknown[][], (Unknown -> Boolean)) -> Boolean",
		category: "grid",
		description: "Checks if any element in a grid satisfies a predicate. Returns true if the predicate returns true for at least one element in the grid.",
		returns: { type: "boolean" },
		args: {
			a: { type: "grid" },
			b: { type: "function" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { isSome } = import(\"grid\");\nisSome([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], isString)",
			"let { isSome } = import(\"grid\");\nisSome([\n  [\"Albert\", \"father\"],\n  [\"Nina\", \"mother\"],\n  [\"Kian\", \"son\"],\n], isString)",
			"let { isSome } = import(\"grid\");\nisSome([\n  [1, 2],\n  [3, 4],\n], isString)"
		],
		seeAlso: [
			"collection.isAny",
			"grid.isCellEvery",
			"grid.isSomeRow",
			"grid.isSomeCol"
		]
	},
	isEveryRow: {
		type: "(Unknown[][], (Unknown[] -> Boolean)) -> Boolean",
		category: "grid",
		description: "Checks if all rows in a grid satisfy a predicate. Returns true only if the predicate returns true for every row in the grid.",
		returns: { type: "boolean" },
		args: {
			a: { type: "grid" },
			b: { type: "function" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { isEveryRow } = import(\"grid\");\nisEveryRow([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], -> isString($[0]))",
			"let { isEveryRow } = import(\"grid\");\nisEveryRow([\n  [\"Albert\", \"father\"],\n  [\"Nina\", \"mother\"],\n  [\"Kian\", \"son\"],\n], -> isString($[0]))",
			"let { isEveryRow } = import(\"grid\");\nisEveryRow([\n  [1, 2],\n  [3, 4],\n], -> isString($[0]))"
		],
		seeAlso: [
			"grid.isSomeRow",
			"grid.isEveryCol",
			"grid.isCellEvery"
		]
	},
	isSomeRow: {
		type: "(Unknown[][], (Unknown[] -> Boolean)) -> Boolean",
		category: "grid",
		description: "Checks if any row in a grid satisfies a predicate. Returns true if the predicate returns true for at least one row in the grid.",
		returns: { type: "boolean" },
		args: {
			a: { type: "grid" },
			b: { type: "function" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { isSomeRow } = import(\"grid\");\nisSomeRow([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], -> $ contains \"Albert\")",
			"let { isSomeRow } = import(\"grid\");\nisSomeRow([\n  [\"Albert\", \"father\"],\n  [\"Nina\", \"mother\"],\n  [\"Kian\", \"son\"],\n], -> $ contains \"Albert\")",
			"let { isSomeRow } = import(\"grid\");\nisSomeRow([\n  [1, 2],\n  [3, 4],\n], -> $ contains \"Albert\")"
		],
		seeAlso: [
			"grid.isEveryRow",
			"grid.isSomeCol",
			"grid.isSome"
		]
	},
	isEveryCol: {
		type: "(Unknown[][], (Unknown[] -> Boolean)) -> Boolean",
		category: "grid",
		description: "Checks if all columns in a grid satisfy a predicate. Returns true only if the predicate returns true for every column in the grid.",
		returns: { type: "boolean" },
		args: {
			a: { type: "grid" },
			b: { type: "function" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { isEveryCol } = import(\"grid\");\nisEveryCol([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], -> isString($[0]))",
			"let { isEveryCol } = import(\"grid\");\nisEveryCol([\n  [\"Albert\", \"father\"],\n  [\"Nina\", \"mother\"],\n  [\"Kian\", \"son\"],\n], -> isString($[0]))",
			"let { isEveryCol } = import(\"grid\");\nisEveryCol([\n  [1, 2],\n  [3, 4],\n], -> isString($[0]))"
		],
		seeAlso: [
			"grid.isSomeCol",
			"grid.isEveryRow",
			"grid.isCellEvery"
		]
	},
	isSomeCol: {
		type: "(Unknown[][], (Unknown[] -> Boolean)) -> Boolean",
		category: "grid",
		description: "Checks if any column in a grid satisfies a predicate. Returns true if the predicate returns true for at least one column in the grid.",
		returns: { type: "boolean" },
		args: {
			a: { type: "grid" },
			b: { type: "function" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { isSomeCol } = import(\"grid\");\nisSomeCol([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], -> $ contains \"Albert\")",
			"let { isSomeCol } = import(\"grid\");\nisSomeCol([\n  [\"Albert\", \"father\"],\n  [\"Nina\", \"mother\"],\n  [\"Kian\", \"son\"],\n], -> $ contains \"Albert\")",
			"let { isSomeCol } = import(\"grid\");\nisSomeCol([\n  [1, 2],\n  [3, 4],\n], -> $ contains \"Albert\")"
		],
		seeAlso: [
			"grid.isEveryCol",
			"grid.isSomeRow",
			"grid.isSome"
		]
	},
	row: {
		type: "(Unknown[][], Number) -> Unknown[]",
		category: "grid",
		description: "Returns the row at index `a` in the grid `b`.",
		returns: { type: "any" },
		args: {
			a: { type: "grid" },
			b: { type: "number" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { row } = import(\"grid\");\nrow([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], 0)",
			"let { row } = import(\"grid\");\nrow([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], 1)",
			"let { row } = import(\"grid\");\nrow([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], 2)"
		],
		seeAlso: ["grid.col", "grid.shape"]
	},
	col: {
		type: "(Unknown[][], Number) -> Unknown[]",
		category: "grid",
		description: "Returns the column at index `a` in the grid `b`.",
		returns: { type: "any" },
		args: {
			a: { type: "grid" },
			b: { type: "number" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { col } = import(\"grid\");\ncol([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], 0)",
			"let { col } = import(\"grid\");\ncol([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], 1)",
			"let { col } = import(\"grid\");\ncol([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], 2)"
		],
		seeAlso: ["grid.row", "grid.shape"]
	},
	shape: {
		type: "(Unknown[][]) -> Number[]",
		category: "grid",
		description: "Returns the shape of the grid `g` as a `vector` of two numbers, where the first number is the number of rows and the second number is the number of columns.",
		returns: { type: "vector" },
		args: { g: {
			type: "grid",
			description: "The grid to get the shape of."
		} },
		variants: [{ argumentNames: ["g"] }],
		examples: [
			"let { shape } = import(\"grid\");\nshape([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n])",
			"let { shape } = import(\"grid\");\nshape([\n  [\"Albert\", \"father\"],\n  [\"Nina\", \"mother\"],\n  [\"Kian\", \"son\"],\n])",
			"let { shape } = import(\"grid\");\nshape([\n  [1, 2],\n  [3, 4],\n])"
		],
		seeAlso: [
			"grid.row",
			"grid.col",
			"grid.reshape"
		]
	},
	fill: {
		type: "(Number, Number, Unknown) -> Unknown[][]",
		category: "grid",
		description: "Creates a grid of the specified size, filled with the specified value.",
		returns: { type: "grid" },
		args: {
			rows: {
				type: "integer",
				description: "The number of rows in the grid."
			},
			cols: {
				type: "integer",
				description: "The number of columns in the grid."
			},
			value: {
				type: "any",
				description: "The value to fill the grid with."
			}
		},
		variants: [{ argumentNames: [
			"rows",
			"cols",
			"value"
		] }],
		examples: ["let { fill } = import(\"grid\");\nfill(2, 3, 0)", "let { fill } = import(\"grid\");\nfill(2, 3, \"x\")"],
		seeAlso: ["grid.generate", "grid.fromArray"]
	},
	generate: {
		type: "(Number, Number, (Number, Number) -> Unknown) -> Unknown[][]",
		category: "grid",
		description: "Generates a grid of the specified size, where each element is generated by the provided function.",
		returns: { type: "grid" },
		args: {
			rows: {
				type: "number",
				description: "The number of rows in the grid."
			},
			cols: {
				type: "number",
				description: "The number of columns in the grid."
			},
			generator: {
				type: "function",
				description: "The function to generate the grid. It takes two arguments: the row index and the column index."
			}
		},
		variants: [{ argumentNames: [
			"rows",
			"cols",
			"generator"
		] }],
		examples: ["let { generate } = import(\"grid\");\ngenerate(3, 3, (i, j) -> i + j)"],
		seeAlso: ["grid.fill", "grid.fromArray"]
	},
	reshape: {
		type: "(Unknown[][], Number) -> Unknown[][]",
		category: "grid",
		description: "Reshapes the grid `a` into a new grid with the specified number of rows `b`. The number of columns is automatically calculated based on the total number of elements in the grid.",
		returns: { type: "grid" },
		args: {
			a: { type: "grid" },
			b: { type: "number" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: ["let { reshape } = import(\"grid\");\nreshape([\n  [\"Albert\", \"father\"],\n  [\"Nina\", \"mother\"],\n  [\"Kian\", \"son\"],\n], 2)"],
		seeAlso: ["grid.shape", "grid.fromArray"]
	},
	transpose: {
		type: "(Unknown[][]) -> Unknown[][]",
		category: "grid",
		description: "Transposes the grid `g`, swapping its rows and columns.",
		returns: { type: "grid" },
		args: { g: {
			type: "grid",
			description: "The grid to transpose."
		} },
		variants: [{ argumentNames: ["g"] }],
		examples: [
			"let { transpose } = import(\"grid\");\ntranspose([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n])",
			"let { transpose } = import(\"grid\");\ntranspose([\n  [\"Albert\", \"father\"],\n  [\"Nina\", \"mother\"],\n  [\"Kian\", \"son\"],\n])",
			"let { transpose } = import(\"grid\");\ntranspose([\n  [1, 2],\n  [3, 4],\n])"
		],
		seeAlso: [
			"grid.flipH",
			"grid.flipV",
			"grid.rotate"
		]
	},
	flipH: {
		type: "(Unknown[][]) -> Unknown[][]",
		category: "grid",
		description: "Flips the grid `g` horizontally.",
		returns: { type: "grid" },
		args: { g: {
			type: "grid",
			description: "The grid to flip horizontally."
		} },
		variants: [{ argumentNames: ["g"] }],
		examples: [
			"let { flipH } = import(\"grid\");\nflipH([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n])",
			"let { flipH } = import(\"grid\");\nflipH([\n  [\"Albert\", \"father\"],\n  [\"Nina\", \"mother\"],\n  [\"Kian\", \"son\"],\n])",
			"let { flipH } = import(\"grid\");\nflipH([\n  [1, 2],\n  [3, 4],\n])"
		],
		seeAlso: [
			"grid.flipV",
			"grid.transpose",
			"grid.rotate"
		]
	},
	flipV: {
		type: "(Unknown[][]) -> Unknown[][]",
		category: "grid",
		description: "Flips the grid `g` vertically.",
		returns: { type: "grid" },
		args: { g: {
			type: "grid",
			description: "The grid to flip vertically."
		} },
		variants: [{ argumentNames: ["g"] }],
		examples: [
			"let { flipV } = import(\"grid\");\nflipV([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n])",
			"let { flipV } = import(\"grid\");\nflipV([\n  [\"Albert\", \"father\"],\n  [\"Nina\", \"mother\"],\n  [\"Kian\", \"son\"],\n])",
			"let { flipV } = import(\"grid\");\nflipV([\n  [1, 2],\n  [3, 4],\n])"
		],
		seeAlso: [
			"grid.flipH",
			"grid.transpose",
			"grid.rotate"
		]
	},
	rotate: {
		type: "(Unknown[][], Number) -> Unknown[][]",
		category: "grid",
		description: "Rotates the grid `g` by the specified angle. The angle is given in terms of 90-degree rotations. Positive values rotate the grid clockwise, while negative values rotate it counterclockwise.",
		returns: { type: "grid" },
		args: {
			a: { type: "grid" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { rotate } = import(\"grid\");\nrotate([\n  [1, 2],\n  [3, 4],\n], 1)",
			"let { rotate } = import(\"grid\");\nrotate([\n  [1, 2],\n  [3, 4],\n], 2)",
			"let { rotate } = import(\"grid\");\nrotate([\n  [1, 2],\n  [3, 4],\n], 3)",
			"let { rotate } = import(\"grid\");\nrotate([\n  [1, 2],\n  [3, 4],\n], 4)",
			"let { rotate } = import(\"grid\");\nrotate([\n  [1, 2],\n  [3, 4],\n], -1)",
			"let { rotate } = import(\"grid\");\nrotate([\n  [1, 2],\n  [3, 4],\n], -2)",
			"let { rotate } = import(\"grid\");\nrotate([\n  [1, 2],\n  [3, 4],\n], -3)"
		],
		seeAlso: [
			"grid.transpose",
			"grid.flipH",
			"grid.flipV"
		]
	},
	crop: {
		type: "(Unknown[][], Number[]) -> Unknown[][]",
		category: "grid",
		description: "Crops the grid `g` from the starting index `begin` to the optional ending index `stop`. The crop is inclusive of the starting index and exclusive of the ending index.",
		returns: { type: "grid" },
		args: {
			g: {
				type: "grid",
				description: "The grid to slice."
			},
			begin: {
				type: "vector",
				description: "The starting index of the slice as a vector of two numbers: `[row, col]`."
			},
			stop: {
				type: "vector",
				description: "Optional ending index of the slice as a vector of two numbers: `[row, col]`."
			}
		},
		variants: [{ argumentNames: ["g", "begin"] }, { argumentNames: [
			"g",
			"begin",
			"stop"
		] }],
		examples: ["let { crop } = import(\"grid\");\ncrop([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], [1, 1], [2, 2])", "let { crop } = import(\"grid\");\ncrop([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], [1, 1])"],
		hideOperatorForm: true,
		seeAlso: ["grid.sliceRows", "grid.sliceCols"]
	},
	sliceRows: {
		type: "(Unknown[][], Number) -> Unknown[][]",
		category: "grid",
		description: "Slices rows of the grid `g` from the starting index `begin` to the optional ending index `stop`. The slice is inclusive of the starting index and exclusive of the ending index.",
		returns: { type: "grid" },
		args: {
			g: {
				type: "grid",
				description: "The grid to slice."
			},
			begin: {
				type: "number",
				description: "The starting index of the slice."
			},
			stop: {
				type: "number",
				description: "Optional ending index of the slice."
			}
		},
		variants: [{ argumentNames: ["g", "begin"] }, { argumentNames: [
			"g",
			"begin",
			"stop"
		] }],
		examples: ["let { sliceRows } = import(\"grid\");\nsliceRows([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], 1, 2)", "let { sliceRows } = import(\"grid\");\nsliceRows([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], 1)"],
		hideOperatorForm: true,
		seeAlso: [
			"grid.crop",
			"grid.sliceCols",
			"grid.spliceRows"
		]
	},
	sliceCols: {
		type: "(Unknown[][], Number) -> Unknown[][]",
		category: "grid",
		description: "Slices columns of the grid `g` from the starting index `begin` to the optional ending index `stop`. The slice is inclusive of the starting index and exclusive of the ending index.",
		returns: { type: "grid" },
		args: {
			g: {
				type: "grid",
				description: "The grid to slice."
			},
			begin: {
				type: "number",
				description: "The starting index of the slice."
			},
			stop: {
				type: "number",
				description: "Optional ending index of the slice."
			}
		},
		variants: [{ argumentNames: ["g", "begin"] }, { argumentNames: [
			"g",
			"begin",
			"stop"
		] }],
		examples: ["let { sliceCols } = import(\"grid\");\nsliceCols([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], 1, 2)", "let { sliceCols } = import(\"grid\");\nsliceCols([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], 1)"],
		hideOperatorForm: true,
		seeAlso: [
			"grid.crop",
			"grid.sliceRows",
			"grid.spliceCols"
		]
	},
	spliceRows: {
		type: "(Unknown[][], Number, Number, ...Unknown[]) -> Unknown[][]",
		category: "grid",
		description: "Splices rows of the grid `g` starting from the index `begin`. Deletes `deleteCount` rows and inserts the specified `items` at that position.",
		returns: { type: "grid" },
		args: {
			g: {
				type: "grid",
				description: "The grid to splice."
			},
			begin: {
				type: "number",
				description: "The starting index of the splice."
			},
			deleteCount: {
				type: "number",
				description: "The number of rows to delete."
			},
			items: {
				type: "array",
				rest: true,
				description: "The rows to insert."
			}
		},
		variants: [{ argumentNames: [
			"g",
			"begin",
			"deleteCount"
		] }, { argumentNames: [
			"g",
			"begin",
			"deleteCount",
			"items"
		] }],
		examples: ["let { spliceRows } = import(\"grid\");\nspliceRows([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], 1, 2)", "let { spliceRows } = import(\"grid\");\nspliceRows([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], 1, 1, [\"Nazanin\", \"mother\", 40])"],
		hideOperatorForm: true,
		seeAlso: ["grid.spliceCols", "grid.sliceRows"]
	},
	spliceCols: {
		type: "(Unknown[][], Number, Number, ...Unknown[]) -> Unknown[][]",
		category: "grid",
		description: "Splices columns of the grid `g` starting from the index `begin`. Deletes `deleteCount` columns and inserts the specified `items` at that position.",
		returns: { type: "grid" },
		args: {
			g: {
				type: "grid",
				description: "The grid to splice."
			},
			begin: {
				type: "number",
				description: "The starting index of the splice."
			},
			deleteCount: {
				type: "number",
				description: "The number of columns to delete."
			},
			items: {
				type: "array",
				rest: true,
				description: "The columns to insert."
			}
		},
		variants: [{ argumentNames: [
			"g",
			"begin",
			"deleteCount"
		] }, { argumentNames: [
			"g",
			"begin",
			"deleteCount",
			"items"
		] }],
		examples: ["let { spliceCols } = import(\"grid\");\nspliceCols([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], 1, 2)", "let { spliceCols } = import(\"grid\");\nspliceCols([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], 1, 1, [\"f\", \"m\", \"s\"])"],
		hideOperatorForm: true,
		seeAlso: ["grid.spliceRows", "grid.sliceCols"]
	},
	concatRows: {
		type: "(Unknown[][], Unknown[][]) -> Unknown[][]",
		category: "grid",
		description: "Concatenates two grids `a` and `b` by rows. The number of columns in both grids must be the same.",
		returns: { type: "grid" },
		args: {
			a: { type: "grid" },
			b: { type: "grid" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: ["let { concatRows } = import(\"grid\");\nconcatRows([\n  [\"Albert\", \"father\"],\n  [\"Nina\", \"mother\"],\n  [\"Kian\", \"son\"],\n], [\n  [1, 2],\n  [3, 4],\n])"],
		seeAlso: ["grid.concatCols", "grid.pushRows"]
	},
	concatCols: {
		type: "(Unknown[][], Unknown[][]) -> Unknown[][]",
		category: "grid",
		description: "Concatenates two grids `a` and `b` by columns. The number of rows in both grids must be the same.",
		returns: { type: "grid" },
		args: {
			a: { type: "grid" },
			b: { type: "grid" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: ["let { concatCols } = import(\"grid\");\nconcatCols([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], [\n  [\"Albert\", \"father\"],\n  [\"Nina\", \"mother\"],\n  [\"Kian\", \"son\"],\n])"],
		seeAlso: ["grid.concatRows", "grid.pushCols"]
	},
	cellMap: {
		type: "(Unknown[][], (Unknown -> Unknown)) -> Unknown[][]",
		category: "grid",
		description: "Maps a function `a` over each element of the grid `b`, returning a new grid with the results.",
		returns: { type: "grid" },
		args: {
			a: { type: "grid" },
			b: { type: "function" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: ["let { cellMap } = import(\"grid\");\ncellMap([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], str)"],
		seeAlso: [
			"map",
			"grid.cellMapi",
			"grid.cellReduce"
		]
	},
	cellMapi: {
		type: "(Unknown[][], (Unknown, Number, Number) -> Unknown) -> Unknown[][]",
		category: "grid",
		description: "Maps a function `a` over each element of the grid `b`, passing the row and column index as additional arguments to the function.",
		returns: { type: "grid" },
		args: {
			a: { type: "grid" },
			b: { type: "function" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: ["let { cellMapi } = import(\"grid\");\ncellMapi([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], -> $ ++ \"(\" ++ $2 ++ \", \" ++ $3 ++ \")\")"],
		seeAlso: [
			"grid.cellMap",
			"grid.cellReducei",
			"map"
		]
	},
	cellReduce: {
		type: "(Unknown[][], (Unknown, Unknown) -> Unknown, Unknown) -> Unknown",
		category: "grid",
		description: "Reduces the grid `a` using the function `b`, returning a single value.",
		returns: { type: "any" },
		args: {
			g: {
				type: "grid",
				description: "The grid to reduce."
			},
			f: {
				type: "function",
				description: "The function to reduce the grid. It takes two arguments: the accumulator and the current element."
			},
			initialValue: {
				type: "any",
				description: "The initial value for the accumulator."
			}
		},
		variants: [{ argumentNames: [
			"g",
			"f",
			"initialValue"
		] }],
		examples: ["// Using \"as\" alias because \"reduce\" shadows a builtin function\nlet { cellReduce } = import(\"grid\");\ncellReduce([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], ++, \"\")"],
		seeAlso: [
			"reduce",
			"grid.cellReducei",
			"grid.cellMap"
		]
	},
	cellReducei: {
		type: "(Unknown[][], (Unknown, Unknown, Number, Number) -> Unknown, Unknown) -> Unknown",
		category: "grid",
		description: "Reduces the grid `a` using the function `b`, passing the row and column indices as additional arguments to the function.",
		returns: { type: "any" },
		args: {
			g: {
				type: "grid",
				description: "The grid to reduce."
			},
			f: {
				type: "function",
				description: "The function to reduce the grid. It takes four arguments: the accumulator, the current element, the row index, and the column index."
			},
			initialValue: {
				type: "any",
				description: "The initial value for the accumulator."
			}
		},
		variants: [{ argumentNames: [
			"g",
			"f",
			"initialValue"
		] }],
		examples: ["// Using \"as\" alias because \"reducei\" shadows a builtin function\nlet { cellReducei } = import(\"grid\");\ncellReducei([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], ++, \"\")"],
		seeAlso: [
			"grid.cellReduce",
			"grid.cellMapi",
			"reduce"
		]
	},
	pushRows: {
		type: "(Unknown[][], ...Unknown[]) -> Unknown[][]",
		category: "grid",
		description: "Pushes the specified rows into the grid `g` and returns the new grid.",
		returns: { type: "grid" },
		args: {
			g: {
				type: "grid",
				description: "The grid to push rows into."
			},
			rows: {
				type: "array",
				rest: true,
				description: "The rows to push into the grid."
			}
		},
		variants: [{ argumentNames: ["g", "rows"] }],
		examples: ["let { pushRows } = import(\"grid\");\npushRows([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], [\"Nazanin\", \"mother\", 40])"],
		hideOperatorForm: true,
		seeAlso: [
			"grid.unshiftRows",
			"grid.popRow",
			"grid.shiftRow",
			"grid.concatRows"
		]
	},
	unshiftRows: {
		type: "(Unknown[][], ...Unknown[]) -> Unknown[][]",
		category: "grid",
		description: "Unshifts the specified rows into the grid `g` and returns the new grid.",
		returns: { type: "grid" },
		args: {
			g: {
				type: "grid",
				description: "The grid to unshift rows into."
			},
			rows: {
				type: "array",
				rest: true,
				description: "The rows to unshift into the grid."
			}
		},
		variants: [{ argumentNames: ["g", "rows"] }],
		examples: ["let { unshiftRows } = import(\"grid\");\nunshiftRows([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], [\"Nazanin\", \"mother\", 40])"],
		hideOperatorForm: true,
		seeAlso: [
			"grid.pushRows",
			"grid.shiftRow",
			"grid.popRow"
		]
	},
	popRow: {
		type: "(Unknown[][]) -> Unknown[][]",
		category: "grid",
		description: "Pops the last row from the grid `g` and returns the new grid.",
		returns: { type: "grid" },
		args: { g: {
			type: "grid",
			description: "The grid to pop a row from."
		} },
		variants: [{ argumentNames: ["g"] }],
		examples: ["let { popRow } = import(\"grid\");\npopRow([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n])"],
		seeAlso: [
			"grid.shiftRow",
			"grid.pushRows",
			"grid.unshiftRows"
		]
	},
	shiftRow: {
		type: "(Unknown[][]) -> Unknown[][]",
		category: "grid",
		description: "Shifts the first row from the grid `g` and returns the new grid.",
		returns: { type: "grid" },
		args: { g: {
			type: "grid",
			description: "The grid to shift a row from."
		} },
		variants: [{ argumentNames: ["g"] }],
		examples: ["let { shiftRow } = import(\"grid\");\nshiftRow([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n])"],
		seeAlso: [
			"grid.popRow",
			"grid.pushRows",
			"grid.unshiftRows"
		]
	},
	pushCols: {
		type: "(Unknown[][], ...Unknown[]) -> Unknown[][]",
		category: "grid",
		description: "Pushes the specified columns into the grid `g` and returns the new grid.",
		returns: { type: "grid" },
		args: {
			g: {
				type: "grid",
				description: "The grid to push columns into."
			},
			cols: {
				type: "array",
				rest: true,
				description: "The columns to push into the grid."
			}
		},
		variants: [{ argumentNames: ["g", "cols"] }],
		examples: ["let { pushCols } = import(\"grid\");\npushCols([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], [\"f\", \"m\", \"s\"])"],
		hideOperatorForm: true,
		seeAlso: [
			"grid.unshiftCols",
			"grid.popCol",
			"grid.shiftCol",
			"grid.concatCols"
		]
	},
	unshiftCols: {
		type: "(Unknown[][], ...Unknown[]) -> Unknown[][]",
		category: "grid",
		description: "Unshifts the specified columns into the grid `g` and returns the new grid.",
		returns: { type: "grid" },
		args: {
			g: {
				type: "grid",
				description: "The grid to unshift columns into."
			},
			cols: {
				type: "array",
				rest: true,
				description: "The columns to unshift into the grid."
			}
		},
		variants: [{ argumentNames: ["g", "cols"] }],
		examples: ["let { unshiftCols } = import(\"grid\");\nunshiftCols([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n], [\"f\", \"m\", \"s\"])"],
		hideOperatorForm: true,
		seeAlso: [
			"grid.pushCols",
			"grid.shiftCol",
			"grid.popCol"
		]
	},
	popCol: {
		type: "(Unknown[][]) -> Unknown[][]",
		category: "grid",
		description: "Pops the last column from the grid `g` and returns the new grid.",
		returns: { type: "grid" },
		args: { g: {
			type: "grid",
			description: "The grid to pop a column from."
		} },
		variants: [{ argumentNames: ["g"] }],
		examples: ["let { popCol } = import(\"grid\");\npopCol([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n])"],
		seeAlso: [
			"grid.shiftCol",
			"grid.pushCols",
			"grid.unshiftCols"
		]
	},
	shiftCol: {
		type: "(Unknown[][]) -> Unknown[][]",
		category: "grid",
		description: "Shifts the first column from the grid `g` and returns the new grid.",
		returns: { type: "grid" },
		args: { g: {
			type: "grid",
			description: "The grid to shift a column from."
		} },
		variants: [{ argumentNames: ["g"] }],
		examples: ["let { shiftCol } = import(\"grid\");\nshiftCol([\n  [\"Albert\", \"father\", 10],\n  [\"Nina\", \"mother\", 20],\n  [\"Kian\", \"son\", 30],\n])"],
		seeAlso: [
			"grid.popCol",
			"grid.pushCols",
			"grid.unshiftCols"
		]
	},
	fromArray: {
		type: "(Unknown[], Number) -> Unknown[][]",
		category: "grid",
		description: "Creates a grid from a flat array with specified dimensions. The array is reshaped into the specified number of rows, and the number of columns is automatically calculated based on the total number of elements in the array.",
		returns: { type: "grid" },
		args: {
			a: { type: "array" },
			b: { type: "number" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: ["let { fromArray } = import(\"grid\");\nfromArray([1, 2, 3, 4], 2)", "let { fromArray } = import(\"grid\");\nfromArray([1, 2, 3, 4], 4)"],
		seeAlso: [
			"grid.fill",
			"grid.generate",
			"grid.reshape"
		]
	}
};
//#endregion
//#region src/builtin/modules/grid/fromArray.ts
/**
* Creates a grid from a flat array with specified dimensions
*
* @param flatArray The flat array of values
* @param rows Number of rows in the resulting grid
* @returns A 2D array representing the grid
*/
function fromArray(flatArray, rows) {
	const grid = [];
	const cols = flatArray.length / rows;
	for (let i = 0; i < rows; i++) {
		const start = i * cols;
		const end = start + cols;
		grid.push(flatArray.slice(start, end));
	}
	return grid;
}
//#endregion
//#region src/builtin/modules/grid/transpose.ts
function transpose(grid) {
	const result = [];
	for (let i = 0; i < grid[0].length; i += 1) {
		const row = [];
		for (let j = 0; j < grid.length; j += 1) row.push(grid[j][i]);
		result.push(row);
	}
	return result;
}
//#endregion
//#region src/builtin/modules/grid/index.ts
function toAnyGrid(val) {
	return val;
}
const gridFunctions = {
	isCellEvery: {
		evaluate: () => {
			throw new Error("isCellEvery: Dvala implementation should be used instead");
		},
		arity: toFixedArity(2)
	},
	isSome: {
		evaluate: () => {
			throw new Error("isSome: Dvala implementation should be used instead");
		},
		arity: toFixedArity(2)
	},
	isEveryRow: {
		evaluate: () => {
			throw new Error("isEveryRow: Dvala implementation should be used instead");
		},
		arity: toFixedArity(2)
	},
	isSomeRow: {
		evaluate: () => {
			throw new Error("isSomeRow: Dvala implementation should be used instead");
		},
		arity: toFixedArity(2)
	},
	isEveryCol: {
		evaluate: () => {
			throw new Error("isEveryCol: Dvala implementation should be used instead");
		},
		arity: toFixedArity(2)
	},
	isSomeCol: {
		evaluate: () => {
			throw new Error("isSomeCol: Dvala implementation should be used instead");
		},
		arity: toFixedArity(2)
	},
	row: {
		evaluate: (params, sourceCodeInfo) => {
			const [grid_, row] = params;
			const grid = assertGrid(grid_, sourceCodeInfo);
			assertNumber(row, sourceCodeInfo, {
				integer: true,
				nonNegative: true,
				lt: grid.length
			});
			return toAnyGrid(grid[row]);
		},
		arity: toFixedArity(2)
	},
	col: {
		evaluate: (params, sourceCodeInfo) => {
			const [grid_, col] = params;
			const grid = assertGrid(grid_, sourceCodeInfo);
			assertNumber(col, sourceCodeInfo, {
				integer: true,
				nonNegative: true,
				lt: grid[0].length
			});
			return toAnyGrid(grid.map((row) => row[col]));
		},
		arity: toFixedArity(2)
	},
	shape: {
		evaluate: (params, sourceCodeInfo) => {
			const grid = assertGrid(params.get(0), sourceCodeInfo);
			return toAnyGrid([grid.length, grid[0].length]);
		},
		arity: toFixedArity(1)
	},
	fill: {
		evaluate: (params, sourceCodeInfo) => {
			const [rows, cols, value] = params;
			assertNumber(rows, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			assertNumber(cols, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			assertAny(value, sourceCodeInfo);
			const result = [];
			for (let i = 0; i < rows; i += 1) {
				const row = [];
				for (let j = 0; j < cols; j += 1) row.push(value);
				result.push(row);
			}
			return toAnyGrid(result);
		},
		arity: toFixedArity(3)
	},
	generate: {
		evaluate: () => {
			throw new Error("generate: Dvala implementation should be used instead");
		},
		arity: toFixedArity(3)
	},
	reshape: {
		evaluate: (params, sourceCodeInfo) => {
			const [grid_, rows] = params;
			const grid = assertGrid(grid_, sourceCodeInfo);
			assertNumber(rows, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			const flatTable = grid.flat();
			if (flatTable.length % rows !== 0) throw new RuntimeError(`The number of elements in the grid must be divisible by rows, but got ${flatTable.length} and ${rows}`, sourceCodeInfo);
			const cols = flatTable.length / rows;
			const result = [];
			for (let i = 0; i < rows; i += 1) {
				const row = [];
				for (let j = 0; j < cols; j += 1) row.push(flatTable[i * cols + j]);
				result.push(row);
			}
			return toAnyGrid(result);
		},
		arity: toFixedArity(2)
	},
	transpose: {
		evaluate: (params, sourceCodeInfo) => {
			return toAnyGrid(transpose(assertGrid(params.get(0), sourceCodeInfo)));
		},
		arity: toFixedArity(1)
	},
	flipH: {
		evaluate: (params, sourceCodeInfo) => {
			return toAnyGrid(assertGrid(params.get(0), sourceCodeInfo).map((row) => row.reverse()));
		},
		arity: toFixedArity(1)
	},
	flipV: {
		evaluate: (params, sourceCodeInfo) => {
			return toAnyGrid(assertGrid(params.get(0), sourceCodeInfo).reverse());
		},
		arity: toFixedArity(1)
	},
	rotate: {
		evaluate: (params, sourceCodeInfo) => {
			const [grid_, times] = params;
			const grid = assertGrid(grid_, sourceCodeInfo);
			assertNumber(times, sourceCodeInfo, { integer: true });
			const t = (times % 4 + 4) % 4;
			if (t === 0 || grid.length === 0) return toAnyGrid(grid.map((row) => [...row]));
			const height = grid.length;
			const width = grid[0].length;
			let result;
			switch (t) {
				case 1:
					result = Array(width).fill(null).map(() => Array(height).fill(null));
					for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) result[x][height - 1 - y] = grid[y][x];
					break;
				case 2:
					result = Array(height).fill(null).map(() => Array(width).fill(null));
					for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) result[height - 1 - y][width - 1 - x] = grid[y][x];
					break;
				case 3:
					result = Array(width).fill(null).map(() => Array(height).fill(null));
					for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) result[width - 1 - x][y] = grid[y][x];
					break;
			}
			return toAnyGrid(result);
		},
		arity: toFixedArity(2)
	},
	crop: {
		evaluate: (params, sourceCodeInfo) => {
			const [grid_, start_, end_] = params;
			const grid = assertGrid(grid_, sourceCodeInfo);
			const start = assertVector(start_, sourceCodeInfo);
			if (start.length !== 2) throw new RuntimeError(`The start vector must have 2 elements, but got ${start.length}`, sourceCodeInfo);
			const [rowStart, colStart] = start;
			assertNumber(rowStart, sourceCodeInfo, {
				integer: true,
				nonNegative: true,
				lt: grid.length
			});
			assertNumber(colStart, sourceCodeInfo, {
				integer: true,
				nonNegative: true,
				lt: grid[0].length
			});
			const end = assertVector(end_ ?? [grid.length, grid[0].length], sourceCodeInfo);
			if (end.length !== 2) throw new RuntimeError(`The end vector must have 2 elements, but got ${end.length}`, sourceCodeInfo);
			const [rowEnd, colEnd] = end;
			assertNumber(rowEnd, sourceCodeInfo, {
				gt: rowStart,
				lte: grid.length
			});
			assertNumber(colEnd, sourceCodeInfo, {
				gt: colStart,
				lte: grid[0].length
			});
			const result = [];
			for (let i = rowStart; i < rowEnd; i += 1) {
				const row = [];
				for (let j = colStart; j < colEnd; j += 1) row.push(grid[i][j]);
				result.push(row);
			}
			return toAnyGrid(result);
		},
		arity: {
			min: 2,
			max: 3
		}
	},
	sliceRows: {
		evaluate: (params, sourceCodeInfo) => {
			const [grid_, rowStart, rowEnd] = params;
			const grid = assertGrid(grid_, sourceCodeInfo);
			if (typeof rowEnd === "undefined") {
				assertNumber(rowStart, sourceCodeInfo, {
					integer: true,
					lte: grid.length,
					gte: -grid.length
				});
				if (rowStart < 0) return toAnyGrid(grid.slice(grid.length + rowStart));
				return toAnyGrid(grid.slice(rowStart));
			}
			assertNumber(rowStart, sourceCodeInfo, {
				integer: true,
				nonNegative: true,
				lte: grid.length
			});
			assertNumber(rowEnd, sourceCodeInfo, { integer: true });
			const end = rowEnd < 0 ? grid.length + rowEnd : rowEnd;
			assertNumber(end, sourceCodeInfo, {
				gt: rowStart,
				lte: grid.length
			});
			return toAnyGrid(grid.slice(rowStart, end));
		},
		arity: {
			min: 2,
			max: 3
		}
	},
	sliceCols: {
		evaluate: (params, sourceCodeInfo) => {
			const [grid_, colStart, colEnd] = params;
			const trMatrix = transpose(assertGrid(grid_, sourceCodeInfo));
			if (typeof colEnd === "undefined") {
				assertNumber(colStart, sourceCodeInfo, {
					integer: true,
					lte: trMatrix.length,
					gte: -trMatrix.length
				});
				if (colStart < 0) return toAnyGrid(transpose(trMatrix.slice(trMatrix.length + colStart)));
				return toAnyGrid(transpose(trMatrix.slice(colStart)));
			}
			assertNumber(colStart, sourceCodeInfo, {
				integer: true,
				nonNegative: true,
				lte: trMatrix.length
			});
			assertNumber(colEnd, sourceCodeInfo, { integer: true });
			const end = colEnd < 0 ? trMatrix.length + colEnd : colEnd;
			assertNumber(end, sourceCodeInfo, {
				gt: colStart,
				lte: trMatrix.length
			});
			return toAnyGrid(transpose(trMatrix.slice(colStart, end)));
		},
		arity: {
			min: 2,
			max: 3
		}
	},
	spliceRows: {
		evaluate: (params, sourceCodeInfo) => {
			const [grid_, rowStart, rowDeleteCount, ...rows_] = params;
			const grid = assertGrid(grid_, sourceCodeInfo);
			assertNumber(rowStart, sourceCodeInfo, {
				integer: true,
				nonNegative: true,
				lte: grid.length
			});
			assertNumber(rowDeleteCount, sourceCodeInfo, {
				integer: true,
				nonNegative: true
			});
			const validatedRows = rows_.length !== 0 ? (() => {
				const rows = assertGrid(rows_, sourceCodeInfo);
				rows.every((row) => {
					if (grid[0].length !== row.length) throw new RuntimeError(`All rows must have the same length as the number of columns in grid, but got ${row.length}`, sourceCodeInfo);
					return true;
				});
				return rows;
			})() : [];
			const result = [];
			for (let i = 0; i < rowStart; i += 1) result.push(grid[i]);
			if (validatedRows.length > 0) result.push(...validatedRows);
			for (let i = rowStart + rowDeleteCount; i < grid.length; i += 1) result.push(grid[i]);
			return toAnyGrid(result);
		},
		arity: { min: 3 }
	},
	spliceCols: {
		evaluate: (params, sourceCodeInfo) => {
			const [grid_, colStart, colDeleteCount, ...cols_] = params;
			const trMatrix = transpose(assertGrid(grid_, sourceCodeInfo));
			assertNumber(colStart, sourceCodeInfo, {
				integer: true,
				nonNegative: true,
				lte: trMatrix.length
			});
			assertNumber(colDeleteCount, sourceCodeInfo, {
				integer: true,
				nonNegative: true
			});
			const validatedCols = cols_.length !== 0 ? (() => {
				const cols = assertGrid(cols_, sourceCodeInfo);
				cols.every((row) => {
					if (trMatrix[0].length !== row.length) throw new RuntimeError(`All rows must have the same length as the number of rows in grid, but got ${row.length}`, sourceCodeInfo);
					return true;
				});
				return cols;
			})() : [];
			const result = [];
			for (let i = 0; i < colStart; i += 1) result.push(trMatrix[i]);
			result.push(...validatedCols);
			for (let i = colStart + colDeleteCount; i < trMatrix.length; i += 1) result.push(trMatrix[i]);
			return toAnyGrid(transpose(result));
		},
		arity: { min: 3 }
	},
	concatRows: {
		evaluate: (params, sourceCodeInfo) => {
			const paramsArr = [...params].map((grid) => assertGrid(grid, sourceCodeInfo));
			const cols = paramsArr[0][0].length;
			paramsArr.slice(1).every((grid) => {
				if (grid[0].length !== cols) throw new RuntimeError(`All grids must have the same number of columns, but got ${cols} and ${grid[0].length}`, sourceCodeInfo);
				return true;
			});
			const result = [];
			paramsArr.forEach((grid) => {
				grid.forEach((row) => {
					result.push(row);
				});
			});
			return toAnyGrid(result);
		},
		arity: { min: 1 }
	},
	concatCols: {
		evaluate: (params, sourceCodeInfo) => {
			const paramsArr = [...params].map((grid) => assertGrid(grid, sourceCodeInfo));
			const rows = paramsArr[0].length;
			paramsArr.slice(1).every((grid) => {
				if (grid.length !== rows) throw new RuntimeError(`All grids must have the same number of rows, but got ${rows} and ${grid.length}`, sourceCodeInfo);
				return true;
			});
			const result = [];
			for (let i = 0; i < rows; i += 1) {
				const row = [];
				paramsArr.forEach((grid) => {
					row.push(...grid[i]);
				});
				result.push(row);
			}
			return toAnyGrid(result);
		},
		arity: { min: 1 }
	},
	cellMap: {
		evaluate: () => {
			throw new Error("cellMap: Dvala implementation should be used instead");
		},
		arity: { min: 2 }
	},
	cellMapi: {
		evaluate: () => {
			throw new Error("cellMapi: Dvala implementation should be used instead");
		},
		arity: toFixedArity(2)
	},
	cellReduce: {
		evaluate: () => {
			throw new Error("cellReduce: Dvala implementation should be used instead");
		},
		arity: toFixedArity(3)
	},
	cellReducei: {
		evaluate: () => {
			throw new Error("cellReducei: Dvala implementation should be used instead");
		},
		arity: toFixedArity(3)
	},
	pushRows: {
		evaluate: (params, sourceCodeInfo) => {
			const [grid_, ...rows_] = params;
			const grid = assertGrid(grid_, sourceCodeInfo);
			const rows = assertGrid(rows_, sourceCodeInfo);
			if (grid[0].length !== rows[0].length) throw new RuntimeError(`All rows must have the same length as the number of columns in grid, but got ${grid[0].length} and ${rows[0].length}`, sourceCodeInfo);
			return toAnyGrid([...grid, ...rows]);
		},
		arity: { min: 2 }
	},
	unshiftRows: {
		evaluate: (params, sourceCodeInfo) => {
			const [grid_, ...rows_] = params;
			const grid = assertGrid(grid_, sourceCodeInfo);
			const rows = assertGrid(rows_, sourceCodeInfo);
			if (grid[0].length !== rows[0].length) throw new RuntimeError(`All rows must have the same length as the number of columns in grid, but got ${grid[0].length} and ${rows[0].length}`, sourceCodeInfo);
			return toAnyGrid([...rows, ...grid]);
		},
		arity: { min: 2 }
	},
	popRow: {
		evaluate: (params, sourceCodeInfo) => {
			const grid = assertGrid(params.get(0), sourceCodeInfo);
			if (grid.length === 1) return null;
			return toAnyGrid(grid.slice(0, -1));
		},
		arity: toFixedArity(1)
	},
	shiftRow: {
		evaluate: (params, sourceCodeInfo) => {
			const grid = assertGrid(params.get(0), sourceCodeInfo);
			if (grid.length === 1) return null;
			return toAnyGrid(grid.slice(1));
		},
		arity: toFixedArity(1)
	},
	pushCols: {
		evaluate: (params, sourceCodeInfo) => {
			const [grid_, ...cols_] = params;
			const grid = assertGrid(grid_, sourceCodeInfo);
			const cols = assertGrid(cols_, sourceCodeInfo);
			if (grid.length !== cols[0].length) throw new RuntimeError(`All columns must have the same length as the number of rows in grid, but got ${cols.length}`, sourceCodeInfo);
			const result = [];
			for (let i = 0; i < grid.length; i += 1) {
				const row = [];
				row.push(...grid[i]);
				cols.forEach((col) => {
					row.push(col[i]);
				});
				result.push(row);
			}
			return toAnyGrid(result);
		},
		arity: { min: 2 }
	},
	unshiftCols: {
		evaluate: (params, sourceCodeInfo) => {
			const [grid_, ...cols_] = params;
			const grid = assertGrid(grid_, sourceCodeInfo);
			const cols = assertGrid(cols_, sourceCodeInfo);
			if (grid.length !== cols[0].length) throw new RuntimeError(`All columns must have the same length as the number of rows in grid, but got ${cols.length}`, sourceCodeInfo);
			const result = [];
			for (let i = 0; i < grid.length; i += 1) {
				const row = [];
				cols.forEach((col) => {
					row.push(col[i]);
				});
				row.push(...grid[i]);
				result.push(row);
			}
			return toAnyGrid(result);
		},
		arity: { min: 2 }
	},
	popCol: {
		evaluate: (params, sourceCodeInfo) => {
			const grid = assertGrid(params.get(0), sourceCodeInfo);
			if (grid[0].length === 1) return null;
			return toAnyGrid(grid.map((row) => row.slice(0, -1)));
		},
		arity: toFixedArity(1)
	},
	shiftCol: {
		evaluate: (params, sourceCodeInfo) => {
			const grid = assertGrid(params.get(0), sourceCodeInfo);
			if (grid[0].length === 1) return null;
			return toAnyGrid(grid.map((row) => row.slice(1)));
		},
		arity: toFixedArity(1)
	},
	fromArray: {
		evaluate: (params, sourceCodeInfo) => {
			const [array, rows] = params;
			assertArray(array, sourceCodeInfo);
			assertNumber(rows, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			if (array.size % rows !== 0) throw new RuntimeError(`The number of elements in the array must be divisible by rows, but got ${array.size} and ${rows}`, sourceCodeInfo);
			return toAnyGrid(fromArray([...array], rows));
		},
		arity: toFixedArity(2)
	}
};
/**
* The grid module containing 2D array manipulation functions.
*/
for (const [key, docs] of Object.entries(moduleDocs$5))
 /* v8 ignore next 2 */
if (gridFunctions[key]) gridFunctions[key].docs = docs;
const gridModule = {
	name: "grid",
	description: "2D grid operations: creation, transformation, transposition, and cell mapping.",
	functions: gridFunctions,
	source: grid_default,
	sourcePath: "packages/dvala-engine/src/builtin/modules/grid/grid.dvala",
	docs: moduleDocs$5
};
//#endregion
//#region src/builtin/modules/json/index.ts
const jsonFunctions = {
	jsonParse: {
		evaluate: ([first], sourceCodeInfo) => {
			assertString(first, sourceCodeInfo);
			return fromJS(JSON.parse(first));
		},
		arity: toFixedArity(1),
		docs: {
			category: "json",
			returns: { type: "any" },
			args: { x: { type: "string" } },
			variants: [{ argumentNames: ["x"] }],
			description: "Returns `JSON.parse(``x``)`.",
			seeAlso: ["json.jsonStringify"],
			examples: ["let { jsonParse } = import(\"json\");\njsonParse(\"[1, 2, 3]\")"]
		}
	},
	jsonStringify: {
		evaluate: ([first, second], sourceCodeInfo) => {
			assertAny(first, sourceCodeInfo);
			const jsValue = toJS(first);
			if (second === void 0) return JSON.stringify(jsValue);
			assertNumber(second, sourceCodeInfo);
			return JSON.stringify(jsValue, null, second);
		},
		arity: {
			min: 1,
			max: 2
		},
		docs: {
			category: "json",
			returns: { type: "string" },
			args: {
				x: { type: "any" },
				indent: {
					type: "integer",
					description: "Number of spaces to use for indentation."
				}
			},
			variants: [{ argumentNames: ["x"] }, { argumentNames: ["x", "indent"] }],
			description: "Returns `JSON.stringify(``x``)`. If second argument is provided, returns `JSON.stringify(``x``, null, ``indent``)`.",
			seeAlso: ["json.jsonParse"],
			examples: ["let { jsonStringify } = import(\"json\");\njsonStringify([1, 2, 3])", "let { jsonStringify } = import(\"json\");\njsonStringify({ a: { b: 10 }}, 2)"],
			hideOperatorForm: true
		}
	}
};
const jsonModule = {
	name: "json",
	description: "JSON serialization and deserialization with pretty-printing support.",
	functions: jsonFunctions,
	source: "{}",
	docs: moduleDocsFromFunctions(jsonFunctions)
};
//#endregion
//#region src/builtin/modules/vector/calcMean.ts
function calcMean(vector) {
	if (vector.length === 0) return 0;
	return vector.reduce((acc, val) => acc + val, 0) / vector.length;
}
//#endregion
//#region src/builtin/modules/vector/calcMedian.ts
function calcMedian$1(vector) {
	const sorted = [...vector].sort((a, b) => a - b);
	const mid = Math.floor(sorted.length / 2);
	return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}
//#endregion
//#region src/builtin/modules/vector/calcMedad.ts
function calcMedad(vector) {
	const median = calcMedian$1(vector);
	return calcMedian$1(vector.map((val) => Math.abs(val - median))) * 1.4826;
}
//#endregion
//#region src/builtin/modules/vector/calcVariance.ts
function calcVariance(vector, optionalMean) {
	const mean = optionalMean ?? calcMean(vector);
	return vector.reduce((acc, val) => acc + (val - mean) ** 2, 0) / vector.length;
}
function calcSampleVariance(vector, optionalMean) {
	const mean = optionalMean ?? calcMean(vector);
	return vector.reduce((acc, val) => acc + (val - mean) ** 2, 0) / (vector.length - 1);
}
//#endregion
//#region src/builtin/modules/vector/calcStdDev.ts
function calcStdDev(vector, optionalMean) {
	const variance = calcVariance(vector, optionalMean);
	return Math.sqrt(variance);
}
function calcSampleStdDev(vector, optionalMean) {
	const variance = calcVariance(vector, optionalMean);
	return Math.sqrt(variance * (vector.length / (vector.length - 1)));
}
//#endregion
//#region src/builtin/modules/linear-algebra/docs.ts
const moduleDocs$4 = {
	reflect: {
		type: "(Number[], Number[]) -> Number[]",
		category: "linearAlgebra",
		description: "Reflects a vector across a given axis.",
		returns: { type: "vector" },
		args: {
			a: {
				type: "vector",
				description: "Vector to reflect."
			},
			b: {
				type: "vector",
				description: "Axis of reflection."
			}
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: ["let { reflect } = import(\"linearAlgebra\");\nreflect([1, 2], [0, 1])", "let { reflect } = import(\"linearAlgebra\");\nreflect([1, 2, 3], [0, 0, 1])"],
		seeAlso: ["linearAlgebra.refract", "linearAlgebra.projection"]
	},
	refract: {
		type: "(Number[], Number[], Number) -> Number[]",
		category: "linearAlgebra",
		description: "Refracts a vector across a given axis.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "Vector to refract."
			},
			axis: {
				type: "vector",
				description: "Axis of refraction."
			},
			eta: {
				type: "number",
				description: "Refraction index."
			}
		},
		variants: [{ argumentNames: [
			"vector",
			"axis",
			"eta"
		] }],
		examples: ["let { refract } = import(\"linearAlgebra\");\nrefract([1, 2], [0, 1], 1.5)", "let { refract } = import(\"linearAlgebra\");\nrefract([1, 2, 3], [0, 0, 1], 1.5)"],
		seeAlso: ["linearAlgebra.reflect"]
	},
	lerp: {
		type: "(Number[], Number[], Number) -> Number[]",
		category: "linearAlgebra",
		description: "Performs linear interpolation between two vectors.",
		returns: { type: "vector" },
		args: {
			a: {
				type: "vector",
				description: "Start vector."
			},
			b: {
				type: "vector",
				description: "End vector."
			},
			t: {
				type: "number",
				description: "Interpolation factor (0 to 1)."
			}
		},
		variants: [{ argumentNames: [
			"a",
			"b",
			"t"
		] }],
		examples: [
			"let { lerp } = import(\"linearAlgebra\");\nlerp([1, 2], [3, 4], 0.5)",
			"let { lerp } = import(\"linearAlgebra\");\nlerp([1, 2], [3, 4], 2)",
			"let { lerp } = import(\"linearAlgebra\");\nlerp([1, 2], [3, 4], -1)",
			"let { lerp } = import(\"linearAlgebra\");\nlerp([1, 2, 3], [4, 5, 6], 0.25)"
		],
		seeAlso: ["linearAlgebra.projection"]
	},
	rotate2d: {
		type: "(Number[], Number) -> Number[]",
		category: "linearAlgebra",
		description: "Rotates a 2D vector by a given angle in radians.",
		returns: { type: "vector" },
		args: {
			a: {
				type: "vector",
				description: "Vector to rotate."
			},
			b: {
				type: "number",
				description: "Angle in b."
			}
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: ["let { rotate2d } = import(\"linearAlgebra\");\nrotate2d([1, 0], PI / 2)", "let { rotate2d } = import(\"linearAlgebra\");\nrotate2d([0, 1], PI)"],
		seeAlso: ["linearAlgebra.rotate3d", "linearAlgebra.angle"]
	},
	rotate3d: {
		type: "(Number[], Number[], Number) -> Number[]",
		category: "linearAlgebra",
		description: "Rotates a 3D vector around a given axis by a given angle in radians.",
		returns: { type: "vector" },
		args: {
			v: {
				type: "vector",
				description: "Vector to rotate."
			},
			axis: {
				type: "vector",
				description: "Axis of rotation."
			},
			radians: {
				type: "number",
				description: "Angle in radians."
			}
		},
		variants: [{ argumentNames: [
			"v",
			"axis",
			"radians"
		] }],
		examples: ["let { rotate3d } = import(\"linearAlgebra\");\nrotate3d([1, 0, 0], [0, 1, 0], PI / 2)", "let { rotate3d } = import(\"linearAlgebra\");\nrotate3d([0, 1, 0], [1, 0, 0], PI)"],
		seeAlso: ["linearAlgebra.rotate2d", "linearAlgebra.angle"]
	},
	dot: {
		type: "(Number[], Number[]) -> Number",
		category: "linearAlgebra",
		description: "Calculates the dot product of two vectors. The result is a scalar.",
		returns: { type: "number" },
		args: {
			a: {
				type: "vector",
				description: "First vector."
			},
			b: {
				type: "vector",
				description: "Second vector."
			}
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: ["let { dot } = import(\"linearAlgebra\");\ndot([1, 2], [3, 4])", "let { dot } = import(\"linearAlgebra\");\ndot([1, 2, 3], [4, 5, 6])"],
		seeAlso: [
			"linearAlgebra.cross",
			"linearAlgebra.cosineSimilarity",
			"linearAlgebra.angle",
			"linearAlgebra.projection",
			"linearAlgebra.isOrthogonal"
		]
	},
	cross: {
		type: "(Number[], Number[]) -> Number[]",
		category: "linearAlgebra",
		description: "Calculates the cross product of two 3D vectors. The result is a vector perpendicular to both input vectors.",
		returns: { type: "vector" },
		args: {
			a: {
				type: "vector",
				description: "First vector (3D)."
			},
			b: {
				type: "vector",
				description: "Second vector (3D)."
			}
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { cross } = import(\"linearAlgebra\");\ncross([1, 2, 3], [4, 5, 6])",
			"let { cross } = import(\"linearAlgebra\");\ncross([1, 0, 0], [0, 1, 0])",
			"let { cross } = import(\"linearAlgebra\");\ncross([0, 0, 1], [1, 0, 0])",
			"let { cross } = import(\"linearAlgebra\");\ncross([1, 2, 3], [0, 0, 0])",
			"let { cross } = import(\"linearAlgebra\");\ncross([0, 0, 0], [1, 2, 3])"
		],
		seeAlso: ["linearAlgebra.dot"]
	},
	normalizeMinmax: {
		type: "(Number[]) -> Number[]",
		category: "linearAlgebra",
		description: "Normalizes the vector using min-max normalization. The result is a vector with values between 0 and 1.",
		returns: {
			type: "number",
			array: true
		},
		args: { v: {
			type: "vector",
			description: "Vector to normalize."
		} },
		variants: [{ argumentNames: ["v"] }],
		examples: [
			"let { normalizeMinmax } = import(\"linearAlgebra\");\nnormalizeMinmax([1, 2, 3])",
			"let { normalizeMinmax } = import(\"linearAlgebra\");\nnormalizeMinmax([1, 2, -3])",
			"let { normalizeMinmax } = import(\"linearAlgebra\");\nnormalizeMinmax([1, 2, 3, 4])",
			"let { normalizeMinmax } = import(\"linearAlgebra\");\nnormalizeMinmax([1, 2, -3, 4])",
			"let { normalizeMinmax } = import(\"linearAlgebra\");\nnormalizeMinmax([1, 2, 3, 40, 50])"
		],
		seeAlso: [
			"linearAlgebra.normalizeZscore",
			"linearAlgebra.normalizeRobust",
			"linearAlgebra.normalizeL1",
			"linearAlgebra.normalizeL2",
			"linearAlgebra.normalizeLog"
		]
	},
	normalizeZscore: {
		type: "(Number[]) -> Number[]",
		category: "linearAlgebra",
		description: "Normalizes the vector using z-score normalization. The result is a vector with mean 0 and standard deviation 1.",
		returns: {
			type: "number",
			array: true
		},
		args: { v: {
			type: "vector",
			description: "Vector to normalize."
		} },
		variants: [{ argumentNames: ["v"] }],
		examples: [
			"let { normalizeZscore } = import(\"linearAlgebra\");\nnormalizeZscore([1, 2, 3])",
			"let { normalizeZscore } = import(\"linearAlgebra\");\nnormalizeZscore([1, 2, -3])",
			"let { normalizeZscore } = import(\"linearAlgebra\");\nnormalizeZscore([1, 2, 3, 4])",
			"let { normalizeZscore } = import(\"linearAlgebra\");\nnormalizeZscore([1, 2, -3, 4])",
			"let { normalizeZscore } = import(\"linearAlgebra\");\nnormalizeZscore([1, 2, 3, 40, 50])"
		],
		seeAlso: [
			"linearAlgebra.normalizeMinmax",
			"linearAlgebra.normalizeRobust",
			"linearAlgebra.normalizeL1",
			"linearAlgebra.normalizeL2",
			"linearAlgebra.normalizeLog"
		]
	},
	normalizeRobust: {
		type: "(Number[]) -> Number[]",
		category: "linearAlgebra",
		description: "Normalizes the vector using robust normalization. The result is a vector with median 0 and median absolute deviation 1.",
		returns: {
			type: "number",
			array: true
		},
		args: { v: {
			type: "vector",
			description: "Vector to normalize."
		} },
		variants: [{ argumentNames: ["v"] }],
		examples: [
			"let { normalizeRobust } = import(\"linearAlgebra\");\nnormalizeRobust([1, 2, 3])",
			"let { normalizeRobust } = import(\"linearAlgebra\");\nnormalizeRobust([1, 2, -3])",
			"let { normalizeRobust } = import(\"linearAlgebra\");\nnormalizeRobust([1, 2, 3, 4])",
			"let { normalizeRobust } = import(\"linearAlgebra\");\nnormalizeRobust([1, 2, -3, 4])",
			"let { normalizeRobust } = import(\"linearAlgebra\");\nnormalizeRobust([1, 2, 3, 40, 50])"
		],
		seeAlso: ["linearAlgebra.normalizeMinmax", "linearAlgebra.normalizeZscore"]
	},
	normalizeL1: {
		type: "(Number[]) -> Number[]",
		category: "linearAlgebra",
		description: "Normalizes the vector using L1 normalization. The result is a vector with L1 norm equal to 1.",
		returns: {
			type: "number",
			array: true
		},
		args: { v: {
			type: "vector",
			description: "Vector to normalize."
		} },
		variants: [{ argumentNames: ["v"] }],
		examples: [
			"let { normalizeL1 } = import(\"linearAlgebra\");\nnormalizeL1([1, 2, 3])",
			"let { normalizeL1 } = import(\"linearAlgebra\");\nnormalizeL1([1, 2, -3])",
			"let { normalizeL1 } = import(\"linearAlgebra\");\nnormalizeL1([1, 2, 3, 4])",
			"let { normalizeL1 } = import(\"linearAlgebra\");\nnormalizeL1([1, 2, -3, 4])",
			"let { normalizeL1 } = import(\"linearAlgebra\");\nnormalizeL1([1, 2, 3, 40, 50])"
		],
		seeAlso: [
			"linearAlgebra.normalizeL2",
			"linearAlgebra.normalizeMinmax",
			"linearAlgebra.manhattanNorm",
			"linearAlgebra.normalizeZscore"
		]
	},
	normalizeL2: {
		type: "(Number[]) -> Number[]",
		category: "linearAlgebra",
		description: "Normalizes the vector using L2 normalization. The result is a vector with L2 norm equal to 1.",
		returns: {
			type: "number",
			array: true
		},
		args: { v: {
			type: "vector",
			description: "Vector to normalize."
		} },
		variants: [{ argumentNames: ["v"] }],
		examples: [
			"let { normalizeL2 } = import(\"linearAlgebra\");\nnormalizeL2([1, 2, 3])",
			"let { normalizeL2 } = import(\"linearAlgebra\");\nnormalizeL2([1, 2, 3])",
			"let { normalizeL2 } = import(\"linearAlgebra\");\nnormalizeL2([1, 2, -3])",
			"let { normalizeL2 } = import(\"linearAlgebra\");\nnormalizeL2([1, 2, 3, 4])",
			"let { normalizeL2 } = import(\"linearAlgebra\");\nnormalizeL2([1, 2, -3, 4])",
			"let { normalizeL2 } = import(\"linearAlgebra\");\nnormalizeL2([1, 2, 3, 40, 50])"
		],
		seeAlso: [
			"linearAlgebra.normalizeL1",
			"linearAlgebra.normalizeMinmax",
			"linearAlgebra.euclideanNorm",
			"linearAlgebra.normalizeZscore"
		]
	},
	normalizeLog: {
		type: "(Number[]) -> Number[]",
		category: "linearAlgebra",
		description: "Normalizes the vector using natural log normalization. The result is a vector with log-transformed values.",
		returns: {
			type: "number",
			array: true
		},
		args: { v: {
			type: "vector",
			description: "Vector to normalize."
		} },
		variants: [{ argumentNames: ["v"] }],
		examples: [
			"let { normalizeLog } = import(\"linearAlgebra\");\nnormalizeLog([1, 2, 3])",
			"let { normalizeLog } = import(\"linearAlgebra\");\nnormalizeLog([1, 2, 3, 4])",
			"let { normalizeLog } = import(\"linearAlgebra\");\nnormalizeLog([1, 2, 3, 40, 50])"
		],
		seeAlso: ["linearAlgebra.normalizeMinmax", "linearAlgebra.normalizeZscore"]
	},
	angle: {
		type: "(Number[], Number[]) -> Number",
		category: "linearAlgebra",
		description: "Calculates the **angle** between two vectors in radians.",
		returns: { type: "number" },
		args: {
			a: { type: "vector" },
			b: { type: "vector" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: ["let { angle } = import(\"linearAlgebra\");\nangle([1, 0], [0, 1])", "let { angle } = import(\"linearAlgebra\");\nangle([1, 0, 1], [0, 1, 0])"],
		seeAlso: [
			"linearAlgebra.dot",
			"linearAlgebra.isCollinear",
			"linearAlgebra.isOrthogonal",
			"linearAlgebra.rotate2d",
			"linearAlgebra.rotate3d",
			"linearAlgebra.isParallel",
			"linearAlgebra.cosineSimilarity",
			"linearAlgebra.toPolar"
		]
	},
	projection: {
		type: "(Number[], Number[]) -> Number[]",
		category: "linearAlgebra",
		description: "Calculates the **projection** of vector `a` onto vector `b`.",
		returns: { type: "vector" },
		args: {
			a: { type: "vector" },
			b: { type: "vector" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: ["let { projection } = import(\"linearAlgebra\");\nprojection([1, 2], [3, 4])", "let { projection } = import(\"linearAlgebra\");\nprojection([1, 2, 3], [4, 5, 6])"],
		seeAlso: [
			"linearAlgebra.dot",
			"linearAlgebra.reflect",
			"linearAlgebra.lerp"
		]
	},
	isCollinear: {
		type: "(Number[], Number[]) -> Boolean",
		category: "linearAlgebra",
		description: "Checks if two vectors are **collinear**.",
		returns: { type: "boolean" },
		args: {
			a: { type: "vector" },
			b: { type: "vector" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { isCollinear } = import(\"linearAlgebra\");\nisCollinear([1, 2], [2, 4])",
			"let { isCollinear } = import(\"linearAlgebra\");\nisCollinear([1, 2], [-2, -4])",
			"let { isCollinear } = import(\"linearAlgebra\");\nisCollinear([1, 2, 3], [2, 4, 6])"
		],
		seeAlso: [
			"linearAlgebra.isParallel",
			"linearAlgebra.isOrthogonal",
			"linearAlgebra.angle"
		]
	},
	isParallel: {
		type: "(Number[], Number[]) -> Boolean",
		category: "linearAlgebra",
		description: "Checks if two vectors are **parallel**.",
		returns: { type: "boolean" },
		args: {
			a: { type: "vector" },
			b: { type: "vector" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { isParallel } = import(\"linearAlgebra\");\nisParallel([1, 2], [2, 4])",
			"let { isParallel } = import(\"linearAlgebra\");\nisParallel([1, 2], [-2, -4])",
			"let { isParallel } = import(\"linearAlgebra\");\nisParallel([1, 2, 3], [2, 4, 6])",
			"let { isParallel } = import(\"linearAlgebra\");\nisParallel([1, 2], [3, 4])"
		],
		seeAlso: [
			"linearAlgebra.isCollinear",
			"linearAlgebra.isOrthogonal",
			"linearAlgebra.angle"
		]
	},
	isOrthogonal: {
		type: "(Number[], Number[]) -> Boolean",
		category: "linearAlgebra",
		description: "Checks if two vectors are **orthogonal**.",
		returns: { type: "boolean" },
		args: {
			a: { type: "vector" },
			b: { type: "vector" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { isOrthogonal } = import(\"linearAlgebra\");\nisOrthogonal([1, 0], [0, 1])",
			"let { isOrthogonal } = import(\"linearAlgebra\");\nisOrthogonal([1, 0, 1], [0, 1, 0])",
			"let { isOrthogonal } = import(\"linearAlgebra\");\nisOrthogonal([1, 2], [2, -1])"
		],
		seeAlso: [
			"linearAlgebra.isCollinear",
			"linearAlgebra.isParallel",
			"linearAlgebra.dot",
			"matrix.isOrthogonalMatrix",
			"linearAlgebra.angle"
		]
	},
	cosineSimilarity: {
		type: "(Number[], Number[]) -> Number",
		category: "linearAlgebra",
		description: "Calculates the **cosine similarity** between two vectors. The result is a value between -1 and 1.",
		returns: { type: "number" },
		args: {
			a: { type: "vector" },
			b: { type: "vector" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { cosineSimilarity } = import(\"linearAlgebra\");\ncosineSimilarity([1, 2], [3, 4])",
			"let { cosineSimilarity } = import(\"linearAlgebra\");\ncosineSimilarity([1, 2, 3], [4, 5, 6])",
			"let { cosineSimilarity } = import(\"linearAlgebra\");\ncosineSimilarity([1, 0], [0, 1])"
		],
		seeAlso: [
			"linearAlgebra.dot",
			"linearAlgebra.angle",
			"linearAlgebra.euclideanDistance"
		]
	},
	euclideanDistance: {
		type: "(Number[], Number[]) -> Number",
		category: "linearAlgebra",
		description: "Calculates the **Euclidean distance** between two vectors. The result is a non-negative number.",
		returns: { type: "number" },
		args: {
			a: { type: "vector" },
			b: { type: "vector" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { euclideanDistance } = import(\"linearAlgebra\");\neuclideanDistance([1, 2], [3, 4])",
			"let { euclideanDistance } = import(\"linearAlgebra\");\neuclideanDistance([1, 2, 3], [4, 5, 6])",
			"let { euclideanDistance } = import(\"linearAlgebra\");\neuclideanDistance([1, 0], [0, 1])"
		],
		seeAlso: [
			"linearAlgebra.manhattanDistance",
			"linearAlgebra.chebyshevDistance",
			"linearAlgebra.minkowskiDistance",
			"linearAlgebra.euclideanNorm",
			"linearAlgebra.cosineSimilarity",
			"linearAlgebra.hammingDistance"
		]
	},
	euclideanNorm: {
		type: "(Number[]) -> Number",
		category: "linearAlgebra",
		description: "Calculates the **Euclidean norm** (L2 norm) of a vector. The result is a non-negative number.",
		returns: { type: "number" },
		args: { v: {
			type: "vector",
			description: "Vector to calculate the norm for."
		} },
		variants: [{ argumentNames: ["v"] }],
		examples: [
			"let { euclideanNorm } = import(\"linearAlgebra\");\neuclideanNorm([1, 2])",
			"let { euclideanNorm } = import(\"linearAlgebra\");\neuclideanNorm([3, 4])",
			"let { euclideanNorm } = import(\"linearAlgebra\");\neuclideanNorm([1, 2, 3])"
		],
		seeAlso: [
			"linearAlgebra.manhattanNorm",
			"linearAlgebra.chebyshevNorm",
			"linearAlgebra.minkowskiNorm",
			"linearAlgebra.euclideanDistance",
			"linearAlgebra.normalizeL2",
			"linearAlgebra.hammingNorm"
		]
	},
	manhattanDistance: {
		type: "(Number[], Number[]) -> Number",
		category: "linearAlgebra",
		description: "Calculates the **Manhattan distance** between two vectors. The result is a non-negative number.",
		returns: { type: "number" },
		args: {
			a: { type: "vector" },
			b: { type: "vector" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { manhattanDistance } = import(\"linearAlgebra\");\nmanhattanDistance([1, 2], [3, 4])",
			"let { manhattanDistance } = import(\"linearAlgebra\");\nmanhattanDistance([1, 2, 3], [4, 5, 6])",
			"let { manhattanDistance } = import(\"linearAlgebra\");\nmanhattanDistance([1, 0], [0, 1])"
		],
		seeAlso: [
			"linearAlgebra.euclideanDistance",
			"linearAlgebra.chebyshevDistance",
			"linearAlgebra.minkowskiDistance",
			"linearAlgebra.manhattanNorm",
			"linearAlgebra.hammingDistance"
		]
	},
	manhattanNorm: {
		type: "(Number[]) -> Number",
		category: "linearAlgebra",
		description: "Calculates the **Manhattan norm** (L1 norm) of a vector. The result is a non-negative number.",
		returns: { type: "number" },
		args: { v: {
			type: "vector",
			description: "Vector to calculate the norm for."
		} },
		variants: [{ argumentNames: ["v"] }],
		examples: [
			"let { manhattanNorm } = import(\"linearAlgebra\");\nmanhattanNorm([1, 2])",
			"let { manhattanNorm } = import(\"linearAlgebra\");\nmanhattanNorm([3, 4])",
			"let { manhattanNorm } = import(\"linearAlgebra\");\nmanhattanNorm([1, 2, 3])"
		],
		seeAlso: [
			"linearAlgebra.euclideanNorm",
			"linearAlgebra.chebyshevNorm",
			"linearAlgebra.minkowskiNorm",
			"linearAlgebra.manhattanDistance",
			"linearAlgebra.normalizeL1",
			"linearAlgebra.hammingNorm"
		]
	},
	hammingDistance: {
		type: "(Number[], Number[]) -> Number",
		category: "linearAlgebra",
		description: "Calculates the **Hamming distance** between two vectors. The result is a non-negative integer.",
		returns: { type: "integer" },
		args: {
			a: { type: "vector" },
			b: { type: "vector" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { hammingDistance } = import(\"linearAlgebra\");\nhammingDistance([1, 2], [3, 4])",
			"let { hammingDistance } = import(\"linearAlgebra\");\nhammingDistance([1, 2, 3], [4, 5, 6])",
			"let { hammingDistance } = import(\"linearAlgebra\");\nhammingDistance([1, 0], [0, 1])"
		],
		seeAlso: [
			"linearAlgebra.euclideanDistance",
			"linearAlgebra.manhattanDistance",
			"linearAlgebra.hammingNorm"
		]
	},
	hammingNorm: {
		type: "(Number[]) -> Number",
		category: "linearAlgebra",
		description: "Calculates the **Hamming norm** of a vector. The result is a non-negative integer.",
		returns: { type: "integer" },
		args: { v: {
			type: "vector",
			description: "Vector to calculate the norm for."
		} },
		variants: [{ argumentNames: ["v"] }],
		examples: [
			"let { hammingNorm } = import(\"linearAlgebra\");\nhammingNorm([1, 2])",
			"let { hammingNorm } = import(\"linearAlgebra\");\nhammingNorm([3, 4])",
			"let { hammingNorm } = import(\"linearAlgebra\");\nhammingNorm([1, 2, 3])"
		],
		seeAlso: [
			"linearAlgebra.euclideanNorm",
			"linearAlgebra.manhattanNorm",
			"linearAlgebra.hammingDistance"
		]
	},
	chebyshevDistance: {
		type: "(Number[], Number[]) -> Number",
		category: "linearAlgebra",
		description: "Calculates the **Chebyshev distance** between two vectors. The result is a non-negative number.",
		returns: { type: "number" },
		args: {
			a: { type: "vector" },
			b: { type: "vector" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { chebyshevDistance } = import(\"linearAlgebra\");\nchebyshevDistance([1, 2], [3, 4])",
			"let { chebyshevDistance } = import(\"linearAlgebra\");\nchebyshevDistance([1, 2, 3], [4, 5, 6])",
			"let { chebyshevDistance } = import(\"linearAlgebra\");\nchebyshevDistance([1, 0], [0, 1])"
		],
		seeAlso: [
			"linearAlgebra.euclideanDistance",
			"linearAlgebra.manhattanDistance",
			"linearAlgebra.minkowskiDistance",
			"linearAlgebra.chebyshevNorm"
		]
	},
	chebyshevNorm: {
		type: "(Number[]) -> Number",
		category: "linearAlgebra",
		description: "Calculates the **Chebyshev norm** of a vector. The result is a non-negative number.",
		returns: { type: "number" },
		args: { v: {
			type: "vector",
			description: "Vector to calculate the norm for."
		} },
		variants: [{ argumentNames: ["v"] }],
		examples: [
			"let { chebyshevNorm } = import(\"linearAlgebra\");\nchebyshevNorm([1, 2])",
			"let { chebyshevNorm } = import(\"linearAlgebra\");\nchebyshevNorm([3, 4])",
			"let { chebyshevNorm } = import(\"linearAlgebra\");\nchebyshevNorm([1, 2, 3])"
		],
		seeAlso: [
			"linearAlgebra.euclideanNorm",
			"linearAlgebra.manhattanNorm",
			"linearAlgebra.minkowskiNorm",
			"linearAlgebra.chebyshevDistance"
		]
	},
	minkowskiDistance: {
		type: "(Number[], Number[], Number) -> Number",
		category: "linearAlgebra",
		description: "Calculates the **Minkowski distance** between two vectors. The result is a non-negative number.",
		returns: { type: "number" },
		args: {
			a: { type: "vector" },
			b: { type: "vector" },
			p: {
				type: "number",
				description: "Order of the norm (p)."
			}
		},
		variants: [{ argumentNames: [
			"a",
			"b",
			"p"
		] }],
		examples: [
			"let { minkowskiDistance } = import(\"linearAlgebra\");\nminkowskiDistance([1, 2], [3, 4], 2)",
			"let { minkowskiDistance } = import(\"linearAlgebra\");\nminkowskiDistance([1, 2, 3], [4, 5, 6], 3)",
			"let { minkowskiDistance } = import(\"linearAlgebra\");\nminkowskiDistance([1, 0], [0, 1], 1)"
		],
		seeAlso: [
			"linearAlgebra.euclideanDistance",
			"linearAlgebra.manhattanDistance",
			"linearAlgebra.chebyshevDistance",
			"linearAlgebra.minkowskiNorm"
		]
	},
	minkowskiNorm: {
		type: "(Number[], Number) -> Number",
		category: "linearAlgebra",
		description: "Calculates the **Minkowski norm** of a vector. The result is a non-negative number.",
		returns: { type: "number" },
		args: {
			a: {
				type: "vector",
				description: "Vector to calculate the norm for."
			},
			b: {
				type: "number",
				description: "Order of the norm (p)."
			}
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { minkowskiNorm } = import(\"linearAlgebra\");\nminkowskiNorm([1, 2], 2)",
			"let { minkowskiNorm } = import(\"linearAlgebra\");\nminkowskiNorm([3, 4], 3)",
			"let { minkowskiNorm } = import(\"linearAlgebra\");\nminkowskiNorm([1, 2, 3], 4)"
		],
		seeAlso: [
			"linearAlgebra.euclideanNorm",
			"linearAlgebra.manhattanNorm",
			"linearAlgebra.chebyshevNorm",
			"linearAlgebra.minkowskiDistance"
		]
	},
	cov: {
		type: "(Number[], Number[]) -> Number",
		category: "linearAlgebra",
		description: "Calculates the **covariance** between two vectors. The result is a number.",
		returns: { type: "number" },
		args: {
			a: { type: "vector" },
			b: { type: "vector" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { cov } = import(\"linearAlgebra\");\ncov([1, 2], [3, 4])",
			"let { cov } = import(\"linearAlgebra\");\ncov([1, 2, 3], [4, 5, 6])",
			"let { cov } = import(\"linearAlgebra\");\ncov([1, 0], [0, 1])"
		],
		seeAlso: [
			"linearAlgebra.corr",
			"linearAlgebra.pearsonCorr",
			"vector.variance"
		]
	},
	corr: {
		type: "(Number[], Number[]) -> Number",
		category: "linearAlgebra",
		description: "Calculates the **correlation** between two vectors. The result is a number between -1 and 1.",
		returns: { type: "number" },
		args: {
			a: { type: "vector" },
			b: { type: "vector" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { corr } = import(\"linearAlgebra\");\ncorr([1, 2], [3, 4])",
			"let { corr } = import(\"linearAlgebra\");\ncorr([1, 2, 3], [4, 5, 6])",
			"let { corr } = import(\"linearAlgebra\");\ncorr([1, 0], [0, 1])"
		],
		seeAlso: [
			"linearAlgebra.cov",
			"linearAlgebra.pearsonCorr",
			"linearAlgebra.spearmanCorr",
			"linearAlgebra.kendallTau",
			"linearAlgebra.autocorrelation",
			"linearAlgebra.crossCorrelation"
		]
	},
	spearmanCorr: {
		type: "(Number[], Number[]) -> Number",
		category: "linearAlgebra",
		description: "Calculates the **Spearman rank correlation** between two vectors. The result is a number between -1 and 1.",
		returns: { type: "number" },
		args: {
			a: { type: "vector" },
			b: { type: "vector" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { spearmanCorr } = import(\"linearAlgebra\");\nspearmanCorr([1, 2], [3, 4])",
			"let { spearmanCorr } = import(\"linearAlgebra\");\nspearmanCorr([1, 2, 3], [4, 5, 6])",
			"let { spearmanCorr } = import(\"linearAlgebra\");\nspearmanCorr([1, 0], [0, 1])"
		],
		seeAlso: [
			"linearAlgebra.pearsonCorr",
			"linearAlgebra.kendallTau",
			"linearAlgebra.corr"
		]
	},
	pearsonCorr: {
		type: "(Number[], Number[]) -> Number",
		category: "linearAlgebra",
		description: "Calculates the **Pearson correlation** between two vectors. The result is a number between -1 and 1.",
		returns: { type: "number" },
		args: {
			a: { type: "vector" },
			b: { type: "vector" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { pearsonCorr } = import(\"linearAlgebra\");\npearsonCorr([1, 2], [3, 4])",
			"let { pearsonCorr } = import(\"linearAlgebra\");\npearsonCorr([1, 2, 3], [4, 5, 6])",
			"let { pearsonCorr } = import(\"linearAlgebra\");\npearsonCorr([1, 0], [0, 1])"
		],
		seeAlso: [
			"linearAlgebra.spearmanCorr",
			"linearAlgebra.kendallTau",
			"linearAlgebra.corr",
			"linearAlgebra.cov"
		]
	},
	kendallTau: {
		type: "(Number[], Number[]) -> Number",
		category: "linearAlgebra",
		description: "Calculates the **Kendall Tau** rank correlation coefficient between two vectors. The result is a number between -1 and 1.",
		returns: { type: "number" },
		args: {
			a: { type: "vector" },
			b: { type: "vector" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { kendallTau } = import(\"linearAlgebra\");\nkendallTau([1, 2], [3, 4])",
			"let { kendallTau } = import(\"linearAlgebra\");\nkendallTau([1, 2, 3], [4, 5, 6])",
			"let { kendallTau } = import(\"linearAlgebra\");\nkendallTau([1, 0], [0, 1])"
		],
		seeAlso: [
			"linearAlgebra.spearmanCorr",
			"linearAlgebra.pearsonCorr",
			"linearAlgebra.corr"
		]
	},
	autocorrelation: {
		type: "(Number[], Number) -> Number",
		category: "linearAlgebra",
		description: "Calculates the **autocorrelation** of a vector. The result is a vector of autocorrelation coefficients.",
		returns: { type: "number" },
		args: {
			a: {
				type: "vector",
				description: "Vector to calculate the autocorrelation for."
			},
			b: {
				type: "integer",
				description: "Lag value for the autocorrelation."
			}
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { autocorrelation } = import(\"linearAlgebra\");\nautocorrelation([1, 2, 3], -2)",
			"let { autocorrelation } = import(\"linearAlgebra\");\nautocorrelation([1, 2, 3], -1)",
			"let { autocorrelation } = import(\"linearAlgebra\");\nautocorrelation([1, 2, 3], 0)",
			"let { autocorrelation } = import(\"linearAlgebra\");\nautocorrelation([1, 2, 3], 1)",
			"let { autocorrelation } = import(\"linearAlgebra\");\nautocorrelation([1, 2, 3], 2)"
		],
		seeAlso: ["linearAlgebra.crossCorrelation", "linearAlgebra.corr"]
	},
	crossCorrelation: {
		type: "(Number[], Number[], Number) -> Number",
		category: "linearAlgebra",
		description: "Calculates the **crossCorrelation** between two vectors. The result is a vector of crossCorrelation coefficients.",
		returns: { type: "number" },
		args: {
			a: { type: "vector" },
			b: { type: "vector" },
			lag: {
				type: "integer",
				description: "Lag value for the crossCorrelation."
			}
		},
		variants: [{ argumentNames: [
			"a",
			"b",
			"lag"
		] }],
		examples: [
			"let { crossCorrelation } = import(\"linearAlgebra\");\ncrossCorrelation([1, 2, 3], [4, 5, 6], -2)",
			"let { crossCorrelation } = import(\"linearAlgebra\");\ncrossCorrelation([1, 2, 3], [4, 5, 6], -1)",
			"let { crossCorrelation } = import(\"linearAlgebra\");\ncrossCorrelation([1, 2, 3], [4, 5, 6], 0)",
			"let { crossCorrelation } = import(\"linearAlgebra\");\ncrossCorrelation([1, 2, 3], [4, 5, 6], 1)",
			"let { crossCorrelation } = import(\"linearAlgebra\");\ncrossCorrelation([1, 2, 3], [4, 5, 6], 2)"
		],
		seeAlso: ["linearAlgebra.autocorrelation", "linearAlgebra.corr"]
	},
	rref: {
		type: "(Number[][]) -> Number[][]",
		category: "linearAlgebra",
		description: "Calculates the **Reduced Row Echelon Form** (RREF) of a matrix.",
		returns: { type: "matrix" },
		args: { m: {
			type: "matrix",
			description: "Matrix to calculate the RREF for."
		} },
		variants: [{ argumentNames: ["m"] }],
		examples: [
			"let { rref } = import(\"linearAlgebra\");\nrref([[1, 2], [3, 4]])",
			"let { rref } = import(\"linearAlgebra\");\nrref([[1, 2, 3], [4, 5, 6], [7, 8, 9]])",
			"let { rref } = import(\"linearAlgebra\");\nrref([[1, 2, 3], [7, 8, 9], [4, 5, 7]])"
		],
		seeAlso: ["linearAlgebra.solve", "matrix.rank"]
	},
	solve: {
		type: "(Number[][], Number[]) -> Number[]",
		category: "linearAlgebra",
		description: "Solves a system of linear equations represented by a matrix and a vector.",
		returns: { type: "vector" },
		args: {
			a: { type: "matrix" },
			b: { type: "vector" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { solve } = import(\"linearAlgebra\");\nsolve([\n  [2, 1, -1, 1], \n  [4, 5, -3, 2], \n  [6, -2, 5, -3], \n  [8, 3, 2, 4]\n], [5, 10, 2, 17])",
			`let { solve } = import("linearAlgebra");
solve([[2, 0, 0], [3, 1, 0], [4, 5, 6]], [4, 5, 38])`,
			`let { solve } = import("linearAlgebra");
solve([[2, 3], [1, -1]], [8, 2])`
		],
		seeAlso: ["linearAlgebra.rref", "matrix.inv"]
	},
	toPolar: {
		type: "(Number[]) -> Number[]",
		category: "linearAlgebra",
		description: "Converts a 2D vector to polar coordinates.",
		returns: { type: "vector" },
		args: { vector: {
			type: "vector",
			description: "2D Vector to convert."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { toPolar } = import(\"linearAlgebra\");\ntoPolar([1, 2])", "let { toPolar } = import(\"linearAlgebra\");\ntoPolar([3, 4])"],
		seeAlso: ["linearAlgebra.fromPolar", "linearAlgebra.angle"]
	},
	fromPolar: {
		type: "(Number[]) -> Number[]",
		category: "linearAlgebra",
		description: "Converts polar coordinates to a 2D vector.",
		returns: { type: "vector" },
		args: { polar: {
			type: "vector",
			description: "Polar coordinates to convert."
		} },
		variants: [{ argumentNames: ["polar"] }],
		examples: [
			"let { fromPolar } = import(\"linearAlgebra\");\nfromPolar([1, PI / 4])",
			"let { fromPolar } = import(\"linearAlgebra\");\nfromPolar([1, 0])",
			"let { fromPolar } = import(\"linearAlgebra\");\nfromPolar([1, -PI / 2])"
		],
		seeAlso: ["linearAlgebra.toPolar"]
	}
};
//#endregion
//#region src/builtin/modules/linear-algebra/helpers/gaussJordanElimination.ts
/**
* Performs Gauss-Jordan elimination on a matrix, transforming it to reduced row echelon form
*
* @param matrix - The input matrix
* @returns A tuple containing the reduced row echelon form matrix and the rank
*/
function gaussJordanElimination(matrix) {
	const m = matrix.map((row) => [...row]);
	const rows = m.length;
	const cols = m[0].length;
	let rank = 0;
	let rowsProcessed = 0;
	for (let col = 0; col < cols; col++) {
		let pivotRow = -1;
		for (let row = rowsProcessed; row < rows; row++) if (!approxZero(m[row][col])) {
			pivotRow = row;
			break;
		}
		if (pivotRow === -1) continue;
		rank += 1;
		if (pivotRow !== rowsProcessed) [m[pivotRow], m[rowsProcessed]] = [m[rowsProcessed], m[pivotRow]];
		const pivotValue = m[rowsProcessed][col];
		for (let j = col; j < cols; j++) m[rowsProcessed][j] /= pivotValue;
		for (let row = 0; row < rows; row++) if (row !== rowsProcessed && !approxZero(m[row][col])) {
			const factor = m[row][col];
			for (let j = col; j < cols; j++) m[row][j] -= factor * m[rowsProcessed][j];
		}
		rowsProcessed++;
		if (rowsProcessed === rows) break;
	}
	return [m, rank];
}
//#endregion
//#region src/builtin/modules/linear-algebra/helpers/solve.ts
/**
* Solves a system of linear equations Ax = b
*
* @param A - The coefficient matrix
* @param b - The constant vector
* @returns The solution vector x, or null if no unique solution exists
*/
function solve(A, b) {
	const n = A.length;
	const [echelon] = gaussJordanElimination(A.map((row, i) => [...row, b[i]]));
	for (let i = 0; i < n; i += 1) if (approxZero(echelon[i][i])) return null;
	const x = Array.from({ length: n }, () => 0);
	for (let i = n - 1; i >= 0; i--) {
		let sum = 0;
		for (let j = i + 1; j < n; j++) sum += echelon[i][j] * x[j];
		x[i] = (echelon[i][n] - sum) / echelon[i][i];
	}
	return x;
}
//#endregion
//#region src/builtin/modules/linear-algebra/helpers/collinear.ts
function areVectorsCollinear(vectorA, vectorB) {
	const isZeroVector = (vec) => vec.every((component) => approxZero(component));
	if (isZeroVector(vectorA) || isZeroVector(vectorB)) return true;
	let index = 0;
	while (index < vectorA.length && approxZero(vectorA[index])) index++;
	const ratio = vectorB[index] / vectorA[index];
	for (let i = 0; i < vectorA.length; i++) {
		if (approxZero(vectorA[i]) && approxZero(vectorB[i])) continue;
		if (approxZero(vectorA[i])) return false;
		if (!approxEqual(vectorB[i] / vectorA[i], ratio)) return false;
	}
	return true;
}
function areVectorsParallel(vectorA, vectorB) {
	if (!areVectorsCollinear(vectorA, vectorB)) return false;
	for (let i = 0; i < vectorA.length; i++) if (!approxZero(vectorA[i]) && !approxZero(vectorB[i])) return Math.sign(vectorA[i]) === Math.sign(vectorB[i]);
	return true;
}
//#endregion
//#region src/builtin/modules/linear-algebra/helpers/isZeroVector.ts
function isZeroVector(vector) {
	return vector.every((component) => approxZero(component));
}
//#endregion
//#region src/builtin/modules/linear-algebra/helpers/pearsonCorr.ts
function pearsonCorr(vectorA, vectorB) {
	const meanA = calcMean(vectorA);
	const meanB = calcMean(vectorB);
	let sumNumerator = 0;
	let sumSquareA = 0;
	let sumSquareB = 0;
	for (let i = 0; i < vectorA.length; i++) {
		const diffA = vectorA[i] - meanA;
		const diffB = vectorB[i] - meanB;
		sumNumerator += diffA * diffB;
		sumSquareA += diffA * diffA;
		sumSquareB += diffB * diffB;
	}
	if (sumSquareA === 0 || sumSquareB === 0) throw new Error("Cannot calculate Pearson correlation coefficient: one of the vectors has zero variance.");
	return sumNumerator / (Math.sqrt(sumSquareA) * Math.sqrt(sumSquareB));
}
//#endregion
//#region src/builtin/modules/linear-algebra/helpers/calcFractionalRanks.ts
function calcFractionalRanks(vector) {
	const indices = [...vector.keys()].sort((a, b) => vector[a] - vector[b]);
	const ranks = Array.from({ length: vector.length }).fill(0);
	let currentRank = 1;
	let i = 0;
	while (i < indices.length) {
		const value = vector[indices[i]];
		let j = i;
		while (j < indices.length && vector[indices[j]] === value) j++;
		const averageRank = currentRank + (j - i - 1) / 2;
		for (let k = i; k < j; k++) ranks[indices[k]] = averageRank;
		currentRank += j - i;
		i = j;
	}
	return ranks;
}
//#endregion
//#region src/builtin/modules/linear-algebra/helpers/kendallTau.ts
/**
* Calculates Kendall's Tau-b rank correlation coefficient between two vectors
* This implementation handles ties and uses an epsilon value for floating-point comparisons
*
* @param vectorA - First vector of numbers
* @param vectorB - Second vector of numbers
* @param epsilon - Threshold for considering two values as equal (default: 1e-10)
* @returns The Kendall's Tau-b correlation coefficient
*/
function kendallTau(vectorA, vectorB, epsilon = 1e-10) {
	let concordant = 0;
	let discordant = 0;
	let tiesInA = 0;
	let tiesInB = 0;
	for (let i = 0; i < vectorA.length; i++) for (let j = i + 1; j < vectorA.length; j++) {
		const diffA = vectorA[i] - vectorA[j];
		const diffB = vectorB[i] - vectorB[j];
		const isTieA = Math.abs(diffA) < epsilon;
		const isTieB = Math.abs(diffB) < epsilon;
		if (isTieA && isTieB) continue;
		else if (isTieA) tiesInA += 1;
		else if (isTieB) tiesInB += 1;
		else if (diffA * diffB > 0) concordant += 1;
		else discordant += 1;
	}
	const n1 = concordant + discordant + tiesInA;
	const n2 = concordant + discordant + tiesInB;
	if (n1 === 0 || n2 === 0) throw new Error("Not enough data to calculate Kendall's Tau");
	return (concordant - discordant) / Math.sqrt(n1 * n2);
}
//#endregion
//#region src/builtin/modules/linear-algebra/helpers/covariance.ts
/**
* Calculate covariance between two segments
*/
function calcCovariance(segmentA, segmentB) {
	const meanA = calcMean(segmentA);
	const meanB = calcMean(segmentB);
	let sum = 0;
	for (let i = 0; i < segmentA.length; i++) sum += (segmentA[i] - meanA) * (segmentB[i] - meanB);
	return sum / segmentA.length;
}
//#endregion
//#region src/builtin/modules/linear-algebra/helpers/corrleation.ts
/**
* Calculate Pearson correlation between two segments
*/
function calcCorrelation(segmentA, segmentB) {
	const meanA = calcMean(segmentA);
	const meanB = calcMean(segmentB);
	const stdA = calcStdDev(segmentA, meanA);
	const stdB = calcStdDev(segmentB, meanB);
	if (stdA === 0 || stdB === 0) {
		if (stdA === 0 && stdB === 0 && meanA === meanB) return 1;
		return 0;
	}
	return calcCovariance(segmentA, segmentB) / (stdA * stdB);
}
/**
* Extract overlapping segments from two vectors based on lag
*/
function extractOverlappingSegments(vectorA, vectorB, lag) {
	const absLag = Math.abs(lag);
	const overlapLength = vectorA.length - absLag;
	if (lag >= 0) return [vectorA.slice(0, overlapLength), vectorB.slice(lag, lag + overlapLength)];
	else return [vectorA.slice(absLag), vectorB.slice(0, overlapLength)];
}
//#endregion
//#region src/builtin/modules/linear-algebra/helpers/getUnit.ts
function getUnit(value, sourceCodeInfo) {
	if (value.length === 0) return value;
	const length = Math.sqrt(value.reduce((acc, item) => acc + item ** 2, 0));
	if (approxZero(length)) throw new RuntimeError("The vector must not be zero", sourceCodeInfo);
	return value.map((item) => item / length);
}
//#endregion
//#region src/builtin/modules/linear-algebra/helpers/dot.ts
function dot(vector1, vector2) {
	return vector1.reduce((acc, item, index) => acc + item * vector2[index], 0);
}
//#endregion
//#region src/builtin/modules/linear-algebra/helpers/subtract.ts
function subtract(vector1, vector2) {
	return vector1.map((item, index) => item - vector2[index]);
}
//#endregion
//#region src/builtin/modules/linear-algebra/helpers/scale.ts
function scale(vector, scalar) {
	return vector.map((item) => item * scalar);
}
//#endregion
//#region src/builtin/modules/linear-algebra/helpers/length.ts
function length(vector) {
	return Math.sqrt(vector.reduce((acc, item) => acc + item ** 2, 0));
}
//#endregion
//#region src/builtin/modules/linear-algebra/linear-algebra.dvala
var linear_algebra_default = "{};\n";
//#endregion
//#region src/builtin/modules/linear-algebra/index.ts
function toAny$1(val) {
	return val;
}
const linearAlgebraNormalExpression = {
	rotate2d: {
		evaluate: ([vector_, radians], sourceCodeInfo) => {
			const vector = assert2dVector(vector_, sourceCodeInfo);
			if (isZeroVector(vector)) return toAny$1(vector);
			assertNumber(radians, sourceCodeInfo, { finite: true });
			const cosTheta = Math.cos(radians);
			const sinTheta = Math.sin(radians);
			return toAny$1([vector[0] * cosTheta - vector[1] * sinTheta, vector[0] * sinTheta + vector[1] * cosTheta]);
		},
		arity: toFixedArity(2)
	},
	rotate3d: {
		evaluate: ([vector_, axis_, radians], sourceCodeInfo) => {
			const vector = assert3dVector(vector_, sourceCodeInfo);
			if (isZeroVector(vector)) return toAny$1(vector);
			assertNumber(radians, sourceCodeInfo, { finite: true });
			const axis = assert3dVector(axis_, sourceCodeInfo);
			if (isZeroVector(axis)) throw new RuntimeError("Rotation axis must not be zero", sourceCodeInfo);
			const cosTheta = Math.cos(radians);
			const sinTheta = Math.sin(radians);
			const [u, vComp, w] = getUnit(axis, sourceCodeInfo);
			const dotProduct = vector[0] * u + vector[1] * vComp + vector[2] * w;
			return toAny$1([
				dotProduct * u * (1 - cosTheta) + vector[0] * cosTheta + (-w * vector[1] + vComp * vector[2]) * sinTheta,
				dotProduct * vComp * (1 - cosTheta) + vector[1] * cosTheta + (w * vector[0] - u * vector[2]) * sinTheta,
				dotProduct * w * (1 - cosTheta) + vector[2] * cosTheta + (-vComp * vector[0] + u * vector[1]) * sinTheta
			]);
		},
		arity: toFixedArity(3)
	},
	reflect: {
		evaluate: ([vector_, normal_], sourceCodeInfo) => {
			const vector = assertVector(vector_, sourceCodeInfo);
			const normal = assertVector(normal_, sourceCodeInfo);
			if (vector.length !== normal.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			if (isZeroVector(normal)) throw new RuntimeError("Reflection normal must not be zero", sourceCodeInfo);
			if (isZeroVector(vector)) return toAny$1(vector);
			const unitNormal = getUnit(normal, sourceCodeInfo);
			return toAny$1(subtract(vector, scale(unitNormal, 2 * dot(vector, unitNormal))));
		},
		arity: toFixedArity(2)
	},
	refract: {
		evaluate: ([vector_, normal_, eta], sourceCodeInfo) => {
			const vector = assertVector(vector_, sourceCodeInfo);
			const normal = assertVector(normal_, sourceCodeInfo);
			assertNumber(eta, sourceCodeInfo, {
				finite: true,
				positive: true
			});
			if (vector.length !== normal.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			if (isZeroVector(normal)) throw new RuntimeError("Refraction normal must not be zero", sourceCodeInfo);
			if (isZeroVector(vector)) return toAny$1(vector);
			const normalizedV = getUnit(vector, sourceCodeInfo);
			const normalizedNormal = getUnit(normal, sourceCodeInfo);
			const dotProduct = dot(normalizedV, normalizedNormal);
			const discriminant = 1 - eta * eta * (1 - dotProduct * dotProduct);
			if (discriminant < 0) return toAny$1(vector);
			return toAny$1(subtract(scale(normalizedV, eta), scale(normalizedNormal, eta * dotProduct + Math.sqrt(discriminant))));
		},
		arity: toFixedArity(3)
	},
	lerp: {
		evaluate: ([vectorA_, vectorB_, t], sourceCodeInfo) => {
			const vectorA = assertVector(vectorA_, sourceCodeInfo);
			const vectorB = assertVector(vectorB_, sourceCodeInfo);
			assertNumber(t, sourceCodeInfo, { finite: true });
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			return toAny$1(vectorA.map((val, i) => val + (vectorB[i] - val) * t));
		},
		arity: toFixedArity(3)
	},
	dot: {
		evaluate: ([vectorA_, vectorB_], sourceCodeInfo) => {
			const vectorA = assertVector(vectorA_, sourceCodeInfo);
			const vectorB = assertVector(vectorB_, sourceCodeInfo);
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			return dot(vectorA, vectorB);
		},
		arity: toFixedArity(2)
	},
	cross: {
		evaluate: ([vectorA_, vectorB_], sourceCodeInfo) => {
			const vectorA = assertVector(vectorA_, sourceCodeInfo);
			const vectorB = assertVector(vectorB_, sourceCodeInfo);
			if (vectorA.length !== 3 || vectorB.length !== 3) throw new RuntimeError("Cross product is only defined for 3D vectors", sourceCodeInfo);
			return toAny$1([
				vectorA[1] * vectorB[2] - vectorA[2] * vectorB[1],
				vectorA[2] * vectorB[0] - vectorA[0] * vectorB[2],
				vectorA[0] * vectorB[1] - vectorA[1] * vectorB[0]
			]);
		},
		arity: toFixedArity(2)
	},
	normalizeMinmax: {
		evaluate: ([vector_], sourceCodeInfo) => {
			const vector = assertVector(vector_, sourceCodeInfo);
			if (vector.length === 0) return toAny$1([]);
			const min = vector.reduce((acc, val) => val < acc ? val : acc, vector[0]);
			const max = vector.reduce((acc, val) => val > acc ? val : acc, vector[0]);
			if (min === max) return toAny$1(vector.map(() => 0));
			return toAny$1(vector.map((val) => (val - min) / (max - min)));
		},
		arity: toFixedArity(1)
	},
	normalizeRobust: {
		evaluate: ([vector_], sourceCodeInfo) => {
			const vector = assertVector(vector_, sourceCodeInfo);
			if (vector.length === 0) return toAny$1([]);
			const median = calcMedian$1(vector);
			const medad = calcMedad(vector);
			if (medad === 0) return toAny$1(vector.map((val) => val - median));
			return toAny$1(vector.map((val) => (val - median) / medad));
		},
		arity: toFixedArity(1)
	},
	normalizeZscore: {
		evaluate: ([vector_], sourceCodeInfo) => {
			const vector = assertVector(vector_, sourceCodeInfo);
			const mean = calcMean(vector);
			const stdDev = calcStdDev(vector);
			if (stdDev === 0) return toAny$1(vector.map(() => 0));
			return toAny$1(vector.map((val) => (val - mean) / stdDev));
		},
		arity: toFixedArity(1)
	},
	normalizeL1: {
		evaluate: ([vector_], sourceCodeInfo) => {
			const vector = assertVector(vector_, sourceCodeInfo);
			if (vector.length === 0) return toAny$1([]);
			const norm = vector.reduce((acc, val) => acc + Math.abs(val), 0);
			if (norm === 0) return toAny$1(vector.map(() => 0));
			return toAny$1(vector.map((val) => val / norm));
		},
		arity: toFixedArity(1)
	},
	normalizeL2: {
		evaluate: ([vector_], sourceCodeInfo) => {
			return toAny$1(getUnit(assertVector(vector_, sourceCodeInfo), sourceCodeInfo));
		},
		arity: toFixedArity(1)
	},
	normalizeLog: {
		evaluate: ([vector_], sourceCodeInfo) => {
			const vector = assertVector(vector_, sourceCodeInfo);
			if (vector.length === 0) return toAny$1([]);
			const min = Math.min(...vector);
			if (min <= 0) throw new RuntimeError("Log normalization requires all values to be positive", sourceCodeInfo);
			return toAny$1(vector.map((val) => Math.log(val / min)));
		},
		arity: toFixedArity(1)
	},
	angle: {
		evaluate: ([vectorA_, vectorB_], sourceCodeInfo) => {
			const vectorA = assertNonEmptyVector(vectorA_, sourceCodeInfo);
			const vectorB = assertNonEmptyVector(vectorB_, sourceCodeInfo);
			if (isZeroVector(vectorA) || isZeroVector(vectorB)) throw new RuntimeError("Cannot calculate angle with zero-length vector", sourceCodeInfo);
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			const dotProduct = vectorA.reduce((acc, val, i) => acc + val * vectorB[i], 0);
			const magnitudeA = Math.sqrt(vectorA.reduce((acc, val) => acc + val * val, 0));
			const magnitudeB = Math.sqrt(vectorB.reduce((acc, val) => acc + val * val, 0));
			return Math.acos(dotProduct / (magnitudeA * magnitudeB));
		},
		arity: toFixedArity(2)
	},
	projection: {
		evaluate: ([vectorA_, vectorB_], sourceCodeInfo) => {
			const vectorA = assertNonEmptyVector(vectorA_, sourceCodeInfo);
			const vectorB = assertNonEmptyVector(vectorB_, sourceCodeInfo);
			if (isZeroVector(vectorB)) throw new RuntimeError("Cannot project onto zero-length vector", sourceCodeInfo);
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			const dotProduct = vectorA.reduce((acc, val, i) => acc + val * vectorB[i], 0);
			const magnitudeB = Math.sqrt(vectorB.reduce((acc, val) => acc + val * val, 0));
			return toAny$1(vectorB.map((val) => dotProduct / magnitudeB ** 2 * val));
		},
		arity: toFixedArity(2)
	},
	isOrthogonal: {
		evaluate: ([vectorA_, vectorB_], sourceCodeInfo) => {
			const vectorA = assertNonEmptyVector(vectorA_, sourceCodeInfo);
			const vectorB = assertNonEmptyVector(vectorB_, sourceCodeInfo);
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			return vectorA.reduce((acc, val, i) => acc + val * vectorB[i], 0) === 0;
		},
		arity: toFixedArity(2)
	},
	isParallel: {
		evaluate: ([vectorA_, vectorB_], sourceCodeInfo) => {
			const vectorA = assertNonEmptyVector(vectorA_, sourceCodeInfo);
			const vectorB = assertNonEmptyVector(vectorB_, sourceCodeInfo);
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			return areVectorsParallel(vectorA, vectorB);
		},
		arity: toFixedArity(2)
	},
	isCollinear: {
		evaluate: ([vectorA_, vectorB_], sourceCodeInfo) => {
			const vectorA = assertNonEmptyVector(vectorA_, sourceCodeInfo);
			const vectorB = assertNonEmptyVector(vectorB_, sourceCodeInfo);
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			return areVectorsCollinear(vectorA, vectorB);
		},
		arity: toFixedArity(2)
	},
	cosineSimilarity: {
		evaluate: ([vectorA_, vectorB_], sourceCodeInfo) => {
			const vectorA = assertNonEmptyVector(vectorA_, sourceCodeInfo);
			const vectorB = assertNonEmptyVector(vectorB_, sourceCodeInfo);
			if (isZeroVector(vectorA) || isZeroVector(vectorB)) throw new RuntimeError("Cannot calculate cosine similarity with zero-length vector", sourceCodeInfo);
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			return vectorA.reduce((acc, val, i) => acc + val * vectorB[i], 0) / (Math.sqrt(vectorA.reduce((acc, val) => acc + val * val, 0)) * Math.sqrt(vectorB.reduce((acc, val) => acc + val * val, 0)));
		},
		arity: toFixedArity(2)
	},
	euclideanDistance: {
		evaluate: ([vectorA_, vectorB_], sourceCodeInfo) => {
			const vectorA = assertNonEmptyVector(vectorA_, sourceCodeInfo);
			const vectorB = assertNonEmptyVector(vectorB_, sourceCodeInfo);
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			return Math.sqrt(vectorA.reduce((acc, val, i) => acc + (val - vectorB[i]) ** 2, 0));
		},
		arity: toFixedArity(2)
	},
	euclideanNorm: {
		evaluate: ([vector_], sourceCodeInfo) => {
			return length(assertNonEmptyVector(vector_, sourceCodeInfo));
		},
		arity: toFixedArity(1)
	},
	manhattanDistance: {
		evaluate: ([vectorA_, vectorB_], sourceCodeInfo) => {
			const vectorA = assertNonEmptyVector(vectorA_, sourceCodeInfo);
			const vectorB = assertNonEmptyVector(vectorB_, sourceCodeInfo);
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			return vectorA.reduce((acc, val, i) => acc + Math.abs(val - vectorB[i]), 0);
		},
		arity: toFixedArity(2)
	},
	manhattanNorm: {
		evaluate: ([vector_], sourceCodeInfo) => {
			return assertNonEmptyVector(vector_, sourceCodeInfo).reduce((acc, val) => acc + Math.abs(val), 0);
		},
		arity: toFixedArity(1)
	},
	hammingDistance: {
		evaluate: ([vectorA_, vectorB_], sourceCodeInfo) => {
			const vectorA = assertNonEmptyVector(vectorA_, sourceCodeInfo);
			const vectorB = assertNonEmptyVector(vectorB_, sourceCodeInfo);
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			return vectorA.reduce((acc, val, i) => acc + (val !== vectorB[i] ? 1 : 0), 0);
		},
		arity: toFixedArity(2)
	},
	hammingNorm: {
		evaluate: ([vector_], sourceCodeInfo) => {
			return assertNonEmptyVector(vector_, sourceCodeInfo).reduce((acc, val) => acc + (val !== 0 ? 1 : 0), 0);
		},
		arity: toFixedArity(1)
	},
	chebyshevDistance: {
		evaluate: ([vectorA_, vectorB_], sourceCodeInfo) => {
			const vectorA = assertNonEmptyVector(vectorA_, sourceCodeInfo);
			const vectorB = assertNonEmptyVector(vectorB_, sourceCodeInfo);
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			return Math.max(...vectorA.map((val, i) => Math.abs(val - vectorB[i])));
		},
		arity: toFixedArity(2)
	},
	chebyshevNorm: {
		evaluate: ([vector_], sourceCodeInfo) => {
			const vector = assertNonEmptyVector(vector_, sourceCodeInfo);
			return Math.max(...vector.map((val) => Math.abs(val)));
		},
		arity: toFixedArity(1)
	},
	minkowskiDistance: {
		evaluate: ([vectorA_, vectorB_, p], sourceCodeInfo) => {
			const vectorA = assertNonEmptyVector(vectorA_, sourceCodeInfo);
			const vectorB = assertNonEmptyVector(vectorB_, sourceCodeInfo);
			assertNumber(p, sourceCodeInfo, {
				finite: true,
				positive: true
			});
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			return vectorA.reduce((acc, val, i) => acc + Math.abs(val - vectorB[i]) ** p, 0) ** (1 / p);
		},
		arity: toFixedArity(3)
	},
	minkowskiNorm: {
		evaluate: ([vector_, p], sourceCodeInfo) => {
			const vector = assertNonEmptyVector(vector_, sourceCodeInfo);
			assertNumber(p, sourceCodeInfo, {
				finite: true,
				positive: true
			});
			return vector.reduce((acc, val) => acc + Math.abs(val) ** p, 0) ** (1 / p);
		},
		arity: toFixedArity(2)
	},
	cov: {
		evaluate: ([vectorA_, vectorB_], sourceCodeInfo) => {
			const vectorA = assertNonEmptyVector(vectorA_, sourceCodeInfo);
			const vectorB = assertNonEmptyVector(vectorB_, sourceCodeInfo);
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			if (vectorA.length === 1) return 0;
			return calcCovariance(vectorA, vectorB);
		},
		arity: toFixedArity(2)
	},
	corr: {
		evaluate: ([vectorA_, vectorB_], sourceCodeInfo) => {
			const vectorA = assertVector(vectorA_, sourceCodeInfo);
			const vectorB = assertVector(vectorB_, sourceCodeInfo);
			if (vectorA.length <= 1) throw new RuntimeError("Vectors must have at least 2 elements for corr", sourceCodeInfo);
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			const meanA = calcMean(vectorA);
			const meanB = calcMean(vectorB);
			return vectorA.reduce((acc, val, i) => acc + (val - meanA) * (vectorB[i] - meanB), 0) / Math.sqrt(vectorA.reduce((acc, val) => acc + (val - meanA) ** 2, 0) * vectorB.reduce((acc, val) => acc + (val - meanB) ** 2, 0));
		},
		arity: toFixedArity(2)
	},
	spearmanCorr: {
		evaluate: ([vectorA_, vectorB_], sourceCodeInfo) => {
			const vectorA = assertVector(vectorA_, sourceCodeInfo);
			const vectorB = assertVector(vectorB_, sourceCodeInfo);
			if (vectorA.length <= 1) throw new RuntimeError("Vectors must have at least 2 elements for corr", sourceCodeInfo);
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			const ranksA = calcFractionalRanks(vectorA);
			const ranksB = calcFractionalRanks(vectorB);
			try {
				return pearsonCorr(ranksA, ranksB);
			} catch (error) {
				throw new DvalaError(error, sourceCodeInfo);
			}
		},
		arity: toFixedArity(2)
	},
	pearsonCorr: {
		evaluate: ([vectorA_, vectorB_], sourceCodeInfo) => {
			const vectorA = assertVector(vectorA_, sourceCodeInfo);
			const vectorB = assertVector(vectorB_, sourceCodeInfo);
			if (vectorA.length <= 1) throw new RuntimeError("Vectors must have at least 2 elements for pearsonCorr", sourceCodeInfo);
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			try {
				return pearsonCorr(vectorA, vectorB);
			} catch (error) {
				throw new DvalaError(error, sourceCodeInfo);
			}
		},
		arity: toFixedArity(2)
	},
	kendallTau: {
		evaluate: ([vectorA_, vectorB_], sourceCodeInfo) => {
			const vectorA = assertVector(vectorA_, sourceCodeInfo);
			const vectorB = assertVector(vectorB_, sourceCodeInfo);
			if (vectorA.length < 2) throw new RuntimeError("Vectors must have at least 2 elements for kendallTau", sourceCodeInfo);
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			try {
				return kendallTau(vectorA, vectorB);
			} catch (error) {
				throw new DvalaError(error, sourceCodeInfo);
			}
		},
		arity: toFixedArity(2)
	},
	autocorrelation: {
		evaluate: ([vector_, lag], sourceCodeInfo) => {
			const vector = assertVector(vector_, sourceCodeInfo);
			if (vector.length < 2) throw new RuntimeError("Vector must have at least 2 elements for autocorrelation", sourceCodeInfo);
			assertNumber(lag, sourceCodeInfo, {
				integer: true,
				lt: vector.length,
				gt: -vector.length
			});
			if (lag === 0) return 1;
			const absLag = Math.abs(lag);
			const mean = calcMean(vector);
			let numerator = 0;
			const n = vector.length;
			for (let i = 0; i < n - absLag; i++) {
				const currentIndex = lag < 0 ? i + absLag : i;
				const laggedIndex = lag < 0 ? i : i + absLag;
				numerator += (vector[currentIndex] - mean) * (vector[laggedIndex] - mean);
			}
			let denominator = 0;
			for (let i = 0; i < n; i++) denominator += (vector[i] - mean) ** 2;
			if (denominator === 0) return 0;
			return numerator / denominator;
		},
		arity: toFixedArity(2)
	},
	crossCorrelation: {
		evaluate: ([vectorA_, vectorB_, lag], sourceCodeInfo) => {
			const vectorA = assertVector(vectorA_, sourceCodeInfo);
			const vectorB = assertVector(vectorB_, sourceCodeInfo);
			if (vectorA.length < 2) throw new RuntimeError("Vectors must have at least 2 elements", sourceCodeInfo);
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			assertNumber(lag, sourceCodeInfo, {
				integer: true,
				lt: vectorA.length,
				gt: -vectorA.length
			});
			if (lag === 0 && vectorA.length === vectorB.length && vectorA.every((v, i) => v === vectorB[i])) return 1;
			const [segmentA, segmentB] = extractOverlappingSegments(vectorA, vectorB, lag);
			return calcCorrelation(segmentA, segmentB);
		},
		arity: toFixedArity(3)
	},
	rref: {
		evaluate: ([matrix_], sourceCodeInfo) => {
			const [rref] = gaussJordanElimination(assertMatrix(matrix_, sourceCodeInfo));
			return toAny$1(rref);
		},
		arity: toFixedArity(1)
	},
	solve: {
		evaluate: ([matrix_, vector_], sourceCodeInfo) => {
			const matrix = assertSquareMatrix(matrix_, sourceCodeInfo);
			const vector = assertVector(vector_, sourceCodeInfo);
			if (matrix.length !== vector.length) throw new RuntimeError(`The number of rows in the matrix must be equal to the length of the vector, but got ${matrix.length} and ${vector.length}`, sourceCodeInfo);
			return toAny$1(solve(matrix, vector));
		},
		arity: toFixedArity(2)
	},
	toPolar: {
		evaluate: ([vector_], sourceCodeInfo) => {
			const vector = assert2dVector(vector_, sourceCodeInfo);
			if (isZeroVector(vector)) return toAny$1([0, 0]);
			return toAny$1([Math.sqrt(vector[0] ** 2 + vector[1] ** 2), Math.atan2(vector[1], vector[0])]);
		},
		arity: toFixedArity(1)
	},
	fromPolar: {
		evaluate: ([polar_], sourceCodeInfo) => {
			const [r, theta] = assert2dVector(polar_, sourceCodeInfo);
			if (r === 0) return toAny$1([0, 0]);
			return toAny$1([r * Math.cos(theta), r * Math.sin(theta)]);
		},
		arity: toFixedArity(1)
	}
};
for (const [key, docs] of Object.entries(moduleDocs$4))
 /* v8 ignore next 2 */
if (linearAlgebraNormalExpression[key]) linearAlgebraNormalExpression[key].docs = docs;
const linearAlgebraModule = {
	name: "linearAlgebra",
	description: "Vector and matrix operations: dot product, cross product, norms, and linear systems.",
	functions: linearAlgebraNormalExpression,
	source: linear_algebra_default,
	sourcePath: "packages/dvala-engine/src/builtin/modules/linear-algebra/linear-algebra.dvala",
	docs: moduleDocs$4
};
const macrosModule = {
	name: "macros",
	description: "Ready-to-use macros built in pure Dvala: tracing, debugging, conditionals, and control-flow sugar.",
	functions: {},
	source: "do\n  let { prettyPrint, decorate, letBindingName } = import(\"ast\");\n  {\n    trace: macro(ast) -> do\n      // `#trace let name = ...` captures the binding name so log lines read\n      // as `ENTER greet: ...`; anonymous forms keep the plain \"ENTER:\" prefix.\n      let name = letBindingName(ast);\n      let prefix = if isNull(name) then \"\" else \" \" ++ name end;\n      decorate(\n        ast,\n        (fn) -> quote\n          (...args) -> do\n            perform(\n              @dvala.io.print,\n              $^{[\"Str\", \"ENTER\" ++ prefix ++ \": \", 0]} ++ str(args)\n            );\n            let result = apply($^{fn}, args);\n            perform(\n              @dvala.io.print,\n              $^{[\"Str\", \"EXIT\" ++ prefix ++ \": \", 0]} ++ str(result)\n            );\n            result;\n          end;\n        end\n      );\n    end,\n    unless: macro(cond, body) -> quote\n      if !($^{cond}) then $^{body} else null end;\n    end,\n    tap: macro(value, sideEffect) -> quote\n      do\n        let __tapValue = $^{value};\n        $^{sideEffect};\n        __tapValue;\n      end;\n    end,\n    dbg: macro(ast) -> do\n      // Capture source text at expansion time so the debug print shows the\n      // original expression alongside its runtime value.\n      let exprStr = prettyPrint(ast);\n      quote\n        do\n          let __dbgValue = $^{ast};\n          perform(\n            @dvala.io.print,\n            $^{[\"Str\", exprStr, 0]} ++ \" => \" ++ str(__dbgValue)\n          );\n          __dbgValue;\n        end;\n      end;\n    end,\n    cond: macro(...clauses) -> do\n      // Odd clause count means the last arg is the default (else branch).\n      // Build nested If AST nodes from the inside out via loop/recur.\n      let n = count(clauses);\n      let hasDefault = (n mod 2) == 1;\n      let pairsEnd = if hasDefault then n - 1 else n end;\n      let elseBranch =\n        if hasDefault then clauses[n - 1] else [\"Reserved\", \"null\", 0] end;\n      loop(i = pairsEnd, acc = elseBranch) -> if i == 0 then\n        acc\n      else\n        do\n          let c = clauses[i - 2];\n          let v = clauses[i - 1];\n          recur(i - 2, [\"If\", [c, v, acc], 0]);\n        end\n      end;\n    end,\n  };\nend;\n",
	sourcePath: "packages/dvala-engine/src/builtin/modules/macros/macros.dvala",
	docs: {
		trace: {
			category: "macros",
			description: "Wraps a function so its arguments are logged on entry and its return value on exit (via `@dvala.io.print`). Useful for debugging call flow without editing the function body. Accepts either a function expression (direct call or `#trace fn`) or a `let` binding (`#trace let name = fn`); in the let-decorator form the binding name is included in the log prefix (`ENTER greet: ...`).",
			returns: { type: "any" },
			args: { target: {
				type: "any",
				description: "A function expression, or a `let` binding when used as `#trace let x = ...`."
			} },
			variants: [{ argumentNames: ["target"] }],
			examples: [
				"let { trace } = import(\"macros\");\nlet add = trace((a, b) -> a + b);\nadd(3, 4)",
				"let { trace } = import(\"macros\");\nlet greet = #trace (name) -> \"hello, \" ++ name;\n[greet(\"Ada\"), greet(\"Grace\")]",
				"let { trace } = import(\"macros\");\n#trace\nlet greet = (name) -> \"hello, \" ++ name;\n[greet(\"Ada\"), greet(\"Grace\")]"
			]
		},
		unless: {
			category: "macros",
			description: "Evaluates ``body`` only when ``cond`` is falsy; returns `null` otherwise. Inverse of `if`. The body is not evaluated when the condition is truthy — useful for guard clauses.",
			returns: { type: "any" },
			args: {
				cond: { type: "any" },
				body: { type: "any" }
			},
			variants: [{ argumentNames: ["cond", "body"] }],
			examples: ["let { unless } = import(\"macros\");\n[unless(false, \"ran\"), unless(true, \"skipped\")]", "let { unless } = import(\"macros\");\nlet safeDivide = (a, b) -> unless(b == 0, a / b);\n[safeDivide(10, 2), safeDivide(10, 0)]"]
		},
		tap: {
			category: "macros",
			description: "Evaluates ``value``, runs ``sideEffect`` for its effects, then returns the original value unchanged. Ideal for peeking inside a `|>` pipeline without breaking the data flow. Since `tap` is a macro, wrap it in a lambda to slot into a pipe — `_` placeholders rewrite for functions, not macros.",
			returns: { type: "any" },
			args: {
				value: { type: "any" },
				sideEffect: { type: "any" }
			},
			variants: [{ argumentNames: ["value", "sideEffect"] }],
			examples: ["let { tap } = import(\"macros\");\nrange(5)\n  |> map(_, -> $ * $)\n  |> (-> tap($, perform(@dvala.io.print, \"squares: \" ++ str($))))\n  |> filter(_, isOdd)\n  |> reduce(_, +, 0)", "let { tap } = import(\"macros\");\ntap(42, perform(@dvala.io.print, \"checkpoint\"))"]
		},
		dbg: {
			category: "macros",
			description: "Prints `\"<source> => <value>\"` (via `@dvala.io.print`) and returns the value unchanged. The source text is captured at macro-expansion time. Transparent — drop it into any subexpression to log without changing behavior.",
			returns: { type: "any" },
			args: { expr: { type: "any" } },
			variants: [{ argumentNames: ["expr"] }],
			examples: ["let { dbg } = import(\"macros\");\ndbg(1 + 2 * 3)", "let { dbg } = import(\"macros\");\ndbg(2 + 3) * dbg(4 - 1)"]
		},
		cond: {
			category: "macros",
			description: "Scheme/Clojure-style multi-branch conditional. Arguments alternate as predicate/value pairs; a trailing odd-numbered argument is the default. Non-matching branches are never evaluated. Expands to nested `if/else if`. Returns `null` when no predicate matches and no default is given (including when called with no arguments).",
			returns: { type: "any" },
			args: { clauses: {
				type: "any",
				rest: true
			} },
			variants: [{ argumentNames: ["clauses"] }],
			examples: ["let { cond } = import(\"macros\");\nlet describe = (x) -> cond(x < 0, \"negative\", x == 0, \"zero\", \"positive\");\n[describe(-1), describe(0), describe(5)]", "let { cond } = import(\"macros\");\nlet classify = (code) -> cond(code < 300, \"OK\", code < 400, \"Redirect\", code < 500, \"Client Error\", code < 600, \"Server Error\", \"Unknown\");\nmap([200, 301, 404, 500, 999], classify)"]
		}
	}
};
//#endregion
//#region src/builtin/modules/math/math.dvala
var math_default = "{};\n";
//#endregion
//#region src/builtin/modules/math/index.ts
function getNumberVectorOrMatrixOperation(param, sourceCodeInfo) {
	if (isVector(param)) return ["vector", assertVector(param, sourceCodeInfo)];
	if (isMatrix(param)) return ["matrix", assertMatrix(param, sourceCodeInfo)];
	if (!isNumber(param)) throw new RuntimeError(`Invalid parameter type: ${typeof param}`, sourceCodeInfo);
	return ["number", param];
}
function checkedMathFn(fn, val, sourceCodeInfo) {
	const result = fn(val);
	if (!Number.isFinite(result)) throw new ArithmeticError("Number is not finite", sourceCodeInfo);
	return result;
}
function unaryMathOp(fn) {
	return ([param], sourceCodeInfo) => {
		const [operation, operand] = getNumberVectorOrMatrixOperation(param, sourceCodeInfo);
		if (operation === "number") return fn(operand);
		else if (operation === "vector") return fromJS(operand.map((val) => checkedMathFn(fn, val, sourceCodeInfo)));
		else return fromJS(operand.map((row) => row.map((val) => checkedMathFn(fn, val, sourceCodeInfo))));
	};
}
const mathUtilsFunctions = {
	sin: {
		evaluate: unaryMathOp((val) => Math.sin(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number) & ((Number[]) -> Number[]) & ((Number[][]) -> Number[][])",
			category: "math",
			returns: { type: [
				"number",
				"vector",
				"matrix"
			] },
			args: { x: { type: [
				"number",
				"vector",
				"matrix"
			] } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `sin` function computes the sine of an angle (in radians), working on `numbers` and element-wise on `vectors` and `matrices`. When applied to collections, it returns the sine of each element while preserving the original structure.",
			seeAlso: [
				"math.asin",
				"math.sinh",
				"math.cos",
				"math.tan",
				"math.toRad"
			],
			examples: [
				`let { sin } = import("math");
sin(0)`,
				`let { sin } = import("math");
sin(1)`,
				`let { sin } = import("math");
sin(PI)`,
				`let { sin } = import("math");
sin(-0.5)`,
				`let { sin } = import("math");
sin([1, 2, 3])`,
				`let { sin } = import("math");
sin([[1, 2], [3, 4]])`
			]
		}
	},
	asin: {
		evaluate: unaryMathOp((val) => Math.asin(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number) & ((Number[]) -> Number[]) & ((Number[][]) -> Number[][])",
			category: "math",
			returns: { type: [
				"number",
				"vector",
				"matrix"
			] },
			args: { x: { type: [
				"number",
				"vector",
				"matrix"
			] } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `asin` function computes the arcsine (inverse sine) of a `number` in radians, working on `numbers` and element-wise on `vectors` and `matrices`. When applied to collections, it returns the arcsine of each element while preserving the original structure.",
			seeAlso: [
				"math.sin",
				"math.asinh",
				"math.acos",
				"math.atan"
			],
			examples: [
				`let { asin } = import("math");
asin(0)`,
				`let { asin } = import("math");
asin(1)`,
				`let { asin } = import("math");
asin(-0.5)`,
				`let { asin } = import("math");
asin([0, 0.5, 1])`,
				`let { asin } = import("math");
asin([[0, 0.5], [-0.5, -1]])`
			]
		}
	},
	sinh: {
		evaluate: unaryMathOp((val) => Math.sinh(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number) & ((Number[]) -> Number[]) & ((Number[][]) -> Number[][])",
			category: "math",
			returns: { type: [
				"number",
				"vector",
				"matrix"
			] },
			args: { x: { type: [
				"number",
				"vector",
				"matrix"
			] } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `sinh` function computes the hyperbolic sine of a `number`, working on `numbers` and element-wise on `vectors` and `matrices`. When applied to collections, it returns the hyperbolic sine of each element while preserving the original structure.",
			seeAlso: [
				"math.asinh",
				"math.sin",
				"math.cosh",
				"math.tanh"
			],
			examples: [
				`let { sinh } = import("math");
sinh(0)`,
				`let { sinh } = import("math");
sinh(1)`,
				`let { sinh } = import("math");
sinh(-0.5)`,
				`let { sinh } = import("math");
sinh([0.1, 0.2, 0.3])`,
				`let { sinh } = import("math");
sinh([[0.1, 0.2], [0.3, 0.4]])`
			]
		}
	},
	asinh: {
		evaluate: unaryMathOp((val) => Math.asinh(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number) & ((Number[]) -> Number[]) & ((Number[][]) -> Number[][])",
			category: "math",
			returns: { type: [
				"number",
				"vector",
				"matrix"
			] },
			args: { x: { type: [
				"number",
				"vector",
				"matrix"
			] } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `asinh` function computes the inverse hyperbolic sine of a `number`, working on `numbers` and element-wise on `vectors` and `matrices`. When applied to collections, it returns the inverse hyperbolic sine of each element while preserving the original structure.",
			seeAlso: [
				"math.sinh",
				"math.asin",
				"math.acosh",
				"math.atanh"
			],
			examples: [
				`let { asinh } = import("math");
asinh(10)`,
				`let { asinh } = import("math");
asinh(90)`,
				`let { asinh } = import("math");
asinh(50)`,
				`let { asinh } = import("math");
asinh([10, 20, 30])`,
				`let { asinh } = import("math");
asinh([[10, 20], [30, 40]])`
			]
		}
	},
	cos: {
		evaluate: unaryMathOp((val) => Math.cos(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number) & ((Number[]) -> Number[]) & ((Number[][]) -> Number[][])",
			category: "math",
			returns: { type: [
				"number",
				"vector",
				"matrix"
			] },
			args: { x: { type: [
				"number",
				"vector",
				"matrix"
			] } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `cos` function computes the cosine of an angle (in radians), working on `numbers` and element-wise on `vectors` and `matrices`. When applied to collections, it returns the cosine of each element while preserving the original structure.",
			seeAlso: [
				"math.acos",
				"math.cosh",
				"math.sin",
				"math.tan",
				"math.toRad"
			],
			examples: [
				`let { cos } = import("math");
cos(0)`,
				`let { cos } = import("math");
cos(1)`,
				`let { cos } = import("math");
cos(PI)`,
				`let { cos } = import("math");
cos(-0.5)`,
				`let { cos } = import("math");
cos([1, 2, 3])`,
				`let { cos } = import("math");
cos([[1, 2], [3, 4]])`
			]
		}
	},
	acos: {
		evaluate: unaryMathOp((val) => Math.acos(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number) & ((Number[]) -> Number[]) & ((Number[][]) -> Number[][])",
			category: "math",
			returns: { type: [
				"number",
				"vector",
				"matrix"
			] },
			args: { x: { type: [
				"number",
				"vector",
				"matrix"
			] } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `acos` function computes the arccosine (inverse cosine) of a `number` in radians, working on `numbers` and element-wise on `vectors` and `matrices`. When applied to collections, it returns the arccosine of each element while preserving the original structure.",
			seeAlso: [
				"math.cos",
				"math.acosh",
				"math.asin",
				"math.atan"
			],
			examples: [
				`let { acos } = import("math");
acos(0)`,
				`let { acos } = import("math");
acos(1)`,
				`let { acos } = import("math");
acos(-0.5)`,
				`let { acos } = import("math");
acos([0.1, 0.2, 0.3])`,
				`let { acos } = import("math");
acos([[0.1, 0.2], [0.3, 0.4]])`
			]
		}
	},
	cosh: {
		evaluate: unaryMathOp((val) => Math.cosh(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number) & ((Number[]) -> Number[]) & ((Number[][]) -> Number[][])",
			category: "math",
			returns: { type: [
				"number",
				"vector",
				"matrix"
			] },
			args: { x: { type: [
				"number",
				"vector",
				"matrix"
			] } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `cosh` function computes the hyperbolic cosine of a `number`, working on `numbers` and element-wise on `vectors` and `matrices`. When applied to collections, it returns the hyperbolic cosine of each element while preserving the original structure.",
			seeAlso: [
				"math.acosh",
				"math.cos",
				"math.sinh",
				"math.tanh"
			],
			examples: [
				`let { cosh } = import("math");
cosh(0)`,
				`let { cosh } = import("math");
cosh(1)`,
				`let { cosh } = import("math");
cosh(-0.5)`,
				`let { cosh } = import("math");
cosh([0.1, 0.2, 0.3])`,
				`let { cosh } = import("math");
cosh([[0.1, 0.2], [0.3, 0.4]])`
			]
		}
	},
	acosh: {
		evaluate: unaryMathOp((val) => Math.acosh(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number) & ((Number[]) -> Number[]) & ((Number[][]) -> Number[][])",
			category: "math",
			returns: { type: [
				"number",
				"vector",
				"matrix"
			] },
			args: { x: { type: [
				"number",
				"vector",
				"matrix"
			] } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `acosh` function computes the inverse hyperbolic cosine of a `number`, working on `numbers` and element-wise on `vectors` and `matrices`. When applied to collections, it returns the inverse hyperbolic cosine of each element while preserving the original structure.",
			seeAlso: [
				"math.cosh",
				"math.acos",
				"math.asinh",
				"math.atanh"
			],
			examples: [
				`let { acosh } = import("math");
acosh(1)`,
				`let { acosh } = import("math");
acosh(2)`,
				`let { acosh } = import("math");
acosh(100)`,
				`let { acosh } = import("math");
acosh(50)`,
				`let { acosh } = import("math");
acosh([1, 2, 3])`,
				`let { acosh } = import("math");
acosh([[1, 2], [3, 4]])`
			]
		}
	},
	tan: {
		evaluate: unaryMathOp((val) => Math.tan(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number) & ((Number[]) -> Number[]) & ((Number[][]) -> Number[][])",
			category: "math",
			returns: { type: [
				"number",
				"vector",
				"matrix"
			] },
			args: { x: { type: [
				"number",
				"vector",
				"matrix"
			] } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `tan` function computes the tangent of an angle (in radians), working on `numbers` and element-wise on `vectors` and `matrices`. When applied to collections, it returns the tangent of each element while preserving the original structure.",
			seeAlso: [
				"math.atan",
				"math.tanh",
				"math.sin",
				"math.cos",
				"math.toRad"
			],
			examples: [
				`let { tan } = import("math");
tan(0)`,
				`let { tan } = import("math");
tan(1)`,
				`let { tan } = import("math");
tan(PI)`,
				`let { tan } = import("math");
tan(-0.5)`,
				`let { tan } = import("math");
tan([1, 2, 3])`,
				`let { tan } = import("math");
tan([[1, 2], [3, 4]])`
			]
		}
	},
	atan: {
		evaluate: unaryMathOp((val) => Math.atan(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number) & ((Number[]) -> Number[]) & ((Number[][]) -> Number[][])",
			category: "math",
			returns: { type: [
				"number",
				"vector",
				"matrix"
			] },
			args: { x: { type: [
				"number",
				"vector",
				"matrix"
			] } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `atan` function computes the arctangent (inverse tangent) of a `number` in radians, working on `numbers` and element-wise on `vectors` and `matrices`. When applied to collections, it returns the arctangent of each element while preserving the original structure.",
			seeAlso: [
				"math.tan",
				"math.atanh",
				"math.asin",
				"math.acos"
			],
			examples: [
				`let { atan } = import("math");
atan(0)`,
				`let { atan } = import("math");
atan(1)`,
				`let { atan } = import("math");
atan(-0.5)`,
				`let { atan } = import("math");
atan([0.1, 0.2, 0.3])`,
				`let { atan } = import("math");
atan([[0.1, 0.2], [0.3, 0.4]])`
			]
		}
	},
	tanh: {
		evaluate: unaryMathOp((val) => Math.tanh(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number) & ((Number[]) -> Number[]) & ((Number[][]) -> Number[][])",
			category: "math",
			returns: { type: [
				"number",
				"vector",
				"matrix"
			] },
			args: { x: { type: [
				"number",
				"vector",
				"matrix"
			] } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `tanh` function computes the hyperbolic tangent of a `number`, working on `numbers` and element-wise on `vectors` and `matrices`. When applied to collections, it returns the hyperbolic tangent of each element while preserving the original structure.",
			seeAlso: [
				"math.atanh",
				"math.tan",
				"math.sinh",
				"math.cosh"
			],
			examples: [
				`let { tanh } = import("math");
tanh(0)`,
				`let { tanh } = import("math");
tanh(1)`,
				`let { tanh } = import("math");
tanh(-0.5)`,
				`let { tanh } = import("math");
tanh(50)`
			]
		}
	},
	atanh: {
		evaluate: unaryMathOp((val) => Math.atanh(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number) & ((Number[]) -> Number[]) & ((Number[][]) -> Number[][])",
			category: "math",
			returns: { type: [
				"number",
				"vector",
				"matrix"
			] },
			args: { x: { type: [
				"number",
				"vector",
				"matrix"
			] } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `atanh` function computes the inverse hyperbolic tangent of a `number`, working on `numbers` and element-wise on `vectors` and `matrices`. When applied to collections, it returns the inverse hyperbolic tangent of each element while preserving the original structure.",
			seeAlso: [
				"math.tanh",
				"math.atan",
				"math.asinh",
				"math.acosh"
			],
			examples: [
				`let { atanh } = import("math");
atanh(0)`,
				`let { atanh } = import("math");
atanh(0.9)`,
				`let { atanh } = import("math");
atanh(-0.5)`,
				`let { atanh } = import("math");
atanh([0.1, 0.2, 0.3])`,
				`let { atanh } = import("math");
atanh([[0.1, 0.2], [0.3, 0.4]])`
			]
		}
	},
	ln: {
		evaluate: unaryMathOp((val) => Math.log(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number) & ((Number[]) -> Number[]) & ((Number[][]) -> Number[][])",
			category: "math",
			returns: { type: [
				"number",
				"vector",
				"matrix"
			] },
			args: { x: { type: [
				"number",
				"vector",
				"matrix"
			] } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `ln` function computes the natural logarithm (base `e`) of a `number`, working on `numbers` and element-wise on `vectors` and `matrices`. When applied to collections, it returns the natural logarithm of each element while preserving the original structure.",
			seeAlso: [
				"math.log2",
				"math.log10",
				"^"
			],
			examples: [
				`let { ln } = import("math");
ln(0.01)`,
				`let { ln } = import("math");
ln(2.5)`,
				`let { ln } = import("math");
ln(E)`,
				`let { ln } = import("math");
ln([1, 2, 3])`,
				`let { ln } = import("math");
ln([[1, 2], [3, 4]])`
			]
		}
	},
	log2: {
		evaluate: unaryMathOp((val) => Math.log2(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number) & ((Number[]) -> Number[]) & ((Number[][]) -> Number[][])",
			category: "math",
			returns: { type: [
				"number",
				"vector",
				"matrix"
			] },
			args: { x: { type: [
				"number",
				"vector",
				"matrix"
			] } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `log2` function computes the base `2` logarithm of a `number`, working on `numbers` and element-wise on `vectors` and `matrices`. When applied to collections, it returns the base-2 logarithm of each element while preserving the original structure.",
			seeAlso: ["math.ln", "math.log10"],
			examples: [
				`let { log2 } = import("math");
log2(0.01)`,
				`let { log2 } = import("math");
log2(2 ^ 12)`,
				`let { log2 } = import("math");
log2(2.5)`,
				`let { log2 } = import("math");
log2([1, 2, 3])`,
				`let { log2 } = import("math");
log2([[1, 2], [3, 4]])`
			]
		}
	},
	log10: {
		evaluate: unaryMathOp((val) => Math.log10(val)),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number) & ((Number[]) -> Number[]) & ((Number[][]) -> Number[][])",
			category: "math",
			returns: { type: [
				"number",
				"vector",
				"matrix"
			] },
			args: { x: { type: [
				"number",
				"vector",
				"matrix"
			] } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `log10` function computes the base `10` logarithm of a `number`, working on `numbers` and element-wise on `vectors` and `matrices`. When applied to collections, it returns the base-10 logarithm of each element while preserving the original structure.",
			seeAlso: ["math.ln", "math.log2"],
			examples: [
				`let { log10 } = import("math");
log10(0.01)`,
				`let { log10 } = import("math");
log10(10 ^ 12)`,
				`let { log10 } = import("math");
log10(2.5)`,
				`let { log10 } = import("math");
log10([1, 2, 3])`,
				`let { log10 } = import("math");
log10([[1, 2], [3, 4]])`
			]
		}
	},
	toRad: {
		evaluate: unaryMathOp((val) => val * Math.PI / 180),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number) & ((Number[]) -> Number[]) & ((Number[][]) -> Number[][])",
			category: "math",
			returns: { type: [
				"number",
				"vector",
				"matrix"
			] },
			args: { x: { type: [
				"number",
				"vector",
				"matrix"
			] } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `toRad` function converts an angle from degrees to radians, working on `numbers` and element-wise on `vectors` and `matrices`. When applied to collections, it converts each element while preserving the original structure.",
			seeAlso: [
				"math.toDeg",
				"math.sin",
				"math.cos",
				"math.tan"
			],
			examples: [
				`let { toRad } = import("math");
toRad(0)`,
				`let { toRad } = import("math");
toRad(90)`,
				`let { toRad } = import("math");
toRad(180)`,
				`let { toRad } = import("math");
toRad(360)`,
				`let { toRad } = import("math");
toRad([0, 90, 180])`,
				`let { toRad } = import("math");
toRad([[0, 90], [180, 360]])`
			]
		}
	},
	toDeg: {
		evaluate: unaryMathOp((val) => val * 180 / Math.PI),
		arity: toFixedArity(1),
		docs: {
			type: "((Number) -> Number) & ((Number[]) -> Number[]) & ((Number[][]) -> Number[][])",
			category: "math",
			returns: { type: [
				"number",
				"vector",
				"matrix"
			] },
			args: { x: { type: [
				"number",
				"vector",
				"matrix"
			] } },
			variants: [{ argumentNames: ["x"] }],
			description: "The `toDeg` function converts an angle from radians to degrees, working on `numbers` and element-wise on `vectors` and `matrices`. When applied to collections, it converts each element while preserving the original structure.",
			seeAlso: ["math.toRad"],
			examples: [
				`let { toDeg } = import("math");
toDeg(0)`,
				`let { toDeg } = import("math");
toDeg(PI)`,
				`let { toDeg } = import("math");
toDeg(PI / 2)`,
				`let { toDeg } = import("math");
toDeg(3 * PI / 2)`,
				`let { toDeg } = import("math");
toDeg([0, PI, PI / 2])`,
				`let { toDeg } = import("math");
toDeg([[0, PI], [PI / 2, 3 * PI / 2]])`
			]
		}
	}
};
const mathUtilsModule = {
	name: "math",
	description: "Mathematical functions: trigonometry, logarithms, rounding, and constants.",
	functions: mathUtilsFunctions,
	source: math_default,
	sourcePath: "packages/dvala-engine/src/builtin/modules/math/math.dvala",
	docs: moduleDocsFromFunctions(mathUtilsFunctions)
};
//#endregion
//#region src/builtin/modules/matrix/docs.ts
const moduleDocs$3 = {
	mul: {
		type: "(Number[][], Number[][]) -> Number[][]",
		category: "matrix",
		description: "Multiplies two `matrices` using standard `matrix` multiplication based on **dot products** of rows and columns.",
		returns: { type: "matrix" },
		args: {
			a: { type: "matrix" },
			b: { type: "matrix" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: ["let { mul } = import(\"matrix\");\nmul([[1, 2], [3, 4]], [[5, 6], [7, 8]])", "let { mul } = import(\"matrix\");\nmul([[1, 2, 3], [4, 5, 6]], [[7, 8], [9, 10], [11, 12]])"],
		seeAlso: ["matrix.det", "matrix.inv"]
	},
	det: {
		type: "(Number[][]) -> Number",
		category: "matrix",
		description: "Calculates the **determinant** of a square matrix.",
		returns: { type: "number" },
		args: { m: {
			type: "matrix",
			description: "The `matrix` to calculate the determinant of."
		} },
		variants: [{ argumentNames: ["m"] }],
		examples: ["let { det } = import(\"matrix\");\ndet([[1, 2], [3, 4]])", "let { det } = import(\"matrix\");\ndet([[1, 2, 3], [4, 5, 6], [7, 8, 9]])"],
		seeAlso: [
			"matrix.inv",
			"matrix.cofactor",
			"matrix.adj",
			"matrix.trace",
			"matrix.rank",
			"matrix.isInvertible",
			"matrix.mul",
			"matrix.minor"
		]
	},
	inv: {
		type: "(Number[][]) -> Number[][]",
		category: "matrix",
		description: "Calculates the **inverse** of a square matrix.",
		returns: { type: "matrix" },
		args: { m: {
			type: "matrix",
			description: "The `matrix` to calculate the inverse of."
		} },
		variants: [{ argumentNames: ["m"] }],
		examples: ["let { inv } = import(\"matrix\");\ninv([[1, 2], [3, 4]])", "let { inv } = import(\"matrix\");\ninv([[1, 2, 3], [4, 5, 7], [7, 8, 10]])"],
		seeAlso: [
			"matrix.det",
			"matrix.adj",
			"matrix.isInvertible",
			"linearAlgebra.solve",
			"matrix.mul",
			"matrix.isOrthogonalMatrix"
		]
	},
	adj: {
		type: "(Number[][]) -> Number[][]",
		category: "matrix",
		description: "Calculates the **adjugate** of a square matrix.",
		returns: { type: "matrix" },
		args: { m: {
			type: "matrix",
			description: "The `matrix` to calculate the adjugate of."
		} },
		variants: [{ argumentNames: ["m"] }],
		examples: [
			"let { adj } = import(\"matrix\");\nadj([[1, 2], [3, 4]])",
			"let { adj } = import(\"matrix\");\nadj([[1, 2, 3], [4, 5, 6], [7, 8, 9]])",
			"let { adj } = import(\"matrix\");\nadj([[1, 2, 3], [7, 8, 9], [4, 5, 6]])"
		],
		seeAlso: [
			"matrix.cofactor",
			"matrix.det",
			"matrix.inv"
		]
	},
	cofactor: {
		type: "(Number[][]) -> Number[][]",
		category: "matrix",
		description: "Calculates the **cofactor** of a square matrix.",
		returns: { type: "matrix" },
		args: { m: {
			type: "matrix",
			description: "The `matrix` to calculate the cofactor of."
		} },
		variants: [{ argumentNames: ["m"] }],
		examples: [
			"let { cofactor } = import(\"matrix\");\ncofactor([[1, 2], [3, 4]])",
			"let { cofactor } = import(\"matrix\");\ncofactor([[1, 2, 3], [4, 5, 6], [7, 8, 9]])",
			"let { cofactor } = import(\"matrix\");\ncofactor([[1, 2, 3], [7, 8, 9], [4, 5, 6]])"
		],
		seeAlso: [
			"matrix.adj",
			"matrix.minor",
			"matrix.det"
		]
	},
	minor: {
		type: "(Number[][], Number, Number) -> Number[][]",
		category: "matrix",
		description: "Calculates the **minor** of a square matrix.",
		returns: { type: "matrix" },
		args: {
			m: {
				type: "matrix",
				description: "The `matrix` to calculate the minor of."
			},
			row: {
				type: "integer",
				description: "The row index of the element to calculate the minor for."
			},
			col: {
				type: "integer",
				description: "The column index of the element to calculate the minor for."
			}
		},
		variants: [{ argumentNames: [
			"m",
			"row",
			"col"
		] }],
		examples: ["let { minor } = import(\"matrix\");\nminor([[1, 2], [3, 4]], 0, 1)", "let { minor } = import(\"matrix\");\nminor([[1, 2, 3], [4, 5, 6], [7, 8, 9]], 1, 1)"],
		seeAlso: ["matrix.cofactor", "matrix.det"]
	},
	trace: {
		type: "(Number[][]) -> Number",
		category: "matrix",
		description: "Calculates the **trace** of a square matrix.",
		returns: { type: "number" },
		args: { m: {
			type: "matrix",
			description: "The `matrix` to calculate the trace of."
		} },
		variants: [{ argumentNames: ["m"] }],
		examples: ["let { trace } = import(\"matrix\");\ntrace([[1, 2], [3, 4]])", "let { trace } = import(\"matrix\");\ntrace([[1, 2, 3], [4, 5, 6], [7, 8, 9]])"],
		seeAlso: ["matrix.det", "matrix.isDiagonal"]
	},
	isSymmetric: {
		type: "(Number[][]) -> Boolean",
		category: "matrix",
		description: "Checks if a `matrix` is **symmetric**.",
		returns: { type: "boolean" },
		args: { m: {
			type: "matrix",
			description: "The `matrix` to check for symmetry."
		} },
		variants: [{ argumentNames: ["m"] }],
		examples: ["let { isSymmetric } = import(\"matrix\");\nisSymmetric([[1, 2], [2, 1]])", "let { isSymmetric } = import(\"matrix\");\nisSymmetric([[1, 2, 3], [2, 1, 4], [3, 4, 1]])"],
		seeAlso: [
			"matrix.isOrthogonalMatrix",
			"matrix.isDiagonal",
			"matrix.isSquare",
			"matrix.hilbert"
		]
	},
	isTriangular: {
		type: "(Number[][]) -> Boolean",
		category: "matrix",
		description: "Checks if a `matrix` is **triangular**.",
		returns: { type: "boolean" },
		args: { m: {
			type: "matrix",
			description: "The `matrix` to check for triangularity."
		} },
		variants: [{ argumentNames: ["m"] }],
		examples: ["let { isTriangular } = import(\"matrix\");\nisTriangular([[2, 0], [0, 1]])", "let { isTriangular } = import(\"matrix\");\nisTriangular([[1, 2, 3], [0, 4, 5], [0, 0, 6]])"],
		seeAlso: [
			"matrix.isUpperTriangular",
			"matrix.isLowerTriangular",
			"matrix.isDiagonal",
			"matrix.isBanded"
		]
	},
	isUpperTriangular: {
		type: "(Number[][]) -> Boolean",
		category: "matrix",
		description: "Checks if a `matrix` is **upper triangular**.",
		returns: { type: "boolean" },
		args: { m: {
			type: "matrix",
			description: "The `matrix` to check for upper triangularity."
		} },
		variants: [{ argumentNames: ["m"] }],
		examples: ["let { isUpperTriangular } = import(\"matrix\");\nisUpperTriangular([[1, 2], [0, 3]])", "let { isUpperTriangular } = import(\"matrix\");\nisUpperTriangular([[1, 2, 3], [0, 4, 5], [0, 0, 6]])"],
		seeAlso: [
			"matrix.isLowerTriangular",
			"matrix.isTriangular",
			"matrix.isDiagonal"
		]
	},
	isLowerTriangular: {
		type: "(Number[][]) -> Boolean",
		category: "matrix",
		description: "Checks if a `matrix` is **lower triangular**.",
		returns: { type: "boolean" },
		args: { m: {
			type: "matrix",
			description: "The `matrix` to check for lower triangularity."
		} },
		variants: [{ argumentNames: ["m"] }],
		examples: ["let { isLowerTriangular } = import(\"matrix\");\nisLowerTriangular([[1, 0], [2, 3]])", "let { isLowerTriangular } = import(\"matrix\");\nisLowerTriangular([[1, 0, 0], [2, 3, 0], [4, 5, 6]])"],
		seeAlso: [
			"matrix.isUpperTriangular",
			"matrix.isTriangular",
			"matrix.isDiagonal"
		]
	},
	isDiagonal: {
		type: "(Number[][]) -> Boolean",
		category: "matrix",
		description: "Checks if a `matrix` is **diagonal**.",
		returns: { type: "boolean" },
		args: { m: {
			type: "matrix",
			description: "The `matrix` to check for diagonal property."
		} },
		variants: [{ argumentNames: ["m"] }],
		examples: [
			"let { isDiagonal } = import(\"matrix\");\nisDiagonal([[1, 0], [0, 2]])",
			"let { isDiagonal } = import(\"matrix\");\nisDiagonal([[1, 0, 0], [0, 2, 0], [0, 0, 3]])",
			"let { isDiagonal } = import(\"matrix\");\nisDiagonal([[1, 0, 0], [2, 2, 2], [0, 0, 3]])"
		],
		seeAlso: [
			"matrix.isIdentity",
			"matrix.isSymmetric",
			"matrix.isTriangular",
			"matrix.trace",
			"matrix.isUpperTriangular",
			"matrix.isLowerTriangular",
			"matrix.band",
			"matrix.isBanded"
		]
	},
	isSquare: {
		type: "(Number[][]) -> Boolean",
		category: "matrix",
		description: "Checks if a `matrix` is **square**.",
		returns: { type: "boolean" },
		args: { m: {
			type: "matrix",
			description: "The `matrix` to check for square property."
		} },
		variants: [{ argumentNames: ["m"] }],
		examples: [
			"let { isSquare } = import(\"matrix\");\nisSquare([[1, 2], [3, 4]])",
			"let { isSquare } = import(\"matrix\");\nisSquare([[1, 2, 3], [4, 5, 6], [7, 8, 9]])",
			"let { isSquare } = import(\"matrix\");\nisSquare([[1, 2, 3], [4, 5, 6]])"
		],
		seeAlso: [
			"matrix.isSymmetric",
			"matrix.isIdentity",
			"matrix.isInvertible"
		]
	},
	isOrthogonalMatrix: {
		type: "(Number[][]) -> Boolean",
		category: "matrix",
		description: "Checks if a `matrix` is **orthogonal**.",
		returns: { type: "boolean" },
		args: { m: {
			type: "matrix",
			description: "The `matrix` to check for **orthogonality**."
		} },
		variants: [{ argumentNames: ["m"] }],
		examples: [
			"let { isOrthogonalMatrix } = import(\"matrix\");\nisOrthogonalMatrix([[1, 0], [0, 1]])",
			"let { isOrthogonalMatrix } = import(\"matrix\");\nisOrthogonalMatrix([[1, 0], [0, -1]])",
			"let { isOrthogonalMatrix } = import(\"matrix\");\nisOrthogonalMatrix([[1, 2], [3, 4]])"
		],
		seeAlso: [
			"matrix.isSymmetric",
			"matrix.inv",
			"matrix.isIdentity",
			"linearAlgebra.isOrthogonal"
		]
	},
	isIdentity: {
		type: "(Number[][]) -> Boolean",
		category: "matrix",
		description: "Checks if a `matrix` is an **identity matrix**.",
		returns: { type: "boolean" },
		args: { m: {
			type: "matrix",
			description: "The `matrix` to check for identity property."
		} },
		variants: [{ argumentNames: ["m"] }],
		examples: [
			"let { isIdentity } = import(\"matrix\");\nisIdentity([[1, 0], [0, 1]])",
			"let { isIdentity } = import(\"matrix\");\nisIdentity([[1, 0, 0], [0, 1, 0], [0, 0, 1]])",
			"let { isIdentity } = import(\"matrix\");\nisIdentity([[1, 0, 0], [0, 1, 0], [0, 0, 0]])"
		],
		seeAlso: [
			"matrix.isDiagonal",
			"matrix.isSquare",
			"matrix.isOrthogonalMatrix"
		]
	},
	isInvertible: {
		type: "(Number[][]) -> Boolean",
		category: "matrix",
		description: "Checks if a `matrix` is **invertible**.",
		returns: { type: "boolean" },
		args: { m: {
			type: "matrix",
			description: "The `matrix` to check for invertibility."
		} },
		variants: [{ argumentNames: ["m"] }],
		examples: [
			"let { isInvertible } = import(\"matrix\");\nisInvertible([[1, 2], [3, 4]])",
			"let { isInvertible } = import(\"matrix\");\nisInvertible([[1, 2, 3], [4, 5, 6], [7, 8, 9]])",
			"let { isInvertible } = import(\"matrix\");\nisInvertible([[1, 2], [2, 4]])"
		],
		seeAlso: [
			"matrix.det",
			"matrix.inv",
			"matrix.rank",
			"matrix.isSquare"
		]
	},
	hilbert: {
		type: "(Number) -> Number[][]",
		category: "matrix",
		description: "Generates a **Hilbert matrix** of size `n`.",
		returns: { type: "matrix" },
		args: { n: {
			type: "integer",
			description: "The size of the Hilbert matrix."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { hilbert } = import(\"matrix\");\nhilbert(3)", "let { hilbert } = import(\"matrix\");\nhilbert(4)"],
		seeAlso: ["matrix.vandermonde", "matrix.isSymmetric"]
	},
	vandermonde: {
		type: "(Number[]) -> Number[][]",
		category: "matrix",
		description: "Generates a **Vandermonde matrix** from a vector.",
		returns: { type: "matrix" },
		args: { v: {
			type: "vector",
			description: "The vector to generate the Vandermonde matrix from."
		} },
		variants: [{ argumentNames: ["v"] }],
		examples: ["let { vandermonde } = import(\"matrix\");\nvandermonde([1, 2, 3])", "let { vandermonde } = import(\"matrix\");\nvandermonde([1, 0, 1])"],
		seeAlso: ["matrix.hilbert", "matrix.band"]
	},
	band: {
		type: "(Number, Number, Number) -> Number[][]",
		category: "matrix",
		description: "Generates a **banded matrix** of size `n` with lower band index `lband` and upper band index `uband`.",
		returns: { type: "matrix" },
		args: {
			n: {
				type: "integer",
				description: "The size of the banded matrix."
			},
			lband: {
				type: "integer",
				description: "The lower band index."
			},
			uband: {
				type: "integer",
				description: "The upper band index."
			}
		},
		variants: [{ argumentNames: [
			"n",
			"lband",
			"uband"
		] }],
		examples: ["let { band } = import(\"matrix\");\nband(3, 1, 1)", "let { band } = import(\"matrix\");\nband(4, 1, 2)"],
		seeAlso: [
			"matrix.isBanded",
			"matrix.isDiagonal",
			"matrix.vandermonde"
		]
	},
	isBanded: {
		type: "(Number[][], Number, Number) -> Boolean",
		category: "matrix",
		description: "Checks if a `matrix` is **banded** with lower band index `lband` and upper band index `uband`.",
		returns: { type: "boolean" },
		args: {
			m: {
				type: "matrix",
				description: "The `matrix` to check for **banded** property."
			},
			lband: {
				type: "integer",
				description: "The lower band index."
			},
			uband: {
				type: "integer",
				description: "The upper band index."
			}
		},
		variants: [{ argumentNames: [
			"m",
			"lband",
			"uband"
		] }],
		examples: ["let { isBanded } = import(\"matrix\");\nisBanded([\n  [1, 1, 1, 0],\n  [1, 1, 1, 1],\n  [1, 1, 1, 1],\n  [0, 1, 1, 1],\n], 2, 2)", "let { isBanded } = import(\"matrix\");\nisBanded([\n  [1, 1, 1, 0],\n  [1, 1, 1, 1],\n  [1, 1, 1, 1],\n  [0, 1, 1, 1],\n], 1, 1)"],
		seeAlso: [
			"matrix.band",
			"matrix.isTriangular",
			"matrix.isDiagonal"
		]
	},
	rank: {
		type: "(Number[][]) -> Number",
		category: "matrix",
		description: "Calculates the **rank** of a matrix using **Gaussian elimination**.",
		returns: { type: "number" },
		args: { m: {
			type: "matrix",
			description: "The `matrix` to calculate the rank of."
		} },
		variants: [{ argumentNames: ["m"] }],
		examples: [
			"let { rank } = import(\"matrix\");\nrank([[1, 0, 0], [0, 1, 0], [0, 0, 1]])",
			"let { rank } = import(\"matrix\");\nrank([[1, 2, 3], [4, 5, 6], [7, 8, 9]])",
			"let { rank } = import(\"matrix\");\nrank([[2, 4, 6], [3, 6, 9], [4, 8, 12]])"
		],
		seeAlso: [
			"matrix.det",
			"matrix.isInvertible",
			"linearAlgebra.rref"
		]
	},
	frobeniusNorm: {
		type: "(Number[][]) -> Number",
		category: "matrix",
		description: "Calculates the **Frobenius norm** of a matrix.",
		returns: { type: "number" },
		args: { m: {
			type: "matrix",
			description: "The `matrix` to calculate the Frobenius norm of."
		} },
		variants: [{ argumentNames: ["m"] }],
		examples: ["let { frobeniusNorm } = import(\"matrix\");\nfrobeniusNorm([[1, 2], [3, 4]])", "let { frobeniusNorm } = import(\"matrix\");\nfrobeniusNorm([[1, 2, 3], [4, 5, 6], [7, 8, 9]])"],
		seeAlso: [
			"matrix.oneNorm",
			"matrix.infNorm",
			"matrix.maxNorm"
		]
	},
	oneNorm: {
		type: "(Number[][]) -> Number",
		category: "matrix",
		description: "Calculates the **oneNorm** (column norm) of a matrix.",
		returns: { type: "number" },
		args: { m: {
			type: "matrix",
			description: "The `matrix` to calculate the oneNorm of."
		} },
		variants: [{ argumentNames: ["m"] }],
		examples: ["let { oneNorm } = import(\"matrix\");\noneNorm([[1, 2], [3, 4]])", "let { oneNorm } = import(\"matrix\");\noneNorm([[1, 2, 3], [4, 5, 6], [7, 8, 9]])"],
		seeAlso: [
			"matrix.frobeniusNorm",
			"matrix.infNorm",
			"matrix.maxNorm"
		]
	},
	infNorm: {
		type: "(Number[][]) -> Number",
		category: "matrix",
		description: "Calculates the **infinity norm** of a matrix.",
		returns: { type: "number" },
		args: { m: {
			type: "matrix",
			description: "The `matrix` to calculate the infinity norm of."
		} },
		variants: [{ argumentNames: ["m"] }],
		examples: ["let { infNorm } = import(\"matrix\");\ninfNorm([[1, 2], [3, 4]])", "let { infNorm } = import(\"matrix\");\ninfNorm([[1, 2, 3], [4, 5, 6], [7, 8, 9]])"],
		seeAlso: [
			"matrix.frobeniusNorm",
			"matrix.oneNorm",
			"matrix.maxNorm"
		]
	},
	maxNorm: {
		type: "(Number[][]) -> Number",
		category: "matrix",
		description: "Calculates the **max norm** of a matrix.",
		returns: { type: "number" },
		args: { m: {
			type: "matrix",
			description: "The `matrix` to calculate the max norm of."
		} },
		variants: [{ argumentNames: ["m"] }],
		examples: ["let { maxNorm } = import(\"matrix\");\nmaxNorm([[1, 2], [3, 4]])", "let { maxNorm } = import(\"matrix\");\nmaxNorm([[1, 2, 3], [4, 5, 6], [7, 8, 9]])"],
		seeAlso: [
			"matrix.frobeniusNorm",
			"matrix.oneNorm",
			"matrix.infNorm"
		]
	}
};
//#endregion
//#region src/builtin/modules/matrix/helpers/determinant.ts
/**
* Calculates the determinant of a matrix using Gaussian Elimination
* @param matrix A square matrix represented as a 2D array
* @returns The determinant of the matrix
*/
function determinant(matrix) {
	const n = matrix.length;
	const A = [];
	for (let i = 0; i < n; i++) A[i] = [...matrix[i]];
	if (n === 1) return A[0][0];
	if (n === 2) return A[0][0] * A[1][1] - A[0][1] * A[1][0];
	let sign = 1;
	for (let i = 0; i < n - 1; i += 1) {
		let maxRow = i;
		for (let j = i + 1; j < n; j += 1) if (Math.abs(A[j][i]) > Math.abs(A[maxRow][i])) maxRow = j;
		if (approxZero(A[maxRow][i])) return 0;
		if (maxRow !== i) {
			[A[i], A[maxRow]] = [A[maxRow], A[i]];
			sign = -sign;
		}
		for (let j = i + 1; j < n; j += 1) {
			const factor = A[j][i] / A[i][i];
			for (let k = i; k < n; k++) A[j][k] -= factor * A[i][k];
		}
	}
	let det = sign;
	for (let i = 0; i < n; i++) det *= A[i][i];
	return det;
}
//#endregion
//#region src/builtin/modules/matrix/helpers/minor.ts
function minor(matrix, row, col) {
	const n = matrix.length;
	const result = [];
	for (let i = 0; i < n; i++) if (i !== row) {
		const minorRow = [];
		for (let j = 0; j < n; j++) if (j !== col) minorRow.push(matrix[i][j]);
		result.push(minorRow);
	}
	return result;
}
//#endregion
//#region src/builtin/modules/matrix/helpers/adjugate.ts
function adjugate(matrix) {
	const n = matrix.length;
	const adj = [];
	for (let i = 0; i < n; i++) {
		adj[i] = [];
		for (let j = 0; j < n; j++) {
			const min = minor(matrix, j, i);
			const cofactor = (-1) ** (i + j) * determinant(min);
			adj[i][j] = cofactor;
		}
	}
	return adj;
}
//#endregion
//#region src/builtin/modules/matrix/helpers/band.ts
/**
* Creates a band matrix with specified lower and upper bandwidths
*
* @param n Size of the square matrix
* @param lband Lower bandwidth (number of non-zero diagonals below main diagonal)
* @param uband Upper bandwidth (number of non-zero diagonals above main diagonal)
* @returns A 2D array representing the band matrix with 1s in the band and 0s elsewhere
*/
function band(n, lband, uband) {
	const matrix = Array.from({ length: n }, () => Array.from({ length: n }, () => 0));
	for (let i = 0; i < n; i++) for (let j = Math.max(0, i - lband); j <= Math.min(n - 1, i + uband); j++) matrix[i][j] = 1;
	return matrix;
}
//#endregion
//#region src/builtin/modules/matrix/helpers/cofactor.ts
function cofactor(matrix) {
	const n = matrix.length;
	const cofactors = [];
	for (let i = 0; i < n; i++) {
		cofactors[i] = [];
		for (let j = 0; j < n; j++) {
			const min = minor(matrix, i, j);
			const sign = (-1) ** (i + j);
			cofactors[i][j] = sign * determinant(min);
		}
	}
	return cofactors;
}
//#endregion
//#region src/builtin/modules/matrix/helpers/inverse.ts
/**
* Calculate the inverse of a matrix using the adjugate method
* @param matrix The input matrix
* @returns The inverse matrix or null if the matrix is not invertible
*/
function inverse(matrix) {
	const n = matrix.length;
	if (n === 1) {
		const element = matrix[0][0];
		if (approxZero(element)) return null;
		return [[1 / element]];
	}
	const det = determinant(matrix);
	if (approxZero(det)) return null;
	const adj = adjugate(matrix);
	const inverseMatrix = [];
	for (let i = 0; i < n; i++) {
		inverseMatrix[i] = [];
		for (let j = 0; j < n; j++) inverseMatrix[i][j] = adj[i][j] / det;
	}
	return inverseMatrix;
}
//#endregion
//#region src/builtin/modules/matrix/helpers/isBanded.ts
/**
* Checks if a matrix is banded with the given lower and upper bandwidth.
* A matrix is banded if all non-zero elements are within 'lower' diagonals
* below the main diagonal and 'upper' diagonals above the main diagonal.
*
* @param matrix - The matrix to check, represented as a 2D array of numbers
* @param lower - Number of non-zero diagonals below the main diagonal
* @param upper - Number of non-zero diagonals above the main diagonal
* @returns true if the matrix is banded with the given parameters, false otherwise
*/
function isBanded(matrix, lower, upper) {
	const rows = matrix.length;
	const cols = matrix[0].length;
	for (let i = 0; i < rows; i++) for (let j = 0; j < cols; j++) if (matrix[i][j] !== 0 && (i - j > lower || j - i > upper)) return false;
	return true;
}
//#endregion
//#region src/builtin/modules/matrix/helpers/isSquare.ts
function isSquare(matrix) {
	return matrix.length === matrix[0].length;
}
//#endregion
//#region src/builtin/modules/matrix/helpers/isDiagonal.ts
/**
* Checks if a given matrix is diagonal.
*
* A matrix is considered diagonal if it is square (i.e., the number of rows equals the number of columns)
* and all elements outside the main diagonal are zero.
*
* @param matrix - A two-dimensional array of numbers representing the matrix to check.
* @returns `true` if the matrix is diagonal, otherwise `false`.
*/
function isDiagonal(matrix) {
	if (!isSquare(matrix)) return false;
	const rows = matrix.length;
	for (let i = 0; i < rows; i += 1) for (let j = 0; j < rows; j += 1) if (i !== j && matrix[i][j] !== 0) return false;
	return true;
}
//#endregion
//#region src/builtin/modules/matrix/helpers/isIdentity.ts
function isIdentity(matrix) {
	if (!isSquare(matrix)) return false;
	const n = matrix.length;
	for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) if (i === j) {
		if (!approxEqual(matrix[i][j], 1)) return false;
	} else if (!approxZero(matrix[i][j])) return false;
	return true;
}
//#endregion
//#region src/builtin/modules/matrix/helpers/matrixMultiply.ts
/**
* Performs cache-optimized matrix multiplication.
* @param A The first input matrix (m x n)
* @param B The second input matrix (n x p)
* @returns The result matrix C (m x p) where C = A × B
*/
function matrixMultiply(A, B) {
	if (A.length === 0 || B.length === 0 || A[0].length !== B.length) throw new Error("Matrix dimensions do not match for multiplication");
	const m = A.length;
	const n = A[0].length;
	const p = B[0].length;
	const C = Array(m).fill(0).map(() => Array(p).fill(0));
	for (let i = 0; i < m; i++) for (let k = 0; k < n; k++) {
		const aik = A[i][k];
		for (let j = 0; j < p; j++) C[i][j] += aik * B[k][j];
	}
	return C;
}
//#endregion
//#region src/builtin/modules/matrix/helpers/isOrthogonal.ts
function isOrthogonal(matrix) {
	if (!isSquare(matrix)) return false;
	return isIdentity(matrixMultiply(matrix, transpose(matrix)));
}
//#endregion
//#region src/builtin/modules/matrix/helpers/isSymetric.ts
/**
* Checks if a given matrix is symmetric.
* A matrix is symmetric if it is square and its transpose is equal to itself.
*
* @param matrix - A 2D array representing the matrix.
* @returns `true` if the matrix is symmetric, otherwise `false`.
*/
function isSymetric(matrix) {
	const rows = matrix.length;
	if (!isSquare(matrix)) return false;
	for (let i = 0; i < rows; i += 1) for (let j = 0; j < i; j += 1) if (matrix[i][j] !== matrix[j][i]) return false;
	return true;
}
//#endregion
//#region src/builtin/modules/matrix/helpers/isTriangular.ts
/**
* Determines whether a given matrix is triangular.
*
* A triangular matrix is a square matrix where all elements
* below or above the main diagonal are zero. This function
* checks if the matrix is square and symmetric.
*
* @param matrix - A two-dimensional array of numbers representing the matrix.
* @returns `true` if the matrix is triangular, otherwise `false`.
*/
function isTriangular(matrix) {
	if (!isSquare(matrix)) return false;
	const rows = matrix.length;
	let isUpperTriangular = true;
	let isLowerTriangular = true;
	for (let i = 0; i < rows; i++) for (let j = 0; j < rows; j++) {
		if (i > j && matrix[i][j] !== 0) {
			isUpperTriangular = false;
			if (!isLowerTriangular) return false;
		}
		if (i < j && matrix[i][j] !== 0) isLowerTriangular = false;
	}
	return isUpperTriangular || isLowerTriangular;
}
function isTriangularUpper(matrix) {
	if (!isSquare(matrix)) return false;
	const rows = matrix.length;
	for (let i = 0; i < rows; i++) for (let j = 0; j < i; j++) if (matrix[i][j] !== 0) return false;
	return true;
}
function isTriangularLower(matrix) {
	if (!isSquare(matrix)) return false;
	const rows = matrix.length;
	if (!matrix.every((row) => row.length === rows)) return false;
	for (let i = 0; i < rows; i++) for (let j = i + 1; j < rows; j++) if (matrix[i][j] !== 0) return false;
	return true;
}
//#endregion
//#region src/builtin/modules/matrix/helpers/norm1.ts
function norm1(matrix) {
	const numRows = matrix.length;
	const numCols = matrix[0].length;
	let maxColSum = 0;
	for (let j = 0; j < numCols; j += 1) {
		let colSum = 0;
		for (let i = 0; i < numRows; i += 1) colSum += Math.abs(matrix[i][j]);
		maxColSum = Math.max(maxColSum, colSum);
	}
	return maxColSum;
}
//#endregion
//#region src/builtin/modules/matrix/helpers/trace.ts
/**
* Calculates the trace of a square matrix.
* The trace is defined as the sum of the elements on the main diagonal.
*
* @param matrix - A 2D array representing a square matrix.
* @returns The trace of the matrix.
*/
function trace(matrix) {
	return matrix.reduce((sum, row, i) => sum + row[i], 0);
}
//#endregion
//#region src/builtin/modules/matrix/matrix.dvala
var matrix_default = "{};\n";
//#endregion
//#region src/builtin/modules/matrix/index.ts
const matrixNormalExpression = {
	mul: {
		evaluate: ([matrix1_, matrix2_], sourceCodeInfo) => {
			const matrix1 = assertMatrix(matrix1_, sourceCodeInfo);
			const matrix2 = assertMatrix(matrix2_, sourceCodeInfo);
			try {
				return matrixMultiply(matrix1, matrix2);
			} catch (_error) {
				throw new RuntimeError(`The number of columns in the first matrix must be equal to the number of rows in the second matrix, but got ${matrix1[0].length} and ${matrix2.length}`, sourceCodeInfo);
			}
		},
		arity: toFixedArity(2)
	},
	det: {
		evaluate: ([matrix_], sourceCodeInfo) => {
			return determinant(assertSquareMatrix(matrix_, sourceCodeInfo));
		},
		arity: toFixedArity(1)
	},
	inv: {
		evaluate: ([matrix_], sourceCodeInfo) => {
			const result = inverse(assertSquareMatrix(matrix_, sourceCodeInfo));
			if (result === null) throw new RuntimeError("The matrix must be invertible", sourceCodeInfo);
			return result;
		},
		arity: toFixedArity(1)
	},
	adj: {
		evaluate: ([matrix_], sourceCodeInfo) => {
			return adjugate(assertSquareMatrix(matrix_, sourceCodeInfo));
		},
		arity: toFixedArity(1)
	},
	cofactor: {
		evaluate: ([matrix_], sourceCodeInfo) => {
			return cofactor(assertSquareMatrix(matrix_, sourceCodeInfo));
		},
		arity: toFixedArity(1)
	},
	minor: {
		evaluate: ([matrix_, row, col], sourceCodeInfo) => {
			const matrix = assertMatrix(matrix_, sourceCodeInfo);
			assertNumber(row, sourceCodeInfo, {
				integer: true,
				nonNegative: true,
				lte: matrix.length
			});
			assertNumber(col, sourceCodeInfo, {
				integer: true,
				nonNegative: true,
				lte: matrix[0].length
			});
			return minor(matrix, row, col);
		},
		arity: toFixedArity(3)
	},
	trace: {
		evaluate: ([matrix_], sourceCodeInfo) => {
			return trace(assertSquareMatrix(matrix_, sourceCodeInfo));
		},
		arity: toFixedArity(1)
	},
	isSymmetric: {
		evaluate: ([matrix_], sourceCodeInfo) => {
			return isSymetric(assertMatrix(matrix_, sourceCodeInfo));
		},
		arity: toFixedArity(1)
	},
	isTriangular: {
		evaluate: ([matrix_], sourceCodeInfo) => {
			return isTriangular(assertMatrix(matrix_, sourceCodeInfo));
		},
		arity: toFixedArity(1)
	},
	isUpperTriangular: {
		evaluate: ([matrix_], sourceCodeInfo) => {
			return isTriangularUpper(assertMatrix(matrix_, sourceCodeInfo));
		},
		arity: toFixedArity(1)
	},
	isLowerTriangular: {
		evaluate: ([matrix_], sourceCodeInfo) => {
			return isTriangularLower(assertMatrix(matrix_, sourceCodeInfo));
		},
		arity: toFixedArity(1)
	},
	isDiagonal: {
		evaluate: ([matrix_], sourceCodeInfo) => {
			return isDiagonal(assertMatrix(matrix_, sourceCodeInfo));
		},
		arity: toFixedArity(1)
	},
	isSquare: {
		evaluate: ([matrix_], sourceCodeInfo) => {
			return isSquare(assertMatrix(matrix_, sourceCodeInfo));
		},
		arity: toFixedArity(1)
	},
	isOrthogonalMatrix: {
		evaluate: ([matrix_], sourceCodeInfo) => {
			return isOrthogonal(assertMatrix(matrix_, sourceCodeInfo));
		},
		arity: toFixedArity(1)
	},
	isIdentity: {
		evaluate: ([matrix_], sourceCodeInfo) => {
			return isIdentity(assertMatrix(matrix_, sourceCodeInfo));
		},
		arity: toFixedArity(1)
	},
	isInvertible: {
		evaluate: ([matrix_], sourceCodeInfo) => {
			const matrix = assertMatrix(matrix_, sourceCodeInfo);
			if (!isSquareMatrix(matrix)) return false;
			return !approxZero(determinant(matrix));
		},
		arity: toFixedArity(1)
	},
	hilbert: {
		evaluate: ([size], sourceCodeInfo) => {
			assertNumber(size, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			const result = [];
			for (let i = 0; i < size; i += 1) {
				const row = [];
				for (let j = 0; j < size; j += 1) row.push(1 / (i + j + 1));
				result.push(row);
			}
			return result;
		},
		arity: toFixedArity(1)
	},
	vandermonde: {
		evaluate: ([vector_], sourceCodeInfo) => {
			const vector = assertVector(vector_, sourceCodeInfo);
			const result = [];
			for (let i = 0; i < vector.length; i += 1) {
				const row = [];
				for (let j = 0; j < vector.length; j += 1) row.push(vector[i] ** j);
				result.push(row);
			}
			return result;
		},
		arity: toFixedArity(1)
	},
	band: {
		evaluate: ([n, lband, uband], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			assertNumber(lband, sourceCodeInfo, {
				integer: true,
				nonNegative: true,
				lt: n
			});
			assertNumber(uband, sourceCodeInfo, {
				integer: true,
				nonNegative: true,
				lte: n
			});
			return band(n, lband, uband);
		},
		arity: toFixedArity(3)
	},
	isBanded: {
		evaluate: ([matrix_, lband, uband], sourceCodeInfo) => {
			const matrix = assertMatrix(matrix_, sourceCodeInfo);
			const maxBand = Math.max(matrix.length, matrix[0].length);
			assertNumber(lband, sourceCodeInfo, {
				integer: true,
				nonNegative: true,
				lt: maxBand
			});
			assertNumber(uband, sourceCodeInfo, {
				integer: true,
				nonNegative: true,
				lt: maxBand
			});
			return isBanded(matrix, lband, uband);
		},
		arity: toFixedArity(3)
	},
	rank: {
		evaluate: ([matrix_], sourceCodeInfo) => {
			const [, result] = gaussJordanElimination(assertMatrix(matrix_, sourceCodeInfo));
			return result;
		},
		arity: toFixedArity(1)
	},
	frobeniusNorm: {
		evaluate: ([matrix_], sourceCodeInfo) => {
			const matrix = assertMatrix(matrix_, sourceCodeInfo);
			return Math.sqrt(matrix.reduce((sum, row) => sum + row.reduce((rowSum, cell) => rowSum + cell * cell, 0), 0));
		},
		arity: toFixedArity(1)
	},
	oneNorm: {
		evaluate: ([matrix_], sourceCodeInfo) => {
			return norm1(assertMatrix(matrix_, sourceCodeInfo));
		},
		arity: toFixedArity(1)
	},
	infNorm: {
		evaluate: ([matrix_], sourceCodeInfo) => {
			return assertMatrix(matrix_, sourceCodeInfo).reduce((max, row) => Math.max(max, row.reduce((sum, cell) => sum + Math.abs(cell), 0)), 0);
		},
		arity: toFixedArity(1)
	},
	maxNorm: {
		evaluate: ([matrix_], sourceCodeInfo) => {
			return assertMatrix(matrix_, sourceCodeInfo).reduce((maxVal, row) => {
				const rowMax = row.reduce((max, val) => Math.max(max, Math.abs(val)), 0);
				return Math.max(maxVal, rowMax);
			}, 0);
		},
		arity: toFixedArity(1)
	}
};
for (const [key, docs] of Object.entries(moduleDocs$3))
 /* v8 ignore next 2 */
if (matrixNormalExpression[key]) matrixNormalExpression[key].docs = docs;
const matrixModule = {
	name: "matrix",
	description: "Matrix algebra: multiplication, determinant, inverse, rank, and decomposition.",
	functions: matrixNormalExpression,
	source: matrix_default,
	sourcePath: "packages/dvala-engine/src/builtin/modules/matrix/matrix.dvala",
	docs: moduleDocs$3
};
//#endregion
//#region src/builtin/modules/number-theory/docs.ts
const moduleDocs$2 = {
	abundantSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the abundant numbers up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate."
		} },
		variants: [{ argumentNames: ["length"] }],
		examples: ["let { abundantSeq } = import(\"numberTheory\");\nabundantSeq(1)", "let { abundantSeq } = import(\"numberTheory\");\nabundantSeq(5)"],
		seeAlso: [
			"numberTheory.abundantNth",
			"numberTheory.abundantTakeWhile",
			"numberTheory.isAbundant",
			"numberTheory.deficientSeq",
			"numberTheory.perfectSeq"
		]
	},
	abundantTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the abundant numbers while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { abundantTakeWhile } = import(\"numberTheory\");\nabundantTakeWhile(-> $ < 100)"],
		seeAlso: [
			"numberTheory.abundantSeq",
			"numberTheory.abundantNth",
			"numberTheory.isAbundant"
		]
	},
	abundantNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the abundant numbers.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the number in the sequence."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { abundantNth } = import(\"numberTheory\");\nabundantNth(1)", "let { abundantNth } = import(\"numberTheory\");\nabundantNth(5)"],
		seeAlso: [
			"numberTheory.abundantSeq",
			"numberTheory.abundantTakeWhile",
			"numberTheory.isAbundant"
		]
	},
	isAbundant: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a number is abundant.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { isAbundant } = import(\"numberTheory\");\nisAbundant(12)", "let { isAbundant } = import(\"numberTheory\");\nisAbundant(15)"],
		seeAlso: [
			"numberTheory.abundantSeq",
			"numberTheory.abundantNth",
			"numberTheory.isDeficient",
			"numberTheory.isPerfect",
			"numberTheory.sigma",
			"numberTheory.divisors",
			"numberTheory.abundantTakeWhile"
		]
	},
	arithmeticSeq: {
		type: "(Number, Number, Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the arithmetic sequence for a given `start`, `step`, and `length`.",
		returns: {
			type: "integer",
			array: true
		},
		args: {
			start: {
				type: "number",
				description: "The starting term of the sequence."
			},
			step: {
				type: "number",
				description: "The common difference of the sequence."
			},
			length: {
				type: "integer",
				description: "The length of the sequence to generate."
			}
		},
		variants: [{ argumentNames: [
			"start",
			"step",
			"length"
		] }],
		examples: [
			"let { arithmeticSeq } = import(\"numberTheory\");\narithmeticSeq(3, 2, 2)",
			"let { arithmeticSeq } = import(\"numberTheory\");\narithmeticSeq(2, 3, 2)",
			"let { arithmeticSeq } = import(\"numberTheory\");\narithmeticSeq(1, 2, 2)",
			"let { arithmeticSeq } = import(\"numberTheory\");\narithmeticSeq(1, 1.5, 12)"
		],
		seeAlso: [
			"numberTheory.arithmeticNth",
			"numberTheory.arithmeticTakeWhile",
			"numberTheory.isArithmetic",
			"numberTheory.geometricSeq"
		]
	},
	arithmeticTakeWhile: {
		type: "(Number, Number, (Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the arithmetic sequence while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: {
			start: {
				type: "number",
				description: "The starting term of the sequence."
			},
			step: {
				type: "number",
				description: "The common difference of the sequence."
			},
			takeWhile: {
				type: "function",
				description: "A function that takes a number and an index and returns a boolean."
			}
		},
		variants: [{ argumentNames: [
			"start",
			"step",
			"takeWhile"
		] }],
		examples: ["let { arithmeticTakeWhile } = import(\"numberTheory\");\narithmeticTakeWhile(1, 0.25, -> $ < 3)"],
		seeAlso: [
			"numberTheory.arithmeticSeq",
			"numberTheory.arithmeticNth",
			"numberTheory.isArithmetic"
		]
	},
	arithmeticNth: {
		type: "(Number, Number, Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the arithmetic sequence.",
		returns: { type: "number" },
		args: {
			start: {
				type: "number",
				description: "The starting term of the sequence."
			},
			step: {
				type: "number",
				description: "The common difference of the sequence."
			},
			n: {
				type: "integer",
				description: "The index of the term to generate."
			}
		},
		variants: [{ argumentNames: [
			"start",
			"step",
			"n"
		] }],
		examples: [
			"let { arithmeticNth } = import(\"numberTheory\");\narithmeticNth(3, 2, 2)",
			"let { arithmeticNth } = import(\"numberTheory\");\narithmeticNth(2, 3, 2)",
			"let { arithmeticNth } = import(\"numberTheory\");\narithmeticNth(1, 2, 2)",
			"let { arithmeticNth } = import(\"numberTheory\");\narithmeticNth(1, 1.5, 12)"
		],
		seeAlso: [
			"numberTheory.arithmeticSeq",
			"numberTheory.arithmeticTakeWhile",
			"numberTheory.isArithmetic"
		]
	},
	isArithmetic: {
		type: "(Number, Number, Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a number is part of the arithmetic sequence.",
		returns: { type: "boolean" },
		args: {
			start: {
				type: "number",
				description: "The starting term of the sequence."
			},
			step: {
				type: "number",
				description: "The common difference of the sequence."
			},
			n: {
				type: "integer",
				description: "The number to check."
			}
		},
		variants: [{ argumentNames: [
			"start",
			"step",
			"n"
		] }],
		examples: [
			"let { isArithmetic } = import(\"numberTheory\");\nisArithmetic(3, 2, 2)",
			"let { isArithmetic } = import(\"numberTheory\");\nisArithmetic(2, 3, 2)",
			"let { isArithmetic } = import(\"numberTheory\");\nisArithmetic(1, 2, 2)",
			"let { isArithmetic } = import(\"numberTheory\");\nisArithmetic(1, 1.5, 12)"
		],
		seeAlso: [
			"numberTheory.arithmeticSeq",
			"numberTheory.arithmeticNth",
			"numberTheory.isGeometric",
			"numberTheory.arithmeticTakeWhile"
		]
	},
	bellSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the Bell sequence up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate. If not provided, the default is 22 (the maximum length of the pre-calculated bell numbers)."
		} },
		variants: [{ argumentNames: ["length"] }, { argumentNames: [] }],
		examples: [
			"let { bellSeq } = import(\"numberTheory\");\nbellSeq(5)",
			"let { bellSeq } = import(\"numberTheory\");\nbellSeq(10)",
			"let { bellSeq } = import(\"numberTheory\");\nbellSeq()"
		],
		seeAlso: [
			"numberTheory.bellNth",
			"numberTheory.bellTakeWhile",
			"numberTheory.isBell",
			"numberTheory.catalanSeq",
			"numberTheory.stirlingSecond",
			"numberTheory.stirlingFirst"
		]
	},
	bellTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the Bell sequence while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { bellTakeWhile } = import(\"numberTheory\");\nbellTakeWhile(-> $ < 1000)"],
		seeAlso: [
			"numberTheory.bellSeq",
			"numberTheory.bellNth",
			"numberTheory.isBell"
		]
	},
	bellNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the Bell sequence.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the term to generate."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { bellNth } = import(\"numberTheory\");\nbellNth(5)", "let { bellNth } = import(\"numberTheory\");\nbellNth(10)"],
		seeAlso: [
			"numberTheory.bellSeq",
			"numberTheory.bellTakeWhile",
			"numberTheory.isBell"
		]
	},
	isBell: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a number is in the Bell sequence.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { isBell } = import(\"numberTheory\");\nisBell(1)",
			"let { isBell } = import(\"numberTheory\");\nisBell(27644437)",
			"let { isBell } = import(\"numberTheory\");\nisBell(27644436)"
		],
		seeAlso: [
			"numberTheory.bellSeq",
			"numberTheory.bellNth",
			"numberTheory.isCatalan",
			"numberTheory.bellTakeWhile"
		]
	},
	bernoulliSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the Bernoulli sequence up to a specified length.",
		returns: {
			type: "number",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate."
		} },
		variants: [{ argumentNames: ["length"] }],
		examples: ["let { bernoulliSeq } = import(\"numberTheory\");\nbernoulliSeq(5)", "let { bernoulliSeq } = import(\"numberTheory\");\nbernoulliSeq(10)"],
		seeAlso: ["numberTheory.bernoulliNth", "numberTheory.bernoulliTakeWhile"]
	},
	bernoulliTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the Bernoulli sequence while a condition is met.",
		returns: {
			type: "number",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { bernoulliTakeWhile } = import(\"numberTheory\");\nbernoulliTakeWhile(-> abs($) < 100)"],
		seeAlso: ["numberTheory.bernoulliSeq", "numberTheory.bernoulliNth"]
	},
	bernoulliNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the Bernoulli sequence.",
		returns: { type: "number" },
		args: { n: {
			type: "integer",
			description: "The index of the term to generate."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { bernoulliNth } = import(\"numberTheory\");\nbernoulliNth(5)",
			"let { bernoulliNth } = import(\"numberTheory\");\nbernoulliNth(10)",
			"let { bernoulliNth } = import(\"numberTheory\");\nbernoulliNth(23)"
		],
		seeAlso: ["numberTheory.bernoulliSeq", "numberTheory.bernoulliTakeWhile"]
	},
	catalanSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the Catalan sequence up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate. If not provided, the default is 30 (the maximum length of the pre-calculated catalan numbers)."
		} },
		variants: [{ argumentNames: ["length"] }, { argumentNames: [] }],
		examples: [
			"let { catalanSeq } = import(\"numberTheory\");\ncatalanSeq(5)",
			"let { catalanSeq } = import(\"numberTheory\");\ncatalanSeq(10)",
			"let { catalanSeq } = import(\"numberTheory\");\ncatalanSeq()"
		],
		seeAlso: [
			"numberTheory.catalanNth",
			"numberTheory.catalanTakeWhile",
			"numberTheory.isCatalan",
			"numberTheory.bellSeq"
		]
	},
	catalanTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the Catalan sequence while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { catalanTakeWhile } = import(\"numberTheory\");\ncatalanTakeWhile(-> $ < 1000)"],
		seeAlso: [
			"numberTheory.catalanSeq",
			"numberTheory.catalanNth",
			"numberTheory.isCatalan"
		]
	},
	catalanNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the Catalan sequence.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the term to generate."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { catalanNth } = import(\"numberTheory\");\ncatalanNth(5)", "let { catalanNth } = import(\"numberTheory\");\ncatalanNth(10)"],
		seeAlso: [
			"numberTheory.catalanSeq",
			"numberTheory.catalanTakeWhile",
			"numberTheory.isCatalan"
		]
	},
	isCatalan: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Determines if a number is in the Catalan sequence.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { isCatalan } = import(\"numberTheory\");\nisCatalan(5)", "let { isCatalan } = import(\"numberTheory\");\nisCatalan(10)"],
		seeAlso: [
			"numberTheory.catalanSeq",
			"numberTheory.catalanNth",
			"numberTheory.isBell",
			"numberTheory.catalanTakeWhile"
		]
	},
	collatzSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the collatz sequence starting from a given integer.",
		returns: {
			type: "integer",
			array: true
		},
		args: { start: {
			type: "integer",
			description: "The starting integer for the collatz sequence."
		} },
		variants: [{ argumentNames: ["start"] }],
		examples: ["let { collatzSeq } = import(\"numberTheory\");\ncollatzSeq(3)", "let { collatzSeq } = import(\"numberTheory\");\ncollatzSeq(11)"],
		seeAlso: ["numberTheory.jugglerSeq"]
	},
	compositeSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the composite sequence up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate."
		} },
		variants: [{ argumentNames: ["length"] }],
		examples: [
			"let { compositeSeq } = import(\"numberTheory\");\ncompositeSeq(1)",
			"let { compositeSeq } = import(\"numberTheory\");\ncompositeSeq(2)",
			"let { compositeSeq } = import(\"numberTheory\");\ncompositeSeq(10)"
		],
		seeAlso: [
			"numberTheory.compositeNth",
			"numberTheory.compositeTakeWhile",
			"numberTheory.isComposite",
			"numberTheory.primeSeq"
		]
	},
	compositeTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the composite sequence while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { compositeTakeWhile } = import(\"numberTheory\");\ncompositeTakeWhile(-> $ < 50)"],
		seeAlso: [
			"numberTheory.compositeSeq",
			"numberTheory.compositeNth",
			"numberTheory.isComposite"
		]
	},
	compositeNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the composite sequence.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the composite number to retrieve."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { compositeNth } = import(\"numberTheory\");\ncompositeNth(1)",
			"let { compositeNth } = import(\"numberTheory\");\ncompositeNth(2)",
			"let { compositeNth } = import(\"numberTheory\");\ncompositeNth(10)"
		],
		seeAlso: [
			"numberTheory.compositeSeq",
			"numberTheory.compositeTakeWhile",
			"numberTheory.isComposite"
		]
	},
	isComposite: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Determines if a number is composite.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { isComposite } = import(\"numberTheory\");\nisComposite(4)",
			"let { isComposite } = import(\"numberTheory\");\nisComposite(5)",
			"let { isComposite } = import(\"numberTheory\");\nisComposite(11)"
		],
		seeAlso: [
			"numberTheory.compositeSeq",
			"numberTheory.compositeNth",
			"numberTheory.isPrime",
			"numberTheory.primeFactors",
			"numberTheory.compositeTakeWhile"
		]
	},
	deficientSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the deficient numbers up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate."
		} },
		variants: [{ argumentNames: ["length"] }],
		examples: ["let { deficientSeq } = import(\"numberTheory\");\ndeficientSeq(1)", "let { deficientSeq } = import(\"numberTheory\");\ndeficientSeq(5)"],
		seeAlso: [
			"numberTheory.deficientNth",
			"numberTheory.deficientTakeWhile",
			"numberTheory.isDeficient",
			"numberTheory.abundantSeq",
			"numberTheory.perfectSeq"
		]
	},
	deficientTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the deficient numbers while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { deficientTakeWhile } = import(\"numberTheory\");\ndeficientTakeWhile(-> $ < 100)"],
		seeAlso: [
			"numberTheory.deficientSeq",
			"numberTheory.deficientNth",
			"numberTheory.isDeficient"
		]
	},
	deficientNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the deficient numbers.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the number in the sequence."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { deficientNth } = import(\"numberTheory\");\ndeficientNth(5)", "let { deficientNth } = import(\"numberTheory\");\ndeficientNth(12)"],
		seeAlso: [
			"numberTheory.deficientSeq",
			"numberTheory.deficientTakeWhile",
			"numberTheory.isDeficient"
		]
	},
	isDeficient: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a number is deficient.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { isDeficient } = import(\"numberTheory\");\nisDeficient(12)", "let { isDeficient } = import(\"numberTheory\");\nisDeficient(15)"],
		seeAlso: [
			"numberTheory.deficientSeq",
			"numberTheory.deficientNth",
			"numberTheory.isAbundant",
			"numberTheory.isPerfect",
			"numberTheory.sigma",
			"numberTheory.divisors",
			"numberTheory.deficientTakeWhile"
		]
	},
	factorialSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the factorial sequence up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate. If not provided, the default is 19 (the maximum length of the pre-calculated factorial numbers)."
		} },
		variants: [{ argumentNames: ["length"] }, { argumentNames: [] }],
		examples: [
			"let { factorialSeq } = import(\"numberTheory\");\nfactorialSeq(1)",
			"let { factorialSeq } = import(\"numberTheory\");\nfactorialSeq(2)",
			"let { factorialSeq } = import(\"numberTheory\");\nfactorialSeq(3)",
			"let { factorialSeq } = import(\"numberTheory\");\nfactorialSeq(4)",
			"let { factorialSeq } = import(\"numberTheory\");\nfactorialSeq(5)",
			"let { factorialSeq } = import(\"numberTheory\");\nfactorialSeq(10)"
		],
		seeAlso: [
			"numberTheory.factorialNth",
			"numberTheory.factorialTakeWhile",
			"numberTheory.isFactorial",
			"numberTheory.factorial"
		]
	},
	factorialTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the factorial sequence while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { factorialTakeWhile } = import(\"numberTheory\");\nfactorialTakeWhile(-> $ < 1000)"],
		seeAlso: [
			"numberTheory.factorialSeq",
			"numberTheory.factorialNth",
			"numberTheory.isFactorial"
		]
	},
	factorialNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the factorial sequence.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the term to generate."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { factorialNth } = import(\"numberTheory\");\nfactorialNth(1)",
			"let { factorialNth } = import(\"numberTheory\");\nfactorialNth(2)",
			"let { factorialNth } = import(\"numberTheory\");\nfactorialNth(3)",
			"let { factorialNth } = import(\"numberTheory\");\nfactorialNth(4)",
			"let { factorialNth } = import(\"numberTheory\");\nfactorialNth(5)",
			"let { factorialNth } = import(\"numberTheory\");\nfactorialNth(10)"
		],
		seeAlso: [
			"numberTheory.factorialSeq",
			"numberTheory.factorialTakeWhile",
			"numberTheory.isFactorial",
			"numberTheory.factorial"
		]
	},
	isFactorial: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a number is in the factorial sequence.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { isFactorial } = import(\"numberTheory\");\nisFactorial(1)",
			"let { isFactorial } = import(\"numberTheory\");\nisFactorial(2)",
			"let { isFactorial } = import(\"numberTheory\");\nisFactorial(3)",
			"let { isFactorial } = import(\"numberTheory\");\nisFactorial(4)",
			"let { isFactorial } = import(\"numberTheory\");\nisFactorial(5)",
			"let { isFactorial } = import(\"numberTheory\");\nisFactorial(6)",
			"let { isFactorial } = import(\"numberTheory\");\nisFactorial(7)",
			"let { isFactorial } = import(\"numberTheory\");\nisFactorial(8)",
			"let { isFactorial } = import(\"numberTheory\");\nisFactorial(9)",
			"let { isFactorial } = import(\"numberTheory\");\nisFactorial(3628800)"
		],
		seeAlso: [
			"numberTheory.factorialSeq",
			"numberTheory.factorialNth",
			"numberTheory.factorial",
			"numberTheory.factorialTakeWhile"
		]
	},
	fibonacciSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the fibonacci sequence up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate. If not provided, the default is 79 (the maximum length of the pre-calculated Fibonacci numbers)."
		} },
		variants: [{ argumentNames: ["length"] }, { argumentNames: [] }],
		examples: [
			"let { fibonacciSeq } = import(\"numberTheory\");\nfibonacciSeq(1)",
			"let { fibonacciSeq } = import(\"numberTheory\");\nfibonacciSeq(2)",
			"let { fibonacciSeq } = import(\"numberTheory\");\nfibonacciSeq()"
		],
		seeAlso: [
			"numberTheory.fibonacciNth",
			"numberTheory.fibonacciTakeWhile",
			"numberTheory.isFibonacci",
			"numberTheory.lucasSeq",
			"numberTheory.tribonacciSeq",
			"numberTheory.pellSeq",
			"numberTheory.padovanSeq"
		]
	},
	fibonacciTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the fibonacci sequence while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { fibonacciTakeWhile } = import(\"numberTheory\");\nfibonacciTakeWhile(-> $ < 100)"],
		seeAlso: [
			"numberTheory.fibonacciSeq",
			"numberTheory.fibonacciNth",
			"numberTheory.isFibonacci"
		]
	},
	fibonacciNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the fibonacci sequence.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the term to generate."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { fibonacciNth } = import(\"numberTheory\");\nfibonacciNth(5)", "let { fibonacciNth } = import(\"numberTheory\");\nfibonacciNth(50)"],
		seeAlso: [
			"numberTheory.fibonacciSeq",
			"numberTheory.fibonacciTakeWhile",
			"numberTheory.isFibonacci"
		]
	},
	isFibonacci: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Determines if a number is in the fibonacci sequence.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { isFibonacci } = import(\"numberTheory\");\nisFibonacci(0)",
			"let { isFibonacci } = import(\"numberTheory\");\nisFibonacci(1)",
			"let { isFibonacci } = import(\"numberTheory\");\nisFibonacci(2)",
			"let { isFibonacci } = import(\"numberTheory\");\nisFibonacci(3)",
			"let { isFibonacci } = import(\"numberTheory\");\nisFibonacci(4)",
			"let { isFibonacci } = import(\"numberTheory\");\nisFibonacci(5)",
			"let { isFibonacci } = import(\"numberTheory\");\nisFibonacci(6)",
			"let { isFibonacci } = import(\"numberTheory\");\nisFibonacci(7)",
			"let { isFibonacci } = import(\"numberTheory\");\nisFibonacci(8)",
			"let { isFibonacci } = import(\"numberTheory\");\nisFibonacci(9)"
		],
		seeAlso: [
			"numberTheory.fibonacciSeq",
			"numberTheory.fibonacciNth",
			"numberTheory.isLucas",
			"numberTheory.fibonacciTakeWhile",
			"numberTheory.isTribonacci",
			"numberTheory.isPadovan",
			"numberTheory.isPell"
		]
	},
	geometricSeq: {
		type: "(Number, Number, Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the geometric sequence for a given `start`, `ratio`, and `length`.",
		returns: {
			type: "integer",
			array: true
		},
		args: {
			start: {
				type: "number",
				description: "The starting term of the sequence."
			},
			ratio: {
				type: "number",
				description: "The common ratio of the sequence."
			},
			length: {
				type: "integer",
				description: "The length of the sequence to generate."
			}
		},
		variants: [{ argumentNames: [
			"start",
			"ratio",
			"length"
		] }],
		examples: [
			"let { geometricSeq } = import(\"numberTheory\");\ngeometricSeq(3, 2, 2)",
			"let { geometricSeq } = import(\"numberTheory\");\ngeometricSeq(2, 3, 2)",
			"let { geometricSeq } = import(\"numberTheory\");\ngeometricSeq(1, 2, 2)",
			"let { geometricSeq } = import(\"numberTheory\");\ngeometricSeq(1, 1.5, 12)"
		],
		seeAlso: [
			"numberTheory.geometricNth",
			"numberTheory.geometricTakeWhile",
			"numberTheory.isGeometric",
			"numberTheory.arithmeticSeq"
		]
	},
	geometricTakeWhile: {
		type: "(Number, Number, (Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the geometric sequence while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: {
			start: {
				type: "number",
				description: "The starting term of the sequence."
			},
			ratio: {
				type: "number",
				description: "The common ratio of the sequence."
			},
			takeWhile: {
				type: "function",
				description: "A function that takes a number and an index and returns a boolean."
			}
		},
		variants: [{ argumentNames: [
			"start",
			"ratio",
			"takeWhile"
		] }],
		examples: ["let { geometricTakeWhile } = import(\"numberTheory\");\ngeometricTakeWhile(1, 1.5, -> $ < 10)"],
		seeAlso: [
			"numberTheory.geometricSeq",
			"numberTheory.geometricNth",
			"numberTheory.isGeometric"
		]
	},
	geometricNth: {
		type: "(Number, Number, Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the geometric sequence.",
		returns: { type: "number" },
		args: {
			start: {
				type: "number",
				description: "The starting term of the sequence."
			},
			ratio: {
				type: "number",
				description: "The common ratio of the sequence."
			},
			n: {
				type: "integer",
				description: "The index of the term to generate."
			}
		},
		variants: [{ argumentNames: [
			"start",
			"ratio",
			"n"
		] }],
		examples: [
			"let { geometricNth } = import(\"numberTheory\");\ngeometricNth(3, 2, 2)",
			"let { geometricNth } = import(\"numberTheory\");\ngeometricNth(2, 3, 2)",
			"let { geometricNth } = import(\"numberTheory\");\ngeometricNth(1, 2, 2)",
			"let { geometricNth } = import(\"numberTheory\");\ngeometricNth(1, 1.5, 4)"
		],
		seeAlso: [
			"numberTheory.geometricSeq",
			"numberTheory.geometricTakeWhile",
			"numberTheory.isGeometric"
		]
	},
	isGeometric: {
		type: "(Number, Number, Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a number is in the geometric sequence.",
		returns: { type: "boolean" },
		args: {
			start: {
				type: "number",
				description: "The starting term of the sequence."
			},
			ratio: {
				type: "number",
				description: "The common ratio of the sequence."
			},
			n: {
				type: "number",
				description: "The number to check."
			}
		},
		variants: [{ argumentNames: [
			"start",
			"ratio",
			"n"
		] }],
		examples: [
			"let { isGeometric } = import(\"numberTheory\");\nisGeometric(1, 2, 1)",
			"let { isGeometric } = import(\"numberTheory\");\nisGeometric(2, 3, 2)",
			"let { isGeometric } = import(\"numberTheory\");\nisGeometric(3, 2, 2)",
			"let { isGeometric } = import(\"numberTheory\");\nisGeometric(1, 1.5, 2.25)",
			"let { isGeometric } = import(\"numberTheory\");\nisGeometric(1, 1.5, -4)"
		],
		seeAlso: [
			"numberTheory.geometricSeq",
			"numberTheory.geometricNth",
			"numberTheory.isArithmetic",
			"numberTheory.geometricTakeWhile"
		]
	},
	golombSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the Golomb sequence up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate."
		} },
		variants: [{ argumentNames: ["length"] }],
		examples: ["let { golombSeq } = import(\"numberTheory\");\ngolombSeq(5)", "let { golombSeq } = import(\"numberTheory\");\ngolombSeq(20)"],
		seeAlso: [
			"numberTheory.golombNth",
			"numberTheory.golombTakeWhile",
			"numberTheory.isGolomb",
			"numberTheory.recamanSeq"
		]
	},
	golombTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the Golomb sequence while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { golombTakeWhile } = import(\"numberTheory\");\ngolombTakeWhile(-> $ <= 10)"],
		seeAlso: [
			"numberTheory.golombSeq",
			"numberTheory.golombNth",
			"numberTheory.isGolomb"
		]
	},
	golombNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the Golomb sequence.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the term to generate."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { golombNth } = import(\"numberTheory\");\ngolombNth(5)", "let { golombNth } = import(\"numberTheory\");\ngolombNth(1000)"],
		seeAlso: [
			"numberTheory.golombSeq",
			"numberTheory.golombTakeWhile",
			"numberTheory.isGolomb"
		]
	},
	isGolomb: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a number is in the Golomb sequence.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { isGolomb } = import(\"numberTheory\");\nisGolomb(1)",
			"let { isGolomb } = import(\"numberTheory\");\nisGolomb(2)",
			"let { isGolomb } = import(\"numberTheory\");\nisGolomb(3345)",
			"let { isGolomb } = import(\"numberTheory\");\nisGolomb(67867864)"
		],
		seeAlso: [
			"numberTheory.golombSeq",
			"numberTheory.golombNth",
			"numberTheory.golombTakeWhile"
		]
	},
	happySeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the happy sequence up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate."
		} },
		variants: [{ argumentNames: ["length"] }],
		examples: [
			"let { happySeq } = import(\"numberTheory\");\nhappySeq(1)",
			"let { happySeq } = import(\"numberTheory\");\nhappySeq(2)",
			"let { happySeq } = import(\"numberTheory\");\nhappySeq(20)"
		],
		seeAlso: [
			"numberTheory.happyNth",
			"numberTheory.happyTakeWhile",
			"numberTheory.isHappy",
			"numberTheory.luckySeq"
		]
	},
	happyTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the happy sequence while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { happyTakeWhile } = import(\"numberTheory\");\nhappyTakeWhile(-> $ < 100)"],
		seeAlso: [
			"numberTheory.happySeq",
			"numberTheory.happyNth",
			"numberTheory.isHappy"
		]
	},
	happyNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the happy sequence.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the happy number to return."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { happyNth } = import(\"numberTheory\");\nhappyNth(1)",
			"let { happyNth } = import(\"numberTheory\");\nhappyNth(2)",
			"let { happyNth } = import(\"numberTheory\");\nhappyNth(20)"
		],
		seeAlso: [
			"numberTheory.happySeq",
			"numberTheory.happyTakeWhile",
			"numberTheory.isHappy"
		]
	},
	isHappy: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Determines if a number is a happy number.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { isHappy } = import(\"numberTheory\");\nisHappy(1)",
			"let { isHappy } = import(\"numberTheory\");\nisHappy(2)",
			"let { isHappy } = import(\"numberTheory\");\nisHappy(100)"
		],
		seeAlso: [
			"numberTheory.happySeq",
			"numberTheory.happyNth",
			"numberTheory.happyTakeWhile"
		]
	},
	jugglerSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the Juggler sequence starting from a given integer.",
		returns: {
			type: "integer",
			array: true
		},
		args: { start: {
			type: "integer",
			description: "The starting integer for the Juggler sequence."
		} },
		variants: [{ argumentNames: ["start"] }],
		examples: ["let { jugglerSeq } = import(\"numberTheory\");\njugglerSeq(3)", "let { jugglerSeq } = import(\"numberTheory\");\njugglerSeq(5)"],
		seeAlso: ["numberTheory.collatzSeq"]
	},
	lookAndSaySeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the Look-and-Say sequence up to a specified length.",
		returns: {
			type: "string",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate."
		} },
		variants: [{ argumentNames: ["length"] }],
		examples: ["let { lookAndSaySeq } = import(\"numberTheory\");\nlookAndSaySeq(5)"],
		seeAlso: [
			"numberTheory.lookAndSayNth",
			"numberTheory.lookAndSayTakeWhile",
			"numberTheory.isLookAndSay"
		]
	},
	lookAndSayTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the Look-and-Say sequence while a condition is met.",
		returns: {
			type: "string",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes a string and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { lookAndSayTakeWhile } = import(\"numberTheory\");\nlookAndSayTakeWhile((term, index) -> count(term) < 10)", "let { lookAndSayTakeWhile } = import(\"numberTheory\");\nlookAndSayTakeWhile(-> $2 <= 10)"],
		seeAlso: [
			"numberTheory.lookAndSaySeq",
			"numberTheory.lookAndSayNth",
			"numberTheory.isLookAndSay"
		]
	},
	lookAndSayNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the Look-and-Say sequence.",
		returns: { type: "string" },
		args: { n: {
			type: "integer",
			description: "The index of the term in the sequence."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { lookAndSayNth } = import(\"numberTheory\");\nlookAndSayNth(5)"],
		seeAlso: [
			"numberTheory.lookAndSaySeq",
			"numberTheory.lookAndSayTakeWhile",
			"numberTheory.isLookAndSay"
		]
	},
	isLookAndSay: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a string is a valid Look-and-Say term.",
		returns: { type: "boolean" },
		args: { term: {
			type: "string",
			description: "The term to check."
		} },
		variants: [{ argumentNames: ["term"] }],
		examples: ["let { isLookAndSay } = import(\"numberTheory\");\nisLookAndSay(\"111221\")", "let { isLookAndSay } = import(\"numberTheory\");\nisLookAndSay(\"123\")"],
		seeAlso: [
			"numberTheory.lookAndSaySeq",
			"numberTheory.lookAndSayNth",
			"numberTheory.lookAndSayTakeWhile"
		]
	},
	lucasSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the lucas sequence up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate. If not provided, the default is 77 (the maximum length of the pre-calculated Lucas numbers)."
		} },
		variants: [{ argumentNames: ["length"] }, { argumentNames: [] }],
		examples: [
			"let { lucasSeq } = import(\"numberTheory\");\nlucasSeq(1)",
			"let { lucasSeq } = import(\"numberTheory\");\nlucasSeq(2)",
			"let { lucasSeq } = import(\"numberTheory\");\nlucasSeq()"
		],
		seeAlso: [
			"numberTheory.lucasNth",
			"numberTheory.lucasTakeWhile",
			"numberTheory.isLucas",
			"numberTheory.fibonacciSeq"
		]
	},
	lucasTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the lucas sequence while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { lucasTakeWhile } = import(\"numberTheory\");\nlucasTakeWhile(-> $ < 100)"],
		seeAlso: [
			"numberTheory.lucasSeq",
			"numberTheory.lucasNth",
			"numberTheory.isLucas"
		]
	},
	lucasNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the lucas sequence.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the term to generate."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { lucasNth } = import(\"numberTheory\");\nlucasNth(1)",
			"let { lucasNth } = import(\"numberTheory\");\nlucasNth(2)",
			"let { lucasNth } = import(\"numberTheory\");\nlucasNth(10)"
		],
		seeAlso: [
			"numberTheory.lucasSeq",
			"numberTheory.lucasTakeWhile",
			"numberTheory.isLucas"
		]
	},
	isLucas: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Determines if a number is in the lucas sequence.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { isLucas } = import(\"numberTheory\");\nisLucas(1)",
			"let { isLucas } = import(\"numberTheory\");\nisLucas(2)",
			"let { isLucas } = import(\"numberTheory\");\nisLucas(10)"
		],
		seeAlso: [
			"numberTheory.lucasSeq",
			"numberTheory.lucasNth",
			"numberTheory.isFibonacci",
			"numberTheory.lucasTakeWhile"
		]
	},
	luckySeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the lucky sequence up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate."
		} },
		variants: [{ argumentNames: ["length"] }],
		examples: [
			"let { luckySeq } = import(\"numberTheory\");\nluckySeq(1)",
			"let { luckySeq } = import(\"numberTheory\");\nluckySeq(2)",
			"let { luckySeq } = import(\"numberTheory\");\nluckySeq(20)"
		],
		seeAlso: [
			"numberTheory.luckyNth",
			"numberTheory.luckyTakeWhile",
			"numberTheory.isLucky",
			"numberTheory.happySeq",
			"numberTheory.primeSeq"
		]
	},
	luckyTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the lucky sequence while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { luckyTakeWhile } = import(\"numberTheory\");\nluckyTakeWhile(-> $ < 100)"],
		seeAlso: [
			"numberTheory.luckySeq",
			"numberTheory.luckyNth",
			"numberTheory.isLucky"
		]
	},
	luckyNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the lucky sequence.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The position in the sequence."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { luckyNth } = import(\"numberTheory\");\nluckyNth(1)",
			"let { luckyNth } = import(\"numberTheory\");\nluckyNth(2)",
			"let { luckyNth } = import(\"numberTheory\");\nluckyNth(20)"
		],
		seeAlso: [
			"numberTheory.luckySeq",
			"numberTheory.luckyTakeWhile",
			"numberTheory.isLucky"
		]
	},
	isLucky: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a number is a lucky number.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { isLucky } = import(\"numberTheory\");\nisLucky(4)",
			"let { isLucky } = import(\"numberTheory\");\nisLucky(7)",
			"let { isLucky } = import(\"numberTheory\");\nisLucky(33)"
		],
		seeAlso: [
			"numberTheory.luckySeq",
			"numberTheory.luckyNth",
			"numberTheory.isPrime",
			"numberTheory.luckyTakeWhile"
		]
	},
	mersenneSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the Mersenne sequence up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate. If not provided, the default is 9 (the maximum length of the pre-calculated mersenne numbers)."
		} },
		variants: [{ argumentNames: ["length"] }, { argumentNames: [] }],
		examples: [
			"let { mersenneSeq } = import(\"numberTheory\");\nmersenneSeq(1)",
			"let { mersenneSeq } = import(\"numberTheory\");\nmersenneSeq(5)",
			"let { mersenneSeq } = import(\"numberTheory\");\nmersenneSeq()"
		],
		seeAlso: [
			"numberTheory.mersenneNth",
			"numberTheory.mersenneTakeWhile",
			"numberTheory.isMersenne",
			"numberTheory.primeSeq"
		]
	},
	mersenneTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the Mersenne sequence while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { mersenneTakeWhile } = import(\"numberTheory\");\nmersenneTakeWhile(-> $ < 1000)"],
		seeAlso: [
			"numberTheory.mersenneSeq",
			"numberTheory.mersenneNth",
			"numberTheory.isMersenne"
		]
	},
	mersenneNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the Mersenne sequence.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the term to generate."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { mersenneNth } = import(\"numberTheory\");\nmersenneNth(1)", "let { mersenneNth } = import(\"numberTheory\");\nmersenneNth(5)"],
		seeAlso: [
			"numberTheory.mersenneSeq",
			"numberTheory.mersenneTakeWhile",
			"numberTheory.isMersenne"
		]
	},
	isMersenne: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a number is in the Mersenne sequence.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { isMersenne } = import(\"numberTheory\");\nisMersenne(3)",
			"let { isMersenne } = import(\"numberTheory\");\nisMersenne(4)",
			"let { isMersenne } = import(\"numberTheory\");\nisMersenne(7)"
		],
		seeAlso: [
			"numberTheory.mersenneSeq",
			"numberTheory.mersenneNth",
			"numberTheory.isPrime",
			"numberTheory.mersenneTakeWhile"
		]
	},
	padovanSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the Padovan sequence up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate."
		} },
		variants: [{ argumentNames: ["length"] }],
		examples: [
			"let { padovanSeq } = import(\"numberTheory\");\npadovanSeq(5)",
			"let { padovanSeq } = import(\"numberTheory\");\npadovanSeq(10)",
			"let { padovanSeq } = import(\"numberTheory\");\npadovanSeq(20)"
		],
		seeAlso: [
			"numberTheory.padovanNth",
			"numberTheory.padovanTakeWhile",
			"numberTheory.isPadovan",
			"numberTheory.fibonacciSeq"
		]
	},
	padovanTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the Padovan sequence while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { padovanTakeWhile } = import(\"numberTheory\");\npadovanTakeWhile(-> $ < 1000)"],
		seeAlso: [
			"numberTheory.padovanSeq",
			"numberTheory.padovanNth",
			"numberTheory.isPadovan"
		]
	},
	padovanNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the Padovan sequence.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the term to generate."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { padovanNth } = import(\"numberTheory\");\npadovanNth(5)",
			"let { padovanNth } = import(\"numberTheory\");\npadovanNth(10)",
			"let { padovanNth } = import(\"numberTheory\");\npadovanNth(20)"
		],
		seeAlso: [
			"numberTheory.padovanSeq",
			"numberTheory.padovanTakeWhile",
			"numberTheory.isPadovan"
		]
	},
	isPadovan: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a number is in the Padovan sequence.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { isPadovan } = import(\"numberTheory\");\nisPadovan(1)",
			"let { isPadovan } = import(\"numberTheory\");\nisPadovan(265)",
			"let { isPadovan } = import(\"numberTheory\");\nisPadovan(6)"
		],
		seeAlso: [
			"numberTheory.padovanSeq",
			"numberTheory.padovanNth",
			"numberTheory.isFibonacci",
			"numberTheory.padovanTakeWhile"
		]
	},
	partitionSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the partition numbers up to a specified length. If no length is provided, it defaults to 299 (the maximum length of the pre-calculated partition numbers).",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate."
		} },
		variants: [{ argumentNames: ["length"] }, { argumentNames: [] }],
		examples: [
			"let { partitionSeq } = import(\"numberTheory\");\npartitionSeq(1)",
			"let { partitionSeq } = import(\"numberTheory\");\npartitionSeq(10)",
			"let { partitionSeq } = import(\"numberTheory\");\npartitionSeq()"
		],
		seeAlso: [
			"numberTheory.partitionNth",
			"numberTheory.partitionTakeWhile",
			"numberTheory.isPartition",
			"numberTheory.partitions",
			"numberTheory.countPartitions"
		]
	},
	partitionTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the partition numbers while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { partitionTakeWhile } = import(\"numberTheory\");\npartitionTakeWhile(-> $ < 1000)"],
		seeAlso: [
			"numberTheory.partitionSeq",
			"numberTheory.partitionNth",
			"numberTheory.isPartition"
		]
	},
	partitionNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the partition numbers.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the partition number to generate."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { partitionNth } = import(\"numberTheory\");\npartitionNth(1)", "let { partitionNth } = import(\"numberTheory\");\npartitionNth(5)"],
		seeAlso: [
			"numberTheory.partitionSeq",
			"numberTheory.partitionTakeWhile",
			"numberTheory.isPartition"
		]
	},
	isPartition: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a number is in the partition numbers.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { isPartition } = import(\"numberTheory\");\nisPartition(0)",
			"let { isPartition } = import(\"numberTheory\");\nisPartition(1)",
			"let { isPartition } = import(\"numberTheory\");\nisPartition(2)",
			"let { isPartition } = import(\"numberTheory\");\nisPartition(3)",
			"let { isPartition } = import(\"numberTheory\");\nisPartition(4)",
			"let { isPartition } = import(\"numberTheory\");\nisPartition(5)"
		],
		seeAlso: [
			"numberTheory.partitionSeq",
			"numberTheory.partitionNth",
			"numberTheory.partitions",
			"numberTheory.partitionTakeWhile"
		]
	},
	pellSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the Pell sequence up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate. If not provided, the default is 42 (the maximum length of the pre-calculated Pell numbers)."
		} },
		variants: [{ argumentNames: ["length"] }, { argumentNames: [] }],
		examples: [
			"let { pellSeq } = import(\"numberTheory\");\npellSeq(5)",
			"let { pellSeq } = import(\"numberTheory\");\npellSeq(10)",
			"let { pellSeq } = import(\"numberTheory\");\npellSeq()"
		],
		seeAlso: [
			"numberTheory.pellNth",
			"numberTheory.pellTakeWhile",
			"numberTheory.isPell",
			"numberTheory.fibonacciSeq"
		]
	},
	pellTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the Pell sequence while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { pellTakeWhile } = import(\"numberTheory\");\npellTakeWhile(-> $ < 1000)"],
		seeAlso: [
			"numberTheory.pellSeq",
			"numberTheory.pellNth",
			"numberTheory.isPell"
		]
	},
	pellNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the Pell sequence.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the term to generate."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { pellNth } = import(\"numberTheory\");\npellNth(5)",
			"let { pellNth } = import(\"numberTheory\");\npellNth(10)",
			"let { pellNth } = import(\"numberTheory\");\npellNth(20)"
		],
		seeAlso: [
			"numberTheory.pellSeq",
			"numberTheory.pellTakeWhile",
			"numberTheory.isPell"
		]
	},
	isPell: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a number is a Pell number.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { isPell } = import(\"numberTheory\");\nisPell(1)",
			"let { isPell } = import(\"numberTheory\");\nisPell(470832)",
			"let { isPell } = import(\"numberTheory\");\nisPell(10)"
		],
		seeAlso: [
			"numberTheory.pellSeq",
			"numberTheory.pellNth",
			"numberTheory.isFibonacci",
			"numberTheory.pellTakeWhile"
		]
	},
	perfectSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the perfect numbers up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate. If no length is provided, it defaults to 7 (the maximum length of the pre-calculated perfect numbers)."
		} },
		variants: [{ argumentNames: ["length"] }, { argumentNames: [] }],
		examples: [
			"let { perfectSeq } = import(\"numberTheory\");\nperfectSeq(1)",
			"let { perfectSeq } = import(\"numberTheory\");\nperfectSeq(5)",
			"let { perfectSeq } = import(\"numberTheory\");\nperfectSeq()"
		],
		seeAlso: [
			"numberTheory.perfectNth",
			"numberTheory.perfectTakeWhile",
			"numberTheory.isPerfect",
			"numberTheory.abundantSeq",
			"numberTheory.deficientSeq",
			"numberTheory.isAmicable"
		]
	},
	perfectTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the perfect numbers while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { perfectTakeWhile } = import(\"numberTheory\");\nperfectTakeWhile(-> $ < 1000)"],
		seeAlso: [
			"numberTheory.perfectSeq",
			"numberTheory.perfectNth",
			"numberTheory.isPerfect"
		]
	},
	perfectNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the perfect numbers.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the perfect number to generate."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { perfectNth } = import(\"numberTheory\");\nperfectNth(1)", "let { perfectNth } = import(\"numberTheory\");\nperfectNth(5)"],
		seeAlso: [
			"numberTheory.perfectSeq",
			"numberTheory.perfectTakeWhile",
			"numberTheory.isPerfect"
		]
	},
	isPerfect: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a number is in the perfect numbers.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { isPerfect } = import(\"numberTheory\");\nisPerfect(0)",
			"let { isPerfect } = import(\"numberTheory\");\nisPerfect(1)",
			"let { isPerfect } = import(\"numberTheory\");\nisPerfect(2)",
			"let { isPerfect } = import(\"numberTheory\");\nisPerfect(3)",
			"let { isPerfect } = import(\"numberTheory\");\nisPerfect(4)",
			"let { isPerfect } = import(\"numberTheory\");\nisPerfect(5)",
			"let { isPerfect } = import(\"numberTheory\");\nisPerfect(6)",
			"let { isPerfect } = import(\"numberTheory\");\nisPerfect(7)",
			"let { isPerfect } = import(\"numberTheory\");\nisPerfect(8)",
			"let { isPerfect } = import(\"numberTheory\");\nisPerfect(9)"
		],
		seeAlso: [
			"numberTheory.perfectSeq",
			"numberTheory.perfectNth",
			"numberTheory.isAbundant",
			"numberTheory.isDeficient",
			"numberTheory.sigma",
			"numberTheory.perfectTakeWhile",
			"numberTheory.isAmicable",
			"numberTheory.properDivisors"
		]
	},
	perfectSquareSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the perfect square numbers up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate."
		} },
		variants: [{ argumentNames: ["length"] }],
		examples: ["let { perfectSquareSeq } = import(\"numberTheory\");\nperfectSquareSeq(5)", "let { perfectSquareSeq } = import(\"numberTheory\");\nperfectSquareSeq(20)"],
		seeAlso: [
			"numberTheory.perfectSquareNth",
			"numberTheory.perfectSquareTakeWhile",
			"numberTheory.isPerfectSquare",
			"numberTheory.perfectCubeSeq",
			"numberTheory.perfectPowerSeq",
			"numberTheory.polygonalSeq"
		]
	},
	perfectSquareTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the perfect square numbers while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { perfectSquareTakeWhile } = import(\"numberTheory\");\nperfectSquareTakeWhile(-> $ <= 100)"],
		seeAlso: [
			"numberTheory.perfectSquareSeq",
			"numberTheory.perfectSquareNth",
			"numberTheory.isPerfectSquare"
		]
	},
	perfectSquareNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the perfect square numbers.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the term to generate."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { perfectSquareNth } = import(\"numberTheory\");\nperfectSquareNth(1)", "let { perfectSquareNth } = import(\"numberTheory\");\nperfectSquareNth(5)"],
		seeAlso: [
			"numberTheory.perfectSquareSeq",
			"numberTheory.perfectSquareTakeWhile",
			"numberTheory.isPerfectSquare"
		]
	},
	isPerfectSquare: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a number is a perfect square.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { isPerfectSquare } = import(\"numberTheory\");\nisPerfectSquare(16)", "let { isPerfectSquare } = import(\"numberTheory\");\nisPerfectSquare(20)"],
		seeAlso: [
			"numberTheory.perfectSquareSeq",
			"numberTheory.perfectSquareNth",
			"numberTheory.isPerfectCube",
			"numberTheory.isPerfectPower",
			"numberTheory.perfectSquareTakeWhile",
			"numberTheory.perfectPower",
			"numberTheory.isPolygonal"
		]
	},
	perfectCubeSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the perfect cube numbers up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate."
		} },
		variants: [{ argumentNames: ["length"] }],
		examples: ["let { perfectCubeSeq } = import(\"numberTheory\");\nperfectCubeSeq(5)", "let { perfectCubeSeq } = import(\"numberTheory\");\nperfectCubeSeq(20)"],
		seeAlso: [
			"numberTheory.perfectCubeNth",
			"numberTheory.perfectCubeTakeWhile",
			"numberTheory.isPerfectCube",
			"numberTheory.perfectSquareSeq",
			"numberTheory.perfectPowerSeq"
		]
	},
	perfectCubeTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the perfect cube numbers while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { perfectCubeTakeWhile } = import(\"numberTheory\");\nperfectCubeTakeWhile(-> $ <= 100)"],
		seeAlso: [
			"numberTheory.perfectCubeSeq",
			"numberTheory.perfectCubeNth",
			"numberTheory.isPerfectCube"
		]
	},
	perfectCubeNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the perfect cube numbers.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the term to generate."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { perfectCubeNth } = import(\"numberTheory\");\nperfectCubeNth(1)", "let { perfectCubeNth } = import(\"numberTheory\");\nperfectCubeNth(5)"],
		seeAlso: [
			"numberTheory.perfectCubeSeq",
			"numberTheory.perfectCubeTakeWhile",
			"numberTheory.isPerfectCube"
		]
	},
	isPerfectCube: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a number is in the perfect cube numbers.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { isPerfectCube } = import(\"numberTheory\");\nisPerfectCube(7)",
			"let { isPerfectCube } = import(\"numberTheory\");\nisPerfectCube(8)",
			"let { isPerfectCube } = import(\"numberTheory\");\nisPerfectCube(9)"
		],
		seeAlso: [
			"numberTheory.perfectCubeSeq",
			"numberTheory.perfectCubeNth",
			"numberTheory.isPerfectSquare",
			"numberTheory.isPerfectPower",
			"numberTheory.perfectCubeTakeWhile",
			"numberTheory.perfectPower"
		]
	},
	perfectPowerSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the perfect power numbers up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate."
		} },
		variants: [{ argumentNames: ["length"] }],
		examples: ["let { perfectPowerSeq } = import(\"numberTheory\");\nperfectPowerSeq(5)", "let { perfectPowerSeq } = import(\"numberTheory\");\nperfectPowerSeq(20)"],
		seeAlso: [
			"numberTheory.perfectPowerNth",
			"numberTheory.perfectPowerTakeWhile",
			"numberTheory.isPerfectPower",
			"numberTheory.perfectPower",
			"numberTheory.perfectSquareSeq",
			"numberTheory.perfectCubeSeq"
		]
	},
	perfectPowerTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the perfect power numbers while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { perfectPowerTakeWhile } = import(\"numberTheory\");\nperfectPowerTakeWhile(-> $ <= 100)"],
		seeAlso: [
			"numberTheory.perfectPowerSeq",
			"numberTheory.perfectPowerNth",
			"numberTheory.isPerfectPower"
		]
	},
	perfectPowerNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the perfect power numbers.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the term to generate."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { perfectPowerNth } = import(\"numberTheory\");\nperfectPowerNth(3)", "let { perfectPowerNth } = import(\"numberTheory\");\nperfectPowerNth(15)"],
		seeAlso: [
			"numberTheory.perfectPowerSeq",
			"numberTheory.perfectPowerTakeWhile",
			"numberTheory.isPerfectPower"
		]
	},
	isPerfectPower: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a number is in the perfect power numbers.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { isPerfectPower } = import(\"numberTheory\");\nisPerfectPower(7)",
			"let { isPerfectPower } = import(\"numberTheory\");\nisPerfectPower(8)",
			"let { isPerfectPower } = import(\"numberTheory\");\nisPerfectPower(9)",
			"let { isPerfectPower } = import(\"numberTheory\");\nisPerfectPower(10)"
		],
		seeAlso: [
			"numberTheory.perfectPowerSeq",
			"numberTheory.perfectPowerNth",
			"numberTheory.perfectPower",
			"numberTheory.isPerfectSquare",
			"numberTheory.isPerfectCube",
			"numberTheory.perfectPowerTakeWhile"
		]
	},
	polygonalSeq: {
		type: "(Number, Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the polygonal sequence for a given number of sides and length.",
		returns: {
			type: "integer",
			array: true
		},
		args: {
			sides: {
				type: "integer",
				description: "The number of sides of the polygon."
			},
			length: {
				type: "integer",
				description: "The length of the sequence to generate."
			},
			a: { type: "integer" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["sides", "length"] }],
		examples: [
			"let { polygonalSeq } = import(\"numberTheory\");\npolygonalSeq(3, 2)",
			"let { polygonalSeq } = import(\"numberTheory\");\npolygonalSeq(4, 2)",
			"let { polygonalSeq } = import(\"numberTheory\");\npolygonalSeq(5, 3)",
			"let { polygonalSeq } = import(\"numberTheory\");\npolygonalSeq(6, 5)",
			"let { polygonalSeq } = import(\"numberTheory\");\npolygonalSeq(100, 10)"
		],
		seeAlso: [
			"numberTheory.polygonalNth",
			"numberTheory.polygonalTakeWhile",
			"numberTheory.isPolygonal",
			"numberTheory.perfectSquareSeq"
		]
	},
	polygonalTakeWhile: {
		type: "(Number, (Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the polygonal sequence while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: {
			sides: {
				type: "integer",
				description: "The number of sides of the polygon."
			},
			takeWhile: {
				type: "function",
				description: "A function that takes an integer and an index and returns a boolean."
			},
			a: { type: "integer" },
			b: { type: "function" }
		},
		variants: [{ argumentNames: ["sides", "takeWhile"] }],
		examples: ["let { polygonalTakeWhile } = import(\"numberTheory\");\npolygonalTakeWhile(15, -> $ < 1000)"],
		seeAlso: [
			"numberTheory.polygonalSeq",
			"numberTheory.polygonalNth",
			"numberTheory.isPolygonal"
		]
	},
	polygonalNth: {
		type: "(Number, Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the polygonal sequence.",
		returns: { type: "integer" },
		args: {
			sides: {
				type: "integer",
				description: "The number of sides of the polygon."
			},
			n: {
				type: "integer",
				description: "The index of the term to generate."
			},
			a: { type: "integer" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["sides", "n"] }],
		examples: [
			"let { polygonalNth } = import(\"numberTheory\");\npolygonalNth(3, 9)",
			"let { polygonalNth } = import(\"numberTheory\");\npolygonalNth(4, 5)",
			"let { polygonalNth } = import(\"numberTheory\");\npolygonalNth(5, 5)"
		],
		seeAlso: [
			"numberTheory.polygonalSeq",
			"numberTheory.polygonalTakeWhile",
			"numberTheory.isPolygonal"
		]
	},
	isPolygonal: {
		type: "(Number, Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a number is in the polygonal sequence.",
		returns: { type: "boolean" },
		args: {
			sides: {
				type: "integer",
				description: "The number of sides of the polygon."
			},
			n: {
				type: "integer",
				description: "The number to check."
			},
			a: { type: "integer" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["sides", "n"] }],
		examples: [
			"let { isPolygonal } = import(\"numberTheory\");\nisPolygonal(3, 10)",
			"let { isPolygonal } = import(\"numberTheory\");\nisPolygonal(3, 9)",
			"let { isPolygonal } = import(\"numberTheory\");\nisPolygonal(4, 10000)",
			"let { isPolygonal } = import(\"numberTheory\");\nisPolygonal(4, 1000)",
			"let { isPolygonal } = import(\"numberTheory\");\nisPolygonal(6, 45)"
		],
		seeAlso: [
			"numberTheory.polygonalSeq",
			"numberTheory.polygonalNth",
			"numberTheory.isPerfectSquare",
			"numberTheory.polygonalTakeWhile"
		]
	},
	primeSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the prime sequence up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate."
		} },
		variants: [{ argumentNames: ["length"] }],
		examples: [
			"let { primeSeq } = import(\"numberTheory\");\nprimeSeq(1)",
			"let { primeSeq } = import(\"numberTheory\");\nprimeSeq(2)",
			"let { primeSeq } = import(\"numberTheory\");\nprimeSeq(10)"
		],
		seeAlso: [
			"numberTheory.primeNth",
			"numberTheory.primeTakeWhile",
			"numberTheory.isPrime",
			"numberTheory.compositeSeq",
			"numberTheory.mersenneSeq",
			"numberTheory.luckySeq"
		]
	},
	primeTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the prime sequence while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { primeTakeWhile } = import(\"numberTheory\");\nprimeTakeWhile(-> $ < 50)"],
		seeAlso: [
			"numberTheory.primeSeq",
			"numberTheory.primeNth",
			"numberTheory.isPrime"
		]
	},
	primeNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the prime sequence.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the term to generate."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { primeNth } = import(\"numberTheory\");\nprimeNth(1)",
			"let { primeNth } = import(\"numberTheory\");\nprimeNth(2)",
			"let { primeNth } = import(\"numberTheory\");\nprimeNth(10)"
		],
		seeAlso: [
			"numberTheory.primeSeq",
			"numberTheory.primeTakeWhile",
			"numberTheory.isPrime"
		]
	},
	isPrime: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Determines if a number is prime.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { isPrime } = import(\"numberTheory\");\nisPrime(1)",
			"let { isPrime } = import(\"numberTheory\");\nisPrime(2)",
			"let { isPrime } = import(\"numberTheory\");\nisPrime(3)",
			"let { isPrime } = import(\"numberTheory\");\nisPrime(4)",
			"let { isPrime } = import(\"numberTheory\");\nisPrime(997)",
			"let { isPrime } = import(\"numberTheory\");\nisPrime(1001)"
		],
		seeAlso: [
			"numberTheory.primeSeq",
			"numberTheory.primeNth",
			"numberTheory.isComposite",
			"numberTheory.primeFactors",
			"numberTheory.isMersenne",
			"numberTheory.primeTakeWhile",
			"numberTheory.isLucky"
		]
	},
	recamanSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the Recaman sequence up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate."
		} },
		variants: [{ argumentNames: ["length"] }],
		examples: [
			"let { recamanSeq } = import(\"numberTheory\");\nrecamanSeq(5)",
			"let { recamanSeq } = import(\"numberTheory\");\nrecamanSeq(10)",
			"let { recamanSeq } = import(\"numberTheory\");\nrecamanSeq(20)"
		],
		seeAlso: [
			"numberTheory.recamanNth",
			"numberTheory.recamanTakeWhile",
			"numberTheory.isRecaman",
			"numberTheory.golombSeq"
		]
	},
	recamanTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the Recaman sequence while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { recamanTakeWhile } = import(\"numberTheory\");\nrecamanTakeWhile(-> $ < 10)"],
		seeAlso: [
			"numberTheory.recamanSeq",
			"numberTheory.recamanNth",
			"numberTheory.isRecaman"
		]
	},
	recamanNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the Recaman sequence.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the term to generate."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { recamanNth } = import(\"numberTheory\");\nrecamanNth(5)",
			"let { recamanNth } = import(\"numberTheory\");\nrecamanNth(10)",
			"let { recamanNth } = import(\"numberTheory\");\nrecamanNth(20)"
		],
		seeAlso: [
			"numberTheory.recamanSeq",
			"numberTheory.recamanTakeWhile",
			"numberTheory.isRecaman"
		]
	},
	isRecaman: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a number is in the Recaman sequence.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { isRecaman } = import(\"numberTheory\");\nisRecaman(5)",
			"let { isRecaman } = import(\"numberTheory\");\nisRecaman(10)",
			"let { isRecaman } = import(\"numberTheory\");\nisRecaman(20)"
		],
		seeAlso: [
			"numberTheory.recamanSeq",
			"numberTheory.recamanNth",
			"numberTheory.recamanTakeWhile"
		]
	},
	sylvesterSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the Sylvester sequence up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate. If not provided, the default is 6 (the maximum length of the pre-calculated Sylvester numbers)."
		} },
		variants: [{ argumentNames: ["length"] }, { argumentNames: [] }],
		examples: [
			"let { sylvesterSeq } = import(\"numberTheory\");\nsylvesterSeq(5)",
			"let { sylvesterSeq } = import(\"numberTheory\");\nsylvesterSeq()",
			"let { sylvesterSeq } = import(\"numberTheory\");\nsylvesterSeq()"
		],
		seeAlso: [
			"numberTheory.sylvesterNth",
			"numberTheory.sylvesterTakeWhile",
			"numberTheory.isSylvester"
		]
	},
	sylvesterTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the Sylvester sequence while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { sylvesterTakeWhile } = import(\"numberTheory\");\nsylvesterTakeWhile(-> $ < 100000)"],
		seeAlso: [
			"numberTheory.sylvesterSeq",
			"numberTheory.sylvesterNth",
			"numberTheory.isSylvester"
		]
	},
	sylvesterNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the Sylvester sequence.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the term to generate."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { sylvesterNth } = import(\"numberTheory\");\nsylvesterNth(1)", "let { sylvesterNth } = import(\"numberTheory\");\nsylvesterNth(5)"],
		seeAlso: [
			"numberTheory.sylvesterSeq",
			"numberTheory.sylvesterTakeWhile",
			"numberTheory.isSylvester"
		]
	},
	isSylvester: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a number is in the Sylvester sequence.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { isSylvester } = import(\"numberTheory\");\nisSylvester(2)",
			"let { isSylvester } = import(\"numberTheory\");\nisSylvester(3)",
			"let { isSylvester } = import(\"numberTheory\");\nisSylvester(6)"
		],
		seeAlso: [
			"numberTheory.sylvesterSeq",
			"numberTheory.sylvesterNth",
			"numberTheory.sylvesterTakeWhile"
		]
	},
	thueMorseSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the Thue-Morse sequence up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate."
		} },
		variants: [{ argumentNames: ["length"] }],
		examples: [
			"let { thueMorseSeq } = import(\"numberTheory\");\nthueMorseSeq(5)",
			"let { thueMorseSeq } = import(\"numberTheory\");\nthueMorseSeq(10)",
			"let { thueMorseSeq } = import(\"numberTheory\");\nthueMorseSeq(20)"
		],
		seeAlso: [
			"numberTheory.thueMorseNth",
			"numberTheory.thueMorseTakeWhile",
			"numberTheory.isThueMorse"
		]
	},
	thueMorseTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the Thue-Morse sequence while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { thueMorseTakeWhile } = import(\"numberTheory\");\nthueMorseTakeWhile(-> $2 < 10)"],
		seeAlso: [
			"numberTheory.thueMorseSeq",
			"numberTheory.thueMorseNth",
			"numberTheory.isThueMorse"
		]
	},
	thueMorseNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the Thue-Morse sequence.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the term in the sequence."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { thueMorseNth } = import(\"numberTheory\");\nthueMorseNth(5)",
			"let { thueMorseNth } = import(\"numberTheory\");\nthueMorseNth(10)",
			"let { thueMorseNth } = import(\"numberTheory\");\nthueMorseNth(20)"
		],
		seeAlso: [
			"numberTheory.thueMorseSeq",
			"numberTheory.thueMorseTakeWhile",
			"numberTheory.isThueMorse"
		]
	},
	isThueMorse: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a number is part of the Thue-Morse sequence.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { isThueMorse } = import(\"numberTheory\");\nisThueMorse(1)", "let { isThueMorse } = import(\"numberTheory\");\nisThueMorse(2)"],
		seeAlso: [
			"numberTheory.thueMorseSeq",
			"numberTheory.thueMorseNth",
			"numberTheory.thueMorseTakeWhile"
		]
	},
	tribonacciSeq: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Generates the tribonacci sequence up to a specified length.",
		returns: {
			type: "integer",
			array: true
		},
		args: { length: {
			type: "integer",
			description: "The length of the sequence to generate."
		} },
		variants: [{ argumentNames: ["length"] }],
		examples: [
			"let { tribonacciSeq } = import(\"numberTheory\");\ntribonacciSeq(1)",
			"let { tribonacciSeq } = import(\"numberTheory\");\ntribonacciSeq(2)",
			"let { tribonacciSeq } = import(\"numberTheory\");\ntribonacciSeq(10)"
		],
		seeAlso: [
			"numberTheory.tribonacciNth",
			"numberTheory.tribonacciTakeWhile",
			"numberTheory.isTribonacci",
			"numberTheory.fibonacciSeq"
		]
	},
	tribonacciTakeWhile: {
		type: "((Number -> Boolean)) -> Number[]",
		category: "numberTheory",
		description: "Generates the tribonacci sequence while a condition is met.",
		returns: {
			type: "integer",
			array: true
		},
		args: { takeWhile: {
			type: "function",
			description: "A function that takes an integer and an index and returns a boolean."
		} },
		variants: [{ argumentNames: ["takeWhile"] }],
		examples: ["let { tribonacciTakeWhile } = import(\"numberTheory\");\ntribonacciTakeWhile(-> $ < 100)"],
		seeAlso: [
			"numberTheory.tribonacciSeq",
			"numberTheory.tribonacciNth",
			"numberTheory.isTribonacci"
		]
	},
	tribonacciNth: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Generates the nth term of the tribonacci sequence.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The index of the term to generate."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { tribonacciNth } = import(\"numberTheory\");\ntribonacciNth(1)",
			"let { tribonacciNth } = import(\"numberTheory\");\ntribonacciNth(2)",
			"let { tribonacciNth } = import(\"numberTheory\");\ntribonacciNth(10)"
		],
		seeAlso: [
			"numberTheory.tribonacciSeq",
			"numberTheory.tribonacciTakeWhile",
			"numberTheory.isTribonacci"
		]
	},
	isTribonacci: {
		type: "(Number) -> Boolean",
		category: "numberTheory",
		description: "Determines if a number is in the tribonacci sequence.",
		returns: { type: "boolean" },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { isTribonacci } = import(\"numberTheory\");\nisTribonacci(0)",
			"let { isTribonacci } = import(\"numberTheory\");\nisTribonacci(1)",
			"let { isTribonacci } = import(\"numberTheory\");\nisTribonacci(2)",
			"let { isTribonacci } = import(\"numberTheory\");\nisTribonacci(3)",
			"let { isTribonacci } = import(\"numberTheory\");\nisTribonacci(4)",
			"let { isTribonacci } = import(\"numberTheory\");\nisTribonacci(5)",
			"let { isTribonacci } = import(\"numberTheory\");\nisTribonacci(6)",
			"let { isTribonacci } = import(\"numberTheory\");\nisTribonacci(7)",
			"let { isTribonacci } = import(\"numberTheory\");\nisTribonacci(8)",
			"let { isTribonacci } = import(\"numberTheory\");\nisTribonacci(9)",
			"let { isTribonacci } = import(\"numberTheory\");\nisTribonacci(10)"
		],
		seeAlso: [
			"numberTheory.tribonacciSeq",
			"numberTheory.tribonacciNth",
			"numberTheory.isFibonacci",
			"numberTheory.tribonacciTakeWhile"
		]
	},
	countCombinations: {
		type: "(Number, Number) -> Number",
		category: "numberTheory",
		description: "Calculates the number of combinations of n items taken k at a time.",
		returns: { type: "integer" },
		args: {
			a: { type: "integer" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: ["let { countCombinations } = import(\"numberTheory\");\ncountCombinations(5, 3)", "let { countCombinations } = import(\"numberTheory\");\ncountCombinations(10, 2)"],
		seeAlso: [
			"numberTheory.combinations",
			"numberTheory.countPermutations",
			"numberTheory.factorial",
			"numberTheory.multinomial",
			"numberTheory.stirlingSecond",
			"numberTheory.countPartitions",
			"numberTheory.countPowerSet"
		]
	},
	combinations: {
		type: "(Unknown[], Number) -> Unknown[][]",
		category: "numberTheory",
		description: "Generates all possible combinations of a specified size from a collection.",
		returns: {
			type: "array",
			array: true
		},
		args: {
			set: {
				type: "array",
				array: true,
				description: "The input collection to generate combinations from."
			},
			n: {
				type: "integer",
				description: "The size of each combination."
			},
			a: { type: "array" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["set", "n"] }],
		examples: [
			"let { combinations } = import(\"numberTheory\");\ncombinations([1, 2, 3], 2)",
			"let { combinations } = import(\"numberTheory\");\ncombinations([\"a\", \"b\", \"c\"], 2)",
			"let { combinations } = import(\"numberTheory\");\ncombinations([1, 2, 3], 0)",
			"let { combinations } = import(\"numberTheory\");\ncombinations([1, 2, 3], 1)",
			"let { combinations } = import(\"numberTheory\");\ncombinations([1, 2, 3], 3)"
		],
		seeAlso: [
			"numberTheory.countCombinations",
			"numberTheory.permutations",
			"numberTheory.powerSet",
			"numberTheory.cartesianProduct",
			"numberTheory.partitions"
		]
	},
	countDerangements: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Calculates the number of derangements (permutations where no element appears in its original position) of n items.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The total number of items."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { countDerangements } = import(\"numberTheory\");\ncountDerangements(4)", "let { countDerangements } = import(\"numberTheory\");\ncountDerangements(5)"],
		seeAlso: [
			"numberTheory.derangements",
			"numberTheory.countPermutations",
			"numberTheory.factorial"
		]
	},
	derangements: {
		type: "(Unknown[]) -> Unknown[][]",
		category: "numberTheory",
		description: "Generates all derangements (permutations where no element appears in its original position) of a set.",
		returns: {
			type: "array",
			array: true
		},
		args: { set: {
			type: "array",
			array: true,
			description: "The input collection to generate derangements from."
		} },
		variants: [{ argumentNames: ["set"] }],
		examples: ["let { derangements } = import(\"numberTheory\");\nderangements([1, 2, 3, 4])", "let { derangements } = import(\"numberTheory\");\nderangements([\"a\", \"b\", \"c\"])"],
		seeAlso: ["numberTheory.countDerangements", "numberTheory.permutations"]
	},
	divisors: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Returns the divisors of a number.",
		returns: {
			type: "integer",
			array: true
		},
		args: { n: {
			type: "integer",
			description: "The number to find divisors for."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { divisors } = import(\"numberTheory\");\ndivisors(12)",
			"let { divisors } = import(\"numberTheory\");\ndivisors(100)",
			"let { divisors } = import(\"numberTheory\");\ndivisors(37)"
		],
		seeAlso: [
			"numberTheory.countDivisors",
			"numberTheory.properDivisors",
			"numberTheory.sigma",
			"numberTheory.primeFactors",
			"numberTheory.isDivisibleBy",
			"numberTheory.lcm",
			"numberTheory.isAbundant",
			"numberTheory.isDeficient",
			"numberTheory.countProperDivisors"
		]
	},
	countDivisors: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Returns the number of divisors of a number.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The number to count divisors for."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { countDivisors } = import(\"numberTheory\");\ncountDivisors(12)",
			"let { countDivisors } = import(\"numberTheory\");\ncountDivisors(100)",
			"let { countDivisors } = import(\"numberTheory\");\ncountDivisors(37)"
		],
		seeAlso: [
			"numberTheory.divisors",
			"numberTheory.countProperDivisors",
			"numberTheory.sigma"
		]
	},
	properDivisors: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Returns the proper divisors of a number.",
		returns: {
			type: "integer",
			array: true
		},
		args: { n: {
			type: "integer",
			description: "The number to find proper divisors for."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { properDivisors } = import(\"numberTheory\");\nproperDivisors(12)",
			"let { properDivisors } = import(\"numberTheory\");\nproperDivisors(100)",
			"let { properDivisors } = import(\"numberTheory\");\nproperDivisors(37)"
		],
		seeAlso: [
			"numberTheory.countProperDivisors",
			"numberTheory.divisors",
			"numberTheory.isAmicable",
			"numberTheory.isPerfect"
		]
	},
	countProperDivisors: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Returns the number of proper divisors of a number.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The number to count proper divisors for."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { countProperDivisors } = import(\"numberTheory\");\ncountProperDivisors(12)",
			"let { countProperDivisors } = import(\"numberTheory\");\ncountProperDivisors(100)",
			"let { countProperDivisors } = import(\"numberTheory\");\ncountProperDivisors(37)"
		],
		seeAlso: [
			"numberTheory.properDivisors",
			"numberTheory.countDivisors",
			"numberTheory.divisors"
		]
	},
	factorial: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Calculates the factorial of a number.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The number to calculate the factorial for."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { factorial } = import(\"numberTheory\");\nfactorial(5)",
			"let { factorial } = import(\"numberTheory\");\nfactorial(0)",
			"let { factorial } = import(\"numberTheory\");\nfactorial(10)",
			"let { factorial } = import(\"numberTheory\");\nfactorial(20)"
		],
		seeAlso: [
			"numberTheory.factorialSeq",
			"numberTheory.factorialNth",
			"numberTheory.isFactorial",
			"numberTheory.countCombinations",
			"numberTheory.countPermutations",
			"numberTheory.multinomial",
			"numberTheory.countDerangements"
		]
	},
	partitions: {
		type: "(Number) -> Number[][]",
		category: "numberTheory",
		description: "Generates all partitions of a number.",
		returns: {
			type: "array",
			array: true
		},
		args: { n: {
			type: "integer",
			description: "The number to partition."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: ["let { partitions } = import(\"numberTheory\");\npartitions(4)", "let { partitions } = import(\"numberTheory\");\npartitions(8)"],
		seeAlso: [
			"numberTheory.countPartitions",
			"numberTheory.partitionSeq",
			"numberTheory.combinations",
			"numberTheory.isPartition"
		]
	},
	countPartitions: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Returns the number of partitions of a number.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The number to count partitions for."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { countPartitions } = import(\"numberTheory\");\ncountPartitions(4)",
			"let { countPartitions } = import(\"numberTheory\");\ncountPartitions(8)",
			"let { countPartitions } = import(\"numberTheory\");\ncountPartitions(15)"
		],
		seeAlso: [
			"numberTheory.partitions",
			"numberTheory.partitionSeq",
			"numberTheory.countCombinations"
		]
	},
	permutations: {
		type: "(Unknown[]) -> Unknown[][]",
		category: "numberTheory",
		description: "Generates all permutations of a collection.",
		returns: {
			type: "array",
			array: true
		},
		args: { set: {
			type: "array",
			array: true,
			description: "The input collection to generate permutations from."
		} },
		variants: [{ argumentNames: ["set"] }],
		examples: [
			"let { permutations } = import(\"numberTheory\");\npermutations([1, 2, 3])",
			"let { permutations } = import(\"numberTheory\");\npermutations([\"a\", \"b\", \"c\"])",
			"let { permutations } = import(\"numberTheory\");\npermutations([1, 2, 3, 4])",
			"let { permutations } = import(\"numberTheory\");\npermutations([1, 2])",
			"let { permutations } = import(\"numberTheory\");\npermutations([1])",
			"let { permutations } = import(\"numberTheory\");\npermutations([])"
		],
		seeAlso: [
			"numberTheory.countPermutations",
			"numberTheory.combinations",
			"numberTheory.derangements",
			"numberTheory.cartesianProduct"
		]
	},
	countPermutations: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Returns the number of permutations of n items taken k at a time.",
		returns: { type: "integer" },
		args: {
			a: { type: "integer" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { countPermutations } = import(\"numberTheory\");\ncountPermutations(5, 3)",
			"let { countPermutations } = import(\"numberTheory\");\ncountPermutations(10, 2)",
			"let { countPermutations } = import(\"numberTheory\");\ncountPermutations(10, 10)",
			"let { countPermutations } = import(\"numberTheory\");\ncountPermutations(10, 0)",
			"let { countPermutations } = import(\"numberTheory\");\ncountPermutations(10, 1)"
		],
		seeAlso: [
			"numberTheory.permutations",
			"numberTheory.countCombinations",
			"numberTheory.factorial",
			"numberTheory.multinomial",
			"numberTheory.stirlingFirst",
			"numberTheory.countDerangements"
		]
	},
	powerSet: {
		type: "(Unknown[]) -> Unknown[][]",
		category: "numberTheory",
		description: "Generates the power set of a collection.",
		returns: {
			type: "array",
			array: true
		},
		args: { set: {
			type: "any",
			array: true,
			description: "The input collection to generate the power set from."
		} },
		variants: [{ argumentNames: ["set"] }],
		examples: [
			"let { powerSet } = import(\"numberTheory\");\npowerSet([\"a\", \"b\", \"c\"])",
			"let { powerSet } = import(\"numberTheory\");\npowerSet([1, 2])",
			"let { powerSet } = import(\"numberTheory\");\npowerSet([1])",
			"let { powerSet } = import(\"numberTheory\");\npowerSet([])"
		],
		seeAlso: [
			"numberTheory.countPowerSet",
			"numberTheory.combinations",
			"numberTheory.cartesianProduct"
		]
	},
	countPowerSet: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Returns the number of subsets of a set.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The size of the set."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { countPowerSet } = import(\"numberTheory\");\ncountPowerSet(3)",
			"let { countPowerSet } = import(\"numberTheory\");\ncountPowerSet(5)",
			"let { countPowerSet } = import(\"numberTheory\");\ncountPowerSet(10)"
		],
		seeAlso: ["numberTheory.powerSet", "numberTheory.countCombinations"]
	},
	primeFactors: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Returns the prime factors of a number.",
		returns: {
			type: "integer",
			array: true
		},
		args: { n: {
			type: "integer",
			description: "The number to factor."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { primeFactors } = import(\"numberTheory\");\nprimeFactors(12)",
			"let { primeFactors } = import(\"numberTheory\");\nprimeFactors(100)",
			"let { primeFactors } = import(\"numberTheory\");\nprimeFactors(37)"
		],
		seeAlso: [
			"numberTheory.countPrimeFactors",
			"numberTheory.distinctPrimeFactors",
			"numberTheory.isPrime",
			"numberTheory.divisors",
			"numberTheory.eulerTotient",
			"numberTheory.mobius",
			"numberTheory.isComposite",
			"numberTheory.countDistinctPrimeFactors"
		]
	},
	countPrimeFactors: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Returns the number of prime factors of a number.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The number to count prime factors for."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { countPrimeFactors } = import(\"numberTheory\");\ncountPrimeFactors(12)",
			"let { countPrimeFactors } = import(\"numberTheory\");\ncountPrimeFactors(100)",
			"let { countPrimeFactors } = import(\"numberTheory\");\ncountPrimeFactors(37)"
		],
		seeAlso: [
			"numberTheory.primeFactors",
			"numberTheory.distinctPrimeFactors",
			"numberTheory.countDistinctPrimeFactors"
		]
	},
	distinctPrimeFactors: {
		type: "(Number) -> Number[]",
		category: "numberTheory",
		description: "Returns the distinct prime factors of a number.",
		returns: {
			type: "integer",
			array: true
		},
		args: { n: {
			type: "integer",
			description: "The number to find distinct prime factors for."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { distinctPrimeFactors } = import(\"numberTheory\");\ndistinctPrimeFactors(12)",
			"let { distinctPrimeFactors } = import(\"numberTheory\");\ndistinctPrimeFactors(100)",
			"let { distinctPrimeFactors } = import(\"numberTheory\");\ndistinctPrimeFactors(37)"
		],
		seeAlso: [
			"numberTheory.primeFactors",
			"numberTheory.countDistinctPrimeFactors",
			"numberTheory.countPrimeFactors"
		]
	},
	countDistinctPrimeFactors: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Returns the number of distinct prime factors of a number.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The number to count distinct prime factors for."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { countDistinctPrimeFactors } = import(\"numberTheory\");\ncountDistinctPrimeFactors(12)",
			"let { countDistinctPrimeFactors } = import(\"numberTheory\");\ncountDistinctPrimeFactors(100)",
			"let { countDistinctPrimeFactors } = import(\"numberTheory\");\ncountDistinctPrimeFactors(37)"
		],
		seeAlso: [
			"numberTheory.distinctPrimeFactors",
			"numberTheory.primeFactors",
			"numberTheory.countPrimeFactors"
		]
	},
	isCoprime: {
		type: "(Number, Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if two numbers are coprime (i.e., their GCD is 1).",
		returns: { type: "boolean" },
		args: {
			a: { type: "integer" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { isCoprime } = import(\"numberTheory\");\nisCoprime(12, 8)",
			"let { isCoprime } = import(\"numberTheory\");\nisCoprime(12, 5)",
			"let { isCoprime } = import(\"numberTheory\");\nisCoprime(37, 1)",
			"let { isCoprime } = import(\"numberTheory\");\nisCoprime(0, 0)",
			"let { isCoprime } = import(\"numberTheory\");\nisCoprime(0, 5)",
			"let { isCoprime } = import(\"numberTheory\");\nisCoprime(5, 0)",
			"let { isCoprime } = import(\"numberTheory\");\nisCoprime(1, 0)",
			"let { isCoprime } = import(\"numberTheory\");\nisCoprime(0, 1)",
			"let { isCoprime } = import(\"numberTheory\");\nisCoprime(1, 1)",
			"let { isCoprime } = import(\"numberTheory\");\nisCoprime(2, 3)"
		],
		seeAlso: [
			"numberTheory.gcd",
			"numberTheory.eulerTotient",
			"numberTheory.isDivisibleBy",
			"numberTheory.lcm",
			"numberTheory.carmichaelLambda"
		]
	},
	isDivisibleBy: {
		type: "(Number, Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if a number is divisible by another number.",
		returns: { type: "boolean" },
		args: {
			a: { type: "integer" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { isDivisibleBy } = import(\"numberTheory\");\nisDivisibleBy(12, 4)",
			"let { isDivisibleBy } = import(\"numberTheory\");\nisDivisibleBy(12, 5)",
			"let { isDivisibleBy } = import(\"numberTheory\");\nisDivisibleBy(37, 1)",
			"let { isDivisibleBy } = import(\"numberTheory\");\nisDivisibleBy(0, 0)",
			"let { isDivisibleBy } = import(\"numberTheory\");\nisDivisibleBy(0, 5)",
			"let { isDivisibleBy } = import(\"numberTheory\");\nisDivisibleBy(5, 0)"
		],
		seeAlso: [
			"numberTheory.divisors",
			"numberTheory.gcd",
			"numberTheory.isCoprime"
		]
	},
	gcd: {
		type: "(Number, Number) -> Number",
		category: "numberTheory",
		description: "Calculates the greatest common divisor (GCD) of two numbers.",
		returns: { type: "integer" },
		args: {
			a: { type: "integer" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { gcd } = import(\"numberTheory\");\ngcd(100, 25)",
			"let { gcd } = import(\"numberTheory\");\ngcd(37, 1)",
			"let { gcd } = import(\"numberTheory\");\ngcd(0, 0)",
			"let { gcd } = import(\"numberTheory\");\ngcd(0, 5)",
			"let { gcd } = import(\"numberTheory\");\ngcd(5, 0)"
		],
		seeAlso: [
			"numberTheory.lcm",
			"numberTheory.extendedGcd",
			"numberTheory.isCoprime",
			"numberTheory.isDivisibleBy"
		]
	},
	lcm: {
		type: "(Number, Number) -> Number",
		category: "numberTheory",
		description: "Calculates the least common multiple (LCM) of two numbers.",
		returns: { type: "integer" },
		args: {
			a: { type: "integer" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { lcm } = import(\"numberTheory\");\nlcm(100, 25)",
			"let { lcm } = import(\"numberTheory\");\nlcm(37, 1)",
			"let { lcm } = import(\"numberTheory\");\nlcm(0, 5)",
			"let { lcm } = import(\"numberTheory\");\nlcm(5, 0)"
		],
		seeAlso: [
			"numberTheory.gcd",
			"numberTheory.divisors",
			"numberTheory.isCoprime"
		]
	},
	multinomial: {
		type: "(...Number) -> Number",
		category: "numberTheory",
		description: "Calculates the multinomial coefficient from of a list of numbers representing the sizes of each group.",
		returns: { type: "integer" },
		args: { args: {
			type: "integer",
			rest: true,
			description: "The numbers representing the sizes of each group."
		} },
		variants: [{ argumentNames: ["args"] }],
		examples: ["let { multinomial } = import(\"numberTheory\");\nmultinomial(5, 2, 3)", "let { multinomial } = import(\"numberTheory\");\nmultinomial(10, 2, 3, 5)"],
		seeAlso: [
			"numberTheory.countCombinations",
			"numberTheory.factorial",
			"numberTheory.countPermutations"
		],
		hideOperatorForm: true
	},
	isAmicable: {
		type: "(Number, Number) -> Boolean",
		category: "numberTheory",
		description: "Checks if two numbers are amicable (i.e., the sum of the proper divisors of each number equals the other number).",
		returns: { type: "boolean" },
		args: {
			a: { type: "integer" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { isAmicable } = import(\"numberTheory\");\nisAmicable(220, 284)",
			"let { isAmicable } = import(\"numberTheory\");\nisAmicable(1184, 1210)",
			"let { isAmicable } = import(\"numberTheory\");\nisAmicable(2620, 2924)",
			"let { isAmicable } = import(\"numberTheory\");\nisAmicable(5020, 5564)",
			"let { isAmicable } = import(\"numberTheory\");\nisAmicable(6232, 6368)"
		],
		seeAlso: [
			"numberTheory.properDivisors",
			"numberTheory.isPerfect",
			"numberTheory.sigma",
			"numberTheory.perfectSeq"
		]
	},
	eulerTotient: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Calculates the Euler's totient function (φ(n)) of a number, which counts the integers up to n that are coprime to n.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The number to calculate the totient for."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { eulerTotient } = import(\"numberTheory\");\neulerTotient(1)",
			"let { eulerTotient } = import(\"numberTheory\");\neulerTotient(2)",
			"let { eulerTotient } = import(\"numberTheory\");\neulerTotient(10)",
			"let { eulerTotient } = import(\"numberTheory\");\neulerTotient(20)"
		],
		seeAlso: [
			"numberTheory.isCoprime",
			"numberTheory.carmichaelLambda",
			"numberTheory.mobius",
			"numberTheory.primeFactors",
			"numberTheory.mertens"
		]
	},
	mobius: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Calculates the Möbius function (μ(n)) of a number, which is used in number theory.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The number to calculate the Möbius function for."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { mobius } = import(\"numberTheory\");\nmobius(1)",
			"let { mobius } = import(\"numberTheory\");\nmobius(2)",
			"let { mobius } = import(\"numberTheory\");\nmobius(3)",
			"let { mobius } = import(\"numberTheory\");\nmobius(4)",
			"let { mobius } = import(\"numberTheory\");\nmobius(6)",
			"let { mobius } = import(\"numberTheory\");\nmobius(12)",
			"let { mobius } = import(\"numberTheory\");\nmobius(30)"
		],
		seeAlso: [
			"numberTheory.mertens",
			"numberTheory.eulerTotient",
			"numberTheory.primeFactors"
		]
	},
	mertens: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Calculates the Mertens function (M(n)) of a number, which is the sum of the Möbius function up to n.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The number to calculate the Mertens function for."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { mobius } = import(\"numberTheory\");\nmobius(1)",
			"let { mobius } = import(\"numberTheory\");\nmobius(2)",
			"let { mobius } = import(\"numberTheory\");\nmobius(3)",
			"let { mobius } = import(\"numberTheory\");\nmobius(4)",
			"let { mobius } = import(\"numberTheory\");\nmobius(6)",
			"let { mobius } = import(\"numberTheory\");\nmobius(12)",
			"let { mobius } = import(\"numberTheory\");\nmobius(30)"
		],
		seeAlso: ["numberTheory.mobius", "numberTheory.eulerTotient"]
	},
	sigma: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Calculates the sum of divisors function (σ(n)) of a number, which is the sum of all positive divisors of n.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The number to calculate the sum of divisors for."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { sigma } = import(\"numberTheory\");\nsigma(1)",
			"let { sigma } = import(\"numberTheory\");\nsigma(2)",
			"let { sigma } = import(\"numberTheory\");\nsigma(3)",
			"let { sigma } = import(\"numberTheory\");\nsigma(4)",
			"let { sigma } = import(\"numberTheory\");\nsigma(6)",
			"let { sigma } = import(\"numberTheory\");\nsigma(12)",
			"let { sigma } = import(\"numberTheory\");\nsigma(30)"
		],
		seeAlso: [
			"numberTheory.divisors",
			"numberTheory.isPerfect",
			"numberTheory.isAbundant",
			"numberTheory.isDeficient",
			"numberTheory.isAmicable",
			"numberTheory.countDivisors"
		]
	},
	carmichaelLambda: {
		type: "(Number) -> Number",
		category: "numberTheory",
		description: "Calculates the Carmichael function (λ(n)) of a number, which is the smallest positive integer m such that a^m ≡ 1 (mod n) for all integers a coprime to n.",
		returns: { type: "integer" },
		args: { n: {
			type: "integer",
			description: "The number to calculate the Carmichael function for."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { carmichaelLambda } = import(\"numberTheory\");\ncarmichaelLambda(1)",
			"let { carmichaelLambda } = import(\"numberTheory\");\ncarmichaelLambda(2)",
			"let { carmichaelLambda } = import(\"numberTheory\");\ncarmichaelLambda(3)",
			"let { carmichaelLambda } = import(\"numberTheory\");\ncarmichaelLambda(4)",
			"let { carmichaelLambda } = import(\"numberTheory\");\ncarmichaelLambda(6)",
			"let { carmichaelLambda } = import(\"numberTheory\");\ncarmichaelLambda(12)",
			"let { carmichaelLambda } = import(\"numberTheory\");\ncarmichaelLambda(30)"
		],
		seeAlso: [
			"numberTheory.eulerTotient",
			"numberTheory.modExp",
			"numberTheory.isCoprime"
		]
	},
	cartesianProduct: {
		type: "(...Unknown[]) -> Unknown[][]",
		category: "numberTheory",
		description: "Calculates the Cartesian product of two or more sets.",
		returns: {
			type: "array",
			array: true
		},
		args: {
			sets: {
				type: "array",
				array: true,
				description: "The input collections to calculate the Cartesian product from."
			},
			a: { type: "array" },
			b: { type: "array" }
		},
		variants: [{ argumentNames: ["sets"] }],
		examples: [
			"let { cartesianProduct } = import(\"numberTheory\");\ncartesianProduct([1, 2], [\"a\", \"b\"])",
			"let { cartesianProduct } = import(\"numberTheory\");\ncartesianProduct([1, 2], [\"a\", \"b\"], [true, false])",
			"let { cartesianProduct } = import(\"numberTheory\");\ncartesianProduct([1, 2, 3], [\"x\", \"y\", \"z\"])"
		],
		seeAlso: [
			"numberTheory.combinations",
			"numberTheory.powerSet",
			"numberTheory.permutations"
		]
	},
	perfectPower: {
		type: "(Number) -> (Number[] | Null)",
		category: "numberTheory",
		description: "Returns a tuple of the base and exponent if the number is a perfect power, otherwise returns null.",
		returns: { type: ["array", "null"] },
		args: { n: {
			type: "integer",
			description: "The number to check."
		} },
		variants: [{ argumentNames: ["n"] }],
		examples: [
			"let { perfectPower } = import(\"numberTheory\");\nperfectPower(1)",
			"let { perfectPower } = import(\"numberTheory\");\nperfectPower(2)",
			"let { perfectPower } = import(\"numberTheory\");\nperfectPower(4)",
			"let { perfectPower } = import(\"numberTheory\");\nperfectPower(8)",
			"let { perfectPower } = import(\"numberTheory\");\nperfectPower(9)",
			"let { perfectPower } = import(\"numberTheory\");\nperfectPower(16)",
			"let { perfectPower } = import(\"numberTheory\");\nperfectPower(19)"
		],
		seeAlso: [
			"numberTheory.isPerfectPower",
			"numberTheory.perfectPowerSeq",
			"numberTheory.isPerfectSquare",
			"numberTheory.isPerfectCube"
		]
	},
	modExp: {
		type: "(Number, Number, Number) -> Number",
		category: "numberTheory",
		description: "Calculates the modular exponentiation of a base raised to an exponent modulo a modulus.",
		returns: { type: "integer" },
		args: {
			base: { type: "integer" },
			exponent: { type: "integer" },
			modulus: { type: "integer" }
		},
		variants: [{ argumentNames: [
			"base",
			"exponent",
			"modulus"
		] }],
		examples: [
			"let { modExp } = import(\"numberTheory\");\nmodExp(2, 3, 5)",
			"let { modExp } = import(\"numberTheory\");\nmodExp(3, 4, 7)",
			"let { modExp } = import(\"numberTheory\");\nmodExp(5, 6, 11)",
			"let { modExp } = import(\"numberTheory\");\nmodExp(7, 8, 13)"
		],
		seeAlso: [
			"numberTheory.modInv",
			"numberTheory.carmichaelLambda",
			"numberTheory.chineseRemainder"
		]
	},
	modInv: {
		type: "(Number, Number) -> Number",
		category: "numberTheory",
		description: "Calculates the modular multiplicative inverse of a number modulo another number.",
		returns: { type: "integer" },
		args: {
			a: { type: "integer" },
			m: { type: "integer" }
		},
		variants: [{ argumentNames: ["a", "m"] }],
		examples: [
			"let { modInv } = import(\"numberTheory\");\nmodInv(3, 11)",
			"let { modInv } = import(\"numberTheory\");\nmodInv(10, 17)",
			"let { modInv } = import(\"numberTheory\");\nmodInv(5, 13)",
			"let { modInv } = import(\"numberTheory\");\nmodInv(7, 19)"
		],
		seeAlso: [
			"numberTheory.modExp",
			"numberTheory.extendedGcd",
			"numberTheory.chineseRemainder"
		]
	},
	extendedGcd: {
		type: "(Number, Number) -> Number[]",
		category: "numberTheory",
		description: "Calculates the extended greatest common divisor (GCD) of two numbers, returning the GCD and the coefficients of Bézout's identity.",
		returns: {
			type: "integer",
			array: true
		},
		args: {
			a: { type: "integer" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { extendedGcd } = import(\"numberTheory\");\nextendedGcd(30, 12)",
			"let { extendedGcd } = import(\"numberTheory\");\nextendedGcd(56, 98)",
			"let { extendedGcd } = import(\"numberTheory\");\nextendedGcd(101, 10)",
			"let { extendedGcd } = import(\"numberTheory\");\nextendedGcd(17, 13)"
		],
		seeAlso: [
			"numberTheory.gcd",
			"numberTheory.modInv",
			"numberTheory.chineseRemainder"
		]
	},
	chineseRemainder: {
		type: "(Number[], Number[]) -> Number",
		category: "numberTheory",
		description: "Solves a system of simultaneous congruences using the Chinese Remainder Theorem.",
		returns: { type: "integer" },
		args: {
			remainders: {
				type: "integer",
				array: true,
				description: "The remainders of the congruences."
			},
			moduli: {
				type: "integer",
				array: true,
				description: "The moduli of the congruences."
			},
			a: { type: "array" },
			b: { type: "array" }
		},
		variants: [{ argumentNames: ["remainders", "moduli"] }],
		examples: [
			"let { chineseRemainder } = import(\"numberTheory\");\nchineseRemainder([2, 3], [3, 5])",
			"let { chineseRemainder } = import(\"numberTheory\");\nchineseRemainder([1, 2], [3, 4])",
			"let { chineseRemainder } = import(\"numberTheory\");\nchineseRemainder([0, 1], [2, 3])",
			"let { chineseRemainder } = import(\"numberTheory\");\nchineseRemainder([1, 2, 3], [4, 5, 7])"
		],
		seeAlso: [
			"numberTheory.modExp",
			"numberTheory.modInv",
			"numberTheory.extendedGcd"
		]
	},
	stirlingFirst: {
		type: "(Number, Number) -> Number",
		category: "numberTheory",
		description: "Calculates the Stirling numbers of the first kind, which count the number of permutations of n elements with k cycles.",
		returns: { type: "integer" },
		args: {
			a: {
				type: "integer",
				description: "The number of elements."
			},
			b: {
				type: "integer",
				description: "The number of cycles."
			}
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { stirlingFirst } = import(\"numberTheory\");\nstirlingFirst(5, 2)",
			"let { stirlingFirst } = import(\"numberTheory\");\nstirlingFirst(4, 3)",
			"let { stirlingFirst } = import(\"numberTheory\");\nstirlingFirst(6, 1)",
			"let { stirlingFirst } = import(\"numberTheory\");\nstirlingFirst(7, 4)",
			"let { stirlingFirst } = import(\"numberTheory\");\nstirlingFirst(8, 5)"
		],
		seeAlso: [
			"numberTheory.stirlingSecond",
			"numberTheory.bellSeq",
			"numberTheory.countPermutations"
		]
	},
	stirlingSecond: {
		type: "(Number, Number) -> Number",
		category: "numberTheory",
		description: "Calculates the Stirling numbers of the second kind, which count the number of ways to partition n elements into k non-empty subsets.",
		returns: { type: "integer" },
		args: {
			a: {
				type: "integer",
				description: "The number of elements."
			},
			b: {
				type: "integer",
				description: "The number of subsets."
			}
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { stirlingSecond } = import(\"numberTheory\");\nstirlingSecond(5, 2)",
			"let { stirlingSecond } = import(\"numberTheory\");\nstirlingSecond(4, 3)",
			"let { stirlingSecond } = import(\"numberTheory\");\nstirlingSecond(6, 1)",
			"let { stirlingSecond } = import(\"numberTheory\");\nstirlingSecond(7, 4)",
			"let { stirlingSecond } = import(\"numberTheory\");\nstirlingSecond(8, 5)"
		],
		seeAlso: [
			"numberTheory.stirlingFirst",
			"numberTheory.bellSeq",
			"numberTheory.countCombinations"
		]
	}
};
//#endregion
//#region src/builtin/modules/number-theory/number-theory.dvala
var number_theory_default = "do\n  let isPrime = (n) -> if n == 2 then\n    true\n  else if n % 2 == 0 then\n    false\n  else\n    loop(i = 3) -> if i * i > n then\n      true\n    else if n % i == 0 then\n      false\n    else\n      recur(i + 2)\n    end\n  end;\n\n  let sumProperDivisors = (n) -> if n <= 1 then\n    0\n  else\n    loop(i = 2, acc = 1) -> if i * i > n then\n      acc\n    else if n % i == 0 then\n      if i * i == n then\n        recur(i + 1, acc + i)\n      else\n        recur(i + 1, acc + i + n / i)\n      end\n    else\n      recur(i + 1, acc)\n    end\n  end;\n  let isAbundant = (n) -> n > 0 && sumProperDivisors(n) > n;\n  let isDeficient = (n) -> n > 0 && sumProperDivisors(n) < n;\n  let isComposite = (n) -> n > 1 && !(isPrime(n));\n  let isHappy = (n) -> do\n    let sumOfSquaredDigits = (x) -> loop(num = x, acc = 0) -> if num == 0 then\n      acc\n    else\n      do\n        let d = num % 10;\n        recur(floor(num / 10), acc + d * d);\n      end\n    end;\n    loop(slow = n, fast = sumOfSquaredDigits(n)) -> if fast == 1 then\n      true\n    else if slow == fast then\n      false\n    else\n      recur(\n        sumOfSquaredDigits(slow),\n        sumOfSquaredDigits(sumOfSquaredDigits(fast))\n      )\n    end;\n  end;\n  let countBits = (n) -> loop(x = n, acc = 0) -> if x == 0 then\n    acc\n  else\n    recur(floor(x / 2), acc + x % 2)\n  end;\n  let isPerfectPower = (n) -> if n == 1 then\n    true\n  else\n    loop(k = 2) -> if 2 ^ k > n then\n      false\n    else\n      do\n        let b = round(n ^ (1 / k));\n        if b ^ k == n then true else recur(k + 1) end;\n      end\n    end\n  end;\n  let binom = (n, k) -> if k == 0 || k == n then\n    1\n  else\n    loop(i = 0, k2 = min(k, n - k), acc = 1) -> if i >= k2 then\n      acc\n    else\n      recur(i + 1, k2, acc * (n - i) / (i + 1))\n    end\n  end;\n  let bellNumbers = [\n    1,\n    2,\n    5,\n    15,\n    52,\n    203,\n    877,\n    4140,\n    21147,\n    115975,\n    678570,\n    4213597,\n    27644437,\n    190899322,\n    1382958545,\n    10480142147,\n    82864869804,\n    682076806159,\n    5832742205057,\n    51724158235372,\n    474869816156751,\n    4506715738447323,\n  ];\n  let catalanNumbers = [\n    1,\n    2,\n    5,\n    14,\n    42,\n    132,\n    429,\n    1430,\n    4862,\n    16796,\n    58786,\n    208012,\n    742900,\n    2674440,\n    9694845,\n    35357670,\n    129644790,\n    477638700,\n    1767263190,\n    6564120420,\n    24466267020,\n    91482563640,\n    343059613650,\n    1289904147324,\n    4861946401452,\n    18367353072152,\n    69533550916004,\n    263747951750360,\n    1002242216651368,\n    3814986502092304,\n  ];\n  let mersenneNumbers = [3, 7, 31, 127, 2047, 8191, 131071, 524287, 2147483647];\n  let perfectNumbers = [6, 28, 496, 8128, 33550336, 8589869056, 137438691328];\n  let sylvesterNumbers = [2, 6, 42, 1806, 3263442, 10650056950806];\n  let luckyNumbers = [\n    1,\n    3,\n    7,\n    9,\n    13,\n    15,\n    21,\n    25,\n    31,\n    33,\n    37,\n    43,\n    49,\n    51,\n    63,\n    67,\n    69,\n    73,\n    75,\n    79,\n    87,\n    93,\n    99,\n    105,\n    111,\n    115,\n    127,\n    129,\n    133,\n    135,\n    141,\n    151,\n    159,\n    163,\n    169,\n    171,\n    189,\n    193,\n    195,\n    201,\n    205,\n    211,\n    219,\n    223,\n    231,\n    235,\n    237,\n    241,\n    259,\n    261,\n    267,\n    273,\n    283,\n    285,\n    289,\n    297,\n    303,\n    307,\n    319,\n    321,\n    327,\n    331,\n    339,\n    349,\n    357,\n    361,\n    367,\n    385,\n    391,\n    393,\n    399,\n    409,\n    415,\n    421,\n    427,\n    429,\n    433,\n    451,\n    463,\n    475,\n    477,\n    483,\n    487,\n    489,\n    495,\n    511,\n    517,\n    519,\n    529,\n    535,\n    537,\n    541,\n    553,\n    559,\n    577,\n    579,\n    583,\n    591,\n    601,\n    613,\n    615,\n    619,\n    621,\n    631,\n    639,\n    643,\n    645,\n    651,\n    655,\n    673,\n    679,\n    685,\n    693,\n    699,\n    717,\n    723,\n    727,\n    729,\n    735,\n    739,\n    741,\n    745,\n    769,\n    777,\n    781,\n    787,\n    801,\n    805,\n    819,\n    823,\n    831,\n    841,\n    855,\n    867,\n    873,\n    883,\n    885,\n    895,\n    897,\n    903,\n    925,\n    927,\n    931,\n    933,\n    937,\n    957,\n    961,\n    975,\n    979,\n    981,\n    991,\n    993,\n    997,\n    1009,\n    1011,\n    1021,\n    1023,\n    1029,\n    1039,\n    1041,\n    1053,\n    1057,\n    1087,\n    1093,\n    1095,\n    1101,\n    1105,\n    1107,\n    1117,\n    1123,\n    1147,\n    1155,\n    1167,\n    1179,\n    1183,\n    1189,\n    1197,\n    1201,\n    1203,\n    1209,\n    1219,\n    1231,\n    1233,\n    1245,\n    1249,\n    1251,\n    1261,\n    1263,\n    1275,\n    1281,\n    1285,\n    1291,\n    1303,\n    1309,\n    1323,\n    1329,\n    1339,\n    1357,\n    1365,\n    1369,\n    1387,\n    1389,\n    1395,\n    1401,\n    1417,\n    1419,\n    1435,\n    1441,\n    1455,\n    1459,\n    1471,\n    1473,\n    1485,\n    1491,\n    1495,\n    1497,\n    1501,\n    1503,\n    1519,\n    1533,\n    1543,\n    1545,\n    1563,\n    1567,\n    1575,\n    1579,\n    1585,\n    1587,\n    1597,\n    1599,\n    1611,\n    1639,\n    1641,\n    1645,\n    1659,\n    1663,\n    1675,\n    1693,\n    1701,\n    1705,\n    1711,\n    1723,\n    1731,\n    1737,\n    1749,\n    1765,\n    1767,\n    1771,\n    1773,\n    1777,\n    1797,\n    1801,\n    1809,\n    1819,\n    1827,\n    1831,\n    1833,\n    1839,\n    1857,\n    1869,\n    1879,\n    1893,\n    1899,\n    1915,\n    1921,\n    1923,\n    1933,\n    1941,\n    1945,\n    1959,\n    1963,\n    1965,\n    1977,\n    1983,\n    1987,\n    1995,\n    2001,\n    2019,\n    2023,\n    2031,\n    2053,\n    2059,\n    2065,\n    2067,\n    2079,\n    2083,\n    2085,\n    2095,\n    2113,\n    2115,\n    2121,\n    2125,\n    2127,\n    2133,\n    2163,\n    2173,\n    2187,\n    2209,\n    2211,\n    2215,\n    2217,\n    2221,\n    2239,\n    2251,\n    2253,\n    2257,\n    2271,\n    2277,\n    2281,\n    2283,\n    2301,\n    2311,\n    2317,\n    2323,\n    2335,\n    2343,\n    2355,\n    2365,\n    2379,\n    2395,\n    2403,\n    2407,\n    2409,\n    2415,\n    2419,\n    2427,\n    2439,\n    2445,\n    2461,\n    2467,\n    2473,\n    2479,\n    2491,\n    2493,\n    2505,\n    2511,\n    2523,\n    2527,\n    2545,\n    2557,\n    2563,\n    2571,\n    2575,\n    2587,\n    2589,\n    2593,\n    2599,\n    2607,\n    2625,\n    2635,\n    2647,\n    2649,\n    2653,\n    2661,\n    2667,\n    2671,\n    2689,\n    2697,\n    2715,\n    2725,\n    2755,\n    2763,\n    2773,\n    2781,\n    2785,\n    2787,\n    2797,\n    2815,\n    2817,\n    2821,\n    2823,\n    2827,\n    2835,\n    2841,\n    2845,\n    2851,\n    2877,\n    2887,\n    2899,\n    2901,\n    2905,\n    2913,\n    2923,\n    2943,\n    2953,\n    2961,\n    2971,\n    2973,\n    2977,\n    2983,\n    3003,\n    3007,\n    3027,\n    3031,\n    3037,\n    3039,\n    3049,\n    3055,\n    3073,\n    3075,\n    3091,\n    3097,\n    3099,\n    3109,\n    3111,\n    3121,\n    3123,\n    3133,\n    3153,\n    3163,\n    3171,\n    3175,\n    3183,\n    3187,\n    3199,\n    3213,\n    3223,\n    3229,\n    3235,\n    3243,\n    3259,\n    3261,\n    3289,\n    3297,\n    3301,\n    3307,\n    3313,\n    3325,\n    3339,\n    3351,\n    3355,\n    3363,\n    3381,\n    3403,\n    3405,\n    3409,\n    3411,\n    3427,\n    3433,\n    3439,\n    3451,\n    3453,\n    3465,\n    3477,\n    3481,\n    3487,\n    3489,\n    3495,\n    3499,\n    3507,\n    3559,\n    3565,\n    3571,\n    3579,\n    3595,\n    3597,\n    3603,\n    3607,\n    3613,\n    3621,\n    3625,\n    3633,\n    3655,\n    3661,\n    3663,\n    3669,\n    3675,\n    3685,\n    3687,\n    3697,\n    3709,\n    3717,\n    3721,\n    3727,\n    3747,\n    3753,\n    3763,\n    3771,\n    3781,\n    3789,\n    3793,\n    3795,\n    3811,\n    3843,\n    3847,\n    3849,\n    3865,\n    3873,\n    3879,\n    3889,\n    3891,\n    3897,\n    3909,\n    3915,\n    3931,\n    3943,\n    3951,\n    3955,\n    3969,\n    3975,\n    3981,\n    3991,\n    3999,\n    4003,\n    4015,\n    4023,\n    4033,\n    4035,\n    4041,\n    4045,\n    4063,\n    4069,\n    4081,\n    4095,\n    4105,\n    4107,\n    4129,\n    4131,\n    4161,\n    4165,\n    4173,\n    4179,\n    4189,\n    4195,\n    4201,\n    4203,\n    4207,\n    4227,\n    4237,\n    4251,\n    4255,\n    4257,\n    4263,\n    4269,\n    4285,\n    4287,\n    4315,\n    4321,\n    4329,\n    4335,\n    4363,\n    4377,\n    4383,\n    4389,\n    4399,\n    4413,\n    4431,\n    4441,\n    4443,\n    4455,\n    4461,\n    4465,\n    4483,\n    4485,\n    4495,\n    4509,\n    4519,\n    4521,\n    4539,\n    4551,\n    4561,\n    4567,\n    4569,\n    4573,\n    4587,\n    4609,\n    4611,\n    4621,\n    4623,\n    4629,\n    4645,\n    4647,\n    4653,\n    4663,\n    4671,\n    4675,\n    4695,\n    4699,\n    4713,\n    4717,\n    4725,\n    4741,\n    4761,\n    4767,\n    4773,\n    4797,\n    4801,\n    4809,\n    4813,\n    4819,\n    4833,\n    4837,\n    4839,\n    4843,\n    4851,\n    4863,\n    4867,\n    4881,\n    4887,\n    4893,\n    4929,\n    4951,\n    4963,\n    4965,\n    4969,\n    4977,\n    4987,\n    4989,\n    4993,\n    4999,\n    5001,\n    5007,\n    5019,\n    5029,\n    5041,\n    5043,\n    5049,\n    5053,\n    5089,\n    5103,\n    5127,\n    5137,\n    5139,\n    5149,\n    5151,\n    5157,\n    5169,\n    5179,\n    5181,\n    5191,\n    5211,\n    5217,\n    5229,\n    5233,\n    5235,\n    5253,\n    5259,\n    5277,\n    5283,\n    5293,\n    5295,\n    5299,\n    5325,\n    5335,\n    5341,\n    5343,\n    5371,\n    5377,\n    5379,\n    5385,\n    5409,\n    5419,\n    5427,\n    5433,\n    5449,\n    5455,\n    5463,\n    5473,\n    5487,\n    5491,\n    5503,\n    5515,\n    5527,\n    5547,\n    5551,\n    5559,\n    5569,\n    5577,\n    5587,\n    5589,\n    5593,\n    5599,\n    5613,\n    5617,\n    5637,\n    5641,\n    5649,\n    5655,\n    5661,\n    5671,\n    5673,\n    5679,\n    5691,\n    5701,\n    5707,\n    5713,\n    5719,\n    5737,\n    5755,\n    5763,\n    5767,\n    5769,\n    5803,\n    5809,\n    5817,\n    5827,\n    5833,\n    5839,\n    5851,\n    5869,\n    5883,\n    5889,\n    5893,\n    5901,\n    5905,\n    5911,\n    5913,\n    5923,\n    5959,\n    5965,\n    5967,\n    5971,\n    5973,\n    5977,\n    5991,\n    5997,\n    6009,\n    6019,\n    6031,\n    6049,\n    6055,\n    6061,\n    6079,\n    6093,\n    6111,\n    6115,\n    6123,\n    6141,\n    6147,\n    6159,\n    6163,\n    6175,\n    6177,\n    6195,\n    6211,\n    6229,\n    6237,\n    6243,\n    6249,\n    6253,\n    6271,\n    6273,\n    6279,\n    6301,\n    6309,\n    6331,\n    6345,\n    6351,\n    6355,\n    6363,\n    6367,\n    6369,\n    6373,\n    6379,\n    6385,\n    6399,\n    6411,\n    6415,\n    6427,\n    6433,\n    6435,\n    6447,\n    6463,\n    6471,\n    6475,\n    6477,\n    6501,\n    6505,\n    6523,\n    6531,\n    6535,\n    6541,\n    6553,\n    6559,\n    6567,\n    6573,\n    6601,\n    6621,\n    6625,\n    6631,\n    6661,\n    6663,\n    6667,\n    6669,\n    6679,\n    6687,\n    6693,\n    6715,\n    6723,\n    6733,\n    6741,\n    6745,\n    6747,\n    6753,\n    6757,\n    6763,\n    6765,\n    6783,\n    6787,\n    6789,\n    6841,\n    6849,\n    6867,\n    6871,\n    6883,\n    6891,\n    6909,\n    6915,\n    6921,\n    6931,\n    6933,\n    6937,\n    6951,\n    6981,\n    6985,\n    6999,\n    7003,\n    7009,\n    7035,\n    7041,\n    7045,\n    7047,\n    7069,\n    7077,\n    7081,\n    7087,\n    7101,\n    7111,\n    7129,\n    7135,\n    7153,\n    7167,\n    7171,\n    7173,\n    7183,\n    7191,\n    7195,\n    7197,\n    7207,\n    7215,\n    7231,\n    7233,\n    7237,\n    7245,\n    7249,\n    7279,\n    7293,\n    7299,\n    7311,\n    7321,\n    7333,\n    7339,\n    7341,\n    7345,\n    7357,\n    7359,\n    7371,\n    7377,\n    7395,\n    7401,\n    7405,\n    7419,\n    7435,\n    7437,\n    7443,\n    7447,\n    7459,\n    7471,\n    7489,\n    7501,\n    7503,\n    7507,\n    7531,\n    7533,\n    7549,\n    7551,\n    7563,\n    7567,\n    7575,\n    7585,\n    7591,\n    7593,\n    7603,\n    7609,\n    7629,\n    7633,\n    7639,\n    7645,\n    7677,\n    7687,\n    7689,\n    7701,\n    7711,\n    7717,\n    7737,\n    7755,\n    7779,\n    7791,\n    7795,\n    7801,\n    7803,\n    7813,\n    7827,\n    7833,\n    7837,\n    7855,\n    7881,\n    7885,\n    7897,\n    7899,\n    7909,\n    7917,\n    7921,\n    7929,\n    7939,\n    7947,\n    7951,\n    7963,\n    7969,\n    7971,\n    8001,\n    8005,\n    8007,\n    8013,\n    8037,\n    8047,\n    8071,\n    8073,\n    8085,\n    8089,\n    8107,\n    8109,\n    8127,\n    8131,\n    8139,\n    8151,\n    8161,\n    8169,\n    8173,\n    8175,\n    8191,\n    8193,\n    8221,\n    8223,\n    8227,\n    8233,\n    8253,\n    8257,\n    8263,\n    8269,\n    8283,\n    8289,\n    8299,\n    8325,\n    8331,\n    8347,\n    8359,\n    8365,\n    8367,\n    8379,\n    8409,\n    8413,\n    8421,\n    8445,\n    8449,\n    8467,\n    8473,\n    8475,\n    8479,\n    8487,\n    8493,\n    8515,\n    8535,\n    8539,\n    8551,\n    8553,\n    8575,\n    8577,\n    8583,\n    8589,\n    8601,\n    8605,\n    8611,\n    8617,\n    8635,\n    8637,\n    8641,\n    8647,\n    8655,\n    8661,\n    8673,\n    8683,\n    8719,\n    8757,\n    8769,\n    8773,\n    8787,\n    8793,\n    8805,\n    8809,\n    8827,\n    8829,\n    8833,\n    8835,\n    8841,\n    8869,\n    8871,\n    8895,\n    8913,\n    8931,\n    8937,\n    8947,\n    8955,\n    8961,\n    8977,\n    8979,\n    8989,\n    8995,\n    8997,\n    9009,\n    9031,\n    9033,\n    9061,\n    9063,\n    9073,\n    9075,\n    9081,\n    9085,\n    9115,\n    9117,\n    9121,\n    9123,\n    9135,\n    9139,\n    9145,\n    9151,\n    9177,\n    9181,\n    9211,\n    9213,\n    9223,\n    9231,\n    9235,\n    9249,\n    9253,\n    9267,\n    9273,\n    9277,\n    9285,\n    9291,\n    9303,\n    9315,\n    9331,\n    9339,\n    9349,\n    9373,\n    9387,\n    9399,\n    9403,\n    9409,\n    9421,\n    9423,\n    9429,\n    9441,\n    9451,\n    9457,\n    9471,\n    9475,\n    9481,\n    9483,\n    9487,\n    9501,\n    9535,\n    9537,\n    9543,\n    9547,\n    9549,\n    9555,\n    9561,\n    9567,\n    9613,\n    9621,\n    9625,\n    9631,\n    9643,\n    9649,\n    9661,\n    9663,\n    9675,\n    9687,\n    9691,\n    9703,\n    9727,\n    9733,\n    9751,\n    9753,\n    9775,\n    9787,\n    9789,\n    9795,\n    9801,\n    9807,\n    9811,\n    9813,\n    9837,\n    9841,\n    9883,\n    9895,\n    9915,\n    9927,\n    9937,\n    9957,\n    9961,\n    9979,\n    9985,\n    9987,\n    9997,\n    9999,\n    10003,\n    10009,\n    10017,\n    10041,\n    10051,\n    10059,\n    10069,\n    10071,\n    10083,\n    10087,\n    10089,\n    10095,\n    10111,\n    10117,\n    10125,\n    10131,\n    10143,\n    10147,\n    10149,\n    10153,\n    10173,\n    10191,\n    10195,\n    10197,\n    10221,\n    10239,\n    10255,\n    10261,\n    10275,\n    10279,\n    10291,\n    10311,\n    10315,\n    10317,\n    10321,\n    10341,\n    10363,\n    10365,\n    10387,\n    10399,\n    10411,\n    10417,\n    10431,\n    10441,\n    10443,\n    10459,\n    10461,\n    10471,\n    10501,\n    10507,\n    10509,\n    10525,\n    10531,\n    10533,\n    10555,\n    10563,\n    10569,\n    10575,\n    10585,\n    10587,\n    10597,\n    10599,\n    10605,\n    10609,\n    10635,\n    10651,\n    10653,\n    10659,\n    10671,\n    10683,\n    10723,\n    10725,\n    10731,\n    10747,\n    10753,\n    10755,\n    10759,\n    10773,\n    10785,\n    10789,\n    10797,\n    10809,\n    10843,\n    10849,\n    10873,\n    10881,\n    10891,\n    10903,\n    10909,\n    10911,\n    10921,\n    10923,\n    10945,\n    10953,\n    10965,\n    10975,\n    10977,\n    11005,\n    11011,\n    11017,\n    11047,\n    11055,\n    11059,\n    11073,\n    11077,\n    11089,\n    11091,\n    11097,\n    11113,\n    11137,\n    11157,\n    11163,\n    11173,\n    11181,\n    11193,\n    11197,\n    11205,\n    11217,\n    11223,\n    11227,\n    11239,\n    11245,\n    11247,\n    11259,\n    11263,\n    11269,\n    11283,\n    11289,\n    11301,\n    11323,\n    11341,\n    11343,\n    11371,\n    11373,\n    11377,\n    11389,\n    11391,\n    11395,\n    11407,\n    11419,\n    11425,\n    11427,\n    11437,\n    11439,\n    11475,\n    11479,\n    11487,\n    11491,\n    11521,\n    11529,\n    11535,\n    11539,\n    11541,\n    11577,\n    11581,\n    11583,\n    11599,\n    11617,\n    11623,\n    11629,\n    11641,\n    11643,\n    11659,\n    11667,\n    11677,\n    11679,\n    11707,\n    11721,\n    11725,\n    11731,\n    11769,\n    11781,\n    11797,\n    11803,\n    11811,\n    11815,\n    11823,\n    11827,\n    11833,\n    11835,\n    11845,\n    11847,\n    11853,\n    11865,\n    11877,\n    11887,\n    11911,\n    11923,\n    11929,\n    11943,\n    11953,\n    11959,\n    11961,\n    11991,\n    11995,\n    12007,\n    12015,\n    12025,\n    12049,\n    12057,\n    12063,\n    12069,\n    12079,\n    12097,\n    12103,\n    12117,\n    12121,\n    12133,\n    12139,\n    12141,\n    12151,\n    12159,\n    12163,\n    12165,\n    12193,\n    12201,\n    12207,\n    12223,\n    12237,\n    12243,\n    12265,\n    12267,\n    12285,\n    12295,\n    12301,\n    12307,\n    12315,\n    12319,\n    12321,\n    12333,\n    12337,\n    12357,\n    12363,\n    12373,\n    12379,\n    12391,\n    12399,\n    12411,\n    12417,\n    12427,\n    12435,\n    12457,\n    12481,\n    12487,\n    12495,\n    12499,\n    12519,\n    12543,\n    12547,\n    12553,\n    12567,\n    12579,\n    12589,\n    12607,\n    12609,\n    12621,\n    12631,\n    12645,\n    12649,\n    12651,\n    12673,\n    12679,\n    12699,\n    12709,\n    12711,\n    12729,\n    12735,\n    12741,\n    12759,\n    12763,\n    12777,\n    12781,\n    12799,\n    12801,\n    12813,\n    12819,\n    12861,\n    12867,\n    12877,\n    12885,\n    12903,\n    12907,\n    12925,\n    12937,\n    12951,\n    12957,\n    12961,\n    12969,\n    12991,\n    12999,\n    13009,\n    13011,\n    13021,\n    13027,\n    13029,\n    13053,\n    13057,\n    13063,\n    13077,\n    13083,\n    13113,\n    13117,\n    13119,\n    13125,\n    13129,\n    13135,\n    13141,\n    13167,\n    13177,\n    13179,\n    13203,\n    13209,\n    13215,\n    13219,\n    13243,\n    13245,\n    13267,\n    13273,\n    13279,\n    13281,\n    13285,\n    13293,\n    13309,\n    13317,\n    13323,\n    13327,\n    13335,\n    13345,\n    13347,\n    13359,\n    13363,\n    13371,\n    13387,\n    13419,\n    13441,\n    13443,\n    13465,\n    13473,\n    13485,\n    13489,\n    13513,\n    13515,\n    13519,\n    13527,\n    13531,\n    13533,\n    13537,\n    13551,\n    13567,\n    13575,\n    13581,\n    13587,\n    13599,\n    13609,\n    13633,\n    13657,\n    13677,\n    13681,\n    13693,\n    13695,\n    13707,\n    13723,\n    13725,\n    13737,\n    13741,\n    13743,\n    13759,\n    13801,\n    13803,\n    13821,\n    13831,\n    13833,\n    13849,\n    13851,\n    13861,\n    13873,\n    13893,\n    13897,\n    13929,\n    13947,\n    13953,\n    13957,\n    13995,\n    14007,\n    14017,\n    14031,\n    14035,\n    14037,\n    14041,\n    14049,\n    14053,\n    14059,\n    14073,\n    14079,\n    14091,\n    14095,\n    14113,\n    14115,\n    14119,\n    14137,\n    14163,\n    14179,\n    14181,\n    14185,\n    14199,\n    14205,\n    14209,\n    14211,\n    14217,\n    14229,\n    14245,\n    14247,\n    14253,\n    14281,\n    14287,\n    14293,\n    14307,\n    14313,\n    14317,\n    14331,\n    14347,\n    14349,\n    14353,\n    14367,\n    14395,\n    14407,\n    14419,\n    14427,\n    14437,\n    14439,\n    14443,\n    14449,\n    14457,\n    14461,\n    14473,\n    14491,\n    14493,\n    14505,\n    14521,\n    14523,\n    14539,\n    14541,\n    14565,\n    14587,\n    14595,\n    14599,\n    14601,\n    14607,\n    14625,\n    14661,\n    14667,\n    14689,\n    14691,\n    14713,\n    14715,\n    14721,\n    14731,\n    14749,\n    14751,\n    14755,\n    14775,\n    14779,\n    14785,\n    14793,\n    14797,\n    14817,\n    14841,\n    14875,\n    14881,\n    14883,\n    14899,\n    14905,\n    14919,\n    14923,\n    14931,\n    14935,\n    14943,\n    14947,\n    14973,\n    14977,\n    14997,\n    15003,\n    15007,\n    15009,\n    15025,\n    15031,\n    15049,\n    15057,\n    15063,\n    15069,\n    15073,\n    15081,\n    15103,\n    15109,\n    15121,\n    15129,\n    15133,\n    15141,\n    15165,\n    15169,\n    15175,\n    15183,\n    15187,\n    15189,\n    15193,\n    15205,\n    15213,\n    15217,\n    15229,\n    15231,\n    15235,\n    15237,\n    15247,\n    15255,\n    15259,\n    15261,\n    15271,\n    15291,\n    15301,\n    15313,\n    15333,\n    15355,\n    15379,\n    15381,\n    15385,\n    15397,\n    15417,\n    15445,\n    15457,\n    15469,\n    15471,\n    15481,\n    15487,\n    15489,\n    15511,\n    15519,\n    15523,\n    15531,\n    15543,\n    15547,\n    15567,\n    15573,\n    15577,\n    15585,\n    15597,\n    15609,\n    15645,\n    15655,\n    15661,\n    15673,\n    15675,\n    15693,\n    15697,\n    15717,\n    15735,\n    15763,\n    15771,\n    15783,\n    15793,\n    15805,\n    15817,\n    15819,\n    15835,\n    15843,\n    15847,\n    15855,\n    15879,\n    15883,\n    15891,\n    15901,\n    15907,\n    15919,\n    15927,\n    15939,\n    15955,\n    15969,\n    15975,\n    15981,\n    15993,\n    16003,\n    16009,\n    16023,\n    16045,\n    16047,\n    16053,\n    16057,\n    16077,\n    16081,\n    16095,\n    16101,\n    16135,\n    16153,\n    16191,\n    16195,\n    16197,\n    16203,\n    16213,\n    16227,\n    16237,\n    16245,\n    16255,\n    16267,\n    16275,\n    16279,\n    16287,\n    16297,\n    16309,\n    16329,\n    16333,\n    16341,\n    16353,\n    16359,\n    16365,\n    16381,\n    16387,\n    16389,\n    16393,\n    16395,\n    16413,\n    16417,\n    16429,\n    16431,\n    16447,\n    16449,\n    16467,\n    16473,\n    16479,\n    16491,\n    16507,\n    16521,\n    16539,\n    16557,\n    16573,\n    16591,\n    16611,\n    16615,\n    16617,\n    16621,\n    16635,\n    16639,\n    16657,\n    16665,\n    16677,\n    16701,\n    16707,\n    16725,\n    16747,\n    16765,\n    16771,\n    16773,\n    16791,\n    16795,\n    16825,\n    16827,\n    16845,\n    16863,\n    16867,\n    16893,\n    16915,\n    16917,\n    16921,\n    16933,\n    16939,\n    16947,\n    16953,\n    16957,\n    16959,\n    16969,\n    16971,\n    16981,\n    16995,\n    17001,\n    17011,\n    17017,\n    17019,\n    17035,\n    17047,\n    17053,\n    17061,\n    17083,\n    17085,\n    17103,\n    17115,\n    17119,\n    17125,\n    17127,\n    17151,\n    17157,\n    17173,\n    17181,\n    17209,\n    17211,\n    17215,\n    17221,\n    17223,\n    17229,\n    17251,\n    17253,\n    17283,\n    17287,\n    17305,\n    17313,\n    17329,\n    17335,\n    17341,\n    17355,\n    17359,\n    17367,\n    17389,\n    17391,\n    17401,\n    17409,\n    17431,\n    17433,\n    17455,\n    17463,\n    17467,\n    17473,\n    17485,\n    17497,\n    17517,\n    17527,\n    17535,\n    17545,\n    17547,\n    17563,\n    17565,\n    17581,\n    17589,\n    17619,\n    17625,\n    17671,\n    17677,\n    17689,\n    17695,\n    17703,\n    17713,\n    17725,\n    17733,\n    17737,\n    17749,\n    17767,\n    17769,\n    17775,\n    17781,\n    17787,\n    17797,\n    17811,\n    17829,\n    17851,\n    17875,\n    17883,\n    17893,\n    17913,\n    17925,\n    17935,\n    17943,\n    17947,\n    17965,\n    17977,\n    17991,\n    17997,\n    18001,\n    18007,\n    18019,\n    18025,\n    18043,\n    18049,\n    18055,\n    18067,\n    18085,\n    18087,\n    18123,\n    18127,\n    18145,\n    18147,\n    18151,\n    18153,\n    18169,\n    18175,\n    18181,\n    18187,\n    18189,\n    18195,\n    18207,\n    18211,\n    18237,\n    18253,\n    18261,\n    18279,\n    18283,\n    18285,\n    18295,\n    18303,\n    18315,\n    18333,\n    18375,\n    18381,\n    18397,\n    18399,\n    18403,\n    18409,\n    18441,\n    18445,\n    18451,\n    18459,\n    18465,\n    18469,\n    18475,\n    18493,\n    18495,\n    18501,\n    18513,\n    18523,\n    18535,\n    18543,\n    18589,\n    18591,\n    18597,\n    18615,\n    18619,\n    18621,\n    18633,\n    18639,\n    18655,\n    18661,\n    18681,\n    18693,\n    18715,\n    18717,\n    18723,\n    18745,\n    18747,\n    18757,\n    18759,\n    18765,\n    18775,\n    18777,\n    18783,\n    18787,\n    18817,\n    18819,\n    18825,\n    18837,\n    18849,\n    18853,\n    18871,\n    18873,\n    18879,\n    18915,\n    18937,\n    18943,\n    18951,\n    18967,\n    18969,\n    18975,\n    18979,\n    18987,\n    19009,\n    19015,\n    19017,\n    19033,\n    19051,\n    19081,\n    19113,\n    19119,\n    19125,\n    19131,\n    19153,\n    19155,\n    19159,\n    19161,\n    19173,\n    19195,\n    19197,\n    19207,\n    19225,\n    19237,\n    19261,\n    19263,\n    19269,\n    19285,\n    19309,\n    19323,\n    19327,\n    19329,\n    19333,\n    19341,\n    19347,\n    19351,\n    19357,\n    19363,\n    19365,\n    19371,\n    19389,\n    19395,\n    19405,\n    19411,\n    19417,\n    19435,\n    19455,\n    19467,\n    19477,\n    19479,\n    19503,\n    19521,\n    19533,\n    19543,\n    19551,\n    19579,\n    19581,\n    19603,\n    19609,\n    19615,\n    19627,\n    19629,\n    19657,\n    19659,\n    19687,\n    19693,\n    19705,\n    19707,\n    19711,\n    19719,\n    19723,\n    19725,\n    19731,\n    19735,\n    19755,\n    19767,\n    19771,\n    19773,\n    19783,\n    19791,\n    19807,\n    19825,\n    19851,\n    19861,\n    19869,\n    19875,\n    19893,\n    19917,\n    19921,\n    19929,\n    19939,\n    19963,\n    19981,\n    19993,\n    20005,\n    20013,\n    20017,\n    20043,\n    20067,\n    20071,\n    20077,\n    20085,\n    20089,\n    20101,\n    20103,\n    20121,\n    20133,\n    20149,\n    20151,\n    20169,\n    20181,\n    20185,\n    20197,\n    20203,\n    20215,\n    20227,\n    20253,\n    20257,\n    20271,\n    20295,\n    20307,\n    20319,\n    20341,\n    20349,\n    20353,\n    20355,\n    20359,\n    20371,\n    20379,\n    20385,\n    20397,\n    20403,\n    20425,\n    20445,\n    20449,\n    20455,\n    20461,\n    20475,\n    20485,\n    20487,\n    20509,\n    20511,\n    20539,\n    20551,\n    20563,\n    20581,\n    20583,\n    20587,\n    20605,\n    20611,\n    20613,\n    20649,\n    20665,\n    20679,\n    20697,\n    20713,\n    20731,\n    20733,\n    20739,\n    20757,\n    20763,\n    20769,\n    20773,\n    20793,\n    20797,\n    20805,\n    20811,\n    20821,\n    20823,\n    20833,\n    20835,\n    20839,\n    20845,\n    20859,\n    20875,\n    20887,\n    20889,\n    20899,\n    20905,\n    20923,\n    20937,\n    20941,\n    20953,\n    20959,\n    20965,\n    20991,\n    21013,\n    21015,\n    21021,\n    21027,\n    21043,\n    21049,\n    21051,\n    21055,\n    21073,\n    21075,\n    21111,\n    21115,\n    21129,\n    21139,\n    21147,\n    21153,\n    21169,\n    21175,\n    21177,\n    21189,\n    21193,\n    21199,\n    21205,\n    21211,\n    21223,\n    21231,\n    21253,\n    21255,\n    21307,\n    21309,\n    21315,\n    21327,\n    21331,\n    21337,\n    21345,\n    21373,\n    21381,\n    21405,\n    21409,\n    21411,\n    21421,\n    21433,\n    21445,\n    21451,\n    21453,\n    21469,\n    21471,\n    21475,\n    21499,\n    21505,\n    21529,\n    21531,\n    21537,\n    21549,\n    21567,\n    21571,\n    21591,\n    21597,\n    21609,\n    21613,\n    21615,\n    21621,\n    21655,\n    21663,\n    21687,\n    21693,\n    21703,\n    21709,\n    21717,\n    21721,\n    21735,\n    21739,\n    21751,\n    21757,\n    21759,\n    21771,\n    21787,\n    21819,\n    21841,\n    21847,\n    21867,\n    21885,\n    21891,\n    21897,\n    21913,\n    21915,\n    21925,\n    21927,\n    21931,\n    21933,\n    21957,\n    21967,\n    21987,\n    21993,\n    22009,\n    22017,\n    22035,\n    22039,\n    22075,\n    22087,\n    22093,\n    22095,\n    22099,\n    22113,\n    22119,\n    22125,\n    22147,\n    22155,\n    22165,\n    22167,\n    22177,\n    22185,\n    22197,\n    22225,\n    22227,\n    22231,\n    22269,\n    22281,\n    22305,\n    22327,\n    22333,\n    22339,\n    22369,\n    22371,\n    22377,\n    22381,\n    22399,\n    22401,\n    22411,\n    22417,\n    22431,\n    22437,\n    22461,\n    22471,\n    22473,\n    22479,\n    22503,\n    22507,\n    22521,\n    22525,\n    22527,\n    22533,\n    22555,\n    22569,\n    22585,\n    22587,\n    22591,\n    22597,\n    22603,\n    22617,\n    22627,\n    22629,\n    22651,\n    22683,\n    22687,\n    22689,\n    22701,\n    22711,\n    22743,\n    22779,\n    22789,\n    22791,\n    22795,\n    22807,\n    22827,\n    22839,\n    22843,\n    22851,\n    22855,\n    22869,\n    22879,\n    22881,\n    22885,\n    22893,\n    22911,\n    22921,\n    22941,\n    22947,\n    22953,\n    22957,\n    22965,\n    22969,\n    22975,\n    22981,\n    22987,\n    22999,\n    23007,\n    23031,\n    23037,\n    23041,\n    23061,\n    23067,\n    23083,\n    23089,\n    23095,\n    23101,\n    23109,\n    23137,\n    23145,\n    23163,\n    23169,\n    23173,\n    23191,\n    23193,\n    23199,\n    23205,\n    23269,\n    23271,\n    23293,\n    23295,\n    23311,\n    23323,\n    23341,\n    23343,\n    23353,\n    23355,\n    23361,\n    23365,\n    23373,\n    23377,\n    23397,\n    23419,\n    23421,\n    23427,\n    23445,\n    23449,\n    23451,\n    23457,\n    23469,\n    23479,\n    23499,\n    23515,\n    23523,\n    23529,\n    23533,\n    23551,\n    23563,\n    23565,\n    23569,\n    23571,\n    23577,\n    23607,\n    23613,\n    23649,\n    23671,\n    23677,\n    23689,\n    23701,\n    23709,\n    23721,\n    23767,\n    23775,\n    23781,\n    23799,\n    23805,\n    23815,\n    23829,\n    23835,\n    23851,\n    23857,\n    23881,\n    23889,\n    23893,\n    23913,\n    23919,\n    23925,\n    23929,\n    23943,\n    23953,\n    23955,\n    23961,\n    23965,\n    23971,\n    23973,\n    23991,\n    24013,\n    24015,\n    24025,\n    24033,\n    24037,\n    24069,\n    24073,\n    24117,\n    24121,\n    24129,\n    24133,\n    24145,\n    24151,\n    24181,\n    24183,\n    24213,\n    24217,\n    24237,\n    24247,\n    24255,\n    24261,\n    24277,\n    24285,\n    24289,\n    24301,\n    24309,\n    24321,\n    24327,\n    24331,\n    24333,\n    24343,\n    24363,\n    24381,\n    24387,\n    24397,\n    24403,\n    24405,\n    24423,\n    24429,\n    24447,\n    24451,\n    24481,\n    24499,\n    24523,\n    24531,\n    24571,\n    24573,\n    24585,\n    24591,\n    24603,\n    24619,\n    24633,\n    24637,\n    24639,\n    24643,\n    24645,\n    24649,\n    24663,\n    24669,\n    24709,\n    24711,\n    24727,\n    24729,\n    24733,\n    24739,\n    24741,\n    24759,\n    24763,\n    24771,\n    24783,\n    24789,\n    24805,\n    24811,\n    24829,\n    24831,\n    24843,\n    24855,\n    24865,\n    24873,\n    24877,\n    24895,\n    24907,\n    24909,\n    24933,\n    24951,\n    24957,\n    24963,\n    24985,\n    24991,\n    25015,\n    25017,\n    25021,\n    25033,\n    25057,\n    25063,\n    25095,\n    25099,\n    25107,\n    25117,\n    25119,\n    25129,\n    25147,\n    25153,\n    25159,\n    25167,\n    25171,\n    25185,\n    25189,\n    25201,\n    25213,\n    25215,\n    25225,\n    25237,\n    25255,\n    25263,\n    25285,\n    25287,\n    25293,\n    25309,\n    25317,\n    25335,\n    25341,\n    25347,\n    25377,\n    25389,\n    25413,\n    25425,\n    25431,\n    25441,\n    25455,\n    25461,\n    25477,\n    25483,\n    25489,\n    25497,\n    25503,\n    25525,\n    25549,\n    25561,\n    25567,\n    25569,\n    25581,\n    25591,\n    25593,\n    25611,\n    25621,\n    25623,\n    25645,\n    25647,\n    25657,\n    25671,\n    25677,\n    25687,\n    25737,\n    25753,\n    25759,\n    25771,\n    25773,\n    25803,\n    25815,\n    25819,\n    25821,\n    25833,\n    25837,\n    25845,\n    25855,\n    25861,\n    25875,\n    25893,\n    25909,\n    25917,\n    25923,\n    25929,\n    25939,\n    25959,\n    25963,\n    25971,\n    25977,\n    25987,\n    26005,\n    26007,\n    26011,\n    26025,\n    26035,\n    26041,\n    26071,\n    26085,\n    26103,\n    26107,\n    26115,\n    26119,\n    26149,\n    26155,\n    26167,\n    26169,\n    26179,\n    26209,\n    26215,\n    26217,\n    26221,\n    26229,\n    26251,\n    26263,\n    26281,\n    26293,\n    26295,\n    26301,\n    26313,\n    26319,\n    26343,\n    26347,\n    26359,\n    26365,\n    26385,\n    26397,\n    26409,\n    26413,\n    26431,\n    26445,\n    26467,\n    26505,\n    26523,\n    26529,\n    26533,\n    26547,\n    26553,\n    26565,\n    26595,\n    26599,\n    26617,\n    26629,\n    26641,\n    26649,\n    26655,\n    26659,\n    26665,\n    26683,\n    26695,\n    26697,\n    26703,\n    26719,\n    26721,\n    26727,\n    26733,\n    26749,\n    26755,\n    26757,\n    26781,\n    26797,\n    26805,\n    26841,\n    26845,\n    26847,\n    26881,\n    26883,\n    26893,\n    26905,\n    26907,\n    26913,\n    26923,\n    26925,\n    26935,\n    26943,\n    26997,\n    27001,\n    27007,\n    27009,\n    27013,\n    27027,\n    27031,\n    27033,\n    27037,\n    27039,\n    27061,\n    27063,\n    27075,\n    27079,\n    27091,\n    27093,\n    27097,\n    27105,\n    27141,\n    27157,\n    27189,\n    27195,\n    27199,\n    27201,\n    27205,\n    27207,\n    27217,\n    27237,\n    27265,\n    27271,\n    27283,\n    27301,\n    27309,\n    27315,\n    27321,\n    27325,\n    27331,\n    27333,\n    27349,\n    27357,\n    27363,\n    27387,\n    27393,\n    27409,\n    27415,\n    27417,\n    27427,\n    27435,\n    27453,\n    27457,\n    27469,\n    27475,\n    27477,\n    27483,\n    27489,\n    27493,\n    27501,\n    27511,\n    27531,\n    27541,\n    27561,\n    27585,\n    27595,\n    27597,\n    27603,\n    27639,\n    27663,\n    27667,\n    27691,\n    27693,\n    27705,\n    27711,\n    27741,\n    27751,\n    27757,\n    27765,\n    27783,\n    27793,\n    27805,\n    27807,\n    27813,\n    27829,\n    27831,\n    27847,\n    27855,\n    27859,\n    27871,\n    27879,\n    27913,\n    27915,\n    27951,\n    27961,\n    27963,\n    27975,\n    28021,\n    28023,\n    28027,\n    28039,\n    28069,\n    28071,\n    28083,\n    28087,\n    28089,\n    28101,\n    28105,\n    28119,\n    28123,\n    28129,\n    28131,\n    28141,\n    28161,\n    28167,\n    28191,\n    28195,\n    28203,\n    28233,\n    28239,\n    28245,\n    28293,\n    28311,\n    28321,\n    28329,\n    28339,\n    28351,\n    28359,\n    28365,\n    28375,\n    28393,\n    28405,\n    28417,\n    28429,\n    28435,\n    28437,\n    28459,\n    28467,\n    28483,\n    28509,\n    28513,\n    28525,\n    28563,\n    28569,\n    28581,\n    28593,\n    28605,\n    28633,\n    28639,\n    28677,\n    28689,\n    28701,\n    28707,\n    28735,\n    28759,\n    28765,\n    28771,\n    28791,\n    28797,\n    28801,\n    28803,\n    28815,\n    28825,\n    28827,\n    28837,\n    28843,\n    28855,\n    28857,\n    28867,\n    28869,\n    28887,\n    28891,\n    28905,\n    28921,\n    28923,\n    28933,\n    28951,\n    28963,\n    28969,\n    28987,\n    28989,\n    28993,\n    28995,\n    29005,\n    29025,\n    29031,\n    29035,\n    29049,\n    29055,\n    29065,\n    29089,\n    29091,\n    29095,\n    29113,\n    29121,\n    29127,\n    29137,\n    29143,\n    29149,\n    29157,\n    29175,\n    29185,\n    29193,\n    29203,\n    29205,\n    29239,\n    29253,\n    29281,\n    29283,\n    29287,\n    29295,\n    29311,\n    29317,\n    29319,\n    29329,\n    29341,\n    29349,\n    29371,\n    29391,\n    29413,\n    29437,\n    29451,\n    29457,\n    29467,\n    29473,\n    29485,\n    29487,\n    29493,\n    29497,\n    29515,\n    29517,\n    29521,\n    29533,\n    29535,\n    29563,\n    29577,\n    29589,\n    29595,\n    29599,\n    29611,\n    29613,\n    29623,\n    29625,\n    29653,\n    29659,\n    29661,\n    29665,\n    29677,\n    29689,\n    29697,\n    29709,\n    29715,\n    29721,\n    29751,\n    29757,\n    29779,\n    29781,\n    29787,\n    29791,\n    29805,\n    29815,\n    29829,\n    29845,\n    29847,\n    29851,\n    29865,\n    29877,\n    29893,\n    29905,\n    29913,\n    29925,\n    29949,\n    29959,\n    29971,\n    29973,\n    29989,\n    29995,\n    30013,\n    30021,\n    30061,\n    30067,\n    30073,\n    30075,\n    30087,\n    30103,\n    30115,\n    30121,\n    30159,\n    30163,\n    30181,\n    30207,\n    30225,\n    30229,\n    30231,\n    30241,\n    30243,\n    30253,\n    30261,\n    30265,\n    30273,\n    30289,\n    30291,\n    30303,\n    30313,\n    30327,\n    30337,\n    30367,\n    30369,\n    30373,\n    30387,\n    30397,\n    30409,\n    30411,\n    30429,\n    30439,\n    30465,\n    30471,\n    30475,\n    30477,\n    30495,\n    30499,\n    30505,\n    30523,\n    30529,\n    30537,\n    30541,\n    30543,\n    30547,\n    30559,\n    30565,\n    30567,\n    30589,\n    30601,\n    30643,\n    30651,\n    30681,\n    30687,\n    30727,\n    30747,\n    30753,\n    30765,\n    30775,\n    30777,\n    30789,\n    30793,\n    30807,\n    30817,\n    30837,\n    30849,\n    30861,\n    30871,\n    30873,\n    30877,\n    30885,\n    30901,\n    30915,\n    30919,\n    30943,\n    30945,\n    30949,\n    30969,\n    30997,\n    30999,\n    31003,\n    31021,\n    31029,\n    31033,\n    31039,\n    31051,\n    31069,\n    31071,\n    31081,\n    31093,\n    31095,\n    31107,\n    31135,\n    31137,\n    31143,\n    31147,\n    31189,\n    31191,\n    31207,\n    31221,\n    31231,\n    31237,\n    31239,\n    31249,\n    31251,\n    31255,\n    31261,\n    31263,\n    31293,\n    31303,\n    31311,\n    31315,\n    31335,\n    31341,\n    31363,\n    31381,\n    31383,\n    31387,\n    31389,\n    31395,\n    31405,\n    31411,\n    31425,\n    31429,\n  ];\n  let partitionNumbers = [\n    1,\n    2,\n    3,\n    5,\n    7,\n    11,\n    15,\n    22,\n    30,\n    42,\n    56,\n    77,\n    101,\n    135,\n    176,\n    231,\n    297,\n    385,\n    490,\n    627,\n    792,\n    1002,\n    1255,\n    1575,\n    1958,\n    2436,\n    3010,\n    3718,\n    4565,\n    5604,\n    6842,\n    8349,\n    10143,\n    12310,\n    14883,\n    17977,\n    21637,\n    26015,\n    31185,\n    37338,\n    44583,\n    53174,\n    63261,\n    75175,\n    89134,\n    105558,\n    124754,\n    147273,\n    173525,\n    204226,\n    239943,\n    281589,\n    329931,\n    386155,\n    451276,\n    526823,\n    614154,\n    715220,\n    831820,\n    966467,\n    1121505,\n    1300156,\n    1505499,\n    1741630,\n    2012558,\n    2323520,\n    2679689,\n    3087735,\n    3554345,\n    4087968,\n    4697205,\n    5392783,\n    6185689,\n    7089500,\n    8118264,\n    9289091,\n    10619863,\n    12132164,\n    13848650,\n    15796476,\n    18004327,\n    20506255,\n    23338469,\n    26543660,\n    30167357,\n    34262962,\n    38887673,\n    44108109,\n    49995925,\n    56634173,\n    64112359,\n    72533807,\n    82010177,\n    92669720,\n    104651419,\n    118114304,\n    133230930,\n    150198136,\n    169229875,\n    190569292,\n    214481126,\n    241265379,\n    271248950,\n    304801365,\n    342325709,\n    384276336,\n    431149389,\n    483502844,\n    541946240,\n    607163746,\n    679903203,\n    761002156,\n    851376628,\n    952050665,\n    1064144451,\n    1188908248,\n    1327710076,\n    1482074143,\n    1653668665,\n    1844349560,\n    2056148051,\n    2291320912,\n    2552338241,\n    2841940500,\n    3163127352,\n    3519222692,\n    3913864295,\n    4351078600,\n    4835271870,\n    5371315400,\n    5964539504,\n    6620830889,\n    7346629512,\n    8149040695,\n    9035836076,\n    10015581680,\n    11097645016,\n    12292341831,\n    13610949895,\n    15065878135,\n    16670689208,\n    18440293320,\n    20390982757,\n    22540654445,\n    24908858009,\n    27517052599,\n    30388671978,\n    33549419497,\n    37027355200,\n    40853235313,\n    45060624582,\n    49686288421,\n    54770336324,\n    60356673280,\n    66493182097,\n    73232243759,\n    80630964769,\n    88751778802,\n    97662728555,\n    107438159466,\n    118159068427,\n    129913904637,\n    142798995930,\n    156919475295,\n    172389800255,\n    189334822579,\n    207890420102,\n    228204732751,\n    250438925115,\n    274768617130,\n    301384802048,\n    330495499613,\n    362326859895,\n    397125074750,\n    435157697830,\n    476715857290,\n    522115831195,\n    571701605655,\n    625846753120,\n    684957390936,\n    749474411781,\n    819876908323,\n    896684817527,\n    980462880430,\n    1071823774337,\n    1171432692373,\n    1280011042268,\n    1398341745571,\n    1527273599625,\n    1667727404093,\n    1820701100652,\n    1987276856363,\n    2168627105469,\n    2366022741845,\n    2580840212973,\n    2814570987591,\n    3068829878530,\n    3345365983698,\n    3646072432125,\n    3972999029388,\n    4328363658647,\n    4714566886083,\n    5134205287973,\n    5590088317495,\n    6085253859260,\n    6622987708040,\n    7206841706490,\n    7840656226137,\n    8528581302375,\n    9275102575355,\n    10085065885767,\n    10963707205259,\n    11916681236278,\n    12950095925895,\n    14070545699287,\n    15285151248481,\n    16601598107914,\n    18028182516671,\n    19573856161145,\n    21248279009367,\n    23061871173849,\n    25025873760111,\n    27152408925615,\n    29454549941750,\n    31946390696157,\n    34643126322519,\n    37561133582570,\n    40718063627362,\n    44132934884255,\n    47826239745920,\n    51820051838712,\n    56138148670947,\n    60806135438329,\n    65851585970275,\n    71304185514919,\n    77195892663512,\n    83561103925871,\n    90436839668817,\n    97862933703585,\n    105882246722733,\n    114540884553038,\n    123888443077259,\n    133978259344888,\n    144867692496445,\n    156618412527946,\n    169296722391554,\n    182973889854026,\n    197726516681672,\n    213636919820625,\n    230793554364681,\n    249291451168559,\n    269232701252579,\n    290726957916112,\n    313891991306665,\n    338854264248680,\n    365749566870782,\n    394723676655357,\n    425933084409356,\n    459545750448675,\n    495741934760846,\n    534715062908609,\n    576672674947168,\n    621837416509615,\n    670448123060170,\n    722760953690372,\n    779050629562167,\n    839611730366814,\n    904760108316360,\n    974834369944625,\n    1050197489931117,\n    1131238503938606,\n    1218374349844333,\n    1312051800816215,\n    1412749565173450,\n    1520980492851175,\n    1637293969337171,\n    1762278433057269,\n    1896564103591584,\n    2040825852575075,\n    2195786311682516,\n    2362219145337711,\n    2540952590045698,\n    2732873183547535,\n    2938929793929555,\n    3160137867148997,\n    3397584011986773,\n    3652430836071053,\n    3925922161489422,\n    4219388528587095,\n    4534253126900886,\n    4872038056472084,\n    5234371069753672,\n    5622992691950605,\n    6039763882095515,\n    6486674127079088,\n    6965850144195831,\n    7479565078510584,\n    8030248384943040,\n    8620496275465025,\n  ];\n  {\n    fibonacciTakeWhile: (fn) -> loop(result = [], a = 0, b = 1, i = 0) -> if fn(\n      a,\n      i\n    ) then\n      recur(push(result, a), b, a + b, i + 1)\n    else\n      result\n    end,\n    tribonacciTakeWhile: (fn) -> loop(result = [], a = 0, b = 1, c = 1, i = 0) -> if fn(\n      a,\n      i\n    ) then\n      recur(push(result, a), b, c, a + b + c, i + 1)\n    else\n      result\n    end,\n    lucasTakeWhile: (fn) -> loop(result = [], a = 2, b = 1, i = 0) -> if fn(\n      a,\n      i\n    ) then\n      recur(push(result, a), b, a + b, i + 1)\n    else\n      result\n    end,\n    pellTakeWhile: (fn) -> loop(result = [], a = 1, b = 2, i = 0) -> if fn(a, i) then\n      recur(push(result, a), b, 2 * b + a, i + 1)\n    else\n      result\n    end,\n    padovanTakeWhile: (fn) -> loop(result = [], a = 1, b = 1, c = 1, i = 0) -> if fn(\n      a,\n      i\n    ) then\n      recur(push(result, a), b, c, a + b, i + 1)\n    else\n      result\n    end,\n    factorialTakeWhile: (fn) -> loop(result = [], fact = 1, i = 0) -> if fn(\n      fact,\n      i\n    ) then\n      recur(push(result, fact), fact * (i + 1), i + 1)\n    else\n      result\n    end,\n    primeTakeWhile: (fn) -> loop(result = [], n = 2) -> if isPrime(n) then\n      if fn(n, count(result)) then recur(push(result, n), n + 1) else result end\n    else\n      recur(result, n + 1)\n    end,\n    compositeTakeWhile: (fn) -> loop(result = [], n = 4) -> if isComposite(n) then\n      if fn(n, count(result)) then recur(push(result, n), n + 1) else result end\n    else\n      recur(result, n + 1)\n    end,\n    abundantTakeWhile: (fn) -> loop(result = [], n = 12) -> if isAbundant(n) then\n      if fn(n, count(result)) then recur(push(result, n), n + 1) else result end\n    else\n      recur(result, n + 1)\n    end,\n    deficientTakeWhile: (fn) -> loop(result = [], n = 1) -> if isDeficient(n) then\n      if fn(n, count(result)) then recur(push(result, n), n + 1) else result end\n    else\n      recur(result, n + 1)\n    end,\n    perfectSquareTakeWhile: (fn) -> loop(result = [], i = 1) -> do\n      let val = i * i;\n      if fn(val, i - 1) then recur(push(result, val), i + 1) else result end;\n    end,\n    perfectCubeTakeWhile: (fn) -> loop(result = [], i = 1) -> do\n      let val = i * i * i;\n      if fn(val, i - 1) then recur(push(result, val), i + 1) else result end;\n    end,\n    happyTakeWhile: (fn) -> loop(result = [], n = 1) -> if isHappy(n) then\n      if fn(n, count(result)) then recur(push(result, n), n + 1) else result end\n    else\n      recur(result, n + 1)\n    end,\n    arithmeticTakeWhile: (start, step, fn) -> loop(result = [], i = 0) -> do\n      let val = start + i * step;\n      if fn(val, i) then recur(push(result, val), i + 1) else result end;\n    end,\n    geometricTakeWhile: (start, ratio, fn) -> loop(result = [], i = 0) -> do\n      let val = start * ratio ^ i;\n      if fn(val, i) then recur(push(result, val), i + 1) else result end;\n    end,\n    polygonalTakeWhile: (sides, fn) -> loop(result = [], n = 1) -> do\n      let val = (sides - 2) * n * (n - 1) / 2 + n;\n      if fn(val, n - 1) then recur(push(result, val), n + 1) else result end;\n    end,\n    lookAndSayTakeWhile: (fn) -> do\n      let nextTerm = (s) -> loop(result = \"\", i = 0) -> if i >= count(s) then\n        result\n      else\n        do\n          let ch = nth(s, i);\n          let cnt =\n            loop(c = 1, j = i + 1) -> if j < count(s) && nth(s, j) == ch then\n              recur(c + 1, j + 1)\n            else\n              c\n            end;\n          recur(++(result, str(cnt), ch), i + cnt);\n        end\n      end;\n      loop(result = [], term = \"1\", idx = 0) -> if fn(term, idx) then\n        recur(push(result, term), nextTerm(term), idx + 1)\n      else\n        result\n      end;\n    end,\n    bellTakeWhile: (fn) -> loop(result = [], i = 0) -> if i >=\n      count(bellNumbers) then\n      result\n    else if fn(nth(bellNumbers, i), i) then\n      recur(push(result, nth(bellNumbers, i)), i + 1)\n    else\n      result\n    end,\n    catalanTakeWhile: (fn) -> loop(result = [], i = 0) -> if i >=\n      count(catalanNumbers) then\n      result\n    else if fn(nth(catalanNumbers, i), i) then\n      recur(push(result, nth(catalanNumbers, i)), i + 1)\n    else\n      result\n    end,\n    mersenneTakeWhile: (fn) -> loop(result = [], i = 0) -> if i >=\n      count(mersenneNumbers) then\n      result\n    else if fn(nth(mersenneNumbers, i), i) then\n      recur(push(result, nth(mersenneNumbers, i)), i + 1)\n    else\n      result\n    end,\n    partitionTakeWhile: (fn) -> loop(result = [], i = 0) -> if i >=\n      count(partitionNumbers) then\n      result\n    else if fn(nth(partitionNumbers, i), i) then\n      recur(push(result, nth(partitionNumbers, i)), i + 1)\n    else\n      result\n    end,\n    perfectTakeWhile: (fn) -> loop(result = [], i = 0) -> if i >=\n      count(perfectNumbers) then\n      result\n    else if fn(nth(perfectNumbers, i), i) then\n      recur(push(result, nth(perfectNumbers, i)), i + 1)\n    else\n      result\n    end,\n    sylvesterTakeWhile: (fn) -> loop(result = [], i = 0) -> if i >=\n      count(sylvesterNumbers) then\n      result\n    else if fn(nth(sylvesterNumbers, i), i) then\n      recur(push(result, nth(sylvesterNumbers, i)), i + 1)\n    else\n      result\n    end,\n    bernoulliTakeWhile: (fn) -> loop(bArr = [1], n = 1) -> do\n      let bn =\n        if n > 1 && n % 2 == 1 then\n          0\n        else\n          do\n            let s =\n              loop(k = 0, acc = 0) -> if k >= n then\n                acc\n              else\n                recur(k + 1, acc + binom(n + 1, k) * nth(bArr, k))\n              end;\n            (-s) / (n + 1);\n          end\n        end;\n      if fn(bn, n) then recur(push(bArr, bn), n + 1) else bArr end;\n    end,\n    golombTakeWhile: (fn) -> if !(fn(1, 0)) then\n      []\n    else\n      do\n        loop(gArr = [1], n = 2) -> do\n          let gn1 = nth(gArr, n - 2);\n          let ggn1 = nth(gArr, gn1 - 1);\n          let gval = 1 + nth(gArr, n - 1 - ggn1);\n          if fn(gval, n - 1) then recur(push(gArr, gval), n + 1) else gArr end;\n        end;\n      end\n    end,\n    luckyTakeWhile: (fn) -> loop(result = [], i = 0) -> if i >=\n      count(luckyNumbers) then\n      result\n    else if fn(nth(luckyNumbers, i), i) then\n      recur(push(result, nth(luckyNumbers, i)), i + 1)\n    else\n      result\n    end,\n    perfectPowerTakeWhile: (fn) -> loop(result = [], n = 1) -> if isPerfectPower(\n      n\n    ) then\n      if fn(n, count(result)) then recur(push(result, n), n + 1) else result end\n    else\n      recur(result, n + 1)\n    end,\n    recamanTakeWhile: (fn) -> do\n      let inArr = (arr, val) -> loop(i = 0) -> if i >= count(arr) then\n        false\n      else if nth(arr, i) == val then\n        true\n      else\n        recur(i + 1)\n      end;\n      if !(fn(0, 0)) then\n        []\n      else\n        do\n          loop(result = [0], n = 1) -> do\n            let prev = nth(result, n - 1);\n            let backward = prev - n;\n            let nxt =\n              if backward > 0 && !(inArr(result, backward)) then\n                backward\n              else\n                prev + n\n              end;\n            if fn(nxt, n) then recur(push(result, nxt), n + 1) else result end;\n          end;\n        end\n      end;\n    end,\n    thueMorseTakeWhile: (fn) -> loop(result = [], i = 0) -> do\n      let val = countBits(i) % 2;\n      if fn(val, i) then recur(push(result, val), i + 1) else result end;\n    end,\n  };\nend;\n// Helper predicates\n// =========================================================================\n// FINITE SEQUENCES — generate inline\n// =========================================================================\n// =========================================================================\n// PREDICATE SEQUENCES — use inline predicates\n// =========================================================================\n// =========================================================================\n// PARAMETERIZED SEQUENCES\n// =========================================================================\n// =========================================================================\n// FINITE SEQUENCES — iterate precomputed arrays\n// =========================================================================\n// =========================================================================\n// DYNAMIC SEQUENCES — generate on-the-fly\n// =========================================================================\n// bArr accumulates all Bernoulli numbers computed so far; B(0)=1 is always included\n// gArr is 0-indexed: gArr[i] = g(i+1). g(1)=1, g(n)=1+g(n-g(g(n-1)))\n";
//#endregion
//#region src/builtin/modules/number-theory/binomialCefficient.ts
function binomialCoefficient(n, k) {
	if (k === 0 || k === n) return 1;
	let result = 1;
	for (let i = 0; i < k; i++) result *= (n - i) / (i + 1);
	return result;
}
//#endregion
//#region src/builtin/modules/number-theory/combinations.ts
/**
* Generates all possible combinations of a specified size from a collection.
* @param collection The input collection to generate combinations from
* @param size The size of each combination
* @returns An array of arrays, where each inner array is a combination of the specified size
*/
function combinations(collection, size) {
	if (size === 1) return collection.map((item) => [item]);
	const result = [];
	for (let i = 0; i <= collection.length - size; i++) {
		const current = collection[i];
		const subCombinations = combinations(collection.slice(i + 1), size - 1);
		for (const subComb of subCombinations) result.push([current, ...subComb]);
	}
	return result;
}
const combinationsNormalExpressions = {
	combinations: {
		evaluate: ([set, n], sourceCodeInfo) => {
			assertArray(set, sourceCodeInfo);
			assertNumber(n, sourceCodeInfo, {
				integer: true,
				nonNegative: true,
				lte: set.size
			});
			if (n === 0) return PersistentVector.from([PersistentVector.empty()]);
			return PersistentVector.from(combinations([...set], n).map((c) => PersistentVector.from(c)));
		},
		arity: toFixedArity(2)
	},
	countCombinations: {
		evaluate: ([n, k], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo, {
				integer: true,
				nonNegative: true
			});
			assertNumber(k, sourceCodeInfo, {
				integer: true,
				nonNegative: true,
				lte: n
			});
			return binomialCoefficient(n, k);
		},
		arity: toFixedArity(2)
	}
};
//#endregion
//#region src/builtin/modules/number-theory/derangements.ts
function getAllDerangements(arr) {
	const n = arr.size;
	const result = [];
	const used = Array.from({ length: n }, () => false);
	const temp = new Array(n);
	function generateDerangements(pos) {
		if (pos === n) {
			result.push(PersistentVector.from(temp));
			return;
		}
		for (let i = 0; i < n; i++) {
			if (used[i] || i === pos) continue;
			used[i] = true;
			temp[pos] = arr.get(i);
			generateDerangements(pos + 1);
			used[i] = false;
		}
	}
	generateDerangements(0);
	return result;
}
function countDerangements(n) {
	if (n === 1) return 0;
	let a = 1;
	let b = 0;
	let result = 0;
	for (let i = 2; i <= n; i++) {
		result = (i - 1) * (a + b);
		a = b;
		b = result;
	}
	return result;
}
const derangementsNormalExpressions = {
	derangements: {
		evaluate: ([set], sourceCodeInfo) => {
			assertArray(set, sourceCodeInfo);
			return PersistentVector.from(getAllDerangements(set));
		},
		arity: toFixedArity(1)
	},
	countDerangements: {
		evaluate: ([n], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo, {
				finite: true,
				integer: true,
				positive: true
			});
			return countDerangements(n);
		},
		arity: toFixedArity(1)
	}
};
//#endregion
//#region src/builtin/modules/number-theory/divisors.ts
function calcUnsortedDivisors(number) {
	const divisors = [];
	for (let i = 1; i <= Math.sqrt(number); i++) if (number % i === 0) {
		divisors.push(i);
		if (i !== number / i) divisors.push(number / i);
	}
	return divisors;
}
function getDivisors(n) {
	return calcUnsortedDivisors(n).sort((a, b) => a - b);
}
function getProperDivisors(n) {
	return getDivisors(n).slice(0, -1);
}
const divisorsNormalExpressions = {
	divisors: {
		evaluate: ([number], sourceCodeInfo) => {
			assertNumber(number, sourceCodeInfo, {
				finite: true,
				integer: true,
				positive: true
			});
			return getDivisors(number);
		},
		arity: toFixedArity(1)
	},
	countDivisors: {
		evaluate: ([number], sourceCodeInfo) => {
			assertNumber(number, sourceCodeInfo, {
				finite: true,
				integer: true,
				positive: true
			});
			return calcUnsortedDivisors(number).length;
		},
		arity: toFixedArity(1)
	},
	properDivisors: {
		evaluate: ([number], sourceCodeInfo) => {
			assertNumber(number, sourceCodeInfo, {
				finite: true,
				integer: true,
				positive: true
			});
			return getProperDivisors(number);
		},
		arity: toFixedArity(1)
	},
	countProperDivisors: {
		evaluate: ([number], sourceCodeInfo) => {
			assertNumber(number, sourceCodeInfo, {
				finite: true,
				integer: true,
				positive: true
			});
			return calcUnsortedDivisors(number).length - 1;
		},
		arity: toFixedArity(1)
	}
};
//#endregion
//#region src/builtin/modules/number-theory/sequences/factorial.ts
const factorialNumbers = [
	1,
	1,
	2,
	6,
	24,
	120,
	720,
	5040,
	40320,
	362880,
	3628800,
	39916800,
	479001600,
	6227020800,
	87178291200,
	1307674368e3,
	20922789888e3,
	355687428096e3,
	6402373705728e3
];
//#endregion
//#region src/builtin/modules/number-theory/factorial.ts
function factorialOf(n) {
	if (n < 0) throw new Error("Factorial is not defined for negative numbers");
	if (n === 0 || n === 1) return 1;
	if (n <= 18) return factorialNumbers[n];
	let result = factorialNumbers[18];
	for (let i = 19; i <= n; i++) result *= i;
	return result;
}
const factorialNormalExpressions = { factorial: {
	evaluate: ([n], sourceCodeInfo) => {
		assertNumber(n, sourceCodeInfo, {
			integer: true,
			nonNegative: true,
			lte: 170
		});
		return factorialOf(n);
	},
	arity: toFixedArity(1)
} };
//#endregion
//#region src/builtin/modules/number-theory/sequences/partition.ts
const partitionNumbers = [
	1,
	2,
	3,
	5,
	7,
	11,
	15,
	22,
	30,
	42,
	56,
	77,
	101,
	135,
	176,
	231,
	297,
	385,
	490,
	627,
	792,
	1002,
	1255,
	1575,
	1958,
	2436,
	3010,
	3718,
	4565,
	5604,
	6842,
	8349,
	10143,
	12310,
	14883,
	17977,
	21637,
	26015,
	31185,
	37338,
	44583,
	53174,
	63261,
	75175,
	89134,
	105558,
	124754,
	147273,
	173525,
	204226,
	239943,
	281589,
	329931,
	386155,
	451276,
	526823,
	614154,
	715220,
	831820,
	966467,
	1121505,
	1300156,
	1505499,
	1741630,
	2012558,
	2323520,
	2679689,
	3087735,
	3554345,
	4087968,
	4697205,
	5392783,
	6185689,
	7089500,
	8118264,
	9289091,
	10619863,
	12132164,
	13848650,
	15796476,
	18004327,
	20506255,
	23338469,
	26543660,
	30167357,
	34262962,
	38887673,
	44108109,
	49995925,
	56634173,
	64112359,
	72533807,
	82010177,
	92669720,
	104651419,
	118114304,
	133230930,
	150198136,
	169229875,
	190569292,
	214481126,
	241265379,
	271248950,
	304801365,
	342325709,
	384276336,
	431149389,
	483502844,
	541946240,
	607163746,
	679903203,
	761002156,
	851376628,
	952050665,
	1064144451,
	1188908248,
	1327710076,
	1482074143,
	1653668665,
	1844349560,
	2056148051,
	2291320912,
	2552338241,
	2841940500,
	3163127352,
	3519222692,
	3913864295,
	4351078600,
	4835271870,
	5371315400,
	5964539504,
	6620830889,
	7346629512,
	8149040695,
	9035836076,
	10015581680,
	11097645016,
	12292341831,
	13610949895,
	15065878135,
	16670689208,
	18440293320,
	20390982757,
	22540654445,
	24908858009,
	27517052599,
	30388671978,
	33549419497,
	37027355200,
	40853235313,
	45060624582,
	49686288421,
	54770336324,
	60356673280,
	66493182097,
	73232243759,
	80630964769,
	88751778802,
	97662728555,
	107438159466,
	118159068427,
	129913904637,
	142798995930,
	156919475295,
	172389800255,
	189334822579,
	207890420102,
	228204732751,
	250438925115,
	274768617130,
	301384802048,
	330495499613,
	362326859895,
	397125074750,
	435157697830,
	476715857290,
	522115831195,
	571701605655,
	625846753120,
	684957390936,
	749474411781,
	819876908323,
	896684817527,
	980462880430,
	0xf98dace281,
	1171432692373,
	1280011042268,
	1398341745571,
	1527273599625,
	1667727404093,
	1820701100652,
	1987276856363,
	2168627105469,
	2366022741845,
	2580840212973,
	2814570987591,
	3068829878530,
	3345365983698,
	3646072432125,
	3972999029388,
	4328363658647,
	4714566886083,
	5134205287973,
	5590088317495,
	6085253859260,
	6622987708040,
	7206841706490,
	7840656226137,
	8528581302375,
	9275102575355,
	0x92c1cc50847,
	0x9f8afdf968b,
	0xad6918d4f36,
	0xbc72dde1287,
	0xccc0de0d1d7,
	0xde6da02b061,
	0xf195c5ba10a,
	18028182516671,
	19573856161145,
	21248279009367,
	23061871173849,
	25025873760111,
	27152408925615,
	29454549941750,
	31946390696157,
	34643126322519,
	37561133582570,
	40718063627362,
	44132934884255,
	47826239745920,
	51820051838712,
	56138148670947,
	60806135438329,
	65851585970275,
	71304185514919,
	77195892663512,
	83561103925871,
	90436839668817,
	97862933703585,
	0x604ca1b434ad,
	0x682ca0bc6d4e,
	0x70ad06a4328b,
	0x79da3e8e4df8,
	0x83c1a359c63d,
	0x8e7190d8114a,
	0x99f976fff202,
	0xa669edb53a4a,
	0xb3d4caca1bc8,
	0xc24d38b78151,
	0xd1e7cfd30909,
	0xe2bab064772f,
	0xf4dd9f8523e3,
	290726957916112,
	313891991306665,
	338854264248680,
	365749566870782,
	394723676655357,
	425933084409356,
	459545750448675,
	495741934760846,
	534715062908609,
	576672674947168,
	621837416509615,
	670448123060170,
	722760953690372,
	779050629562167,
	839611730366814,
	904760108316360,
	974834369944625,
	0x3bb262854d36d,
	0x404dafd2efa2e,
	0x4541ae2536f6d,
	0x4a94ddd83ba57,
	0x504e363fd1eca,
	0x56752ddfc97e7,
	0x5d11c356f6b53,
	0x642c86bff69f5,
	0x6bcea3db036a0,
	0x7401ecbfdbd63,
	0x7cd0e571c49d4,
	0x8646d010a1b6f,
	0x906fba0855202,
	0x9b5889f52508f,
	0xa70f0ea631153,
	0xb3a20ef033ec5,
	0xc1215ac210755,
	0xcf9ddd28cfa8d,
	0xdf29afbd9f60e,
	0xefd82f26a7157,
	4534253126900886,
	4872038056472084,
	5234371069753672,
	5622992691950605,
	6039763882095515,
	6486674127079088,
	6965850144195831,
	7479565078510584,
	8030248384943040,
	8620496275465025
];
//#endregion
//#region src/builtin/modules/number-theory/partitions.ts
function partitions(n) {
	if (n <= 0) return [[]];
	if (n === 1) return [[1]];
	const result = [];
	function generatePartitions(remaining, max, current) {
		if (remaining === 0) {
			result.push([...current]);
			return;
		}
		for (let i = Math.min(max, remaining); i >= 1; i--) {
			current.push(i);
			generatePartitions(remaining - i, i, current);
			current.pop();
		}
	}
	generatePartitions(n, n, []);
	return result;
}
const partitionsNormalExpressions = {
	partitions: {
		evaluate: ([n], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo, {
				integer: true,
				nonNegative: true
			});
			return partitions(n);
		},
		arity: toFixedArity(1)
	},
	countPartitions: {
		evaluate: ([n], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo, {
				integer: true,
				nonNegative: true
			});
			if (n === 0) return 1;
			if (n > partitionNumbers.length) throw new RuntimeError(`n is too large. The maximum value is ${partitionNumbers.length - 1}.`, sourceCodeInfo);
			return partitionNumbers[n - 1];
		},
		arity: toFixedArity(1)
	}
};
//#endregion
//#region src/builtin/modules/number-theory/permutations.ts
/**
* Generates all possible permutations of a collection.
* @param collection The input collection to generate permutations from
* @returns An array of arrays, where each inner array is a permutation of the input collection
*/
function permutations(collection) {
	if (collection.length === 0) return [[]];
	const result = [];
	for (let i = 0; i < collection.length; i++) {
		const current = collection[i];
		const subPermutations = permutations([...collection.slice(0, i), ...collection.slice(i + 1)]);
		for (const subPerm of subPermutations) result.push([current, ...subPerm]);
	}
	return result;
}
const permutationsNormalExpressions = {
	permutations: {
		evaluate: ([set], sourceCodeInfo) => {
			assertArray(set, sourceCodeInfo);
			return PersistentVector.from(permutations([...set]).map((p) => PersistentVector.from(p)));
		},
		arity: toFixedArity(1)
	},
	countPermutations: {
		evaluate: ([n, k], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo, {
				integer: true,
				nonNegative: true
			});
			assertNumber(k, sourceCodeInfo, {
				integer: true,
				nonNegative: true,
				lte: n
			});
			return factorialOf(n) / factorialOf(n - k);
		},
		arity: toFixedArity(2)
	}
};
//#endregion
//#region src/builtin/modules/number-theory/powerSet.ts
function powerSet(set) {
	const result = [[]];
	for (const value of set) {
		const newSubsets = result.map((subset) => [...subset, value]);
		result.push(...newSubsets);
	}
	return result.map((subset) => PersistentVector.from(subset));
}
const powerSetNormalExpressions = {
	powerSet: {
		evaluate: ([set], sourceCodeInfo) => {
			assertArray(set, sourceCodeInfo);
			return PersistentVector.from(powerSet(set));
		},
		arity: toFixedArity(1)
	},
	countPowerSet: {
		evaluate: ([n], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo, {
				integer: true,
				nonNegative: true
			});
			if (n > 52) throw new ArithmeticError(`countPowerSet(${n}) exceeds safe integer range`, sourceCodeInfo);
			return 2 ** n;
		},
		arity: toFixedArity(1)
	}
};
//#endregion
//#region src/builtin/modules/number-theory/primeFactors.ts
/**
* Returns the prime factorization of a number as an array of its prime factors.
* For example, factors(12) returns [2, 2, 3].
* Special case: factors(1) returns an empty array [].
*
* @param n - A positive integer to factorize
* @returns An array of prime factors in ascending order
*/
function primeFactors(n) {
	if (n === 1) return [];
	const factors = [];
	let divisor = 2;
	while (n > 1) if (n % divisor === 0) {
		factors.push(divisor);
		n /= divisor;
	} else divisor++;
	return factors;
}
const primeFactorsNormalExpressions = {
	primeFactors: {
		evaluate: ([number], sourceCodeInfo) => {
			assertNumber(number, sourceCodeInfo, {
				finite: true,
				integer: true,
				positive: true
			});
			return primeFactors(number);
		},
		arity: toFixedArity(1)
	},
	distinctPrimeFactors: {
		evaluate: ([n], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo, {
				finite: true,
				integer: true,
				positive: true
			});
			const factors = primeFactors(n);
			const distinctFactors = new Set(factors);
			return Array.from(distinctFactors);
		},
		arity: toFixedArity(1)
	},
	countPrimeFactors: {
		evaluate: ([n], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo, {
				finite: true,
				integer: true,
				positive: true
			});
			return primeFactors(n).length;
		},
		arity: toFixedArity(1)
	},
	countDistinctPrimeFactors: {
		evaluate: ([n], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo, {
				finite: true,
				integer: true,
				positive: true
			});
			const factors = primeFactors(n);
			return new Set(factors).size;
		},
		arity: toFixedArity(1)
	}
};
//#endregion
//#region src/builtin/modules/number-theory/sequences/abundant.ts
function isAbundant(num) {
	return getProperDivisors(num).reduce((acc, curr) => acc + curr, 0) > num;
}
const abundantSequence = {
	abundantSeq: (length) => {
		const abundants = [];
		let num = 2;
		while (abundants.length < length) {
			if (isAbundant(num)) abundants.push(num);
			num += 1;
		}
		return abundants;
	},
	isAbundant: (n) => isAbundant(n)
};
//#endregion
//#region src/builtin/modules/number-theory/sequences/arithmetic.ts
/**
* Checks if a number is a member of an arithmetic sequence.
* @param start The first term of the sequence
* @param step The common difference between terms
* @param n The number to check
* @returns true if the number is in the sequence, false otherwise
*/
function isInArithmeticSequence(start, step, n) {
	if (step === 0) return approxEqual(n, start);
	const position = (n - start) / step;
	if (position < 0) return false;
	const roundedPosition = Math.round(position);
	const calculatedValue = start + step * roundedPosition;
	return approxEqual(position, roundedPosition) && approxEqual(calculatedValue, n);
}
const arithmeticNormalExpressions = {
	arithmeticSeq: {
		evaluate: ([start, step, length], sourceCodeInfo) => {
			assertNumber(start, sourceCodeInfo, { finite: true });
			assertNumber(step, sourceCodeInfo, { finite: true });
			assertNumber(length, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			return Array.from({ length }, (_, i) => start + i * step);
		},
		arity: toFixedArity(3)
	},
	arithmeticTakeWhile: {
		/* v8 ignore next 1 */
		evaluate: () => {
			throw new Error("unreachable: overridden by dvalaImpl");
		},
		arity: toFixedArity(3)
	},
	arithmeticNth: {
		evaluate: ([start, step, n], sourceCodeInfo) => {
			assertNumber(start, sourceCodeInfo, { finite: true });
			assertNumber(step, sourceCodeInfo, { finite: true });
			assertNumber(n, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			return start + (n - 1) * step;
		},
		arity: toFixedArity(3)
	},
	isArithmetic: {
		evaluate: ([start, step, n], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo);
			assertNumber(start, sourceCodeInfo, { finite: true });
			assertNumber(step, sourceCodeInfo, { finite: true });
			return isInArithmeticSequence(start, step, n);
		},
		arity: toFixedArity(3)
	}
};
//#endregion
//#region src/builtin/modules/number-theory/sequences/bell.ts
const bellNumbers = [
	1,
	2,
	5,
	15,
	52,
	203,
	877,
	4140,
	21147,
	115975,
	678570,
	4213597,
	27644437,
	190899322,
	1382958545,
	10480142147,
	82864869804,
	682076806159,
	5832742205057,
	51724158235372,
	474869816156751,
	4506715738447323
];
//#endregion
//#region src/builtin/modules/number-theory/sequences/bernoulli.ts
function getBernoulliSeq(length) {
	const bernoulli = [1];
	for (let n = 1; n < length; n += 1) {
		let sum = 0;
		for (let k = 0; k < n; k += 1) sum += binomialCoefficient(n + 1, k) * bernoulli[k];
		bernoulli[n] = n > 1 && n % 2 === 1 ? 0 : -sum / (n + 1);
	}
	return bernoulli;
}
const bernoulliNormalExpressions = {
	bernoulliSeq: {
		evaluate: ([length], sourceCodeInfo) => {
			assertNumber(length, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			return getBernoulliSeq(length);
		},
		arity: toFixedArity(1)
	},
	bernoulliNth: {
		evaluate: ([n], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			return getBernoulliSeq(n)[n - 1];
		},
		arity: toFixedArity(1)
	},
	bernoulliTakeWhile: {
		/* v8 ignore next 1 */
		evaluate: () => {
			throw new Error("unreachable: overridden by dvalaImpl");
		},
		arity: toFixedArity(1)
	}
};
//#endregion
//#region src/builtin/modules/number-theory/sequences/catalan.ts
const catalanNumbers = [
	1,
	2,
	5,
	14,
	42,
	132,
	429,
	1430,
	4862,
	16796,
	58786,
	208012,
	742900,
	2674440,
	9694845,
	35357670,
	129644790,
	477638700,
	1767263190,
	6564120420,
	24466267020,
	91482563640,
	343059613650,
	1289904147324,
	4861946401452,
	18367353072152,
	69533550916004,
	0xefe09af96cd8,
	0x38f88b34d6a68,
	0xd8db5c347b610
];
//#endregion
//#region src/builtin/modules/number-theory/sequences/collatz.ts
const collatzSequence = {
	collatzSeq: (start) => {
		let x = start;
		const collatz = [x];
		while (x !== 1) {
			if (x % 2 === 0) x /= 2;
			else x = 3 * x + 1;
			collatz.push(x);
		}
		return collatz;
	},
	noNth: true,
	noTakeWhile: true
};
//#endregion
//#region src/builtin/modules/number-theory/sequences/prime.ts
function isPrime(num) {
	if (num <= 1) return false;
	if (num <= 3) return true;
	if (num % 2 === 0 || num % 3 === 0) return false;
	for (let i = 5; i * i <= num; i += 6) if (num % i === 0 || num % (i + 2) === 0) return false;
	return true;
}
const primeSequence = {
	primeSeq: (length) => {
		const primes = [];
		let num = 2;
		while (primes.length < length) {
			if (isPrime(num)) primes.push(num);
			num += 1;
		}
		return primes;
	},
	isPrime: (n) => isPrime(n)
};
//#endregion
//#region src/builtin/modules/number-theory/sequences/composite.ts
function isComposite(num) {
	if (num <= 1) return false;
	return !isPrime(num);
}
const compositeSequence = {
	compositeSeq: (length) => {
		const composites = [];
		let num = 2;
		while (composites.length < length) {
			if (isComposite(num)) composites.push(num);
			num += 1;
		}
		return composites;
	},
	isComposite: (n) => isComposite(n)
};
//#endregion
//#region src/builtin/modules/number-theory/sequences/deficient.ts
function isDeficient(num) {
	return getProperDivisors(num).reduce((acc, curr) => acc + curr, 0) < num;
}
const deficientSequence = {
	deficientSeq: (length) => {
		const deficients = [];
		let num = 1;
		while (deficients.length < length) {
			if (isDeficient(num)) deficients.push(num);
			num += 1;
		}
		return deficients;
	},
	isDeficient: (n) => isDeficient(n)
};
//#endregion
//#region src/builtin/modules/number-theory/sequences/fibonacci.ts
const fibonacciNumbers = [
	0,
	1,
	1,
	2,
	3,
	5,
	8,
	13,
	21,
	34,
	55,
	89,
	144,
	233,
	377,
	610,
	987,
	1597,
	2584,
	4181,
	6765,
	10946,
	17711,
	28657,
	46368,
	75025,
	121393,
	196418,
	317811,
	514229,
	832040,
	1346269,
	2178309,
	3524578,
	5702887,
	9227465,
	14930352,
	24157817,
	39088169,
	63245986,
	102334155,
	165580141,
	267914296,
	433494437,
	701408733,
	1134903170,
	1836311903,
	2971215073,
	4807526976,
	7778742049,
	12586269025,
	20365011074,
	32951280099,
	53316291173,
	86267571272,
	139583862445,
	225851433717,
	365435296162,
	591286729879,
	956722026041,
	1548008755920,
	2504730781961,
	4052739537881,
	6557470319842,
	0x9a661ca20bb,
	0xf9d297a859d,
	27777890035288,
	44945570212853,
	72723460248141,
	0x6b04f4c2fe42,
	0xad2934c6d08f,
	308061521170129,
	498454011879264,
	806515533049393,
	0x4a2dce62b0d91,
	0x780626e057bc2,
	0xc233f54308953,
	5527939700884757,
	8944394323791464
];
//#endregion
//#region src/builtin/modules/number-theory/sequences/geometric.ts
/**
* Checks if a number is a member of a geometric sequence.
* @param initialTerm The first term of the sequence (a)
* @param ratio The common ratio of the sequence (r)
* @param number The number to check
* @returns true if the number is in the sequence, false otherwise
*/
function isInGeometricSequence(initialTerm, ratio, number) {
	if (approxEqual(initialTerm, 0)) return approxEqual(number, 0);
	if (approxEqual(ratio, 1)) return approxEqual(number, initialTerm);
	if (approxEqual(ratio, 0)) return approxEqual(number, 0) || approxEqual(number, initialTerm);
	if (approxEqual(number, initialTerm)) return true;
	if (approxEqual(ratio, -1)) return approxEqual(number, initialTerm) || approxEqual(number, -initialTerm);
	if (ratio < 0) {
		const logResult = Math.log(Math.abs(number / initialTerm)) / Math.log(Math.abs(ratio));
		const roundedLogResult = Math.round(logResult);
		if (!approxEqual(roundedLogResult, logResult) || roundedLogResult < 0) return false;
		const expectedSign = roundedLogResult % 2 === 0 ? Math.sign(initialTerm) : Math.sign(initialTerm) * Math.sign(ratio);
		return Math.sign(number) === expectedSign;
	}
	if (ratio > 1 && number < initialTerm || ratio < 1 && number > initialTerm) return false;
	const logResult = Math.log(number / initialTerm) / Math.log(ratio);
	const roundedLogResult = Math.round(logResult);
	if (!approxEqual(roundedLogResult, logResult) || roundedLogResult < 0) return false;
	return approxEqual(initialTerm * ratio ** roundedLogResult, number);
}
const geometricNormalExpressions = {
	geometricSeq: {
		evaluate: ([start, ratio, length], sourceCodeInfo) => {
			assertNumber(start, sourceCodeInfo, { finite: true });
			assertNumber(ratio, sourceCodeInfo, { finite: true });
			assertNumber(length, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			return Array.from({ length }, (_, i) => start * ratio ** i);
		},
		arity: toFixedArity(3)
	},
	geometricTakeWhile: {
		/* v8 ignore next 1 */
		evaluate: () => {
			throw new Error("unreachable: overridden by dvalaImpl");
		},
		arity: toFixedArity(3)
	},
	geometricNth: {
		evaluate: ([start, ratio, n], sourceCodeInfo) => {
			assertNumber(start, sourceCodeInfo, { finite: true });
			assertNumber(ratio, sourceCodeInfo, { finite: true });
			assertNumber(n, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			return start * ratio ** (n - 1);
		},
		arity: toFixedArity(3)
	},
	isGeometric: {
		evaluate: ([start, ratio, n], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo);
			assertNumber(start, sourceCodeInfo, { finite: true });
			assertNumber(ratio, sourceCodeInfo, { finite: true });
			return isInGeometricSequence(start, ratio, n);
		},
		arity: toFixedArity(3)
	}
};
//#endregion
//#region src/builtin/modules/number-theory/sequences/golomb.ts
function getGolombSeq(n) {
	const golomb = [0, 1];
	for (let i = 2; i <= n; i += 1) golomb.push(1 + golomb[i - golomb[golomb[i - 1]]]);
	return golomb.slice(1);
}
const golombSequence = {
	golombSeq: (length) => getGolombSeq(length),
	isGolomb: () => true
};
//#endregion
//#region src/builtin/modules/number-theory/sequences/happy.ts
function isHappyNumber(n) {
	if (n <= 0) return false;
	const seen = /* @__PURE__ */ new Set();
	while (n !== 1 && !seen.has(n)) {
		seen.add(n);
		n = getSumOfSquaredDigits(n);
	}
	return n === 1;
}
function getSumOfSquaredDigits(n) {
	let sum = 0;
	while (n > 0) {
		const digit = n % 10;
		sum += digit * digit;
		n = Math.floor(n / 10);
	}
	return sum;
}
const happySequence = {
	happySeq: (length) => {
		const happyNumbers = [];
		for (let i = 1; happyNumbers.length < length; i++) {
			let n = i;
			const seen = /* @__PURE__ */ new Set();
			while (n !== 1 && !seen.has(n)) {
				seen.add(n);
				n = String(n).split("").reduce((sum, digit) => sum + Number(digit) ** 2, 0);
			}
			if (n === 1) happyNumbers.push(i);
		}
		return happyNumbers;
	},
	isHappy: (n) => isHappyNumber(n)
};
//#endregion
//#region src/builtin/modules/number-theory/sequences/juggler.ts
const jugglerSequence = {
	jugglerSeq: (start) => {
		let next = start;
		const juggler = [next];
		while (next > 1) {
			next = next % 2 === 0 ? Math.floor(Math.sqrt(next)) : Math.floor(next ** (3 / 2));
			juggler.push(next);
		}
		return juggler;
	},
	noNth: true,
	noTakeWhile: true
};
//#endregion
//#region src/builtin/modules/number-theory/sequences/lookAndSay.ts
/**
* Checks if a number is part of the Look-and-Say sequence.
*
* The Look-and-Say sequence starts with "1" and each subsequent term describes
* the previous term by counting consecutive digits. For example:
* 1, 11, 21, 1211, 111221, 312211, 13112221, ...
*
* @param {string|number} target - The number to check (can be a string or number)
* @returns {boolean} - Whether the target is in the sequence
*/
function isLookAndSay(target) {
	let current = "1";
	if (current === target) return true;
	while (true) {
		current = getNextLookAndSayTerm(current);
		if (current === target) return true;
		if (current.length > target.length) return false;
	}
}
/**
* Generates the next term in the Look-and-Say sequence
*
* @param {string} term - The current term
* @returns {string} - The next term in the sequence
*/
function getNextLookAndSayTerm(term) {
	let result = "";
	let count = 1;
	for (let i = 0; i < term.length; i++) if (i + 1 < term.length && term[i] === term[i + 1]) count++;
	else {
		result += count.toString() + term[i];
		count = 1;
	}
	return result;
}
const lookAndSaySequence = {
	string: true,
	lookAndSaySeq: (length) => {
		const lookAndSay = ["1"];
		for (let i = 1; i < length; i += 1) lookAndSay[i] = lookAndSay[i - 1].replace(/(\d)\1*/g, (match) => `${match.length}${match[0]}`);
		return lookAndSay;
	},
	isLookAndSay: (n) => isLookAndSay(n)
};
//#endregion
//#region src/builtin/modules/number-theory/sequences/lucas.ts
const lucasNumbers = [
	2,
	1,
	3,
	4,
	7,
	11,
	18,
	29,
	47,
	76,
	123,
	199,
	322,
	521,
	843,
	1364,
	2207,
	3571,
	5778,
	9349,
	15127,
	24476,
	39603,
	64079,
	103682,
	167761,
	271443,
	439204,
	710647,
	1149851,
	1860498,
	3010349,
	4870847,
	7881196,
	12752043,
	20633239,
	33385282,
	54018521,
	87403803,
	141422324,
	228826127,
	370248451,
	599074578,
	969323029,
	1568397607,
	2537720636,
	4106118243,
	6643838879,
	10749957122,
	17393796001,
	28143753123,
	45537549124,
	73681302247,
	119218851371,
	192900153618,
	312119004989,
	505019158607,
	817138163596,
	1322157322203,
	2139295485799,
	3461452808002,
	5600748293801,
	9062201101803,
	0xd55fbe3dc94,
	23725150497407,
	38388099893011,
	62113250390418,
	0x5b67cb4878a5,
	0x93e5a9822a37,
	0xef4d74caa2dc,
	425730551631123,
	688846502588399,
	0x3f5b3b1643d02,
	0x66834447bacf1,
	0xa5de7f5dfe9f3,
	4721424167835364,
	7639424778862807
];
//#endregion
//#region src/maybePromise.ts
/**
* Chain a value that might be a Promise. If the value is sync, calls fn synchronously.
* If it's a Promise, chains with .then().
*/
function chain(value, fn) {
	if (value instanceof Promise) return value.then(fn);
	return fn(value);
}
/**
* Like Array.map but handles MaybePromise callbacks sequentially.
* In the sync case, runs as a simple loop. Switches to async only when needed.
*/
function mapSequential(arr, fn) {
	const results = [];
	for (let i = 0; i < arr.length; i++) {
		const result = fn(arr[i], i);
		if (result instanceof Promise) return chainRemainingMap(result, results, arr, fn, i);
		results.push(result);
	}
	return results;
}
async function chainRemainingMap(currentPromise, results, arr, fn, startIndex) {
	results.push(await currentPromise);
	for (let i = startIndex + 1; i < arr.length; i++) results.push(await fn(arr[i], i));
	return results;
}
/**
* Like Array.reduce but handles MaybePromise callbacks sequentially.
* In the sync case, runs as a simple loop. Switches to async only when needed.
*/
function reduceSequential(arr, fn, initial) {
	let result = initial;
	for (let i = 0; i < arr.length; i++) {
		const next = fn(result, arr[i], i);
		if (next instanceof Promise) return chainRemainingReduce(next, arr, fn, i);
		result = next;
	}
	return result;
}
async function chainRemainingReduce(currentPromise, arr, fn, startIndex) {
	let result = await currentPromise;
	for (let i = startIndex + 1; i < arr.length; i++) result = await fn(result, arr[i], i);
	return result;
}
/**
* Like Array.forEach but handles MaybePromise callbacks sequentially.
* In the sync case, runs as a simple loop. Switches to async only when needed.
*/
function forEachSequential(arr, fn) {
	for (let i = 0; i < arr.length; i++) {
		const result = fn(arr[i], i);
		if (result instanceof Promise) return chainRemainingForEach(result, arr, fn, i);
	}
}
async function chainRemainingForEach(currentPromise, arr, fn, startIndex) {
	await currentPromise;
	for (let i = startIndex + 1; i < arr.length; i++) await fn(arr[i], i);
}
/**
* Try/catch that handles MaybePromise values correctly.
* If tryFn returns a Promise, catches both sync throws and Promise rejections.
*/
function tryCatch(tryFn, catchFn) {
	try {
		const result = tryFn();
		if (result instanceof Promise) return result.catch(catchFn);
		return result;
	} catch (error) {
		return catchFn(error);
	}
}
/**
* Like Array.some but handles MaybePromise callbacks sequentially.
* Returns the first truthy MaybePromise result, or false.
*/
function someSequential(arr, fn) {
	for (let i = 0; i < arr.length; i++) {
		const result = fn(arr[i], i);
		if (result instanceof Promise) return chainRemainingSome(result, arr, fn, i);
		if (result) return true;
	}
	return false;
}
async function chainRemainingSome(currentPromise, arr, fn, startIndex) {
	if (await currentPromise) return true;
	for (let i = startIndex + 1; i < arr.length; i++) if (await fn(arr[i], i)) return true;
	return false;
}
/**
* Like Array.every but handles MaybePromise callbacks sequentially.
* Returns false as soon as a falsy result is found.
*/
function everySequential(arr, fn) {
	for (let i = 0; i < arr.length; i++) {
		const result = fn(arr[i], i);
		if (result instanceof Promise) return chainRemainingEvery(result, arr, fn, i);
		if (!result) return false;
	}
	return true;
}
async function chainRemainingEvery(currentPromise, arr, fn, startIndex) {
	if (!await currentPromise) return false;
	for (let i = startIndex + 1; i < arr.length; i++) if (!await fn(arr[i], i)) return false;
	return true;
}
/**
* Like Array.filter but handles MaybePromise callbacks sequentially.
* Returns the filtered array.
*/
function filterSequential(arr, fn) {
	const results = [];
	for (let i = 0; i < arr.length; i++) {
		const result = fn(arr[i], i);
		if (result instanceof Promise) return chainRemainingFilter(result, results, arr, fn, i);
		if (result) results.push(arr[i]);
	}
	return results;
}
async function chainRemainingFilter(currentPromise, results, arr, fn, startIndex) {
	if (await currentPromise) results.push(arr[startIndex]);
	for (let i = startIndex + 1; i < arr.length; i++) if (await fn(arr[i], i)) results.push(arr[i]);
	return results;
}
/**
* Like Array.findIndex but handles MaybePromise callbacks sequentially.
* Returns -1 if no element matches.
*/
function findIndexSequential(arr, fn) {
	for (let i = 0; i < arr.length; i++) {
		const result = fn(arr[i], i);
		if (result instanceof Promise) return chainRemainingFindIndex(result, arr, fn, i);
		if (result) return i;
	}
	return -1;
}
async function chainRemainingFindIndex(currentPromise, arr, fn, startIndex) {
	if (await currentPromise) return startIndex;
	for (let i = startIndex + 1; i < arr.length; i++) if (await fn(arr[i], i)) return i;
	return -1;
}
//#endregion
//#region src/builtin/modules/number-theory/sequences/lucky.ts
/**
* Generates lucky numbers while the predicate function returns true.
*
* @param predicate - Function that tests if we should continue generating numbers.
*                    Takes the current lucky number and index as parameters.
* @returns An array of lucky numbers
*/
function generateLuckyNumbers(predicate) {
	const numbers = [];
	for (let i = 1; i <= 2e3; i++) numbers.push(i);
	let filteredNumbers = [1];
	for (let i = 1; i < numbers.length; i++) if (numbers[i] % 2 !== 0) filteredNumbers.push(numbers[i]);
	const luckyNumbers = [1];
	let count = 1;
	return chain(predicate(1, 0), (keepFirst) => {
		if (!keepFirst) return [];
		let index = 1;
		function loop() {
			const luckyNumber = filteredNumbers[index];
			return chain(predicate(luckyNumber, count), (keep) => {
				if (!keep) return luckyNumbers;
				luckyNumbers.push(luckyNumber);
				count++;
				const step = luckyNumber;
				const newFiltered = [];
				for (let i = 0; i < filteredNumbers.length; i++) if ((i + 1) % step !== 0) newFiltered.push(filteredNumbers[i]);
				filteredNumbers = newFiltered;
				index++;
				if (index >= filteredNumbers.length - 5) {
					let next = filteredNumbers[filteredNumbers.length - 1] + 2;
					while (filteredNumbers.length < index + 1e3) {
						filteredNumbers.push(next);
						next += 2;
					}
				}
				return loop();
			});
		}
		return loop();
	});
}
/**
* Generates lucky numbers up to a specified length or count
*
* Lucky numbers are a subset of integers defined by a specific sieving process:
* 1. Start with all positive integers: 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, ...
* 2. Keep 1, delete every 2nd number: 1, 3, 5, 7, 9, 11, 13, 15, 17, 19, ...
* 3. The second remaining number is 3, so keep it and delete every 3rd number: 1, 3, 7, 9, 13, 15, 19, ...
* 4. The third remaining number is 7, so keep it and delete every 7th number
* 5. Continue this process to get all lucky numbers
*
* @param count - The number of lucky numbers to generate
* @returns An array containing the first 'count' lucky numbers
*/
function getLuckyNumbers(count) {
	const numbers = [];
	let n = 1;
	const initialSize = count * (count < 100 ? 20 : 30);
	while (numbers.length < initialSize) {
		numbers.push(n);
		n += 2;
	}
	let sieveIndex = 1;
	while (sieveIndex < numbers.length && sieveIndex < count) {
		const sieveValue = numbers[sieveIndex];
		let j = 0;
		for (let i = 0; i < numbers.length; i++) if ((i + 1) % sieveValue !== 0) numbers[j++] = numbers[i];
		numbers.length = j;
		/* v8 ignore next 3 */
		if (sieveIndex < numbers.length) sieveIndex++;
	}
	return numbers.slice(0, count);
}
const luckySequence = {
	luckySeq: (length) => getLuckyNumbers(length),
	isLucky: (n) => generateLuckyNumbers((l) => l <= n).includes(n)
};
//#endregion
//#region src/builtin/modules/number-theory/sequences/mersenne.ts
const mersenneNumbers = [
	3,
	7,
	31,
	127,
	2047,
	8191,
	131071,
	524287,
	2147483647
];
//#endregion
//#region src/builtin/modules/number-theory/sequences/padovan.ts
/**
* Checks if a number is a Padovan number.
* Padovan numbers follow the recurrence relation: P(n) = P(n-2) + P(n-3) for n >= 3,
* with initial values P(0) = P(1) = P(2) = 1.
*
* The first few Padovan numbers are:
* 1, 1, 1, 2, 2, 3, 4, 5, 7, 9, 12, 16, 21, 28, 37, 49, 65, 86, 114, 151, 200, ...
*
* @param num - The number to check
* @returns True if the number is a Padovan number, false otherwise
*/
function isPadovan(num) {
	if (!Number.isInteger(num) || num <= 0) return false;
	if (num === 1) return true;
	const padovanNumbers = [
		1,
		1,
		1,
		2,
		2,
		3,
		4,
		5,
		7,
		9,
		12,
		16,
		21,
		28,
		37,
		49,
		65,
		86,
		114,
		151,
		200,
		265,
		351,
		465,
		616,
		816,
		1081,
		1432,
		1897,
		2513,
		3329,
		4410,
		5842,
		7739,
		10252,
		13581,
		17991,
		23833,
		31572,
		41824,
		55405,
		73396,
		97229
	];
	if (padovanNumbers.includes(num)) return true;
	if (num > padovanNumbers[padovanNumbers.length - 1] && num <= Number.MAX_SAFE_INTEGER) {
		let a = padovanNumbers[padovanNumbers.length - 3];
		let b = padovanNumbers[padovanNumbers.length - 2];
		let c = padovanNumbers[padovanNumbers.length - 1];
		let next;
		while (c < num) {
			next = a + b;
			a = b;
			b = c;
			c = next;
			if (c === num) return true;
			if (!Number.isSafeInteger(c)) return false;
		}
	}
	return false;
}
const padovanSequence = {
	padovanSeq: (length) => {
		const padovan = [
			1,
			1,
			1
		];
		for (let i = 3; i < length; i += 1) padovan[i] = padovan[i - 2] + padovan[i - 3];
		return padovan.slice(0, length);
	},
	isPadovan: (n) => isPadovan(n)
};
//#endregion
//#region src/builtin/modules/number-theory/sequences/pell.ts
const pellNumbers = [
	1,
	2,
	5,
	12,
	29,
	70,
	169,
	408,
	985,
	2378,
	5741,
	13860,
	33461,
	80782,
	195025,
	470832,
	1136689,
	2744210,
	6625109,
	15994428,
	38613965,
	93222358,
	225058681,
	543339720,
	1311738121,
	3166815962,
	7645370045,
	18457556052,
	44560482149,
	107578520350,
	259717522849,
	627013566048,
	1513744654945,
	3654502875938,
	8822750406821,
	21300003689580,
	51422757785981,
	0x70e8e1937766,
	299713796309065,
	723573111879672,
	0x634c28af7f039,
	0xefb9b143d586a
];
//#endregion
//#region src/builtin/modules/number-theory/sequences/perfect.ts
const perfectNumbers = [
	6,
	28,
	496,
	8128,
	33550336,
	8589869056,
	137438691328
];
//#endregion
//#region src/builtin/modules/number-theory/sequences/perfectCube.ts
const perfectCubeSequence = {
	perfectCubeSeq: (length) => {
		const perfectcubes = [];
		for (let i = 1; i <= length; i++) perfectcubes.push(i ** 3);
		return perfectcubes;
	},
	isPerfectCube: (n) => n > 0 && Number.isInteger(Math.cbrt(n))
};
//#endregion
//#region src/builtin/modules/number-theory/sequences/perfectPower.ts
/**
* Checks if a number is a perfect power and returns the base and exponent if it is.
* A perfect power is a number that can be expressed as an integer power of another integer.
*
* @param n - The number to check
* @returns [base, exponent] if n is a perfect power, null otherwise
*/
function perfectPower(n) {
	if (n < 2) {
		if (n === 1) return [1, 2];
		return null;
	}
	const maxK = Math.floor(Math.log2(n)) + 1;
	for (let k = 2; k <= maxK; k++) {
		const b = n ** (1 / k);
		const roundedB = Math.round(b);
		if (approxEqual(roundedB ** k, n)) return [roundedB, k];
	}
	return null;
}
const perfectPowerSequence = {
	perfectPowerSeq: (length) => {
		const perfectPowers = [];
		for (let i = 1; perfectPowers.length < length; i++) if (perfectPower(i)) perfectPowers.push(i);
		return perfectPowers;
	},
	isPerfectPower: (n) => perfectPower(n) !== null
};
//#endregion
//#region src/builtin/modules/number-theory/sequences/perfectSquare.ts
const perfectSquareSequence = {
	perfectSquareSeq: (length) => {
		const perfectSquares = [];
		for (let i = 1; i <= length; i++) perfectSquares.push(i ** 2);
		return perfectSquares;
	},
	isPerfectSquare: (n) => n > 0 && Number.isInteger(Math.sqrt(n))
};
//#endregion
//#region src/builtin/modules/number-theory/sequences/poligonal.ts
const poligonalNormalExpressions = {
	polygonalSeq: {
		evaluate: ([sides, n], sourceCodeInfo) => {
			assertNumber(sides, sourceCodeInfo, {
				integer: true,
				gte: 3
			});
			assertNumber(n, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			const polygonal = [];
			for (let i = 1; i <= n; i += 1) polygonal[i - 1] = (i * i * (sides - 2) - i * (sides - 4)) / 2;
			return polygonal;
		},
		arity: toFixedArity(2)
	},
	polygonalTakeWhile: {
		/* v8 ignore next 1 */
		evaluate: () => {
			throw new Error("unreachable: overridden by dvalaImpl");
		},
		arity: toFixedArity(2)
	},
	polygonalNth: {
		evaluate: ([sides, n], sourceCodeInfo) => {
			assertNumber(sides, sourceCodeInfo, {
				integer: true,
				gte: 3
			});
			assertNumber(n, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			return (n * n * (sides - 2) - n * (sides - 4)) / 2;
		},
		arity: toFixedArity(2)
	},
	isPolygonal: {
		evaluate: ([sides, n], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo, { integer: true });
			assertNumber(sides, sourceCodeInfo, {
				integer: true,
				gte: 3
			});
			if (n <= 0) return false;
			const a = sides - 2;
			const b = sides - 4;
			const discriminant = 8 * a * n + b * b;
			const sqrtPart = Math.sqrt(discriminant);
			if (!Number.isInteger(sqrtPart)) return false;
			const numerator = sqrtPart + b;
			if (numerator % (2 * a) !== 0) return false;
			const x = numerator / (2 * a);
			return Number.isInteger(x) && x > 0;
		},
		arity: toFixedArity(2)
	}
};
//#endregion
//#region src/builtin/modules/number-theory/sequences/recaman.ts
/**
* Generates the first 'n' terms of the Recamán sequence.
*
* @param n - Number of terms to generate
* @returns Array containing the first n terms of the Recamán sequence
*/
function generateRecamanSequence(n) {
	if (n === 1) return [0];
	const sequence = [0];
	const seen = new Set([0]);
	for (let i = 1; i < n; i++) {
		let next = sequence[i - 1] - i;
		if (next <= 0 || seen.has(next)) next = sequence[i - 1] + i;
		sequence.push(next);
		seen.add(next);
	}
	return sequence;
}
const recamanSequence = {
	recamanSeq: (length) => generateRecamanSequence(length),
	isRecaman: () => true
};
//#endregion
//#region src/builtin/modules/number-theory/sequences/sylvester.ts
const sylvesterNumbers = [
	2,
	6,
	42,
	1806,
	3263442,
	0x9afa8dc8416
];
//#endregion
//#region src/builtin/modules/number-theory/sequences/thueMorse.ts
const thueMorseSequence = {
	thueMorseSeq: (length) => {
		const thueMorse = [];
		for (let i = 0; i < length; i += 1) thueMorse[i] = countSetBits(i) % 2;
		return thueMorse;
	},
	isThueMorse: (n) => n === 1 || n === 0
};
function countSetBits(num) {
	let count = 0;
	while (num) {
		count += num & 1;
		num >>= 1;
	}
	return count;
}
//#endregion
//#region src/builtin/modules/number-theory/sequences/tribonacci.ts
const tribonacciNumbers = [
	0,
	1,
	1,
	2,
	4,
	7,
	13,
	24,
	44,
	81,
	149,
	274,
	504,
	927,
	1705,
	3136,
	5768,
	10609,
	19513,
	35890,
	66012,
	121415,
	223317,
	410744,
	755476,
	1389537,
	2555757,
	4700770,
	8646064,
	15902591,
	29249425,
	53798080,
	98950096,
	181997601,
	334745777,
	615693474,
	1132436852,
	2082876103,
	3831006429,
	7046319384,
	12960201916,
	23837527729,
	43844049029,
	80641778674,
	148323355432,
	272809183135,
	501774317241,
	922906855808,
	1697490356184,
	3122171529233,
	5742568741225,
	0x99b36012952,
	19426970897100,
	35731770264967,
	65720971788709,
	0x6df08059d9f8,
	0xca35cf00a924,
	408933139743937,
	752145307699165,
	0x4ea34736b35c2,
	0x90a3317d5c260,
	4680045560037375,
	8607945812375585
];
//#endregion
//#region src/builtin/modules/number-theory/sequences/index.ts
const sequenceNormalExpressions = {};
addSequence(abundantSequence);
addSequence(collatzSequence);
addSequence(compositeSequence);
addSequence(deficientSequence);
addSequence(golombSequence);
addSequence(happySequence);
addSequence(jugglerSequence);
addSequence(lookAndSaySequence);
addSequence(luckySequence);
addSequence(padovanSequence);
addSequence(perfectSquareSequence);
addSequence(perfectCubeSequence);
addSequence(perfectPowerSequence);
addSequence(primeSequence);
addSequence(recamanSequence);
addSequence(thueMorseSequence);
addNormalExpressions$1(getFiniteNumberSequence("tribonacci", tribonacciNumbers));
addNormalExpressions$1(getFiniteNumberSequence("catalan", catalanNumbers));
addNormalExpressions$1(getFiniteNumberSequence("factorial", factorialNumbers));
addNormalExpressions$1(getFiniteNumberSequence("fibonacci", fibonacciNumbers));
addNormalExpressions$1(getFiniteNumberSequence("lucas", lucasNumbers));
addNormalExpressions$1(getFiniteNumberSequence("mersenne", mersenneNumbers));
addNormalExpressions$1(getFiniteNumberSequence("partition", partitionNumbers));
addNormalExpressions$1(getFiniteNumberSequence("pell", pellNumbers));
addNormalExpressions$1(getFiniteNumberSequence("perfect", perfectNumbers));
addNormalExpressions$1(getFiniteNumberSequence("sylvester", sylvesterNumbers));
addNormalExpressions$1(getFiniteNumberSequence("bell", bellNumbers));
addNormalExpressions$1(arithmeticNormalExpressions);
addNormalExpressions$1(bernoulliNormalExpressions);
addNormalExpressions$1(geometricNormalExpressions);
addNormalExpressions$1(poligonalNormalExpressions);
function addNormalExpressions$1(normalExpressions) {
	for (const [key, value] of Object.entries(normalExpressions)) {
		/* v8 ignore next 3 */
		if (sequenceNormalExpressions[key]) throw new Error(`Duplicate normal expression key found: ${key}`);
		sequenceNormalExpressions[key] = value;
	}
}
function getFiniteNumberSequence(name, sequence) {
	return {
		[`${name}Seq`]: createSeqNormalExpression((length) => sequence.slice(0, length), sequence.length),
		[`${name}TakeWhile`]: createTakeWhileNormalExpression(sequence.length),
		[`${name}Nth`]: createNthNormalExpression(() => sequence, sequence.length),
		[`is${name.charAt(0).toUpperCase()}${name.slice(1)}`]: createNumberPredNormalExpression((n) => sequence.includes(n))
	};
}
function addSequence(sequence) {
	for (const [key, value] of Object.entries(sequence)) {
		/* v8 ignore next 3 */
		if (sequenceNormalExpressions[key]) throw new Error(`Duplicate normal expression key found: ${key}`);
		if (key.endsWith("Seq")) {
			sequenceNormalExpressions[key] = createSeqNormalExpression(value, sequence.maxLength);
			if (!sequence.noNth) sequenceNormalExpressions[key.replace(/Seq$/, "Nth")] = createNthNormalExpression(value, sequence.maxLength);
			if (!sequence.noTakeWhile) sequenceNormalExpressions[key.replace(/Seq$/, "TakeWhile")] = createTakeWhileNormalExpression(sequence.maxLength);
		} else if (key.startsWith("is")) if (sequence.string) sequenceNormalExpressions[key] = createStringPredNormalExpression(value);
		else sequenceNormalExpressions[key] = createNumberPredNormalExpression(value);
	}
}
function createSeqNormalExpression(seqFunction, maxLength) {
	return {
		evaluate: (params, sourceCodeInfo) => {
			const length = params.get(0) ?? maxLength;
			assertNumber(length, sourceCodeInfo, {
				integer: true,
				positive: true,
				lte: maxLength
			});
			const result = seqFunction(length, sourceCodeInfo);
			if (typeof result[0] === "number") {
				/* v8 ignore next 3 */
				if (result.some((n) => n > Number.MAX_SAFE_INTEGER)) throw new RuntimeError("Result exceeds maximum safe integer", sourceCodeInfo);
			}
			return result;
		},
		arity: typeof maxLength === "number" ? { max: 1 } : toFixedArity(1)
	};
}
function createTakeWhileNormalExpression(maxLength) {
	return {
		/* v8 ignore next 1 */
		evaluate: () => {
			throw new Error("unreachable: overridden by dvalaImpl");
		},
		arity: typeof maxLength === "number" ? { max: 1 } : toFixedArity(1)
	};
}
function createNthNormalExpression(seqFunction, maxLength) {
	return {
		evaluate: (params, sourceCodeInfo) => {
			const n = params.get(0);
			assertNumber(n, sourceCodeInfo, {
				integer: true,
				positive: true,
				lte: maxLength
			});
			const sequence = seqFunction(n, sourceCodeInfo);
			if (typeof sequence[0] === "number") {
				/* v8 ignore next 3 */
				if (sequence.some((val) => val > Number.MAX_SAFE_INTEGER)) throw new RuntimeError("Result exceeds maximum safe integer", sourceCodeInfo);
			}
			return sequence[n - 1];
		},
		arity: toFixedArity(1)
	};
}
function createNumberPredNormalExpression(predFunction) {
	return {
		evaluate: (params, sourceCodeInfo) => {
			const value = params.get(0);
			assertNumber(value, sourceCodeInfo);
			return predFunction(value, sourceCodeInfo);
		},
		arity: toFixedArity(1)
	};
}
function createStringPredNormalExpression(predFunction) {
	return {
		evaluate: (params, sourceCodeInfo) => {
			const value = params.get(0);
			assertString(value, sourceCodeInfo);
			return predFunction(value, sourceCodeInfo);
		},
		arity: toFixedArity(1)
	};
}
//#endregion
//#region src/builtin/modules/number-theory/index.ts
function gcd(a, b) {
	while (b !== 0) {
		const temp = b;
		b = a % b;
		a = temp;
	}
	return Math.abs(a);
}
function lcm(a, b) {
	return Math.floor(a * b / gcd(a, b));
}
function mobius(n) {
	if (n === 1) return 1;
	const factors = primeFactors(n);
	if (new Set(factors).size !== factors.length) return 0;
	return factors.length % 2 === 0 ? 1 : -1;
}
/**
* Efficiently computes (base^exponent) % modulus using the square-and-multiply algorithm
* Based on the pseudocode algorithm for modular exponentiation
*
* @param base - The base number
* @param exponent - The exponent (must be non-negative)
* @param modulus - The modulus (must be positive)
* @returns The result of (base^exponent) % modulus
*/
function modExp(base, exponent, modulus) {
	if (modulus === 1) return 0;
	let result = 1;
	base = base % modulus;
	while (exponent > 0) {
		if (exponent % 2 === 1) result = result * base % modulus;
		exponent = exponent >> 1;
		base = base * base % modulus;
	}
	return result;
}
/**
* Extended Euclidean Algorithm
* Finds gcd(a,b) and coefficients x,y such that ax + by = gcd(a,b)
*/
function extendedGcd(a, b) {
	if (b === 0) return [
		a,
		1,
		0
	];
	const [g, x, y] = extendedGcd(b, a % b);
	return [
		g,
		y,
		x - Math.floor(a / b) * y
	];
}
/**
* Modular Multiplicative Inverse
* Finds x such that (a * x) % m = 1
*/
function modInverse(a, m) {
	const [g, x] = extendedGcd(a, m);
	if (g !== 1) throw new Error(`Modular inverse does not exist (gcd(${a}, ${m}) = ${g})`);
	return (x % m + m) % m;
}
/**
* Chinese Remainder Theorem
* Solve system of congruences: x ≡ remainders[i] (mod moduli[i])
* Returns the smallest positive integer that satisfies all congruences
*/
function chineseRemainder(remainders, moduli) {
	for (let i = 0; i < moduli.length; i++) for (let j = i + 1; j < moduli.length; j++) {
		const extGcd = extendedGcd(moduli[i], moduli[j])[0];
		if (extGcd !== 1) throw new Error(`Moduli must be pairwise coprime, but gcd(${moduli[i]}, ${moduli[j]}) = ${extGcd}`);
	}
	const product = moduli.reduce((acc, val) => acc * val, 1);
	let sum = 0;
	for (let i = 0; i < remainders.length; i++) {
		const ai = remainders[i];
		const ni = moduli[i];
		const bi = product / ni;
		const inverse = modInverse(bi, ni);
		sum = (sum + ai * bi * inverse) % product;
	}
	return sum;
}
const combinatoricalNormalExpression = {
	isCoprime: {
		evaluate: ([a, b], sourceCodeInfo) => {
			assertNumber(a, sourceCodeInfo, { integer: true });
			assertNumber(b, sourceCodeInfo, { integer: true });
			return gcd(a, b) === 1;
		},
		arity: toFixedArity(2)
	},
	isDivisibleBy: {
		evaluate: ([value, divisor], sourceCodeInfo) => {
			assertNumber(value, sourceCodeInfo, { integer: true });
			assertNumber(divisor, sourceCodeInfo, { integer: true });
			if (divisor === 0) return false;
			return value % divisor === 0;
		},
		arity: toFixedArity(2)
	},
	gcd: {
		evaluate: ([a, b], sourceCodeInfo) => {
			assertNumber(a, sourceCodeInfo);
			assertNumber(b, sourceCodeInfo);
			return gcd(a, b);
		},
		arity: toFixedArity(2)
	},
	lcm: {
		evaluate: ([a, b], sourceCodeInfo) => {
			assertNumber(a, sourceCodeInfo);
			assertNumber(b, sourceCodeInfo);
			return lcm(a, b);
		},
		arity: toFixedArity(2)
	},
	multinomial: {
		evaluate: ([ ...args_], sourceCodeInfo) => {
			const args = assertVector(args_, sourceCodeInfo);
			return factorialOf(args.reduce((acc, curr) => {
				assertNumber(curr, sourceCodeInfo, {
					integer: true,
					nonNegative: true
				});
				return acc + curr;
			}, 0)) / args.reduce((acc, curr) => acc * factorialOf(curr), 1);
		},
		arity: { min: 1 }
	},
	isAmicable: {
		evaluate: ([a, b], sourceCodeInfo) => {
			assertNumber(a, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			assertNumber(b, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			const sumA = getProperDivisors(a).reduce((acc, curr) => acc + curr, 0);
			const sumB = getProperDivisors(b).reduce((acc, curr) => acc + curr, 0);
			return sumA === b && sumB === a && a !== b;
		},
		arity: toFixedArity(2)
	},
	eulerTotient: {
		evaluate: ([n], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			let result = n;
			for (let p = 2; p * p <= n; p += 1) if (n % p === 0) {
				while (n % p === 0) n /= p;
				result -= result / p;
			}
			if (n > 1) result -= result / n;
			return result;
		},
		arity: toFixedArity(1)
	},
	mobius: {
		evaluate: ([n], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			if (n === 1) return 1;
			const factors = primeFactors(n);
			if (new Set(factors).size !== factors.length) return 0;
			return factors.length % 2 === 0 ? 1 : -1;
		},
		arity: toFixedArity(1)
	},
	mertens: {
		evaluate: ([n], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			if (n === 1) return 1;
			let result = 0;
			for (let i = 1; i <= n; i++) {
				const mobiusValue = mobius(i);
				result += mobiusValue;
			}
			return result;
		},
		arity: toFixedArity(1)
	},
	sigma: {
		evaluate: ([n], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			return getDivisors(n).reduce((acc, curr) => acc + curr, 0);
		},
		arity: toFixedArity(1)
	},
	carmichaelLambda: {
		evaluate: ([n], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			if (n === 1) return 1;
			const primes = primeFactors(n);
			const factorMap = /* @__PURE__ */ new Map();
			for (const prime of primes) factorMap.set(prime, (factorMap.get(prime) || 0) + 1);
			const lambdaValues = [];
			for (const [p, k] of factorMap.entries()) if (p === 2) if (k === 1) lambdaValues.push(1);
			else if (k === 2) lambdaValues.push(2);
			else lambdaValues.push(2 ** (k - 2));
			else lambdaValues.push((p - 1) * p ** (k - 1));
			return lambdaValues.reduce((acc, val) => lcm(acc, val), 1);
		},
		arity: toFixedArity(1)
	},
	cartesianProduct: {
		evaluate: (params, sourceCodeInfo) => {
			for (const set of params) assertArray(set, sourceCodeInfo);
			const result = [...params].reduce((acc, set) => {
				const newAcc = [];
				for (const arr of acc) for (const value of set) newAcc.push(arr.append(value));
				return newAcc;
			}, [PersistentVector.empty()]);
			return PersistentVector.from(result);
		},
		arity: { min: 1 }
	},
	perfectPower: {
		evaluate: ([n], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			return perfectPower(n) || null;
		},
		arity: toFixedArity(1)
	},
	modExp: {
		evaluate: ([base, exponent, modulus], sourceCodeInfo) => {
			assertNumber(base, sourceCodeInfo, { finite: true });
			assertNumber(exponent, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			assertNumber(modulus, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			return modExp(base, exponent, modulus);
		},
		arity: toFixedArity(3)
	},
	modInv: {
		evaluate: ([a, m], sourceCodeInfo) => {
			assertNumber(a, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			assertNumber(m, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			try {
				return modInverse(a, m);
			} catch (error) {
				throw new DvalaError(error, sourceCodeInfo);
			}
		},
		arity: toFixedArity(2)
	},
	extendedGcd: {
		evaluate: ([a, b], sourceCodeInfo) => {
			assertNumber(a, sourceCodeInfo, { integer: true });
			assertNumber(b, sourceCodeInfo, { integer: true });
			return extendedGcd(a, b);
		},
		arity: toFixedArity(2)
	},
	chineseRemainder: {
		evaluate: ([remainders_, moduli_], sourceCodeInfo) => {
			const remainders = assertVector(remainders_, sourceCodeInfo);
			const moduli = assertVector(moduli_, sourceCodeInfo);
			if (remainders.length !== moduli.length) throw new RuntimeError("Remainders and moduli must have the same length.", sourceCodeInfo);
			try {
				return chineseRemainder(remainders, moduli);
			} catch (error) {
				throw new RuntimeError(error.message, sourceCodeInfo);
			}
		},
		arity: toFixedArity(2)
	},
	stirlingFirst: {
		evaluate: ([n, k], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			assertNumber(k, sourceCodeInfo, {
				integer: true,
				positive: true,
				lte: n
			});
			const dp = Array.from({ length: n + 1 }, () => Array(k + 1).fill(0));
			dp[0][0] = 1;
			for (let i = 1; i <= n; i++) for (let j = 1; j <= Math.min(i, k); j++) dp[i][j] = dp[i - 1][j - 1] + (i - 1) * dp[i - 1][j];
			return dp[n][k];
		},
		arity: toFixedArity(2)
	},
	stirlingSecond: {
		evaluate: ([n, k], sourceCodeInfo) => {
			assertNumber(n, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			assertNumber(k, sourceCodeInfo, {
				integer: true,
				positive: true,
				lte: n
			});
			if (k === 1) return 1;
			if (k === n) return 1;
			const dp = Array.from({ length: n + 1 }, () => Array(k + 1).fill(0));
			dp[0][0] = 1;
			for (let i = 1; i <= n; i++) for (let j = 1; j <= Math.min(i, k); j++) dp[i][j] = j * dp[i - 1][j] + dp[i - 1][j - 1];
			return dp[n][k];
		},
		arity: toFixedArity(2)
	}
};
addSequences(sequenceNormalExpressions);
addNormalExpressions(factorialNormalExpressions);
addNormalExpressions(divisorsNormalExpressions);
addNormalExpressions(combinationsNormalExpressions);
addNormalExpressions(permutationsNormalExpressions);
addNormalExpressions(partitionsNormalExpressions);
addNormalExpressions(primeFactorsNormalExpressions);
addNormalExpressions(derangementsNormalExpressions);
addNormalExpressions(powerSetNormalExpressions);
function addSequences(sequences) {
	for (const [key, value] of Object.entries(sequences)) {
		/* v8 ignore next 3 */
		if (combinatoricalNormalExpression[key]) throw new Error(`Duplicate normal expression key found: ${key}`);
		combinatoricalNormalExpression[key] = value;
	}
}
function addNormalExpressions(normalExpressions) {
	for (const [key, value] of Object.entries(normalExpressions)) {
		/* v8 ignore next 3 */
		if (combinatoricalNormalExpression[key]) throw new Error(`Duplicate normal expression key found: ${key}`);
		combinatoricalNormalExpression[key] = value;
	}
}
for (const [key, docs] of Object.entries(moduleDocs$2))
 /* v8 ignore next 2 */
if (combinatoricalNormalExpression[key]) combinatoricalNormalExpression[key].docs = docs;
const numberTheoryModule = {
	name: "numberTheory",
	description: "Number theory sequences and predicates: primes, Fibonacci, Catalan, and more.",
	functions: combinatoricalNormalExpression,
	source: number_theory_default,
	sourcePath: "packages/dvala-engine/src/builtin/modules/number-theory/number-theory.dvala",
	docs: moduleDocs$2
};
//#endregion
//#region src/builtin/modules/sequence/sequence.dvala
var sequence_default = "{\n  mapcat: (arr, fn) -> flatten(map(arr, fn), 1),\n  position: (seq, fn) -> do\n    let items =\n      if isNull(seq) then\n        null\n      else if isString(seq) then\n        for(i in range(count(seq))) -> nth(seq, i)\n      else\n        seq\n      end;\n    if isNull(items) then\n      null\n    else\n      loop(i = 0) -> if i >= count(items) then\n        null\n      else if fn(nth(items, i)) then\n        i\n      else\n        recur(i + 1)\n      end\n    end;\n  end,\n  sortBy: (seq, keyfn, ...args) -> do\n    let items =\n      if isString(seq) then\n        for(i in range(count(seq))) -> nth(seq, i)\n      else\n        [...seq]\n      end;\n    let keyed = map(items, (elem) -> [keyfn(elem), elem]);\n    let sorted =\n      if count(args) == 0 then\n        sort(keyed, (a, b) -> compare(first(a), first(b)))\n      else\n        sort(keyed, (a, b) -> first(args)(first(a), first(b)))\n      end;\n    let result = map(sorted, second);\n    if isString(seq) then join(result, \"\") else result end;\n  end,\n  remove: (seq, fn) -> do\n    let result =\n      filter(\n        if isString(seq) then\n          for(i in range(count(seq))) -> nth(seq, i)\n        else\n          seq\n        end,\n        (elem) -> !(fn(elem))\n      );\n    if isString(seq) then join(result, \"\") else result end;\n  end,\n  splitWith: (seq, fn) -> do\n    let items =\n      if isString(seq) then\n        for(i in range(count(seq))) -> nth(seq, i)\n      else\n        seq\n      end;\n    let idx =\n      loop(i = 0) -> if i >= count(items) then\n        -1\n      else if !(fn(nth(items, i))) then\n        i\n      else\n        recur(i + 1)\n      end;\n    if idx == -1 then\n      [seq, if isArray(seq) then [] else \"\" end]\n    else\n      [slice(seq, 0, idx), slice(seq, idx)]\n    end;\n  end,\n  groupBy: (seq, fn) -> do\n    let items =\n      if isString(seq) then\n        for(i in range(count(seq))) -> nth(seq, i)\n      else\n        seq\n      end;\n    reduce(\n      items,\n      (result, val) -> do\n        let key = fn(val);\n        let existing = get(result, key);\n        assoc(\n          result,\n          key,\n          if isNull(existing) then [val] else [...existing, val] end\n        );\n      end,\n      {}\n    );\n  end,\n  partitionBy: (seq, fn) -> do\n    let items =\n      if isString(seq) then\n        for(i in range(count(seq))) -> nth(seq, i)\n      else\n        seq\n      end;\n    if count(items) == 0 then\n      []\n    else\n      do\n        let result =\n          reduce(\n            rest(items),\n            (acc, elem) -> do\n              let value = fn(elem);\n              if value != first(acc) then\n                [value, [...second(acc), [elem]]]\n              else\n                [\n                  value,\n                  [\n                    ...slice(second(acc), 0, count(second(acc)) - 1),\n                    [...last(second(acc)), elem],\n                  ],\n                ]\n              end;\n            end,\n            [fn(first(items)), [[first(items)]]]\n          );\n        let groups = second(result);\n        if isString(seq) then\n          map(groups, (group) -> join(group, \"\"))\n        else\n          groups\n        end;\n      end\n    end;\n  end,\n};\n";
//#endregion
//#region src/builtin/modules/sequence/index.ts
const sequenceUtilsFunctions = {
	mapcat: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("mapcat is implemented in Dvala");
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Unknown[], (Unknown -> Unknown[])) -> Unknown[]",
			category: "sequence",
			returns: { type: "collection" },
			args: {
				a: { type: "collection" },
				b: { type: "function" },
				colls: {
					type: "collection",
					array: true
				},
				fun: { type: "function" }
			},
			variants: [{ argumentNames: ["colls", "fun"] }],
			description: "Returns the result of applying concat to the result of applying map to `fun` and `colls`.",
			seeAlso: [
				"flatten",
				"map",
				"++"
			],
			examples: [
				"let { mapcat } = import(\"sequence\");\nmapcat([[3, 2, 1, 0], [6, 5, 4], [9, 8, 7]], reverse)",
				`
let { mapcat } = import("sequence");
let foo = (n) -> do
  [n - 1, n, n + 1]
end;
mapcat([1, 2, 3], foo)`,
				`
let { mapcat } = import("sequence");
mapcat(
  [[1, 2], [2, 2], [2, 3]],
  -> $ filter isOdd
)`
			]
		}
	},
	position: {
		evaluate: () => {
			throw new Error("position: Dvala implementation should be used instead");
		},
		arity: toFixedArity(2),
		docs: {
			type: "((Unknown[] | Null), (Unknown -> Boolean)) -> (Number | Null)",
			category: "sequence",
			returns: { type: ["number", "null"] },
			args: {
				a: { type: "sequence" },
				b: { type: "function" },
				seq: { type: ["sequence", "null"] },
				fun: { type: "function" }
			},
			variants: [{ argumentNames: ["seq", "fun"] }],
			description: "Returns the index of the first elements that passes the test implemented by `fun`. If no element was found, `null` is returned.",
			seeAlso: [
				"indexOf",
				"some",
				"find"
			],
			examples: [
				`
let su = import("sequence");
su.position(
  ["Albert", "Mojir", 160, [1, 2]],
  isString
)`,
				`
let su = import("sequence");
su.position(
  [5, 10, 15, 20],
  -> $ > 10
)`,
				`
let su = import("sequence");
su.position(
  [5, 10, 15, 20],
  -> $ > 100
)`,
				`
let su = import("sequence");
su.position(
  null,
  -> $ > 100
)`
			]
		}
	},
	lastIndexOf: {
		evaluate: ([seq, value], sourceCodeInfo) => {
			assertAny(value, sourceCodeInfo);
			if (seq === null) return null;
			assertSeq(seq, sourceCodeInfo);
			if (typeof seq === "string") {
				assertString(value, sourceCodeInfo);
				const index = seq.lastIndexOf(value);
				return index !== -1 ? index : null;
			} else {
				let lastIndex = -1;
				let i = 0;
				for (const item of seq) {
					if (deepEqual(asAny(item, sourceCodeInfo), value, sourceCodeInfo)) lastIndex = i;
					i++;
				}
				return lastIndex !== -1 ? lastIndex : null;
			}
		},
		arity: toFixedArity(2),
		docs: {
			type: "((Unknown[] | Null), Unknown) -> (Number | Null)",
			category: "sequence",
			returns: { type: ["number", "null"] },
			args: {
				a: { type: "sequence" },
				b: { type: "any" },
				seq: { type: ["sequence", "null"] },
				x: { type: "any" }
			},
			variants: [{ argumentNames: ["seq", "x"] }],
			description: "Returns the last index of `x` in `seq`. If element is not present in `seq` `null` is returned.",
			seeAlso: ["indexOf"],
			examples: [
				"let su = import(\"sequence\"); su.lastIndexOf([[1], [2], [1], [2]], [1])",
				"let su = import(\"sequence\"); su.lastIndexOf([\"Albert\", \"Mojir\", 160, [1, 2]], \"Mojir\")",
				"let su = import(\"sequence\"); su.lastIndexOf([5, 10, 15, 20, 15], 15)",
				"let su = import(\"sequence\"); su.lastIndexOf([5, 10, 15, 20], 1)",
				"let su = import(\"sequence\"); su.lastIndexOf(null, 1)"
			]
		}
	},
	splice: {
		evaluate: (params, sourceCodeInfo) => {
			const seq = asSeq(params.get(0), sourceCodeInfo);
			const start = asNumber(params.get(1), sourceCodeInfo, { integer: true });
			const deleteCount = asNumber(params.get(2), sourceCodeInfo, {
				integer: true,
				nonNegative: true
			});
			const rest = [];
			for (let i = 3; i < params.size; i++) rest.push(params.get(i));
			const seqLen = typeof seq === "string" ? seq.length : seq.size;
			const from = start < 0 ? seqLen + start : start;
			if (isPersistentVector(seq)) {
				let result = PersistentVector.empty();
				let i = 0;
				let restInserted = false;
				for (const item of seq) {
					if (i === from) {
						for (const r of rest) result = result.append(r);
						restInserted = true;
					}
					if (i < from || i >= from + deleteCount) result = result.append(item);
					i++;
				}
				if (!restInserted) for (const r of rest) result = result.append(r);
				return result;
			}
			rest.forEach((elem) => assertString(elem, sourceCodeInfo));
			return `${seq.substring(0, from)}${rest.join("")}${seq.substring(from + deleteCount)}`;
		},
		arity: { min: 3 },
		docs: {
			type: "(Unknown[], Number, Number, ...Unknown) -> Unknown[]",
			category: "sequence",
			returns: { type: "sequence" },
			args: {
				seq: {
					type: "sequence",
					rest: true
				},
				start: { type: "integer" },
				deleteCount: { type: "integer" },
				items: {
					type: "any",
					rest: true
				}
			},
			variants: [{ argumentNames: [
				"seq",
				"start",
				"deleteCount"
			] }, { argumentNames: [
				"seq",
				"start",
				"deleteCount",
				"items"
			] }],
			description: "Returns a a spliced array. Removes `deleteCount` elements from `seq` starting at `start` and replaces them with `items`. If `start` is negative, it is counting from the end of the array.",
			seeAlso: ["slice", "sequence.removeAt"],
			examples: [
				"let su = import(\"sequence\"); su.splice([1, 2, 3, 4, 5], 2, 2, \"x\")",
				"let su = import(\"sequence\"); su.splice([1, 2, 3, 4, 5], -2, 1, \"x\")",
				"let su = import(\"sequence\"); su.splice(\"Albert\", 2, 2, \"fo\")"
			]
		}
	},
	sortBy: {
		evaluate: () => {
			throw new Error("sortBy: Dvala implementation should be used instead");
		},
		arity: {
			min: 2,
			max: 3
		},
		docs: {
			type: "(Unknown[], (Unknown -> Unknown)) -> Unknown[]",
			category: "sequence",
			returns: {
				type: "any",
				rest: true
			},
			args: {
				a: { type: "sequence" },
				b: { type: "function" },
				seq: { type: "sequence" },
				keyfn: { type: "function" },
				comparer: { type: "function" }
			},
			variants: [{ argumentNames: ["seq", "keyfn"] }, { argumentNames: [
				"seq",
				"keyfn",
				"comparer"
			] }],
			description: "Returns a sorted sequence of the items in `seq`, where the sort order is determined by comparing `(keyfn item)`. If no `comparer` is supplied, uses builtin `compare`.",
			seeAlso: ["sort", "compare"],
			examples: [
				"let su = import(\"sequence\"); su.sortBy([\"Albert\", \"Mojir\", \"Nina\"], count)",
				"let su = import(\"sequence\"); su.sortBy([\"Albert\", \"Mojir\", \"Nina\"], count)",
				"let su = import(\"sequence\"); su.sortBy(\"Albert\", lowerCase, -> $2 compare $)"
			]
		}
	},
	unshift: {
		evaluate: ([seq, ...values], sourceCodeInfo) => {
			assertSeq(seq, sourceCodeInfo);
			if (typeof seq === "string") {
				for (const v of values) if (typeof v !== "string" || v.length !== 1) throw new TypeError(`Expected char, got ${typeof v === "string" ? `"${v}"` : String(v)}`);
				return [...values, seq].join("");
			}
			let result = PersistentVector.empty();
			for (const v of values) result = result.append(v);
			for (const item of seq) result = result.append(item);
			return result;
		},
		arity: { min: 2 },
		docs: {
			type: "(Unknown[], ...Unknown) -> Unknown[]",
			category: "sequence",
			returns: { type: "sequence" },
			args: {
				a: { type: "sequence" },
				b: { type: "any" },
				seq: { type: "sequence" },
				values: {
					type: "any",
					rest: true
				}
			},
			variants: [{ argumentNames: ["seq", "values"] }],
			description: "Returns copy of `seq` with `values` added to the beginning.",
			seeAlso: ["push", "++"],
			examples: [
				"let su = import(\"sequence\"); su.unshift([1, 2, 3], 4)",
				"let su = import(\"sequence\"); su.unshift([1, 2, 3], 4)",
				"let su = import(\"sequence\"); su.unshift([1, 2, 3], 4, 5, 6)",
				`
let su = import("sequence");
let l = [1, 2, 3];
su.unshift(l, 4);
l`
			]
		}
	},
	distinct: {
		evaluate: ([input], sourceCodeInfo) => {
			assertSeq(input, sourceCodeInfo);
			if (isPersistentVector(input)) {
				let result = PersistentVector.empty();
				for (const item of input) {
					assertAny(item, sourceCodeInfo);
					let found = false;
					for (const existingItem of result) if (deepEqual(existingItem, item, sourceCodeInfo)) {
						found = true;
						break;
					}
					if (!found) result = result.append(item);
				}
				return result;
			}
			return Array.from(new Set(input.split(""))).join("");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Unknown[]) -> Unknown[]",
			category: "sequence",
			returns: { type: "sequence" },
			args: { seq: { type: "sequence" } },
			variants: [{ argumentNames: ["seq"] }],
			description: "Returns a copy of `seq` with no duplicates.",
			seeAlso: ["sequence.frequencies"],
			examples: [
				"let su = import(\"sequence\"); su.distinct([[1], [2], [3], [1], [3], [5]])",
				"let su = import(\"sequence\"); su.distinct([1, 2, 3, 1, 3, 5])",
				"let su = import(\"sequence\"); su.distinct(\"Albert Mojir\")",
				"let su = import(\"sequence\"); su.distinct([])",
				"let su = import(\"sequence\"); su.distinct(\"\")"
			]
		}
	},
	remove: {
		evaluate: () => {
			throw new Error("remove: Dvala implementation should be used instead");
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Unknown[], (Unknown -> Boolean)) -> Unknown[]",
			category: "sequence",
			returns: { type: "sequence" },
			args: {
				a: { type: "sequence" },
				b: { type: "function" },
				seq: { type: "sequence" },
				fun: { type: "function" }
			},
			variants: [{ argumentNames: ["seq", "fun"] }],
			description: "Returns a new sequence of items in `seq` for witch `pred(item)` returns a falsy value.",
			seeAlso: ["filter", "sequence.removeAt"],
			examples: [
				"let su = import(\"sequence\"); su.remove([1, 2, 3, 1, 3, 5], isOdd)",
				"let su = import(\"sequence\"); su.remove([1, 2, 3, 1, 3, 5], isEven)",
				"let su = import(\"sequence\"); su.remove(\"Albert Mojir\", -> \"aoueiyAOUEIY\" contains $)"
			]
		}
	},
	removeAt: {
		evaluate: ([input, index], sourceCodeInfo) => {
			assertNumber(index, sourceCodeInfo, { integer: true });
			assertSeq(input, sourceCodeInfo);
			const inputLen = typeof input === "string" ? input.length : input.size;
			const at = index < 0 ? inputLen + index : index;
			if (at < 0 || at >= inputLen) return input;
			if (isPersistentVector(input)) {
				let result = PersistentVector.empty();
				let i = 0;
				for (const item of input) {
					if (i !== at) result = result.append(item);
					i++;
				}
				return result;
			}
			return `${input.substring(0, at)}${input.substring(at + 1)}`;
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Unknown[], Number) -> Unknown[]",
			category: "sequence",
			returns: { type: "sequence" },
			args: {
				a: { type: "sequence" },
				b: { type: "integer" },
				seq: { type: "sequence" },
				n: { type: "number" }
			},
			variants: [{ argumentNames: ["seq", "n"] }],
			description: "Returns a new sequence of all items in `seq` except item at position `n`. If `n` is negative, it is counting from the end of the sequence.",
			seeAlso: ["sequence.remove", "sequence.splice"],
			examples: [
				"let su = import(\"sequence\"); su.removeAt([1, 2, 3, 1, 3, 5], 2)",
				"let su = import(\"sequence\"); su.removeAt(\"Albert\", -2)",
				"let su = import(\"sequence\"); su.removeAt([1, 2, 3, 1, 3, 5], 0)",
				"let su = import(\"sequence\"); su.removeAt([1, 2, 3, 1, 3, 5], -1)",
				"let su = import(\"sequence\"); su.removeAt(\"Albert Mojir\", 6)"
			]
		}
	},
	splitAt: {
		evaluate: ([seq, pos], sourceCodeInfo) => {
			assertNumber(pos, sourceCodeInfo, { integer: true });
			assertSeq(seq, sourceCodeInfo);
			const seqLen = typeof seq === "string" ? seq.length : seq.size;
			const at = pos < 0 ? seqLen + pos : pos;
			if (isPersistentVector(seq)) {
				let before = PersistentVector.empty();
				let after = PersistentVector.empty();
				let i = 0;
				for (const item of seq) {
					if (i < at) before = before.append(item);
					else after = after.append(item);
					i++;
				}
				return PersistentVector.from([before, after]);
			}
			return PersistentVector.from([seq.slice(0, at), seq.slice(at)]);
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Unknown[], Number) -> Unknown[]",
			category: "sequence",
			returns: { type: "sequence" },
			args: {
				a: { type: "sequence" },
				b: { type: "integer" },
				seq: { type: "sequence" },
				n: { type: "number" }
			},
			variants: [{ argumentNames: ["seq", "n"] }],
			description: "Returns a pair of sequence `[take(pos input), drop(pos input)]`.",
			seeAlso: [
				"sequence.splitWith",
				"take",
				"drop"
			],
			examples: [
				"let su = import(\"sequence\"); su.splitAt([1, 2, 3, 4, 5], 2)",
				"let su = import(\"sequence\"); su.splitAt(\"Albert\", -2)",
				"let su = import(\"sequence\"); su.splitAt([1, 2, 3, 4, 5], -2)",
				"let su = import(\"sequence\"); su.splitAt(\"Albert\", 2)"
			]
		}
	},
	splitWith: {
		evaluate: () => {
			throw new Error("splitWith: Dvala implementation should be used instead");
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Unknown[], (Unknown -> Boolean)) -> Unknown[]",
			category: "sequence",
			returns: { type: "sequence" },
			args: {
				a: { type: "sequence" },
				b: { type: "function" },
				seq: { type: "sequence" },
				fun: { type: "function" }
			},
			variants: [{ argumentNames: ["seq", "fun"] }],
			description: "Returns a pair of sequences `[takeWhile(input, fun), dropWhile(input, fun)]`.",
			seeAlso: [
				"sequence.splitAt",
				"takeWhile",
				"dropWhile"
			],
			examples: [
				"let su = import(\"sequence\"); su.splitWith([1, 2, 3, 4, 5], isOdd)",
				"let su = import(\"sequence\"); su.splitWith([1, 2, 3, 4, 5], -> $ > 3)",
				"let su = import(\"sequence\"); su.splitWith(\"Albert\", -> $ <= \"o\")"
			]
		}
	},
	frequencies: {
		evaluate: ([seq], sourceCodeInfo) => {
			assertSeq(seq, sourceCodeInfo);
			let result = PersistentMap.empty();
			const items = typeof seq === "string" ? seq.split("") : [...seq];
			for (const val of items) {
				assertString(val, sourceCodeInfo);
				const count = collHasKey(result, val) ? result.get(val) : 0;
				result = result.assoc(val, count + 1);
			}
			return result;
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Unknown[]) -> {String: Number}",
			category: "sequence",
			returns: { type: "object" },
			args: { seq: { type: "sequence" } },
			variants: [{ argumentNames: ["seq"] }],
			description: "Returns an object from distinct items in `seq` to the number of times they appear. Note that all items in `seq` must be valid object keys i.e. strings.",
			seeAlso: [
				"sequence.groupBy",
				"sequence.distinct",
				"countValues"
			],
			examples: ["let su = import(\"sequence\"); su.frequencies([\"Albert\", \"Mojir\", \"Nina\", \"Mojir\"])", "let su = import(\"sequence\"); su.frequencies(\"Pneumonoultramicroscopicsilicovolcanoconiosis\")"]
		}
	},
	groupBy: {
		evaluate: () => {
			throw new Error("groupBy: Dvala implementation should be used instead");
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Unknown[], (Unknown -> String)) -> {String: Unknown[]}",
			category: "sequence",
			returns: { type: "object" },
			args: {
				a: { type: "sequence" },
				b: { type: "function" },
				seq: { type: "sequence" },
				fun: { type: "function" }
			},
			variants: [{ argumentNames: ["seq", "fun"] }],
			description: "Returns an object of the elements of `seq` keyed by the result of `fun` on each element. The value at each key will be an array of the corresponding elements.",
			seeAlso: ["sequence.frequencies", "sequence.partitionBy"],
			examples: [
				"let su = import(\"sequence\"); su.groupBy([{ name: \"Albert\" }, { name: \"Albert\" }, { name: \"Mojir\" }], \"name\")",
				"let su = import(\"sequence\"); su.groupBy([{name: \"Albert\"}, {name: \"Albert\"}, {name: \"Mojir\"}], \"name\")",
				"let su = import(\"sequence\"); su.groupBy(\"Albert Mojir\", -> if \"aoueiAOUEI\" contains $ then \"vowel\" else \"other\" end)"
			]
		}
	},
	partition: {
		evaluate: (params, sourceCodeInfo) => {
			const seq = asSeq(params.get(0), sourceCodeInfo);
			const n = toNonNegativeInteger(asNumber(params.get(1), sourceCodeInfo));
			return partitionHelper(n, params.size >= 3 ? toNonNegativeInteger(asNumber(params.get(2), sourceCodeInfo)) : n, seq, params.size === 4 ? params.get(3) === null ? PersistentVector.empty() : asArray(params.get(3), sourceCodeInfo) : void 0, sourceCodeInfo);
		},
		arity: {
			min: 2,
			max: 4
		},
		docs: {
			type: "(Unknown[], Number) -> Unknown[]",
			category: "sequence",
			returns: { type: "sequence" },
			args: {
				a: { type: "sequence" },
				b: { type: "number" },
				seq: { type: "sequence" },
				n: { type: "number" },
				step: { type: "number" },
				pad: { type: "array" }
			},
			variants: [
				{ argumentNames: ["seq", "n"] },
				{ argumentNames: [
					"seq",
					"n",
					"step"
				] },
				{ argumentNames: [
					"seq",
					"n",
					"step",
					"pad"
				] }
			],
			description: "Returns an array of sequences of `n` items each, at offsets `step` apart. If `step` is not supplied, defaults to `n`. If a `pad` array is supplied, use its elements as necessary to complete last partition upto `n` items. In case there are not enough padding elements, return a partition with less than `n` items.",
			seeAlso: ["sequence.partitionAll", "sequence.partitionBy"],
			examples: [
				"let su = import(\"sequence\"); su.partition(range(20), 4)",
				"let su = import(\"sequence\"); su.partition(range(20), 4)",
				"let su = import(\"sequence\"); su.partition(range(22), 4)",
				"let su = import(\"sequence\"); su.partition(range(20), 4, 6)",
				"let su = import(\"sequence\"); su.partition(range(20), 4, 3)",
				"let su = import(\"sequence\"); su.partition(range(20), 3, 6, [\"a\"])",
				"let su = import(\"sequence\"); su.partition(range(20), 4, 6, [\"a\"])",
				"let su = import(\"sequence\"); su.partition(range(20), 4, 6, [\"a\", \"b\", \"c\", \"d\"])",
				"let su = import(\"sequence\"); su.partition([\"a\", \"b\", \"c\", \"d\", \"e\", \"f\"], 3, 1)",
				"let su = import(\"sequence\"); su.partition([1, 2, 3, 4], 10)",
				"let su = import(\"sequence\"); su.partition([1, 2, 3, 4], 10, 10)",
				"let su = import(\"sequence\"); su.partition([1, 2, 3, 4], 10, 10, [])",
				"let su = import(\"sequence\"); su.partition([1, 2, 3, 4], 10, 10, null)",
				"let su = import(\"sequence\"); su.partition(\"superfragilistic\", 5)",
				"let su = import(\"sequence\"); su.partition(\"superfragilistic\", 5, 5, null)",
				"let su = import(\"sequence\"); let foo = [5, 6, 7, 8]; su.partition(foo, 2, 1, foo)"
			]
		}
	},
	partitionAll: {
		evaluate: (params, sourceCodeInfo) => {
			const seq = asSeq(params.get(0), sourceCodeInfo);
			const n = toNonNegativeInteger(asNumber(params.get(1), sourceCodeInfo));
			return partitionHelper(n, params.size === 3 ? toNonNegativeInteger(asNumber(params.get(2), sourceCodeInfo)) : n, seq, PersistentVector.empty(), sourceCodeInfo);
		},
		arity: {
			min: 2,
			max: 3
		},
		docs: {
			type: "(Unknown[], Number) -> Unknown[]",
			category: "sequence",
			returns: { type: "sequence" },
			args: {
				a: { type: "sequence" },
				b: { type: "number" },
				seq: { type: "sequence" },
				n: { type: "number" },
				step: { type: "number" }
			},
			variants: [{ argumentNames: ["seq", "n"] }, { argumentNames: [
				"seq",
				"n",
				"step"
			] }],
			description: "Returns an array of sequences like partition, but may include partitions with fewer than n items at the end.",
			seeAlso: ["sequence.partition", "sequence.partitionBy"],
			examples: [
				"let su = import(\"sequence\"); su.partitionAll([0, 1, 2, 3, 4, 5, 6, 7, 8, 9], 4)",
				"let su = import(\"sequence\"); su.partitionAll([0, 1, 2, 3, 4, 5, 6, 7, 8, 9], 4)",
				"let su = import(\"sequence\"); su.partition([0, 1, 2, 3, 4, 5, 6, 7, 8, 9], 4)",
				"let su = import(\"sequence\"); su.partitionAll([0, 1, 2, 3, 4, 5, 6, 7, 8, 9], 2, 4)"
			]
		}
	},
	partitionBy: {
		evaluate: () => {
			throw new Error("partitionBy: Dvala implementation should be used instead");
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Unknown[], (Unknown -> Unknown)) -> Unknown[]",
			category: "sequence",
			returns: { type: "sequence" },
			args: {
				a: { type: "sequence" },
				b: { type: "function" },
				seq: { type: "sequence" },
				fun: { type: "function" }
			},
			variants: [{ argumentNames: ["seq", "fun"] }],
			description: "Applies `fun` to each value in `seq`, splitting it each time `fun` returns a new value. Returns an array of sequences.",
			seeAlso: [
				"sequence.partition",
				"sequence.partitionAll",
				"sequence.groupBy"
			],
			examples: [
				"let su = import(\"sequence\"); su.partitionBy([1, 2, 3, 4, 5], isOdd)",
				"let su = import(\"sequence\"); su.partitionBy([1, 2, 3, 4, 5], -> $ == 3)",
				"let su = import(\"sequence\"); su.partitionBy([1, 1, 1, 2, 2, 3, 3], isOdd)",
				"let su = import(\"sequence\"); su.partitionBy(\"Leeeeeerrroyyy\", identity)"
			]
		}
	},
	isEndsWith: {
		evaluate: ([str, search], sourceCodeInfo) => {
			assertSeq(str, sourceCodeInfo);
			if (typeof str === "string") {
				assertString(search, sourceCodeInfo);
				return str.endsWith(search);
			}
			return deepEqual(asAny(str.get(str.size - 1), sourceCodeInfo), asAny(search, sourceCodeInfo), sourceCodeInfo);
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Unknown[], Unknown) -> Boolean",
			category: "sequence",
			returns: { type: "boolean" },
			args: {
				a: { type: "sequence" },
				b: { type: "sequence" },
				seq: { type: "sequence" },
				suffix: { type: "sequence" }
			},
			variants: [{ argumentNames: ["seq", "suffix"] }],
			description: "Returns `true` if `seq` ends with `suffix`, otherwise `false`.",
			seeAlso: ["sequence.isStartsWith"],
			examples: [
				"let su = import(\"sequence\"); su.isEndsWith([[1], [2], [3], [4], [5]], [5])",
				"let su = import(\"sequence\"); su.isEndsWith([[1], [2], [3], [4], [5]], 5)",
				"let su = import(\"sequence\"); su.isEndsWith([1, 2, 3, 4, 5], 5)",
				"let su = import(\"sequence\"); su.isEndsWith([1, 2, 3, 4, 5], [5])",
				"let su = import(\"sequence\"); su.isEndsWith(\"Albert\", \"rt\")",
				"let su = import(\"sequence\"); su.isEndsWith(\"Albert\", \"RT\")"
			]
		}
	},
	isStartsWith: {
		evaluate: ([seq, search], sourceCodeInfo) => {
			assertSeq(seq, sourceCodeInfo);
			if (typeof seq === "string") {
				assertString(search, sourceCodeInfo);
				return seq.startsWith(search);
			}
			return deepEqual(asAny(seq.get(0), sourceCodeInfo), asAny(search, sourceCodeInfo), sourceCodeInfo);
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Unknown[], Unknown) -> Boolean",
			category: "sequence",
			returns: { type: "boolean" },
			args: {
				a: { type: "sequence" },
				b: { type: "sequence" },
				seq: { type: "sequence" },
				prefix: { type: "sequence" }
			},
			variants: [{ argumentNames: ["seq", "prefix"] }],
			description: "Returns `true` if `seq` starts with `prefix`, otherwise `false`.",
			seeAlso: ["sequence.isEndsWith"],
			examples: [
				"let su = import(\"sequence\"); su.isStartsWith([[1], [2], [3], [4], [5]], [1])",
				"let su = import(\"sequence\"); su.isStartsWith([1, 2, 3, 4, 5], 1)",
				"let su = import(\"sequence\"); su.isStartsWith([1, 2, 3, 4, 5], [1])",
				"let su = import(\"sequence\"); su.isStartsWith(\"Albert\", \"Al\")",
				"let su = import(\"sequence\"); su.isStartsWith(\"Albert\", \"al\")"
			]
		}
	},
	interleave: {
		evaluate: ([ ...seqs], sourceCodeInfo) => {
			const isStringSeq = typeof seqs[0] === "string";
			const normalizedSeqs = isStringSeq ? seqs.map((seq) => {
				assertString(seq, sourceCodeInfo);
				return seq.split("");
			}) : seqs.map((seq) => {
				assertArray(seq, sourceCodeInfo);
				return seq;
			});
			const getLen = (s) => Array.isArray(s) ? s.length : s.size;
			const getItem = (s, i) => Array.isArray(s) ? s[i] : s.get(i);
			const maxLength = Math.min(...normalizedSeqs.map(getLen));
			let result = PersistentVector.empty();
			for (let i = 0; i < maxLength; i += 1) for (const seq of normalizedSeqs)
 /* v8 ignore next 2 */
			if (i < getLen(seq)) result = result.append(getItem(seq, i));
			return isStringSeq ? [...result].join("") : result;
		},
		arity: { min: 1 },
		docs: {
			type: "(...Unknown[]) -> Unknown[]",
			category: "sequence",
			returns: { type: "sequence" },
			args: {
				a: { type: "sequence" },
				b: { type: "sequence" },
				seqs: {
					type: "sequence",
					rest: true
				}
			},
			variants: [{ argumentNames: ["seqs"] }],
			description: "Returns a sequence of the first item from each of the `seqs`, then the second item from each of the `seqs`, until all items from the shortest seq are exhausted.",
			seeAlso: ["sequence.interpose", "zipmap"],
			examples: [
				"let su = import(\"sequence\"); su.interleave([1, 2, 3], [4, 5, 6])",
				"let su = import(\"sequence\"); su.interleave(\"Albert\", \".,.,.,\")",
				"let su = import(\"sequence\"); su.interleave([1, 2, 3], [4, 5, 6])",
				"let su = import(\"sequence\"); su.interleave([1, 2, 3], [4, 5, 6], [7, 8, 9])",
				"let su = import(\"sequence\"); su.interleave([1, 2, 3], [4, 5, 6], [7, 8])",
				"let su = import(\"sequence\"); su.interleave([1, 2, 3], [4, 5, 6], [7])",
				"let su = import(\"sequence\"); su.interleave([1, 2, 3], [4, 5, 6], [])",
				"let su = import(\"sequence\"); su.interleave([1, 2, 3], [])",
				"let su = import(\"sequence\"); su.interleave([])"
			]
		}
	},
	interpose: {
		evaluate: ([seq, separator], sourceCodeInfo) => {
			assertSeq(seq, sourceCodeInfo);
			if (typeof seq === "string") {
				assertString(separator, sourceCodeInfo);
				return seq.split("").join(separator);
			}
			if (seq.size === 0) return PersistentVector.empty();
			let result = PersistentVector.empty();
			let i = 0;
			for (const item of seq) {
				result = result.append(item);
				if (i < seq.size - 1) result = result.append(separator);
				i++;
			}
			return result;
		},
		arity: toFixedArity(2),
		docs: {
			type: "(Unknown[], Unknown) -> Unknown[]",
			category: "sequence",
			returns: { type: "sequence" },
			args: {
				a: { type: "sequence" },
				b: { type: "any" },
				seq: { type: "sequence" },
				separator: { type: "any" }
			},
			variants: [{ argumentNames: ["seq", "separator"] }],
			description: "Returns a sequence of the elements of `seq` separated by `separator`. If `seq` is a string, the separator must be a string.",
			seeAlso: ["sequence.interleave", "join"],
			examples: [
				"let su = import(\"sequence\"); su.interpose(\"Albert\", \"-\")",
				"let su = import(\"sequence\"); su.interpose([1, 2, 3, 4, 5], \"a\")",
				"let su = import(\"sequence\"); su.interpose([\"Albert\", \"Mojir\", \"Nina\"], \", \")",
				"let su = import(\"sequence\"); su.interpose(\"Albert\", \".\")"
			]
		}
	}
};
function partitionHelper(n, step, seq, pad, sourceCodeInfo) {
	assertNumber(step, sourceCodeInfo, { positive: true });
	const isStringSeq = typeof seq === "string";
	const seqLen = isStringSeq ? seq.length : seq.size;
	let result = PersistentVector.empty();
	let start = 0;
	outer: while (start < seqLen) {
		let innerArr = PersistentVector.empty();
		for (let i = start; i < start + n; i += 1) if (i >= seqLen) {
			const padIndex = i - seqLen;
			if (!pad) {
				start += step;
				continue outer;
			}
			if (padIndex >= pad.size) break;
			innerArr = innerArr.append(pad.get(padIndex));
		} else {
			const item = isStringSeq ? seq[i] : seq.get(i);
			innerArr = innerArr.append(item);
		}
		result = result.append(isStringSeq ? [...innerArr].join("") : innerArr);
		start += step;
	}
	return result;
}
const sequenceUtilsModule = {
	name: "sequence",
	description: "Sequence generation and transformation: iterate, unfold, windows, and chunks.",
	functions: sequenceUtilsFunctions,
	source: sequence_default,
	sourcePath: "packages/dvala-engine/src/builtin/modules/sequence/sequence.dvala",
	docs: moduleDocsFromFunctions(sequenceUtilsFunctions)
};
//#endregion
//#region src/builtin/modules/string/string.dvala
var string_default = "{};\n";
//#endregion
//#region src/builtin/modules/string/index.ts
const stringUtilsFunctions = {
	stringRepeat: {
		evaluate: ([str, count], sourceCodeInfo) => {
			assertString(str, sourceCodeInfo);
			assertNumber(count, sourceCodeInfo, {
				integer: true,
				nonNegative: true
			});
			return str.repeat(count);
		},
		arity: toFixedArity(2),
		docs: {
			type: "(String, Number) -> String",
			category: "string",
			returns: { type: "string" },
			args: {
				a: { type: "string" },
				b: { type: "integer" },
				s: { type: "string" },
				n: { type: "integer" }
			},
			variants: [{ argumentNames: ["s", "n"] }],
			description: "Repeates `s` `n` times.",
			seeAlso: ["str", "repeat"],
			examples: [
				`let { stringRepeat } = import("string");
"*" stringRepeat 10`,
				`let { stringRepeat } = import("string");
stringRepeat("*", 10)`,
				`let { stringRepeat } = import("string");
stringRepeat("***", 0)`
			]
		}
	},
	fromCharCode: {
		evaluate: ([num], sourceCodeInfo) => {
			assertNumber(num, sourceCodeInfo, { finite: true });
			const int = toNonNegativeInteger(num);
			try {
				return String.fromCodePoint(int);
			} catch (error) {
				throw new DvalaError(error, sourceCodeInfo);
			}
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number) -> String",
			category: "string",
			returns: { type: "string" },
			args: { code: { type: "number" } },
			variants: [{ argumentNames: ["code"] }],
			description: "Return character for code point `code`.",
			seeAlso: ["string.toCharCode"],
			examples: [`let { fromCharCode } = import("string");
fromCharCode(65)`, `let { fromCharCode } = import("string");
fromCharCode(0)`]
		}
	},
	toCharCode: {
		evaluate: ([str], sourceCodeInfo) => {
			assertString(str, sourceCodeInfo, { nonEmpty: true });
			return asNonUndefined(str.codePointAt(0), sourceCodeInfo);
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String) -> Number",
			category: "string",
			returns: { type: "number" },
			args: { c: { type: "string" } },
			variants: [{ argumentNames: ["c"] }],
			description: "Return code point for first character in `c`.",
			seeAlso: ["string.fromCharCode"],
			examples: [`let { toCharCode } = import("string");
toCharCode("A")`, `let { toCharCode } = import("string");
toCharCode("Albert")`]
		}
	},
	trimLeft: {
		evaluate: ([str], sourceCodeInfo) => {
			assertString(str, sourceCodeInfo);
			return str.replace(/^\s+/, "");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String) -> String",
			category: "string",
			returns: { type: "string" },
			args: { s: { type: "string" } },
			variants: [{ argumentNames: ["s"] }],
			description: "Returns a new string with leading whitespaces removed.",
			seeAlso: ["trim", "string.trimRight"],
			examples: [
				`let { trimLeft } = import("string");
trimLeft("  Albert  ")`,
				`let { trimLeft } = import("string");
trimLeft("   ")`,
				`let { trimLeft } = import("string");
trimLeft("")`
			]
		}
	},
	trimRight: {
		evaluate: ([str], sourceCodeInfo) => {
			assertString(str, sourceCodeInfo);
			return str.replace(/\s+$/, "");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String) -> String",
			category: "string",
			returns: { type: "string" },
			args: { s: { type: "string" } },
			variants: [{ argumentNames: ["s"] }],
			description: "Returns a new string with trailing whitespaces removed.",
			seeAlso: ["trim", "string.trimLeft"],
			examples: [
				`let { trimRight } = import("string");
trimRight("  Albert  ")`,
				`let { trimRight } = import("string");
trimRight("   ")`,
				`let { trimRight } = import("string");
trimRight("")`
			]
		}
	},
	splitLines: {
		evaluate: ([str], sourceCodeInfo) => {
			assertString(str, sourceCodeInfo);
			return str.split(/\r\n|\n|\r/).filter((line) => line !== "");
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String) -> String[]",
			category: "string",
			returns: {
				type: "string",
				array: true
			},
			args: { s: { type: "string" } },
			variants: [{ argumentNames: ["s"] }],
			description: "Divides `s` into an array of substrings, each representing a line.",
			seeAlso: ["split"],
			examples: [
				`let { splitLines } = import("string");
splitLines("Albert\nMojir\n")`,
				`let { splitLines } = import("string");
splitLines("Albert\n\nMojir")`,
				`let { splitLines } = import("string");
splitLines("Albert\nMojir\n\n")`,
				`let { splitLines } = import("string");
splitLines("")`
			]
		}
	},
	padLeft: {
		evaluate: ([str, length, padString], sourceCodeInfo) => {
			assertString(str, sourceCodeInfo);
			assertNumber(length, sourceCodeInfo, { integer: true });
			if (padString !== void 0) assertString(padString, sourceCodeInfo);
			return str.padStart(length, padString);
		},
		arity: {
			min: 2,
			max: 3
		},
		docs: {
			type: "(String, Number) -> String",
			category: "string",
			returns: { type: "string" },
			args: {
				a: { type: "string" },
				b: { type: "integer" },
				s: { type: "string" },
				length: { type: "integer" },
				padString: { type: "string" }
			},
			variants: [{ argumentNames: ["s", "length"] }, { argumentNames: [
				"s",
				"length",
				"padString"
			] }],
			description: "Pads from the start of `s` with `padString` (multiple times, if needed) until the resulting string reaches the given `length`.",
			seeAlso: ["string.padRight"],
			examples: [
				`let { padLeft } = import("string");
"Albert" padLeft 20`,
				`let { padLeft } = import("string");
padLeft("Albert", 20)`,
				`let { padLeft } = import("string");
padLeft("Albert", 20, "-*-")`,
				`let { padLeft } = import("string");
padLeft("Albert", 5)`,
				`let { padLeft } = import("string");
padLeft("Albert", -1)`
			]
		}
	},
	padRight: {
		evaluate: ([str, length, padString], sourceCodeInfo) => {
			assertString(str, sourceCodeInfo);
			assertNumber(length, sourceCodeInfo, { integer: true });
			if (padString !== void 0) assertString(padString, sourceCodeInfo);
			return str.padEnd(length, padString);
		},
		arity: {
			min: 2,
			max: 3
		},
		docs: {
			type: "(String, Number) -> String",
			category: "string",
			returns: { type: "string" },
			args: {
				a: { type: "string" },
				b: { type: "integer" },
				s: { type: "string" },
				length: { type: "integer" },
				padString: { type: "string" }
			},
			variants: [{ argumentNames: ["s", "length"] }, { argumentNames: [
				"s",
				"length",
				"padString"
			] }],
			description: "Pads from the start of `s` with `padString` (multiple times, if needed) until the resulting string reaches the given `length`.",
			seeAlso: ["string.padLeft"],
			examples: [
				`let { padRight } = import("string");
"Albert" padRight 20`,
				`let { padRight } = import("string");
padRight("Albert", 20)`,
				`let { padRight } = import("string");
padRight("Albert", 20, "-*-")`,
				`let { padRight } = import("string");
padRight("Albert", 5)`,
				`let { padRight } = import("string");
padRight("Albert", -1)`
			]
		}
	},
	template: {
		evaluate: ([templateString, ...placeholders], sourceCodeInfo) => {
			assertString(templateString, sourceCodeInfo);
			const templateStrings = templateString.split("||||");
			if (templateStrings.length <= 1) return applyPlaceholders(templateStrings[0], placeholders, sourceCodeInfo);
			else {
				const count = placeholders[0];
				assertNumber(count, sourceCodeInfo, {
					integer: true,
					nonNegative: true
				});
				const stringPlaceholders = [`${count}`, ...placeholders.slice(1)];
				if (templateStrings.length === 2) {
					const placehoder = templateStrings[count === 1 ? 0 : 1];
					return applyPlaceholders(placehoder, stringPlaceholders, sourceCodeInfo);
				} else {
					const placehoder = templateStrings[Math.min(count, templateStrings.length - 1)];
					return applyPlaceholders(placehoder, stringPlaceholders, sourceCodeInfo);
				}
			}
		},
		arity: {
			min: 1,
			max: 10
		},
		docs: {
			type: "(String, ...Unknown) -> String",
			category: "string",
			returns: { type: "string" },
			args: {
				s: { type: "string" },
				params: {
					type: "any",
					rest: true
				}
			},
			variants: [{ argumentNames: ["s", "params"] }],
			description: "Applies placeholders to a string. Support for basic pluralization - see examples. If pluralization is used, first placeholder must be a number.",
			seeAlso: ["str"],
			examples: [
				`let { template } = import("string");
template("Hi, $1 and $2", "Carl", "Larry")`,
				`let { template } = import("string");
template("Hi $1, $2, $3, $4, $5, $6, $7, $8 and $9", "A", "B", "C", "D", "E", "F", "G", "H", "I")`,
				`let { template } = import("string");
template("$1 book||||$1 books", 0)`,
				`let { template } = import("string");
template("$1 book||||$1 books", 1)`,
				`let { template } = import("string");
template("$1 book||||$1 books", 2)`,
				`let { template } = import("string");
template("No book||||$1 book||||$1 books", 0)`,
				`let { template } = import("string");
template("No book||||$1 book||||$1 books", 1)`,
				`let { template } = import("string");
template("No book||||$1 book||||$1 books", 10)`,
				`let { template } = import("string");
template("No book||||One book||||Two books||||Three books||||$1 books", 0)`,
				`let { template } = import("string");
template("No book||||One book||||Two books||||Three books||||$1 books", 1)`,
				`let { template } = import("string");
template("No book||||One book||||Two books||||Three books||||$1 books", 2)`,
				`let { template } = import("string");
template("No book||||One book||||Two books||||Three books||||$1 books", 3)`,
				`let { template } = import("string");
template("No book||||One book||||Two books||||Three books||||$1 books", 4)`
			],
			hideOperatorForm: true
		}
	},
	encodeBase64: {
		evaluate: ([value], sourceCodeInfo) => {
			assertString(value, sourceCodeInfo);
			return btoa(encodeURIComponent(value).replace(/%([0-9A-F]{2})/g, (_match, p1) => {
				return String.fromCharCode(Number.parseInt(p1, 16));
			}));
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String) -> String",
			category: "string",
			returns: { type: "string" },
			args: { s: { type: "string" } },
			variants: [{ argumentNames: ["s"] }],
			description: "Returns a Base64 encoded string from `s`.",
			seeAlso: ["string.decodeBase64"],
			examples: [`let { encodeBase64 } = import("string");
encodeBase64("Albert")`]
		}
	},
	decodeBase64: {
		evaluate: ([value], sourceCodeInfo) => {
			assertString(value, sourceCodeInfo);
			try {
				return decodeURIComponent(Array.prototype.map.call(atob(value), (c) => {
					return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
				}).join(""));
			} catch (error) {
				throw new DvalaError(error, sourceCodeInfo);
			}
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String) -> String",
			category: "string",
			returns: { type: "string" },
			args: { base64string: { type: "string" } },
			variants: [{ argumentNames: ["base64string"] }],
			description: "Returns a Base64 decoded string from `base64string`.",
			seeAlso: ["string.encodeBase64"],
			examples: [`let { decodeBase64 } = import("string");
decodeBase64("QWxiZXJ0IPCfkLs=")`]
		}
	},
	encodeUriComponent: {
		evaluate: ([value], sourceCodeInfo) => {
			assertString(value, sourceCodeInfo);
			return encodeURIComponent(value);
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String) -> String",
			category: "string",
			returns: { type: "string" },
			args: { s: { type: "string" } },
			variants: [{ argumentNames: ["s"] }],
			description: "Returns an escaped `URI` string.",
			seeAlso: ["string.decodeUriComponent"],
			examples: [`let { encodeUriComponent } = import("string");
encodeUriComponent("Hi everyone!?")`]
		}
	},
	decodeUriComponent: {
		evaluate: ([value], sourceCodeInfo) => {
			assertString(value, sourceCodeInfo);
			try {
				return decodeURIComponent(value);
			} catch (error) {
				throw new DvalaError(error, sourceCodeInfo);
			}
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String) -> String",
			category: "string",
			returns: { type: "string" },
			args: { s: { type: "string" } },
			variants: [{ argumentNames: ["s"] }],
			description: "Returns an un-escaped `URI` string.",
			seeAlso: ["string.encodeUriComponent"],
			examples: [`let { decodeUriComponent } = import("string");
decodeUriComponent("Hi%20everyone!%3F%20%F0%9F%91%8D")`]
		}
	},
	capitalize: {
		evaluate: ([str], sourceCodeInfo) => {
			assertString(str, sourceCodeInfo);
			return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String) -> String",
			category: "string",
			returns: { type: "string" },
			args: { s: { type: "string" } },
			variants: [{ argumentNames: ["s"] }],
			description: "Returns `s` with the first character converted to uppercase and the rest to lowercase.",
			seeAlso: ["lowerCase", "upperCase"],
			examples: [
				`let { capitalize } = import("string");
capitalize("albert")`,
				`let { capitalize } = import("string");
capitalize("ALBERT")`,
				`let { capitalize } = import("string");
capitalize("aLBERT")`,
				`let { capitalize } = import("string");
capitalize("")`
			]
		}
	}
};
const doubleDollarRegexp = /\$\$/g;
function applyPlaceholders(templateString, placeholders, sourceCodeInfo) {
	for (let i = 0; i < 9; i += 1) {
		const re = new RegExp(`(\\$\\$|[^$]|^)\\$${i + 1}`, "g");
		if (re.test(templateString)) {
			const placeHolder = asStringOrNumber(placeholders[i], sourceCodeInfo);
			templateString = templateString.replace(re, `$1${placeHolder}`);
		}
	}
	templateString = templateString.replace(doubleDollarRegexp, "$");
	return templateString;
}
const stringUtilsModule = {
	name: "string",
	description: "String manipulation: pad, repeat, capitalize, camelCase, and word wrapping.",
	functions: stringUtilsFunctions,
	source: string_default,
	sourcePath: "packages/dvala-engine/src/builtin/modules/string/string.dvala",
	docs: moduleDocsFromFunctions(stringUtilsFunctions)
};
//#endregion
//#region src/builtin/modules/test/test.dvala
var test_default = "{\n  describe: (name, body) -> do\n    perform(@test.pushDescribe, name);\n    body();\n    perform(@test.popDescribe, null);\n  end,\n  skip: macro(expr) -> quote\n    do\n      perform(@test.pushSkip, null);\n      $^{expr};\n      perform(@test.popSkip, null);\n    end;\n  end,\n};\n";
//#endregion
//#region src/builtin/modules/test/docs.ts
const moduleDocs$1 = {
	test: {
		type: "(String, (() -> Unknown)) -> @{test.register} Null",
		category: "test",
		description: "Registers a test case with a name and a function body. The test is collected by the test runner and executed in isolation.",
		returns: { type: "null" },
		args: {
			name: {
				type: "string",
				description: "The name of the test case."
			},
			body: {
				type: "function",
				description: "A zero-argument function containing the test assertions."
			}
		},
		variants: [{ argumentNames: ["name", "body"] }],
		examples: ["let { test } = import(\"test\");\ntest(\"addition\", -> assertEqual(1 + 1, 2))"],
		seeAlso: ["test.describe", "test.skip"],
		hideOperatorForm: true
	},
	describe: {
		type: "(String, (() -> Unknown)) -> @{test.register} Null",
		category: "test",
		description: "Groups related tests under a descriptive label. Can be nested. The body function is executed immediately to collect the tests within.",
		returns: { type: "null" },
		args: {
			name: {
				type: "string",
				description: "The name of the test group."
			},
			body: {
				type: "function",
				description: "A zero-argument function containing test() and/or nested describe() calls."
			}
		},
		variants: [{ argumentNames: ["name", "body"] }],
		examples: ["let { test, describe } = import(\"test\");\ndescribe(\"math\", -> test(\"abs\", -> assertEqual(abs(-1), 1)))"],
		seeAlso: ["test.test", "test.skip"],
		hideOperatorForm: true
	},
	skip: {
		type: "(Unknown) -> @{test.register} Null",
		category: "test",
		description: "A macro that marks its argument as skipped. Works with both `test` and `describe` — any tests registered inside the expression are reported but not executed. Use with the `#` prefix: `#skip test(...)` or `#skip describe(...)`.",
		returns: { type: "null" },
		args: { expr: {
			type: "any",
			description: "A test or describe expression to skip (received as unevaluated AST)."
		} },
		variants: [{ argumentNames: ["expr"] }],
		examples: ["let { test, skip } = import(\"test\");\n#skip test(\"not ready yet\", -> assertEqual(1, 2))"],
		seeAlso: ["test.test", "test.describe"],
		hideOperatorForm: true
	}
};
//#endregion
//#region src/builtin/modules/test/index.ts
function createTestCollector() {
	return {
		tests: [],
		describeStack: [],
		skipDepth: 0
	};
}
/**
* Creates a test module bound to a specific collector.
* Each .test.dvala file gets its own collector + module instance,
* so test registrations are isolated per file.
*/
function createTestModule(collector) {
	const fullName = (name) => {
		return [...collector.describeStack, name].join(" > ");
	};
	const testNormalExpressions = {
		test: {
			evaluate: ([name, body], sourceCodeInfo) => {
				assertString(name, sourceCodeInfo);
				if (!isDvalaFunction(body)) throw new TypeError$1("Second argument to test must be a function", sourceCodeInfo ?? void 0);
				collector.tests.push({
					fullName: fullName(name),
					body,
					skip: collector.skipDepth > 0
				});
				return null;
			},
			arity: {
				min: 2,
				max: 2
			}
		},
		describe: {
			/* v8 ignore next 1 */
			evaluate: () => null,
			arity: {
				min: 2,
				max: 2
			}
		}
	};
	for (const [key, docs] of Object.entries(moduleDocs$1)) if (testNormalExpressions[key]) testNormalExpressions[key].docs = docs;
	return {
		name: "test",
		description: "Lightweight test framework with describe, it, and assertion helpers.",
		functions: testNormalExpressions,
		source: test_default,
		sourcePath: "packages/dvala-engine/src/builtin/modules/test/test.dvala",
		docs: moduleDocs$1
	};
}
//#endregion
//#region src/builtin/modules/time/index.ts
const timeFunctions = {
	epochToIsoDate: {
		evaluate: ([ms], sourceCodeInfo) => {
			assertNumber(ms, sourceCodeInfo);
			return new Date(ms).toISOString();
		},
		arity: toFixedArity(1),
		docs: {
			type: "(Number) -> String",
			category: "time",
			returns: { type: "string" },
			args: { ms: { type: "number" } },
			variants: [{ argumentNames: ["ms"] }],
			description: "Returns IOS date time string from `ms` (milliseconds elapsed since the UNIX epoch).",
			seeAlso: ["time.isoDateToEpoch"],
			examples: ["let { epochToIsoDate } = import(\"time\");\nepochToIsoDate(1649756230899)", "let { epochToIsoDate } = import(\"time\");\nepochToIsoDate(0)"]
		}
	},
	isoDateToEpoch: {
		evaluate: ([dateTime], sourceCodeInfo) => {
			assertString(dateTime, sourceCodeInfo);
			const ms = new Date(dateTime).valueOf();
			assertNumber(ms, sourceCodeInfo, { finite: true });
			return ms;
		},
		arity: toFixedArity(1),
		docs: {
			type: "(String) -> Number",
			category: "time",
			returns: { type: "number" },
			args: { iso: { type: "string" } },
			variants: [{ argumentNames: ["iso"] }],
			description: "Returns milliseconds elapsed since the UNIX epoch to `iso`.",
			seeAlso: ["time.epochToIsoDate"],
			examples: ["let { isoDateToEpoch } = import(\"time\");\nisoDateToEpoch(\"2022-04-12T09:37:10.899Z\")", "let { isoDateToEpoch } = import(\"time\");\nisoDateToEpoch(\"1980-01-01\")"]
		}
	}
};
const timeModule = {
	name: "time",
	description: "Date and time formatting, parsing, and calendar utilities.",
	functions: timeFunctions,
	source: "{}",
	docs: moduleDocsFromFunctions(timeFunctions)
};
//#endregion
//#region src/builtin/modules/vector/docs.ts
const moduleDocs = {
	movingFn: {
		type: "(Number[], Number, (Number[] -> Number)) -> Number[]",
		category: "vector",
		returns: { type: "array" },
		args: {
			arr: { type: "array" },
			windowSize: {
				type: "number",
				description: "The size of the moving window."
			},
			fn: { type: "function" }
		},
		variants: [{ argumentNames: [
			"arr",
			"windowSize",
			"fn"
		] }],
		description: "Returns the result of applying `fn` to each moving window of size `windowSize` in `arr`.",
		seeAlso: ["vector.runningFn", "vector.movingMean"],
		examples: [
			"let { movingFn } = import(\"vector\");\nmovingFn([1, 2, 3], 2, sum)",
			"let { movingFn } = import(\"vector\");\nmovingFn([1, 2, 3], 1, sum)",
			"let { movingFn } = import(\"vector\");\nmovingFn([1, 2, 3], 3, sum)"
		]
	},
	runningFn: {
		type: "(Number[], (Number[] -> Number)) -> Number[]",
		category: "vector",
		returns: { type: "array" },
		args: {
			a: { type: "array" },
			b: { type: "function" }
		},
		variants: [{ argumentNames: ["a", "b"] }],
		description: "Returns the result of applying `b` to each element of `a`.",
		seeAlso: ["vector.movingFn", "vector.runningMean"],
		examples: [
			"let { runningFn } = import(\"vector\");\nrunningFn([1, 2, 3], sum)",
			"let { runningFn } = import(\"vector\");\nrunningFn([1, 2, 3], max)",
			"let { runningFn } = import(\"vector\");\nrunningFn([1, 2, 3], min)"
		]
	},
	mean: {
		type: "(Number[]) -> Number",
		category: "vector",
		returns: { type: "number" },
		args: { vector: {
			type: "vector",
			description: "The vector to calculate the mean of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		description: "Returns the arithmetic **mean** of all elements in the `vector`. Throws for an empty vector.",
		seeAlso: [
			"vector.median",
			"sum",
			"prod",
			"vector.movingMean",
			"vector.centeredMovingMean",
			"vector.runningMean",
			"vector.geometricMean",
			"vector.harmonicMean",
			"vector.rms",
			"vector.mode"
		],
		examples: ["let { mean } = import(\"vector\");\nmean([1, 2, 3, 4, 5])", "let { mean } = import(\"vector\");\nmean([1, -2, 3])"]
	},
	median: {
		type: "(Number[]) -> Number",
		category: "vector",
		returns: { type: "number" },
		args: { vector: {
			type: "vector",
			description: "The vector to calculate the median of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		description: "Returns the **median** of all elements in the `vector`. For even-length vectors, returns the average of the two middle values. Throws for an empty vector.",
		seeAlso: [
			"vector.mean",
			"sum",
			"prod",
			"vector.movingMedian",
			"vector.centeredMovingMedian",
			"vector.runningMedian",
			"vector.mode",
			"vector.quartiles",
			"vector.percentile",
			"vector.iqr",
			"vector.medad"
		],
		examples: [
			"let { median } = import(\"vector\");\nmedian([1, 2, 3, 4, 5])",
			"let { median } = import(\"vector\");\nmedian([1, 2, 3, 4])",
			"let { median } = import(\"vector\");\nmedian([3, 1, 4, 1, 5])"
		]
	},
	movingMean: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Returns the **moving mean** of the `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving mean** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: ["let { movingMean } = import(\"vector\");\nmovingMean([1, 2, 3, 4, 5], 3)", "let { movingMean } = import(\"vector\");\nmovingMean([1, 2, 3, 4, 5], 5)"],
		seeAlso: [
			"vector.movingFn",
			"vector.mean",
			"vector.centeredMovingMean",
			"vector.runningMean"
		]
	},
	centeredMovingMean: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Returns the **centered moving mean** of the `vector` with a given window size.",
		returns: { type: "array" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving mean** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the centered moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: [
			"let { centeredMovingMean } = import(\"vector\");\ncenteredMovingMean([1, 2, 3, 4, 5], 3)",
			"let { centeredMovingMean } = import(\"vector\");\ncenteredMovingMean([1, 2, 3, 4, 5], 3, 0, 10)",
			"let { centeredMovingMean } = import(\"vector\");\ncenteredMovingMean([1, 2, 3, 4, 5], 3, 10)"
		],
		seeAlso: [
			"vector.mean",
			"vector.movingMean",
			"vector.runningMean"
		]
	},
	runningMean: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Returns the **running mean** of the `vector`.",
		returns: { type: "vector" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running mean** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningMean } = import(\"vector\");\nrunningMean([1, 2, 3, 4, 5])"],
		seeAlso: [
			"vector.runningFn",
			"vector.mean",
			"vector.movingMean",
			"vector.centeredMovingMean"
		]
	},
	geometricMean: {
		type: "(Number[]) -> Number",
		category: "vector",
		description: "Returns the **geometric mean** of all elements in the `vector`.",
		returns: { type: "number" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **geometric mean** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { geometricMean } = import(\"vector\");\ngeometricMean([1, 2, 3])", "let { geometricMean } = import(\"vector\");\ngeometricMean([1, 2, 9])"],
		seeAlso: [
			"vector.movingGeometricMean",
			"vector.centeredMovingGeometricMean",
			"vector.runningGeometricMean",
			"vector.mean",
			"vector.harmonicMean"
		]
	},
	movingGeometricMean: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Returns the **moving geometric mean** of the `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving geometric mean** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: ["let { movingGeometricMean } = import(\"vector\");\nmovingGeometricMean([1, 2, 3, 4, 5], 3)", "let { movingGeometricMean } = import(\"vector\");\nmovingGeometricMean([1, 2, 3, 4, 5], 5)"],
		seeAlso: [
			"vector.geometricMean",
			"vector.centeredMovingGeometricMean",
			"vector.runningGeometricMean"
		]
	},
	centeredMovingGeometricMean: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Returns the **centered moving geometric mean** of the `vector` with a given window size.",
		returns: { type: "array" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving geometric mean** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the centered moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: [
			"let { centeredMovingGeometricMean } = import(\"vector\");\ncenteredMovingGeometricMean([1, 2, 3, 4, 5], 3)",
			"let { centeredMovingGeometricMean } = import(\"vector\");\ncenteredMovingGeometricMean([1, 2, 3, 4, 5], 3, 0, 10)",
			"let { centeredMovingGeometricMean } = import(\"vector\");\ncenteredMovingGeometricMean([1, 2, 3, 4, 5], 3, 10)"
		],
		seeAlso: [
			"vector.geometricMean",
			"vector.movingGeometricMean",
			"vector.runningGeometricMean"
		]
	},
	runningGeometricMean: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Returns the **running geometric mean** of the `vector`.",
		returns: { type: "vector" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running geometric mean** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningGeometricMean } = import(\"vector\");\nrunningGeometricMean([1, 2, 3, 4, 5])"],
		seeAlso: [
			"vector.geometricMean",
			"vector.movingGeometricMean",
			"vector.centeredMovingGeometricMean"
		]
	},
	harmonicMean: {
		type: "(Number[]) -> Number",
		category: "vector",
		description: "Returns the **harmonic mean** of all elements in the `vector`.",
		returns: { type: "number" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **harmonic mean** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { harmonicMean } = import(\"vector\");\nharmonicMean([1, 2, 3])", "let { harmonicMean } = import(\"vector\");\nharmonicMean([1, 2, 9])"],
		seeAlso: [
			"vector.movingHarmonicMean",
			"vector.centeredMovingHarmonicMean",
			"vector.runningHarmonicMean",
			"vector.mean",
			"vector.geometricMean"
		]
	},
	movingHarmonicMean: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Returns the **moving harmonic mean** of the `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving harmonic mean** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: ["let { movingHarmonicMean } = import(\"vector\");\nmovingHarmonicMean([1, 2, 3, 4, 5], 3)", "let { movingHarmonicMean } = import(\"vector\");\nmovingHarmonicMean([1, 2, 3, 4, 5], 5)"],
		seeAlso: [
			"vector.harmonicMean",
			"vector.centeredMovingHarmonicMean",
			"vector.runningHarmonicMean"
		]
	},
	centeredMovingHarmonicMean: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Returns the **centered moving harmonic mean** of the `vector` with a given window size.",
		returns: { type: "array" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving harmonic mean** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the centered moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: [
			"let { centeredMovingHarmonicMean } = import(\"vector\");\ncenteredMovingHarmonicMean([1, 2, 3, 4, 5], 3)",
			"let { centeredMovingHarmonicMean } = import(\"vector\");\ncenteredMovingHarmonicMean([1, 2, 3, 4, 5], 3, 0, 10)",
			"let { centeredMovingHarmonicMean } = import(\"vector\");\ncenteredMovingHarmonicMean([1, 2, 3, 4, 5], 3, 10)"
		],
		seeAlso: [
			"vector.harmonicMean",
			"vector.movingHarmonicMean",
			"vector.runningHarmonicMean"
		]
	},
	runningHarmonicMean: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Returns the **running harmonic mean** of the `vector`.",
		returns: { type: "vector" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running harmonic mean** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningHarmonicMean } = import(\"vector\");\nrunningHarmonicMean([1, 2, 3, 4, 5])"],
		seeAlso: [
			"vector.harmonicMean",
			"vector.movingHarmonicMean",
			"vector.centeredMovingHarmonicMean"
		]
	},
	movingMedian: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Returns the **moving median** of the `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving median** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: ["let { movingMedian } = import(\"vector\");\nmovingMedian([1, 2, 3, 4, 5], 3)", "let { movingMedian } = import(\"vector\");\nmovingMedian([1, 2, 3, 4, 5], 5)"],
		seeAlso: [
			"vector.median",
			"vector.centeredMovingMedian",
			"vector.runningMedian"
		]
	},
	centeredMovingMedian: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Returns the **centered moving median** of the `vector` with a given window size.",
		returns: { type: "array" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving median** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the centered moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: [
			"let { centeredMovingMedian } = import(\"vector\");\ncenteredMovingMedian([1, 2, 3, 4, 5], 3)",
			"let { centeredMovingMedian } = import(\"vector\");\ncenteredMovingMedian([1, 2, 3, 4, 5], 3, 0, 10)",
			"let { centeredMovingMedian } = import(\"vector\");\ncenteredMovingMedian([1, 2, 3, 4, 5], 3, 10)"
		],
		seeAlso: [
			"vector.median",
			"vector.movingMedian",
			"vector.runningMedian"
		]
	},
	runningMedian: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Returns the **running median** of the `vector`.",
		returns: { type: "vector" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running median** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningMedian } = import(\"vector\");\nrunningMedian([1, 2, 3, 4, 5])"],
		seeAlso: [
			"vector.median",
			"vector.movingMedian",
			"vector.centeredMovingMedian"
		]
	},
	variance: {
		type: "(Number[]) -> Number",
		category: "vector",
		description: "Returns the **variance** of all elements in the `vector`.",
		returns: { type: "number" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **variance** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { variance } = import(\"vector\");\nvariance([1, 2, 3])", "let { variance } = import(\"vector\");\nvariance([1, 2, -3])"],
		seeAlso: [
			"linearAlgebra.cov",
			"vector.movingVariance",
			"vector.centeredMovingVariance",
			"vector.runningVariance",
			"vector.stdev",
			"vector.sampleVariance",
			"vector.mad"
		]
	},
	movingVariance: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Returns the **moving variance** of the `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving variance** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: ["let { movingVariance } = import(\"vector\");\nmovingVariance([1, 2, 3, 4, 5], 3)", "let { movingVariance } = import(\"vector\");\nmovingVariance([1, 2, 3, 4, 5], 5)"],
		seeAlso: [
			"vector.variance",
			"vector.centeredMovingVariance",
			"vector.runningVariance"
		]
	},
	centeredMovingVariance: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Returns the **centered moving variance** of the `vector` with a given window size.",
		returns: { type: "array" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving variance** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the centered moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: [
			"let { centeredMovingVariance } = import(\"vector\");\ncenteredMovingVariance([1, 2, 3, 4, 5], 3)",
			"let { centeredMovingVariance } = import(\"vector\");\ncenteredMovingVariance([1, 2, 3, 4, 5], 3, 1)",
			"let { centeredMovingVariance } = import(\"vector\");\ncenteredMovingVariance([1, 2, 3, 4, 5], 3, 1, 5)",
			"let { centeredMovingVariance } = import(\"vector\");\ncenteredMovingVariance([1, 2, 3, 4, 5], 3, 0, 6)"
		],
		seeAlso: [
			"vector.variance",
			"vector.movingVariance",
			"vector.runningVariance"
		]
	},
	runningVariance: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Returns the **running variance** of the `vector`.",
		returns: { type: "vector" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running variance** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningVariance } = import(\"vector\");\nrunningVariance([1, 2, 3, 4, 5])"],
		seeAlso: [
			"vector.variance",
			"vector.movingVariance",
			"vector.centeredMovingVariance"
		]
	},
	sampleVariance: {
		type: "(Number[]) -> Number",
		category: "vector",
		description: "Returns the sample variance of all elements in the vector.",
		returns: { type: "number" },
		args: { vector: {
			type: "vector",
			description: "Non emtpy vector to calculate the sample variance of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: [
			"let { sampleVariance } = import(\"vector\");\nsampleVariance([1, 2, 3])",
			"let { sampleVariance } = import(\"vector\");\nsampleVariance([1, 2, -3])",
			"let { sampleVariance } = import(\"vector\");\nsampleVariance([1, 2, 3, 4])",
			"let { sampleVariance } = import(\"vector\");\nsampleVariance([1, 2, -3, 4])",
			"let { sampleVariance } = import(\"vector\");\nsampleVariance([1, 2, 3, 40, 50])"
		],
		seeAlso: [
			"vector.movingSampleVariance",
			"vector.centeredMovingSampleVariance",
			"vector.runningSampleVariance",
			"vector.variance",
			"vector.sampleStdev"
		]
	},
	movingSampleVariance: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Returns the **moving sample variance** of the `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving sample variance** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: ["let { movingSampleVariance } = import(\"vector\");\nmovingSampleVariance([1, 2, 3, 4, 5], 3)", "let { movingSampleVariance } = import(\"vector\");\nmovingSampleVariance([1, 2, 3, 4, 5], 5)"],
		seeAlso: [
			"vector.sampleVariance",
			"vector.centeredMovingSampleVariance",
			"vector.runningSampleVariance"
		]
	},
	centeredMovingSampleVariance: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Returns the **centered moving sample variance** of the `vector` with a given window size.",
		returns: { type: "array" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving sample variance** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the centered moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: [
			"let { centeredMovingSampleVariance } = import(\"vector\");\ncenteredMovingSampleVariance([1, 2, 3, 4, 5], 3)",
			"let { centeredMovingSampleVariance } = import(\"vector\");\ncenteredMovingSampleVariance([1, 2, 3, 4, 5], 3, 1)",
			"let { centeredMovingSampleVariance } = import(\"vector\");\ncenteredMovingSampleVariance([1, 2, 3, 4, 5], 3, 1, 5)",
			"let { centeredMovingSampleVariance } = import(\"vector\");\ncenteredMovingSampleVariance([1, 2, 3, 4, 5], 3, 0, 6)"
		],
		seeAlso: [
			"vector.sampleVariance",
			"vector.movingSampleVariance",
			"vector.runningSampleVariance"
		]
	},
	runningSampleVariance: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Returns the **running sample variance** of the `vector`.",
		returns: { type: "array" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running sample variance** of. First element in result is `null` since **sample variance** is not defined for a single element."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningSampleVariance } = import(\"vector\");\nrunningSampleVariance([1, 2, 3, 4, 5])"],
		seeAlso: [
			"vector.sampleVariance",
			"vector.movingSampleVariance",
			"vector.centeredMovingSampleVariance"
		]
	},
	stdev: {
		type: "(Number[]) -> Number",
		category: "vector",
		description: "Returns the standard deviation of all elements in the vector.",
		returns: { type: "number" },
		args: { vector: {
			type: "vector",
			description: "Non emtpy vector to calculate the standard deviation of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: [
			"let { stdev } = import(\"vector\");\nstdev([1, 2, 3])",
			"let { stdev } = import(\"vector\");\nstdev([1, 2, -3])",
			"let { stdev } = import(\"vector\");\nstdev([1, 2, 3, 4])",
			"let { stdev } = import(\"vector\");\nstdev([1, 2, -3, 4])",
			"let { stdev } = import(\"vector\");\nstdev([1, 2, 3, 40, 50])"
		],
		seeAlso: [
			"vector.movingStdev",
			"vector.centeredMovingStdev",
			"vector.runningStdev",
			"vector.variance",
			"vector.sampleStdev",
			"vector.rms",
			"vector.mad"
		]
	},
	movingStdev: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Returns the **moving standard deviation** of the `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving standard deviation** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: ["let { movingStdev } = import(\"vector\");\nmovingStdev([1, 2, 3, 4, 5], 3)", "let { movingStdev } = import(\"vector\");\nmovingStdev([1, 2, 3, 4, 5], 5)"],
		seeAlso: [
			"vector.stdev",
			"vector.centeredMovingStdev",
			"vector.runningStdev"
		]
	},
	centeredMovingStdev: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Returns the **centered moving standard deviation** of the `vector` with a given window size.",
		returns: { type: "array" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving standard deviation** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the centered moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: [
			"let { centeredMovingStdev } = import(\"vector\");\ncenteredMovingStdev([1, 2, 3, 4, 5], 3)",
			"let { centeredMovingStdev } = import(\"vector\");\ncenteredMovingStdev([1, 2, 3, 4, 5], 3, 1)",
			"let { centeredMovingStdev } = import(\"vector\");\ncenteredMovingStdev([1, 2, 3, 4, 5], 3, 1, 5)",
			"let { centeredMovingStdev } = import(\"vector\");\ncenteredMovingStdev([1, 2, 3, 4, 5], 3, 0, 6)"
		],
		seeAlso: [
			"vector.stdev",
			"vector.movingStdev",
			"vector.runningStdev"
		]
	},
	runningStdev: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Returns the **running standard deviation** of the `vector`.",
		returns: { type: "vector" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running standard deviation** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningStdev } = import(\"vector\");\nrunningStdev([1, 2, 3, 4, 5])"],
		seeAlso: [
			"vector.stdev",
			"vector.movingStdev",
			"vector.centeredMovingStdev"
		]
	},
	sampleStdev: {
		type: "(Number[]) -> Number",
		category: "vector",
		description: "Returns the sample standard deviation of all elements in the vector.",
		returns: { type: "number" },
		args: { vector: {
			type: "vector",
			description: "Non emtpy vector to calculate the sample standard deviation of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: [
			"let { sampleStdev } = import(\"vector\");\nsampleStdev([1, 2, 3])",
			"let { sampleStdev } = import(\"vector\");\nsampleStdev([1, 2, -3])",
			"let { sampleStdev } = import(\"vector\");\nsampleStdev([1, 2, 3, 4])",
			"let { sampleStdev } = import(\"vector\");\nsampleStdev([1, 2, -3, 4])",
			"let { sampleStdev } = import(\"vector\");\nsampleStdev([1, 2, 3, 40, 50])"
		],
		seeAlso: [
			"vector.movingSampleStdev",
			"vector.centeredMovingSampleStdev",
			"vector.runningSampleStdev",
			"vector.stdev",
			"vector.sampleVariance"
		]
	},
	movingSampleStdev: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Returns the **moving sample standard deviation** of the `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving sample standard deviation** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: ["let { movingSampleStdev } = import(\"vector\");\nmovingSampleStdev([1, 2, 3, 4, 5], 3)", "let { movingSampleStdev } = import(\"vector\");\nmovingSampleStdev([1, 2, 3, 4, 5], 5)"],
		seeAlso: [
			"vector.sampleStdev",
			"vector.centeredMovingSampleStdev",
			"vector.runningSampleStdev"
		]
	},
	centeredMovingSampleStdev: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Returns the **centered moving sample standard deviation** of the `vector` with a given window size.",
		returns: { type: "array" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving sample standard deviation** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the centered moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: [
			"let { centeredMovingSampleStdev } = import(\"vector\");\ncenteredMovingSampleStdev([1, 2, 3, 4, 5], 3)",
			"let { centeredMovingSampleStdev } = import(\"vector\");\ncenteredMovingSampleStdev([1, 2, 3, 4, 5], 3, 1)",
			"let { centeredMovingSampleStdev } = import(\"vector\");\ncenteredMovingSampleStdev([1, 2, 3, 4, 5], 3, 1, 5)",
			"let { centeredMovingSampleStdev } = import(\"vector\");\ncenteredMovingSampleStdev([1, 2, 3, 4, 5], 3, 0, 6)"
		],
		seeAlso: [
			"vector.sampleStdev",
			"vector.movingSampleStdev",
			"vector.runningSampleStdev"
		]
	},
	runningSampleStdev: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Returns the **running sample standard deviation** of the `vector`.",
		returns: { type: "array" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running sample standard deviation** of. First element in result is `null` since **sample standard deviation** is not defined for a single element."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningSampleStdev } = import(\"vector\");\nrunningSampleStdev([1, 2, 3, 4, 5])"],
		seeAlso: [
			"vector.sampleStdev",
			"vector.movingSampleStdev",
			"vector.centeredMovingSampleStdev"
		]
	},
	iqr: {
		type: "(Number[]) -> Number",
		category: "vector",
		description: "Calculates the **interquartile range** of a `vector`. Returns the difference between the third and first quartiles.",
		returns: { type: "number" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **interquartile range** of. Minimum length is 4."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: [
			"let { iqr } = import(\"vector\");\niqr([1, 2, 3, 4])",
			"let { iqr } = import(\"vector\");\niqr([5, 4, 3, 2, 1, 2, 3, 4, 5])",
			"let { iqr } = import(\"vector\");\niqr(range(1, 1000))",
			"let { iqr } = import(\"vector\");\niqr(map(range(1000), -> 1e6 / ($ + 1) ^ 2))",
			"let { iqr } = import(\"vector\");\nlet { ln } = import(\"math\");\niqr(map(range(1000), -> ln($ + 1)))"
		],
		seeAlso: [
			"vector.movingIqr",
			"vector.centeredMovingIqr",
			"vector.runningIqr",
			"vector.quartiles",
			"vector.median",
			"vector.mad",
			"vector.medad",
			"vector.isOutliers",
			"vector.outliers"
		]
	},
	movingIqr: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Calculates the **moving interquartile range** of a `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving interquartile range** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: [
			"let { movingIqr } = import(\"vector\");\nmovingIqr([1, 2, 4, 7, 11, 16], 4)",
			"let { movingIqr } = import(\"vector\");\nmovingIqr([1, 2, 4, 7, 11, 16], 5)",
			"let { movingIqr } = import(\"vector\");\nmovingIqr([1, 2, 4, 7, 11, 16], 6)"
		],
		seeAlso: [
			"vector.iqr",
			"vector.centeredMovingIqr",
			"vector.runningIqr"
		]
	},
	centeredMovingIqr: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Calculates the **centered moving interquartile range** of a `vector` with a given window size.",
		returns: { type: "array" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving interquartile range** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: ["let { centeredMovingIqr } = import(\"vector\");\ncenteredMovingIqr([1, 2, 4, 7, 11, 16], 4)", "let { centeredMovingIqr } = import(\"vector\");\ncenteredMovingIqr([1, 2, 4, 7, 11, 16], 4, 0, 0)"],
		seeAlso: [
			"vector.iqr",
			"vector.movingIqr",
			"vector.runningIqr"
		]
	},
	runningIqr: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Calculates the **running interquartile range** of a `vector`. First three element in result is `null` since **running interquartile range** is not defined for less than four elements.",
		returns: {
			type: "number",
			array: true
		},
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running interquartile range** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningIqr } = import(\"vector\");\nrunningIqr([1, 2, 3, 4, 5, 6])", "let { runningIqr } = import(\"vector\");\nrunningIqr([-1, -2, -3, 1, 2, 3])"],
		seeAlso: [
			"vector.iqr",
			"vector.movingIqr",
			"vector.centeredMovingIqr"
		]
	},
	movingSum: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Returns the **moving sum** of the `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving sum** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: ["let { movingSum } = import(\"vector\");\nmovingSum([1, 2, 3, 4, 5], 3)", "let { movingSum } = import(\"vector\");\nmovingSum([1, 2, 3, 4, 5], 5)"],
		seeAlso: [
			"sum",
			"vector.centeredMovingSum",
			"vector.runningSum"
		]
	},
	centeredMovingSum: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Returns the **centered moving sum** of the `vector` with a given window size.",
		returns: { type: "array" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving sum** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the centered moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: [
			"let { centeredMovingSum } = import(\"vector\");\ncenteredMovingSum([1, 2, 3, 4, 5], 3)",
			"let { centeredMovingSum } = import(\"vector\");\ncenteredMovingSum([1, 2, 3, 4, 5], 3, 0, 0)",
			"let { centeredMovingSum } = import(\"vector\");\ncenteredMovingSum([1, 2, 3, 4, 5], 3, 10)"
		],
		seeAlso: [
			"sum",
			"vector.movingSum",
			"vector.runningSum"
		]
	},
	runningSum: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Returns the **running sum** of the `vector`.",
		returns: { type: "vector" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running sum** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningSum } = import(\"vector\");\nrunningSum([1, 2, 3])", "let { runningSum } = import(\"vector\");\nrunningSum([1, -2, -3])"],
		seeAlso: [
			"sum",
			"vector.movingSum",
			"vector.centeredMovingSum",
			"cumsum"
		]
	},
	movingProd: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Returns the **moving product** of the `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving product** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: ["let { movingProd } = import(\"vector\");\nmovingProd([1, 2, 3, 4, 5], 3)", "let { movingProd } = import(\"vector\");\nmovingProd([1, 2, 3, 4, 5], 5)"],
		seeAlso: [
			"prod",
			"vector.centeredMovingProd",
			"vector.runningProd"
		]
	},
	centeredMovingProd: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Returns the **centered moving product** of the `vector` with a given window size.",
		returns: { type: "array" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving product** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: ["let { centeredMovingProd } = import(\"vector\");\ncenteredMovingProd([1, 2, 3, 4, 5], 3)", "let { centeredMovingProd } = import(\"vector\");\ncenteredMovingProd([1, 2, 3, 4, 5], 3, 0, 0)"],
		seeAlso: [
			"prod",
			"vector.movingProd",
			"vector.runningProd"
		]
	},
	runningProd: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Returns the **running product** of the `vector`.",
		returns: { type: "vector" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running product** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningProd } = import(\"vector\");\nrunningProd([1, 2, 3, 4, 5])", "let { runningProd } = import(\"vector\");\nrunningProd([1, -2, -3])"],
		seeAlso: [
			"prod",
			"vector.movingProd",
			"vector.centeredMovingProd",
			"cumprod"
		]
	},
	span: {
		type: "(Number[]) -> Number",
		category: "vector",
		description: "Returns the difference between the maximum and minimum values in a vector.",
		returns: { type: "number" },
		args: { vector: {
			type: "vector",
			description: "The vector to calculate the span of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: [
			"let { span } = import(\"vector\");\nspan([1, 2, 3])",
			"let { span } = import(\"vector\");\nspan([1, 1, 2, 3, 3])",
			"let { span } = import(\"vector\");\nspan([1, 2, -3])"
		],
		seeAlso: [
			"vector.movingSpan",
			"vector.centeredMovingSpan",
			"vector.runningSpan",
			"min",
			"max"
		]
	},
	movingSpan: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Calculates the **moving span** of a `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving span** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: [
			"let { movingSpan } = import(\"vector\");\nmovingSpan([1, 2, 4, 7, 11, 16], 4)",
			"let { movingSpan } = import(\"vector\");\nmovingSpan([1, 2, 4, 7, 11, 16], 5)",
			"let { movingSpan } = import(\"vector\");\nmovingSpan([1, 2, 4, 7, 11, 16], 6)"
		],
		seeAlso: [
			"vector.span",
			"vector.centeredMovingSpan",
			"vector.runningSpan"
		]
	},
	centeredMovingSpan: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Calculates the **centered moving span** of a `vector` with a given window size. The result is padded with `leftPadding` on the left and right.",
		returns: { type: "array" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving span** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			leftPadding: {
				type: "number",
				description: "The value to pad the result with on the left."
			},
			rightPadding: {
				type: "number",
				description: "The value to pad the result with on the right."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: ["let { centeredMovingSpan } = import(\"vector\");\ncenteredMovingSpan([1, 2, 4, 7, 11, 16], 4)", "let { centeredMovingSpan } = import(\"vector\");\ncenteredMovingSpan([1, 2, 4, 7, 11, 16], 3, 0, 100)"],
		seeAlso: [
			"vector.span",
			"vector.movingSpan",
			"vector.runningSpan"
		]
	},
	runningSpan: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Calculates the **running span** of a `vector` with a given window size.",
		returns: { type: "vector" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running span** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningSpan } = import(\"vector\");\nrunningSpan([1, 2, 4])"],
		seeAlso: [
			"vector.span",
			"vector.movingSpan",
			"vector.centeredMovingSpan"
		]
	},
	skewness: {
		type: "(Number[]) -> Number",
		category: "vector",
		description: "Calculates the **skewness** of a `vector`. Returns the third standardized moment.",
		returns: { type: "number" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **skewness** of. Minimum length is 3."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { skewness } = import(\"vector\");\nskewness([1, 2, 3, 6, 20])", "let { skewness } = import(\"vector\");\nskewness([1, 2, 2, 3])"],
		seeAlso: [
			"vector.movingSkewness",
			"vector.centeredMovingSkewness",
			"vector.runningSkewness",
			"vector.kurtosis",
			"vector.sampleSkewness",
			"vector.excessKurtosis"
		]
	},
	movingSkewness: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Calculates the **moving skewness** of a `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving skewness** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: ["let { movingSkewness } = import(\"vector\");\nmovingSkewness([1, 2, 4, 7, 11, 16], 4)", "let { movingSkewness } = import(\"vector\");\nmovingSkewness([1, 2, 4, 7, 11, 16], 5)"],
		seeAlso: [
			"vector.skewness",
			"vector.centeredMovingSkewness",
			"vector.runningSkewness"
		]
	},
	centeredMovingSkewness: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Calculates the **centered moving skewness** of a `vector` with a given window size and padding.",
		returns: { type: "array" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving skewness** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: ["let { centeredMovingSkewness } = import(\"vector\");\ncenteredMovingSkewness([1, 2, 4, 7, 11, 16], 4)", "let { centeredMovingSkewness } = import(\"vector\");\ncenteredMovingSkewness([1, 2, 4, 7, 11, 16], 4, 0, 0)"],
		seeAlso: [
			"vector.skewness",
			"vector.movingSkewness",
			"vector.runningSkewness"
		]
	},
	runningSkewness: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Calculates the **running skewness** of a `vector` with a given window size. First two element in result is `null` since **running skewness** is not defined for less than three elements.",
		returns: { type: "array" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running skewness** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningSkewness } = import(\"vector\");\nrunningSkewness([1, 2, 4, 7, 11])"],
		seeAlso: [
			"vector.skewness",
			"vector.movingSkewness",
			"vector.centeredMovingSkewness"
		]
	},
	sampleSkewness: {
		type: "(Number[]) -> Number",
		category: "vector",
		description: "Calculates the **sample skewness** of a `vector`. Returns the third standardized moment.",
		returns: { type: "number" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **sample skewness** of. Minimum length is 3."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { sampleSkewness } = import(\"vector\");\nsampleSkewness([1, 2, 3, 6, 20])", "let { sampleSkewness } = import(\"vector\");\nsampleSkewness([1, 2, 2, 3])"],
		seeAlso: [
			"vector.movingSampleSkewness",
			"vector.centeredMovingSampleSkewness",
			"vector.runningSampleSkewness",
			"vector.skewness",
			"vector.sampleKurtosis"
		]
	},
	movingSampleSkewness: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Calculates the **moving sample skewness** of a `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving sample skewness** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: ["let { movingSampleSkewness } = import(\"vector\");\nmovingSampleSkewness([1, 2, 4, 7, 11, 16], 4)", "let { movingSampleSkewness } = import(\"vector\");\nmovingSampleSkewness([1, 2, 4, 7, 11, 16], 5)"],
		seeAlso: [
			"vector.sampleSkewness",
			"vector.centeredMovingSampleSkewness",
			"vector.runningSampleSkewness"
		]
	},
	centeredMovingSampleSkewness: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Calculates the **centered moving sample skewness** of a `vector` with a given window size and padding.",
		returns: { type: "array" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving sample skewness** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: ["let { centeredMovingSampleSkewness } = import(\"vector\");\ncenteredMovingSampleSkewness([1, 2, 4, 7, 11, 16], 4)", "let { centeredMovingSampleSkewness } = import(\"vector\");\ncenteredMovingSampleSkewness([1, 2, 4, 7, 11, 16], 3, 0, 100)"],
		seeAlso: [
			"vector.sampleSkewness",
			"vector.movingSampleSkewness",
			"vector.runningSampleSkewness"
		]
	},
	runningSampleSkewness: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Calculates the **running sample skewness** of a `vector` with a given window size. First two element in result is `null` since **running sample skewness** is not defined for less than three elements.",
		returns: { type: "array" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running sample skewness** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningSampleSkewness } = import(\"vector\");\nrunningSampleSkewness([1, 2, 4, 7, 11])"],
		seeAlso: [
			"vector.sampleSkewness",
			"vector.movingSampleSkewness",
			"vector.centeredMovingSampleSkewness"
		]
	},
	excessKurtosis: {
		type: "(Number[]) -> Number",
		category: "vector",
		description: "Calculates the **excess kurtosis** of a `vector`. Returns the third standardized moment.",
		returns: { type: "number" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **excess kurtosis** of. Minimum length is 3."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { excessKurtosis } = import(\"vector\");\nexcessKurtosis([1, 2, 3, 6, 20])", "let { excessKurtosis } = import(\"vector\");\nexcessKurtosis([1, 2, 2, 3])"],
		seeAlso: [
			"vector.movingExcessKurtosis",
			"vector.centeredMovingExcessKurtosis",
			"vector.runningExcessKurtosis",
			"vector.kurtosis",
			"vector.sampleExcessKurtosis",
			"vector.skewness"
		]
	},
	movingExcessKurtosis: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Calculates the **moving excess kurtosis** of a `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving excess kurtosis** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: ["let { movingExcessKurtosis } = import(\"vector\");\nmovingExcessKurtosis([1, 2, 4, 7, 11, 16], 4)", "let { movingExcessKurtosis } = import(\"vector\");\nmovingExcessKurtosis([1, 2, 4, 7, 11, 16], 5)"],
		seeAlso: [
			"vector.excessKurtosis",
			"vector.centeredMovingExcessKurtosis",
			"vector.runningExcessKurtosis"
		]
	},
	centeredMovingExcessKurtosis: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Calculates the **centered moving excess kurtosis** of a `vector` with a given window size and padding.",
		returns: { type: "array" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving excess kurtosis** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: ["let { centeredMovingExcessKurtosis } = import(\"vector\");\ncenteredMovingExcessKurtosis([1, 2, 4, 7, 11, 16], 4)", "let { centeredMovingExcessKurtosis } = import(\"vector\");\ncenteredMovingExcessKurtosis([1, 2, 4, 7, 11, 16], 4, 0, 0)"],
		seeAlso: [
			"vector.excessKurtosis",
			"vector.movingExcessKurtosis",
			"vector.runningExcessKurtosis"
		]
	},
	runningExcessKurtosis: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Calculates the **running excess kurtosis** of a `vector` with a given window size. First two element in result is `null` since **running excess kurtosis** is not defined for less than three elements.",
		returns: { type: "array" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running excess kurtosis** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningExcessKurtosis } = import(\"vector\");\nrunningExcessKurtosis([1, 2, 4, 7, 11])"],
		seeAlso: [
			"vector.excessKurtosis",
			"vector.movingExcessKurtosis",
			"vector.centeredMovingExcessKurtosis"
		]
	},
	kurtosis: {
		type: "(Number[]) -> Number",
		category: "vector",
		description: "Calculates the **kurtosis** of a `vector`. Returns the third standardized moment.",
		returns: { type: "number" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **kurtosis** of. Minimum length is 3."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { kurtosis } = import(\"vector\");\nkurtosis([1, 2, 3, 6, 20])", "let { kurtosis } = import(\"vector\");\nkurtosis([1, 2, 2, 3])"],
		seeAlso: [
			"vector.movingKurtosis",
			"vector.centeredMovingKurtosis",
			"vector.runningKurtosis",
			"vector.excessKurtosis",
			"vector.sampleKurtosis",
			"vector.skewness"
		]
	},
	movingKurtosis: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Calculates the **moving kurtosis** of a `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving kurtosis** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: ["let { movingKurtosis } = import(\"vector\");\nmovingKurtosis([1, 2, 4, 7, 11, 16], 4)", "let { movingKurtosis } = import(\"vector\");\nmovingKurtosis([1, 2, 4, 7, 11, 16], 5)"],
		seeAlso: [
			"vector.kurtosis",
			"vector.centeredMovingKurtosis",
			"vector.runningKurtosis"
		]
	},
	centeredMovingKurtosis: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Calculates the **centered moving kurtosis** of a `vector` with a given window size and padding.",
		returns: { type: "array" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving kurtosis** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: ["let { centeredMovingKurtosis } = import(\"vector\");\ncenteredMovingKurtosis([1, 2, 4, 7, 11, 16], 4)", "let { centeredMovingKurtosis } = import(\"vector\");\ncenteredMovingKurtosis([1, 2, 4, 7, 11, 16], 4, 0, 0)"],
		seeAlso: [
			"vector.kurtosis",
			"vector.movingKurtosis",
			"vector.runningKurtosis"
		]
	},
	runningKurtosis: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Calculates the **running kurtosis** of a `vector` with a given window size. First two element in result is `null` since **running kurtosis** is not defined for less than three elements.",
		returns: { type: "array" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running kurtosis** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningKurtosis } = import(\"vector\");\nrunningKurtosis([1, 2, 4, 7, 11])"],
		seeAlso: [
			"vector.kurtosis",
			"vector.movingKurtosis",
			"vector.centeredMovingKurtosis"
		]
	},
	sampleExcessKurtosis: {
		type: "(Number[]) -> Number",
		category: "vector",
		description: "Calculates the **sample excess kurtosis** of a `vector`. Returns the third standardized moment.",
		returns: { type: "number" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **sample excess kurtosis** of. Minimum length is 3."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { sampleExcessKurtosis } = import(\"vector\");\nsampleExcessKurtosis([1, 2, 3, 6, 20])", "let { sampleExcessKurtosis } = import(\"vector\");\nsampleExcessKurtosis([1, 2, 2, 3])"],
		seeAlso: [
			"vector.movingSampleExcessKurtosis",
			"vector.centeredMovingSampleExcessKurtosis",
			"vector.runningSampleExcessKurtosis",
			"vector.sampleKurtosis",
			"vector.excessKurtosis"
		]
	},
	movingSampleExcessKurtosis: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Calculates the **moving sample excess kurtosis** of a `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving sample excess kurtosis** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: ["let { movingSampleExcessKurtosis } = import(\"vector\");\nmovingSampleExcessKurtosis([1, 2, 4, 7, 11, 16], 4)", "let { movingSampleExcessKurtosis } = import(\"vector\");\nmovingSampleExcessKurtosis([1, 2, 4, 7, 11, 16], 5)"],
		seeAlso: [
			"vector.sampleExcessKurtosis",
			"vector.centeredMovingSampleExcessKurtosis",
			"vector.runningSampleExcessKurtosis"
		]
	},
	centeredMovingSampleExcessKurtosis: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Calculates the **centered moving sample excess kurtosis** of a `vector` with a given window size and padding.",
		returns: { type: "array" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving sample excess kurtosis** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: ["let { centeredMovingSampleExcessKurtosis } = import(\"vector\");\ncenteredMovingSampleExcessKurtosis([1, 2, 4, 7, 11, 16], 4)", "let { centeredMovingSampleExcessKurtosis } = import(\"vector\");\ncenteredMovingSampleExcessKurtosis([1, 2, 4, 7, 11, 16], 4, 0, 100)"],
		seeAlso: [
			"vector.sampleExcessKurtosis",
			"vector.movingSampleExcessKurtosis",
			"vector.runningSampleExcessKurtosis"
		]
	},
	runningSampleExcessKurtosis: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Calculates the **running sample excess kurtosis** of a `vector` with a given window size. First two element in result is `null` since **running sample excess kurtosis** is not defined for less than three elements.",
		returns: { type: "array" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running sample excess kurtosis** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningSampleExcessKurtosis } = import(\"vector\");\nrunningSampleExcessKurtosis([1, 2, 4, 7, 11])"],
		seeAlso: [
			"vector.sampleExcessKurtosis",
			"vector.movingSampleExcessKurtosis",
			"vector.centeredMovingSampleExcessKurtosis"
		]
	},
	sampleKurtosis: {
		type: "(Number[]) -> Number",
		category: "vector",
		description: "Calculates the **sample kurtosis** of a `vector`. Returns the third standardized moment.",
		returns: { type: "number" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **sample kurtosis** of. Minimum length is 3."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { sampleKurtosis } = import(\"vector\");\nsampleKurtosis([1, 2, 3, 6, 20])", "let { sampleKurtosis } = import(\"vector\");\nsampleKurtosis([1, 2, 2, 3])"],
		seeAlso: [
			"vector.movingSampleKurtosis",
			"vector.centeredMovingSampleKurtosis",
			"vector.runningSampleKurtosis",
			"vector.sampleExcessKurtosis",
			"vector.kurtosis",
			"vector.sampleSkewness"
		]
	},
	movingSampleKurtosis: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Calculates the **moving sample kurtosis** of a `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving sample kurtosis** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: ["let { movingSampleKurtosis } = import(\"vector\");\nmovingSampleKurtosis([1, 2, 4, 7, 11, 16], 4)", "let { movingSampleKurtosis } = import(\"vector\");\nmovingSampleKurtosis([1, 2, 4, 7, 11, 16], 5)"],
		seeAlso: [
			"vector.sampleKurtosis",
			"vector.centeredMovingSampleKurtosis",
			"vector.runningSampleKurtosis"
		]
	},
	centeredMovingSampleKurtosis: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Calculates the **centered moving sample kurtosis** of a `vector` with a given window size and padding.",
		returns: { type: "array" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving sample kurtosis** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: ["let { centeredMovingSampleKurtosis } = import(\"vector\");\ncenteredMovingSampleKurtosis([1, 2, 4, 7, 11, 16], 4)", "let { centeredMovingSampleKurtosis } = import(\"vector\");\ncenteredMovingSampleKurtosis([1, 2, 4, 7, 11, 16], 4, 0, 100)"],
		seeAlso: [
			"vector.sampleKurtosis",
			"vector.movingSampleKurtosis",
			"vector.runningSampleKurtosis"
		]
	},
	runningSampleKurtosis: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Calculates the **running sample kurtosis** of a `vector` with a given window size. First two element in result is `null` since **running sample kurtosis** is not defined for less than three elements.",
		returns: { type: "array" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running sample kurtosis** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningSampleKurtosis } = import(\"vector\");\nrunningSampleKurtosis([1, 2, 4, 7, 11])"],
		seeAlso: [
			"vector.sampleKurtosis",
			"vector.movingSampleKurtosis",
			"vector.centeredMovingSampleKurtosis"
		]
	},
	rms: {
		type: "(Number[]) -> Number",
		category: "vector",
		description: "Calculates the **root mean square** of a `vector`. Returns the square root of the average of the squares of the elements.",
		returns: { type: "number" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **root mean square** of. Minimum length is 1."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: [
			"let { rms } = import(\"vector\");\nrms([1, 2, 3, 4])",
			"let { rms } = import(\"vector\");\nrms([5, 4, 3, 2, 1])",
			"let { rms } = import(\"vector\");\nrms(range(1, 1000))",
			"let { rms } = import(\"vector\");\nrms(map(range(1000), -> 1e6 / ($ + 1) ^ 2))",
			"let { rms } = import(\"vector\");\nlet { ln } = import(\"math\");\nrms(map(range(1000), -> ln($ + 1)))"
		],
		seeAlso: [
			"vector.movingRms",
			"vector.centeredMovingRms",
			"vector.runningRms",
			"vector.mean",
			"vector.stdev"
		]
	},
	movingRms: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Calculates the **moving root mean square** of a `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving root mean square** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: [
			"let { movingRms } = import(\"vector\");\nmovingRms([1, 2, 4, 7, 11, 16], 4)",
			"let { movingRms } = import(\"vector\");\nmovingRms([1, 2, 4, 7, 11, 16], 5)",
			"let { movingRms } = import(\"vector\");\nmovingRms([1, 2, 4, 7, 11, 16], 6)"
		],
		seeAlso: [
			"vector.rms",
			"vector.centeredMovingRms",
			"vector.runningRms"
		]
	},
	centeredMovingRms: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Calculates the **centered moving root mean square** of a `vector` with a given window size and padding value.",
		returns: {
			type: "number",
			array: true
		},
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving root mean square** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: [
			"let { centeredMovingRms } = import(\"vector\");\ncenteredMovingRms([1, 2, 4, 7, 11, 16], 4)",
			"let { centeredMovingRms } = import(\"vector\");\ncenteredMovingRms([1, 2, 4, 7, 11, 16], 5, 0)",
			"let { centeredMovingRms } = import(\"vector\");\ncenteredMovingRms([1, 2, 4, 7, 11, 16], 6, 0, 0)"
		],
		seeAlso: [
			"vector.rms",
			"vector.movingRms",
			"vector.runningRms"
		]
	},
	runningRms: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Calculates the **running root mean square** of a `vector`.",
		returns: { type: "vector" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running root mean square** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: [
			"let { runningRms } = import(\"vector\");\nrunningRms([1, 2, 3, 4, 5, 6])",
			"let { runningRms } = import(\"vector\");\nrunningRms([1, -3, 2])",
			"let { runningRms } = import(\"vector\");\nrunningRms([-1, -2, -3])",
			"let { runningRms } = import(\"vector\");\nrunningRms([0])"
		],
		seeAlso: [
			"vector.rms",
			"vector.movingRms",
			"vector.centeredMovingRms"
		]
	},
	mad: {
		type: "(Number[]) -> Number",
		category: "vector",
		description: "Returns the **mean absolute deviation** of all elements in the `vector`.",
		returns: { type: "number" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **mean absolute deviation** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { mad } = import(\"vector\");\nmad([1, 2, 3])", "let { mad } = import(\"vector\");\nmad([1, 2, -3])"],
		seeAlso: [
			"vector.movingMad",
			"vector.centeredMovingMad",
			"vector.runningMad",
			"vector.medad",
			"vector.stdev",
			"vector.variance",
			"vector.iqr"
		]
	},
	movingMad: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Returns the **moving mean absolute deviation** of the `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving mean absolute deviation** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: ["let { movingMad } = import(\"vector\");\nmovingMad([1, 2, 3, 4, 5], 3)", "let { movingMad } = import(\"vector\");\nmovingMad([1, 2, 3, 4, 5], 5)"],
		seeAlso: [
			"vector.mad",
			"vector.centeredMovingMad",
			"vector.runningMad"
		]
	},
	centeredMovingMad: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Returns the **centered moving mean absolute deviation** of the `vector` with a given window size.",
		returns: { type: "array" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving mean absolute deviation** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: ["let { centeredMovingMad } = import(\"vector\");\ncenteredMovingMad([1, 2, 3, 4, 5], 3)", "let { centeredMovingMad } = import(\"vector\");\ncenteredMovingMad([1, 2, 3, 4, 5], 5)"],
		seeAlso: [
			"vector.mad",
			"vector.movingMad",
			"vector.runningMad"
		]
	},
	runningMad: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Returns the **running mean absolute deviation** of the `vector`.",
		returns: { type: "vector" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running mean absolute deviation** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningMad } = import(\"vector\");\nrunningMad([1, 2, 3])", "let { runningMad } = import(\"vector\");\nrunningMad([1, 2, -3])"],
		seeAlso: [
			"vector.mad",
			"vector.movingMad",
			"vector.centeredMovingMad"
		]
	},
	medad: {
		type: "(Number[]) -> Number",
		category: "vector",
		description: "Returns the **median absolute deviation** of all elements in the `vector`.",
		returns: { type: "number" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **median absolute deviation** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { medad } = import(\"vector\");\nmedad([1, 2, 3])", "let { medad } = import(\"vector\");\nmedad([1, 2, -3])"],
		seeAlso: [
			"vector.movingMedad",
			"vector.centeredMovingMedad",
			"vector.runningMedad",
			"vector.mad",
			"vector.median",
			"vector.iqr"
		]
	},
	movingMedad: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Returns the **moving median absolute deviation** of the `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving median absolute deviation** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: ["let { movingMedad } = import(\"vector\");\nmovingMedad([1, 2, 3, 4, 5], 3)", "let { movingMedad } = import(\"vector\");\nmovingMedad([1, 2, 3, 4, 5], 5)"],
		seeAlso: [
			"vector.medad",
			"vector.centeredMovingMedad",
			"vector.runningMedad"
		]
	},
	centeredMovingMedad: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Returns the **centered moving median absolute deviation** of the `vector` with a given window size.",
		returns: { type: "array" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving median absolute deviation** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: ["let { centeredMovingMedad } = import(\"vector\");\ncenteredMovingMedad([1, 2, 3, 4, 5], 3)", "let { centeredMovingMedad } = import(\"vector\");\ncenteredMovingMedad([1, 2, 3, 4, 5], 5)"],
		seeAlso: [
			"vector.medad",
			"vector.movingMedad",
			"vector.runningMedad"
		]
	},
	runningMedad: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Returns the **running median absolute deviation** of the `vector`.",
		returns: { type: "vector" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running median absolute deviation** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningMedad } = import(\"vector\");\nrunningMedad([1, 2, 3])", "let { runningMedad } = import(\"vector\");\nrunningMedad([1, 2, -3])"],
		seeAlso: [
			"vector.medad",
			"vector.movingMedad",
			"vector.centeredMovingMedad"
		]
	},
	giniCoefficient: {
		type: "(Number[]) -> Number",
		category: "vector",
		description: "Returns the **gini coefficient** of all elements in the `vector`.",
		returns: { type: "number" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **gini coefficient** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { giniCoefficient } = import(\"vector\");\nginiCoefficient([1, 2, 3])", "let { giniCoefficient } = import(\"vector\");\nginiCoefficient([1, 1, 3])"],
		seeAlso: [
			"vector.movingGiniCoefficient",
			"vector.centeredMovingGiniCoefficient",
			"vector.runningGiniCoefficient",
			"vector.entropy"
		]
	},
	movingGiniCoefficient: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Returns the **moving gini coefficient** of the `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving gini coefficient** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: ["let { movingGiniCoefficient } = import(\"vector\");\nmovingGiniCoefficient([1, 2, 3], 2)", "let { movingGiniCoefficient } = import(\"vector\");\nmovingGiniCoefficient([1, 1, 3], 2)"],
		seeAlso: [
			"vector.giniCoefficient",
			"vector.centeredMovingGiniCoefficient",
			"vector.runningGiniCoefficient"
		]
	},
	centeredMovingGiniCoefficient: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Returns the **centered moving gini coefficient** of the `vector` with a given window size.",
		returns: { type: "array" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving gini coefficient** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: ["let { centeredMovingGiniCoefficient } = import(\"vector\");\ncenteredMovingGiniCoefficient([1, 2, 3], 2)", "let { centeredMovingGiniCoefficient } = import(\"vector\");\ncenteredMovingGiniCoefficient([1, 1, 3], 2)"],
		seeAlso: [
			"vector.giniCoefficient",
			"vector.movingGiniCoefficient",
			"vector.runningGiniCoefficient"
		]
	},
	runningGiniCoefficient: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Returns the **running gini coefficient** of the `vector`.",
		returns: { type: "array" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running gini coefficient** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningGiniCoefficient } = import(\"vector\");\nrunningGiniCoefficient([1, 2, 3])", "let { runningGiniCoefficient } = import(\"vector\");\nrunningGiniCoefficient([1, 1, 3])"],
		seeAlso: [
			"vector.giniCoefficient",
			"vector.movingGiniCoefficient",
			"vector.centeredMovingGiniCoefficient"
		]
	},
	entropy: {
		type: "(Number[]) -> Number",
		category: "vector",
		description: "Calculates the **entropy** of a `vector`. The entropy is a measure of the uncertainty associated with a random variable.",
		returns: { type: "number" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **entropy** of. Minimum length is 1."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: [
			"let { entropy } = import(\"vector\");\nentropy([1, 1, 2, 3, 3, 3])",
			"let { entropy } = import(\"vector\");\nentropy([1, 2, 3])",
			"let { entropy } = import(\"vector\");\nentropy([1, 2, 2, 3])",
			"let { entropy } = import(\"vector\");\nentropy([0])",
			"let { entropy } = import(\"vector\");\nentropy([1])",
			"let { entropy } = import(\"vector\");\nentropy([1, 2])"
		],
		seeAlso: [
			"vector.movingEntropy",
			"vector.centeredMovingEntropy",
			"vector.runningEntropy",
			"vector.giniCoefficient"
		]
	},
	movingEntropy: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Calculates the **moving entropy** of a `vector` with a given window size.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **moving entropy** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "windowSize"] }],
		examples: [
			"let { movingEntropy } = import(\"vector\");\nmovingEntropy([1, 1, 2, 3, 3, 3], 4)",
			"let { movingEntropy } = import(\"vector\");\nmovingEntropy([1, 1, 2, 3, 3, 3], 3)",
			"let { movingEntropy } = import(\"vector\");\nmovingEntropy([1, 2], 2)"
		],
		seeAlso: [
			"vector.entropy",
			"vector.centeredMovingEntropy",
			"vector.runningEntropy"
		]
	},
	centeredMovingEntropy: {
		type: "(Number[], Number) -> Unknown[]",
		category: "vector",
		description: "Calculates the **centered moving entropy** of a `vector` with a given window size.",
		returns: {
			type: "number",
			array: true
		},
		args: {
			vector: {
				type: "vector",
				description: "The `vector` to calculate the **centered moving entropy** of."
			},
			windowSize: {
				type: "integer",
				description: "The size of the moving window."
			},
			leftPadding: {
				type: "number",
				description: "Optional value to use for padding. Default is `null`."
			},
			rightPadding: {
				type: "number",
				description: "Optional value to use for right padding. Default is `null`."
			},
			a: { type: "vector" },
			b: { type: "integer" }
		},
		variants: [
			{ argumentNames: ["vector", "windowSize"] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding"
			] },
			{ argumentNames: [
				"vector",
				"windowSize",
				"leftPadding",
				"rightPadding"
			] }
		],
		examples: [
			"let { centeredMovingEntropy } = import(\"vector\");\ncenteredMovingEntropy([1, 1, 2, 3, 3, 3], 4)",
			"let { centeredMovingEntropy } = import(\"vector\");\ncenteredMovingEntropy([1, 1, 2, 3, 3, 3], 3)",
			"let { centeredMovingEntropy } = import(\"vector\");\ncenteredMovingEntropy([1, 2], 2)"
		],
		seeAlso: [
			"vector.entropy",
			"vector.movingEntropy",
			"vector.runningEntropy"
		]
	},
	runningEntropy: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Calculates the **running entropy** of a `vector`.",
		returns: { type: "vector" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to calculate the **running entropy** of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: ["let { runningEntropy } = import(\"vector\");\nrunningEntropy([1, 1, 2, 3, 3, 3])", "let { runningEntropy } = import(\"vector\");\nrunningEntropy([1, 2])"],
		seeAlso: [
			"vector.entropy",
			"vector.movingEntropy",
			"vector.centeredMovingEntropy"
		]
	},
	mode: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Returns the mode of all elements in the vector.",
		returns: {
			type: "number",
			array: true
		},
		args: { vector: {
			type: "vector",
			description: "The vector to calculate the mode of."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: [
			"let { mode } = import(\"vector\");\nmode([1, 2, 3])",
			"let { mode } = import(\"vector\");\nmode([1, 2, -3, 1])",
			"let { mode } = import(\"vector\");\nmode([2, 2, 3, 3, 4])",
			"let { mode } = import(\"vector\");\nmode([2, 2, 3, 3])",
			"let { mode } = import(\"vector\");\nmode([1, 2, 3, 2, 1, 2])"
		],
		seeAlso: ["vector.mean", "vector.median"]
	},
	linspace: {
		type: "(Number, Number, Number) -> Number[]",
		category: "vector",
		description: "Generates a vector of evenly spaced numbers between two values.",
		returns: {
			type: "number",
			array: true
		},
		args: {
			start: {
				type: "number",
				description: "The starting value."
			},
			stop: {
				type: "number",
				description: "The ending value."
			},
			n: {
				type: "integer",
				description: "The number of values to generate."
			}
		},
		variants: [{ argumentNames: [
			"start",
			"stop",
			"n"
		] }],
		examples: ["let { linspace } = import(\"vector\");\nlinspace(0, 10, 6)", "let { linspace } = import(\"vector\");\nlinspace(10, 20, 25)"],
		seeAlso: ["range"]
	},
	quartiles: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Calculates the quartiles of a vector. Returns an array containing the first, second (median), and third quartiles.",
		returns: {
			type: "number",
			array: true
		},
		args: { vector: {
			type: "vector",
			description: "The vector to calculate the quartiles of. Minimum length is 4."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: [
			"let { quartiles } = import(\"vector\");\nquartiles([1, 2, 3, 4])",
			"let { quartiles } = import(\"vector\");\nquartiles([5, 4, 3, 2, 1, 2, 3, 4, 5])",
			"let { quartiles } = import(\"vector\");\nquartiles(range(1, 1000))",
			"let { quartiles } = import(\"vector\");\nquartiles(map(range(1000), -> 1e6 / ($ + 1) ^ 2))",
			"let { quartiles } = import(\"vector\");\nlet { ln } = import(\"math\");\nquartiles(map(range(1000), -> ln($ + 1)))"
		],
		seeAlso: [
			"vector.percentile",
			"vector.quantile",
			"vector.median",
			"vector.iqr"
		]
	},
	percentile: {
		type: "(Number[], Number) -> Number",
		category: "vector",
		description: "Calculates the percentile of a vector. Returns the value at the specified percentile.",
		returns: { type: "number" },
		args: {
			vector: {
				type: "vector",
				description: "The non empty vector to calculate the percentile of."
			},
			percentile: {
				type: "number",
				description: "The percentile to calculate. Must be between 0 and 1."
			},
			a: { type: "number" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "percentile"] }],
		examples: [
			"let { percentile } = import(\"vector\");\npercentile([1, 2, 3, 4, 5, 6, 7, 8, 9, 10], 35)",
			"let { percentile } = import(\"vector\");\npercentile(map(range(100), -> $ ^ 0.5), 0)",
			"let { percentile } = import(\"vector\");\npercentile(map(range(100), -> $ ^ 0.5), 10)",
			"let { percentile } = import(\"vector\");\npercentile(map(range(100), -> $ ^ 0.5), 20)",
			"let { percentile } = import(\"vector\");\npercentile(map(range(100), -> $ ^ 0.5), 30)",
			"let { percentile } = import(\"vector\");\npercentile(map(range(100), -> $ ^ 0.5), 40)",
			"let { percentile } = import(\"vector\");\npercentile(map(range(100), -> $ ^ 0.5), 50)",
			"let { percentile } = import(\"vector\");\npercentile(map(range(100), -> $ ^ 0.5), 60)",
			"let { percentile } = import(\"vector\");\npercentile(map(range(100), -> $ ^ 0.5), 70)",
			"let { percentile } = import(\"vector\");\npercentile(map(range(100), -> $ ^ 0.5), 80)",
			"let { percentile } = import(\"vector\");\npercentile(map(range(100), -> $ ^ 0.5), 90)",
			"let { percentile } = import(\"vector\");\npercentile(map(range(100), -> $ ^ 0.5), 100)"
		],
		seeAlso: [
			"vector.quantile",
			"vector.quartiles",
			"vector.median",
			"vector.ecdf",
			"vector.winsorize"
		]
	},
	quantile: {
		type: "(Number[], Number) -> Number",
		category: "vector",
		description: "Calculates the quantile of a vector. Returns the value at the specified quantile.",
		returns: { type: "number" },
		args: {
			vector: {
				type: "vector",
				description: "The non empty vector to calculate the quantile of."
			},
			quantile: {
				type: "number",
				description: "The quantile to calculate. Must be between 0 and 1."
			},
			a: { type: "number" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "quantile"] }],
		examples: [
			"let { quantile } = import(\"vector\");\nquantile([1, 2, 3, 4, 5, 6, 7, 8, 9, 10], 0.35)",
			"let { quantile } = import(\"vector\");\nquantile(map(range(100), -> $ ^ 0.5), 0)",
			"let { quantile } = import(\"vector\");\nquantile(map(range(100), -> $ ^ 0.5), 0.1)",
			"let { quantile } = import(\"vector\");\nquantile(map(range(100), -> $ ^ 0.5), 0.2)",
			"let { quantile } = import(\"vector\");\nquantile(map(range(100), -> $ ^ 0.5), 0.3)",
			"let { quantile } = import(\"vector\");\nquantile(map(range(100), -> $ ^ 0.5), 0.4)",
			"let { quantile } = import(\"vector\");\nquantile(map(range(100), -> $ ^ 0.5), 0.5)",
			"let { quantile } = import(\"vector\");\nquantile(map(range(100), -> $ ^ 0.5), 0.6)",
			"let { quantile } = import(\"vector\");\nquantile(map(range(100), -> $ ^ 0.5), 0.7)",
			"let { quantile } = import(\"vector\");\nquantile(map(range(100), -> $ ^ 0.5), 0.8)",
			"let { quantile } = import(\"vector\");\nquantile(map(range(100), -> $ ^ 0.5), 0.9)",
			"let { quantile } = import(\"vector\");\nquantile(map(range(100), -> $ ^ 0.5), 1)"
		],
		seeAlso: [
			"vector.percentile",
			"vector.quartiles",
			"vector.ecdf"
		]
	},
	histogram: {
		type: "(Number[], Number) -> Number[]",
		category: "vector",
		description: "Creates a histogram from a numeric `array` by dividing the data range into the specified number of bins. Returns an `array` of `[binStart, binEnd, count]` tuples representing each bin's range and the number of values within it. Handles empty arrays, identical values, and properly places maximum values in the last bin.",
		returns: {
			type: "array",
			array: true
		},
		args: {
			vector: {
				type: "vector",
				description: "The numeric array to create a histogram from."
			},
			bins: {
				type: "integer",
				description: "The number of bins to divide the data range into."
			},
			a: { type: "number" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "bins"] }],
		examples: [
			"let { histogram } = import(\"vector\");\nhistogram([1, 2, 2, 3, 2, 6, 4, 3, 2, 4, 1, 3, 2, 9], 3)",
			"let { histogram } = import(\"vector\");\nhistogram([1, 2, 3, 4, 5], 5)",
			"let { histogram } = import(\"vector\");\nhistogram([1, 2, 3, 4, 5], 10)",
			"let { histogram } = import(\"vector\");\nhistogram([1, 2, 3, 4, 5], 1)"
		],
		seeAlso: ["vector.bincount", "vector.ecdf"]
	},
	ecdf: {
		type: "(Number[]) -> ((Number) -> Number)",
		category: "vector",
		description: "Calculates the empirical cumulative distribution function value for a given threshold in a non empty dataset. Returns the proportion of values in the `array` that are less than or equal to the specified threshold.",
		returns: { type: "number" },
		args: {
			vector: {
				type: "vector",
				description: "The numeric array to calculate the ECDF from."
			},
			threshold: {
				type: "number",
				description: "The threshold value to calculate the ECDF for."
			},
			a: { type: "number" },
			b: { type: "integer" }
		},
		variants: [{ argumentNames: ["vector", "threshold"] }],
		examples: [
			"let { ecdf } = import(\"vector\");\necdf([1, 2, 2, 3, 2, 6, 4, 3, 2, 4, 1, 3, 2, 9, 10, 12], 5)",
			"let { ecdf } = import(\"vector\");\necdf([1, 2, 3, 4, 5], 3)",
			"let { ecdf } = import(\"vector\");\necdf([1, 2, 3, 4, 5], 0)",
			"let { ecdf } = import(\"vector\");\necdf([1, 2, 3, 4, 5], 10)",
			"let { ecdf } = import(\"vector\");\necdf([1, 2, 3, 4, 5], 2)"
		],
		seeAlso: [
			"vector.histogram",
			"vector.percentile",
			"vector.quantile"
		]
	},
	isOutliers: {
		type: "(Number[]) -> Boolean[]",
		category: "vector",
		description: "Checks if the `vector` contains outliers based on the interquartile range (IQR) method. Returns `true` if outliers are present, `false` otherwise.",
		returns: { type: "boolean" },
		args: { vector: {
			type: "vector",
			description: "The `vector` to check for outliers."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: [
			"let { isOutliers } = import(\"vector\");\nisOutliers([1, 2, 3])",
			"let { isOutliers } = import(\"vector\");\nisOutliers([1, 2, -3])",
			"let { isOutliers } = import(\"vector\");\nisOutliers([1, 2, 3, 2, 4, 120])"
		],
		seeAlso: [
			"vector.outliers",
			"vector.winsorize",
			"vector.iqr"
		]
	},
	outliers: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Identifies outliers in the `vector` based on the interquartile range (IQR) method. Returns an array of outlier values.",
		returns: {
			type: "number",
			array: true
		},
		args: { vector: {
			type: "vector",
			description: "The `vector` to check for outliers."
		} },
		variants: [{ argumentNames: ["vector"] }],
		examples: [
			"let { outliers } = import(\"vector\");\noutliers([1, 2, 3])",
			"let { outliers } = import(\"vector\");\noutliers([1, 2, -3])",
			"let { outliers } = import(\"vector\");\noutliers([1, 2, 3, 2, 4, 120])"
		],
		seeAlso: [
			"vector.isOutliers",
			"vector.winsorize",
			"vector.iqr"
		]
	},
	bincount: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "counts occurrences of each `integer` in a vector, returning an array where index `i` contains the count of value `i`, with optional **minimum size** and **weights parameters**.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The vector to count occurrences in."
			},
			minSize: {
				type: "integer",
				description: "Optional minimum size of the output array."
			},
			weights: {
				type: "number",
				array: true,
				description: "Optional weights for each element in the vector."
			}
		},
		variants: [
			{ argumentNames: ["vector"] },
			{ argumentNames: ["vector", "minSize"] },
			{ argumentNames: [
				"vector",
				"minSize",
				"weights"
			] }
		],
		examples: ["let { bincount } = import(\"vector\");\nbincount([1, 2, 3])", "let { bincount } = import(\"vector\");\nbincount([1, 2, 2, 3, 3])"],
		seeAlso: ["countValues", "vector.histogram"],
		hideOperatorForm: true
	},
	winsorize: {
		type: "(Number[]) -> Number[]",
		category: "vector",
		description: "Limits extreme values in a `vector` by replacing values below the **lower quantile** and above the **upper quantile** with the values at those quantiles. The function takes a `vector` of values and **quantile thresholds** (between 0 and 1), with the upper quantile. Winsorization reduces the influence of outliers while preserving the overall distribution shape, making statistical analyses more robust.",
		returns: { type: "vector" },
		args: {
			vector: {
				type: "vector",
				description: "The vector to winsorize."
			},
			lowerQuantile: {
				type: "number",
				description: "The lower quantile threshold (between 0 and 1)."
			},
			upperQuantile: {
				type: "number",
				description: "Optional Upper quantile threshold (between 0 and 1). Defaults to `(1 - lowerQuantile)` if `lowerQuantile <= 0.5` otherwise `1`."
			}
		},
		variants: [{ argumentNames: ["vector", "lowerQuantile"] }, { argumentNames: [
			"vector",
			"lowerQuantile",
			"upperQuantile"
		] }],
		examples: [
			"let { winsorize } = import(\"vector\");\nwinsorize([2, 5, 8, 10, 15, 18, 20, 35, 60, 100], 0.25)",
			"let { winsorize } = import(\"vector\");\nwinsorize([2, 5, 8, 10, 15, 18, 20, 35, 60, 100], 0.25, 0.75)",
			"let { winsorize } = import(\"vector\");\nwinsorize([2, 5, 8, 10, 15, 18, 20, 35, 60, 100], 0.25, 0.5)"
		],
		seeAlso: [
			"vector.outliers",
			"vector.isOutliers",
			"vector.percentile"
		],
		hideOperatorForm: true
	},
	mse: {
		type: "(Number[], Number[]) -> Number",
		category: "vector",
		description: "Calculates the **Mean Squared Error (MSE)** between two vectors. Returns the average of the squared differences between corresponding elements.",
		returns: { type: "number" },
		args: {
			a: {
				type: "vector",
				description: "The first vector."
			},
			b: {
				type: "vector",
				description: "The second vector."
			}
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { mse } = import(\"vector\");\nmse([1, 2, 3], [1, 2, 3])",
			"let { mse } = import(\"vector\");\nmse([1, 2, 3], [4, 5, 6])",
			"let { mse } = import(\"vector\");\nmse([1, 2, 3], [2, 2, 2])",
			"let { mse } = import(\"vector\");\nmse([1, 2], [3, 3])",
			"let { mse } = import(\"vector\");\nmse([1], [3])"
		],
		seeAlso: [
			"vector.rmse",
			"vector.mae",
			"vector.smape"
		]
	},
	rmse: {
		type: "(Number[], Number[]) -> Number",
		category: "vector",
		description: "Calculates the **Root Mean Squared Error (RMSE)** between two vectors. Returns the square root of the average of the squared differences between corresponding elements.",
		returns: { type: "number" },
		args: {
			a: {
				type: "vector",
				description: "The first vector."
			},
			b: {
				type: "vector",
				description: "The second vector."
			}
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { rmse } = import(\"vector\");\nrmse([1, 2, 3], [1, 2, 3])",
			"let { rmse } = import(\"vector\");\nrmse([1, 2, 3], [4, 5, 6])",
			"let { rmse } = import(\"vector\");\nrmse([1, 2, 3], [2, 2, 2])",
			"let { rmse } = import(\"vector\");\nrmse([1, 2], [3, 3])",
			"let { rmse } = import(\"vector\");\nrmse([1], [3])"
		],
		seeAlso: [
			"vector.mse",
			"vector.mae",
			"vector.smape"
		]
	},
	mae: {
		type: "(Number[], Number[]) -> Number",
		category: "vector",
		description: "Calculates the **Mean Absolute Error (MAE)** between two vectors. Returns the average of the absolute differences between corresponding elements.",
		returns: { type: "number" },
		args: {
			a: {
				type: "vector",
				description: "The first vector."
			},
			b: {
				type: "vector",
				description: "The second vector."
			}
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { mae } = import(\"vector\");\nmae([1, 2, 3], [1, 2, 3])",
			"let { mae } = import(\"vector\");\nmae([1, 2, 3], [4, 5, 6])",
			"let { mae } = import(\"vector\");\nmae([1, 2, 3], [2, 2, 2])",
			"let { mae } = import(\"vector\");\nmae([1, 2], [3, 3])",
			"let { mae } = import(\"vector\");\nmae([1], [3])"
		],
		seeAlso: [
			"vector.mse",
			"vector.rmse",
			"vector.smape"
		]
	},
	smape: {
		type: "(Number[], Number[]) -> Number",
		category: "vector",
		description: "Calculates the **Symmetric Mean Absolute Percentage Error (SMAPE)** between two vectors. Returns the average of the absolute percentage differences between corresponding elements.",
		returns: { type: "number" },
		args: {
			a: {
				type: "vector",
				description: "The first vector."
			},
			b: {
				type: "vector",
				description: "The second vector."
			}
		},
		variants: [{ argumentNames: ["a", "b"] }],
		examples: [
			"let { smape } = import(\"vector\");\nsmape([1, 2, 3], [1, 2, 3])",
			"let { smape } = import(\"vector\");\nsmape([1, 2, 3], [4, 5, 6])",
			"let { smape } = import(\"vector\");\nsmape([1, 2, 3], [2, 2, 2])",
			"let { smape } = import(\"vector\");\nsmape([1, 2], [3, 3])",
			"let { smape } = import(\"vector\");\nsmape([1], [3])"
		],
		seeAlso: [
			"vector.mse",
			"vector.rmse",
			"vector.mae"
		]
	}
};
//#endregion
//#region src/builtin/modules/vector/bincount.ts
/**
* Counts occurrences of each integer value in an array of non-negative integers.
*
* @param array - Array of non-negative integers to count
* @param minLength - Minimum length of the output array (default: 0)
* @param weights - Optional array of weights (same length as input array)
* @returns An array where index i contains the count of occurrences of i in the input array
*/
function bincount(array, minLength = 0, weights) {
	if (array.length === 0) return Array.from({ length: minLength }, () => 0);
	const maxValue = Math.max(...array);
	const outputLength = Math.max(maxValue + 1, minLength);
	const counts = Array.from({ length: outputLength }, () => 0);
	for (let i = 0; i < array.length; i++) {
		const value = Math.floor(array[i]);
		/* v8 ignore next 4 */
		if (value < outputLength) counts[value] += weights ? weights[i] : 1;
	}
	return counts;
}
//#endregion
//#region src/builtin/modules/vector/histogram.ts
/**
* Creates a histogram from an array of numbers, returning bin ranges and counts.
* Each bin is represented as a tuple of [minValue, maxValue, count].
*
* @param vector - Array of numeric values
* @param bins - Number of bins to divide the data into
* @returns Array of tuples, each containing [binStart, binEnd, count]
*/
function calcHistogram(vector, bins) {
	if (vector.length === 0) {
		const result = [];
		for (let i = 0; i < bins; i++) result.push([
			0,
			0,
			0
		]);
		return result;
	}
	const min = Math.min(...vector);
	const max = Math.max(...vector);
	if (min === max) {
		const result = [];
		for (let i = 0; i < bins; i++) result.push([
			min,
			min,
			0
		]);
		result[0][2] = vector.length;
		return result;
	}
	const binSize = (max - min) / bins;
	const histogram = [];
	for (let i = 0; i < bins; i++) {
		const binStart = min + i * binSize;
		const binEnd = i === bins - 1 ? max : min + (i + 1) * binSize;
		histogram.push([
			binStart,
			binEnd,
			0
		]);
	}
	for (const value of vector) if (value === max) histogram[bins - 1][2] += 1;
	else {
		const binIndex = Math.min(Math.floor((value - min) / binSize), bins - 1);
		histogram[binIndex][2] += 1;
	}
	return histogram;
}
//#endregion
//#region src/builtin/modules/vector/mode.ts
/**
* Calculates the mode (most frequent value(s)) of a dataset
* @param values An array of values of any type
* @returns An array containing the mode(s) of the dataset
*/
function mode(values) {
	const frequencyMap = /* @__PURE__ */ new Map();
	for (const value of values) frequencyMap.set(value, (frequencyMap.get(value) || 0) + 1);
	let maxFrequency = 0;
	for (const frequency of frequencyMap.values()) if (frequency > maxFrequency) maxFrequency = frequency;
	if (maxFrequency === 1) return values;
	const modes = [];
	for (const [value, frequency] of frequencyMap.entries()) if (frequency === maxFrequency) modes.push(value);
	return modes;
}
//#endregion
//#region src/builtin/modules/vector/outliers.ts
/**
* Checks if a vector has no extreme outliers using the IQR method
* @param vector An array of numbers to check for extreme outliers
* @returns true if there are no extreme outliers, false otherwise
*/
function hasOutliers(vector) {
	return outliers(vector).length > 0;
}
function outliers(vector) {
	if (vector.length <= 1) return [];
	const sorted = [...vector].sort((a, b) => a - b);
	const q1Index = Math.floor(sorted.length * .25);
	const q1 = sorted.length % 4 === 0 ? (sorted[q1Index - 1] + sorted[q1Index]) / 2 : sorted[q1Index];
	const q3Index = Math.floor(sorted.length * .75);
	const q3 = sorted.length % 4 === 0 ? (sorted[q3Index - 1] + sorted[q3Index]) / 2 : sorted[q3Index];
	const iqr = q3 - q1;
	const lowerBound = q1 - iqr * 1.5;
	const upperBound = q3 + iqr * 1.5;
	return vector.filter((val) => val < lowerBound || val > upperBound);
}
//#endregion
//#region src/builtin/modules/vector/percentile.ts
/**
* Calculate the percentile value from an array of numbers
* @param data Array of numbers to calculate percentile from
* @param percentile The percentile to calculate (0-100)
* @returns The value at the specified percentile
*/
function calcPercentile(data, percentile) {
	const sortedData = [...data].sort((a, b) => a - b);
	if (percentile === 0) return sortedData[0];
	if (percentile === 100) return sortedData[sortedData.length - 1];
	const index = percentile / 100 * (sortedData.length - 1);
	if (Number.isInteger(index)) return sortedData[index];
	const lowerIndex = Math.floor(index);
	const upperIndex = Math.ceil(index);
	const weight = index - lowerIndex;
	return sortedData[lowerIndex] * (1 - weight) + sortedData[upperIndex] * weight;
}
//#endregion
//#region src/builtin/modules/vector/quartiles.ts
function quartiles(vector) {
	const sorted = [...vector].sort((a, b) => a - b);
	const midIndex = sorted.length / 2;
	let q2;
	if (sorted.length % 2 === 0) q2 = (sorted[midIndex - 1] + sorted[midIndex]) / 2;
	else q2 = sorted[Math.floor(midIndex)];
	const lowerHalf = sorted.slice(0, Math.floor(sorted.length / 2));
	const upperHalf = sorted.slice(Math.ceil(sorted.length / 2));
	let q1, q3;
	if (lowerHalf.length % 2 === 0) {
		const midLower = lowerHalf.length / 2;
		q1 = (lowerHalf[midLower - 1] + lowerHalf[midLower]) / 2;
	} else q1 = lowerHalf[Math.floor(lowerHalf.length / 2)];
	if (upperHalf.length % 2 === 0) {
		const midUpper = upperHalf.length / 2;
		q3 = (upperHalf[midUpper - 1] + upperHalf[midUpper]) / 2;
	} else q3 = upperHalf[Math.floor(upperHalf.length / 2)];
	return [
		q1,
		q2,
		q3
	];
}
//#endregion
//#region src/builtin/modules/vector/reductionFunctions/mean.ts
const meanReductionFunction = { mean: (vector) => calcMean(vector) };
const geometricMeanReductionFunction = { geometricMean: (vector) => {
	if (vector.some((val) => val < 0)) throw new Error("Geometric mean is not defined for non-positive numbers");
	return Math.exp(vector.reduce((acc, val) => acc + Math.log(val), 0) / vector.length);
} };
const harmonicMeanReductionFunction = { harmonicMean: (vector) => vector.length / vector.reduce((acc, val) => acc + 1 / val, 0) };
//#endregion
//#region src/builtin/modules/vector/reductionFunctions/median.ts
const medianReductionFunction = { median: (vector) => calcMedian$1(vector) };
//#endregion
//#region src/builtin/modules/vector/reductionFunctions/prod.ts
const prodReductionFunction = {
	prod: (vector) => vector.reduce((acc, val) => acc * val, 1),
	padding: 1,
	minLength: 0
};
//#endregion
//#region src/builtin/modules/vector/reductionFunctions/sum.ts
const sumReductionFunction = {
	sum: (vector) => vector.reduce((acc, val) => acc + val, 0),
	minLength: 0
};
//#endregion
//#region src/builtin/modules/vector/reductionFunctions/variance.ts
const varianceReductionFunction = { variance: (vector) => calcVariance(vector) };
const sampleVarianceReductionFunction = {
	sampleVariance: (vector) => calcSampleVariance(vector),
	minLength: 2
};
//#endregion
//#region src/builtin/modules/vector/reductionFunctions/standardDeviation.ts
const stdevReductionFunction = { stdev: (vector) => calcStdDev(vector) };
const sampleStdevReductionFunction = {
	sampleStdev: (vector) => calcSampleStdDev(vector),
	minLength: 2
};
//#endregion
//#region src/builtin/modules/vector/reductionFunctions/iqr.ts
const iqrReductionFunction = {
	iqr: (vector) => {
		const [q1, , q3] = quartiles(vector);
		return q3 - q1;
	},
	minLength: 4
};
//#endregion
//#region src/builtin/modules/vector/reductionFunctions/span.ts
const spanReductionFunction = {
	span: (vector) => vector.length === 0 ? 0 : Math.max(...vector) - Math.min(...vector),
	minLength: 0
};
//#endregion
//#region src/builtin/modules/vector/reductionFunctions/skewness.ts
function skewness(vector) {
	const mean = calcMean(vector);
	const stdDev = calcStdDev(vector);
	if (stdDev === 0) throw new Error("Standard deviation is zero, skewness is undefined");
	return vector.reduce((acc, val) => acc + (val - mean) ** 3, 0) / (vector.length * stdDev ** 3);
}
function sampleSkewness(vector) {
	const n = vector.length;
	const mean = vector.reduce((acc, val) => acc + val, 0) / n;
	let sumSquaredDiffs = 0;
	let sumCubedDiffs = 0;
	for (const val of vector) {
		const diff = val - mean;
		sumSquaredDiffs += diff * diff;
		sumCubedDiffs += diff * diff * diff;
	}
	const sampleVariance = sumSquaredDiffs / (n - 1);
	const sampleStdDev = Math.sqrt(sampleVariance);
	if (sampleStdDev === 0) throw new Error("Cannot calculate sample skewness when standard deviation is 0");
	return n / ((n - 1) * (n - 2)) * sumCubedDiffs / sampleStdDev ** 3;
}
const skewnessReductionFunction = {
	skewness: (vector) => skewness(vector),
	minLength: 3
};
const sampleSkewnessReductionFunction = {
	sampleSkewness: (vector) => sampleSkewness(vector),
	minLength: 3
};
//#endregion
//#region src/builtin/modules/vector/reductionFunctions/kurtosis.ts
function kurtosis(vector) {
	const mean = calcMean(vector);
	const stdDev = calcStdDev(vector);
	if (stdDev === 0) throw new Error("Standard deviation is zero, kurtosis is undefined");
	return vector.reduce((acc, val) => acc + (val - mean) ** 4, 0) / (vector.length * stdDev ** 4);
}
function excessKurtosis(vector) {
	return kurtosis(vector) - 3;
}
/**
* Calculates the sample kurtosis (bias-corrected)
* @param vector Array of numeric values
* @returns The sample kurtosis
* @throws Error if sample size is less than 4 or if variance is zero
*/
function sampleKurtosis(vector) {
	const n = vector.length;
	const mean = vector.reduce((sum, val) => sum + val, 0) / n;
	const variance = vector.reduce((sum, val) => sum + (val - mean) ** 2, 0) / (n - 1);
	if (variance === 0) throw new Error("Variance is zero, kurtosis is undefined");
	const fourthMomentSum = vector.reduce((sum, val) => sum + (val - mean) ** 4, 0);
	return n * (n + 1) * fourthMomentSum / ((n - 1) * (n - 2) * (n - 3) * variance ** 2);
}
/**
* Calculates the sample excess kurtosis (bias-corrected)
* @param vector Array of numeric values
* @returns The sample excess kurtosis
* @throws Error if sample size is less than 4 or if variance is zero
*/
function sampleExcessKurtosis(vector) {
	const n = vector.length;
	const mean = vector.reduce((sum, val) => sum + val, 0) / n;
	const variance = vector.reduce((sum, val) => sum + (val - mean) ** 2, 0) / (n - 1);
	if (variance === 0) throw new Error("Variance is zero, kurtosis is undefined");
	const fourthMomentSum = vector.reduce((sum, val) => sum + (val - mean) ** 4, 0);
	return n * (n + 1) * fourthMomentSum / ((n - 1) * (n - 2) * (n - 3) * variance ** 2) - 3 * (n - 1) * (n - 1) / ((n - 2) * (n - 3));
}
const kurtosisReductionFunction = {
	kurtosis: (vector) => kurtosis(vector),
	minLength: 4
};
const eccessKurtosisReductionFunction = {
	excessKurtosis: (vector) => excessKurtosis(vector),
	minLength: 4
};
const sampleKurtosisReductionFunction = {
	sampleKurtosis: (vector) => sampleKurtosis(vector),
	minLength: 4
};
const sampleExcessKurtosisReductionFunction = {
	sampleExcessKurtosis: (vector) => sampleExcessKurtosis(vector),
	minLength: 4
};
//#endregion
//#region src/builtin/modules/vector/reductionFunctions/rms.ts
const rmsReductionFunction = { rms: (vector) => Math.sqrt(vector.reduce((acc, val) => acc + val ** 2, 0) / vector.length) };
//#endregion
//#region src/builtin/modules/vector/calcMad.ts
function calcMad(vector) {
	const median = calcMedian$1(vector);
	return vector.reduce((acc, val) => acc + Math.abs(val - median), 0) / vector.length;
}
//#endregion
//#region src/builtin/modules/vector/reductionFunctions/mad.ts
const madReductionFunction = { mad: (vector) => calcMad(vector) };
//#endregion
//#region src/builtin/modules/vector/reductionFunctions/medad.ts
const medadReductionFunction = { medad: (vector) => calcMedad(vector) };
//#endregion
//#region src/builtin/modules/vector/reductionFunctions/giniCoefficient.ts
const giniCoefficientReductionFunction = {
	giniCoefficient: (vector) => {
		if (vector.some((x) => x < 0)) throw new Error("Gini coefficient is not defined for negative values");
		const sorted = [...vector].sort((a, b) => a - b);
		const n = sorted.length;
		const sum = sorted.reduce((acc, val) => acc + val, 0);
		if (sum === 0) return 0;
		return 2 * sorted.reduce((acc, val, i) => acc + (i + 1) * val, 0) / (n * sum) - (n + 1) / n;
	},
	minLength: 1
};
//#endregion
//#region src/builtin/modules/vector/entropy.ts
/**
* Calculates the Shannon entropy of a vector.
* Entropy measures the amount of uncertainty or randomness in the data.
*
* @param vector - An array of values to calculate entropy for
* @returns The entropy value (in bits) or 0 for empty arrays
*/
function calculateEntropy(vector) {
	const frequencies = /* @__PURE__ */ new Map();
	for (const value of vector) frequencies.set(value, (frequencies.get(value) || 0) + 1);
	const total = vector.length;
	let entropy = 0;
	for (const frequency of frequencies.values()) {
		const probability = frequency / total;
		/* v8 ignore next 3 */
		if (probability > 0) entropy -= probability * Math.log2(probability);
	}
	return entropy;
}
//#endregion
//#region src/builtin/modules/vector/reductionFunctions/entropy.ts
const entropyReductionFunction = {
	entropy: (vector) => calculateEntropy(vector),
	minLength: 1
};
//#endregion
//#region src/builtin/modules/vector/reductionFunctions/index.ts
const reductionFunctionNormalExpressions = {};
addReductionFunctions$1(meanReductionFunction);
addReductionFunctions$1(geometricMeanReductionFunction);
addReductionFunctions$1(harmonicMeanReductionFunction);
addReductionFunctions$1(medianReductionFunction);
addReductionFunctions$1(sumReductionFunction);
addReductionFunctions$1(prodReductionFunction);
addReductionFunctions$1(varianceReductionFunction);
addReductionFunctions$1(sampleVarianceReductionFunction);
addReductionFunctions$1(stdevReductionFunction);
addReductionFunctions$1(sampleStdevReductionFunction);
addReductionFunctions$1(iqrReductionFunction);
addReductionFunctions$1(spanReductionFunction);
addReductionFunctions$1(skewnessReductionFunction);
addReductionFunctions$1(sampleSkewnessReductionFunction);
addReductionFunctions$1(eccessKurtosisReductionFunction);
addReductionFunctions$1(kurtosisReductionFunction);
addReductionFunctions$1(sampleExcessKurtosisReductionFunction);
addReductionFunctions$1(sampleKurtosisReductionFunction);
addReductionFunctions$1(rmsReductionFunction);
addReductionFunctions$1(madReductionFunction);
addReductionFunctions$1(medadReductionFunction);
addReductionFunctions$1(giniCoefficientReductionFunction);
addReductionFunctions$1(entropyReductionFunction);
delete reductionFunctionNormalExpressions.sum;
delete reductionFunctionNormalExpressions.prod;
delete reductionFunctionNormalExpressions.mean;
delete reductionFunctionNormalExpressions.median;
function addReductionFunctions$1(fns) {
	for (const [key, value] of Object.entries(fns)) {
		/* v8 ignore next 3 */
		if (reductionFunctionNormalExpressions[key]) throw new Error(`Duplicate normal expression key found: ${key}`);
		if (key !== "minLength" && key !== "padding" && typeof value === "function") {
			const reductionFn = value;
			const baseKey = key.replace(/^/, "");
			const capKey = baseKey.charAt(0).toUpperCase() + baseKey.slice(1);
			const movingKey = `moving${capKey}`;
			const centeredMovingKey = `centeredMoving${capKey}`;
			const runningKey = `running${capKey}`;
			const minLength = fns.minLength ?? 1;
			assertNumber(minLength, void 0, {
				integer: true,
				finite: true,
				gte: 0
			});
			reductionFunctionNormalExpressions[key] = createReductionNormalExpression(reductionFn, minLength);
			reductionFunctionNormalExpressions[movingKey] = createMovingNormalExpression(reductionFn, minLength);
			reductionFunctionNormalExpressions[centeredMovingKey] = createCenteredMovingNormalExpression(reductionFn, minLength, fns.padding ?? null);
			reductionFunctionNormalExpressions[runningKey] = createRunningNormalExpression(reductionFn, minLength);
		}
	}
}
function createReductionNormalExpression(reductionFunction, minLength) {
	return {
		evaluate: ([vector_], sourceCodeInfo) => {
			const vector = assertVector(vector_, sourceCodeInfo);
			if (vector.length < minLength) throw new RuntimeError(`Vector length must be at least ${minLength}`, sourceCodeInfo);
			try {
				return reductionFunction(vector);
			} catch (error) {
				throw new DvalaError(error, sourceCodeInfo);
			}
		},
		arity: toFixedArity(1)
	};
}
function createMovingNormalExpression(reductionFunction, minLength) {
	return {
		evaluate: ([vector_, windowSize], sourceCodeInfo) => {
			const vector = assertVector(vector_, sourceCodeInfo);
			assertNumber(windowSize, sourceCodeInfo, {
				integer: true,
				finite: true,
				gte: minLength,
				lte: vector.length
			});
			if (vector.length === 0) return [];
			try {
				if (windowSize >= vector.length) return [reductionFunction(vector)];
				const result = [];
				for (let i = 0; i < vector.length - windowSize + 1; i += 1) result.push(reductionFunction(vector.slice(i, i + windowSize)));
				return result;
			} catch (error) {
				throw new DvalaError(error, sourceCodeInfo);
			}
		},
		arity: toFixedArity(2)
	};
}
function createCenteredMovingNormalExpression(reductionFunction, minLength, padding) {
	return {
		evaluate: ([vector_, windowSize, leftPadding, rightPadding], sourceCodeInfo) => {
			const vector = assertVector(vector_, sourceCodeInfo);
			if (vector.length < minLength) throw new RuntimeError(`Vector length must be at least ${minLength}`, sourceCodeInfo);
			assertNumber(windowSize, sourceCodeInfo, {
				integer: true,
				finite: true,
				gte: minLength,
				lte: vector.length
			});
			leftPadding = leftPadding ?? padding;
			if (leftPadding !== null) assertNumber(leftPadding, sourceCodeInfo, { finite: true });
			rightPadding = rightPadding ?? padding;
			if (rightPadding !== null) assertNumber(rightPadding, sourceCodeInfo, { finite: true });
			if (vector.length === 0) return [];
			const halfWindowSize = Math.floor(windowSize / 2);
			const paddedVector = [
				...Array(halfWindowSize).fill(leftPadding),
				...vector,
				...Array(halfWindowSize).fill(rightPadding)
			];
			const start = typeof leftPadding === "number" ? 0 : halfWindowSize;
			const end = vector.length - (typeof rightPadding === "number" ? 0 : windowSize - halfWindowSize - 1);
			const result = [...Array(start).fill(null)];
			try {
				for (let i = start; i < end; i += 1) result.push(reductionFunction(paddedVector.slice(i, i + windowSize)));
			} catch (error) {
				throw new DvalaError(error, sourceCodeInfo);
			}
			result.push(...Array(vector.length - end).fill(null));
			return result;
		},
		arity: {
			min: 2,
			max: 4
		}
	};
}
function createRunningNormalExpression(reductionFunction, minLength) {
	return {
		evaluate: ([vector_], sourceCodeInfo) => {
			const vector = assertVector(vector_, sourceCodeInfo);
			if (vector.length < minLength) throw new RuntimeError(`Vector length must be at least ${minLength}`, sourceCodeInfo);
			if (vector.length === 0) return [];
			try {
				const nullsCount = Math.max(minLength - 1, 0);
				const result = Array(nullsCount).fill(null);
				for (let i = nullsCount; i < vector.length; i += 1) result.push(reductionFunction(vector.slice(0, i + 1)));
				return result;
			} catch (error) {
				throw new DvalaError(error, sourceCodeInfo);
			}
		},
		arity: toFixedArity(1)
	};
}
//#endregion
//#region src/builtin/modules/vector/vector.dvala
var vector_default = "{\n  movingFn: (arr, windowSize, fn) -> do\n    if windowSize > count(arr) then\n      perform(@dvala.error, \"Expected windowSize <= array length\")\n    else\n      null\n    end;\n    for(i in range(count(arr) - windowSize + 1)) -> do\n      fn(slice(arr, i, i + windowSize));\n    end;\n  end,\n  runningFn: (arr, fn) -> for(i in range(count(arr))) -> do\n    fn(slice(arr, 0, i + 1));\n  end,\n};\n";
//#endregion
//#region src/builtin/modules/vector/index.ts
function calcMedian(vector) {
	const sorted = [...vector].sort((a, b) => a - b);
	const mid = Math.floor(sorted.length / 2);
	return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}
const vectorFunctions = {
	movingFn: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("movingFn is implemented in Dvala");
		},
		arity: toFixedArity(3),
		docs: {
			category: "vector",
			returns: { type: "array" },
			args: {
				arr: { type: "array" },
				windowSize: {
					type: "number",
					description: "The size of the moving window."
				},
				fn: { type: "function" }
			},
			variants: [{ argumentNames: [
				"arr",
				"windowSize",
				"fn"
			] }],
			description: "Returns the result of applying `fn` to each moving window of size `windowSize` in `arr`.",
			seeAlso: ["vector.runningFn", "vector.movingMean"],
			examples: [
				"let { movingFn } = import(\"vector\");\nmovingFn([1, 2, 3], 2, sum)",
				"let { movingFn } = import(\"vector\");\nmovingFn([1, 2, 3], 1, sum)",
				"let { movingFn } = import(\"vector\");\nmovingFn([1, 2, 3], 3, sum)"
			]
		}
	},
	runningFn: {
		evaluate: () => {
			/* v8 ignore next -- migrated to .dvala; this TS stub never executes */
			throw new Error("runningFn is implemented in Dvala");
		},
		arity: toFixedArity(2),
		docs: {
			category: "vector",
			returns: { type: "array" },
			args: {
				a: { type: "array" },
				b: { type: "function" }
			},
			variants: [{ argumentNames: ["a", "b"] }],
			description: "Returns the result of applying `b` to each element of `a`.",
			seeAlso: ["vector.movingFn", "vector.runningMean"],
			examples: [
				"let { runningFn } = import(\"vector\");\nrunningFn([1, 2, 3], sum)",
				"let { runningFn } = import(\"vector\");\nrunningFn([1, 2, 3], max)",
				"let { runningFn } = import(\"vector\");\nrunningFn([1, 2, 3], min)"
			]
		}
	},
	mean: {
		evaluate: ([vector_], sourceCodeInfo) => {
			const vector = assertNonEmptyVector(vector_, sourceCodeInfo);
			return vector.reduce((acc, val) => acc + val, 0) / vector.length;
		},
		arity: toFixedArity(1),
		docs: {
			category: "vector",
			returns: { type: "number" },
			args: { vector: {
				type: "vector",
				description: "The vector to calculate the mean of."
			} },
			variants: [{ argumentNames: ["vector"] }],
			description: "Returns the arithmetic **mean** of all elements in the `vector`. Throws for an empty vector.",
			seeAlso: [
				"vector.median",
				"sum",
				"prod",
				"vector.movingMean",
				"vector.centeredMovingMean",
				"vector.runningMean",
				"vector.geometricMean",
				"vector.harmonicMean",
				"vector.rms",
				"vector.mode"
			],
			examples: ["let { mean } = import(\"vector\");\nmean([1, 2, 3, 4, 5])", "let { mean } = import(\"vector\");\nmean([1, -2, 3])"]
		}
	},
	median: {
		evaluate: ([vector_], sourceCodeInfo) => {
			return calcMedian(assertNonEmptyVector(vector_, sourceCodeInfo));
		},
		arity: toFixedArity(1),
		docs: {
			category: "vector",
			returns: { type: "number" },
			args: { vector: {
				type: "vector",
				description: "The vector to calculate the median of."
			} },
			variants: [{ argumentNames: ["vector"] }],
			description: "Returns the **median** of all elements in the `vector`. For even-length vectors, returns the average of the two middle values. Throws for an empty vector.",
			seeAlso: [
				"vector.mean",
				"sum",
				"prod",
				"vector.movingMedian",
				"vector.centeredMovingMedian",
				"vector.runningMedian",
				"vector.mode",
				"vector.quartiles",
				"vector.percentile",
				"vector.iqr",
				"vector.medad"
			],
			examples: [
				"let { median } = import(\"vector\");\nmedian([1, 2, 3, 4, 5])",
				"let { median } = import(\"vector\");\nmedian([1, 2, 3, 4])",
				"let { median } = import(\"vector\");\nmedian([3, 1, 4, 1, 5])"
			]
		}
	},
	mode: {
		evaluate: ([vector_], sourceCodeInfo) => {
			return mode(assertNonEmptyVector(vector_, sourceCodeInfo));
		},
		arity: toFixedArity(1)
	},
	linspace: {
		evaluate: ([start, end, numPoints], sourceCodeInfo) => {
			assertNumber(start, sourceCodeInfo, { finite: true });
			assertNumber(end, sourceCodeInfo, { finite: true });
			assertNumber(numPoints, sourceCodeInfo, {
				integer: true,
				nonNegative: true
			});
			if (numPoints === 0) return [];
			if (numPoints === 1) return [start];
			const step = (end - start) / (numPoints - 1);
			return Array.from({ length: numPoints }, (_, i) => start + i * step);
		},
		arity: toFixedArity(3)
	},
	quartiles: {
		evaluate: ([vector_], sourceCodeInfo) => {
			const vector = assertVector(vector_, sourceCodeInfo);
			if (vector.length < 4) throw new RuntimeError("Quartiles require at least four values", sourceCodeInfo);
			return quartiles(vector);
		},
		arity: toFixedArity(1)
	},
	percentile: {
		evaluate: ([vector_, percentile], sourceCodeInfo) => {
			const vector = assertNonEmptyVector(vector_, sourceCodeInfo);
			assertNumber(percentile, sourceCodeInfo, {
				finite: true,
				nonNegative: true,
				lte: 100
			});
			return calcPercentile(vector, percentile);
		},
		arity: toFixedArity(2)
	},
	quantile: {
		evaluate: ([vector_, quantile], sourceCodeInfo) => {
			const vector = assertVector(vector_, sourceCodeInfo);
			assertNumber(quantile, sourceCodeInfo, {
				finite: true,
				nonNegative: true,
				lte: 1
			});
			return calcPercentile(vector, quantile * 100);
		},
		arity: toFixedArity(2)
	},
	histogram: {
		evaluate: ([vector_, bins], sourceCodeInfo) => {
			const vector = assertVector(vector_, sourceCodeInfo);
			assertNumber(bins, sourceCodeInfo, {
				integer: true,
				positive: true
			});
			return calcHistogram(vector, bins);
		},
		arity: toFixedArity(2)
	},
	ecdf: {
		evaluate: ([vector_, value], sourceCodeInfo) => {
			const vector = assertNonEmptyVector(vector_, sourceCodeInfo);
			assertNumber(value, sourceCodeInfo, { finite: true });
			const sorted = [...vector].sort((a, b) => a - b);
			const index = sorted.findIndex((val) => val > value);
			return index === -1 ? 1 : index / sorted.length;
		},
		arity: toFixedArity(2)
	},
	isOutliers: {
		evaluate: ([vector_], sourceCodeInfo) => {
			return hasOutliers(assertVector(vector_, sourceCodeInfo));
		},
		arity: toFixedArity(1)
	},
	outliers: {
		evaluate: ([vector_], sourceCodeInfo) => {
			return outliers(assertVector(vector_, sourceCodeInfo));
		},
		arity: toFixedArity(1)
	},
	bincount: {
		evaluate: (params, sourceCodeInfo) => {
			const vector = assertVector(params.get(0), sourceCodeInfo);
			vector.forEach((val) => assertNumber(val, sourceCodeInfo, {
				finite: true,
				integer: true,
				nonNegative: true
			}));
			const minSize = params.get(1) ?? 0;
			assertNumber(minSize, sourceCodeInfo, {
				integer: true,
				nonNegative: true
			});
			const weights_ = params.get(2) ?? void 0;
			const weights = weights_ !== void 0 ? assertVector(weights_, sourceCodeInfo) : void 0;
			if (weights !== void 0) {
				if (weights.length !== vector.length) throw new RuntimeError("Weights vector must be the same length as the input vector", sourceCodeInfo);
				weights.forEach((val) => assertNumber(val, sourceCodeInfo, { finite: true }));
			}
			return bincount(vector, minSize, weights);
		},
		arity: {
			min: 1,
			max: 3
		}
	},
	winsorize: {
		evaluate: ([vector_, lowerQuantile, upperQuantile], sourceCodeInfo) => {
			const vector = assertVector(vector_, sourceCodeInfo);
			assertNumber(lowerQuantile, sourceCodeInfo, {
				finite: true,
				gte: 0,
				lte: 1
			});
			upperQuantile ??= lowerQuantile > .5 ? 1 : 1 - lowerQuantile;
			assertNumber(upperQuantile, sourceCodeInfo, {
				finite: true,
				gte: lowerQuantile,
				lte: 1
			});
			if (vector.length === 0) return [];
			const sorted = [...vector].sort((a, b) => a - b);
			const lowerIndex = Math.max(0, Math.floor(lowerQuantile * vector.length));
			const upperIndex = Math.min(vector.length - 1, Math.max(0, Math.floor(upperQuantile * vector.length) - 1));
			const lowerBound = sorted[lowerIndex];
			const upperBound = sorted[upperIndex];
			return vector.map((val) => Math.max(lowerBound, Math.min(val, upperBound)));
		},
		arity: {
			min: 2,
			max: 3
		}
	},
	mse: {
		evaluate: ([vectorA_, vectorB_], sourceCodeInfo) => {
			const vectorA = assertNonEmptyVector(vectorA_, sourceCodeInfo);
			const vectorB = assertNonEmptyVector(vectorB_, sourceCodeInfo);
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			return vectorA.reduce((acc, val, i) => acc + (val - vectorB[i]) ** 2, 0) / vectorA.length;
		},
		arity: toFixedArity(2)
	},
	rmse: {
		evaluate: ([vectorA_, vectorB_], sourceCodeInfo) => {
			const vectorA = assertNonEmptyVector(vectorA_, sourceCodeInfo);
			const vectorB = assertNonEmptyVector(vectorB_, sourceCodeInfo);
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			return Math.sqrt(vectorA.reduce((acc, val, i) => acc + (val - vectorB[i]) ** 2, 0) / vectorA.length);
		},
		arity: toFixedArity(2)
	},
	mae: {
		evaluate: ([vectorA_, vectorB_], sourceCodeInfo) => {
			const vectorA = assertNonEmptyVector(vectorA_, sourceCodeInfo);
			const vectorB = assertNonEmptyVector(vectorB_, sourceCodeInfo);
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			return vectorA.reduce((acc, val, i) => acc + Math.abs(val - vectorB[i]), 0) / vectorA.length;
		},
		arity: toFixedArity(2)
	},
	smape: {
		evaluate: ([vectorA_, vectorB_], sourceCodeInfo) => {
			const vectorA = assertNonEmptyVector(vectorA_, sourceCodeInfo);
			const vectorB = assertNonEmptyVector(vectorB_, sourceCodeInfo);
			if (vectorA.length !== vectorB.length) throw new RuntimeError("Vectors must be of the same length", sourceCodeInfo);
			return vectorA.reduce((acc, val, i) => {
				const diff = Math.abs(val - vectorB[i]);
				const denom = (Math.abs(val) + Math.abs(vectorB[i])) / 2;
				return acc + (denom === 0 ? 0 : diff / denom);
			}, 0) / vectorA.length;
		},
		arity: toFixedArity(2)
	}
};
addReductionFunctions(reductionFunctionNormalExpressions);
function addReductionFunctions(sequences) {
	for (const [key, value] of Object.entries(sequences)) {
		/* v8 ignore next 3 */
		if (vectorFunctions[key]) throw new Error(`Duplicate normal expression key found: ${key}`);
		vectorFunctions[key] = value;
	}
}
for (const [key, docs] of Object.entries(moduleDocs))
 /* v8 ignore next 2 */
if (vectorFunctions[key]) vectorFunctions[key].docs = docs;
const vectorModule = {
	name: "vector",
	description: "Statistical functions: mean, median, stdev, quartiles, histograms, and outliers.",
	functions: vectorFunctions,
	source: vector_default,
	sourcePath: "packages/dvala-engine/src/builtin/modules/vector/vector.dvala",
	docs: moduleDocs
};
//#endregion
//#region src/reference/api.ts
function getNumberTheorySequenceNames(name) {
	return [
		`numberTheory.${name}Seq`,
		`numberTheory.${name}Nth`,
		`numberTheory.${name}TakeWhile`,
		`numberTheory.is${name.charAt(0).toUpperCase()}${name.slice(1)}`
	];
}
function getVectorReductionNames(name) {
	const capName = name.charAt(0).toUpperCase() + name.slice(1);
	return [
		`vector.${name}`,
		`vector.moving${capName}`,
		`vector.centeredMoving${capName}`,
		`vector.running${capName}`
	];
}
function getVectorMovingNames(name) {
	return [
		`vector.moving-${name}`,
		`vector.centered-moving-${name}`,
		`vector.running-${name}`
	];
}
const api = {
	collection: [
		"filter",
		"map",
		"reduce",
		"count",
		"get",
		"contains",
		"assoc",
		"++"
	],
	collectionUtils: [
		"collection.filteri",
		"collection.mapi",
		"collection.reducei",
		"collection.reduceRight",
		"collection.reduceiRight",
		"collection.reductions",
		"collection.reductionsi",
		"collection.getIn",
		"collection.assocIn",
		"collection.update",
		"collection.updateIn",
		"collection.notEmpty",
		"collection.isEvery",
		"collection.notEvery",
		"collection.isAny",
		"collection.notAny"
	],
	array: [
		"range",
		"repeat",
		"flatten"
	],
	sequence: [
		"nth",
		"push",
		"pop",
		"indexOf",
		"some",
		"reverse",
		"first",
		"second",
		"last",
		"rest",
		"next",
		"sort",
		"slice",
		"take",
		"takeLast",
		"drop",
		"dropLast",
		"takeWhile",
		"dropWhile",
		"minIndex",
		"maxIndex",
		"sortIndices",
		"countValues"
	],
	sequenceUtils: [
		"sequence.mapcat",
		"sequence.position",
		"sequence.lastIndexOf",
		"sequence.unshift",
		"sequence.splice",
		"sequence.sortBy",
		"sequence.distinct",
		"sequence.remove",
		"sequence.removeAt",
		"sequence.splitAt",
		"sequence.splitWith",
		"sequence.frequencies",
		"sequence.groupBy",
		"sequence.partition",
		"sequence.partitionAll",
		"sequence.partitionBy",
		"sequence.isStartsWith",
		"sequence.isEndsWith",
		"sequence.interleave",
		"sequence.interpose"
	],
	math: [
		"+",
		"-",
		"*",
		"/",
		"mod",
		"%",
		"quot",
		"inc",
		"dec",
		"sqrt",
		"cbrt",
		"^",
		"round",
		"trunc",
		"floor",
		"ceil",
		"min",
		"max",
		"abs",
		"sign",
		"sum",
		"prod",
		"cumsum",
		"cumprod"
	],
	mathUtils: [
		"math.ln",
		"math.log2",
		"math.log10",
		"math.sin",
		"math.cos",
		"math.tan",
		"math.asin",
		"math.acos",
		"math.atan",
		"math.sinh",
		"math.cosh",
		"math.tanh",
		"math.asinh",
		"math.acosh",
		"math.atanh",
		"math.toRad",
		"math.toDeg"
	],
	functional: [
		"|>",
		"apply",
		"identity",
		"comp",
		"constantly"
	],
	functionalUtils: [
		"functional.juxt",
		"functional.complement",
		"functional.everyPred",
		"functional.somePred",
		"functional.fnull"
	],
	meta: [
		"doc",
		"arity",
		"effectName",
		"qualifiedName",
		"qualifiedMatcher"
	],
	misc: [
		"!=",
		"==",
		"<",
		">",
		"<=",
		">=",
		"not",
		"boolean",
		"compare",
		"typeOf",
		"raise"
	],
	object: [
		"dissoc",
		"keys",
		"vals",
		"entries",
		"find",
		"merge",
		"mergeWith",
		"zipmap",
		"selectKeys"
	],
	predicate: [
		"isBoolean",
		"isNull",
		"isNumber",
		"isString",
		"isFunction",
		"isInteger",
		"isArray",
		"isObject",
		"isCollection",
		"isSequence",
		"isRegexp",
		"isEffect",
		"isZero",
		"isPos",
		"isNeg",
		"isEven",
		"isOdd",
		"isFalse",
		"isTrue",
		"isEmpty",
		"isNotEmpty",
		"isVector",
		"isGrid",
		"isMatrix",
		"isMonotonic",
		"isStrictlyMonotonic",
		"isIncreasing",
		"isDecreasing",
		"isStrictlyIncreasing",
		"isStrictlyDecreasing"
	],
	regularExpression: [
		"regexp",
		"reMatch",
		"replace",
		"replaceAll"
	],
	string: [
		"str",
		"number",
		"lowerCase",
		"upperCase",
		"trim",
		"split",
		"join",
		"isBlank"
	],
	stringUtils: [
		"string.stringRepeat",
		"string.fromCharCode",
		"string.toCharCode",
		"string.trimLeft",
		"string.trimRight",
		"string.padLeft",
		"string.padRight",
		"string.splitLines",
		"string.template",
		"string.encodeBase64",
		"string.decodeBase64",
		"string.encodeUriComponent",
		"string.decodeUriComponent",
		"string.capitalize"
	],
	bitwise: [
		"<<",
		">>",
		">>>",
		"&",
		"|",
		"xor"
	],
	bitwiseUtils: [
		"bitwise.bitNot",
		"bitwise.bitAndNot",
		"bitwise.bitFlip",
		"bitwise.bitClear",
		"bitwise.bitSet",
		"bitwise.bitTest"
	],
	assertion: ["assert"],
	assertionUtils: [
		"assertion.assertEqual",
		"assertion.assertNotEqual",
		"assertion.assertGt",
		"assertion.assertLt",
		"assertion.assertGte",
		"assertion.assertLte",
		"assertion.assertTrue",
		"assertion.assertFalse",
		"assertion.assertTruthy",
		"assertion.assertFalsy",
		"assertion.assertNull",
		"assertion.assertFails",
		"assertion.assertFailsWith",
		"assertion.assertSucceeds",
		"assertion.assertArray",
		"assertion.assertBoolean",
		"assertion.assertCollection",
		"assertion.assertFunction",
		"assertion.assertGrid",
		"assertion.assertInteger",
		"assertion.assertMatrix",
		"assertion.assertNumber",
		"assertion.assertObject",
		"assertion.assertRegexp",
		"assertion.assertSequence",
		"assertion.assertString",
		"assertion.assertVector"
	],
	grid: [
		"grid.isCellEvery",
		"grid.isSome",
		"grid.isEveryRow",
		"grid.isSomeRow",
		"grid.isEveryCol",
		"grid.isSomeCol",
		"grid.row",
		"grid.col",
		"grid.shape",
		"grid.fill",
		"grid.generate",
		"grid.reshape",
		"grid.transpose",
		"grid.flipH",
		"grid.flipV",
		"grid.rotate",
		"grid.crop",
		"grid.sliceRows",
		"grid.sliceCols",
		"grid.spliceRows",
		"grid.spliceCols",
		"grid.concatRows",
		"grid.concatCols",
		"grid.cellMap",
		"grid.cellMapi",
		"grid.cellReduce",
		"grid.cellReducei",
		"grid.pushRows",
		"grid.unshiftRows",
		"grid.popRow",
		"grid.shiftRow",
		"grid.pushCols",
		"grid.unshiftCols",
		"grid.popCol",
		"grid.shiftCol",
		"grid.fromArray"
	],
	matrix: [
		"matrix.mul",
		"matrix.det",
		"matrix.inv",
		"matrix.adj",
		"matrix.cofactor",
		"matrix.minor",
		"matrix.trace",
		"matrix.isSymmetric",
		"matrix.isTriangular",
		"matrix.isUpperTriangular",
		"matrix.isLowerTriangular",
		"matrix.isDiagonal",
		"matrix.isSquare",
		"matrix.isOrthogonalMatrix",
		"matrix.isIdentity",
		"matrix.isInvertible",
		"matrix.hilbert",
		"matrix.vandermonde",
		"matrix.band",
		"matrix.isBanded",
		"matrix.rank",
		"matrix.frobeniusNorm",
		"matrix.oneNorm",
		"matrix.infNorm",
		"matrix.maxNorm"
	],
	vectorUtils: [
		"vector.movingFn",
		"vector.runningFn",
		"vector.mean",
		"vector.median",
		"vector.mode",
		"vector.linspace",
		"vector.quartiles",
		"vector.percentile",
		"vector.quantile",
		"vector.histogram",
		"vector.ecdf",
		"vector.isOutliers",
		"vector.outliers",
		"vector.bincount",
		"vector.winsorize",
		"vector.mse",
		"vector.mae",
		"vector.rmse",
		"vector.smape",
		...getVectorMovingNames("mean"),
		...getVectorMovingNames("median"),
		...getVectorReductionNames("variance"),
		...getVectorReductionNames("sampleVariance"),
		...getVectorMovingNames("sum"),
		...getVectorMovingNames("prod"),
		...getVectorReductionNames("stdev"),
		...getVectorReductionNames("sampleStdev"),
		...getVectorReductionNames("iqr"),
		...getVectorReductionNames("span"),
		...getVectorReductionNames("geometricMean"),
		...getVectorReductionNames("harmonicMean"),
		...getVectorReductionNames("skewness"),
		...getVectorReductionNames("sampleSkewness"),
		...getVectorReductionNames("kurtosis"),
		...getVectorReductionNames("sampleKurtosis"),
		...getVectorReductionNames("excessKurtosis"),
		...getVectorReductionNames("sampleExcessKurtosis"),
		...getVectorReductionNames("rms"),
		...getVectorReductionNames("mad"),
		...getVectorReductionNames("medad"),
		...getVectorReductionNames("giniCoefficient"),
		...getVectorReductionNames("entropy"),
		...getVectorReductionNames("skewness")
	],
	linAlg: [
		"linearAlgebra.reflect",
		"linearAlgebra.refract",
		"linearAlgebra.lerp",
		"linearAlgebra.rotate2d",
		"linearAlgebra.rotate3d",
		"linearAlgebra.dot",
		"linearAlgebra.cross",
		"linearAlgebra.normalizeMinmax",
		"linearAlgebra.normalizeZscore",
		"linearAlgebra.normalizeRobust",
		"linearAlgebra.normalizeL1",
		"linearAlgebra.normalizeL2",
		"linearAlgebra.normalizeLog",
		"linearAlgebra.angle",
		"linearAlgebra.projection",
		"linearAlgebra.isOrthogonal",
		"linearAlgebra.isParallel",
		"linearAlgebra.isCollinear",
		"linearAlgebra.cosineSimilarity",
		"linearAlgebra.euclideanDistance",
		"linearAlgebra.euclideanNorm",
		"linearAlgebra.manhattanDistance",
		"linearAlgebra.manhattanNorm",
		"linearAlgebra.hammingDistance",
		"linearAlgebra.hammingNorm",
		"linearAlgebra.chebyshevDistance",
		"linearAlgebra.chebyshevNorm",
		"linearAlgebra.minkowskiDistance",
		"linearAlgebra.minkowskiNorm",
		"linearAlgebra.cov",
		"linearAlgebra.corr",
		"linearAlgebra.spearmanCorr",
		"linearAlgebra.pearsonCorr",
		"linearAlgebra.kendallTau",
		"linearAlgebra.autocorrelation",
		"linearAlgebra.crossCorrelation",
		"linearAlgebra.rref",
		"linearAlgebra.solve",
		"linearAlgebra.toPolar",
		"linearAlgebra.fromPolar"
	],
	numberTheory: [
		...getNumberTheorySequenceNames("abundant"),
		...getNumberTheorySequenceNames("arithmetic"),
		...getNumberTheorySequenceNames("bell"),
		...getNumberTheorySequenceNames("catalan"),
		...getNumberTheorySequenceNames("composite"),
		...getNumberTheorySequenceNames("deficient"),
		...getNumberTheorySequenceNames("factorial"),
		...getNumberTheorySequenceNames("fibonacci"),
		...getNumberTheorySequenceNames("geometric"),
		...getNumberTheorySequenceNames("golomb"),
		...getNumberTheorySequenceNames("happy"),
		...getNumberTheorySequenceNames("lookAndSay"),
		...getNumberTheorySequenceNames("lucas"),
		...getNumberTheorySequenceNames("lucky"),
		...getNumberTheorySequenceNames("mersenne"),
		...getNumberTheorySequenceNames("padovan"),
		...getNumberTheorySequenceNames("partition"),
		...getNumberTheorySequenceNames("pell"),
		...getNumberTheorySequenceNames("perfect"),
		...getNumberTheorySequenceNames("perfectCube"),
		...getNumberTheorySequenceNames("perfectPower"),
		...getNumberTheorySequenceNames("perfectSquare"),
		...getNumberTheorySequenceNames("polygonal"),
		...getNumberTheorySequenceNames("prime"),
		...getNumberTheorySequenceNames("recaman"),
		...getNumberTheorySequenceNames("sylvester"),
		...getNumberTheorySequenceNames("thueMorse"),
		...getNumberTheorySequenceNames("tribonacci"),
		"numberTheory.collatzSeq",
		"numberTheory.jugglerSeq",
		"numberTheory.bernoulliSeq",
		"numberTheory.bernoulliTakeWhile",
		"numberTheory.bernoulliNth",
		"numberTheory.combinations",
		"numberTheory.countCombinations",
		"numberTheory.derangements",
		"numberTheory.countDerangements",
		"numberTheory.divisors",
		"numberTheory.countDivisors",
		"numberTheory.properDivisors",
		"numberTheory.countProperDivisors",
		"numberTheory.primeFactors",
		"numberTheory.countPrimeFactors",
		"numberTheory.distinctPrimeFactors",
		"numberTheory.countDistinctPrimeFactors",
		"numberTheory.factorial",
		"numberTheory.partitions",
		"numberTheory.countPartitions",
		"numberTheory.permutations",
		"numberTheory.countPermutations",
		"numberTheory.powerSet",
		"numberTheory.countPowerSet",
		"numberTheory.isCoprime",
		"numberTheory.isDivisibleBy",
		"numberTheory.gcd",
		"numberTheory.lcm",
		"numberTheory.multinomial",
		"numberTheory.isAmicable",
		"numberTheory.eulerTotient",
		"numberTheory.mobius",
		"numberTheory.mertens",
		"numberTheory.sigma",
		"numberTheory.carmichaelLambda",
		"numberTheory.cartesianProduct",
		"numberTheory.perfectPower",
		"numberTheory.modExp",
		"numberTheory.modInv",
		"numberTheory.extendedGcd",
		"numberTheory.chineseRemainder",
		"numberTheory.stirlingFirst",
		"numberTheory.stirlingSecond"
	],
	convert: [
		"convert.angstromToUm",
		"convert.angstromToMm",
		"convert.angstromToCm",
		"convert.angstromToM",
		"convert.angstromToKm",
		"convert.angstromToIn",
		"convert.angstromToFt",
		"convert.angstromToYd",
		"convert.angstromToMi",
		"convert.angstromToNmi",
		"convert.umToAngstrom",
		"convert.umToMm",
		"convert.umToCm",
		"convert.umToM",
		"convert.umToKm",
		"convert.umToIn",
		"convert.umToFt",
		"convert.umToYd",
		"convert.umToMi",
		"convert.umToNmi",
		"convert.mmToAngstrom",
		"convert.mmToUm",
		"convert.mmToCm",
		"convert.mmToM",
		"convert.mmToKm",
		"convert.mmToIn",
		"convert.mmToFt",
		"convert.mmToYd",
		"convert.mmToMi",
		"convert.mmToNmi",
		"convert.cmToAngstrom",
		"convert.cmToUm",
		"convert.cmToMm",
		"convert.cmToM",
		"convert.cmToKm",
		"convert.cmToIn",
		"convert.cmToFt",
		"convert.cmToYd",
		"convert.cmToMi",
		"convert.cmToNmi",
		"convert.mToAngstrom",
		"convert.mToUm",
		"convert.mToMm",
		"convert.mToCm",
		"convert.mToKm",
		"convert.mToIn",
		"convert.mToFt",
		"convert.mToYd",
		"convert.mToMi",
		"convert.mToNmi",
		"convert.kmToAngstrom",
		"convert.kmToUm",
		"convert.kmToMm",
		"convert.kmToCm",
		"convert.kmToM",
		"convert.kmToIn",
		"convert.kmToFt",
		"convert.kmToYd",
		"convert.kmToMi",
		"convert.kmToNmi",
		"convert.inToAngstrom",
		"convert.inToUm",
		"convert.inToMm",
		"convert.inToCm",
		"convert.inToM",
		"convert.inToKm",
		"convert.inToFt",
		"convert.inToYd",
		"convert.inToMi",
		"convert.inToNmi",
		"convert.ftToAngstrom",
		"convert.ftToUm",
		"convert.ftToMm",
		"convert.ftToCm",
		"convert.ftToM",
		"convert.ftToKm",
		"convert.ftToIn",
		"convert.ftToYd",
		"convert.ftToMi",
		"convert.ftToNmi",
		"convert.ydToAngstrom",
		"convert.ydToUm",
		"convert.ydToMm",
		"convert.ydToCm",
		"convert.ydToM",
		"convert.ydToKm",
		"convert.ydToIn",
		"convert.ydToFt",
		"convert.ydToMi",
		"convert.ydToNmi",
		"convert.miToAngstrom",
		"convert.miToUm",
		"convert.miToMm",
		"convert.miToCm",
		"convert.miToM",
		"convert.miToKm",
		"convert.miToIn",
		"convert.miToFt",
		"convert.miToYd",
		"convert.miToNmi",
		"convert.nmiToAngstrom",
		"convert.nmiToUm",
		"convert.nmiToMm",
		"convert.nmiToCm",
		"convert.nmiToM",
		"convert.nmiToKm",
		"convert.nmiToIn",
		"convert.nmiToFt",
		"convert.nmiToYd",
		"convert.nmiToMi",
		"convert.mgToG",
		"convert.mgToKg",
		"convert.mgToT",
		"convert.mgToOz",
		"convert.mgToLb",
		"convert.mgToSt",
		"convert.gToMg",
		"convert.gToKg",
		"convert.gToT",
		"convert.gToOz",
		"convert.gToLb",
		"convert.gToSt",
		"convert.kgToMg",
		"convert.kgToG",
		"convert.kgToT",
		"convert.kgToOz",
		"convert.kgToLb",
		"convert.kgToSt",
		"convert.tToMg",
		"convert.tToG",
		"convert.tToKg",
		"convert.tToOz",
		"convert.tToLb",
		"convert.tToSt",
		"convert.ozToMg",
		"convert.ozToG",
		"convert.ozToKg",
		"convert.ozToT",
		"convert.ozToLb",
		"convert.ozToSt",
		"convert.lbToMg",
		"convert.lbToG",
		"convert.lbToKg",
		"convert.lbToT",
		"convert.lbToOz",
		"convert.lbToSt",
		"convert.stToMg",
		"convert.stToG",
		"convert.stToKg",
		"convert.stToT",
		"convert.stToOz",
		"convert.stToLb",
		"convert.mlToCl",
		"convert.mlToDl",
		"convert.mlToL",
		"convert.mlToTsp",
		"convert.mlToTbsp",
		"convert.mlToFlOz",
		"convert.mlToCup",
		"convert.mlToPt",
		"convert.mlToQt",
		"convert.mlToGal",
		"convert.clToMl",
		"convert.clToDl",
		"convert.clToL",
		"convert.clToTsp",
		"convert.clToTbsp",
		"convert.clToFlOz",
		"convert.clToCup",
		"convert.clToPt",
		"convert.clToQt",
		"convert.clToGal",
		"convert.dlToMl",
		"convert.dlToCl",
		"convert.dlToL",
		"convert.dlToTsp",
		"convert.dlToTbsp",
		"convert.dlToFlOz",
		"convert.dlToCup",
		"convert.dlToPt",
		"convert.dlToQt",
		"convert.dlToGal",
		"convert.lToMl",
		"convert.lToCl",
		"convert.lToDl",
		"convert.lToTsp",
		"convert.lToTbsp",
		"convert.lToFlOz",
		"convert.lToCup",
		"convert.lToPt",
		"convert.lToQt",
		"convert.lToGal",
		"convert.tspToMl",
		"convert.tspToCl",
		"convert.tspToDl",
		"convert.tspToL",
		"convert.tspToTbsp",
		"convert.tspToFlOz",
		"convert.tspToCup",
		"convert.tspToPt",
		"convert.tspToQt",
		"convert.tspToGal",
		"convert.tbspToMl",
		"convert.tbspToCl",
		"convert.tbspToDl",
		"convert.tbspToL",
		"convert.tbspToTsp",
		"convert.tbspToFlOz",
		"convert.tbspToCup",
		"convert.tbspToPt",
		"convert.tbspToQt",
		"convert.tbspToGal",
		"convert.fl-ozToMl",
		"convert.fl-ozToCl",
		"convert.fl-ozToDl",
		"convert.fl-ozToL",
		"convert.fl-ozToTsp",
		"convert.fl-ozToTbsp",
		"convert.fl-ozToCup",
		"convert.fl-ozToPt",
		"convert.fl-ozToQt",
		"convert.fl-ozToGal",
		"convert.cupToMl",
		"convert.cupToCl",
		"convert.cupToDl",
		"convert.cupToL",
		"convert.cupToTsp",
		"convert.cupToTbsp",
		"convert.cupToFlOz",
		"convert.cupToPt",
		"convert.cupToQt",
		"convert.cupToGal",
		"convert.ptToMl",
		"convert.ptToCl",
		"convert.ptToDl",
		"convert.ptToL",
		"convert.ptToTsp",
		"convert.ptToTbsp",
		"convert.ptToFlOz",
		"convert.ptToCup",
		"convert.ptToQt",
		"convert.ptToGal",
		"convert.qtToMl",
		"convert.qtToCl",
		"convert.qtToDl",
		"convert.qtToL",
		"convert.qtToTsp",
		"convert.qtToTbsp",
		"convert.qtToFlOz",
		"convert.qtToCup",
		"convert.qtToPt",
		"convert.qtToGal",
		"convert.galToMl",
		"convert.galToCl",
		"convert.galToDl",
		"convert.galToL",
		"convert.galToTsp",
		"convert.galToTbsp",
		"convert.galToFlOz",
		"convert.galToCup",
		"convert.galToPt",
		"convert.galToQt",
		"convert.msToS",
		"convert.msToMin",
		"convert.msToH",
		"convert.msToDay",
		"convert.msToWeek",
		"convert.sToMs",
		"convert.sToMin",
		"convert.sToH",
		"convert.sToDay",
		"convert.sToWeek",
		"convert.minToMs",
		"convert.minToS",
		"convert.minToH",
		"convert.minToDay",
		"convert.minToWeek",
		"convert.hToMs",
		"convert.hToS",
		"convert.hToMin",
		"convert.hToDay",
		"convert.hToWeek",
		"convert.dayToMs",
		"convert.dayToS",
		"convert.dayToMin",
		"convert.dayToH",
		"convert.dayToWeek",
		"convert.weekToMs",
		"convert.weekToS",
		"convert.weekToMin",
		"convert.weekToH",
		"convert.weekToDay",
		"convert.mm2ToCm2",
		"convert.mm2ToM2",
		"convert.mm2ToKm2",
		"convert.mm2ToIn2",
		"convert.mm2ToFt2",
		"convert.mm2ToYd2",
		"convert.mm2ToAcre",
		"convert.mm2ToHectare",
		"convert.cm2ToMm2",
		"convert.cm2ToM2",
		"convert.cm2ToKm2",
		"convert.cm2ToIn2",
		"convert.cm2ToFt2",
		"convert.cm2ToYd2",
		"convert.cm2ToAcre",
		"convert.cm2ToHectare",
		"convert.m2ToMm2",
		"convert.m2ToCm2",
		"convert.m2ToKm2",
		"convert.m2ToIn2",
		"convert.m2ToFt2",
		"convert.m2ToYd2",
		"convert.m2ToAcre",
		"convert.m2ToHectare",
		"convert.km2ToMm2",
		"convert.km2ToCm2",
		"convert.km2ToM2",
		"convert.km2ToIn2",
		"convert.km2ToFt2",
		"convert.km2ToYd2",
		"convert.km2ToAcre",
		"convert.km2ToHectare",
		"convert.in2ToMm2",
		"convert.in2ToCm2",
		"convert.in2ToM2",
		"convert.in2ToKm2",
		"convert.in2ToFt2",
		"convert.in2ToYd2",
		"convert.in2ToAcre",
		"convert.in2ToHectare",
		"convert.ft2ToMm2",
		"convert.ft2ToCm2",
		"convert.ft2ToM2",
		"convert.ft2ToKm2",
		"convert.ft2ToIn2",
		"convert.ft2ToYd2",
		"convert.ft2ToAcre",
		"convert.ft2ToHectare",
		"convert.yd2ToMm2",
		"convert.yd2ToCm2",
		"convert.yd2ToM2",
		"convert.yd2ToKm2",
		"convert.yd2ToIn2",
		"convert.yd2ToFt2",
		"convert.yd2ToAcre",
		"convert.yd2ToHectare",
		"convert.acreToMm2",
		"convert.acreToCm2",
		"convert.acreToM2",
		"convert.acreToKm2",
		"convert.acreToIn2",
		"convert.acreToFt2",
		"convert.acreToYd2",
		"convert.acreToHectare",
		"convert.hectareToMm2",
		"convert.hectareToCm2",
		"convert.hectareToM2",
		"convert.hectareToKm2",
		"convert.hectareToIn2",
		"convert.hectareToFt2",
		"convert.hectareToYd2",
		"convert.hectareToAcre",
		"convert.mpsToKm/h",
		"convert.mpsToMph",
		"convert.mpsToKn",
		"convert.mpsToFt/s",
		"convert.kmhToM/s",
		"convert.kmhToMph",
		"convert.kmhToKn",
		"convert.kmhToFt/s",
		"convert.mphToM/s",
		"convert.mphToKm/h",
		"convert.mphToKn",
		"convert.mphToFt/s",
		"convert.knToM/s",
		"convert.knToKm/h",
		"convert.knToMph",
		"convert.knToFt/s",
		"convert.fpsToM/s",
		"convert.fpsToKm/h",
		"convert.fpsToMph",
		"convert.fpsToKn",
		"convert.bToKb",
		"convert.bToMb",
		"convert.bToGb",
		"convert.bToTb",
		"convert.bToPb",
		"convert.kbToB",
		"convert.kbToMb",
		"convert.kbToGb",
		"convert.kbToTb",
		"convert.kbToPb",
		"convert.mbToB",
		"convert.mbToKb",
		"convert.mbToGb",
		"convert.mbToTb",
		"convert.mbToPb",
		"convert.gbToB",
		"convert.gbToKb",
		"convert.gbToMb",
		"convert.gbToTb",
		"convert.gbToPb",
		"convert.tbToB",
		"convert.tbToKb",
		"convert.tbToMb",
		"convert.tbToGb",
		"convert.tbToPb",
		"convert.pbToB",
		"convert.pbToKb",
		"convert.pbToMb",
		"convert.pbToGb",
		"convert.pbToTb",
		"convert.paToKpa",
		"convert.paToBar",
		"convert.paToAtm",
		"convert.paToPsi",
		"convert.paToMmhg",
		"convert.kpaToPa",
		"convert.kpaToBar",
		"convert.kpaToAtm",
		"convert.kpaToPsi",
		"convert.kpaToMmhg",
		"convert.barToPa",
		"convert.barToKpa",
		"convert.barToAtm",
		"convert.barToPsi",
		"convert.barToMmhg",
		"convert.atmToPa",
		"convert.atmToKpa",
		"convert.atmToBar",
		"convert.atmToPsi",
		"convert.atmToMmhg",
		"convert.psiToPa",
		"convert.psiToKpa",
		"convert.psiToBar",
		"convert.psiToAtm",
		"convert.psiToMmhg",
		"convert.mmhgToPa",
		"convert.mmhgToKpa",
		"convert.mmhgToBar",
		"convert.mmhgToAtm",
		"convert.mmhgToPsi",
		"convert.jToKj",
		"convert.jToCal",
		"convert.jToKcal",
		"convert.jToWh",
		"convert.jToKwh",
		"convert.jToBtu",
		"convert.kjToJ",
		"convert.kjToCal",
		"convert.kjToKcal",
		"convert.kjToWh",
		"convert.kjToKwh",
		"convert.kjToBtu",
		"convert.calToJ",
		"convert.calToKj",
		"convert.calToKcal",
		"convert.calToWh",
		"convert.calToKwh",
		"convert.calToBtu",
		"convert.kcalToJ",
		"convert.kcalToKj",
		"convert.kcalToCal",
		"convert.kcalToWh",
		"convert.kcalToKwh",
		"convert.kcalToBtu",
		"convert.whToJ",
		"convert.whToKj",
		"convert.whToCal",
		"convert.whToKcal",
		"convert.whToKwh",
		"convert.whToBtu",
		"convert.kwhToJ",
		"convert.kwhToKj",
		"convert.kwhToCal",
		"convert.kwhToKcal",
		"convert.kwhToWh",
		"convert.kwhToBtu",
		"convert.btuToJ",
		"convert.btuToKj",
		"convert.btuToCal",
		"convert.btuToKcal",
		"convert.btuToWh",
		"convert.btuToKwh",
		"convert.wToKw",
		"convert.wToMw",
		"convert.wToHp",
		"convert.kwToW",
		"convert.kwToMw",
		"convert.kwToHp",
		"convert.mwToW",
		"convert.mwToKw",
		"convert.mwToHp",
		"convert.hpToW",
		"convert.hpToKw",
		"convert.hpToMw",
		"convert.hzToKhz",
		"convert.hzToMhz",
		"convert.hzToGhz",
		"convert.khzToHz",
		"convert.khzToMhz",
		"convert.khzToGhz",
		"convert.mhzToHz",
		"convert.mhzToKhz",
		"convert.mhzToGhz",
		"convert.ghzToHz",
		"convert.ghzToKhz",
		"convert.ghzToMhz",
		"convert.degToRad",
		"convert.degToGrad",
		"convert.degToTurn",
		"convert.radToDeg",
		"convert.radToGrad",
		"convert.radToTurn",
		"convert.gradToDeg",
		"convert.gradToRad",
		"convert.gradToTurn",
		"convert.turnToDeg",
		"convert.turnToRad",
		"convert.turnToGrad",
		"convert.cToF",
		"convert.cToK",
		"convert.fToC",
		"convert.fToK",
		"convert.kToC",
		"convert.kToF"
	],
	json: ["json.jsonParse", "json.jsonStringify"],
	time: ["time.epochToIso-date", "time.iso-dateToEpoch"],
	handler: ["handler.retry", "handler.fallback"],
	ast: [
		"ast.num",
		"ast.strNode",
		"ast.bool",
		"ast.nil",
		"ast.sym",
		"ast.builtin",
		"ast.effectNode",
		"ast.call",
		"ast.ifNode",
		"ast.block",
		"ast.nodeType",
		"ast.isNum",
		"ast.isStr",
		"ast.isSym",
		"ast.isBuiltin",
		"ast.isCall",
		"ast.isIf",
		"ast.isBlock",
		"ast.isLet",
		"ast.isFn",
		"ast.isBool",
		"ast.isNil",
		"ast.isEffectNode",
		"ast.isAstNode",
		"ast.payload",
		"ast.prettyPrint"
	],
	macros: [
		"macros.trace",
		"macros.unless",
		"macros.tap",
		"macros.dbg",
		"macros.cond"
	],
	shorthand: [
		"-short-regexp",
		"-short-fn",
		"-short-object"
	],
	prelude: [
		"-prelude-Positive",
		"-prelude-NonNegative",
		"-prelude-NonZero",
		"-prelude-NonEmpty"
	],
	datatype: [
		"-type-number",
		"-type-string",
		"-type-object",
		"-type-array",
		"-type-vector",
		"-type-matrix",
		"-type-grid",
		"-type-boolean",
		"-type-atom",
		"-type-function",
		"-type-integer",
		"-type-any",
		"-type-null",
		"-type-collection",
		"-type-sequence",
		"-type-regexp",
		"-type-effect",
		"-type-never"
	]
};
const coreApiFunctionNames = [
	...api.collection,
	...api.array,
	...api.sequence,
	...api.math,
	...api.functional,
	...api.meta,
	...api.misc,
	...api.object,
	...api.predicate,
	...api.regularExpression,
	...api.string,
	...api.bitwise,
	...api.assertion
];
const moduleApiFunctionNames = [
	...api.matrix,
	...api.vectorUtils,
	...api.linAlg,
	...api.grid,
	...api.numberTheory,
	...api.mathUtils,
	...api.functionalUtils,
	...api.assertionUtils,
	...api.stringUtils,
	...api.collectionUtils,
	...api.sequenceUtils,
	...api.bitwiseUtils,
	...api.convert,
	...api.json,
	...api.time
];
const apiFunctionNames = [...coreApiFunctionNames, ...moduleApiFunctionNames];
const coreApiNames = [
	...coreApiFunctionNames,
	...api.shorthand,
	...api.datatype,
	...api.prelude
];
const apiNames = [
	...apiFunctionNames,
	...api.shorthand,
	...api.datatype,
	...api.prelude
];
function isApiName(arg) {
	return apiNames.includes(arg);
}
function isCoreApiName(arg) {
	return coreApiNames.includes(arg);
}
//#endregion
//#region src/reference/types.ts
function isFunctionReference(ref) {
	return "returns" in ref && "args" in ref && "variants" in ref && !("effect" in ref);
}
function isCustomReference(ref) {
	return "customVariants" in ref;
}
function isShorthandReference(ref) {
	return "shorthand" in ref;
}
function isDatatypeReference(ref) {
	return "datatype" in ref;
}
function isPreludeReference(ref) {
	return "prelude" in ref;
}
function isEffectReference(ref) {
	return "effect" in ref;
}
//#endregion
//#region src/scopeToGlobalContext.ts
function scopeToGlobalContext(scope) {
	if (!scope) return void 0;
	const context = {};
	for (const [key, value] of Object.entries(scope)) context[key] = { value: validateFromJS(value, `scope binding "${key}"`) };
	return context;
}
//#endregion
//#region src/resume.ts
/**
* Standalone `resume()` function for resuming suspended continuations.
*
* The primary API for running Dvala programs is `createDvala()` from `./createDvala`.
*/
/**
* Resume a suspended continuation.
*
* Takes a `Snapshot` from a previous `RunResult` of type `'suspended'`, a
* resume value, and optional handlers. Re-enters the trampoline at
* the point of suspension with the provided value.
*
* `modules` must be provided again if the Dvala program uses `import`.
*
* Always resolves — never rejects. May return `completed`, `suspended`
* (if another suspend is hit), or `error`.
*
* ```typescript
* const { snapshot } = suspendedResult
* const next = await resume(snapshot, humanDecision, { handlers })
* ```
*/
async function resume(snapshot, value, options) {
	try {
		const modules = options?.modules ? new Map(options.modules.map((m) => [m.name, m])) : void 0;
		const scopeContext = scopeToGlobalContext(options?.scope);
		const deserialized = deserializeFromObject(snapshot.continuation, {
			modules,
			scope: scopeContext
		});
		const deserializeOptions = { modules };
		const initialSnapshotState = {
			snapshots: deserialized.snapshots,
			nextSnapshotIndex: deserialized.nextSnapshotIndex,
			maxSnapshots: options?.maxSnapshots,
			autoCheckpoint: !options?.disableAutoCheckpoint,
			...options?.terminalSnapshot ? { terminalSnapshot: true } : {}
		};
		const result = deserialized.initialStep ? await continueWithEffects(deserialized.initialStep, options?.handlers, initialSnapshotState, deserializeOptions, options?.terminalSnapshot) : await resumeWithEffects(deserialized.k, validateFromJS(value, "resume() value"), options?.handlers, initialSnapshotState, deserializeOptions);
		if (result.type === "completed") return {
			...result,
			value: toJS(result.value)
		};
		return result;
	} catch (error) {
		if (error instanceof DvalaError) return {
			type: "error",
			error
		};
		return {
			type: "error",
			error: new DvalaError(`${error}`, void 0)
		};
	}
}
//#endregion
//#region src/retrigger.ts
/**
* Resume a suspended continuation by re-triggering the original effect.
*
* Takes a `Snapshot` from a previous `RunResult` of type `'suspended'` and
* re-dispatches the original effect (captured in `snapshot.effectName` /
* `snapshot.effectArg`) to the registered host handlers. The handler then
* calls `resume(value)`, `fail()`, or `suspend()` as normal.
*
* Throws if the snapshot has no captured effect (i.e. suspension occurred
* outside of an effect handler, such as in a parallel/race branch).
*
* Always resolves — never rejects. May return `completed`, `suspended`
* (if the handler suspends again), or `error`.
*
* ```typescript
* const { snapshot } = suspendedResult
* const next = await retrigger(snapshot, { handlers })
* ```
*/
async function retrigger(snapshot, options) {
	if (!snapshot.effectName || snapshot.effectArg === void 0) return {
		type: "error",
		error: new RuntimeError("Cannot retrigger: snapshot has no captured effect (suspended outside of an effect handler)", void 0)
	};
	try {
		const deserializeOptions = { modules: options?.modules ? new Map(options.modules.map((m) => [m.name, m])) : void 0 };
		const deserialized = deserializeFromObject(snapshot.continuation, deserializeOptions);
		const result = await retriggerWithEffects(deserialized.k, snapshot.effectName, snapshot.effectArg, options?.handlers, {
			snapshots: deserialized.snapshots,
			nextSnapshotIndex: deserialized.nextSnapshotIndex,
			maxSnapshots: options?.maxSnapshots,
			autoCheckpoint: !options?.disableAutoCheckpoint,
			...options?.terminalSnapshot ? { terminalSnapshot: true } : {}
		}, deserializeOptions);
		if (result.type === "completed") return {
			...result,
			value: toJS(result.value)
		};
		return result;
	} catch (error) {
		if (error instanceof DvalaError) return {
			type: "error",
			error
		};
		return {
			type: "error",
			error: new DvalaError(`${error}`, void 0)
		};
	}
}
//#endregion
export { ContextStackImpl, EPSILON, HaltSignal, MODULE_DESCRIPTION_MAX_LENGTH, ResumeFromSignal, SUSPENDED_MESSAGE, SuspensionSignal, addToSet, allStandardEffectDefinitions, andSpecialExpression, applyFrame, approxEqual, approxZero, arrayNormalExpression, arraySpecialExpression, assertModule, assertValidHostValue, assertionNormalExpression, astModule, bitwiseNormalExpression, bitwiseUtilsModule, builtin, categories, chain, checkArrayLengthConstraint, checkObjectTypeConstraint, checkTypeAtPath, clearEffectRefInternMap, collHasKey, collectionNormalExpression, collectionUtilsModule, compare, contentHash, continueWithEffects, convertModule, coreCategories, coreCategoryDescriptions, createContextStack, createSnapshot, createTestCollector, createTestModule, dedupSubTrees, deepEqual, describeSerializationIssue, deserializeFromObject, effectSpecialExpression, evaluate, evaluateAsync, evaluateNode, evaluateNodeForFold, evaluateWithEffects, evaluateWithSyncEffects, everySequential, expandPoolRefs, extractArrayRest, extractCheckpointSnapshots, extractMatchArrayRest, extractMatchObjectRest, extractMatchValueByPath, extractObjectRest, extractValueByPath, factorialOf, filterSequential, findIndexSequential, findMatchingHandlers, flattenBindingPattern, flattenMatchPattern, forEachSequential, forSpecialExpression, formatCallStack, fromJS, functionalNormalExpression, functionalUtilsModule, generateDocString, generateUUID, getAllBindingTargetNames, getEffectRef, getFunctionUnresolvedSymbols, getMetaNormalExpression, getStandardEffectDefinition, getStandardEffectHandler, getUndefinedSymbols, getUnit, gridModule, handlerModule, hostHandler, importSpecialExpression, initCoreDvalaSources, isApiName, isContextEntry, isCoreApiName, isCustomReference, isDataType, isDatatypeReference, isEffectReference, isFunctionDocs, isFunctionReference, isHaltSignal, isPoolRef, isPreludeReference, isResumeFromSignal, isSerializable, isShorthandReference, isSuspensionSignal, joinSets, jsonModule, lambdaSpecialExpression, letSpecialExpression, linearAlgebraModule, loopSpecialExpression, macrosModule, mapSequential, matchSpecialExpression, mathNormalExpression, mathUtilsModule, matrixModule, miscNormalExpression, moduleDocsFromFunctions, normalExpressionKeys, normalExpressionTypes, normalExpressions, numberTheoryModule, objectNormalExpression, objectSpecialExpression, orSpecialExpression, parallelSpecialExpression, performSpecialExpression, predicatesNormalExpression, qqSpecialExpression, qualifiedNameMatchesPattern, raceSpecialExpression, reconstructCallStack, recurSpecialExpression, reduceSequential, regexpNormalExpression, resume, resumeWithEffects, retrigger, retriggerWithEffects, runAsyncTrampoline, runSyncTrampoline, scopeToGlobalContext, sequenceNormalExpression, sequenceUtilsModule, serializeSuspensionBlob, serializeTerminalSnapshot, serializeToObject, setEffectReference, setNormalExpressionReference, settledSpecialExpression, smartTrim, someSequential, specialExpressionKeys, specialExpressions, standardEffectNames, stepNode, stringNormalExpression, stringUtilsModule, tick, timeModule, toAny, toJS, toNonNegativeInteger, tryCatch, validateBindingRootType, validateFromJS, vectorModule, walkDefaults };

//# sourceMappingURL=index.js.map